import { ChantierQuoteWizard, ChantierSavedSummary } from "./ChantierQuoteWizard";
import { calculateChantier, chantierDefaults, chantierDescription, chantierSubmissionErrors, chantierSnapshot, chantierCostingParams } from "./chantier-quote";
import { calculateMaterialEstimate, materialUnitLabels, type MaterialEstimateItem } from "./material-estimate";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import {
  configured,
  getAuthSessionPersistence,
  setAuthSessionPersistence,
  supabase,
} from "./lib/supabase";
import { establishPasswordUpdateSession } from "./auth-password-update";
import "./styles.css";
import "./legacy-shell.css";
import "./workflow.css";
import "./admin.css";
import {
  ClientBillingRich,
  ClientDashboardRich,
  ClientDocumentsRich,
  ClientMissionsRich,
  ClientPropertiesRich,
  AdminSupplierInvoicesRich,
  ContractorAvailabilityRich,
  ContractorBillingRich,
  ContractorDocumentsRich,
  MapsLink,
  ProfileEditorRich,
  WorkerAgendaRich,
  WorkerDashboardRich,
  WorkerJourneyRich,
  WorkerMissionsRich,
} from "./rich-portals";
import type {
  PortalData,
  PortalSupplierInvoice,
  PortalSupplierInvoiceMission,
} from "./rich-portals";
import {
  downloadMissionCalendar,
  googleCalendarMissionUrl,
} from "./calendar";
import {
  configureShowcaseUser,
  getShowcaseRole,
  isShowcaseMode,
  resetShowcaseData,
  setShowcaseRole,
  type ShowcaseRole,
} from "./showcase-db";
import "./showcase-mode.css";
import "./ux-polish.css";
import "./mobile-app.css";
import { PlatformAdminApp } from "./platform-admin";
import {
  createBillingIdempotencyKey,
  createHenrriBillingProvider,
  type BillingDocumentPreparation,
  type BillingQuoteLifecycle,
} from "./billing-provider";
import {
  recordIntegrationOperation,
  type IntegrationExternalLink,
} from "./integration-journal";
import {
  AdminBillingCases,
  type BillingCaseLinkCandidate,
} from "./admin-billing-cases";
import { AdminCrm } from "./admin-crm";
import { AdminActionPlan } from "./admin-action-plan";
import { CoachNotesPanel } from "./coach-notes";
import { AdminHrPlanning, WorkerOnCallEligibility } from "./admin-hr-planning";
import { AdminWorkerDevelopment } from "./worker-development";
import { AdminHistoricalImport } from "./admin-historical-import";
import { AdminPrivacyCenter, PrivacyRightsPanel } from "./admin-privacy";
import { AdminReliabilityCenter } from "./admin-reliability";
import { ApplicationFeedbackDialog } from "./application-feedback";
import { ReviewQrCode } from "./review-qr";
import {
  deactivateMobilePush,
  initializeMobilePush,
  requestMobilePushPermission,
  type MobilePushState,
  type MobilePushTarget,
} from "./mobile-push";
import type {
  BillingCaseAmendment,
  BillingCaseCostReviewCandidate,
  BillingCaseConsistencyReview,
  BillingCaseInvoiceCandidate,
  BillingCaseInvoicePreparation,
  BillingCaseLedgerEntry,
  BillingCaseLink,
  BillingCaseOperationalSync,
  BillingCaseProgress,
  BillingCaseSummary,
} from "./billing-cases";
import {
  simulateProcessDefinition,
  type BusinessEventEnvelope,
  type ProcessDefinition,
  type ProcessSimulationResult,
} from "./process-engine";
import {
  buildGuidanceEvidencePricingProposals,
  detectQuoteGuidanceMode,
  evaluateQuoteGuidance,
  type QuoteGuidanceEvaluation,
} from "./quote-guidance";
import {
  calculateReferralCommission,
  DEFAULT_REFERRAL_COMMISSION_RATE,
  DEFAULT_REFERRAL_PRICING_PROTECTION_RATE,
} from "./referral-commission";
type Role = "admin" | "employee" | "contractor" | "client";
type Tab =
  | "dashboard"
  | "agenda"
  | "routes"
  | "missions"
  | "clients"
  | "crm"
  | "action_plan"
  | "billing"
  | "accounting"
  | "profitability"
  | "stocks"
  | "director"
  | "properties"
  | "compensation"
  | "availability"
  | "documents"
  | "team"
  | "time"
  | "leave"
  | "mileage"
  | "journey"
  | "proposals"
  | "payroll"
  | "company"
  | "settings"
  | "profile";
type Profile = {
  id: string;
  role: Role;
  display_name: string;
  organization_id: string;
  home_organization_id?: string;
  active_organization_id?: string;
  email?: string;
  phone?: string;
  calendar_enabled?: boolean;
  permission_set_id?: string | null;
  access_status?: "invited" | "active" | "suspended";
  dashboard_preferences?: DashboardPreferences;
  is_primary_admin?: boolean;
};
type OrganizationContext = {
  organization_id: string;
  organization_name: string;
  role: Role;
  status: "active";
  permission_set_id: string | null;
  is_primary_admin: boolean;
  is_active: boolean;
};
type PaymentTermsMode = "immediate" | "days_after_issue" | "end_of_month";
type Client = {
  id: string;
  name: string;
  profile_id?: string | null;
  category?: string | null;
  client_type?: "standard" | "concierge";
  address?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_title?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  calendar_enabled?: boolean;
  billing_emission?: string;
  billing_grouping?: string;
  target_margin_rate?: number | null;
  average_round_trip_minutes?: number | null;
  monthly_admin_minutes?: number | null;
  incident_count?: number;
  strategic_priority?: boolean;
  scoring_notes?: string | null;
  commission_enabled?: boolean;
  commission_rate?: number | null;
  commission_basis?: "ex_vat" | "inc_vat";
  commission_vat_rate?: number;
  commission_label?: string;
  payment_terms_mode?: PaymentTermsMode | null;
  payment_terms_days?: number | null;
  active?: boolean;
};
type Worker = {
  id: string;
  profile_id: string | null;
  kind: "employee" | "contractor";
  first_name: string | null;
  last_name: string | null;
  monthly_hours: number;
  daily_hours: number;
  net_salary?: number | null;
  employer_cost_monthly?: number | null;
  ticket_count: number | null;
  ticket_value: number | null;
  mileage_enabled: boolean;
  active: boolean;
  pricing_mode?: string | null;
  hourly_rate?: number | null;
  fixed_fee?: number | null;
  monthly_fee?: number | null;
  business_name?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  address?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  profile_notes?: string | null;
  skills?: string[];
  company_organization_id?: string | null;
  business_relationship_id?: string | null;
};
type WorkerCompanyProfile = {
  id: string;
  worker_id: string;
  legal_name: string | null;
  legal_form: string | null;
  siren: string | null;
  registered_address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
  website: string | null;
  billing_email: string | null;
  authorized_services: string[];
  service_areas: string[];
  compliance_status: "to_review" | "compliant" | "restricted" | "blocked";
  restriction_reason: string | null;
  bank_name: string | null;
  account_holder: string | null;
  iban: string | null;
  bic: string | null;
  last_reviewed_at: string | null;
};
type WorkerCompanyRepresentative = {
  id: string;
  worker_id: string;
  first_name: string | null;
  last_name: string;
  title: string;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  active: boolean;
};
type WorkerCompanyProfileEvent = {
  id: string;
  worker_id: string;
  event_type: "profile_saved" | "status_changed" | "representative_saved" | "representative_archived";
  previous_status: string | null;
  new_status: string | null;
  note: string | null;
  snapshot: Record<string, unknown>;
  created_at: string;
};
type WorkerRateCard = {
  id: string;
  worker_id: string;
  name: string;
  pricing_mode: "hourly" | "fixed" | "per_unit";
  rate: number;
  unit_label: string | null;
  notes: string | null;
  active: boolean;
  client_id?: string | null;
  property_id?: string | null;
  property_service_id?: string | null;
};
type WorkerCommercialProposal = {
  id: string;
  worker_id: string;
  proposal_kind: "mission" | "new_service";
  mission_id: string | null;
  client_id?: string | null;
  property_id?: string | null;
  proposed_service_name: string | null;
  pricing_mode: "fixed" | "hourly" | "per_unit" | "monthly";
  quantity: number;
  proposed_supplier_unit_rate: number;
  complementary_cost_ex_vat: number;
  proposed_supplier_total_ex_vat: number;
  client_price_reference_ex_vat?: number | null;
  target_mode_snapshot?: "margin" | "markup";
  target_rate_snapshot?: number;
  minimum_client_price_ex_vat?: number;
  projected_margin_rate?: number | null;
  projected_markup_rate?: number | null;
  profitability_alert?: boolean;
  description: string | null;
  status: "submitted" | "under_review" | "accepted" | "rejected" | "countered" | "withdrawn";
  version: number;
  previous_version_id: string | null;
  submitted_at: string;
  reviewed_at?: string | null;
  decision_notes: string | null;
};
type QuoteSourcingWorkflow = {
  id: string;
  quote_id: string;
  quote_line_id: string;
  client_id: string;
  worker_id: string;
  description_snapshot: string;
  client_target_price_ex_vat: number | null;
  target_mode_snapshot: "margin" | "markup";
  target_rate_snapshot: number;
  max_supplier_budget_ex_vat: number | null;
  accepted_offer_id: string | null;
  accepted_supplier_cost_ex_vat: number | null;
  suggested_client_price_ex_vat: number | null;
  final_client_price_ex_vat: number | null;
  status: "awaiting_supplier" | "supplier_proposed" | "countered" | "admin_review" | "approved_for_client" | "rejected" | "cancelled";
  approved_at: string | null;
  created_at: string;
};
type QuoteSourcingOffer = {
  id: string;
  workflow_id: string;
  worker_id: string;
  pricing_mode: "fixed" | "hourly" | "per_unit" | "monthly";
  quantity: number;
  supplier_unit_rate_ex_vat: number;
  complementary_cost_ex_vat: number;
  supplier_total_ex_vat: number;
  minimum_client_price_ex_vat: number;
  status: "submitted" | "accepted" | "countered" | "rejected" | "withdrawn";
  version: number;
  previous_offer_id: string | null;
  contractor_notes: string | null;
  counter_budget_ex_vat: number | null;
  decision_notes: string | null;
  submitted_at: string;
};
type QuoteSourcingTask = {
  id: string;
  quote_id: string;
  description_snapshot: string;
  request_title?: string | null;
  property_name?: string | null;
  property_address?: string | null;
  requested_quantity?: number | null;
  qualification_items?: Array<{
    label: string;
    answer: unknown;
    unit_label: string | null;
    options?: Array<{ value: string; label: string }>;
  }>;
  qualification_notes?: string | null;
  status: QuoteSourcingWorkflow["status"];
  latest_offer_id: string | null;
  latest_offer_status: QuoteSourcingOffer["status"] | null;
  latest_offer_version: number | null;
  latest_supplier_total_ex_vat: number | null;
  counter_budget_ex_vat: number | null;
  decision_notes: string | null;
  created_at: string;
};
type Mission = {
  id: string;
  client_id: string;
  property_id?: string | null;
  service_id?: string | null;
  property_service_id?: string | null;
  client_display_name?: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  workflow_status?: string;
  billing_status?: string;
  invoice_strategy?: "client_default" | "grouped" | "individual";
  checklist_model_id?: string | null;
  location: string;
  planned_hours: number;
  amount_ex_vat?: number;
  billing_mode?: string;
  billing_rate?: number;
  billing_quantity?: number;
  notes?: string | null;
  client_report?: string | null;
  next_actions?: string | null;
  admin_notes?: string | null;
  admin_validated_at?: string | null;
  required_skills?: string[];
  is_test?: boolean;
  client?: { name: string } | null;
};
type MissionServiceLine = {
  id: string;
  mission_id: string;
  client_service_id: string | null;
  property_service_id: string | null;
  label_snapshot: string;
  billing_mode_snapshot: string;
  quantity: number;
  unit_price_ex_vat: number;
  amount_ex_vat: number;
  vat_rate: number;
  planned_hours: number | null;
  position: number;
};
type Assignment = {
  id: string;
  mission_id: string;
  worker_id: string;
  assigned_hours: number;
  source: string;
  status: string;
  pricing_mode?: string | null;
  agreed_rate?: number | null;
  agreed_amount?: number | null;
  worker_rate_card_id?: string | null;
  proposed_at?: string | null;
  responded_at?: string | null;
  response_notes?: string | null;
  skill_override?: boolean;
  skill_override_reason?: string | null;
  missing_skills_snapshot?: string[];
};
type TimeEntry = {
  id: string;
  worker_id: string;
  work_date: string;
  started_at_ts: string | null;
  break_minutes: number;
  worked_minutes: number;
  status: string;
};
type Leave = {
  id: string;
  worker_id: string;
  starts_on: string;
  ends_on: string;
  reason: string | null;
  status: string;
};
type Mileage = {
  id: string;
  worker_id: string | null;
  owner_profile_id?: string | null;
  vehicle_id?: string | null;
  mission_id?: string | null;
  travel_date: string;
  kilometers: number;
  reason: string;
  status: string;
  calculated_amount?: number | null;
  annual_km_before?: number | null;
  annual_km_after?: number | null;
  scale_year?: number | null;
  scale_version?: string | null;
  scale_source_url?: string | null;
  route_calculation_id?: string | null;
  entry_source?: "legacy" | "route_calculation" | "exceptional_correction";
  correction_reason?: string | null;
  correction_evidence_path?: string | null;
};
type RouteMileageCalculation = {
  id: string;
  work_date: string;
  route_team_id: string | null;
  beneficiary_worker_id: string | null;
  beneficiary_profile_id: string | null;
  vehicle_id: string;
  include_return_to_headquarters: boolean;
  status: "pending_provider" | "calculating" | "calculated" | "review_required" | "approved" | "rejected" | "cancelled";
  provider: string;
  provider_route_version: string | null;
  origin_address: string;
  total_distance_meters: number | null;
  total_duration_seconds: number | null;
  calculated_at: string | null;
  error_message: string | null;
};
type RouteMileageStop = {
  id: string;
  route_calculation_id: string;
  mission_id: string | null;
  stop_kind: "headquarters_start" | "mission" | "headquarters_return";
  position: number;
  address_snapshot: string;
  planned_starts_at: string | null;
};
type RouteMileageLeg = {
  id: string;
  route_calculation_id: string;
  position: number;
  origin_address_snapshot: string;
  destination_address_snapshot: string;
  distance_meters: number;
  duration_seconds: number;
  provider_route_version: string | null;
  calculated_at: string;
};
type WorkerVehicle = {
  id: string;
  worker_id: string | null;
  owner_profile_id: string | null;
  vehicle_kind: "car" | "motorcycle" | "moped";
  energy_type: "thermal_hybrid" | "electric";
  brand: string;
  model: string | null;
  registration_number: string | null;
  fiscal_power: number;
  registration_document_path: string | null;
  document_status: "missing" | "pending" | "approved" | "rejected";
  document_notes: string | null;
  active: boolean;
};
type DocumentRow = {
  id: string;
  owner_profile_id?: string | null;
  kind: string;
  period: string | null;
  original_name: string;
  storage_path?: string;
  expires_on?: string | null;
  status?: string;
  requirement_id?: string | null;
  client_requirement_id?: string | null;
  created_at?: string;
};
type ClientService = {
  id: string;
  client_id: string;
  catalog_item_id?: string | null;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours?: number | null;
  active?: boolean;
};
type BillingCatalogFocus = {
  scope: "client" | "property";
  serviceId: string;
  clientId: string;
  propertyId?: string | null;
  label: string;
  pricingAnalysis?: {
    missionCount: number;
    averageRevenue: number;
    averageKnownCost: number;
    currentMarginRate: number;
    currentUnitPrice: number;
    billingMode: string;
    minimumMarginRate: number;
    recommendedMarginRate: number;
    comfortableMarginRate: number;
    minimumUnitPrice: number;
    recommendedUnitPrice: number;
    comfortableUnitPrice: number;
    missingCost: boolean;
  };
};
type ServiceCatalogItem = {
  id: string;
  item_code: string;
  name: string;
  description: string | null;
  item_kind: "service" | "product" | "rental" | "other";
  default_billing_mode: string;
  default_unit_label: string;
  default_unit_price_ex_vat: number;
  default_vat_rate: number;
  default_duration_hours: number | null;
  target_margin_rate: number | null;
  active: boolean;
  updated_at: string;
};
type ServiceCatalogVersion = {
  id: string;
  catalog_item_id: string;
  version: number;
  snapshot: Record<string, unknown>;
  change_reason: string;
  created_at: string;
};
type ServiceQualificationQuestionnaire = {
  id: string;
  catalog_item_id: string;
  version: number;
  name: string;
  description: string | null;
  status: "draft" | "published" | "archived";
  is_current_version: boolean;
  change_reason: string;
  created_at: string;
  published_at: string | null;
};
type QualificationPricingRule = {
  rule_id: string;
  label: string;
  operator: "equals" | "contains" | "gte" | "lte" | "between";
  comparison_value: string | number | boolean;
  comparison_value_max?: number | null;
  unit_price_delta_ex_vat?: number | null;
  unit_price_multiplier?: number | null;
  labor_hours_per_output_unit?: number | null;
  labor_time_multiplier?: number | null;
  fixed_cost_delta_ex_vat?: number | null;
  inventory_item_id?: string | null;
  inventory_application_passes?: number | null;
  inventory_billing_mode?: "included" | "separate" | null;
  description_line?: string | null;
  track_activity_time?: boolean;
  activity_label?: string | null;
  line_item_mode?: "catalog" | "client" | "one_off" | null;
  line_item_catalog_id?: string | null;
  line_item_name?: string | null;
  line_item_quantity?: number | null;
  line_item_unit_price_ex_vat?: number | null;
  line_item_vat_rate?: number | null;
};
type ServiceQualificationQuestion = {
  id: string;
  questionnaire_id: string;
  question_key: string;
  label: string;
  help_text: string | null;
  response_type: "boolean" | "number" | "integer" | "single_choice" | "multiple_choice" | "text";
  required: boolean;
  unit_label: string | null;
  options: Array<{ value: string; label: string }>;
  costing_target: "output_quantity" | "total_labor_hours" | "team_size" | "travel_cost_ex_vat" | "other_cost_ex_vat" | "contingency_rate" | null;
  pricing_rules: QualificationPricingRule[];
  position: number;
};
type QualificationRuleEvidence = {
  catalog_item_id: string;
  questionnaire_id: string;
  question_key: string;
  rule_id: string;
  rule_label: string;
  evidence_count: number;
  average_actual_labor_hours_per_output_unit: number | null;
  minimum_actual_labor_hours_per_output_unit: number | null;
  maximum_actual_labor_hours_per_output_unit: number | null;
  average_actual_cost_ex_vat_per_output_unit: number | null;
  last_observed_at: string | null;
  median_actual_labor_hours_per_output_unit?: number | null;
  prudent_actual_labor_hours_per_output_unit?: number | null;
};
function questionnaireTechnicalId(prefix: "q" | "r") {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "").slice(0, 16)}`;
}
type QuoteQualificationRun = {
  id: string;
  quote_id: string;
  quote_line_id: string;
  questionnaire_id: string;
  catalog_item_id: string;
  run_number: number;
  status: "needs_review" | "ready" | "cancelled";
  answers: Record<string, unknown>;
  derived_inputs: Record<string, unknown>;
  missing_required: string[];
  costing_run_id: string | null;
  notes: string | null;
  created_at: string;
};
type ServiceCostingProfile = {
  id: string;
  catalog_item_id: string;
  version: number;
  active: boolean;
  output_unit: "m2" | "m3" | "metre_lineaire" | "unite" | "forfait";
  expected_output_per_labor_hour: number | null;
  default_team_size: number;
  default_labor_cost_per_hour: number | null;
  default_travel_cost_ex_vat: number;
  contingency_rate: number;
  target_mode: "margin" | "markup";
  target_rate: number;
  source_kind: "manual" | "validated_missions";
  evidence_count: number;
  confidence: "to_review" | "low" | "medium" | "high" | "verified";
  required_skills: string[];
  assumptions_notes: string | null;
  created_at: string;
};
type ServiceCostingComponent = {
  id: string;
  profile_id: string;
  component_type: "equipment" | "other";
  business_kind: "equipment" | "purchase" | "other";
  label: string;
  cost_basis: "per_job" | "per_elapsed_hour" | "per_output_unit";
  quantity: number;
  unit_label: string;
  unit_cost_ex_vat: number;
  supplier_id: string | null;
  purchase_reference: string | null;
  purchase_url: string | null;
  source_note: string | null;
  position: number;
};
type QuoteCostingRun = {
  id: string;
  quote_id: string;
  quote_line_id: string;
  catalog_item_id: string | null;
  costing_profile_id: string | null;
  run_number: number;
  status: "draft" | "needs_review" | "applied" | "cancelled";
  output_quantity: number | null;
  output_unit: string | null;
  team_size: number;
  total_labor_hours: number | null;
  elapsed_hours: number | null;
  labor_cost_per_hour: number | null;
  labor_cost_ex_vat: number;
  material_cost_ex_vat: number;
  equipment_cost_ex_vat: number;
  travel_cost_ex_vat: number;
  other_cost_ex_vat: number;
  contingency_rate: number;
  contingency_cost_ex_vat: number;
  total_cost_ex_vat: number;
  target_mode: "margin" | "markup";
  target_rate: number;
  suggested_sale_price_ex_vat: number | null;
  proposed_sale_price_ex_vat: number;
  projected_margin_rate: number | null;
  projected_markup_rate: number | null;
  profitability_alert: boolean;
  missing_inputs: string[];
  notes: string | null;
  applied_at: string | null;
  created_at: string;
};
type QuoteCostingRunLine = {
  id: string;
  costing_run_id: string;
  component_type: "labor" | "material" | "equipment" | "travel" | "other" | "contingency";
  label: string;
  quantity: number;
  unit_label: string;
  unit_cost_ex_vat: number;
  amount_ex_vat: number;
  source_kind: string;
  confidence: string;
  position: number;
};
type ProcurementRequirement = {
  id: string;
  costing_run_id: string;
  costing_run_line_id: string | null;
  material_estimate_id: string | null;
  quote_id: string;
  quote_line_id: string;
  mission_id: string | null;
  supplier_id: string | null;
  inventory_item_id: string | null;
  source_kind: "external_purchase" | "stock_shortage";
  label: string;
  quantity: number;
  unit_label: string;
  estimated_unit_cost_ex_vat: number;
  estimated_total_cost_ex_vat: number;
  purchase_reference: string | null;
  purchase_url: string | null;
  needed_on: string | null;
  status: "to_review" | "approved" | "cancelled" | "superseded";
  source_snapshot: Record<string, unknown>;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
};
type MissionCostingReview = {
  id: string;
  mission_id: string;
  quote_costing_run_id: string | null;
  catalog_item_id: string | null;
  costing_profile_id: string | null;
  review_number: number;
  status: "ready" | "needs_review" | "approved" | "rejected";
  planned_output_quantity: number | null;
  actual_output_quantity: number | null;
  output_unit: string | null;
  planned_labor_hours: number | null;
  actual_labor_hours: number;
  planned_labor_cost_ex_vat: number;
  actual_labor_cost_ex_vat: number;
  planned_material_cost_ex_vat: number;
  actual_material_cost_ex_vat: number;
  planned_equipment_cost_ex_vat: number;
  actual_equipment_cost_ex_vat: number;
  planned_travel_cost_ex_vat: number;
  actual_travel_cost_ex_vat: number;
  planned_other_cost_ex_vat: number;
  actual_other_cost_ex_vat: number;
  planned_contingency_cost_ex_vat: number;
  planned_total_cost_ex_vat: number;
  actual_total_cost_ex_vat: number;
  cost_variance_ex_vat: number;
  cost_variance_rate: number | null;
  planned_yield_per_labor_hour: number | null;
  observed_yield_per_labor_hour: number | null;
  missing_inputs: string[];
  notes: string | null;
  created_at: string;
  decided_at: string | null;
  decision_notes: string | null;
  published_profile_id: string | null;
};
type MissionCostingReviewLine = {
  id: string;
  review_id: string;
  component_type: "labor" | "material" | "equipment" | "travel" | "other" | "contingency";
  label: string;
  planned_amount_ex_vat: number;
  actual_amount_ex_vat: number;
  variance_ex_vat: number;
  source_kind: string;
  source_ids: string[];
  completeness: "complete" | "manual" | "missing";
  note: string | null;
  position: number;
};
type CostingYieldRecommendation = {
  id: string;
  catalog_item_id: string;
  base_profile_id: string;
  recommendation_number: number;
  status: "ready" | "needs_review" | "approved" | "rejected";
  output_unit: ServiceCostingProfile["output_unit"];
  minimum_sample_size: number;
  outlier_tolerance_percent: number;
  candidate_review_count: number;
  included_review_count: number;
  excluded_review_count: number;
  current_yield_per_labor_hour: number | null;
  median_yield_per_labor_hour: number | null;
  recommended_yield_per_labor_hour: number | null;
  total_output_quantity: number;
  total_labor_hours: number;
  included_review_ids: string[];
  excluded_review_ids: string[];
  explanation: Record<string, unknown>;
  created_at: string;
  decided_at: string | null;
  decision_notes: string | null;
  published_profile_id: string | null;
};
type BillingTreatment = "standard" | "recharge" | "disbursement";
type ServiceBillingRule = {
  id: string;
  client_service_id: string | null;
  property_service_id: string | null;
  billing_treatment: BillingTreatment;
  supplier_unit_cost_ex_vat: number | null;
  target_mode: "margin" | "markup" | null;
  target_rate: number | null;
  disbursement_principal_name: string | null;
  disbursement_mandate_reference: string | null;
  fiscal_warning_acknowledged_at: string | null;
};
type BillingSnapshot = {
  id: string;
  billing_treatment: BillingTreatment;
  supplier_unit_cost_ex_vat: number | null;
  supplier_total_cost_ex_vat: number | null;
  client_price_ex_vat: number;
  target_mode_snapshot: "margin" | "markup" | null;
  target_rate_snapshot: number | null;
  minimum_client_price_ex_vat: number | null;
  projected_margin_rate: number | null;
  projected_markup_rate: number | null;
  profitability_alert: boolean;
  disbursement_principal_name: string | null;
  disbursement_mandate_reference: string | null;
  frozen_at: string;
};
type MissionBillingSnapshot = BillingSnapshot & { mission_id: string };
type CommercialLineBillingSnapshot = BillingSnapshot & {
  quote_line_id: string | null;
  invoice_line_id: string | null;
};
type MissionRequest = {
  id: string;
  client_id: string;
  property_id?: string | null;
  service_id: string | null;
  property_service_id?: string | null;
  requested_service_keys?: string[];
  desired_starts_at: string;
  desired_ends_at: string;
  location: string;
  client_notes: string | null;
  status: string;
  created_at: string;
};
type MissionChecklistOverview = {
  available: boolean;
  mission_id: string;
  run_id?: string;
  name?: string;
  version?: number;
  instructions?: string | null;
  status?: string;
  pending_exception_count?: number;
  missing?: Array<Record<string, unknown>>;
  repeatable_zones?: Array<{
    zone_key: string;
    zone_label: string;
    instance_count: number;
  }>;
  items?: Array<{
    id: string;
    label: string;
    is_required: boolean;
    minimum_photo_count: number;
    is_completed: boolean;
    zone_key?: string;
    zone_label?: string;
    zone_display_label?: string;
    zone_repeatable?: boolean;
    zone_instance_index?: number;
    proof_exception?: {
      id: string;
      reason: string;
      status: "pending_review" | "accepted" | "rejected" | "resolved_by_evidence";
      requested_at: string;
      review_note?: string | null;
      reviewed_at?: string | null;
    } | null;
    evidence?: Array<{ id: string }>;
  }>;
};
type MissionChecklistModelOption = {
  id: string;
  name: string;
  description: string | null;
};
type MissionReport = {
  id: string;
  mission_id: string;
  worker_id: string;
  result: string;
  actual_hours: number;
  observations: string | null;
  client_note: string | null;
  status: string;
  submitted_at: string;
  route_team_id?: string | null;
  submitted_by_worker_id?: string | null;
  team_report_batch_id?: string | null;
};
type MissionReportSubmission = {
  id: string;
  mission_id: string;
  worker_id: string;
  submitted_by_worker_id: string;
  route_team_id: string | null;
  team_report_batch_id: string | null;
  source: "individual" | "team_leader";
  result: string;
  actual_hours: number;
  observations: string | null;
  client_note: string | null;
  submitted_at: string;
};
type WorkerHistoryEntry = {
  id: string;
  date: string;
  category: string;
  label: string;
  title: string;
  detail: string;
  missionId?: string;
};
type MissionReportActivityEntry = {
  id: string;
  report_id: string;
  mission_id: string;
  worker_id: string;
  activity_key: string;
  activity_label: string;
  minutes: number;
  notes: string | null;
  created_at: string;
};
type Invoice = {
  id: string;
  client_id: string;
  quote_id?: string | null;
  number: string | null;
  billing_period: string | null;
  grouping?: string | null;
  document_kind?: "standard" | "deposit" | "progress" | "final" | "credit";
  parent_invoice_id?: string | null;
  progress_percent?: number | null;
  project_total_ex_vat?: number | null;
  credit_reason?: string | null;
  status: string;
  issue_on: string | null;
  due_on: string | null;
  total_ex_vat: number;
  total_vat: number;
  total_inc_vat: number;
  amount_paid?: number;
  last_payment_on?: string | null;
  next_reminder_on?: string | null;
  reminder_count?: number;
  henrri_id?: string | null;
  henrri_pdf_url?: string | null;
  payment_terms_mode?: PaymentTermsMode | null;
  payment_terms_days?: number | null;
  collection_track?:
    | "expected"
    | "friendly"
    | "formal_notice"
    | "legal_procedure"
    | "disputed"
    | "payment_plan";
  collection_notes?: string | null;
  collection_next_review_on?: string | null;
  collection_updated_at?: string | null;
  collection_updated_by?: string | null;
};
type InvoiceLine = {
  id: string;
  invoice_id: string;
  mission_id: string | null;
  service_id: string | null;
  description: string;
  quantity: number;
  unit_price_ex_vat: number;
  vat_rate: number;
  position: number;
  turnover_declaration_id?: string | null;
  mission_stock_usage_id?: string | null;
  property_service_id?: string | null;
  source_invoice_line_id?: string | null;
};
type ClientTurnoverDeclaration = {
  id: string;
  client_id: string;
  period: string;
  turnover_amount: number;
  turnover_basis: "ex_vat" | "inc_vat";
  commission_rate: number;
  commission_amount_ex_vat: number;
  vat_rate: number;
  label: string;
  source_reference: string | null;
  notes: string | null;
  status: "draft" | "ready" | "invoiced" | "cancelled";
  invoice_id: string | null;
  created_at: string;
};
type ReferralPartner = {
  id: string;
  display_name: string;
  company_name: string | null;
  default_commission_rate: number;
  default_pricing_protection_rate: number;
  active: boolean;
};
type InvoiceReferralCommission = {
  id: string;
  invoice_id: string;
  client_id: string;
  referral_partner_id: string;
  partner_name_snapshot: string;
  commission_rate: number;
  commission_base_ex_vat: number;
  commission_amount_ex_vat: number;
  status:
    | "estimated"
    | "pending_approval"
    | "approved"
    | "paid"
    | "waived"
    | "cancelled";
  approved_by: string | null;
  approved_at: string | null;
  paid_by: string | null;
  paid_at: string | null;
  payment_reference: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
};
type Quote = {
  id: string;
  client_id: string;
  mission_id?: string | null;
  number: string;
  issued_on: string;
  valid_until: string | null;
  status: string;
  discount_rate?: number;
  deposit_rate?: number;
  total_ex_vat: number;
  total_vat: number;
  total_inc_vat: number;
  notes: string | null;
  payment_terms_mode?: PaymentTermsMode | null;
  payment_terms_days?: number | null;
};
type QuoteLine = {
  id: string;
  quote_id: string;
  service_id: string | null;
  description: string;
  quantity: number;
  unit_price_ex_vat: number;
  vat_rate: number;
  position: number;
  property_service_id?: string | null;
};
type CommercialAttachment = {
  id: string;
  quote_id: string | null;
  invoice_id: string | null;
  document_id: string;
  label: string | null;
  created_at: string;
};
type CommercialEvent = {
  id: string;
  quote_id: string | null;
  invoice_id: string | null;
  event_type: string;
  status_from: string | null;
  status_to: string | null;
  note: string | null;
  created_at: string;
};
type InvoicePayment = {
  id: string;
  invoice_id: string;
  received_on: string;
  amount: number;
  method: string;
  reference: string | null;
  status: string;
  henrri_payment_id: string | null;
  notes: string | null;
  created_at: string;
};
type BillingPaymentAllocation = {
  external_document_id?: number | string;
  invoice_id: string;
  invoice_number?: string | null;
  suggested_amount?: number | null;
};
type BillingPaymentCandidate = {
  id: string;
  provider: string;
  environment: "sandbox" | "production";
  external_payment_id: string;
  amount: number;
  received_at: string;
  match_status: "exact" | "multiple" | "unmatched";
  suggested_allocations: BillingPaymentAllocation[];
  review_status: "pending" | "confirmed" | "ignored" | "error";
  confirmed_allocations: Array<{ invoice_id: string; amount: number }> | null;
  resolution_note: string | null;
  reviewed_at: string | null;
  last_error: string | null;
  created_at: string;
};
type InvoiceReminder = {
  id: string;
  invoice_id: string;
  channel: string;
  status: string;
  subject: string | null;
  body: string | null;
  scheduled_on: string | null;
  sent_at: string | null;
  created_at: string;
  milestone_days_after_due?: 1 | 7 | 15 | null;
};
type Expense = {
  id: string;
  owner_profile_id: string;
  mission_id: string | null;
  client_id: string | null;
  spent_on: string;
  category: string;
  description: string;
  merchant: string | null;
  payment_method: string;
  amount_inc_vat: number;
  amount_ex_vat: number;
  vat_rate: number;
  vat_amount: number;
  receipt_path?: string | null;
  status: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  reimbursement_status?: "not_applicable" | "pending" | "paid";
  reimbursed_on?: string | null;
  reimbursement_reference?: string | null;
  stock_item_id?: string | null;
  stock_quantity?: number | null;
  stock_unit_cost?: number | null;
};
type InventoryItem = MaterialEstimateItem & {
  id: string;
  name: string;
  category: string | null;
  sku: string | null;
  unit: string;
  current_quantity: number;
  minimum_quantity: number;
  average_unit_cost: number;
  preferred_supplier: string | null;
  item_kind?: "stock" | "kit";
  supplier_id?: string | null;
  supplier_product_url?: string | null;
  sale_enabled?: boolean;
  sale_price_ex_vat?: number | null;
  sale_vat_rate?: number;
  sale_label?: string | null;
  active: boolean;
};
type InventorySupplier = {
  id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  notes: string | null;
  active: boolean;
};
type InventoryKitComponent = {
  id: string;
  kit_item_id: string;
  component_item_id: string;
  quantity: number;
};
type MissionStockUsage = {
  id: string;
  mission_id: string;
  report_id: string;
  worker_id: string;
  item_id: string;
  quantity: number;
  billable: boolean;
  unit_price_ex_vat: number | null;
  vat_rate: number;
  billing_status: string;
  invoice_line_id: string | null;
  notes: string | null;
  billing_notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at: string;
};
type InventoryMovement = {
  id: string;
  item_id: string;
  movement_type: string;
  quantity: number;
  unit_cost: number | null;
  occurred_on: string;
  expense_report_id: string | null;
  mission_id: string | null;
  notes: string | null;
};
type DirectorCompensation = {
  id: string;
  profile_id: string | null;
  leader_id: string;
  period: string;
  net_salary: number;
  social_contributions: number;
  other_costs: number;
  dividends: number;
  status: "planned" | "approved" | "paid";
  paid_on: string | null;
  notes: string | null;
};
type OrganizationLeader = {
  id: string;
  profile_id: string | null;
  worker_id: string | null;
  first_name: string | null;
  last_name: string;
  title: string;
  email: string | null;
  phone: string | null;
  starts_on: string | null;
  ends_on: string | null;
  notes: string | null;
  active: boolean;
};
type DirectorTimeEntry = {
  id: string;
  leader_id: string;
  work_date: string;
  category: "commercial_networking" | "operations" | "management" | "administration_finance" | "team" | "other";
  minutes: number;
  note: string;
  created_by: string;
  created_at: string;
};
type ProfileChangeRequest = {
  id: string;
  requester_profile_id: string;
  target_type: "client" | "worker";
  target_id: string;
  profile_changes: Record<string, unknown>;
  target_changes: Record<string, unknown>;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_notes: string | null;
  created_at: string;
};
type DashboardWidgetKey =
  | "monthly_revenue"
  | "monthly_profit"
  | "monthly_costs"
  | "annual_revenue"
  | "annual_profit"
  | "annual_costs"
  | "collected"
  | "bank_balance";
type DashboardPreferences = Partial<Record<DashboardWidgetKey, boolean>> & {
  order?: DashboardWidgetKey[];
};
type AppearanceMode = "light" | "dark";
type ScoringPreferences = {
  margin_weight?: number;
  revenue_weight?: number;
  payment_weight?: number;
  recurrence_weight?: number;
  travel_weight?: number;
  admin_weight?: number;
  incident_weight?: number;
  max_round_trip_minutes?: number;
  max_monthly_admin_minutes?: number;
};
type CashPlanMonthAdjustment = {
  season?: "low" | "normal" | "high";
  additional_receipts?: number;
  planned_hiring_cost?: number;
  planned_investment?: number;
  other_outflows?: number;
  note?: string;
};
type CashPlanSettings = {
  baseline_monthly_receipts?: number | null;
  safety_months?: number;
  low_season_reserve?: number;
  significant_spend_threshold?: number;
  high_season_factor?: number;
  low_season_factor?: number;
  monthly_adjustments?: Record<string, CashPlanMonthAdjustment>;
  notes?: string;
};
type Organization = {
  id: string;
  name: string;
  legal_name?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  iban?: string | null;
  bic?: string | null;
  bank_name?: string | null;
  representative_first_name?: string | null;
  representative_last_name?: string | null;
  representative_title?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  current_account_balance?: number | null;
  current_account_updated_at?: string | null;
  dashboard_preferences?: DashboardPreferences;
  default_vat_rate?: number;
  target_margin_rate?: number;
  profitability_target_mode?: "margin" | "markup";
  profitability_target_rate?: number;
  fiscal_year_start_month?: number;
  route_buffer_minutes?: number;
  mileage_rate_per_km?: number | null;
  scoring_preferences?: ScoringPreferences;
  cash_plan_settings?: CashPlanSettings;
  default_invoice_due_days?: number;
  default_payment_terms_mode?: PaymentTermsMode;
  invoice_reminder_days_after_due?: number;
  document_alert_days?: number;
  contract_alert_days?: number;
  low_stock_alerts_enabled?: boolean;
  default_billing_emission?: "immediate" | "month_end" | "manual";
  default_billing_grouping?: "grouped" | "individual";
  use_preferred_workers?: boolean;
  prioritize_available_employees?: boolean;
  mileage_return_to_headquarters?: boolean;
};

const applicationProfileSelect =
  "id,role,display_name,organization_id,email,phone,calendar_enabled,permission_set_id,access_status,dashboard_preferences,is_primary_admin";

function isMissingContextRpc(message: string) {
  return /get_my_application_context|list_my_organization_contexts|schema cache|could not find the function|does not exist/i.test(
    message,
  );
}

async function loadApplicationProfile(session: Session): Promise<{
  profile: Profile | null;
  error: string;
}> {
  if (!isShowcaseMode) {
    const context = await supabase!.rpc("get_my_application_context");
    if (context.data && typeof context.data === "object" && !Array.isArray(context.data)) {
      const row = context.data as unknown as Profile;
      return {
        profile: { ...row, email: row.email || session.user.email },
        error: "",
      };
    }
    if (context.error && !isMissingContextRpc(context.error.message)) {
      return { profile: null, error: context.error.message };
    }
  }
  const legacy = await supabase!
    .from("profiles")
    .select(applicationProfileSelect)
    .eq("id", session.user.id)
    .maybeSingle();
  return {
    profile: legacy.data
      ? ({ ...legacy.data, email: legacy.data.email || session.user.email } as Profile)
      : null,
    error: legacy.error?.message || "",
  };
}

async function loadOrganizationContexts(
  profile: Profile,
): Promise<OrganizationContext[]> {
  if (!isShowcaseMode) {
    const result = await supabase!.rpc("list_my_organization_contexts");
    if (!result.error && Array.isArray(result.data)) {
      return result.data as OrganizationContext[];
    }
    if (result.error && !isMissingContextRpc(result.error.message)) return [];
  }
  const current = await supabase!
    .from("organizations")
    .select("id,name")
    .eq("id", profile.organization_id)
    .maybeSingle();
  return [
    {
      organization_id: profile.organization_id,
      organization_name: current.data?.name || "Société active",
      role: profile.role,
      status: "active",
      permission_set_id: profile.permission_set_id || null,
      is_primary_admin: Boolean(profile.is_primary_admin),
      is_active: true,
    },
  ];
}
type CashBalance = {
  id: string;
  recorded_at: string;
  balance: number;
  previous_balance: number | null;
  difference: number | null;
  known_receipts: number | null;
  known_outflows: number | null;
  expected_net_change: number | null;
  unexplained_difference: number | null;
  reconciliation_from: string | null;
  notes: string | null;
};
type FixedCharge = {
  id: string;
  name: string;
  vendor: string | null;
  category: string;
  amount_ex_vat: number;
  vat_rate: number;
  frequency: "weekly" | "monthly" | "quarterly" | "annual";
  starts_on: string;
  ends_on: string | null;
  next_due_on: string | null;
  payment_method: string | null;
  notes: string | null;
  active: boolean;
};
type Availability = {
  id: string;
  worker_id: string;
  starts_at: string;
  ends_at: string;
  availability: "available" | "unavailable";
  notes: string | null;
};
type Requirement = {
  id: string;
  worker_id: string;
  kind: string;
  label: string;
  due_on: string | null;
  status: string;
  notes: string | null;
};
type WorkerContract = {
  id: string;
  worker_id: string;
  title: string;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  document_id: string | null;
  template_id?: string | null;
  template_version?: number | null;
  sent_at?: string | null;
  signed_at?: string | null;
  next_reminder_at?: string | null;
  rendered_content?: string | null;
  generated_at?: string | null;
  notes: string | null;
};
type WorkerCancellationRule = {
  id: string;
  worker_id: string;
  contract_id: string;
  enabled: boolean;
  notice_hours: number;
  penalty_mode: "none" | "fixed" | "percentage_of_assignment";
  penalty_value: number;
  clause_reference: string | null;
};
type WorkerCancellationNotice = {
  id: string;
  mission_id: string;
  assignment_id: string;
  worker_id: string;
  contract_id: string | null;
  change_event_id: string;
  reason: string;
  evidence_document_id: string | null;
  submitted_at: string;
  notice_hours: number;
  rule_snapshot: Record<string, unknown>;
  proposed_penalty_ex_vat: number;
  approved_penalty_ex_vat: number | null;
  financial_adjustment_id: string | null;
  status: "submitted" | "approved" | "rejected" | "cancelled";
  review_notes: string | null;
  reviewed_at: string | null;
};
type ContractTemplate = {
  id: string;
  audience: "client" | "contractor" | "employee";
  name: string;
  version: number;
  content: string;
  required_document_kinds: string[];
  renewal_notice_days: number;
  active: boolean;
};
type ClientContract = {
  id: string;
  client_id: string;
  template_id: string | null;
  title: string;
  template_version: number | null;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  document_id: string | null;
  sent_at: string | null;
  signed_at: string | null;
  next_reminder_at: string | null;
  rendered_content?: string | null;
  generated_at?: string | null;
  notes: string | null;
  contract_kind?: "standard" | "recurring_professional" | "recurring_municipality";
  offer_name?: string | null;
  service_scope?: string | null;
  billing_frequency?: "monthly" | "quarterly" | "annual" | null;
  billing_amount_ex_vat?: number | null;
  vat_rate?: number | null;
  payment_terms_days?: number | null;
  quote_id?: string | null;
  payment_provider?: "none" | "henrri" | "external_provider";
  payment_instruction?: string | null;
  prepared_at?: string | null;
};
type TenantSignatureEnvelope = {
  id: string;
  source_type: "client_contract" | "worker_contract";
  source_id: string;
  signer_profile_id: string | null;
  signer_name: string;
  signer_email: string;
  provider: string | null;
  status: string;
  requested_at: string | null;
  viewed_at: string | null;
  signed_at: string | null;
  last_error: string | null;
};
type OrganizationUserInvitation = {
  id:string; target_type:"client"|"worker"|"coach"; target_id:string;
  role:"admin"|"client"|"employee"|"contractor"; email:string; display_name:string;
  status:"pending"|"sent"|"accepted"|"expired"|"revoked"|"failed";
  expires_at:string; accepted_at:string|null; last_error:string|null;
  sent_at?:string|null; created_at?:string;
  source_quote_id?:string|null;
};
type ClientRequirement = {
  id: string;
  client_id: string;
  contract_id: string | null;
  kind: string;
  label: string;
  due_on: string | null;
  status: string;
  notes: string | null;
};
type ClientProperty = {
  id: string;
  client_id: string;
  property_type_id?: string | null;
  name: string;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  contact_name?: string | null;
  access_notes?: string | null;
  active: boolean;
};
type ClientPropertyType = {
  id: string;
  client_id: string;
  name: string;
  site_code?: string | null;
  site_label?: string | null;
  base_service_name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours?: number | null;
  active: boolean;
};
type PropertyService = {
  id: string;
  property_id: string;
  catalog_item_id?: string | null;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours?: number | null;
  notes?: string | null;
  active: boolean;
};
type PreferredWorker = {
  id: string;
  client_id: string;
  property_id: string | null;
  worker_id: string;
  priority: number;
  active: boolean;
};
type ServiceProposal = {
  id: string;
  client_id: string;
  property_id: string | null;
  mission_request_id: string | null;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours: number | null;
  notes: string | null;
  status: string;
  client_response_reason?: string | null;
  revision_requested?: boolean;
  responded_at: string | null;
  created_service_id: string | null;
  created_property_service_id: string | null;
  created_at: string;
};
type ClientPropertyRequest = {
  id: string;
  client_id: string;
  requested_by: string;
  request_kind?: "create" | "update" | "archive";
  target_property_id?: string | null;
  name: string;
  address: string;
  contact_name: string | null;
  access_notes: string | null;
  requested_reason?: string | null;
  previous_snapshot?: Record<string, unknown> | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_notes: string | null;
  created_property_id: string | null;
  created_at: string;
};
type PropertyServiceQuoteRequest = {
  id: string;
  client_id: string;
  property_id: string;
  requested_by: string;
  service_name: string;
  requested_description: string;
  client_notes: string | null;
  status: string;
  quote_id: string | null;
  created_property_service_id: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_response: string | null;
  client_response_kind: "accepted" | "revision_requested" | "rejected" | null;
  client_response_reason: string | null;
  responded_by: string | null;
  responded_at: string | null;
  created_at: string;
};
type PropertyServiceQuoteTerms = {
  request_id: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours: number | null;
  admin_notes: string | null;
};
type ReportAttachment = {
  id: string;
  report_id: string;
  worker_id: string;
  storage_path: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  client_visible: boolean;
  created_at: string;
};
type DailyRouteTeam = {
  id: string;
  work_date: string;
  name: string;
  notes: string | null;
  leader_worker_id?: string | null;
};
type DailyRouteTeamMember = {
  team_id: string;
  work_date: string;
  worker_id: string;
};
type RouteTeamTemplate = {
  id: string;
  name: string;
  notes: string | null;
  leader_worker_id: string | null;
  active: boolean;
};
type RouteTeamTemplateMember = {
  template_id: string;
  worker_id: string;
};
type RouteTeamContext = {
  team_id: string;
  work_date: string;
  leader_worker_id: string | null;
  worker_id: string;
  display_name: string;
  mission_id: string | null;
  assigned_hours: number | null;
  assignment_status: string | null;
};
type MissionRecurrence = {
  id: string;
  client_id: string;
  property_id: string | null;
  service_id: string | null;
  property_service_id: string | null;
  preferred_worker_id: string | null;
  name: string;
  frequency: "daily" | "weekly" | "biweekly" | "monthly";
  starts_on: string;
  ends_on: string | null;
  start_time: string;
  duration_minutes: number;
  location: string;
  billing_mode: string;
  billing_rate: number;
  billing_quantity: number;
  amount_ex_vat: number;
  vat_rate: number;
  notes: string | null;
  active: boolean;
  next_occurrence_on: string;
  generated_until: string | null;
};
type OrgProfile = {
  id: string;
  role: Role;
  display_name: string;
  email: string | null;
  phone: string | null;
  permission_set_id?: string | null;
  access_status?: "invited" | "active" | "suspended";
  is_primary_admin?: boolean;
  process_notification_preferences?: ProcessNotificationPreferenceSettings;
};
type ProcessNotificationChannelSettings = {
  internal: boolean;
  push: boolean;
  email: boolean;
  sms: boolean;
};
type ProcessNotificationPreferenceSettings = {
  information: ProcessNotificationChannelSettings;
  alert: ProcessNotificationChannelSettings;
  quiet_hours: {
    enabled: boolean;
    start: string;
    end: string;
  };
};
type PermissionSet = {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  active: boolean;
  access_mode?: "standard" | "coach_read_only";
};
type AccessAuditLog = {
  id: number;
  actor_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
};
type SupportAccessGrant = {
  id: string;
  platform_admin_user_id: string;
  status: "requested" | "approved" | "rejected" | "revoked" | "expired";
  reason: string;
  access_scope: string[];
  expires_at: string;
  approved_at: string | null;
  revoked_at: string | null;
  created_at: string;
};
type SubscriptionPlanSummary = {
  id: string;
  name: string;
  monthly_price_ex_vat: number | null;
  annual_price_ex_vat: number | null;
  currency: string;
  limits: Record<string, number>;
};
type SubscriptionUpgradeRequest = {
  id: string;
  organization_id: string;
  current_plan_id: string;
  recommended_plan_id: string | null;
  trigger_metric: "users" | "storage" | "manual";
  current_value: number;
  current_limit: number | null;
  status: "recommended" | "awaiting_signature" | "signed" | "rejected" | "expired" | "cancelled";
  client_response_notes: string | null;
  created_at: string;
};
type SubscriptionAmendmentSummary = {
  id: string;
  upgrade_request_id: string | null;
  new_plan_id: string;
  billing_cycle: "monthly" | "annual";
  status: "draft" | "awaiting_signature" | "signed" | "declined" | "cancelled";
  signed_at: string | null;
};
type OrganizationIntegration = {
  id: string;
  organization_id: string;
  provider: "henrri" | "bank" | "google_calendar" | "google_maps";
  enabled: boolean;
  environment: "sandbox" | "production";
  status: "not_configured" | "pending" | "connected" | "error" | "disabled";
  account_identifier: string | null;
  provider_name: string | null;
  settings: {
    import_customers?: boolean;
    import_items?: boolean;
    import_documents?: boolean;
    export_documents?: boolean;
    admin_calendar?: boolean;
    client_calendars?: boolean;
    worker_calendars?: boolean;
    sync_balance?: boolean;
    sync_transactions?: boolean;
    geocoding?: boolean;
    route_optimization?: boolean;
    travel_times?: boolean;
  };
  last_sync_at: string | null;
  last_error: string | null;
};
type IntegrationSyncCursor = {
  id: string;
  provider: OrganizationIntegration["provider"];
  resource:
    | "customers"
    | "items"
    | "documents"
    | "payments"
    | "calendar_events"
    | "transactions"
    | "geocoding"
    | "routes";
  min_external_id: number;
  last_started_at: string | null;
  last_success_at: string | null;
  status: "idle" | "running" | "success" | "error";
  last_error: string | null;
};
type CalendarConnection = {
  id: string;
  profile_id: string;
  status: "not_connected" | "pending" | "connected" | "error" | "disabled";
  calendar_id: string;
  calendar_name: string | null;
  google_account_email: string | null;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_error: string | null;
  connected_at: string | null;
};
type AppNotification = {
  id: string;
  recipient_profile_id: string;
  mission_id: string | null;
  kind: string;
  title: string;
  body: string | null;
  read_at: string | null;
  created_at: string;
};
type ProcessBusinessEvent = {
  id: string;
  event_type: string;
  occurred_at: string;
  actor_role: string;
  subject_type: string;
  subject_id: string;
  context: Record<string, unknown>;
  correlation_id: string | null;
};
type ProcessRun = {
  id: string;
  event_id: string;
  process_key_snapshot: string;
  process_version_snapshot: number;
  status: "planned" | "running" | "completed" | "failed" | "cancelled";
  explanation: Record<string, unknown>;
  planned_at: string;
  completed_at: string | null;
};
type ProcessRequiredAction = {
  id: string;
  process_run_id: string;
  event_id: string;
  step_key: string;
  title: string;
  description: string;
  assignee_role: string | null;
  assignee_profile_id: string | null;
  status: "open" | "in_progress" | "completed" | "cancelled";
  due_at: string;
  reminder_schedule: string[];
  escalation_at: string | null;
  requires_human_decision: boolean;
  allowed_decisions: string[];
  direct_link: string;
  decision: string | null;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
};
type ProcessNotificationIntent = {
  id: string;
  process_run_id: string;
  event_id: string;
  step_key: string;
  level: "information" | "alert";
  title: string;
  body: string;
  recipient_role: string | null;
  recipient_profile_id: string | null;
  channel_intents: Array<"internal" | "push" | "email" | "sms">;
  status: "planned" | "dispatched" | "cancelled" | "failed";
  dispatched_at: string | null;
  last_error: string | null;
  created_at: string;
};
type ProcessNotificationDelivery = {
  id: string;
  notification_intent_id: string;
  process_run_id: string;
  event_id: string;
  recipient_profile_id: string;
  channel: "internal" | "push" | "email" | "sms";
  status:
    | "suppressed"
    | "queued"
    | "leased"
    | "retry_scheduled"
    | "delivered"
    | "dead_letter"
    | "cancelled";
  suppression_reason: string | null;
  scheduled_at: string;
  next_attempt_at: string;
  lease_owner: string | null;
  lease_until: string | null;
  attempt_count: number;
  max_attempts: number;
  provider: string | null;
  external_message_id: string | null;
  destination_fingerprint: string | null;
  last_error: string | null;
  delivered_at: string | null;
  created_at: string;
};
type ProcessDefinitionRow = {
  id: string;
  organization_id: string | null;
  process_key: string;
  name: string;
  version: number;
  status: "draft" | "active" | "retired";
  trigger_event_types: string[];
  trigger_conditions: Array<{
    field: string;
    operator: "equals" | "not_equals" | "in" | "exists" | "greater_than" | "less_than";
    value?: unknown;
  }>;
  steps: Array<Record<string, unknown>>;
  published_at: string | null;
  effective_from: string | null;
  effective_until: string | null;
  source_definition_id: string | null;
  draft_notes: string | null;
  created_at: string;
  updated_at: string;
};
type ProcessDefinitionPublicationReview = {
  id: string;
  organization_id: string;
  definition_id: string;
  process_key_snapshot: string;
  version_snapshot: number;
  definition_fingerprint: string;
  definition_snapshot: Record<string, unknown>;
  simulation_evidence: {
    event?: BusinessEventEnvelope;
    active?: ProcessSimulationResult | null;
    draft?: ProcessSimulationResult;
  };
  request_note: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  requested_by: string;
  requested_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  activated_at: string | null;
};
type ProcessJournalEntry = {
  id: string;
  process_run_id: string | null;
  event_id: string;
  entry_type: string;
  message: string;
  payload: Record<string, unknown>;
  actor_profile_id: string | null;
  created_at: string;
};
type MissionChangeEvent = {
  id: string;
  mission_id: string;
  event_type: "modification" | "supplement" | "urgent" | "reschedule" | "cancellation" | "access_issue";
  status: "proposed" | "approved" | "applied" | "rejected" | "cancelled";
  reason: string;
  operational_notes: string | null;
  previous_snapshot: Record<string, unknown>;
  proposed_changes: Record<string, unknown>;
  applied_snapshot: Record<string, unknown> | null;
  client_amount_delta_ex_vat: number;
  supplier_amount_delta_ex_vat: number;
  requires_client_approval: boolean;
  client_approval_status: "not_required" | "pending" | "accepted" | "rejected";
  evidence_document_id: string | null;
  requested_by: string;
  reviewed_by: string | null;
  review_notes: string | null;
  reviewed_at: string | null;
  applied_at: string | null;
  created_at: string;
};
type MissionQualityClaim = import("./rich-portals").PortalMissionQualityClaim;
type MissionQualityClaimEvent = import("./rich-portals").PortalMissionQualityClaimEvent;
type MissionQualityClaimAttachment = import("./rich-portals").PortalMissionQualityClaimAttachment;
type WorkerQualityMetric = import("./rich-portals").PortalWorkerQualityMetric;
type GoogleReviewRequest = import("./rich-portals").PortalGoogleReviewRequest;
type ClientPortfolioReview = {
  id: string;
  organization_id: string;
  client_id: string;
  period_start: string;
  period_end: string;
  suggested_class: "A" | "B" | "C";
  decided_class: "A" | "B" | "C";
  decision: "develop" | "maintain" | "renegotiate" | "stop";
  causes: string[];
  metrics_snapshot: Record<string, string | number | null>;
  minimum_revenue_ex_vat: number | null;
  rationale: string;
  next_action: string;
  next_review_on: string;
  status: "active" | "superseded";
  reviewed_at: string;
};
type MissionQualityAnalytic = import("./rich-portals").PortalMissionQualityAnalytic;
type WorkerQualityControlReview = {
  id: string;
  mission_id: string;
  quality_claim_id: string | null;
  source_kind: "client_return" | "non_blocking_missing_information" | "mission_observation" | "manual_control";
  title: string;
  observation: string;
  missing_information: Array<{ label?: string; instructions?: string }>;
  information_purpose: string | null;
  penalty_eligible: boolean;
  proposed_penalty_amount_ex_vat: number | null;
  penalty_decision: "not_applicable" | "pending" | "applied" | "waived";
  decision_action: string | null;
  decision_note: string | null;
  financial_adjustment_id: string | null;
  decided_at: string | null;
  created_at: string;
};
type WorkerQualityControlDossier = {
  worker: { id: string; display_name: string; kind: "employee" | "contractor" };
  period: { from: string; to: string };
  summary: {
    mission_count: number;
    client_return_count: number;
    mission_with_missing_non_blocking_count: number;
    missing_non_blocking_item_count: number;
    penalty_pending_count: number;
    penalty_applied_count: number;
    penalty_waived_count: number;
  };
  missions: Array<{
    id: string;
    starts_at: string;
    location: string | null;
    status: string;
    workflow_status: string | null;
    billing_status: string | null;
    client_name: string | null;
    reports: Array<{ id: string; result: string; actual_hours: number; observations: string | null; client_note: string | null; status: string; submitted_at: string }>;
    claims: Array<{ id: string; request_kind: string; category: string; description: string; status: string; qualification: string | null; admin_notes: string | null; client_response: string | null; created_at: string }>;
    missing_non_blocking_count: number;
    missing_non_blocking_items: Array<{ label?: string; instructions?: string }>;
    controls: WorkerQualityControlReview[];
  }>;
};
type MissionFinancialAdjustment = {
  id: string;
  mission_id: string;
  change_event_id: string;
  assignment_id: string | null;
  worker_id: string | null;
  adjustment_kind: "client_supplement" | "supplier_surcharge" | "supplier_penalty" | "supplier_deduction";
  amount_ex_vat: number;
  vat_rate: number;
  reason: string;
  evidence_document_id: string | null;
  status: "proposed" | "approved" | "rejected" | "applied" | "cancelled";
  supplier_invoice_id: string | null;
  created_at: string;
};
type BillingProviderDocumentTransformation = {
  id: string;
  provider: string;
  environment: "sandbox" | "production";
  source_quote_id: string;
  target_invoice_id: string;
  source_external_id: string;
  target_external_id: string | null;
  status: "reserved" | "succeeded" | "needs_review";
  last_error: string | null;
  requested_at: string;
  completed_at: string | null;
};
type DeploymentCapability =
  | "advanced_routes"
  | "billing_cases"
  | "billing_case_governance"
  | "billing_case_invoicing"
  | "billing_case_operations"
  | "billing_payment_reconciliation"
  | "billing_treatments"
  | "henrri_documents"
  | "henrri_quote_conversion"
  | "guided_quote_costing"
  | "service_qualification"
  | "mission_costing_feedback"
  | "mission_activity_learning"
  | "mission_checklists"
  | "mission_quality_claims"
  | "google_review_requests"
  | "client_portfolio_reviews"
  | "director_time_allocation"
  | "recurring_client_contracts"
  | "mission_changes"
  | "process_actions"
  | "process_definition_editor"
  | "process_notification_executor"
  | "process_notification_preferences"
  | "quote_sourcing"
  | "receivables_collection_portfolio"
  | "service_catalog"
  | "supplier_invoicing"
  | "worker_company_dossier"
  | "worker_pricing_proposals"
  | "referral_commissions"
  | "mission_request_multi_services";
type DataState = {
  unavailableCapabilities?: DeploymentCapability[];
  clients: Client[];
  missions: Mission[];
  missionServiceLines?: MissionServiceLine[];
  assignments: Assignment[];
  workers: Worker[];
  times: TimeEntry[];
  leaves: Leave[];
  mileages: Mileage[];
  vehicles?: WorkerVehicle[];
  documents: DocumentRow[];
  supplierInvoices?: PortalSupplierInvoice[];
  supplierInvoiceMissions?: PortalSupplierInvoiceMission[];
  missionChangeEvents?: MissionChangeEvent[];
  missionQualityClaims?: MissionQualityClaim[];
  missionQualityClaimEvents?: MissionQualityClaimEvent[];
  missionQualityClaimAttachments?: MissionQualityClaimAttachment[];
  workerQualityMetrics?: WorkerQualityMetric[];
  googleReviewRequests?: GoogleReviewRequest[];
  clientPortfolioReviews?: ClientPortfolioReview[];
  missionFinancialAdjustments?: MissionFinancialAdjustment[];
  workerCancellationRules?: WorkerCancellationRule[];
  workerCancellationNotices?: WorkerCancellationNotice[];
  services: ClientService[];
  serviceCatalogItems?: ServiceCatalogItem[];
  serviceCatalogVersions?: ServiceCatalogVersion[];
  serviceQualificationQuestionnaires?: ServiceQualificationQuestionnaire[];
  serviceQualificationQuestions?: ServiceQualificationQuestion[];
  qualificationRuleEvidence?: QualificationRuleEvidence[];
  quoteQualificationRuns?: QuoteQualificationRun[];
  serviceCostingProfiles?: ServiceCostingProfile[];
  serviceCostingComponents?: ServiceCostingComponent[];
  quoteCostingRuns?: QuoteCostingRun[];
  quoteCostingRunLines?: QuoteCostingRunLine[];
  procurementRequirements?: ProcurementRequirement[];
  missionCostingReviews?: MissionCostingReview[];
  missionCostingReviewLines?: MissionCostingReviewLine[];
  costingYieldRecommendations?: CostingYieldRecommendation[];
  requests: MissionRequest[];
  reports: MissionReport[];
  missionReportSubmissions?: MissionReportSubmission[];
  missionReportActivityEntries?: MissionReportActivityEntry[];
  invoices: Invoice[];
  invoiceLines?: InvoiceLine[];
  quotes: Quote[];
  quoteLines?: QuoteLine[];
  commercialAttachments?: CommercialAttachment[];
  commercialEvents?: CommercialEvent[];
  invoicePayments?: InvoicePayment[];
  paymentReconciliationCandidates?: BillingPaymentCandidate[];
  billingCases?: BillingCaseSummary[];
  billingCaseLinks?: BillingCaseLink[];
  billingCaseProgress?: BillingCaseProgress[];
  billingCaseLedger?: BillingCaseLedgerEntry[];
  billingCaseAmendments?: BillingCaseAmendment[];
  billingCaseConsistencyReviews?: BillingCaseConsistencyReview[];
  billingCaseInvoicePreparations?: BillingCaseInvoicePreparation[];
  billingCaseOperationalSyncs?: BillingCaseOperationalSync[];
  invoiceReminders?: InvoiceReminder[];
  expenses: Expense[];
  organization: Organization | null;
  cashBalances: CashBalance[];
  fixedCharges?: FixedCharge[];
  workerRateCards?: WorkerRateCard[];
  workerCompanyProfiles?: WorkerCompanyProfile[];
  workerCompanyRepresentatives?: WorkerCompanyRepresentative[];
  workerCompanyProfileEvents?: WorkerCompanyProfileEvent[];
  workerCommercialProposals?: WorkerCommercialProposal[];
  quoteSourcingWorkflows?: QuoteSourcingWorkflow[];
  quoteSourcingOffers?: QuoteSourcingOffer[];
  quoteSourcingTasks?: QuoteSourcingTask[];
  serviceBillingRules?: ServiceBillingRule[];
  missionBillingSnapshots?: MissionBillingSnapshot[];
  commercialLineBillingSnapshots?: CommercialLineBillingSnapshot[];
  inventoryItems?: InventoryItem[];
  inventoryMovements?: InventoryMovement[];
  inventorySuppliers?: InventorySupplier[];
  inventoryKitComponents?: InventoryKitComponent[];
  missionStockUsages?: MissionStockUsage[];
  routeMileageCalculations?: RouteMileageCalculation[];
  routeMileageStops?: RouteMileageStop[];
  routeMileageLegs?: RouteMileageLeg[];
  directorCompensations?: DirectorCompensation[];
  directorTimeEntries?: DirectorTimeEntry[];
  organizationLeaders?: OrganizationLeader[];
  profileChangeRequests?: ProfileChangeRequest[];
  permissionSets?: PermissionSet[];
  integrations?: OrganizationIntegration[];
  integrationCursors?: IntegrationSyncCursor[];
  billingProviderDocumentTransformations?: BillingProviderDocumentTransformation[];
  integrationExternalLinks?: IntegrationExternalLink[];
  calendarConnections?: CalendarConnection[];
  notifications?: AppNotification[];
  processBusinessEvents?: ProcessBusinessEvent[];
  processDefinitions?: ProcessDefinitionRow[];
  processDefinitionPublicationReviews?: ProcessDefinitionPublicationReview[];
  processRuns?: ProcessRun[];
  processRequiredActions?: ProcessRequiredAction[];
  processNotificationIntents?: ProcessNotificationIntent[];
  processNotificationDeliveries?: ProcessNotificationDelivery[];
  processJournalEntries?: ProcessJournalEntry[];
  availability: Availability[];
  requirements: Requirement[];
  contracts: WorkerContract[];
  properties: ClientProperty[];
  propertyServices: PropertyService[];
  preferredWorkers: PreferredWorker[];
  serviceProposals?: ServiceProposal[];
  propertyRequests?: ClientPropertyRequest[];
  propertyServiceQuoteRequests?: PropertyServiceQuoteRequest[];
  propertyServiceQuoteTerms?: PropertyServiceQuoteTerms[];
  reportAttachments?: ReportAttachment[];
  dailyRouteTeams?: DailyRouteTeam[];
  dailyRouteTeamMembers?: DailyRouteTeamMember[];
  routeTeamTemplates?: RouteTeamTemplate[];
  routeTeamTemplateMembers?: RouteTeamTemplateMember[];
  routeTeamContext?: RouteTeamContext[];
  recurrences?: MissionRecurrence[];
  profiles?: OrgProfile[];
  contractTemplates?: ContractTemplate[];
  clientContracts?: ClientContract[];
  clientRequirements?: ClientRequirement[];
  organizationUserInvitations?: OrganizationUserInvitation[];
  accessAuditLogs?: AccessAuditLog[];
  supportAccessGrants?: SupportAccessGrant[];
  subscriptionPlans?: SubscriptionPlanSummary[];
  subscriptionUpgradeRequests?: SubscriptionUpgradeRequest[];
  subscriptionAmendments?: SubscriptionAmendmentSummary[];
  signatureEnvelopes?: TenantSignatureEnvelope[];
  turnoverDeclarations?: ClientTurnoverDeclaration[];
  referralPartners?: ReferralPartner[];
  invoiceReferralCommissions?: InvoiceReferralCommission[];
};
const emptyData: DataState = {
  unavailableCapabilities: [],
  clients: [],
  missions: [],
  missionServiceLines: [],
  assignments: [],
  workers: [],
  times: [],
  leaves: [],
  mileages: [],
  vehicles: [],
  documents: [],
  supplierInvoices: [],
  supplierInvoiceMissions: [],
  missionChangeEvents: [],
  missionQualityClaims: [],
  missionQualityClaimEvents: [],
  missionQualityClaimAttachments: [],
  workerQualityMetrics: [],
  googleReviewRequests: [],
  clientPortfolioReviews: [],
  missionFinancialAdjustments: [],
  workerCancellationRules: [],
  workerCancellationNotices: [],
  services: [],
  serviceCatalogItems: [],
  serviceCatalogVersions: [],
  serviceQualificationQuestionnaires: [],
  serviceQualificationQuestions: [],
  qualificationRuleEvidence: [],
  quoteQualificationRuns: [],
  serviceCostingProfiles: [],
  serviceCostingComponents: [],
  quoteCostingRuns: [],
  quoteCostingRunLines: [],
  procurementRequirements: [],
  missionCostingReviews: [],
  missionCostingReviewLines: [],
  costingYieldRecommendations: [],
  requests: [],
  reports: [],
  invoices: [],
  invoiceLines: [],
  quotes: [],
  quoteLines: [],
  commercialAttachments: [],
  commercialEvents: [],
  invoicePayments: [],
  billingCases: [],
  billingCaseLinks: [],
  billingCaseProgress: [],
  billingCaseLedger: [],
  billingCaseAmendments: [],
  billingCaseConsistencyReviews: [],
  billingCaseInvoicePreparations: [],
  billingCaseOperationalSyncs: [],
  invoiceReminders: [],
  expenses: [],
  organization: null,
  cashBalances: [],
  fixedCharges: [],
  workerRateCards: [],
  workerCompanyProfiles: [],
  workerCompanyRepresentatives: [],
  workerCompanyProfileEvents: [],
  workerCommercialProposals: [],
  quoteSourcingWorkflows: [],
  quoteSourcingOffers: [],
  quoteSourcingTasks: [],
  serviceBillingRules: [],
  missionBillingSnapshots: [],
  commercialLineBillingSnapshots: [],
  inventoryItems: [],
  inventoryMovements: [],
  inventorySuppliers: [],
  inventoryKitComponents: [],
  missionStockUsages: [],
  routeMileageCalculations: [],
  routeMileageStops: [],
  routeMileageLegs: [],
  directorCompensations: [],
  directorTimeEntries: [],
  organizationLeaders: [],
  profileChangeRequests: [],
  permissionSets: [],
  integrations: [],
  integrationCursors: [],
  billingProviderDocumentTransformations: [],
  calendarConnections: [],
  notifications: [],
  processBusinessEvents: [],
  processDefinitions: [],
  processDefinitionPublicationReviews: [],
  processRuns: [],
  processRequiredActions: [],
  processNotificationIntents: [],
  processNotificationDeliveries: [],
  processJournalEntries: [],
  availability: [],
  requirements: [],
  contracts: [],
  properties: [],
  propertyServices: [],
  preferredWorkers: [],
  serviceProposals: [],
  propertyRequests: [],
  propertyServiceQuoteRequests: [],
  propertyServiceQuoteTerms: [],
  reportAttachments: [],
  dailyRouteTeams: [],
  dailyRouteTeamMembers: [],
  routeTeamTemplates: [],
  routeTeamTemplateMembers: [],
  recurrences: [],
  profiles: [],
  contractTemplates: [],
  clientContracts: [],
  clientRequirements: [],
  organizationUserInvitations: [],
  accessAuditLogs: [],
  supportAccessGrants: [],
  subscriptionPlans: [],
  subscriptionUpgradeRequests: [],
  subscriptionAmendments: [],
  turnoverDeclarations: [],
  referralPartners: [],
  invoiceReferralCommissions: [],
};
const deploymentCapabilityLabels: Record<DeploymentCapability, string> = {
  advanced_routes: "tournées avancées, modèles d’équipe et calculs kilométriques",
  billing_cases: "dossiers de facturation et chantiers 360°",
  billing_case_governance: "avenants et contrôles de cohérence des dossiers",
  billing_case_invoicing: "situations, soldes et avoirs contrôlés des dossiers",
  billing_case_operations: "coûts opérationnels validés des dossiers",
  billing_payment_reconciliation: "rapprochement contrôlé des règlements fournisseur",
  billing_treatments: "traitements comptables et instantanés de rentabilité",
  henrri_documents: "PDF et Factur-X privés issus de Henrri",
  henrri_quote_conversion: "finalisation Sandbox et transformation devis → facture brouillon",
  mission_activity_learning: "répartition du temps réel par étape de mission",
  mission_request_multi_services: "demandes client composées de plusieurs prestations",
  mission_changes: "changements, suppléments et annulations financières de mission",
  process_actions: "centre des actions requises et décisions humaines",
  process_definition_editor: "éditeur contrôlé des brouillons de processus",
  process_notification_executor: "file d’exécution multicanale des processus",
  process_notification_preferences: "préférences personnelles de notification des processus",
  quote_sourcing: "circuit de chiffrage sous-traitant → ARD → client",
  receivables_collection_portfolio:
    "portefeuille de créances et relances J+1/J+7/J+15",
  service_catalog: "bibliothèque centrale de prestations et produits",
  guided_quote_costing: "chiffrage guidé et explicable des devis",
  service_qualification: "questionnaires métier versionnés pour les devis",
  mission_checklists: "checklists terrain et photos obligatoires avant clôture",
  mission_quality_claims: "réclamations client, retouches et indicateurs qualité contrôlés",
  google_review_requests: "collecte contrôlée des avis Google après mission validée",
  client_portfolio_reviews: "revues humaines du portefeuille clients A/B/C",
  director_time_allocation: "mesure du temps commercial, réseau et opérationnel des dirigeants",
  recurring_client_contracts: "contrats récurrents professionnels et communes",
  mission_costing_feedback: "comparaison coûts prévus / réels des missions validées",
  supplier_invoicing: "factures et règlements des sous-traitants",
  worker_company_dossier: "dossier entreprise avancé des sous-traitants",
  worker_pricing_proposals: "propositions tarifaires des sous-traitants",
  referral_commissions: "apporteurs d’affaires et commissions internes",
};
const hasDeploymentCapability = (
  data: DataState,
  capability: DeploymentCapability,
) => !(data.unavailableCapabilities || []).includes(capability);
const missingDeploymentObject = (message: string) =>
  /schema cache|does not exist|could not find|relation .* does not exist|column .* does not exist|function .* does not exist/i.test(
    message,
  );
const frDate = (v: string, time = false) =>
  new Intl.DateTimeFormat(
    "fr-FR",
    time
      ? { dateStyle: "medium", timeStyle: "short" }
      : { dateStyle: "medium" },
  ).format(new Date(v));
const hours = (m: number) => `${Math.round((m / 60) * 100) / 100} h`;
const labels: Record<string, string> = {
  admin: "Administrateur",
  coach: "Coach · lecture seule",
  client: "Client",
  contractor: "Sous-traitant",
  employee: "Employé",
  pending: "En attente",
  exact: "Correspondance exacte",
  multiple: "Plusieurs factures",
  unmatched: "Sans correspondance",
  proposed: "Proposée",
  accepted: "Acceptée",
  refused: "Refusée",
  approved: "Validé",
  rejected: "Refusé",
  cancelled: "Annulé",
  assigned: "Affecté",
  completed: "Terminée",
  active: "Active",
  paused: "En pause",
  draft: "Brouillon",
  sent: "Envoyé",
  ready: "Prête",
  needs_review: "À contrôler",
  planned: "Prévue",
  overdue: "En retard",
  paid: "Payée",
  partially_paid: "Partiellement payée",
  confirmed: "Confirmé",
  prepared: "Préparée",
  reversed: "Annulé",
  expired: "Expiré",
  credited: "Avoir",
  signed: "Signé",
  terminated: "Terminé",
  required: "À fournir",
  requested: "À décider",
  revoked: "Révoqué",
  invited: "Invité",
  submitted: "Déposé, à vérifier",
  under_review: "À l’étude",
  countered: "Contre-proposition",
  changes_requested: "Nouvelle proposition demandée",
  withdrawn: "Retirée",
  awaiting_supplier: "Chiffrage ST attendu",
  supplier_proposed: "Offre ST à étudier",
  admin_review: "Validation ARD requise",
  approved_for_client: "Prêt pour le client",
  standard: "Prestation standard",
  recharge: "Frais à refacturer",
  disbursement: "Débours",
  missing: "Carte grise manquante",
  validated: "Validé",
  waived: "Dispensé",
  estimated: "Estimée",
  pending_approval: "À contrôler",
  immediate: "Immédiate",
  month_end: "Fin de mois",
  grouped: "Regroupée",
  individual: "Individuelle",
  not_required: "Non requis",
  applied: "Appliqué",
  client_supplement: "Supplément client",
  supplier_surcharge: "Surcoût fournisseur",
  supplier_penalty: "Pénalité fournisseur",
  supplier_deduction: "Déduction fournisseur",
  to_review: "À contrôler",
  compliant: "Conforme",
  restricted: "Restreint",
  blocked: "Bloqué",
  retouch_requested: "Retouche à organiser",
  retouch_scheduled: "Retouche planifiée",
  founded: "Fondée",
  partly_founded: "Partiellement fondée",
  unfounded: "Non fondée",
  commercial_gesture: "Geste commercial",
  quality: "Qualité de la prestation",
  missing_item: "Élément oublié",
  damage: "Dommage constaté",
  delay: "Horaire ou retard",
  behavior: "Comportement",
  other: "Autre",
};
const commercialEventLabel = (event: CommercialEvent) => {
  if (event.event_type === "status_changed")
    return `${labels[event.status_from || ""] || event.status_from || "—"} → ${labels[event.status_to || ""] || event.status_to || "—"}`;
  if (event.event_type === "payment_recorded") return "Règlement enregistré";
  if (event.event_type === "reminder_prepared") return "Relance préparée";
  if (["sent", "issued"].includes(event.event_type)) return "Facture émise";
  if (event.event_type === "attachment_added") return "Pièce ajoutée";
  if (event.event_type === "line_added") return "Ligne ajoutée";
  if (event.event_type === "line_updated") return "Ligne modifiée";
  if (event.event_type === "line_deleted") return "Ligne supprimée";
  if (event.event_type === "created") return "Document créé";
  return "Mise à jour du document";
};
const workerName = (w: Worker) =>
  [w.first_name, w.last_name].filter(Boolean).join(" ") || "Sans nom";
const normalizeSkill = (skill: string) =>
  skill.trim().toLocaleLowerCase("fr-FR");
const uniqueSkills = (skills: string[]) =>
  Array.from(
    new Map(
      skills
        .map((skill) => skill.trim())
        .filter(Boolean)
        .map((skill) => [normalizeSkill(skill), skill]),
    ).values(),
  );
const missingWorkerSkills = (worker: Worker | undefined, required: string[]) => {
  if (!worker) return required;
  const available = new Set((worker.skills || []).map(normalizeSkill));
  return uniqueSkills(required).filter((skill) => !available.has(normalizeSkill(skill)));
};
const workerRateAppliesToMission = (rate: WorkerRateCard, mission: Partial<Mission>) =>
  (!rate.client_id || rate.client_id === mission.client_id) &&
  (!rate.property_id || rate.property_id === mission.property_id) &&
  (!rate.property_service_id || rate.property_service_id === mission.property_service_id);
const localInput = (value: string) => {
  const d = new Date(value),
    pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
function Notice({
  children,
  tone = "info",
}: {
  children: React.ReactNode;
  tone?: "info" | "error" | "success" | "warning";
}) {
  return (
    <p
      className={`notice ${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      {children}
    </p>
  );
}
function BrandLockup({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand-lockup${compact ? " compact" : ""}`}>
      <img
        src={compact ? "/brand/ard-manager-icon-dark.png" : "/brand/ard-manager-logo-dark.png"}
        alt="ARD Manager"
      />
    </span>
  );
}
function NavigationIcon({ tab }: { tab: Tab }) {
  const icon = (() => {
    if (tab === "dashboard" || tab === "action_plan") return "dashboard";
    if (tab === "agenda" || tab === "availability" || tab === "leave") return "calendar";
    if (tab === "routes" || tab === "mileage") return "route";
    if (tab === "missions" || tab === "time") return "mission";
    if (["clients", "crm", "team", "director", "profile", "journey"].includes(tab)) return "people";
    if (["billing", "accounting", "profitability", "compensation", "payroll"].includes(tab)) return "finance";
    if (tab === "stocks") return "stock";
    if (tab === "documents" || tab === "proposals") return "document";
    if (tab === "properties" || tab === "company") return "building";
    return "settings";
  })();
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {icon === "dashboard" && <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />}
      {icon === "calendar" && <path d="M5 4v3m14-3v3M4 9h16M5 6h14a1 1 0 0 1 1 1v12H4V7a1 1 0 0 1 1-1Zm3 7h3m2 0h3m-8 3h3" />}
      {icon === "route" && <path d="M6 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm16 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5 7c1 5 12 1 13 7 .3 1.8-1.5 2.2-3 2" />}
      {icon === "mission" && <path d="M8 6V4h8v2m-11 0h14a1 1 0 0 1 1 1v12H4V7a1 1 0 0 1 1-1Zm-1 5h16m-10 0v2h4v-2" />}
      {icon === "people" && <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7-1a3 3 0 1 0 0-6m-7 9c-4 0-6 2-6 6h12c0-4-2-6-6-6Zm7 0c3 0 5 2 5 5h-4" />}
      {icon === "finance" && <path d="M4 20V10m5 10V4m6 16v-7m5 7V7M3 20h18" />}
      {icon === "stock" && <path d="m12 3 8 4-8 4-8-4 8-4Zm-8 4v10l8 4 8-4V7m-8 4v10" />}
      {icon === "document" && <path d="M6 3h8l4 4v14H6V3Zm8 0v5h4M9 12h6m-6 4h6" />}
      {icon === "building" && <path d="M4 21V8l8-5 8 5v13M8 10h2m4 0h2m-8 4h2m4 0h2m-5 7v-3h2v3" />}
      {icon === "settings" && <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0-5v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4m0-12.8L17 7M7 17l-1.4 1.4" />}
    </svg>
  );
}
function Login() {
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [rememberSession, setRememberSession] = useState(getAuthSessionPersistence()),
    [message, setMessage] = useState("");
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isShowcaseMode) {
      setMessage("Connexion simulée : le Showcase reste isolé et n’envoie aucune donnée.");
      return;
    }
    setMessage("Connexion…");
    setAuthSessionPersistence(rememberSession);
    const { error } = await supabase!.auth.signInWithPassword({
      email,
      password,
    });
    setMessage(error ? error.message : "");
  }
  async function recover() {
    if (!email) return setMessage("Saisissez votre adresse email.");
    if (isShowcaseMode) {
      setMessage("Récupération simulée : aucun e-mail n’a été envoyé.");
      return;
    }
    const { error } = await supabase!.auth.resetPasswordForEmail(email, {
      redirectTo: location.origin + "/update-password",
    });
    setMessage(
      error ? error.message : "Un nouveau lien vient de vous être envoyé.",
    );
  }
  return (
    <main className="login">
      <section className="card">
        <BrandLockup />
        <p className="login-eyebrow">Pilotez · Organisez · Développez</p>
        <h1>Connectez-vous</h1>
        <p>Accédez simplement et en toute sécurité à votre espace de travail.</p>
        <form onSubmit={submit}>
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Mot de passe
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <label className="login-remember">
            <input
              type="checkbox"
              checked={rememberSession}
              onChange={(event) => setRememberSession(event.target.checked)}
            />
            <span>
              <strong>Se souvenir de moi</strong>
              <small>{rememberSession ? "La connexion restera active sur cet appareil." : "La connexion prendra fin à la fermeture de cette session."}</small>
            </span>
          </label>
          <button>Se connecter</button>
          <button className="link-button" type="button" onClick={recover}>
            Mot de passe oublié
          </button>
          {message && <Notice tone="error">{message}</Notice>}
        </form>
        {isShowcaseMode && (
          <button className="showcase-login-return" type="button" onClick={() => (location.href = "/?showcase=1")}>
            Retour au Showcase
          </button>
        )}
      </section>
    </main>
  );
}
let passwordUpdateSessionPromise: ReturnType<
  typeof establishPasswordUpdateSession
> | null = null;

function ensurePasswordUpdateSession() {
  if (!supabase) {
    return Promise.resolve({
      session: null,
      error: "Connexion sécurisée indisponible.",
      source: "missing" as const,
    });
  }
  const auth = supabase.auth;
  if (!passwordUpdateSessionPromise) {
    passwordUpdateSessionPromise = establishPasswordUpdateSession(
      {
        getSession: () => auth.getSession(),
        exchangeCodeForSession: (code) =>
          auth.exchangeCodeForSession(code),
        setSession: (tokens) => auth.setSession(tokens),
        verifyOtp: (credentials) => auth.verifyOtp(credentials),
      },
      location.href,
    );
  }
  return passwordUpdateSessionPromise;
}

function cleanPasswordUpdateUrl() {
  const cleanUrl = new URL(location.href);
  cleanUrl.hash = "";
  ["code", "token", "token_hash", "type", "error", "error_description"].forEach(
    (key) => cleanUrl.searchParams.delete(key),
  );
  history.replaceState({}, "", cleanUrl.pathname + cleanUrl.search);
}

function PasswordUpdate({
  done,
  session,
}: {
  done: () => void;
  session: Session | null;
}) {
  const [a, setA] = useState(""),
    [b, setB] = useState(""),
    [message, setMessage] = useState(""),
    [checking, setChecking] = useState(!session),
    [sessionReady, setSessionReady] = useState(Boolean(session)),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (session) {
      cleanPasswordUpdateUrl();
      setSessionReady(true);
      setChecking(false);
      return;
    }
    let active = true;
    void ensurePasswordUpdateSession().then((result) => {
      if (!active) return;
      setSessionReady(Boolean(result.session));
      setChecking(false);
      if (result.session) {
        cleanPasswordUpdateUrl();
      } else {
        setMessage(
          result.error ||
            "Ce lien d’activation n’est plus valide. Demandez un nouveau lien.",
        );
      }
    });
    return () => {
      active = false;
    };
  }, [session]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (a.length < 12) return setMessage("Utilisez au moins 12 caractères.");
    if (a !== b) return setMessage("Les mots de passe ne correspondent pas.");
    if (!sessionReady) {
      return setMessage(
        "Le lien sécurisé n’est pas actif. Revenez à la connexion pour demander un nouveau lien.",
      );
    }
    setBusy(true);
    const { error } = await supabase!.auth.updateUser({ password: a });
    setBusy(false);
    if (error) {
      if (/auth session missing|session.*missing/i.test(error.message)) {
        passwordUpdateSessionPromise = null;
        setSessionReady(false);
        return setMessage(
          "La session du lien a expiré. Demandez un nouveau lien d’activation puis ouvrez-le une seule fois dans ce navigateur.",
        );
      }
      return setMessage(error.message);
    }
    history.replaceState({}, "", location.origin);
    setMessage("Mot de passe enregistré.");
    setTimeout(done, 500);
  }
  return (
    <main className="login">
      <section className="card">
        <BrandLockup />
        <p className="login-eyebrow">Pilotez · Organisez · Développez</p>
        <h1>Définir le mot de passe</h1>
        <form onSubmit={save}>
          <label>
            Nouveau mot de passe
            <input
              type="password"
              autoComplete="new-password"
              value={a}
              onChange={(e) => setA(e.target.value)}
              required
            />
          </label>
          <label>
            Confirmer
            <input
              type="password"
              autoComplete="new-password"
              value={b}
              onChange={(e) => setB(e.target.value)}
              required
            />
          </label>
          <button disabled={checking || busy || !sessionReady}>
            {checking
              ? "Vérification du lien…"
              : busy
                ? "Enregistrement…"
                : "Enregistrer"}
          </button>
          {checking && <Notice>Vérification du lien sécurisé en cours…</Notice>}
          {message && (
            <Notice tone={message === "Mot de passe enregistré." ? "success" : "error"}>
              {message}
            </Notice>
          )}
          {!checking && !sessionReady && (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                history.replaceState({}, "", location.origin);
                done();
              }}
            >
              Retour à la connexion pour recevoir un nouveau lien
            </button>
          )}
        </form>
      </section>
    </main>
  );
}
function Setup() {
  return (
    <main className="login">
      <section className="card">
        <BrandLockup />
        <p className="login-eyebrow">Pilotez · Organisez · Développez</p>
        <h1>Connexion Supabase requise</h1>
        <p>
          Renseignez l’URL et la clé publiable dans <code>.env.local</code>.
        </p>
      </section>
    </main>
  );
}
function Status({ value }: { value: string }) {
  return <span className={`status ${value}`}>{labels[value] || value}</span>;
}
function MissionList({
  items,
  actions,
  showBilling = true,
}: {
  items: Mission[];
  actions?: (mission: Mission) => React.ReactNode;
  showBilling?: boolean;
}) {
  const modes: Record<string, string> = {
    hourly: "Horaire",
    fixed: "Forfait",
    per_unit: "À l’unité",
    monthly: "Mensuel",
  };
  return items.length ? (
    <div className="list">
      {items.map((m) => (
        <article key={m.id}>
          <time>
            {frDate(m.starts_at, true)}
            <small>→ {frDate(m.ends_at, true)}</small>
          </time>
          <div>
            <strong>
              {m.client?.name || "Mission"}{" "}
              {m.is_test && <span className="test-badge">TEST</span>}
            </strong>
            <small>
              {m.location || "Lieu non renseigné"}
              {showBilling && (
                <>
                  {" "}
                  · {modes[m.billing_mode || ""] || "Mode non défini"} ·{" "}
                  {m.amount_ex_vat || 0} € HT
                </>
              )}
            </small>
          </div>
          <div className="row-actions">
            <Status value={m.status} />
            {actions?.(m)}
          </div>
        </article>
      ))}
    </div>
  ) : (
    <p className="empty">Aucune mission accessible.</p>
  );
}
function Records({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: React.ReactNode[];
}) {
  return (
    <section className="panel records">
      <h2>{title}</h2>
      {rows.length ? rows : <p className="empty">{empty}</p>}
    </section>
  );
}
function Dashboard({
  data,
  worker,
}: {
  data: DataState;
  worker: Worker | null;
}) {
  const month = parisDay(new Date()).slice(0, 7),
    actual =
      data.times
        .filter(
          (t) => t.work_date.startsWith(month) && t.status !== "cancelled",
        )
        .reduce((s, t) => s + t.worked_minutes, 0) / 60,
    target = worker?.monthly_hours || 0;
  return (
    <>
      <h1>Tableau de bord</h1>
      <div className="stats">
        <article>
          <span>Missions visibles</span>
          <strong>{data.missions.length}</strong>
        </article>
        <article>
          <span>À venir</span>
          <strong>
            {
              data.missions.filter((m) => new Date(m.starts_at) > new Date())
                .length
            }
          </strong>
        </article>
        {worker?.kind === "employee" ? (
          <article>
            <span>Objectif mensuel</span>
            <strong>
              {actual.toFixed(1)} / {target} h
            </strong>
            <small>
              {target
                ? `${Math.round((actual / target) * 100)} % réalisé`
                : "Quota à renseigner"}
            </small>
          </article>
        ) : (
          <article>
            <span>Demandes à traiter</span>
            <strong>
              {
                [...data.leaves, ...data.times, ...data.mileages].filter(
                  (x) => x.status === "pending",
                ).length
              }
            </strong>
          </article>
        )}
      </div>
      <section className="panel">
        <h2>Prochaines missions</h2>
        <MissionList
          items={data.missions
            .filter((m) => new Date(m.ends_at) >= new Date())
            .slice(0, 8)}
          showBilling={!worker}
        />
      </section>
    </>
  );
}

const money = (value: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    value || 0,
  );

const compactMoney = (value: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: Math.abs(value) < 1000 ? 0 : 1,
  }).format(value || 0);

type ChartPoint = { x: number; y: number };

// Courbe continue sans sur-oscillation : chaque segment conserve les valeurs
// exactes tout en partageant une tangente horizontale avec le suivant.
const smoothChartPath = (points: ChartPoint[]) => {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  return points.slice(1).reduce((path, point, index) => {
    const previous = points[index],
      middleX = (previous.x + point.x) / 2;
    return `${path} C ${middleX} ${previous.y}, ${middleX} ${point.y}, ${point.x} ${point.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
};

const escapeFinancialHtml = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

type FinancialSummarySubject =
  | { type: "client"; client: Client }
  | { type: "worker"; worker: Worker };

function AdminEntityFinancialSummary({
  data,
  subject,
  onClose,
}: {
  data: DataState;
  subject: FinancialSummarySubject;
  onClose: () => void;
}) {
  const [year, setYear] = useState(new Date().getFullYear()),
    isClient = subject.type === "client",
    entityName = isClient
      ? subject.client.name
      : workerName(subject.worker),
    missionById = new Map(data.missions.map((mission) => [mission.id, mission])),
    reportByMission = new Map(data.reports.map((report) => [report.mission_id, report])),
    rateCardById = new Map(
      (data.workerRateCards || []).map((rate) => [rate.id, rate]),
    ),
    workerAssignments = isClient
      ? []
      : data.assignments.filter(
          (assignment) =>
            assignment.worker_id === subject.worker.id &&
            ["accepted", "assigned"].includes(assignment.status),
        ),
    monthRows = Array.from({ length: 12 }, (_, monthIndex) => {
      const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
      if (isClient) {
        const invoices = data.invoices.filter(
          (invoice) =>
            invoice.client_id === subject.client.id &&
            invoice.issue_on?.slice(0, 7) === key &&
            invoice.status !== "draft",
        );
        return {
          key,
          label: new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
            new Date(year, monthIndex, 1),
          ),
          amount: invoices.reduce(
            (sum, invoice) => sum + Number(invoice.total_ex_vat || 0),
            0,
          ),
        };
      }
      const matching = workerAssignments.filter((assignment) => {
        const mission = missionById.get(assignment.mission_id);
        return (
          mission?.ends_at.slice(0, 7) === key && mission.status !== "cancelled"
        );
      });
      const nonMonthly = matching
        .filter(
          (assignment) =>
            (assignment.pricing_mode || subject.worker.pricing_mode) !== "monthly",
        )
        .reduce((sum, assignment) => {
          const mission = missionById.get(assignment.mission_id),
            report = reportByMission.get(assignment.mission_id),
            mode = assignment.pricing_mode || subject.worker.pricing_mode,
            hoursWorked =
              report?.actual_hours ||
              assignment.assigned_hours ||
              mission?.planned_hours ||
              0,
            rateCard = assignment.worker_rate_card_id
              ? rateCardById.get(assignment.worker_rate_card_id)
              : null;
          if (assignment.agreed_amount != null)
            return sum + Number(assignment.agreed_amount);
          if (mode === "hourly")
            return (
              sum +
              hoursWorked *
                Number(
                  assignment.agreed_rate ?? subject.worker.hourly_rate ?? 0,
                )
            );
          if (["fixed", "per_unit"].includes(mode || ""))
            return (
              sum + Number(rateCard?.rate ?? subject.worker.fixed_fee ?? 0)
            );
          return sum;
        }, 0);
      const monthly = matching.some(
        (assignment) =>
          (assignment.pricing_mode || subject.worker.pricing_mode) === "monthly",
      )
        ? Number(
            subject.worker.monthly_fee ??
              matching.find(
                (assignment) =>
                  (assignment.pricing_mode || subject.worker.pricing_mode) ===
                  "monthly",
              )?.agreed_amount ??
              0,
          )
        : 0;
      return {
        key,
        label: new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
          new Date(year, monthIndex, 1),
        ),
        amount: nonMonthly + monthly,
      };
    }),
    annualAmount = monthRows.reduce((sum, row) => sum + row.amount, 0),
    clientInvoices = isClient
      ? data.invoices
          .filter(
            (invoice) =>
              invoice.client_id === subject.client.id &&
              invoice.issue_on?.startsWith(String(year)),
          )
          .sort((left, right) =>
            (right.issue_on || "").localeCompare(left.issue_on || ""),
          )
      : [],
    supplierInvoices = !isClient
      ? (data.supplierInvoices || [])
          .filter(
            (invoice) =>
              invoice.worker_id === subject.worker.id &&
              invoice.issue_on.startsWith(String(year)),
          )
          .sort((left, right) => right.issue_on.localeCompare(left.issue_on))
      : [],
    paidAmount = isClient
      ? clientInvoices.reduce(
          (sum, invoice) => sum + Number(invoice.amount_paid || 0),
          0,
        )
      : supplierInvoices
          .filter((invoice) => invoice.status === "paid")
          .reduce(
            (sum, invoice) => sum + Number(invoice.total_inc_vat || 0),
            0,
          ),
    invoiceTotal = isClient
      ? clientInvoices.reduce(
          (sum, invoice) => sum + Number(invoice.total_inc_vat || 0),
          0,
        )
      : supplierInvoices.reduce(
          (sum, invoice) => sum + Number(invoice.total_inc_vat || 0),
          0,
        ),
    maxAmount = Math.max(1, ...monthRows.map((row) => row.amount)),
    points = monthRows.map((row, index) => ({
      x: 4 + (index / 11) * 92,
      y: 88 - (row.amount / maxAmount) * 74,
    })),
    path = smoothChartPath(points),
    clientServices = isClient
      ? [
          ...data.services.filter(
            (service) => service.client_id === subject.client.id,
          ),
          ...data.propertyServices.filter((service) =>
            data.properties.some(
              (property) =>
                property.id === service.property_id &&
                property.client_id === subject.client.id,
            ),
          ),
        ]
      : [],
    workerRates = !isClient
      ? (data.workerRateCards || []).filter(
          (rate) => rate.worker_id === subject.worker.id && rate.active,
        )
      : [];
  function printSummary() {
    const organization = data.organization?.name || "ARD Manager",
      pricingModeLabels: Record<string, string> = {
        hourly: "À l’heure",
        fixed: "Forfait",
        per_unit: "À l’unité",
        monthly: "Forfait mensuel",
      },
      pricingRows = isClient
        ? clientServices.map((service) => ({
            name: service.name,
            mode:
              pricingModeLabels[service.billing_mode] || service.billing_mode,
            detail:
              "property_id" in service && service.notes
                ? service.notes
                : "Condition enregistrée dans le dossier client",
            amount: Number(service.unit_price_ex_vat || 0),
            unit: "HT",
          }))
        : workerRates.map((rate) => ({
            name: rate.name,
            mode: pricingModeLabels[rate.pricing_mode] || rate.pricing_mode,
            detail: rate.notes || "Condition active enregistrée dans la fiche",
            amount: Number(rate.rate || 0),
            unit: `HT${rate.unit_label ? ` / ${rate.unit_label}` : ""}`,
          })),
      documentRows = isClient
        ? clientInvoices.map((invoice) => ({
            number: invoice.number || "Sans numéro",
            date: invoice.issue_on || "",
            exVat: invoice.total_ex_vat,
            vat: invoice.total_vat,
            incVat: invoice.total_inc_vat,
            status: labels[invoice.status] || invoice.status,
          }))
        : supplierInvoices.map((invoice) => ({
            number: invoice.invoice_number,
            date: invoice.issue_on,
            exVat: invoice.total_ex_vat,
            vat: invoice.total_vat,
            incVat: invoice.total_inc_vat,
            status: labels[invoice.status] || invoice.status,
          })),
      popup = window.open("", "_blank");
    if (!popup) return;
    popup.opener = null;
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Synthèse financière ${escapeFinancialHtml(entityName)}</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{margin:0;color:#191b20;font:12px Arial,sans-serif}header{display:flex;justify-content:space-between;gap:20px;padding:20px;border-radius:16px;background:#191b20;color:white}header b{color:#c6a15b}h1{margin:5px 0 0}h2{margin:0 0 8px;font-size:16px}.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin:18px 0}.kpis div{padding:12px;border:1px solid #d8c28f;border-radius:11px}.kpis span{display:block;color:#666}.kpis strong{display:block;margin-top:5px;font-size:17px}.chart{padding:12px;border:1px solid #eadfca;border-radius:14px;background:#fffdf7}.months{display:grid;grid-template-columns:repeat(6,1fr);gap:5px;margin-top:8px}.months span{padding:6px;border-radius:7px;background:#f5f1e8;text-align:center}.months small,.months strong{display:block}.rates{margin-top:16px;padding:13px;border:1px solid #d8c28f;border-radius:13px;background:#fffdf7;break-inside:avoid}.rates table{margin-top:0}.rates td:last-child,.rates th:last-child{text-align:right}.rates small{display:block;color:#666;margin-top:2px}table{width:100%;margin-top:18px;border-collapse:collapse}th,td{padding:8px 6px;border-bottom:1px solid #e2e4e8;text-align:right}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2){text-align:left}thead{background:#f5f1e8}.notice{margin-top:18px;color:#666;font-size:10px}</style></head><body><header><div><b>${escapeFinancialHtml(organization)}</b><h1>Synthèse financière</h1></div><div><strong>${escapeFinancialHtml(entityName)}</strong><p>Année ${year}</p></div></header><div class="kpis"><div><span>${isClient ? "CA facturé HT" : "Coût / CA ST calculé HT"}</span><strong>${escapeFinancialHtml(money(annualAmount))}</strong></div><div><span>Total des factures TTC</span><strong>${escapeFinancialHtml(money(invoiceTotal))}</strong></div><div><span>${isClient ? "Encaissé" : "Payé"}</span><strong>${escapeFinancialHtml(money(paidAmount))}</strong></div></div><div class="chart"><svg viewBox="0 0 100 100" width="100%" height="170" preserveAspectRatio="none"><path d="${escapeFinancialHtml(path)}" fill="none" stroke="#c6a15b" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/></svg><div class="months">${monthRows.map((row) => `<span><small>${escapeFinancialHtml(row.label)}</small><strong>${escapeFinancialHtml(money(row.amount))}</strong></span>`).join("")}</div></div><section class="rates"><h2>${isClient ? "Prestations et prix enregistrés" : "Conditions tarifaires enregistrées"}</h2><table><thead><tr><th>Condition</th><th>Mode</th><th>Tarif</th></tr></thead><tbody>${pricingRows.map((row) => `<tr><td><strong>${escapeFinancialHtml(row.name)}</strong><small>${escapeFinancialHtml(row.detail)}</small></td><td>${escapeFinancialHtml(row.mode)}</td><td><strong>${escapeFinancialHtml(money(row.amount))} ${escapeFinancialHtml(row.unit)}</strong></td></tr>`).join("") || `<tr><td colspan="3">Aucune condition tarifaire active enregistrée.</td></tr>`}</tbody></table></section><table><thead><tr><th>Facture</th><th>Date</th><th>HT</th><th>TVA</th><th>TTC</th><th>État</th></tr></thead><tbody>${documentRows.map((row) => `<tr><td>${escapeFinancialHtml(row.number)}</td><td>${escapeFinancialHtml(row.date ? new Date(`${row.date}T12:00:00`).toLocaleDateString("fr-FR") : "—")}</td><td>${escapeFinancialHtml(money(row.exVat))}</td><td>${escapeFinancialHtml(money(row.vat))}</td><td>${escapeFinancialHtml(money(row.incVat))}</td><td>${escapeFinancialHtml(row.status)}</td></tr>`).join("") || `<tr><td colspan="6">Aucune facture pour cette année.</td></tr>`}</tbody></table><p class="notice">Lecture de pilotage générée depuis ARD Manager. Les forfaits restent au montant convenu et ne varient pas avec le temps passé. Les montants restent provisoires lorsque des coûts, factures ou règlements ne sont pas encore enregistrés ou validés.</p><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script></body></html>`);
    popup.document.close();
  }
  return (
    <div className="portal-overlay financial-summary-overlay" role="dialog" aria-modal="true" aria-label={`Synthèse financière de ${entityName}`}>
      <section className="portal-detail financial-summary-modal">
        <div className="portal-detail-head">
          <div><span className="eyebrow">Lecture financière</span><h2>{entityName}</h2></div>
          <button className="icon-close" aria-label="Fermer" onClick={onClose}>×</button>
        </div>
        <div className="financial-summary-actions">
          <label>Année<select value={year} onChange={(event) => setYear(Number(event.target.value))}>{[0,1,2,3,4].map((offset) => <option key={offset} value={new Date().getFullYear()-offset}>{new Date().getFullYear()-offset}</option>)}</select></label>
          <button type="button" onClick={printSummary}>Exporter cette synthèse en PDF</button>
        </div>
        <div className="stats financial-summary-kpis">
          <article><span>{isClient ? "CA facturé HT" : "Coût / CA ST calculé HT"}</span><strong>{money(annualAmount)}</strong></article>
          <article><span>Total factures TTC</span><strong>{money(invoiceTotal)}</strong></article>
          <article><span>{isClient ? "Encaissé" : "Payé"}</span><strong>{money(paidAmount)}</strong></article>
        </div>
        <section className="panel financial-summary-chart">
          <div className="section-heading"><div><span className="eyebrow">Évolution mensuelle · {year}</span><h3>{isClient ? "Chiffre d’affaires" : "Coût des prestations"}</h3></div></div>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={`Évolution financière mensuelle de ${entityName}`}><path d={`${path} L 96 92 L 4 92 Z`} className="area"/><path d={path} className="line"/>{points.map((point,index)=><circle key={monthRows[index].key} cx={point.x} cy={point.y} r="1.7"><title>{monthRows[index].label} : {money(monthRows[index].amount)}</title></circle>)}</svg>
          <div className="financial-summary-months">{monthRows.map((row)=><span key={row.key}><small>{row.label}</small><strong>{money(row.amount)}</strong></span>)}</div>
        </section>
        <div className="financial-summary-grid">
          <section className="panel"><h3>Factures de l’année</h3><div className="financial-document-list">{(isClient ? clientInvoices : supplierInvoices).map((invoice) => <article key={invoice.id}><span><strong>{isClient ? (invoice as Invoice).number || "Sans numéro" : (invoice as PortalSupplierInvoice).invoice_number}</strong><small>{frDate((invoice as Invoice).issue_on || (invoice as PortalSupplierInvoice).issue_on)}</small></span><b>{money((invoice as Invoice).total_inc_vat || (invoice as PortalSupplierInvoice).total_inc_vat)} TTC</b><Status value={invoice.status}/></article>)}{!(isClient ? clientInvoices : supplierInvoices).length && <p className="empty">Aucune facture pour cette année.</p>}</div></section>
          <section className="panel"><h3>{isClient ? "Prestations et prix enregistrés" : "Conditions tarifaires enregistrées"}</h3><div className="financial-rate-list">{isClient ? clientServices.map((service) => <article key={service.id}><span><strong>{service.name}</strong><small>{service.billing_mode}</small></span><b>{money(service.unit_price_ex_vat)} HT</b></article>) : workerRates.map((rate) => <article key={rate.id}><span><strong>{rate.name}</strong><small>{rate.pricing_mode}{rate.notes ? ` · ${rate.notes}` : ""}</small></span><b>{money(rate.rate)} HT{rate.unit_label ? ` / ${rate.unit_label}` : ""}</b></article>)}</div></section>
        </div>
        <Notice>Les forfaits sont conservés au montant convenu et ne varient pas avec le temps passé. Cette synthèse reste provisoire si des pièces ou règlements sont manquants.</Notice>
      </section>
    </div>
  );
}

type ExecutiveDonutSlice = {
  label: string;
  value: number;
  color: string;
};

function ExecutiveDonut({
  label,
  slices,
  centerLabel,
  centerValue,
}: {
  label: string;
  slices: ExecutiveDonutSlice[];
  centerLabel: string;
  centerValue: string;
}) {
  const visibleSlices = slices.filter((slice) => slice.value > 0),
    total = visibleSlices.reduce((sum, slice) => sum + slice.value, 0);
  let cursor = 0;
  const gradient = total
    ? `conic-gradient(${visibleSlices
        .map((slice) => {
          const start = (cursor / total) * 100;
          cursor += slice.value;
          const end = (cursor / total) * 100;
          return `${slice.color} ${start}% ${end}%`;
        })
        .join(", ")})`
    : "conic-gradient(var(--ard-line) 0 100%)";
  return (
    <div className="executive-donut-layout">
      <div
        className="executive-donut"
        style={{ background: gradient }}
        role="img"
        aria-label={label}
      >
        <div>
          <strong>{centerValue}</strong>
          <span>{centerLabel}</span>
        </div>
      </div>
      <div className="executive-donut-legend">
        {visibleSlices.length ? (
          visibleSlices.map((slice) => (
            <div key={slice.label}>
              <i style={{ background: slice.color }} />
              <span>{slice.label}</span>
              <strong>{money(slice.value)}</strong>
              <small>{Math.round((slice.value / total) * 100)} %</small>
            </div>
          ))
        ) : (
          <p className="empty">Aucune donnée chiffrée sur la période.</p>
        )}
      </div>
    </div>
  );
}

type ExecutiveBarItem = {
  label: string;
  value: number;
  display: string;
  tone?: "gold" | "slate" | "danger" | "positive";
  detail?: string;
};

function ExecutiveBars({
  label,
  items,
}: {
  label: string;
  items: ExecutiveBarItem[];
}) {
  const maximum = Math.max(1, ...items.map((item) => Math.abs(item.value)));
  if (!items.length)
    return <p className="empty">Aucune donnée chiffrée sur la période.</p>;
  return (
    <div className="executive-bars" role="img" aria-label={label}>
      {items.map((item) => (
        <div className="executive-bar-row" key={item.label}>
          <div>
            <span>{item.label}</span>
            <strong>{item.display}</strong>
          </div>
          <div className="executive-bar-track" aria-hidden="true">
            <i
              className={item.tone || "gold"}
              style={{ width: `${Math.max(item.value ? 4 : 0, (Math.abs(item.value) / maximum) * 100)}%` }}
            />
          </div>
          {item.detail && <small>{item.detail}</small>}
        </div>
      ))}
    </div>
  );
}

const resolvePaymentTerms = (
  organization?: Organization | null,
  client?: Client | null,
  document?: Pick<Invoice | Quote, "payment_terms_mode" | "payment_terms_days"> | null,
) => ({
  mode:
    document?.payment_terms_mode ||
    client?.payment_terms_mode ||
    organization?.default_payment_terms_mode ||
    "days_after_issue",
  days:
    document?.payment_terms_days ??
    client?.payment_terms_days ??
    organization?.default_invoice_due_days ??
    30,
});

const paymentTermsLabel = (mode: PaymentTermsMode, days: number) =>
  mode === "immediate"
    ? "Paiement comptant"
    : mode === "end_of_month"
      ? "Paiement en fin de mois"
      : `Paiement à ${days} jour${days > 1 ? "s" : ""}`;

const paymentDueOn = (
  issuedOn: string,
  mode: PaymentTermsMode,
  days: number,
) => {
  const date = new Date(`${issuedOn}T12:00:00`);
  if (mode === "end_of_month")
    date.setMonth(date.getMonth() + 1, 0);
  else if (mode === "days_after_issue") date.setDate(date.getDate() + days);
  return parisDay(date);
};
async function addTurnoverCommissionToInvoice(
  declaration: ClientTurnoverDeclaration,
  data: DataState,
) {
  if (!data.organization) return "Société introuvable.";
  const client = data.clients.find((item) => item.id === declaration.client_id);
  if (!client) return "Client introuvable.";
  const grouped = client.billing_grouping !== "individual";
  let invoice = grouped
    ? data.invoices.find(
        (item) =>
          item.client_id === declaration.client_id &&
          item.status === "draft" &&
          item.billing_period?.slice(0, 7) === declaration.period.slice(0, 7) &&
          (item.document_kind || "standard") === "standard",
      )
    : undefined;
  if (!invoice) {
    const terms = resolvePaymentTerms(data.organization, client);
    const created = await supabase!
      .from("invoices")
      .insert({
        organization_id: data.organization.id,
        client_id: declaration.client_id,
        billing_period: declaration.period,
        grouping: grouped ? "grouped" : "individual",
        document_kind: "standard",
        status: "draft",
        total_ex_vat: 0,
        total_vat: 0,
        total_inc_vat: 0,
        payment_terms_mode: terms.mode,
        payment_terms_days: terms.days,
      })
      .select(
        "id,client_id,number,billing_period,grouping,document_kind,status,total_ex_vat,total_vat,total_inc_vat,payment_terms_mode,payment_terms_days",
      )
      .single();
    if (created.error) return created.error.message;
    invoice = created.data as Invoice;
  }
  const periodLabel = new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
    }).format(new Date(`${declaration.period.slice(0, 10)}T12:00:00`)),
    inserted = await supabase!.from("invoice_lines").insert({
      invoice_id: invoice.id,
      turnover_declaration_id: declaration.id,
      mission_id: null,
      service_id: null,
      description: `${declaration.label} — ${periodLabel} — ${declaration.commission_rate}% du CA ${declaration.turnover_basis === "inc_vat" ? "TTC" : "HT"}`,
      quantity: 1,
      unit_price_ex_vat: declaration.commission_amount_ex_vat,
      vat_rate: declaration.vat_rate,
      position:
        (data.invoiceLines || []).filter((line) => line.invoice_id === invoice!.id)
          .length + 1,
    });
  if (inserted.error)
    return inserted.error.code === "23505"
      ? "Cette commission est déjà présente dans une facture."
      : inserted.error.message;
  const refreshed = await supabase!
    .from("invoice_lines")
    .select("quantity,unit_price_ex_vat,vat_rate")
    .eq("invoice_id", invoice.id);
  if (refreshed.error) return refreshed.error.message;
  const totals = (refreshed.data || []).reduce(
    (sum, line) => {
      const exVat = Number(line.quantity) * Number(line.unit_price_ex_vat);
      sum.exVat += exVat;
      sum.vat += (exVat * Number(line.vat_rate)) / 100;
      return sum;
    },
    { exVat: 0, vat: 0 },
  );
  const updated = await supabase!
    .from("invoices")
    .update({
      total_ex_vat: Math.round(totals.exVat * 100) / 100,
      total_vat: Math.round(totals.vat * 100) / 100,
      total_inc_vat: Math.round((totals.exVat + totals.vat) * 100) / 100,
    })
    .eq("id", invoice.id);
  if (updated.error) return updated.error.message;
  const linked = await supabase!
    .from("client_turnover_declarations")
    .update({ status: "invoiced", invoice_id: invoice.id })
    .eq("id", declaration.id);
  if (linked.error) return linked.error.message;
  await supabase!.from("commercial_document_events").insert({
    organization_id: data.organization.id,
    invoice_id: invoice.id,
    event_type: "turnover_commission_added",
    note: `${declaration.commission_rate}% sur ${money(declaration.turnover_amount)} de CA ${declaration.turnover_basis === "inc_vat" ? "TTC" : "HT"}.`,
  });
  return null;
}
const mileageEntitlement2026 = (vehicle: WorkerVehicle, distance: number) => {
  const d = Math.max(0, distance),
    electric = vehicle.energy_type === "electric",
    power = vehicle.fiscal_power;
  if (vehicle.vehicle_kind === "car") {
    const bands = electric
      ? power <= 3
        ? [0.635, 0.379, 1278, 0.444]
        : power === 4
          ? [0.727, 0.408, 1596, 0.488]
          : power === 5
            ? [0.763, 0.428, 1674, 0.512]
            : power === 6
              ? [0.798, 0.449, 1748, 0.536]
              : [0.836, 0.473, 1818, 0.564]
      : power <= 3
        ? [0.529, 0.316, 1065, 0.37]
        : power === 4
          ? [0.606, 0.34, 1330, 0.407]
          : power === 5
            ? [0.636, 0.357, 1395, 0.427]
            : power === 6
              ? [0.665, 0.374, 1457, 0.447]
              : [0.697, 0.394, 1515, 0.47];
    return d <= 5000
      ? d * bands[0]
      : d <= 20000
        ? d * bands[1] + bands[2]
        : d * bands[3];
  }
  if (vehicle.vehicle_kind === "motorcycle") {
    const bands = electric
      ? power <= 2
        ? [0.474, 0.119, 1069, 0.298]
        : power <= 5
          ? [0.562, 0.098, 1390, 0.33]
          : [0.727, 0.095, 1900, 0.412]
      : power <= 2
        ? [0.395, 0.099, 891, 0.248]
        : power <= 5
          ? [0.468, 0.082, 1158, 0.275]
          : [0.606, 0.079, 1583, 0.343];
    return d <= 3000
      ? d * bands[0]
      : d <= 6000
        ? d * bands[1] + bands[2]
        : d * bands[3];
  }
  return electric
    ? d <= 3000
      ? d * 0.378
      : d <= 6000
        ? d * 0.095 + 853
        : d * 0.238
    : d <= 3000
      ? d * 0.315
      : d <= 6000
        ? d * 0.079 + 711
        : d * 0.198;
};
const parisDay = (value: Date | string) =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));

const missionOperationalStatus = (
  mission: Mission,
  claims: MissionQualityClaim[] = [],
) => {
  const hasOpenClaim = claims.some(
    (claim) =>
      claim.mission_id === mission.id &&
      !["resolved", "rejected", "cancelled"].includes(claim.status),
  );
  if (hasOpenClaim) return { label: "Problème signalé", tone: "problem" };
  if (mission.status === "completed" || mission.workflow_status === "approved")
    return { label: "Terminée", tone: "done" };
  if (["in_progress", "started"].includes(mission.status))
    return { label: "En cours", tone: "progress" };
  return { label: "À faire", tone: "todo" };
};

function AdminMissionPreview({
  mission,
  data,
  onClose,
  onEdit,
  reload,
}: {
  mission: Mission;
  data: DataState;
  onClose: () => void;
  onEdit: (mission: Mission) => void;
  reload: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const assignments = data.assignments.filter(
    (a) => a.mission_id === mission.id && a.status !== "cancelled",
  ),
    property = data.properties.find((item) => item.id === mission.property_id),
    serviceLines = (data.missionServiceLines || [])
      .filter((line) => line.mission_id === mission.id)
      .sort((left, right) => left.position - right.position),
    primaryService = mission.property_service_id
      ? (data.propertyServices || []).find((item) => item.id === mission.property_service_id)
      : data.services.find((item) => item.id === mission.service_id),
    approvedMissionChanges = (data.missionChangeEvents || []).filter(
      (item) =>
        item.mission_id === mission.id &&
        ["approved", "applied"].includes(item.status) &&
        ["supplement", "modification"].includes(item.event_type),
    );
  async function cancel() {
    if (!confirm("Annuler cette mission et retirer sa mise en facturation ?"))
      return;
    setBusy(true);
    const now = new Date().toISOString();
    const { error } = await supabase!
      .from("missions")
      .update({
        status: "cancelled",
        workflow_status: "cancelled",
        billing_status: "cancelled",
        cancelled_at: now,
        cancellation_reason: "Annulation administrateur",
      })
      .eq("id", mission.id);
    setBusy(false);
    if (error) setMessage(error.message);
    else {
      reload();
      onClose();
    }
  }
  return (
    <div className="portal-overlay" role="dialog" aria-modal="true">
      <section className="portal-detail">
        <div className="portal-detail-head">
          <div>
            <span className="eyebrow">Mission</span>
            <h2>
              {mission.client?.name || mission.client_display_name || "Mission"}
            </h2>
          </div>
          <button className="icon-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="detail-grid">
          <div>
            <span>Début</span>
            <strong>{frDate(mission.starts_at, true)}</strong>
          </div>
          <div>
            <span>Fin</span>
            <strong>{frDate(mission.ends_at, true)}</strong>
          </div>
          <div>
            <span>Lieu</span>
            <strong>{mission.location || "Non renseigné"}</strong>
          </div>
          <div>
            <span>Bien concerné</span>
            <strong>{property?.name || "Client entier / bien non renseigné"}</strong>
          </div>
          <div>
            <span>Montant client</span>
            <strong>{money(mission.amount_ex_vat || 0)} HT</strong>
          </div>
          <div>
            <span>État</span>
            <Status value={mission.status} />
          </div>
          <div>
            <span>Facturation</span>
            <strong>
              {labels[mission.billing_status || ""] ||
                mission.billing_status ||
                "Planifiée"}
            </strong>
          </div>
        </div>
        <div className="detail-note mission-content-summary">
          <strong>Ce que comporte la mission</strong>
          <ul className="mission-service-summary-list">
            {(serviceLines.length
              ? serviceLines
              : primaryService
                ? [{
                    id: `legacy-${mission.id}`,
                    label_snapshot: primaryService.name,
                    amount_ex_vat: mission.amount_ex_vat || 0,
                    planned_hours: mission.planned_hours,
                  }]
                : []
            ).map((line) => (
              <li key={line.id}>
                <span>{line.label_snapshot}</span>
                <small>
                  {money(line.amount_ex_vat)} HT
                  {line.planned_hours != null ? ` · ${line.planned_hours} h prévues` : ""}
                </small>
              </li>
            ))}
            {approvedMissionChanges.map((change) => (
              <li key={change.id}>
                <span>{change.reason}</span>
                <small>Ajout ou modification validée</small>
              </li>
            ))}
          </ul>
          {!serviceLines.length && !primaryService && !approvedMissionChanges.length && (
            <Notice tone="warning">
              Le contenu opérationnel de cette mission n’est pas encore suffisamment renseigné.
            </Notice>
          )}
        </div>
        {mission.notes && (
          <div className="detail-note">
            <strong>Consignes</strong>
            <p>{mission.notes}</p>
          </div>
        )}
        <div className="detail-note">
          <strong>Personnes rattachées</strong>
          {assignments.length ? (
            assignments.map((a) => {
              const assigned = data.workers.find((w) => w.id === a.worker_id);
              return (
                <p key={a.id}>
                  {assigned ? workerName(assigned) : "Personne inconnue"} ·{" "}
                  {a.assigned_hours} h · {labels[a.status] || a.status}
                </p>
              );
            })
          ) : (
            <p>Aucune personne affectée.</p>
          )}
        </div>
        {message && <Notice tone="error">{message}</Notice>}
        <div className="detail-actions">
          <a
            className="secondary map-action"
            href={googleCalendarMissionUrl(mission, mission.notes)}
            target="_blank"
            rel="noreferrer"
          >
            Ajouter à Google Agenda
          </a>
          {mission.location && (
            <a
              className="secondary map-action"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mission.location)}`}
              target="_blank"
              rel="noreferrer"
            >
              Ouvrir l’itinéraire
            </a>
          )}
          <button className="secondary" onClick={() => onEdit(mission)}>
            Modifier
          </button>
          <button
            className="danger"
            disabled={busy || mission.status === "cancelled"}
            onClick={cancel}
          >
            {busy ? "Annulation…" : "Annuler la mission"}
          </button>
        </div>
      </section>
    </div>
  );
}

function AdminSubscriptionUpgradeNotice({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const request = (data.subscriptionUpgradeRequests || []).find(
      (item) =>
        item.organization_id === profile.organization_id &&
        ["recommended", "awaiting_signature"].includes(item.status),
    ),
    plan = (data.subscriptionPlans || []).find(
      (item) => item.id === request?.recommended_plan_id,
    ),
    amendment = (data.subscriptionAmendments || []).find(
      (item) => item.upgrade_request_id === request?.id,
    );
  if (!request) return null;

  async function respond(accept: boolean) {
    if (!accept && !confirm("Refuser cette proposition de changement de forfait ?"))
      return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc(
      "respond_subscription_upgrade_request",
      {
        p_request_id: request!.id,
        p_accept: accept,
        p_notes: null,
      },
    );
    setBusy(false);
    if (error) setMessage(error.message);
    else {
      setMessage(
        accept
          ? "Changement accepté. L’avenant doit maintenant être signé avant toute activation."
          : "Proposition refusée. Votre forfait actuel reste inchangé.",
      );
      reload();
    }
  }

  return (
    <section className="panel subscription-upgrade-notice">
      <div>
        <span className="eyebrow">Abonnement ARD Manager</span>
        <h2>
          {request.status === "recommended"
            ? "Votre usage dépasse la limite du forfait"
            : "Changement de forfait en attente de signature"}
        </h2>
        <p>
          {request.trigger_metric === "users"
            ? `${request.current_value} utilisateur(s) actif(s) pour une limite de ${request.current_limit ?? "non définie"}.`
            : "Une évolution de forfait a été proposée en fonction de votre usage."}
          {plan ? ` Forfait proposé : ${plan.name}.` : " Le forfait supérieur reste à définir."}
        </p>
        {plan && (
          <small>
            {plan.monthly_price_ex_vat != null
              ? `${money(plan.monthly_price_ex_vat)} HT / mois`
              : "Tarif mensuel à définir"}
            {plan.annual_price_ex_vat != null
              ? ` · ${money(plan.annual_price_ex_vat)} HT / an`
              : ""}
          </small>
        )}
        {request.status === "awaiting_signature" && (
          <p>
            L’accord est enregistré. Les nouveaux droits ne seront ouverts
            qu’après signature et validation de l’avenant
            {amendment ? ` (${labels[amendment.status] || amendment.status})` : ""}.
          </p>
        )}
        {message && <Notice tone="info">{message}</Notice>}
      </div>
      {request.status === "recommended" && (
        <div className="row-actions">
          <button disabled={busy || !request.recommended_plan_id} onClick={() => respond(true)}>
            {busy ? "Traitement…" : "Accepter et préparer l’avenant"}
          </button>
          <button className="secondary" disabled={busy} onClick={() => respond(false)}>
            Refuser
          </button>
        </div>
      )}
    </section>
  );
}

function AdminDashboard({
  profile,
  data,
  reload,
  onEditMission,
  onOpenReport,
  onOpenInvoice,
  onOpenSupplierInvoice,
  onOpenExpense,
  onNavigate,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  onEditMission: (m: Mission) => void;
  onOpenReport: (missionId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onOpenSupplierInvoice: (invoiceId: string) => void;
  onOpenExpense: (expenseId: string) => void;
  onNavigate: (tab: Tab) => void;
}) {
  const [selected, setSelected] = useState<Mission | null>(null),
    [costPeriod, setCostPeriod] = useState<"month" | "year" | null>(null),
    [selectedKpi, setSelectedKpi] = useState<DashboardWidgetKey | null>(null),
    [selectedChange, setSelectedChange] = useState<ProfileChangeRequest | null>(
      null,
    ),
    [reviewNotes, setReviewNotes] = useState(""),
    [reviewError, setReviewError] = useState(""),
    [reviewBusy, setReviewBusy] = useState(false),
    [actionFilter, setActionFilter] = useState<"open" | "history">("open"),
    [notificationLevel, setNotificationLevel] = useState<
      "information" | "alert"
    >("information"),
    [actionPlanHeadline, setActionPlanHeadline] = useState<{
      openAttention: number;
      overdue: number;
      reviews: number;
      completed: number;
      total: number;
      nextScan: string;
    } | null>(null),
    month = parisDay(new Date()).slice(0, 7),
    year = month.slice(0, 4),
    today = parisDay(new Date()),
    prefs =
      profile.dashboard_preferences ||
      data.organization?.dashboard_preferences ||
      {};
  useEffect(() => {
    let active = true;
    async function loadActionPlanHeadline() {
      if (!supabase) return;
      const [statusResult, actionsResult] = await Promise.all([
        supabase.rpc("get_action_plan_automation_status"),
        supabase
          .from("action_plan_actions")
          .select("id,status")
          .eq("organization_id", profile.organization_id),
      ]);
      if (!active || statusResult.error || actionsResult.error || !statusResult.data) return;
      const status = statusResult.data as {
        run?: { overdue_count?: number; review_count?: number };
        items?: Array<{ status?: string }>;
        next_scheduled_scan?: string;
      };
      const actions = (actionsResult.data || []) as Array<{ status?: string }>;
      setActionPlanHeadline({
        openAttention: (status.items || []).filter((item) => item.status === "open").length,
        overdue: Number(status.run?.overdue_count || 0),
        reviews: Number(status.run?.review_count || 0),
        completed: actions.filter((item) => item.status === "completed").length,
        total: actions.length,
        nextScan: String(status.next_scheduled_scan || "Analyse quotidienne à 07:05"),
      });
    }
    void loadActionPlanHeadline();
    return () => {
      active = false;
    };
  }, [profile.organization_id]);
  const monthInvoices = data.invoices.filter(
    (i) =>
      (i.issue_on || i.billing_period || "").startsWith(month) &&
      !["draft", "cancelled", "credited"].includes(i.status),
  );
  const ca = monthInvoices.reduce(
      (s, i) => s + i.total_ex_vat * (i.document_kind === "credit" ? -1 : 1),
      0,
    ),
    collected = (data.invoicePayments || [])
      .filter(
        (payment) =>
          payment.received_on.startsWith(month) &&
          payment.status === "confirmed",
      )
      .reduce((sum, payment) => sum + payment.amount, 0);
  const yearInvoices = data.invoices.filter(
      (i) =>
        (i.issue_on || i.billing_period || "").startsWith(year) &&
        !["draft", "cancelled", "credited"].includes(i.status),
    ),
    annualCa = yearInvoices.reduce(
      (s, i) => s + i.total_ex_vat * (i.document_kind === "credit" ? -1 : 1),
      0,
    );
  const calculateCosts = (prefix: string, salaryMonths: number) => {
    const operatingRows = data.expenses.filter(
        (expense) =>
          expense.spent_on.startsWith(prefix) &&
          !["rejected", "cancelled"].includes(expense.status),
      ),
      operating = operatingRows.reduce(
        (sum, expense) => sum + expense.amount_ex_vat,
        0,
      ),
      periodStart = prefix.length === 4 ? prefix + "-01-01" : prefix + "-01",
      periodEnd =
        prefix.length === 4
          ? today
          : parisDay(
              new Date(
                Number(prefix.slice(0, 4)),
                Number(prefix.slice(5, 7)),
                0,
              ),
            ),
      fixedRows = (data.fixedCharges || []).filter(
        (charge) =>
          charge.starts_on <= periodEnd &&
          (!charge.ends_on || charge.ends_on >= periodStart),
      ),
      fixed = fixedRows.reduce((sum, charge) => {
        const monthlyValue =
            charge.frequency === "weekly"
              ? (charge.amount_ex_vat * 52) / 12
              : charge.frequency === "quarterly"
                ? charge.amount_ex_vat / 3
                : charge.frequency === "annual"
                  ? charge.amount_ex_vat / 12
                  : charge.amount_ex_vat,
          firstMonth =
            charge.starts_on.slice(0, 4) === prefix
              ? Math.max(0, Number(charge.starts_on.slice(5, 7)) - 1)
              : 0,
          lastMonth =
            charge.ends_on && charge.ends_on.slice(0, 4) === prefix
              ? Math.min(
                  salaryMonths - 1,
                  Number(charge.ends_on.slice(5, 7)) - 1,
                )
              : salaryMonths - 1,
          months =
            prefix.length === 4 ? Math.max(0, lastMonth - firstMonth + 1) : 1;
        return sum + monthlyValue * months;
      }, 0),
      periodMissionIds = new Set(
        data.missions
          .filter(
            (mission) =>
              parisDay(mission.starts_at).startsWith(prefix) &&
              mission.status !== "cancelled",
          )
          .map((mission) => mission.id),
      ),
      monthlyContractors = new Set<string>(),
      contractorByWorker = new Map<string, number>();
    let contractors = 0;
    const addContractorCost = (worker: Worker, amount: number) => {
      contractors += amount;
      contractorByWorker.set(
        worker.id,
        (contractorByWorker.get(worker.id) || 0) + amount,
      );
    };
    data.assignments
      .filter(
        (assignment) =>
          periodMissionIds.has(assignment.mission_id) &&
          !["cancelled", "refused"].includes(assignment.status),
      )
      .forEach((assignment) => {
        const worker = data.workers.find(
          (item) =>
            item.id === assignment.worker_id && item.kind === "contractor",
        );
        if (!worker) return;
        if (assignment.agreed_amount != null) {
          addContractorCost(worker, assignment.agreed_amount);
          return;
        }
        if (
          assignment.pricing_mode === "hourly" ||
          worker.pricing_mode === "hourly"
        ) {
          addContractorCost(
            worker,
            Number(assignment.assigned_hours || 0) *
              Number(assignment.agreed_rate ?? worker.hourly_rate ?? 0),
          );
          return;
        }
        if (
          assignment.pricing_mode === "fixed" ||
          worker.pricing_mode === "fixed"
        ) {
          addContractorCost(
            worker,
            Number(assignment.agreed_rate ?? worker.fixed_fee ?? 0),
          );
          return;
        }
        if (
          worker.pricing_mode === "monthly" &&
          !monthlyContractors.has(worker.id)
        ) {
          monthlyContractors.add(worker.id);
          addContractorCost(
            worker,
            Number(worker.monthly_fee || 0) * salaryMonths,
          );
        }
      });
    const employees = data.workers.filter(
        (worker) => worker.kind === "employee" && worker.active,
      ),
      employeePayroll = employees.reduce(
        (sum, worker) =>
          sum +
          Number(worker.employer_cost_monthly ?? worker.net_salary ?? 0) *
            salaryMonths,
        0,
      ),
      tickets = employees.reduce(
        (sum, worker) =>
          sum +
          Number(worker.ticket_count || 0) *
            Number(worker.ticket_value || 0) *
            salaryMonths,
        0,
      ),
      mileageRows = data.mileages.filter(
        (entry) =>
          entry.travel_date.startsWith(prefix) && entry.status === "approved",
      ),
      mileageKm = mileageRows.reduce(
        (sum, entry) => sum + Number(entry.kilometers || 0),
        0,
      ),
      mileage = mileageRows.reduce(
        (sum, entry) => sum + Number(entry.calculated_amount || 0),
        0,
      ),
      directorRows = (data.directorCompensations || []).filter((entry) =>
        entry.period.startsWith(prefix),
      ),
      directorCost = directorRows.reduce(
        (sum, entry) =>
          sum +
          Number(entry.net_salary || 0) +
          Number(entry.social_contributions || 0) +
          Number(entry.other_costs || 0),
        0,
      ),
      directorDividends = directorRows.reduce(
        (sum, entry) => sum + Number(entry.dividends || 0),
        0,
      ),
      referralCommissionRows = (data.invoiceReferralCommissions || []).filter(
        (entry) => {
          if (["waived", "cancelled"].includes(entry.status)) return false;
          const invoice = data.invoices.find(
            (item) => item.id === entry.invoice_id,
          );
          return (invoice?.issue_on || invoice?.billing_period || "").startsWith(
            prefix,
          );
        },
      ),
      referralCommissions = referralCommissionRows.reduce(
        (sum, entry) => sum + Number(entry.commission_amount_ex_vat || 0),
        0,
      ),
      total =
        operating +
        fixed +
        contractors +
        employeePayroll +
        tickets +
        mileage +
        directorCost +
        referralCommissions;
    return {
      operatingRows,
      operating,
      fixedRows,
      fixed,
      contractors,
      contractorRows: Array.from(contractorByWorker.entries())
        .map(([workerId, amount]) => ({
          worker: data.workers.find((item) => item.id === workerId),
          amount,
        }))
        .sort((a, b) => b.amount - a.amount),
      employeePayroll,
      employeeRows: employees
        .map((worker) => ({
          worker,
          payroll:
            Number(worker.employer_cost_monthly ?? worker.net_salary ?? 0) *
            salaryMonths,
          tickets:
            Number(worker.ticket_count || 0) *
            Number(worker.ticket_value || 0) *
            salaryMonths,
          complete: worker.employer_cost_monthly != null,
        }))
        .sort((a, b) => b.payroll + b.tickets - (a.payroll + a.tickets)),
      tickets,
      mileageRows,
      mileageKm,
      mileage,
      directorRows,
      directorCost,
      directorDividends,
      referralCommissionRows,
      referralCommissions,
      total,
      salaryMonths,
      employerCostsIncomplete: employees.some(
        (worker) => worker.employer_cost_monthly == null,
      ),
      mileageCostIncomplete: mileageRows.some(
        (entry) => entry.calculated_amount == null,
      ),
    };
  };
  const monthCosts = calculateCosts(month, 1),
    elapsedMonths = new Date().getMonth() + 1,
    yearCosts = calculateCosts(year, elapsedMonths),
    result = ca - monthCosts.total,
    annualResult = annualCa - yearCosts.total;
  const todayMissions = data.missions.filter(
    (m) => parisDay(m.starts_at) === today && m.status !== "cancelled",
  );
  const requests = data.requests.filter((r) => r.status === "pending"),
    reports = data.reports.filter((r) => r.status === "pending"),
    hrPending = [...data.times, ...data.leaves, ...data.mileages].filter(
      (x) => x.status === "pending",
    ).length,
    clientContractAlerts = [
      ...(data.clientRequirements || []).filter((item) =>
        ["submitted", "rejected", "expired"].includes(item.status),
      ),
      ...(data.clientContracts || []).filter(
        (item) =>
          item.status === "sent" ||
          (!!item.ends_on &&
            new Date(item.ends_on).getTime() <=
              Date.now() +
                (data.organization?.contract_alert_days ?? 30) * 86400000),
      ),
    ].length,
    workerContractAlerts = [
      ...data.requirements.filter((item) =>
        ["submitted", "rejected", "expired"].includes(item.status),
      ),
      ...data.contracts.filter(
        (item) =>
          item.status === "sent" ||
          (!!item.ends_on &&
            new Date(item.ends_on).getTime() <=
              Date.now() +
                (data.organization?.contract_alert_days ?? 30) * 86400000),
      ),
    ].length,
    profileChanges = (data.profileChangeRequests || []).filter(
      (item) => item.status === "pending",
    ),
    processActions = [...(data.processRequiredActions || [])].sort(
      (left, right) =>
        actionFilter === "open"
          ? new Date(left.due_at).getTime() - new Date(right.due_at).getTime()
          : new Date(right.resolved_at || right.created_at).getTime() -
            new Date(left.resolved_at || left.created_at).getTime(),
    ),
    openProcessActions = processActions.filter((item) =>
      ["open", "in_progress"].includes(item.status),
    ),
    overdueProcessActions = openProcessActions.filter(
      (item) => new Date(item.due_at).getTime() < Date.now(),
    ),
    completedProcessActions = processActions.filter(
      (item) => item.status === "completed",
    ),
    displayedProcessActions =
      actionFilter === "open"
        ? openProcessActions
        : completedProcessActions.slice(0, 20),
    processNotifications = [...(data.processNotificationIntents || [])].sort(
      (left, right) =>
        new Date(right.created_at).getTime() -
        new Date(left.created_at).getTime(),
    ),
    processDeliveries = [...(data.processNotificationDeliveries || [])].sort(
      (left, right) =>
        new Date(right.created_at).getTime() -
        new Date(left.created_at).getTime(),
    ),
    queuedProcessDeliveries = processDeliveries.filter(
      (item) => item.status === "queued",
    ),
    retryProcessDeliveries = processDeliveries.filter(
      (item) => item.status === "retry_scheduled",
    ),
    leasedProcessDeliveries = processDeliveries.filter(
      (item) => item.status === "leased",
    ),
    deadLetterProcessDeliveries = processDeliveries.filter(
      (item) => item.status === "dead_letter",
    ),
    informationNotifications = processNotifications.filter(
      (item) => item.level === "information",
    ),
    alertNotifications = processNotifications.filter(
      (item) => item.level === "alert",
    ),
    displayedProcessNotifications =
      notificationLevel === "information"
        ? informationNotifications
        : alertNotifications;
  async function reviewProfileChange(approve: boolean) {
    if (!selectedChange) return;
    setReviewBusy(true);
    const { error } = await supabase!.rpc("review_profile_change_request", {
      request_id: selectedChange.id,
      approve,
      review_notes: reviewNotes || null,
    });
    setReviewBusy(false);
    setReviewError(error?.message || "");
    if (!error) {
      setSelectedChange(null);
      setReviewNotes("");
      reload();
    }
  }
  const assignmentNames = (m: Mission) =>
    data.assignments
      .filter((a) => a.mission_id === m.id && a.status !== "cancelled")
      .map((a) => {
        const assigned = data.workers.find((w) => w.id === a.worker_id);
        return assigned ? workerName(assigned) : "Personne inconnue";
      })
      .join(", ");
  const visible = (key: DashboardWidgetKey) => prefs[key] !== false;
  const defaultKpiOrder: DashboardWidgetKey[] = [
    "monthly_revenue",
    "monthly_profit",
    "monthly_costs",
    "annual_revenue",
    "annual_profit",
    "annual_costs",
    "collected",
    "bank_balance",
  ];
  const kpiOrder = [
    ...(prefs.order || []).filter((key) => defaultKpiOrder.includes(key)),
    ...defaultKpiOrder.filter((key) => !(prefs.order || []).includes(key)),
  ];
  const displayedCosts =
    costPeriod === "month"
      ? monthCosts
      : costPeriod === "year"
        ? yearCosts
        : null;
  const monthPayments = (data.invoicePayments || []).filter(
      (payment) =>
        payment.received_on.startsWith(month) && payment.status === "confirmed",
    ),
    invoicesForKpi =
      selectedKpi === "monthly_revenue" || selectedKpi === "monthly_profit"
        ? monthInvoices
        : selectedKpi === "annual_revenue" || selectedKpi === "annual_profit"
          ? yearInvoices
          : [],
    missionsForInvoice = (invoiceId: string) =>
      Array.from(
        new Set(
          (data.invoiceLines || [])
            .filter((line) => line.invoice_id === invoiceId && line.mission_id)
            .map((line) => line.mission_id as string),
        ),
      )
        .map((missionId) => data.missions.find((mission) => mission.id === missionId))
        .filter((mission): mission is Mission => Boolean(mission)),
    selectedKpiDetail = (() => {
      switch (selectedKpi) {
        case "monthly_revenue":
          return {
            title: "CA HT du mois",
            value: ca,
            period: month,
            formula:
              "Somme du HT des factures émises sur le mois, moins le HT des avoirs. Les brouillons, annulations et documents déjà crédités sont exclus.",
            sources: `${monthInvoices.length} facture(s) ou avoir(s) issue(s) de Devis & factures.`,
          };
        case "monthly_profit":
          return {
            title: "Marge de gestion provisoire du mois",
            value: result,
            period: month,
            formula: `${money(ca)} de CA HT − ${money(monthCosts.total)} de coûts connus = ${money(result)}.`,
            sources:
              "Factures du mois, dépenses, charges fixes, sous-traitance affectée, coût employeur, tickets, IK validées, coûts dirigeant et commissions d’apporteurs.",
            costs: monthCosts,
          };
        case "annual_revenue":
          return {
            title: `CA HT annuel ${year}`,
            value: annualCa,
            period: year,
            formula:
              "Somme du HT des factures émises depuis le début de l’exercice affiché, moins le HT des avoirs. Les brouillons, annulations et documents déjà crédités sont exclus.",
            sources: `${yearInvoices.length} facture(s) ou avoir(s) issue(s) de Devis & factures.`,
          };
        case "annual_profit":
          return {
            title: `Marge de gestion provisoire annuelle ${year}`,
            value: annualResult,
            period: year,
            formula: `${money(annualCa)} de CA HT − ${money(yearCosts.total)} de coûts connus = ${money(annualResult)}.`,
            sources: `Factures et coûts connus sur ${elapsedMonths} mois. Les coûts manquants ne sont jamais inventés.`,
            costs: yearCosts,
          };
        case "collected":
          return {
            title: "Trésorerie encaissée ce mois",
            value: collected,
            period: month,
            formula:
              "Somme des règlements dont la date de réception appartient au mois et dont l’état est confirmé.",
            sources: `${monthPayments.length} règlement(s) confirmé(s), indépendamment du simple statut affiché sur la facture.`,
            payments: monthPayments,
          };
        case "bank_balance":
          return {
            title: "Solde bancaire renseigné",
            value: data.organization?.current_account_balance ?? null,
            period: data.organization?.current_account_updated_at || "Non daté",
            formula:
              "Dernier solde bancaire saisi ou rapproché dans ARD Manager. Ce montant n’est pas recalculé à partir du CA et ne remplace pas le relevé bancaire.",
            sources: data.organization?.current_account_updated_at
              ? `Valeur mise à jour le ${frDate(data.organization.current_account_updated_at, true)}.`
              : "Aucune date de mise à jour n’est enregistrée.",
          };
        default:
          return null;
      }
    })(),
    selectedKpiCosts =
      selectedKpi === "monthly_profit"
        ? monthCosts
        : selectedKpi === "annual_profit"
          ? yearCosts
          : null;
  const currentMonthIndex = Number(month.slice(5, 7)) - 1;
  const annualOverview = Array.from({ length: 12 }, (_, index) => {
    const key = `${year}-${String(index + 1).padStart(2, "0")}`,
      label = new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
        new Date(Number(year), index, 1),
      ),
      revenue = data.invoices
        .filter(
          (invoice) =>
            (invoice.issue_on || invoice.billing_period || "").startsWith(key) &&
            !["draft", "cancelled", "credited"].includes(invoice.status),
        )
        .reduce(
          (sum, invoice) =>
            sum +
            invoice.total_ex_vat *
              (invoice.document_kind === "credit" ? -1 : 1),
          0,
        ),
      costs = index <= currentMonthIndex ? calculateCosts(key, 1).total : 0;
    return { key, label, revenue, costs, margin: revenue - costs };
  });
  const cashHistoryByMonth = new Map<string, CashBalance>();
  [...(data.cashBalances || [])]
    .filter((item) => item.recorded_at.startsWith(year))
    .sort((left, right) => left.recorded_at.localeCompare(right.recorded_at))
    .forEach((item) => cashHistoryByMonth.set(item.recorded_at.slice(0, 7), item));
  const cashHistory = [...cashHistoryByMonth.values()].slice(-12);
  const cashMin = Math.min(...cashHistory.map((item) => item.balance), 0),
    cashMax = Math.max(...cashHistory.map((item) => item.balance), 1),
    cashRange = Math.max(1, cashMax - cashMin),
    cashCoordinates = cashHistory
      .map((item, index) => {
        const x = cashHistory.length === 1 ? 50 : (index / (cashHistory.length - 1)) * 100,
          y = 94 - ((item.balance - cashMin) / cashRange) * 82;
        return { x, y };
      }),
    cashPath = smoothChartPath(cashCoordinates);
  const annualKnownRevenue = annualOverview.reduce(
      (sum, item) => sum + item.revenue,
      0,
    ),
    annualKnownCosts = annualOverview.reduce(
      (sum, item) => sum + item.costs,
      0,
    ),
    annualKnownMargin = annualKnownRevenue - annualKnownCosts,
    annualMarginRate = annualKnownRevenue
      ? (annualKnownMargin / annualKnownRevenue) * 100
      : null,
    costsCoveredRate = annualKnownCosts
      ? Math.min(999, (annualKnownRevenue / annualKnownCosts) * 100)
      : null,
    cashDelta = cashHistory.length > 1
      ? cashHistory.at(-1)!.balance - cashHistory[0].balance
      : null,
    elapsedOverview = annualOverview.slice(0, Math.max(1, currentMonthIndex + 1)),
    bestMarginMonth = elapsedOverview.reduce(
      (best, item) => (item.margin > best.margin ? item : best),
      elapsedOverview[0],
    );
  const executiveTrendMax = Math.max(
      1,
      ...elapsedOverview.flatMap((item) => [item.revenue, item.costs, item.margin]),
    ),
    executiveTrendMin = Math.min(
      0,
      ...elapsedOverview.flatMap((item) => [item.revenue, item.costs, item.margin]),
    ),
    executiveTrendRange = Math.max(1, executiveTrendMax - executiveTrendMin),
    executiveTrendZeroY = 8 + ((executiveTrendMax / executiveTrendRange) * 84),
    executiveTrendCoordinates = elapsedOverview.map((item, index) => ({
      item,
      x:
        elapsedOverview.length === 1
          ? 50
          : 3 + (index / (elapsedOverview.length - 1)) * 94,
      revenueY: 8 + ((executiveTrendMax - item.revenue) / executiveTrendRange) * 84,
      costsY: 8 + ((executiveTrendMax - item.costs) / executiveTrendRange) * 84,
      resultY: 8 + ((executiveTrendMax - item.margin) / executiveTrendRange) * 84,
    })),
    revenueTrendPath = smoothChartPath(
      executiveTrendCoordinates.map(({ x, revenueY }) => ({ x, y: revenueY })),
    ),
    costsTrendPath = smoothChartPath(
      executiveTrendCoordinates.map(({ x, costsY }) => ({ x, y: costsY })),
    ),
    resultTrendPath = smoothChartPath(
      executiveTrendCoordinates.map(({ x, resultY }) => ({ x, y: resultY })),
    );
  const annualClientRows = data.clients
      .map((client) => {
        const invoices = yearInvoices.filter(
            (invoice) => invoice.client_id === client.id,
          ),
          revenue = invoices.reduce(
            (sum, invoice) =>
              sum +
              Number(invoice.total_ex_vat || 0) *
                (invoice.document_kind === "credit" ? -1 : 1),
            0,
          ),
          invoiced = invoices.reduce(
            (sum, invoice) =>
              sum +
              Number(invoice.total_inc_vat || 0) *
                (invoice.document_kind === "credit" ? -1 : 1),
            0,
          ),
          paid = invoices.reduce(
            (sum, invoice) => sum + Number(invoice.amount_paid || 0),
            0,
          ),
          missions = data.missions.filter(
            (mission) =>
              mission.client_id === client.id &&
              parisDay(mission.starts_at).startsWith(year) &&
              mission.status !== "cancelled",
          ).length;
        return {
          client,
          invoices: invoices.length,
          missions,
          revenue,
          outstanding: Math.max(0, invoiced - paid),
          share: annualKnownRevenue
            ? (revenue / annualKnownRevenue) * 100
            : 0,
        };
      })
      .filter(
        (row) =>
          row.client.active !== false ||
          row.revenue !== 0 ||
          row.invoices > 0 ||
          row.missions > 0,
      )
      .sort((left, right) => right.revenue - left.revenue),
    annualOutstanding = annualClientRows.reduce(
      (sum, row) => sum + row.outstanding,
      0,
    ),
    annualMissions = data.missions.filter(
      (mission) =>
        parisDay(mission.starts_at).startsWith(year) &&
        mission.status !== "cancelled",
    ),
    completedAnnualMissions = annualMissions.filter((mission) =>
      ["completed", "reported", "approved"].includes(
        mission.workflow_status || mission.status,
      ),
    ).length;
  const annualAssignmentCost = (assignment: Assignment) => {
      const worker = data.workers.find((item) => item.id === assignment.worker_id);
      if (!worker) return { cost: 0, missing: true };
      if (worker.kind === "contractor") {
        if (
          assignment.pricing_mode === "monthly" ||
          worker.pricing_mode === "monthly"
        ) {
          const mission = data.missions.find(
              (item) => item.id === assignment.mission_id,
            ),
            monthKey = mission ? parisDay(mission.starts_at).slice(0, 7) : "",
            missionCount = data.assignments.filter((candidate) => {
              if (
                candidate.worker_id !== worker.id ||
                ["cancelled", "refused"].includes(candidate.status)
              ) return false;
              const assignedMission = data.missions.find(
                (item) => item.id === candidate.mission_id,
              );
              return !!assignedMission &&
                assignedMission.status !== "cancelled" &&
                parisDay(assignedMission.starts_at).startsWith(monthKey);
            }).length;
          const monthlyAmount = worker.monthly_fee ?? assignment.agreed_amount;
          return {
            cost: Number(monthlyAmount || 0) / Math.max(1, missionCount),
            missing: monthlyAmount == null,
          };
        }
        if (assignment.agreed_amount != null) {
          return { cost: Number(assignment.agreed_amount), missing: false };
        }
        if (
          assignment.pricing_mode === "fixed" ||
          worker.pricing_mode === "fixed"
        ) {
          const fixedRate = assignment.agreed_rate ?? worker.fixed_fee;
          return { cost: Number(fixedRate || 0), missing: fixedRate == null };
        }
        const hourlyRate = assignment.agreed_rate ?? worker.hourly_rate;
        return {
          cost:
            Number(assignment.assigned_hours || 0) * Number(hourlyRate || 0),
          missing: hourlyRate == null,
        };
      }
      const monthlyHours = Number(worker.monthly_hours || 0),
        employerCost = worker.employer_cost_monthly,
        ticketCost =
          Number(worker.ticket_count || 0) * Number(worker.ticket_value || 0),
        hourlyKnownCost = monthlyHours > 0
          ? (Number(employerCost ?? worker.net_salary ?? 0) + ticketCost) /
            monthlyHours
          : 0;
      return {
        cost: Number(assignment.assigned_hours || 0) * hourlyKnownCost,
        missing: monthlyHours <= 0 || employerCost == null,
      };
    },
    annualMissionPerformanceRows = annualMissions.map((mission) => {
      const client = data.clients.find((item) => item.id === mission.client_id),
        propertyService = data.propertyServices.find(
          (item) => item.id === mission.property_service_id,
        ),
        service = data.services.find((item) => item.id === mission.service_id),
        assignmentCosts = data.assignments
          .filter(
            (assignment) =>
              assignment.mission_id === mission.id &&
              !["cancelled", "refused"].includes(assignment.status),
          )
          .map(annualAssignmentCost),
        laborCost = assignmentCosts.reduce((sum, row) => sum + row.cost, 0),
        expenses = data.expenses.filter(
          (expense) =>
            expense.mission_id === mission.id &&
            !["cancelled", "rejected"].includes(expense.status),
        ),
        directExpenseCost = expenses.reduce(
          (sum, expense) => sum + Number(expense.amount_ex_vat || 0),
          0,
        ),
        mileages = data.mileages.filter(
          (entry) => entry.mission_id === mission.id && entry.status === "approved",
        ),
        mileageCost = mileages.reduce(
          (sum, entry) => sum + Number(entry.calculated_amount || 0),
          0,
        ),
        stockMovements = (data.inventoryMovements || []).filter(
          (movement) =>
            movement.mission_id === mission.id &&
            movement.movement_type === "usage",
        ),
        stockCost = stockMovements.reduce(
          (sum, movement) =>
            sum + Number(movement.quantity || 0) * Number(movement.unit_cost || 0),
          0,
        ),
        invoiceRevenue = (data.invoiceLines || [])
          .filter((line) => line.mission_id === mission.id)
          .reduce(
            (sum, line) =>
              sum + Number(line.quantity || 0) * Number(line.unit_price_ex_vat || 0),
            0,
          ),
        referralCost = (data.invoiceLines || [])
          .filter((line) => line.mission_id === mission.id)
          .reduce((sum, line) => {
            const referral = (data.invoiceReferralCommissions || []).find(
              (item) =>
                item.invoice_id === line.invoice_id &&
                !["waived", "cancelled"].includes(item.status),
            );
            if (!referral) return sum;
            const lineRevenue =
              Number(line.quantity || 0) * Number(line.unit_price_ex_vat || 0);
            return sum + (lineRevenue * Number(referral.commission_rate || 0)) / 100;
          }, 0),
        revenue = Number(mission.amount_ex_vat || invoiceRevenue || 0),
        cost =
          laborCost + directExpenseCost + mileageCost + stockCost + referralCost,
        missingCost =
          assignmentCosts.some((row) => row.missing) ||
          mileages.some((entry) => entry.calculated_amount == null) ||
          stockMovements.some((movement) => movement.unit_cost == null);
      return {
        id: mission.id,
        clientId: mission.client_id,
        clientName:
          client?.name || mission.client_display_name || "Client non identifié",
        serviceId:
          mission.property_service_id ||
          mission.service_id ||
          "unclassified-service",
        serviceName:
          propertyService?.name || service?.name || "Prestation non renseignée",
        revenue,
        cost,
        margin: revenue - cost,
        marginRate: revenue > 0 ? ((revenue - cost) / revenue) * 100 : null,
        missingCost,
      };
    });
  const aggregateAnnualContributions = (
    keyOf: (row: (typeof annualMissionPerformanceRows)[number]) => string,
    labelOf: (row: (typeof annualMissionPerformanceRows)[number]) => string,
  ) => {
    const groups = new Map<
      string,
      {
        id: string;
        label: string;
        missions: number;
        revenue: number;
        cost: number;
        margin: number;
        marginRate: number | null;
        missingCost: boolean;
      }
    >();
    annualMissionPerformanceRows.forEach((row) => {
      const id = keyOf(row),
        current = groups.get(id) || {
          id,
          label: labelOf(row),
          missions: 0,
          revenue: 0,
          cost: 0,
          margin: 0,
          marginRate: null,
          missingCost: false,
        };
      current.missions += 1;
      current.revenue += row.revenue;
      current.cost += row.cost;
      current.margin += row.margin;
      current.marginRate = current.revenue > 0
        ? (current.margin / current.revenue) * 100
        : null;
      current.missingCost = current.missingCost || row.missingCost;
      groups.set(id, current);
    });
    return Array.from(groups.values());
  };
  const annualClientContributionRows = aggregateAnnualContributions(
      (row) => row.clientId,
      (row) => row.clientName,
    ),
    annualServiceContributionRows = aggregateAnnualContributions(
      (row) => row.serviceId,
      (row) => row.serviceName,
    ),
    bestClientContributions = [...annualClientContributionRows]
      .sort((left, right) => right.margin - left.margin)
      .slice(0, 5),
    weakestClientContributions = [...annualClientContributionRows]
      .sort((left, right) => left.margin - right.margin)
      .slice(0, 5),
    bestServiceContributions = [...annualServiceContributionRows]
      .sort((left, right) => right.margin - left.margin)
      .slice(0, 5),
    weakestServiceContributions = [...annualServiceContributionRows]
      .sort((left, right) => left.margin - right.margin)
      .slice(0, 5);
  const activeInventoryItems = (data.inventoryItems || []).filter(
      (item) => item.active,
    ),
    inventoryBookValue = activeInventoryItems.reduce(
      (sum, item) =>
        sum + Number(item.current_quantity || 0) * Number(item.average_unit_cost || 0),
      0,
    ),
    lowStockItems = activeInventoryItems.filter(
      (item) => Number(item.current_quantity || 0) <= Number(item.minimum_quantity || 0),
    ),
    annualStockRows = activeInventoryItems.map((item) => {
      const movements = (data.inventoryMovements || []).filter(
          (movement) =>
            movement.item_id === item.id && movement.occurred_on.startsWith(year),
        ),
        purchases = movements.filter((movement) => movement.movement_type === "purchase"),
        usages = movements.filter((movement) => movement.movement_type === "usage"),
        billableUsages = (data.missionStockUsages || []).filter((usage) => {
          if (usage.item_id !== item.id || !usage.billable) return false;
          const mission = data.missions.find((candidate) => candidate.id === usage.mission_id),
            activityDate = mission ? parisDay(mission.starts_at) : usage.created_at.slice(0, 10);
          return activityDate.startsWith(year);
        }),
        purchaseQuantity = purchases.reduce(
          (sum, movement) => sum + Number(movement.quantity || 0),
          0,
        ),
        purchaseAmount = purchases.reduce(
          (sum, movement) =>
            sum + Number(movement.quantity || 0) * Number(movement.unit_cost || 0),
          0,
        ),
        usedQuantity = usages.reduce(
          (sum, movement) => sum + Number(movement.quantity || 0),
          0,
        ),
        usedCost = usages.reduce(
          (sum, movement) =>
            sum + Number(movement.quantity || 0) * Number(movement.unit_cost || 0),
          0,
        ),
        soldQuantity = billableUsages.reduce(
          (sum, usage) => sum + Number(usage.quantity || 0),
          0,
        ),
        soldRevenue = billableUsages.reduce(
          (sum, usage) =>
            sum + Number(usage.quantity || 0) * Number(usage.unit_price_ex_vat || 0),
          0,
        ),
        estimatedSoldCost =
          soldQuantity * Number(item.average_unit_cost || 0);
      return {
        item,
        stockValue:
          Number(item.current_quantity || 0) * Number(item.average_unit_cost || 0),
        purchaseQuantity,
        purchaseAmount,
        usedQuantity,
        usedCost,
        soldQuantity,
        soldRevenue,
        estimatedSoldCost,
        estimatedSoldMargin: soldRevenue - estimatedSoldCost,
      };
    }),
    topInventoryValues = [...annualStockRows]
      .sort((left, right) => right.stockValue - left.stockValue)
      .slice(0, 8),
    topPurchasedStock = [...annualStockRows]
      .filter((row) => row.purchaseAmount > 0)
      .sort((left, right) => right.purchaseAmount - left.purchaseAmount)
      .slice(0, 5),
    topSoldStock = [...annualStockRows]
      .filter((row) => row.soldRevenue > 0)
      .sort((left, right) => right.soldRevenue - left.soldRevenue)
      .slice(0, 5),
    weakestSoldStock = [...annualStockRows]
      .filter((row) => row.soldRevenue > 0)
      .sort((left, right) => left.estimatedSoldMargin - right.estimatedSoldMargin)
      .slice(0, 5);
  const annualExpenseCategoryMap = new Map<string, number>();
  yearCosts.operatingRows.forEach((expense) => {
    const label = expense.category || "Autres achats et frais";
    annualExpenseCategoryMap.set(
      label,
      (annualExpenseCategoryMap.get(label) || 0) + Number(expense.amount_ex_vat || 0),
    );
  });
  if (yearCosts.referralCommissions > 0) {
    annualExpenseCategoryMap.set(
      "Commissions d’apporteurs",
      yearCosts.referralCommissions,
    );
  }
  const annualExpenseCategories = Array.from(annualExpenseCategoryMap.entries())
    .map(([label, amount]) => ({ label, amount }))
    .sort((left, right) => right.amount - left.amount)
    .slice(0, 8);
  function printAnnualBusinessReport() {
    const organizationName = data.organization?.name || "ARD Manager",
      generatedAt = new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "long",
        timeStyle: "short",
        timeZone: "Europe/Paris",
      }).format(new Date()),
      dataWarnings = [
        yearCosts.employerCostsIncomplete
          ? "Certains coûts employeur sont incomplets : la marge est susceptible d’être surestimée."
          : null,
        yearCosts.mileageCostIncomplete
          ? "Certaines indemnités kilométriques ne disposent pas encore d’un coût calculé."
          : null,
        data.organization?.current_account_balance == null
          ? "Le solde bancaire n’est pas renseigné."
          : null,
        annualMissionPerformanceRows.some((row) => row.missingCost)
          ? "Certaines marges par client ou prestation reposent sur des coûts directs incomplets. Les lignes concernées sont signalées."
          : null,
        activeInventoryItems.some(
          (item) => Number(item.current_quantity || 0) > 0 && !Number(item.average_unit_cost || 0),
        )
          ? "Certains articles en stock n’ont pas de coût moyen : la valorisation du stock est incomplète."
          : null,
        topSoldStock.length
          ? "La marge des produits facturables est une estimation fondée sur le coût moyen actuel du stock ; elle doit être rapprochée des écritures comptables."
          : null,
        openProcessActions.length
          ? `${openProcessActions.length} action(s) métier restent à traiter, dont ${overdueProcessActions.length} en retard.`
          : null,
      ].filter(Boolean),
      contributionRowsHtml = (
        rows: typeof bestClientContributions,
        emptyLabel: string,
      ) => rows.length
        ? rows.map((row) => `<tr><td><strong>${escapeFinancialHtml(row.label)}</strong></td><td>${row.missions}</td><td>${escapeFinancialHtml(money(row.revenue))}</td><td>${escapeFinancialHtml(money(row.cost))}</td><td>${escapeFinancialHtml(money(row.margin))}</td><td>${row.marginRate == null ? "—" : `${escapeFinancialHtml(row.marginRate.toLocaleString("fr-FR", { maximumFractionDigits: 1 }))} %`}</td><td>${row.missingCost ? "Coûts incomplets" : "Données connues complètes"}</td></tr>`).join("")
        : `<tr><td colspan="7">${escapeFinancialHtml(emptyLabel)}</td></tr>`,
      stockRowsHtml = (
        rows: typeof topInventoryValues,
        mode: "inventory" | "purchases" | "sales",
      ) => rows.length
        ? rows.map((row) => mode === "inventory"
          ? `<tr><td><strong>${escapeFinancialHtml(row.item.name)}</strong></td><td>${escapeFinancialHtml(String(row.item.current_quantity))} ${escapeFinancialHtml(row.item.unit)}</td><td>${escapeFinancialHtml(money(row.item.average_unit_cost))}</td><td>${escapeFinancialHtml(money(row.stockValue))}</td><td>${Number(row.item.current_quantity || 0) <= Number(row.item.minimum_quantity || 0) ? "Seuil atteint" : "Disponible"}</td></tr>`
          : mode === "purchases"
            ? `<tr><td><strong>${escapeFinancialHtml(row.item.name)}</strong></td><td>${escapeFinancialHtml(row.purchaseQuantity.toLocaleString("fr-FR", { maximumFractionDigits: 2 }))} ${escapeFinancialHtml(row.item.unit)}</td><td>${escapeFinancialHtml(money(row.purchaseAmount))}</td></tr>`
            : `<tr><td><strong>${escapeFinancialHtml(row.item.name)}</strong></td><td>${escapeFinancialHtml(row.soldQuantity.toLocaleString("fr-FR", { maximumFractionDigits: 2 }))} ${escapeFinancialHtml(row.item.unit)}</td><td>${escapeFinancialHtml(money(row.soldRevenue))}</td><td>${escapeFinancialHtml(money(row.estimatedSoldCost))}</td><td>${escapeFinancialHtml(money(row.estimatedSoldMargin))}</td></tr>`).join("")
        : `<tr><td colspan="5">Aucune donnée disponible sur ${year}.</td></tr>`,
      annualReportDetails = `<style>.pair-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px}.rank-card{padding:10px;border:1px solid #e2e4e8;border-radius:12px;break-inside:avoid}.rank-card h3{margin:0 0 7px;font-size:13px}.rank-card table{font-size:9px}.rank-card th,.rank-card td{padding:5px 4px}.subtle{color:#666;font-size:9px}.section-title{display:flex;align-items:end;justify-content:space-between;gap:12px}.section-title p{margin:0;color:#666;font-size:9px}@media print{.pair-grid{break-inside:avoid}.page-break{break-before:page}}</style><section class="section"><div class="section-title"><h2>Rentabilité contributive : meilleurs résultats et points faibles</h2><p>CA des missions moins coûts directs connus ; hors répartition arbitraire des frais généraux.</p></div><div class="pair-grid"><article class="rank-card"><h3>Top clients par marge connue</h3><table><thead><tr><th>Client</th><th>Missions</th><th>CA</th><th>Coûts</th><th>Marge</th><th>Taux</th><th>Qualité</th></tr></thead><tbody>${contributionRowsHtml(bestClientContributions, "Aucun client analysable.")}</tbody></table></article><article class="rank-card"><h3>Clients les moins contributeurs</h3><table><thead><tr><th>Client</th><th>Missions</th><th>CA</th><th>Coûts</th><th>Marge</th><th>Taux</th><th>Qualité</th></tr></thead><tbody>${contributionRowsHtml(weakestClientContributions, "Aucun client analysable.")}</tbody></table></article><article class="rank-card"><h3>Top prestations par marge connue</h3><table><thead><tr><th>Prestation</th><th>Missions</th><th>CA</th><th>Coûts</th><th>Marge</th><th>Taux</th><th>Qualité</th></tr></thead><tbody>${contributionRowsHtml(bestServiceContributions, "Aucune prestation analysable.")}</tbody></table></article><article class="rank-card"><h3>Prestations les moins contributrices</h3><table><thead><tr><th>Prestation</th><th>Missions</th><th>CA</th><th>Coûts</th><th>Marge</th><th>Taux</th><th>Qualité</th></tr></thead><tbody>${contributionRowsHtml(weakestServiceContributions, "Aucune prestation analysable.")}</tbody></table></article></div></section><section class="section page-break"><div class="section-title"><h2>État des stocks et achats</h2><p>Quantités présentes à la date du document et mouvements enregistrés sur ${year}.</p></div><div class="kpis"><div><span>Articles actifs</span><strong>${activeInventoryItems.length}</strong></div><div><span>Valeur de stock connue</span><strong>${escapeFinancialHtml(money(inventoryBookValue))}</strong></div><div><span>Seuil bas atteint</span><strong>${lowStockItems.length}</strong></div><div><span>Achats et frais saisis</span><strong>${escapeFinancialHtml(money(yearCosts.operating))}</strong></div></div><article class="rank-card"><h3>Stock présent : principales valeurs immobilisées</h3><table><thead><tr><th>Article</th><th>Quantité</th><th>Coût moyen</th><th>Valeur</th><th>État</th></tr></thead><tbody>${stockRowsHtml(topInventoryValues, "inventory")}</tbody></table></article><div class="pair-grid"><article class="rank-card"><h3>Top achats de stock</h3><table><thead><tr><th>Article</th><th>Quantité achetée</th><th>Montant HT</th></tr></thead><tbody>${stockRowsHtml(topPurchasedStock, "purchases")}</tbody></table></article><article class="rank-card"><h3>Principaux achats et frais par catégorie</h3><table><thead><tr><th>Catégorie</th><th>Montant HT</th></tr></thead><tbody>${annualExpenseCategories.map((row) => `<tr><td><strong>${escapeFinancialHtml(row.label)}</strong></td><td>${escapeFinancialHtml(money(row.amount))}</td></tr>`).join("") || `<tr><td colspan="2">Aucun achat ou frais enregistré sur ${year}.</td></tr>`}</tbody></table></article></div></section><section class="section"><div class="section-title"><h2>Produits facturables : ventes et marge estimée</h2><p>Calcul fondé sur les consommations déclarées facturables et le coût moyen actuel.</p></div><div class="pair-grid"><article class="rank-card"><h3>Produits les plus vendus</h3><table><thead><tr><th>Produit</th><th>Quantité</th><th>CA HT</th><th>Coût estimé</th><th>Marge estimée</th></tr></thead><tbody>${stockRowsHtml(topSoldStock, "sales")}</tbody></table></article><article class="rank-card"><h3>Produits les moins rentables</h3><table><thead><tr><th>Produit</th><th>Quantité</th><th>CA HT</th><th>Coût estimé</th><th>Marge estimée</th></tr></thead><tbody>${stockRowsHtml(weakestSoldStock, "sales")}</tbody></table></article></div><p class="subtle">Une absence de ligne ne signifie pas une absence de vente comptable : elle signifie qu’aucune consommation de stock facturable exploitable n’a été enregistrée dans ARD Manager pour la période.</p></section>`,
      popup = window.open("", "_blank");
    if (!popup) return;
    popup.opener = null;
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>État annuel ${escapeFinancialHtml(organizationName)} ${year}</title><style>@page{size:A4;margin:13mm}*{box-sizing:border-box}body{margin:0;color:#191b20;font:11px Arial,sans-serif}header{display:flex;justify-content:space-between;gap:24px;padding:20px;border-radius:16px;background:#191b20;color:white}header b{color:#c6a15b;letter-spacing:.08em}header h1{margin:5px 0 4px;font-size:23px}header p{margin:3px 0;color:#dfe1e5}.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:16px 0}.kpis div{padding:11px;border:1px solid #d8c28f;border-radius:11px;background:#fffdf7}.kpis span{display:block;color:#666}.kpis strong{display:block;margin-top:4px;font-size:16px}.chart{padding:12px;border:1px solid #eadfca;border-radius:14px;background:#fffdf7;break-inside:avoid}.legend{display:flex;gap:16px;margin-bottom:6px}.legend i{display:inline-block;width:18px;height:3px;margin-right:5px;border-radius:4px;vertical-align:middle}.monthly{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-top:8px}.monthly div{padding:7px;border-radius:8px;background:#f5f1e8}.monthly strong,.monthly span{display:block}.monthly span{color:#666}.section{margin-top:17px;break-inside:avoid}.section h2{margin:0 0 7px;font-size:16px}table{width:100%;border-collapse:collapse}th,td{padding:7px 6px;border-bottom:1px solid #e2e4e8;text-align:right}th:first-child,td:first-child{text-align:left}thead{background:#f5f1e8}.warnings{padding:12px;border-radius:12px;background:#fff8e7;border:1px solid #e8c56e}.warnings li{margin:4px 0}.notice{margin-top:17px;padding-top:10px;border-top:1px solid #ddd;color:#666;font-size:10px}footer{margin-top:18px;color:#777;font-size:9px;text-align:center}</style></head><body><header><div><b>${escapeFinancialHtml(organizationName)}</b><h1>État annuel de l’entreprise</h1><p>Situation de pilotage ${year} arrêtée au ${escapeFinancialHtml(generatedAt)}</p></div><div><strong>Document confidentiel</strong><p>Préparé depuis ARD Manager</p></div></header><div class="kpis"><div><span>CA facturé HT</span><strong>${escapeFinancialHtml(money(annualKnownRevenue))}</strong></div><div><span>Coûts connus</span><strong>${escapeFinancialHtml(money(annualKnownCosts))}</strong></div><div><span>Résultat provisoire</span><strong>${escapeFinancialHtml(money(annualKnownMargin))}</strong><small>${annualMarginRate == null ? "Taux indisponible" : `${escapeFinancialHtml(annualMarginRate.toLocaleString("fr-FR", { maximumFractionDigits: 1 }))} % du CA`}</small></div><div><span>Trésorerie disponible</span><strong>${data.organization?.current_account_balance == null ? "À renseigner" : escapeFinancialHtml(money(data.organization.current_account_balance))}</strong></div></div><section class="chart"><div class="legend"><span><i style="background:#c6a15b"></i>CA facturé</span><span><i style="background:#4f6575"></i>Coûts connus</span><span><i style="background:#2f8f63"></i>Résultat</span></div><svg viewBox="0 0 100 100" width="100%" height="170" preserveAspectRatio="none"><line x1="0" x2="100" y1="${escapeFinancialHtml(executiveTrendZeroY)}" y2="${escapeFinancialHtml(executiveTrendZeroY)}" stroke="#d8dbe0" stroke-dasharray="3 3" vector-effect="non-scaling-stroke"/><path d="${escapeFinancialHtml(revenueTrendPath)}" fill="none" stroke="#c6a15b" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/><path d="${escapeFinancialHtml(costsTrendPath)}" fill="none" stroke="#4f6575" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/><path d="${escapeFinancialHtml(resultTrendPath)}" fill="none" stroke="#2f8f63" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/></svg><div class="monthly">${elapsedOverview.map((item) => `<div><strong>${escapeFinancialHtml(item.label.replace(".", ""))}</strong><span>CA ${escapeFinancialHtml(compactMoney(item.revenue))}</span><span>Coûts ${escapeFinancialHtml(compactMoney(item.costs))}</span><span>Résultat ${escapeFinancialHtml(compactMoney(item.margin))}</span></div>`).join("")}</div></section><section class="section"><h2>Activité et portefeuille clients</h2><div class="kpis"><div><span>Clients suivis</span><strong>${annualClientRows.length}</strong></div><div><span>Missions de l’année</span><strong>${annualMissions.length}</strong></div><div><span>Missions finalisées</span><strong>${completedAnnualMissions}</strong></div><div><span>Reste à encaisser</span><strong>${escapeFinancialHtml(money(annualOutstanding))}</strong></div></div><table><thead><tr><th>Client</th><th>CA HT</th><th>Part du CA</th><th>Factures</th><th>Missions</th><th>Reste dû TTC</th></tr></thead><tbody>${annualClientRows.map((row) => `<tr><td><strong>${escapeFinancialHtml(row.client.name)}</strong></td><td>${escapeFinancialHtml(money(row.revenue))}</td><td>${escapeFinancialHtml(row.share.toLocaleString("fr-FR", { maximumFractionDigits: 1 }))} %</td><td>${row.invoices}</td><td>${row.missions}</td><td>${escapeFinancialHtml(money(row.outstanding))}</td></tr>`).join("") || `<tr><td colspan="6">Aucune activité client enregistrée sur ${year}.</td></tr>`}</tbody></table></section><section class="section warnings"><h2>Qualité des données et points d’attention</h2>${dataWarnings.length ? `<ul>${dataWarnings.map((warning) => `<li>${escapeFinancialHtml(warning)}</li>`).join("")}</ul>` : `<p>Aucune réserve automatique détectée sur les données actuellement disponibles.</p>`}</section><p class="notice">Ce document est un état de pilotage interne produit à partir des données connues dans ARD Manager. Le résultat présenté est provisoire : il ne constitue ni un audit légal, ni des comptes certifiés, ni une valorisation de l’entreprise. Toute transmission à un investisseur, une banque ou un tiers doit être accompagnée des pièces comptables officielles et, si nécessaire, d’une validation par l’expert-comptable.</p><footer>${escapeFinancialHtml(organizationName)} · État annuel ${year} · Généré le ${escapeFinancialHtml(generatedAt)}</footer><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),300));<\/script></body></html>`);
    popup.document
      .querySelector(".warnings")
      ?.insertAdjacentHTML("beforebegin", annualReportDetails);
    popup.document.close();
  }
  return (
    <div className="admin-dashboard">
      <div className="page-title">
        <div>
          <span className="eyebrow">Centre de contrôle dirigeant</span>
          <h1>Vue d’ensemble de l’entreprise</h1>
        </div>
        <div className="inline-actions">
          <button className="secondary" onClick={() => onNavigate("agenda")}>
            Ouvrir l’agenda
          </button>
          <button className="secondary" onClick={printAnnualBusinessReport}>
            Exporter l’état annuel (PDF)
          </button>
          <button className="secondary" onClick={() => onNavigate("settings")}>
            Configurer
          </button>
        </div>
      </div>
      <EmergencyOperationsCenter
        profile={profile}
        data={data}
        mode="priority"
        onOpenMissions={() => onNavigate("missions")}
      />
      {actionPlanHeadline && (
        <section className="panel dashboard-action-plan-pulse" data-testid="dashboard-action-plan-pulse">
          <div className="dashboard-action-plan-head">
            <div>
              <span className="eyebrow">Plan d’action 4 mois</span>
              <h2>Les décisions à garder en vue</h2>
              <p>Les mesures sont actualisées automatiquement ; les décisions et validations restent humaines.</p>
            </div>
            <button type="button" onClick={() => onNavigate("action_plan")}>Ouvrir le plan d’action</button>
          </div>
          <div className="dashboard-action-plan-metrics">
            <article><span>Attentions ouvertes</span><strong>{actionPlanHeadline.openAttention}</strong></article>
            <article><span>Actions en retard</span><strong>{actionPlanHeadline.overdue}</strong></article>
            <article><span>Revues à préparer</span><strong>{actionPlanHeadline.reviews}</strong></article>
            <article><span>Actions terminées</span><strong>{actionPlanHeadline.completed}/{actionPlanHeadline.total}</strong></article>
          </div>
          <small>Prochaine analyse : {actionPlanHeadline.nextScan}</small>
        </section>
      )}
      <div className="stats admin-kpis">
        {kpiOrder.map((key) => {
          if (!visible(key)) return null;
          if (key === "monthly_revenue")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label="Expliquer le CA HT du mois">
              <span>CA HT du mois</span>
              <strong>{money(ca)}</strong>
              <small>{monthInvoices.length} facture(s) · Voir le calcul ›</small>
            </button>
          </article>
            );
          if (key === "monthly_profit")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label="Expliquer la marge de gestion du mois">
              <span>Marge de gestion provisoire du mois</span>
              <strong className={result < 0 ? "negative" : ""}>
                {money(result)}
              </strong>
              <small>CA HT − coûts connus · Voir les sources ›</small>
            </button>
          </article>
            );
          if (key === "monthly_costs")
            return (
              <article key={key} className="clickable-kpi">
                <button onClick={() => setCostPeriod("month")}>
                  <span>Coûts connus du mois</span>
                  <strong>{money(monthCosts.total)}</strong>
                  <small>Ouvrir le détail par nature ›</small>
                </button>
              </article>
            );
          if (key === "annual_revenue")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label={`Expliquer le CA HT annuel ${year}`}>
              <span>CA HT annuel</span>
              <strong>{money(annualCa)}</strong>
              <small>{yearInvoices.length} facture(s) · Voir le calcul ›</small>
            </button>
          </article>
            );
          if (key === "annual_profit")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label={`Expliquer la marge de gestion annuelle ${year}`}>
              <span>Marge de gestion provisoire annuelle</span>
              <strong className={annualResult < 0 ? "negative" : ""}>
                {money(annualResult)}
              </strong>
              <small>CA HT − coûts connus · Voir les sources ›</small>
            </button>
          </article>
            );
          if (key === "annual_costs")
            return (
              <article key={key} className="clickable-kpi">
                <button onClick={() => setCostPeriod("year")}>
                  <span>Coûts connus sur {year}</span>
                  <strong>{money(yearCosts.total)}</strong>
                  <small>Ouvrir le détail par nature ›</small>
                </button>
              </article>
            );
          if (key === "collected")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label="Expliquer la trésorerie encaissée ce mois">
              <span>Trésorerie encaissée ce mois</span>
              <strong>{money(collected)}</strong>
              <small>Règlements confirmés · Voir le détail ›</small>
            </button>
          </article>
            );
          if (key === "bank_balance")
            return (
          <article key={key} className="clickable-kpi">
            <button onClick={() => setSelectedKpi(key)} aria-label="Expliquer le solde bancaire">
              <span>Solde bancaire</span>
              <strong>
                {data.organization?.current_account_balance == null
                  ? "À renseigner"
                  : money(data.organization.current_account_balance)}
              </strong>
              <small>
                {data.organization?.current_account_updated_at
                  ? `Mis à jour le ${frDate(data.organization.current_account_updated_at)} · Voir la source ›`
                  : "À gérer dans Comptabilité · Voir la source ›"}
              </small>
            </button>
          </article>
            );
          return null;
        })}
      </div>
      <section className="panel executive-charts">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Tableau de bord dirigeant</span>
            <h2>Ce qu’il faut retenir en {year}</h2>
            <p>
              Des montants lisibles, leur évolution et les écarts à surveiller.
              Le facturé, les coûts calculés et l’argent disponible restent
              volontairement séparés.
            </p>
          </div>
          <div className="executive-chart-actions">
            <button className="secondary compact" onClick={() => onNavigate("profitability")}>
              Analyser la rentabilité
            </button>
            <button className="secondary compact" onClick={() => onNavigate("accounting")}>
              Ouvrir la comptabilité
            </button>
          </div>
        </div>
        <div className="executive-summary-strip">
          <article>
            <span>CA facturé HT</span>
            <strong>{money(annualKnownRevenue)}</strong>
            <small>{yearInvoices.length} facture(s) sur {year}</small>
          </article>
          <article>
            <span>Coûts connus</span>
            <strong>{money(annualKnownCosts)}</strong>
            <small>
              {costsCoveredRate == null
                ? "Aucun coût renseigné"
                : `${costsCoveredRate.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} % couverts par le CA`}
            </small>
          </article>
          <article className={annualKnownMargin < 0 ? "is-negative" : "is-positive"}>
            <span>Résultat de gestion provisoire</span>
            <strong>{money(annualKnownMargin)}</strong>
            <small>
              {annualMarginRate == null
                ? "Taux indisponible sans CA"
                : `${annualMarginRate.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} % du CA`}
            </small>
          </article>
          <article>
            <span>Trésorerie disponible</span>
            <strong>
              {data.organization?.current_account_balance == null
                ? "À renseigner"
                : money(data.organization.current_account_balance)}
            </strong>
            <small>
              {cashDelta == null
                ? "Un second relevé permettra de voir la tendance"
                : `${cashDelta >= 0 ? "+" : ""}${money(cashDelta)} depuis le premier relevé affiché`}
            </small>
          </article>
        </div>
        <div className={`executive-insight ${annualKnownMargin < 0 ? "is-warning" : "is-success"}`}>
          <div>
            <span className="eyebrow">À surveiller maintenant</span>
            <strong>
              {annualKnownMargin < 0
                ? `Les coûts connus dépassent le CA facturé de ${money(Math.abs(annualKnownMargin))}.`
                : `Le CA facturé couvre les coûts connus avec ${money(annualKnownMargin)} de résultat provisoire.`}
            </strong>
          </div>
          <p>
            Meilleur mois provisoire : <b>{bestMarginMonth.label.replace(".", "")} · {money(bestMarginMonth.margin)}</b>.
            Les coûts incomplets ou non rapprochés restent à contrôler avant décision.
          </p>
        </div>
        <div className="executive-chart-grid">
          <article className="executive-chart-card">
            <div className="executive-chart-card-head">
              <div>
                <h3>Activité et résultat mois par mois</h3>
                <small>Montants HT · les valeurs exactes sont écrites sous chaque mois</small>
              </div>
              <div className="executive-chart-legend" aria-label="Légende du graphique">
                <span><i className="revenue" /> CA facturé</span>
                <span><i className="costs" /> Coûts connus</span>
                <span><i className="result" /> Résultat (CA − coûts)</span>
              </div>
            </div>
            <div className="executive-three-lines-layout">
              <div className="annual-performance-scale" aria-hidden="true">
                <span>{compactMoney(executiveTrendMax)}</span>
                {executiveTrendMin < 0 && executiveTrendMax > 0 ? <span>0 €</span> : null}
                <span>{compactMoney(executiveTrendMin)}</span>
              </div>
              <div className="executive-three-lines-plot">
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label={`Courbes du chiffre d’affaires, des coûts connus et du résultat provisoire de janvier à ${elapsedOverview.at(-1)!.label.replace(".", "")} ${year}`}
                  >
                    <line className="trend-zero-line" x1="0" x2="100" y1={executiveTrendZeroY} y2={executiveTrendZeroY} vectorEffect="non-scaling-stroke" />
                    <path className="revenue-line" d={revenueTrendPath} vectorEffect="non-scaling-stroke" />
                    <path className="costs-line" d={costsTrendPath} vectorEffect="non-scaling-stroke" />
                    <path className="result-line" d={resultTrendPath} vectorEffect="non-scaling-stroke" />
                  </svg>
                  <div
                    className="executive-trend-months"
                    style={{ gridTemplateColumns: `repeat(${elapsedOverview.length}, minmax(30px, 1fr))` }}
                  >
                    {elapsedOverview.map((item) => (
                      <span className={item.key === month ? "current" : ""} key={item.key}>
                        {item.label.replace(".", "")}
                      </span>
                    ))}
                  </div>
              </div>
            </div>
            <div
              className="executive-month-values"
              aria-label="Montants mensuels détaillés"
            >
              {annualOverview.map((item, index) => (
                <article className={`${item.key === month ? "current" : ""}${index > currentMonthIndex ? " future" : ""}`} key={item.key}>
                  <h4>{item.label.replace(".", "")}</h4>
                  {index > currentMonthIndex ? (
                    <span className="future-label">À venir</span>
                  ) : (
                    <>
                      <span className="revenue"><b>CA</b>{compactMoney(item.revenue)}</span>
                      <span className="costs"><b>Coûts</b>{compactMoney(item.costs)}</span>
                      <span className={item.margin < 0 ? "result-negative" : "result-positive"}><b>Résultat</b>{compactMoney(item.margin)}</span>
                    </>
                  )}
                </article>
              ))}
            </div>
          </article>
          <article className="executive-chart-card">
            <h3>Trésorerie réellement disponible</h3>
            <small>Soldes bancaires saisis ou rapprochés · ce n’est pas le CA</small>
            <div className="cash-current-value">
              <span>Dernier solde renseigné</span>
              <strong>
                {data.organization?.current_account_balance == null
                  ? "À renseigner"
                  : money(data.organization.current_account_balance)}
              </strong>
            </div>
            {cashHistory.length ? (
              <div className="cash-history-chart">
                <div className="cash-chart-layout">
                  <div className="cash-chart-scale" aria-hidden="true">
                    <span>{compactMoney(cashMax)}</span>
                    <span>{compactMoney(cashMin + cashRange / 2)}</span>
                    <span>{compactMoney(cashMin)}</span>
                  </div>
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Évolution des soldes de trésorerie saisis">
                    <path className="cash-line" d={cashPath} vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
                <div className="cash-point-values" aria-label="Montants de trésorerie par relevé">
                  {cashHistory.map((item) => (
                    <span key={item.id} title={`${frDate(item.recorded_at)} · ${money(item.balance)}`}>
                      <b>{new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(new Date(item.recorded_at)).replace(".", "")}</b>
                      <strong>{compactMoney(item.balance)}</strong>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="empty">Le graphique apparaîtra après le premier relevé de solde.</p>
            )}
          </article>
        </div>
      </section>
      <div className="control-grid">
        <section className="panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Opérations</span>
              <h2>Missions du jour</h2>
            </div>
            <strong>{todayMissions.length}</strong>
          </div>
          {todayMissions.length ? (
            todayMissions.map((m) => (
              <button
                className="mission-control-row"
                key={m.id}
                onClick={() => setSelected(m)}
              >
                <span>
                  <strong>
                    {new Date(m.starts_at).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    · {m.client?.name || m.client_display_name}
                  </strong>
                  <small>
                    {m.location} · {assignmentNames(m) || "Non affectée"}
                  </small>
                </span>
                <i>Voir ›</i>
              </button>
            ))
          ) : (
            <p className="empty">Aucune mission aujourd’hui.</p>
          )}
        </section>
        <section className="panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">À traiter</span>
              <h2>Validations</h2>
            </div>
            <strong>
              {requests.length +
                reports.length +
                hrPending +
                clientContractAlerts +
                workerContractAlerts +
                profileChanges.length}
            </strong>
          </div>
          <button
            className="validation-summary"
            onClick={() => onNavigate("missions")}
          >
            <span>Demandes de mission</span>
            <b>{requests.length}</b>
          </button>
          <button
            className="validation-summary"
            onClick={() => onNavigate("missions")}
          >
            <span>Rapports terrain à lire</span>
            <b>{reports.length}</b>
          </button>
          <button
            className="validation-summary"
            onClick={() => onNavigate("team")}
          >
            <span>Congés, IK et heures</span>
            <b>{hrPending}</b>
          </button>
          <button
            className="validation-summary"
            onClick={() => onNavigate("clients")}
          >
            <span>Contrats et pièces clients</span>
            <b>{clientContractAlerts}</b>
          </button>
          <button
            className="validation-summary"
            onClick={() => onNavigate("team")}
          >
            <span>Contrats et pièces équipe</span>
            <b>{workerContractAlerts}</b>
          </button>
          <button
            className="validation-summary"
            disabled={!profileChanges.length}
            onClick={() => setSelectedChange(profileChanges[0] || null)}
          >
            <span>Modifications de fiches à vérifier</span>
            <b>{profileChanges.length}</b>
          </button>
        </section>
      </div>
      {hasDeploymentCapability(data, "process_actions") && (
        <section className="panel process-action-center">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Pilotage par exception</span>
              <h2>Actions requises</h2>
              <p>
                Chaque ligne explique ce qui doit être contrôlé. L’action métier
                reste validée par une personne avant toute conséquence.
              </p>
            </div>
            <div className="process-action-summary" aria-label="Résumé des actions requises">
              <span><strong>{openProcessActions.length}</strong> à traiter</span>
              <span className={overdueProcessActions.length ? "late" : ""}>
                <strong>{overdueProcessActions.length}</strong> en retard
              </span>
            </div>
          </div>
          <div className="segmented process-action-filters" role="group" aria-label="Filtrer les actions requises">
            <button
              className={actionFilter === "open" ? "active" : ""}
              onClick={() => setActionFilter("open")}
            >
              À traiter ({openProcessActions.length})
            </button>
            <button
              className={actionFilter === "history" ? "active" : ""}
              onClick={() => setActionFilter("history")}
            >
              Historique ({completedProcessActions.length})
            </button>
          </div>
          <div className="process-action-list">
            {displayedProcessActions.map((action) => {
              const event = (data.processBusinessEvents || []).find(
                  (item) => item.id === action.event_id,
                ),
                run = (data.processRuns || []).find(
                  (item) => item.id === action.process_run_id,
                ),
                overdue =
                  ["open", "in_progress"].includes(action.status) &&
                  new Date(action.due_at).getTime() < Date.now(),
                canOpenReport =
                  event?.event_type === "mission.report_submitted" &&
                  event.subject_type === "mission",
                canOpenProcurement =
                  event?.event_type === "procurement.requirement_prepared" &&
                  event.subject_type === "procurement_requirement",
                canOpenLeave =
                  event?.event_type === "workforce.leave_requested" &&
                  event.subject_type === "leave_request",
                canOpenContractorCancellation =
                  event?.event_type ===
                    "mission.contractor_cancellation_submitted" &&
                  event.subject_type === "worker_cancellation_notice",
                canOpenAssignmentRefusal =
                  event?.event_type === "mission.assignment_refused" &&
                  event.subject_type === "mission_assignment",
                canOpenClientContract =
                  event?.event_type === "contract.status_changed" &&
                  event.subject_type === "client_contract",
                canOpenWorkerContract =
                  event?.event_type === "contract.status_changed" &&
                  event.subject_type === "worker_contract",
                canOpenInvoice =
                  event?.subject_type === "invoice" &&
                  ["billing.invoice_status_changed", "billing.payment_recorded"].includes(
                    event.event_type,
                  ),
                canOpenSupplierInvoice =
                  event?.subject_type === "supplier_invoice" &&
                  event.event_type === "purchasing.supplier_invoice_status_changed",
                canOpenExpense =
                  event?.subject_type === "expense_report" &&
                  [
                    "accounting.expense_report_status_changed",
                    "accounting.expense_reimbursement_recorded",
                  ].includes(event.event_type),
                canOpenInventory =
                  event?.event_type === "inventory.stock_level_changed" &&
                  event.subject_type === "inventory_item";
              return (
                <article className={`process-action-row${overdue ? " overdue" : ""}`} key={action.id}>
                  <div className="process-action-main">
                    <div className="process-action-title">
                      <span className={`status ${overdue ? "overdue" : action.status}`}>
                        {labels[overdue ? "overdue" : action.status] || action.status}
                      </span>
                      <strong>{action.title}</strong>
                    </div>
                    <p>{action.description}</p>
                    <small>
                      Échéance {frDate(action.due_at, true)}
                      {run ? ` · ${run.process_key_snapshot} v${run.process_version_snapshot}` : ""}
                    </small>
                    {event && (
                      <small>
                        Déclenchée par {String(event.context.worker_name || event.context.party_name || event.actor_role)}
                        {event.context.client_name ? ` · ${String(event.context.client_name)}` : ""}
                      </small>
                    )}
                    {action.status === "completed" && (
                      <small className="process-action-decision">
                        Décision : {action.decision === "approve" ? "Validé" : action.decision === "request_changes" ? "Corrections demandées" : action.decision === "reassign" ? "Mission réaffectée" : action.decision === "cancel_mission" ? "Mission annulée" : action.decision === "signed" ? "Signature confirmée" : action.decision === "activate" ? "Contrat activé" : action.decision || "Terminée"}
                        {action.resolved_at ? ` · ${frDate(action.resolved_at, true)}` : ""}
                        {action.resolution_note ? ` · ${action.resolution_note}` : ""}
                      </small>
                    )}
                  </div>
                  {canOpenReport ? (
                    <button
                      className="secondary compact"
                      onClick={() => onOpenReport(event.subject_id)}
                    >
                      {action.status === "completed" ? "Relire le compte rendu" : "Ouvrir le contrôle"}
                    </button>
                  ) : canOpenProcurement ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("stocks")}
                    >
                      Ouvrir les approvisionnements
                    </button>
                  ) : canOpenLeave ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("team")}
                    >
                      Ouvrir les congés
                    </button>
                  ) : canOpenContractorCancellation ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("missions")}
                    >
                      Ouvrir la mission
                    </button>
                  ) : canOpenAssignmentRefusal ? (
                    <button
                      className="secondary compact"
                      onClick={() => {
                        const mission = data.missions.find(
                          (item) => item.id === String(event.context.mission_id || ""),
                        );
                        if (mission) onEditMission(mission);
                        else onNavigate("missions");
                      }}
                    >
                      Ouvrir la mission
                    </button>
                  ) : canOpenClientContract ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("clients")}
                    >
                      Ouvrir le contrat client
                    </button>
                  ) : canOpenWorkerContract ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("team")}
                    >
                      Ouvrir le contrat équipe
                    </button>
                  ) : canOpenInvoice ? (
                    <button
                      className="secondary compact"
                      onClick={() => onOpenInvoice(event.subject_id)}
                    >
                      Ouvrir la facture
                    </button>
                  ) : canOpenSupplierInvoice ? (
                    <button
                      className="secondary compact"
                      onClick={() => onOpenSupplierInvoice(event.subject_id)}
                    >
                      Ouvrir la facture ST
                    </button>
                  ) : canOpenExpense ? (
                    <button
                      className="secondary compact"
                      onClick={() => onOpenExpense(event.subject_id)}
                    >
                      Ouvrir la note de frais
                    </button>
                  ) : canOpenInventory ? (
                    <button
                      className="secondary compact"
                      onClick={() => onNavigate("stocks")}
                    >
                      Ouvrir le stock
                    </button>
                  ) : (
                    <span className="status muted">Lien métier à configurer</span>
                  )}
                </article>
              );
            })}
            {!displayedProcessActions.length && (
              <p className="empty">
                {actionFilter === "open"
                  ? "Aucune action requise à traiter."
                  : "Aucune action terminée dans l’historique."}
              </p>
            )}
          </div>
        </section>
      )}
      {hasDeploymentCapability(data, "process_actions") && (
        <section className="panel process-notification-center">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Information, alerte et traçabilité</span>
              <h2>Informations et alertes</h2>
              <p>
                Une intention décrit ce que le processus prévoit. Elle ne vaut
                jamais preuve d’envoi tant que son état ne le confirme pas.
              </p>
            </div>
            <div className="process-notification-summary" aria-label="Résumé des informations et alertes">
              <span><strong>{informationNotifications.length}</strong> information(s)</span>
              <span className={alertNotifications.length ? "late" : ""}>
                <strong>{alertNotifications.length}</strong> alerte(s)
              </span>
            </div>
          </div>
          {hasDeploymentCapability(data, "process_notification_executor") && (
            <div className="process-delivery-overview">
              <div>
                <span className="eyebrow">Exécution contrôlée</span>
                <h3>File multicanale</h3>
                <p>
                  Chaque destinataire et chaque canal disposent de leur propre
                  état, de leurs tentatives et de leur preuve de livraison.
                </p>
              </div>
              <div className="process-delivery-grid" aria-label="État de la file multicanale">
                <span><strong>{queuedProcessDeliveries.length}</strong> En attente</span>
                <span><strong>{retryProcessDeliveries.length}</strong> En reprise</span>
                <span><strong>{leasedProcessDeliveries.length}</strong> En traitement</span>
                <span className={deadLetterProcessDeliveries.length ? "late" : ""}>
                  <strong>{deadLetterProcessDeliveries.length}</strong> Échecs définitifs
                </span>
              </div>
              <small>
                Aucun fournisseur externe n’est activé dans ce lot. Les
                livraisons externes restent en attente tant qu’un exécuteur
                autorisé n’est pas déployé.
              </small>
            </div>
          )}
          <div className="segmented process-notification-filters" role="group" aria-label="Filtrer les informations et alertes">
            <button
              className={notificationLevel === "information" ? "active" : ""}
              onClick={() => setNotificationLevel("information")}
            >
              Informations ({informationNotifications.length})
            </button>
            <button
              className={notificationLevel === "alert" ? "active" : ""}
              onClick={() => setNotificationLevel("alert")}
            >
              Alertes ({alertNotifications.length})
            </button>
          </div>
          <div className="process-notification-list">
            {displayedProcessNotifications.map((notification) => {
              const event = (data.processBusinessEvents || []).find(
                  (item) => item.id === notification.event_id,
                ),
                run = (data.processRuns || []).find(
                  (item) => item.id === notification.process_run_id,
                ),
                journal = (data.processJournalEntries || [])
                  .filter(
                    (entry) =>
                      entry.process_run_id === notification.process_run_id,
                  )
                  .sort(
                    (left, right) =>
                      new Date(right.created_at).getTime() -
                      new Date(left.created_at).getTime(),
                  )[0],
                deliveries = processDeliveries.filter(
                  (delivery) =>
                    delivery.notification_intent_id === notification.id,
                ),
                channelLabels = notification.channel_intents.map(
                  (channel) =>
                    ({
                      internal: "interne",
                      push: "push",
                      email: "e-mail",
                      sms: "SMS",
                    })[channel],
                ),
                statusLabel =
                  notification.status === "planned"
                    ? "Prévue — aucun envoi confirmé"
                    : notification.status === "dispatched"
                      ? "Distribution confirmée"
                      : notification.status === "failed"
                        ? "Échec de distribution"
                        : "Annulée";
              return (
                <article
                  className={`process-notification-row ${notification.level}`}
                  key={notification.id}
                >
                  <div className="process-notification-title">
                    <span className={`status ${notification.level}`}>
                      {notification.level === "alert" ? "Alerte" : "Information"}
                    </span>
                    <strong>{notification.title}</strong>
                  </div>
                  <p>{notification.body}</p>
                  <small>
                    {statusLabel} · canaux prévus : {channelLabels.join(", ") || "aucun"}
                    {notification.dispatched_at
                      ? ` · ${frDate(notification.dispatched_at, true)}`
                      : ""}
                  </small>
                  {notification.last_error && (
                    <small className="process-notification-error">
                      Erreur : {notification.last_error}
                    </small>
                  )}
                  {hasDeploymentCapability(data, "process_notification_executor") &&
                    deliveries.length > 0 && (
                      <div className="process-delivery-list">
                        {deliveries.map((delivery) => {
                          const channelLabel =
                              ({
                                internal: "ARD Manager",
                                push: "Push",
                                email: "E-mail",
                                sms: "SMS",
                              })[delivery.channel],
                            deliveryStatusLabel =
                              delivery.status === "suppressed"
                                ? "Désactivée par le destinataire"
                                : delivery.status === "queued"
                                  ? "En attente d’un exécuteur"
                                  : delivery.status === "leased"
                                    ? "Traitement en cours"
                                    : delivery.status === "retry_scheduled"
                                      ? "Nouvelle tentative planifiée"
                                      : delivery.status === "delivered"
                                        ? "Distribuée"
                                        : delivery.status === "dead_letter"
                                          ? "Échec définitif"
                                          : "Annulée";
                          return (
                            <div
                              className={`process-delivery-row ${delivery.status}`}
                              key={delivery.id}
                            >
                              <div>
                                <strong>{channelLabel}</strong>
                                <span>{deliveryStatusLabel}</span>
                              </div>
                              <small>
                                Tentative {delivery.attempt_count}/{delivery.max_attempts}
                                {delivery.status === "queued"
                                  ? ` · disponible ${frDate(delivery.next_attempt_at, true)}`
                                  : ""}
                                {delivery.status === "retry_scheduled"
                                  ? ` · reprise ${frDate(delivery.next_attempt_at, true)}`
                                  : ""}
                                {delivery.status === "leased" && delivery.lease_until
                                  ? ` · bail jusqu’au ${frDate(delivery.lease_until, true)}`
                                  : ""}
                                {delivery.delivered_at
                                  ? ` · ${frDate(delivery.delivered_at, true)}`
                                  : ""}
                              </small>
                              {(delivery.provider || delivery.external_message_id) && (
                                <small>
                                  Preuve : {delivery.provider || "fournisseur"}
                                  {delivery.external_message_id
                                    ? ` · référence ${delivery.external_message_id}`
                                    : ""}
                                </small>
                              )}
                              {delivery.last_error && (
                                <small className="process-notification-error">
                                  Dernière erreur : {delivery.last_error}
                                </small>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  <small className="process-notification-explanation">
                    {event ? `Déclencheur : ${event.event_type}` : "Déclencheur indisponible"}
                    {run
                      ? ` · ${run.process_key_snapshot} v${run.process_version_snapshot}`
                      : ""}
                    {journal ? ` · ${journal.message}` : ""}
                  </small>
                  {event?.subject_type === "mission" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onOpenReport(event.subject_id)}
                    >
                      Ouvrir la mission
                    </button>
                  )}
                  {event?.subject_type === "procurement_requirement" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("stocks")}
                    >
                      Ouvrir les approvisionnements
                    </button>
                  )}
                  {event?.subject_type === "leave_request" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("team")}
                    >
                      Ouvrir les congés
                    </button>
                  )}
                  {event?.subject_type === "worker_cancellation_notice" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("missions")}
                    >
                      Ouvrir la mission
                    </button>
                  )}
                  {event?.subject_type === "mission_assignment" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => {
                        const mission = data.missions.find(
                          (item) => item.id === String(event.context.mission_id || ""),
                        );
                        if (mission) onEditMission(mission);
                        else onNavigate("missions");
                      }}
                    >
                      Ouvrir la mission
                    </button>
                  )}
                  {event?.subject_type === "quote" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("billing")}
                    >
                      Ouvrir le devis
                    </button>
                  )}
                  {event?.subject_type === "client_contract" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("clients")}
                    >
                      Ouvrir le contrat client
                    </button>
                  )}
                  {event?.subject_type === "worker_contract" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("team")}
                    >
                      Ouvrir le contrat équipe
                    </button>
                  )}
                  {event?.subject_type === "invoice" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onOpenInvoice(event.subject_id)}
                    >
                      Ouvrir la facture
                    </button>
                  )}
                  {event?.subject_type === "supplier_invoice" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onOpenSupplierInvoice(event.subject_id)}
                    >
                      Ouvrir la facture ST
                    </button>
                  )}
                  {event?.subject_type === "expense_report" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onOpenExpense(event.subject_id)}
                    >
                      Ouvrir la note de frais
                    </button>
                  )}
                  {event?.subject_type === "inventory_item" && (
                    <button
                      className="secondary compact process-notification-open"
                      onClick={() => onNavigate("stocks")}
                    >
                      Ouvrir le stock
                    </button>
                  )}
                </article>
              );
            })}
            {!displayedProcessNotifications.length && (
              <p className="empty">
                {notificationLevel === "information"
                  ? "Aucune information de processus."
                  : "Aucune alerte de processus."}
              </p>
            )}
          </div>
        </section>
      )}
      <section className="panel admin-dashboard-billing">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Lecture du mois</span>
            <h2>État de facturation</h2>
          </div>
          <button
            className="secondary compact"
            onClick={() => onNavigate("billing")}
          >
            Voir factures et devis
          </button>
        </div>
        <div className="finance-strip">
          <div>
            <span>À facturer</span>
            <strong>
              {money(
                data.missions
                  .filter((m) => m.billing_status === "to_invoice")
                  .reduce((s, m) => s + (m.amount_ex_vat || 0), 0),
              )}
            </strong>
          </div>
          <div>
            <span>À encaisser</span>
            <strong>
              {money(
                data.invoices
                  .filter((i) =>
                    ["ready", "sent", "partially_paid", "overdue"].includes(
                      i.status,
                    ),
                  )
                  .reduce(
                    (s, i) =>
                      s + Math.max(0, i.total_inc_vat - (i.amount_paid || 0)),
                    0,
                  ),
              )}
            </strong>
          </div>
          <div>
            <span>Payé ce mois</span>
            <strong>{money(collected)}</strong>
          </div>
        </div>
      </section>
      {selectedKpi && selectedKpiDetail && (
        <div
          className="portal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Détail du KPI ${selectedKpiDetail.title}`}
        >
          <section className="portal-detail cost-detail-modal kpi-detail-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Chiffre expliqué · {selectedKpiDetail.period}</span>
                <h2>{selectedKpiDetail.title}</h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedKpi(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="kpi-explanation-grid">
              <article>
                <span>Valeur affichée</span>
                <strong>
                  {selectedKpiDetail.value == null
                    ? "À renseigner"
                    : money(selectedKpiDetail.value)}
                </strong>
                <small>Valeur issue des seules données connues dans ARD Manager.</small>
              </article>
              <article>
                <span>Formule appliquée</span>
                <p>{selectedKpiDetail.formula}</p>
              </article>
              <article>
                <span>Sources retenues</span>
                <p>{selectedKpiDetail.sources}</p>
              </article>
            </div>
            {invoicesForKpi.length > 0 && (
              <section className="table-wrap kpi-source-table">
                <h3>Factures et missions responsables du montant</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Client</th>
                      <th>Date</th>
                      <th>HT retenu</th>
                      <th>Missions liées</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesForKpi.map((invoice) => {
                      const client = data.clients.find((item) => item.id === invoice.client_id),
                        linkedMissions = missionsForInvoice(invoice.id),
                        signedAmount =
                          invoice.total_ex_vat *
                          (invoice.document_kind === "credit" ? -1 : 1);
                      return (
                        <tr key={invoice.id}>
                          <td>{invoice.number || "Sans numéro"}</td>
                          <td>{client?.name || "Client inconnu"}</td>
                          <td>
                            {invoice.issue_on || invoice.billing_period
                              ? frDate(invoice.issue_on || invoice.billing_period || "")
                              : "Date inconnue"}
                          </td>
                          <td>{money(signedAmount)}</td>
                          <td>
                            {linkedMissions.length ? (
                              <div className="kpi-mission-links">
                                {linkedMissions.map((mission) => (
                                  <button
                                    type="button"
                                    className="text-button"
                                    key={mission.id}
                                    onClick={() => {
                                      setSelectedKpi(null);
                                      setSelected(mission);
                                    }}
                                  >
                                    {mission.location || frDate(mission.starts_at)}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <small>Aucune mission liée</small>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="secondary compact"
                              onClick={() => {
                                setSelectedKpi(null);
                                onOpenInvoice(invoice.id);
                              }}
                            >
                              Ouvrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}
            {selectedKpi === "collected" && (
              <section className="table-wrap kpi-source-table">
                <h3>Règlements confirmés retenus</h3>
                {monthPayments.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Date reçue</th>
                        <th>Facture</th>
                        <th>Méthode</th>
                        <th>Référence</th>
                        <th>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthPayments.map((payment) => {
                        const invoice = data.invoices.find(
                          (item) => item.id === payment.invoice_id,
                        );
                        return (
                          <tr key={payment.id}>
                            <td>{frDate(payment.received_on)}</td>
                            <td>{invoice?.number || "Facture inconnue"}</td>
                            <td>{labels[payment.method] || payment.method}</td>
                            <td>{payment.reference || "—"}</td>
                            <td>{money(payment.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="empty">Aucun règlement confirmé sur ce mois.</p>
                )}
              </section>
            )}
            {selectedKpiCosts && (
              <section className="kpi-cost-sources">
                <h3>Coûts soustraits du chiffre d’affaires</h3>
                <div className="cost-breakdown-grid compact-breakdown">
                  {[
                    ["Dépenses", selectedKpiCosts.operating],
                    ["Charges fixes", selectedKpiCosts.fixed],
                    ["Sous-traitance", selectedKpiCosts.contractors],
                    ["Salaires", selectedKpiCosts.employeePayroll],
                    ["Tickets", selectedKpiCosts.tickets],
                    ["IK validées", selectedKpiCosts.mileage],
                    ["Dirigeant", selectedKpiCosts.directorCost],
                    ["Apporteurs", selectedKpiCosts.referralCommissions],
                  ].map(([label, amount]) => (
                    <article key={String(label)}>
                      <span>{label}</span>
                      <strong>{money(Number(amount))}</strong>
                    </article>
                  ))}
                </div>
                {(selectedKpiCosts.employerCostsIncomplete ||
                  selectedKpiCosts.mileageCostIncomplete) && (
                  <Notice tone="warning">
                    Le résultat reste provisoire : au moins un coût employeur ou une indemnité kilométrique est incomplet.
                  </Notice>
                )}
              </section>
            )}
            <div className="detail-actions">
              {(selectedKpi === "monthly_profit" || selectedKpi === "annual_profit") && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    const period = selectedKpi === "monthly_profit" ? "month" : "year";
                    setSelectedKpi(null);
                    setCostPeriod(period);
                  }}
                >
                  Ouvrir tout le détail des coûts
                </button>
              )}
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  const destination =
                    selectedKpi === "collected" || selectedKpi === "bank_balance"
                      ? "accounting"
                      : "billing";
                  setSelectedKpi(null);
                  onNavigate(destination);
                }}
              >
                {selectedKpi === "collected" || selectedKpi === "bank_balance"
                  ? "Ouvrir la comptabilité"
                  : "Ouvrir devis et factures"}
              </button>
            </div>
          </section>
        </div>
      )}
      {selectedChange && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail profile-change-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Validation des informations</span>
                <h2>Modification de fiche demandée</h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedChange(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <p className="section-intro">
              Demande de{" "}
              {(data.profiles || []).find(
                (item) => item.id === selectedChange.requester_profile_id,
              )?.display_name || "Compte utilisateur"}{" "}
              · {frDate(selectedChange.created_at, true)}
            </p>
            <div className="profile-change-grid">
              {[
                ...Object.entries(selectedChange.profile_changes),
                ...Object.entries(selectedChange.target_changes),
              ].map(([key, value], index) => (
                <div key={`${key}-${index}`}>
                  <span>
                    {(
                      {
                        display_name: "Nom affiché",
                        email: "Email",
                        phone: "Téléphone",
                        calendar_enabled: "Agenda Google",
                        name: "Nom / raison sociale",
                        first_name: "Prénom",
                        last_name: "Nom",
                        business_name: "Raison sociale",
                        address: "Adresse",
                        siret: "SIRET",
                        vat_number: "N° TVA",
                        notes: "Informations utiles",
                        profile_notes: "Informations utiles",
                      } as Record<string, string>
                    )[key] || key}
                  </span>
                  <strong>
                    {typeof value === "boolean"
                      ? value
                        ? "Oui"
                        : "Non"
                      : String(value || "Non renseigné")}
                  </strong>
                </div>
              ))}
            </div>
            <label>
              Note interne de validation
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
              />
            </label>
            <div className="detail-actions">
              <button
                className="danger"
                disabled={reviewBusy}
                onClick={() => reviewProfileChange(false)}
              >
                Refuser
              </button>
              <button
                disabled={reviewBusy}
                onClick={() => reviewProfileChange(true)}
              >
                Valider et appliquer
              </button>
            </div>
            {reviewError && <Notice tone="error">{reviewError}</Notice>}
            {profileChanges.length > 1 && (
              <small>
                {profileChanges.length - 1} autre(s) demande(s) resteront à
                traiter ensuite.
              </small>
            )}
          </section>
        </div>
      )}
      {costPeriod && displayedCosts && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail cost-detail-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Lecture des coûts</span>
                <h2>
                  {costPeriod === "month"
                    ? "Détail du mois en cours"
                    : "Détail annuel provisoire"}
                </h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setCostPeriod(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="cost-breakdown-grid">
              <article>
                <span>Fond de roulement et frais</span>
                <strong>{money(displayedCosts.operating)}</strong>
                <small>Dépenses saisies HT</small>
              </article>
              <article>
                <span>Charges fixes récurrentes</span>
                <strong>{money(displayedCosts.fixed)}</strong>
                <small>
                  {displayedCosts.fixedRows.length} charge(s) projetée(s)
                </small>
              </article>
              <article>
                <span>Sous-traitance</span>
                <strong>{money(displayedCosts.contractors)}</strong>
                <small>Forfaits et heures affectés</small>
              </article>
              <article>
                <span>Salaires / coût employeur</span>
                <strong>{money(displayedCosts.employeePayroll)}</strong>
                <small>
                  {displayedCosts.employerCostsIncomplete
                    ? "Incomplet : salaire net utilisé si coût employeur absent"
                    : "Coût employeur configuré"}
                </small>
              </article>
              <article>
                <span>Tickets restaurant</span>
                <strong>{money(displayedCosts.tickets)}</strong>
                <small>Quantités et valeurs configurées</small>
              </article>
              <article>
                <span>Indemnités kilométriques</span>
                <strong>
                  {displayedCosts.mileageCostIncomplete
                    ? "Calcul incomplet"
                    : money(displayedCosts.mileage)}
                </strong>
                <small>{displayedCosts.mileageKm} km validés</small>
              </article>
              <article>
                <span>Rémunération et charges dirigeant</span>
                <strong>{money(displayedCosts.directorCost)}</strong>
                <small>
                  Dividendes suivis à part :{" "}
                  {money(displayedCosts.directorDividends)}
                </small>
              </article>
              <article>
                <span>Commissions d’apporteurs</span>
                <strong>{money(displayedCosts.referralCommissions)}</strong>
                <small>
                  {displayedCosts.referralCommissionRows.length} facture(s)
                  concernée(s), hors commissions écartées
                </small>
              </article>
              <article className="total">
                <span>Total connu</span>
                <strong>{money(displayedCosts.total)}</strong>
                <small>
                  {costPeriod === "year"
                    ? "Salaires et forfaits mensuels estimés sur " +
                      displayedCosts.salaryMonths +
                      " mois"
                    : "Période mensuelle"}
                </small>
              </article>
            </div>
            {(displayedCosts.employerCostsIncomplete ||
              displayedCosts.mileageCostIncomplete) && (
              <Notice>
                Ce total n’est pas un bénéfice comptable : certaines charges
                restent incomplètes. Renseignez le coût employeur dans Équipe &
                RH et validez les véhicules/cartes grises pour fiabiliser les
                IK.
              </Notice>
            )}
            <section className="table-wrap">
              <h3>Dépenses courantes et hors fond de roulement</h3>
              {displayedCosts.operatingRows.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Catégorie</th>
                      <th>Dépense</th>
                      <th>HT</th>
                      <th>TVA</th>
                      <th>TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedCosts.operatingRows.map((expense) => (
                      <tr key={expense.id}>
                        <td>{frDate(expense.spent_on)}</td>
                        <td>{labels[expense.category] || expense.category}</td>
                        <td>{expense.description}</td>
                        <td>{money(expense.amount_ex_vat)}</td>
                        <td>{money(expense.vat_amount)}</td>
                        <td>{money(expense.amount_inc_vat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty">
                  Aucune dépense courante saisie sur cette période.
                </p>
              )}
            </section>
            <div className="commercial-support-grid cost-people-grid">
              <section className="detail-note">
                <h3>Coûts de sous-traitance</h3>
                {displayedCosts.contractorRows.length ? (
                  <div className="payment-list">
                    {displayedCosts.contractorRows.map((row) => (
                      <div key={row.worker?.id || row.amount}>
                        <strong>
                          {row.worker ? workerName(row.worker) : "ST inconnu"}
                        </strong>
                        <span>
                          {row.worker?.pricing_mode || "Tarification mission"}
                        </span>
                        <b>{money(row.amount)}</b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty">
                    Aucun coût ST calculé sur cette période.
                  </p>
                )}
              </section>
              <section className="detail-note">
                <h3>Coûts des employés</h3>
                {displayedCosts.employeeRows.length ? (
                  <div className="employee-cost-list">
                    {displayedCosts.employeeRows.map((row) => (
                      <div key={row.worker.id}>
                        <span>
                          <strong>{workerName(row.worker)}</strong>
                          <small>
                            {row.complete
                              ? "Coût employeur"
                              : "Salaire net uniquement — incomplet"}
                          </small>
                        </span>
                        <span>
                          {money(row.payroll)} + {money(row.tickets)} tickets
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty">Aucun employé actif.</p>
                )}
              </section>
            </div>
            <div className="detail-actions">
              <button
                className="secondary"
                onClick={() => {
                  setCostPeriod(null);
                  onNavigate("accounting");
                }}
              >
                Ouvrir la comptabilité
              </button>
              <button
                className="secondary"
                onClick={() => {
                  setCostPeriod(null);
                  onNavigate("team");
                }}
              >
                Vérifier les coûts équipe
              </button>
              <button
                className="secondary"
                onClick={() => {
                  setCostPeriod(null);
                  onNavigate("team");
                }}
              >
                Gérer les véhicules et les IK
              </button>
            </div>
          </section>
        </div>
      )}
      {selected && (
        <AdminMissionPreview
          mission={selected}
          data={data}
          reload={reload}
          onClose={() => setSelected(null)}
          onEdit={(m) => {
            setSelected(null);
            onEditMission(m);
          }}
        />
      )}
    </div>
  );
}

function AdminAgenda({
  data,
  reload,
  onEditMission,
}: {
  data: DataState;
  reload: () => void;
  onEditMission: (m: Mission) => void;
}) {
  const [view, setView] = useState<"week" | "month">("week"),
    [cursor, setCursor] = useState(() => new Date()),
    [selectedDay, setSelectedDay] = useState<string | null>(null),
    [selectedMission, setSelectedMission] = useState<Mission | null>(null);
  const weekStart = new Date(cursor);
  weekStart.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
  weekStart.setHours(0, 0, 0, 0);
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1),
    monthGridStart = new Date(monthStart);
  monthGridStart.setDate(1 - ((monthStart.getDay() + 6) % 7));
  const days = Array.from({ length: view === "week" ? 7 : 42 }, (_, i) => {
      const d = new Date(view === "week" ? weekStart : monthGridStart);
      d.setDate(d.getDate() + i);
      return d;
    }),
    step = view === "week" ? 7 : 1,
    selectedDayMissions = selectedDay
      ? data.missions
          .filter(
            (mission) =>
              parisDay(mission.starts_at) === selectedDay &&
              mission.status !== "cancelled",
          )
          .sort((left, right) => left.starts_at.localeCompare(right.starts_at))
      : [];
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Planning global</span>
          <h1>Agenda des missions</h1>
        </div>
        <div className="inline-actions">
          <button
            className="secondary"
            onClick={() =>
              downloadMissionCalendar(
                data.missions.filter(
                  (mission) =>
                    mission.status !== "cancelled" &&
                    days.some(
                      (day) => parisDay(mission.starts_at) === parisDay(day),
                    ),
                ),
                `planning-ard-${parisDay(days[0])}-${parisDay(days.at(-1)!)}.ics`,
                "Planning ARD Manager",
              )
            }
          >
            Exporter .ics
          </button>
          <button
            className={view === "week" ? "" : "secondary"}
            onClick={() => setView("week")}
          >
            Semaine
          </button>
          <button
            className={view === "month" ? "" : "secondary"}
            onClick={() => setView("month")}
          >
            Mois
          </button>
          <button
            className="secondary"
            onClick={() =>
              setCursor((d) =>
                view === "week"
                  ? new Date(d.getFullYear(), d.getMonth(), d.getDate() - step)
                  : new Date(d.getFullYear(), d.getMonth() - 1, 1),
              )
            }
          >
            ←
          </button>
          <button onClick={() => setCursor(new Date())}>Aujourd’hui</button>
          <button
            className="secondary"
            onClick={() =>
              setCursor((d) =>
                view === "week"
                  ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + step)
                  : new Date(d.getFullYear(), d.getMonth() + 1, 1),
              )
            }
          >
            →
          </button>
        </div>
      </div>
      <h2 className="agenda-period">
        {view === "month"
          ? cursor.toLocaleDateString("fr-FR", {
              month: "long",
              year: "numeric",
            })
          : `${days[0].toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} — ${days.at(-1)!.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`}
      </h2>
      <div className={`admin-agenda ${view === "month" ? "month-view" : ""}`}>
        {days.map((day) => {
          const missions = data.missions.filter(
              (m) =>
                parisDay(m.starts_at) === parisDay(day) &&
                m.status !== "cancelled",
            ),
            outside = view === "month" && day.getMonth() !== cursor.getMonth();
          return (
            <section
              className={`${outside ? "outside " : ""}agenda-day-cell`}
              key={day.toISOString()}
              role="button"
              tabIndex={0}
              aria-label={`Voir toutes les missions du ${day.toLocaleDateString("fr-FR")}`}
              onClick={() => setSelectedDay(parisDay(day))}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelectedDay(parisDay(day));
                }
              }}
            >
              <header>
                <strong>
                  {day.toLocaleDateString("fr-FR", {
                    weekday: "short",
                    day: "numeric",
                    month: view === "week" ? "short" : undefined,
                  })}
                </strong>
                <span>{missions.length || ""}</span>
              </header>
              {missions.map((m) => (
                <button
                  key={m.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedMission(m);
                  }}
                >
                  <time>
                    {new Date(m.starts_at).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </time>
                  <strong>{m.client?.name || m.client_display_name}</strong>
                  {view === "week" && <small>{m.location}</small>}
                  <span
                    className={`mission-day-status ${missionOperationalStatus(m, data.missionQualityClaims || []).tone}`}
                  >
                    {missionOperationalStatus(m, data.missionQualityClaims || []).label}
                  </span>
                </button>
              ))}
              {view === "week" && !missions.length && <p>—</p>}
            </section>
          );
        })}
      </div>
      {selectedDay && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail agenda-day-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Planning de la journée</span>
                <h2>
                  {new Date(`${selectedDay}T12:00:00`).toLocaleDateString("fr-FR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </h2>
                <small>{selectedDayMissions.length} mission(s) prévue(s)</small>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedDay(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="agenda-day-mission-list">
              {selectedDayMissions.map((mission) => {
                const missionAssignments = data.assignments.filter(
                  (assignment) =>
                    assignment.mission_id === mission.id &&
                    assignment.status !== "cancelled",
                );
                return (
                  <button
                    type="button"
                    className="agenda-day-mission-card"
                    key={mission.id}
                    onClick={() => {
                      setSelectedMission(mission);
                    }}
                  >
                    <time>
                      {new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {" — "}
                      {new Date(mission.ends_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </time>
                    <span>
                      <strong>{mission.client?.name || mission.client_display_name || "Client"}</strong>
                      <small>{mission.location || "Adresse à préciser"}</small>
                      <small>
                        {missionAssignments.length
                          ? missionAssignments
                              .map((assignment) => {
                                const worker = data.workers.find(
                                  (candidate) => candidate.id === assignment.worker_id,
                                );
                                return worker ? workerName(worker) : "Intervenant";
                              })
                              .join(" · ")
                          : "Intervenant à affecter"}
                      </small>
                    </span>
                    <span
                      className={`mission-day-status ${missionOperationalStatus(mission, data.missionQualityClaims || []).tone}`}
                    >
                      {missionOperationalStatus(mission, data.missionQualityClaims || []).label}
                    </span>
                    <i>Ouvrir ›</i>
                  </button>
                );
              })}
              {!selectedDayMissions.length && (
                <p className="empty">Aucune mission prévue ce jour.</p>
              )}
            </div>
          </section>
        </div>
      )}
      {selectedMission && (
        <AdminMissionPreview
          mission={selectedMission}
          data={data}
          reload={reload}
          onClose={() => setSelectedMission(null)}
          onEdit={(mission) => {
            setSelectedMission(null);
            onEditMission(mission);
          }}
        />
      )}
    </>
  );
}

function ClientDayAgenda({
  data,
  onOpenMissions,
}: {
  data: DataState;
  onOpenMissions: () => void;
}) {
  const [date, setDate] = useState(parisDay(new Date()));
  const missions = data.missions
    .filter(
      (mission) =>
        parisDay(mission.starts_at) === date && mission.status !== "cancelled",
    )
    .sort((left, right) => left.starts_at.localeCompare(right.starts_at));
  return (
    <>
      <div className="page-title client-day-agenda-title">
        <div>
          <span className="eyebrow">Suivi de mes biens</span>
          <h1>Ma journée</h1>
          <p>Suivez rapidement l’avancement de toutes les missions prévues.</p>
        </div>
        <label className="period-picker">
          Journée
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
      </div>
      <section className="panel client-day-agenda">
        <div className="section-heading">
          <div>
            <h2>
              {new Date(`${date}T12:00:00`).toLocaleDateString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </h2>
            <small>{missions.length} mission(s) prévue(s)</small>
          </div>
          <button className="secondary compact" onClick={onOpenMissions}>
            Toutes mes missions
          </button>
        </div>
        <div className="client-day-status-legend" aria-label="Légende des statuts">
          <span className="todo">À faire</span>
          <span className="progress">En cours</span>
          <span className="done">Terminée</span>
          <span className="problem">Problème signalé</span>
        </div>
        <div className="client-day-mission-list">
          {missions.map((mission) => {
            const property = data.properties.find(
              (candidate) => candidate.id === mission.property_id,
            );
            const status = missionOperationalStatus(
              mission,
              data.missionQualityClaims || [],
            );
            return (
              <article className="client-day-mission-card" key={mission.id}>
                <time>
                  {new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </time>
                <div>
                  <strong>{property?.name || "Bien concerné"}</strong>
                  <small>{mission.location || property?.address || "Adresse à préciser"}</small>
                  {mission.client_report && <p>{mission.client_report}</p>}
                </div>
                <span className={`mission-day-status ${status.tone}`}>
                  {status.label}
                </span>
              </article>
            );
          })}
          {!missions.length && (
            <p className="empty">Aucune mission prévue pour cette journée.</p>
          )}
        </div>
      </section>
    </>
  );
}

function RouteGroupMileagePlanner({
  profile,
  data,
  reload,
  workerIds,
  routeTeamId,
  defaultWorkerId,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  workerIds: string[];
  routeTeamId: string | null;
  defaultWorkerId: string | null;
}) {
  const [driverId, setDriverId] = useState(
    defaultWorkerId && workerIds.includes(defaultWorkerId)
      ? defaultWorkerId
      : workerIds[0] || "",
  );
  return (
    <details className="route-mileage-details">
      <summary>Calculer les IK de cette tournée</summary>
      <div className="details-content">
        {workerIds.length > 1 && (
          <label>
            Conducteur indemnisé — une seule personne
            <select value={driverId} onChange={(event) => setDriverId(event.target.value)}>
              {workerIds.map((id) => {
                const worker = data.workers.find((item) => item.id === id);
                return <option key={id} value={id}>{worker ? workerName(worker) : "Intervenant"}</option>;
              })}
            </select>
          </label>
        )}
        <Notice>
          Le binôme partage une seule feuille de route. Les kilomètres ne sont
          attribués qu’au conducteur sélectionné, jamais à tous les membres.
        </Notice>
        {driverId && (
          <RouteMileagePlanner
            profile={profile}
            data={data}
            reload={reload}
            workerId={driverId}
            ownerProfileId={null}
            routeTeamId={routeTeamId}
            title="Trajet du groupe"
          />
        )}
      </div>
    </details>
  );
}

function AdminRoutes({
  profile,
  data,
  reload,
  onSelect,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  onSelect: (mission: Mission) => void;
}) {
  const [date, setDate] = useState(parisDay(new Date())),
    [teamName, setTeamName] = useState("Binôme"),
    [teamNotes, setTeamNotes] = useState(""),
    [selectedWorkers, setSelectedWorkers] = useState<string[]>([]),
    [leaderWorkerId, setLeaderWorkerId] = useState(""),
    [saveTemplate, setSaveTemplate] = useState(false),
    [templateName, setTemplateName] = useState(""),
    [selectedTemplateId, setSelectedTemplateId] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [deleteTeamId, setDeleteTeamId] = useState<string | null>(null);
  const advancedRoutes = hasDeploymentCapability(data, "advanced_routes"),
    activeAssignments = data.assignments.filter((a) =>
      ["accepted", "assigned"].includes(a.status),
    ),
    dayMissions = data.missions
      .filter((m) => parisDay(m.starts_at) === date && m.status !== "cancelled")
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    dayMissionIds = new Set(dayMissions.map((m) => m.id)),
    allDayAssignments = data.assignments.filter(
      (assignment) =>
        dayMissionIds.has(assignment.mission_id) &&
        !["cancelled", "rejected"].includes(assignment.status),
    ),
    dayAssignments = activeAssignments.filter((a) =>
      dayMissionIds.has(a.mission_id),
    ),
    dayWorkerIds = [...new Set(dayAssignments.map((a) => a.worker_id))],
    teams = (data.dailyRouteTeams || []).filter((t) => t.work_date === date),
    members = (data.dailyRouteTeamMembers || []).filter(
      (member) => member.work_date === date,
    ),
    templates = (data.routeTeamTemplates || []).filter((item) => item.active),
    templateMembers = data.routeTeamTemplateMembers || [],
    groupedWorkerIds = new Set(members.map((member) => member.worker_id)),
    headquarters = [
      data.organization?.address,
      data.organization?.postal_code,
      data.organization?.city,
    ]
      .filter(Boolean)
      .join(" ");

  const routeUrl = (missions: Mission[]) => {
    if (!headquarters || !missions.length) return "";
    const locations = missions
      .map((mission) => mission.location)
      .filter(Boolean);
    if (!locations.length) return "";
    const destination = locations.at(-1)!;
    const waypoints = locations.slice(0, -1);
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(headquarters)}&destination=${encodeURIComponent(destination)}${waypoints.length ? `&waypoints=${encodeURIComponent(waypoints.join("|"))}` : ""}`;
  };

  const missionsForWorkers = (workerIds: string[]) => {
    const missionIds = new Set(
      dayAssignments
        .filter((a) => workerIds.includes(a.worker_id))
        .map((a) => a.mission_id),
    );
    return dayMissions.filter((mission) => missionIds.has(mission.id));
  };

  const groups = [
    ...teams.map((team) => {
      const workerIds = members
        .filter((member) => member.team_id === team.id)
        .map((member) => member.worker_id);
      return {
        id: team.id,
        name: team.name,
        notes: team.notes,
        leaderWorkerId: team.leader_worker_id || null,
        workerIds,
        missions: missionsForWorkers(workerIds),
        team: true,
      };
    }),
    ...dayWorkerIds
      .filter((id) => !groupedWorkerIds.has(id))
      .map((id) => ({
        id,
        name: workerName(data.workers.find((worker) => worker.id === id)!),
        notes: null,
        leaderWorkerId: null,
        workerIds: [id],
        missions: missionsForWorkers([id]),
        team: false,
      })),
  ];

  function toggleWorker(workerId: string) {
    setSelectedWorkers((current) =>
      current.includes(workerId)
        ? current.filter((id) => id !== workerId)
        : [...current, workerId],
    );
    if (leaderWorkerId === workerId) setLeaderWorkerId("");
  }

  function applyTemplate(templateId: string) {
    setSelectedTemplateId(templateId);
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    const workerIds = templateMembers
      .filter((item) => item.template_id === template.id)
      .map((item) => item.worker_id)
      .filter((id) => data.workers.some((worker) => worker.id === id && worker.active));
    setTeamName(template.name);
    setTeamNotes(template.notes || "");
    setSelectedWorkers(workerIds);
    setLeaderWorkerId(
      template.leader_worker_id && workerIds.includes(template.leader_worker_id)
        ? template.leader_worker_id
        : "",
    );
    setSaveTemplate(false);
    setTemplateName("");
  }

  async function createTeam(e: React.FormEvent) {
    e.preventDefault();
    if (selectedWorkers.length < 2) {
      setMessage("Sélectionnez au moins deux personnes pour créer un binôme.");
      return;
    }
    setBusy(true);
    let error = null;
    let legacyFallback = !advancedRoutes;
    if (advancedRoutes) {
      const current = await supabase!.rpc("create_daily_route_team_v2", {
        p_work_date: date,
        p_name: teamName.trim() || "Binôme",
        p_notes: teamNotes.trim() || null,
        p_worker_ids: selectedWorkers,
        p_leader_worker_id: leaderWorkerId || null,
        p_save_template: saveTemplate,
        p_template_name: saveTemplate
          ? templateName.trim() || teamName.trim() || "Équipe"
          : null,
      });
      error = current.error;
    }
    if (!advancedRoutes || (error && /create_daily_route_team_v2|schema cache|function/i.test(error.message))) {
      const legacy = await supabase!.rpc("create_daily_route_team", {
        p_work_date: date,
        p_name: teamName.trim() || "Binôme",
        p_notes: teamNotes.trim() || null,
        p_worker_ids: selectedWorkers,
      });
      error = legacy.error;
      legacyFallback = !legacy.error;
    }
    if (error) {
      setMessage(error.message);
    } else {
      setMessage(
        legacyFallback
          ? "Binôme créé. Le responsable et la mémorisation seront actifs après la migration préparée."
          : `Binôme créé : le temps total est réparti entre ses membres${leaderWorkerId ? " et le responsable est identifié" : ""}${saveTemplate ? "; l’équipe est mémorisée" : ""}.`,
      );
      setSelectedWorkers([]);
      setTeamName("Binôme");
      setTeamNotes("");
      setLeaderWorkerId("");
      setSaveTemplate(false);
      setTemplateName("");
      setSelectedTemplateId("");
      reload();
    }
    setBusy(false);
  }

  async function removeTeam(teamId: string) {
    setBusy(true);
    const { error } = await supabase!.rpc("delete_daily_route_team", {
      p_team_id: teamId,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Binôme supprimé et anciennes affectations restaurées.",
    );
    if (!error) {
      setDeleteTeamId(null);
      reload();
    }
  }

  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Organisation de la journée</span>
          <h1>Feuilles de route</h1>
        </div>
        <label className="period-picker">
          Journée
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onInput={(e) => setDate(e.currentTarget.value)}
          />
        </label>
      </div>
      {!headquarters && (
        <Notice tone="error">
          Renseignez l’adresse du siège dans Société avant d’utiliser les
          itinéraires.
        </Notice>
      )}
      {!advancedRoutes && (
        <Notice>
          Le regroupement quotidien de base est disponible. Le responsable d’équipe, les modèles réutilisables et le calcul kilométrique automatique restent désactivés jusqu’à la migration contrôlée du module tournées avancées.
        </Notice>
      )}
      {message && (
        <Notice
          tone={
            message.includes("créé") || message.includes("supprimé")
              ? "success"
              : "info"
          }
        >
          {message}
        </Notice>
      )}
      <section className="panel route-origin">
        <span className="eyebrow">Point de départ obligatoire</span>
        <h2>{headquarters || "Siège social non renseigné"}</h2>
        <p>
          Les missions restent classées selon leurs horaires. Aucun temps de
          trajet n’est inventé avant la connexion d’un fournisseur
          cartographique.
        </p>
      </section>
      <section className="panel route-day-overview">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Toutes les interventions du jour</span>
            <h2>{dayMissions.length} mission(s) à organiser</h2>
          </div>
        </div>
        <div className="route-day-mission-list">
          {dayMissions.map((mission) => {
            const relatedAssignments = allDayAssignments.filter(
              (assignment) => assignment.mission_id === mission.id,
            );
            const confirmedAssignments = relatedAssignments.filter((assignment) =>
              ["accepted", "assigned"].includes(assignment.status),
            );
            const pendingAssignments = relatedAssignments.filter(
              (assignment) => assignment.status === "proposed",
            );
            const assignmentLabel = confirmedAssignments.length
              ? `Affectée à ${confirmedAssignments
                  .map((assignment) => {
                    const worker = data.workers.find(
                      (candidate) => candidate.id === assignment.worker_id,
                    );
                    return worker ? workerName(worker) : "Intervenant";
                  })
                  .join(" · ")}`
              : pendingAssignments.length
                ? "Proposition envoyée — réponse en attente"
                : "Intervenant à affecter";
            return (
              <button
                type="button"
                className="route-day-mission-card"
                key={mission.id}
                onClick={() => onSelect(mission)}
              >
                <time>
                  {new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </time>
                <span>
                  <strong>{mission.client?.name || mission.client_display_name || "Client"}</strong>
                  <small>{mission.location || "Adresse à préciser"}</small>
                  <small>{assignmentLabel}</small>
                </span>
                <span
                  className={`mission-day-status ${missionOperationalStatus(mission, data.missionQualityClaims || []).tone}`}
                >
                  {missionOperationalStatus(mission, data.missionQualityClaims || []).label}
                </span>
                <i>Voir ›</i>
              </button>
            );
          })}
          {!dayMissions.length && (
            <p className="empty">Aucune mission prévue pour cette journée.</p>
          )}
        </div>
        <small>
          Une mission apparaît ici dès sa création. Elle rejoint ensuite une feuille de route nominative dès que son affectation est confirmée.
        </small>
      </section>
      <div className="route-board">
        {groups.map((group) => (
          <section className="panel route-card" key={group.id}>
            <div className="section-heading">
              <div>
                <span className="eyebrow">
                  {group.team ? "Équipe temporaire" : "Intervenant"}
                </span>
                <h2>{group.name}</h2>
                <small>
                  {group.workerIds
                    .map((id) =>
                      data.workers.find((worker) => worker.id === id),
                    )
                    .filter(Boolean)
                    .map((worker) => workerName(worker!))
                    .join(" + ")}
                </small>
                {group.leaderWorkerId && (
                  <small>
                    Responsable : {workerName(
                      data.workers.find((worker) => worker.id === group.leaderWorkerId)!,
                    )}
                  </small>
                )}
              </div>
              {group.team && (
                <button
                  className="danger compact"
                  onClick={() => setDeleteTeamId(group.id)}
                >
                  Supprimer le binôme
                </button>
              )}
            </div>
            {group.notes && <p>{group.notes}</p>}
            <div className="route-steps">
              <div className="route-step origin">
                <b>Départ</b>
                <span>{headquarters || "Siège à renseigner"}</span>
              </div>
              {group.missions.map((mission, index) => (
                <button key={mission.id} onClick={() => onSelect(mission)}>
                  <b>{index + 1}</b>
                  <time>
                    {new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {" → "}
                    {new Date(mission.ends_at).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </time>
                  <span>
                    <strong>
                      {mission.client?.name || mission.client_display_name}
                    </strong>
                    <small>{mission.location}</small>
                    {group.team && (
                      <small>
                        {mission.planned_hours} h au total ·{" "}
                        {(
                          mission.planned_hours / group.workerIds.length
                        ).toLocaleString("fr-FR", {
                          maximumFractionDigits: 2,
                        })}{" "}
                        h par personne
                      </small>
                    )}
                  </span>
                  <i>Voir ›</i>
                </button>
              ))}
            </div>
            {!group.missions.length && (
              <p className="empty">Aucune mission affectée à ce groupe.</p>
            )}
            {routeUrl(group.missions) && (
              <a
                className="map-action secondary"
                href={routeUrl(group.missions)}
                target="_blank"
                rel="noreferrer"
              >
                Ouvrir la tournée dans Google Maps
              </a>
            )}
            {advancedRoutes && !!group.missions.length && (
              <RouteGroupMileagePlanner
                profile={profile}
                data={data}
                reload={reload}
                workerIds={group.workerIds}
                routeTeamId={group.team ? group.id : null}
                defaultWorkerId={group.leaderWorkerId || group.workerIds[0] || null}
              />
            )}
          </section>
        ))}
        {!groups.length && (
          <section className="panel">
            <p className="empty">Aucune tournée affectée pour cette journée.</p>
          </section>
        )}
      </div>
      <details className="panel route-team-creator" data-testid="route-team-creator">
        <summary>
          <strong>Créer ou mémoriser un binôme</strong>
          <small>Ouvrir uniquement pour organiser une nouvelle équipe sur cette journée</small>
        </summary>
        <div className="route-team-creator-body">
          <h2>Créer un binôme pour cette journée</h2>
          <p className="section-intro">
            Le binôme regroupe la feuille de route et répartit le temps prévu
            entre ses membres. Exemple : 2 h au total deviennent 1 h par personne
            à deux.
          </p>
          <form className="form-grid" onSubmit={createTeam}>
          {advancedRoutes && templates.length > 0 && (
            <label className="wide">
              Reprendre une équipe enregistrée
              <select
                value={selectedTemplateId}
                onChange={(event) => applyTemplate(event.target.value)}
              >
                <option value="">Nouvelle équipe</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Nom du groupe
            <input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Note d’organisation
            <input
              value={teamNotes}
              onChange={(e) => setTeamNotes(e.target.value)}
            />
          </label>
          <div className="team-worker-checks wide">
            {data.workers.map((worker) => (
              <label key={worker.id}>
                <input
                  type="checkbox"
                  checked={selectedWorkers.includes(worker.id)}
                  onChange={() => toggleWorker(worker.id)}
                  disabled={groupedWorkerIds.has(worker.id)}
                />
                {workerName(worker)} ·{" "}
                {worker.kind === "employee" ? "Employé" : "ST"}
              </label>
            ))}
          </div>
          {advancedRoutes && (
            <>
              <label>
                Responsable facultatif
                <select
                  value={leaderWorkerId}
                  onChange={(event) => setLeaderWorkerId(event.target.value)}
                >
                  <option value="">Aucun responsable désigné</option>
                  {selectedWorkers.map((id) => {
                    const worker = data.workers.find((item) => item.id === id);
                    return worker ? (
                      <option key={worker.id} value={worker.id}>
                        {workerName(worker)}
                      </option>
                    ) : null;
                  })}
                </select>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={saveTemplate}
                  onChange={(event) => setSaveTemplate(event.target.checked)}
                />
                Enregistrer cette équipe pour la réutiliser
              </label>
              {saveTemplate && (
                <label className="wide">
                  Nom de l’équipe enregistrée
                  <input
                    value={templateName}
                    onChange={(event) => setTemplateName(event.target.value)}
                    placeholder={teamName || "Équipe réutilisable"}
                  />
                </label>
              )}
            </>
          )}
          <button disabled={busy}>
            {busy ? "Création…" : "Créer le binôme"}
          </button>
          </form>
        </div>
      </details>
      {deleteTeamId && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail compact-dialog">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Confirmation</span>
                <h2>Supprimer ce binôme ?</h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setDeleteTeamId(null)}
              >
                ×
              </button>
            </div>
            <p>
              Les heures et affectations présentes avant la création du binôme
              seront restaurées. Les modifications manuelles ultérieures sont
              conservées.
            </p>
            <div className="row-actions">
              <button
                className="secondary"
                onClick={() => setDeleteTeamId(null)}
              >
                Annuler
              </button>
              <button
                className="danger"
                disabled={busy}
                onClick={() => removeTeam(deleteTeamId)}
              >
                {busy ? "Suppression…" : "Confirmer la suppression"}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function AdminRecurrences({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const plusDays = (value: string, count: number) => {
    const date = new Date(`${value}T12:00:00`);
    date.setDate(date.getDate() + count);
    return parisDay(date);
  };
  const firstClient = data.clients[0];
  const blank = () => ({
    clientId: firstClient?.id || "",
    propertyId: "",
    serviceKey: "",
    name: "",
    frequency: "weekly" as MissionRecurrence["frequency"],
    startsOn: plusDays(parisDay(new Date()), 1),
    endsOn: "",
    startTime: "09:00",
    durationHours: 2,
    location: firstClient?.address || "",
    billingMode: "fixed",
    billingRate: 0,
    billingQuantity: 1,
    amountExVat: 0,
    vatRate: data.organization?.default_vat_rate || 20,
    preferredWorkerId: "",
    notes: "",
  });
  const [open, setOpen] = useState(false),
    [draft, setDraft] = useState(blank),
    [editingId, setEditingId] = useState<string | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const recurrences = data.recurrences || [],
    properties = data.properties.filter(
      (property) => property.client_id === draft.clientId && property.active,
    ),
    availableServices = [
      ...data.services
        .filter((service) => service.client_id === draft.clientId)
        .map((service) => ({
          key: `client:${service.id}`,
          id: service.id,
          name: service.name,
          billingMode: service.billing_mode,
          rate: service.unit_price_ex_vat,
          vatRate: service.vat_rate,
          duration: service.default_duration_hours || 0,
        })),
      ...data.propertyServices
        .filter((service) => service.property_id === draft.propertyId)
        .map((service) => ({
          key: `property:${service.id}`,
          id: service.id,
          name: service.name,
          billingMode: service.billing_mode,
          rate: service.unit_price_ex_vat,
          vatRate: service.vat_rate,
          duration: service.default_duration_hours || 0,
        })),
    ];
  const frequencyLabels: Record<MissionRecurrence["frequency"], string> = {
    daily: "Chaque jour",
    weekly: "Chaque semaine",
    biweekly: "Toutes les 2 semaines",
    monthly: "Chaque mois",
  };

  function selectService(key: string) {
    const service = availableServices.find((item) => item.key === key);
    if (!service) {
      setDraft((current) => ({ ...current, serviceKey: "" }));
      return;
    }
    const duration = service.duration || draft.durationHours || 1,
      amount =
        service.billingMode === "hourly"
          ? service.rate * duration
          : service.rate;
    setDraft((current) => ({
      ...current,
      serviceKey: key,
      name: service.name,
      billingMode: service.billingMode,
      billingRate: service.rate,
      billingQuantity: 1,
      amountExVat: +amount.toFixed(2),
      vatRate: service.vatRate,
      durationHours: duration,
    }));
  }

  function editRecurrence(rule: MissionRecurrence) {
    setEditingId(rule.id);
    setDraft({
      clientId: rule.client_id,
      propertyId: rule.property_id || "",
      serviceKey: rule.property_service_id
        ? `property:${rule.property_service_id}`
        : `client:${rule.service_id}`,
      name: rule.name,
      frequency: rule.frequency,
      startsOn: rule.starts_on,
      endsOn: rule.ends_on || "",
      startTime: rule.start_time.slice(0, 5),
      durationHours: rule.duration_minutes / 60,
      location: rule.location,
      billingMode: rule.billing_mode,
      billingRate: rule.billing_rate,
      billingQuantity: rule.billing_quantity,
      amountExVat: rule.amount_ex_vat,
      vatRate: rule.vat_rate,
      preferredWorkerId: rule.preferred_worker_id || "",
      notes: rule.notes || "",
    });
    setMessage("");
    setOpen(true);
  }

  async function saveRecurrence(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.serviceKey) {
      setMessage("Choisissez une prestation rattachée au client ou au bien.");
      return;
    }
    setBusy(true);
    const propertyService = draft.serviceKey.startsWith("property:"),
      serviceId = draft.serviceKey.split(":")[1],
      editablePayload = {
          organization_id: profile.organization_id,
          client_id: draft.clientId,
          property_id: draft.propertyId || null,
          service_id: propertyService ? null : serviceId,
          property_service_id: propertyService ? serviceId : null,
          preferred_worker_id: draft.preferredWorkerId || null,
          name: draft.name.trim(),
          frequency: draft.frequency,
          starts_on: draft.startsOn,
          ends_on: draft.endsOn || null,
          start_time: draft.startTime,
          duration_minutes: Math.round(draft.durationHours * 60),
          location: draft.location.trim(),
          billing_mode: draft.billingMode,
          billing_rate: draft.billingRate,
          billing_quantity: draft.billingQuantity,
          amount_ex_vat: draft.amountExVat,
          vat_rate: draft.vatRate,
          notes: draft.notes.trim() || null,
        },
      result = editingId
        ? await supabase!
            .from("mission_recurrences")
            .update(editablePayload)
            .eq("id", editingId)
            .select("id")
            .single()
        : await supabase!
            .from("mission_recurrences")
            .insert({
              ...editablePayload,
              next_occurrence_on: draft.startsOn,
              created_by: profile.id,
            })
            .select("id")
            .single();
    if (result.error) {
      setBusy(false);
      setMessage(result.error.message);
      return;
    }
    if (editingId) {
      setBusy(false);
      setMessage(
        "Récurrence modifiée. Les missions déjà générées sont conservées ; les prochaines générations utiliseront ces nouveaux réglages.",
      );
      setOpen(false);
      setEditingId(null);
      setDraft(blank());
      reload();
      return;
    }
    const until = draft.endsOn || plusDays(draft.startsOn, 90),
      generated = await supabase!.rpc("generate_recurring_missions", {
        p_recurrence_id: result.data.id,
        p_until: until,
      });
    setBusy(false);
    if (generated.error) {
      setMessage(
        `Récurrence enregistrée, mais les missions n’ont pas été générées : ${generated.error.message}`,
      );
    } else {
      const summary = generated.data?.[0];
      setMessage(
        `Récurrence créée : ${summary?.created_count || 0} mission(s), ${summary?.assigned_count || 0} affectée(s), ${summary?.proposed_count || 0} proposée(s) et ${summary?.unassigned_count || 0} à affecter.`,
      );
      setOpen(false);
      setEditingId(null);
      setDraft(blank());
    }
    reload();
  }

  async function generateNext(rule: MissionRecurrence) {
    setBusy(true);
    const base =
        rule.next_occurrence_on > parisDay(new Date())
          ? rule.next_occurrence_on
          : parisDay(new Date()),
      until = rule.ends_on || plusDays(base, 90),
      { data: result, error } = await supabase!.rpc(
        "generate_recurring_missions",
        { p_recurrence_id: rule.id, p_until: until },
      );
    setBusy(false);
    const summary = result?.[0];
    setMessage(
      error
        ? error.message
        : `${summary?.created_count || 0} nouvelle(s) mission(s) générée(s), sans doublon.`,
    );
    if (!error) reload();
  }

  async function toggle(rule: MissionRecurrence) {
    const { error } = await supabase!
      .from("mission_recurrences")
      .update({ active: !rule.active })
      .eq("id", rule.id);
    setMessage(
      error
        ? error.message
        : !rule.active
          ? "Récurrence réactivée."
          : "Récurrence mise en pause ; les missions déjà créées sont conservées.",
    );
    if (!error) reload();
  }

  return (
    <section className="panel recurrence-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Planification automatique</span>
          <h2>Interventions récurrentes</h2>
        </div>
        <button onClick={() => { setEditingId(null); setDraft(blank()); setMessage(""); setOpen(true); }}>Nouvelle récurrence</button>
      </div>
      <p className="section-intro">
        Une règle génère les missions à venir une seule fois, avec la
        prestation, le tarif et la priorité d’affectation choisis.
      </p>
      {message && <Notice tone="info">{message}</Notice>}
      <div className="recurrence-list">
        {recurrences.map((rule) => {
          const client = data.clients.find(
              (item) => item.id === rule.client_id,
            ),
            property = data.properties.find(
              (item) => item.id === rule.property_id,
            ),
            worker = data.workers.find(
              (item) => item.id === rule.preferred_worker_id,
            );
          return (
            <article key={rule.id}>
              <div>
                <strong>{rule.name}</strong>
                <small>
                  {client?.name || "Client"}
                  {property ? ` · ${property.name}` : ""} ·{" "}
                  {frequencyLabels[rule.frequency]} à{" "}
                  {rule.start_time.slice(0, 5)}
                </small>
                <small>
                  {rule.duration_minutes / 60} h · {money(rule.amount_ex_vat)}{" "}
                  HT · TVA {rule.vat_rate} % · priorité :{" "}
                  {worker ? workerName(worker) : "automatique"}
                </small>
                <small>
                  Prochaine : {frDate(rule.next_occurrence_on)} · Généré
                  jusqu’au :{" "}
                  {rule.generated_until
                    ? frDate(rule.generated_until)
                    : "aucune date"}
                </small>
              </div>
              <div className="row-actions">
                <Status value={rule.active ? "active" : "paused"} />
                <button
                  className="secondary compact"
                  onClick={() => editRecurrence(rule)}
                >
                  Modifier
                </button>
                {rule.active && (
                  <button
                    className="secondary compact"
                    disabled={busy}
                    onClick={() => generateNext(rule)}
                  >
                    Générer 90 jours
                  </button>
                )}
                <button className="ghost compact" onClick={() => toggle(rule)}>
                  {rule.active ? "Mettre en pause" : "Réactiver"}
                </button>
              </div>
            </article>
          );
        })}
        {!recurrences.length && (
          <p className="empty">Aucune intervention récurrente configurée.</p>
        )}
      </div>
      {open && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail mission-editor-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Planification</span>
                <h2>{editingId ? "Modifier l’intervention récurrente" : "Nouvelle intervention récurrente"}</h2>
              </div>
              <button className="icon-close" onClick={() => { setOpen(false); setEditingId(null); }}>
                ×
              </button>
            </div>
            <form className="form-grid" onSubmit={saveRecurrence}>
              <label>
                Client
                <select
                  value={draft.clientId}
                  onChange={(e) => {
                    const client = data.clients.find(
                      (item) => item.id === e.target.value,
                    );
                    setDraft((current) => ({
                      ...current,
                      clientId: e.target.value,
                      propertyId: "",
                      serviceKey: "",
                      name: "",
                      location: client?.address || "",
                    }));
                  }}
                  required
                >
                  <option value="">Choisir</option>
                  {data.clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              {properties.length > 0 && (
                <label>
                  Bien
                  <select
                    value={draft.propertyId}
                    onChange={(e) => {
                      const property = properties.find(
                        (item) => item.id === e.target.value,
                      );
                      setDraft((current) => ({
                        ...current,
                        propertyId: e.target.value,
                        serviceKey: "",
                        name: "",
                        location:
                          property?.address ||
                          data.clients.find(
                            (item) => item.id === current.clientId,
                          )?.address ||
                          "",
                      }));
                    }}
                  >
                    <option value="">Client entier</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Prestation
                <select
                  value={draft.serviceKey}
                  onChange={(e) => selectService(e.target.value)}
                  required
                >
                  <option value="">Choisir</option>
                  {availableServices.map((service) => (
                    <option key={service.key} value={service.key}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Intitulé
                <input
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Fréquence
                <select
                  value={draft.frequency}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      frequency: e.target
                        .value as MissionRecurrence["frequency"],
                    }))
                  }
                >
                  {Object.entries(frequencyLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Première intervention
                <input
                  type="date"
                  value={draft.startsOn}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      startsOn: e.target.value,
                    }))
                  }
                  onInput={(e) => {
                    const startsOn = e.currentTarget.value;
                    setDraft((current) => ({ ...current, startsOn }));
                  }}
                  required
                />
              </label>
              <label>
                Fin de récurrence (facultatif)
                <input
                  type="date"
                  min={draft.startsOn}
                  value={draft.endsOn}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      endsOn: e.target.value,
                    }))
                  }
                  onInput={(e) => {
                    const endsOn = e.currentTarget.value;
                    setDraft((current) => ({ ...current, endsOn }));
                  }}
                />
              </label>
              <label>
                Heure de début
                <input
                  type="time"
                  value={draft.startTime}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      startTime: e.target.value,
                    }))
                  }
                  onInput={(e) => {
                    const startTime = e.currentTarget.value;
                    setDraft((current) => ({ ...current, startTime }));
                  }}
                  required
                />
              </label>
              <label>
                Temps total prévu (h)
                <input
                  type="number"
                  min="0.25"
                  max="168"
                  step="0.25"
                  value={draft.durationHours}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      durationHours: +e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="wide">
                Adresse d’intervention
                <input
                  value={draft.location}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      location: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Montant HT par mission
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.amountExVat}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      amountExVat: +e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                TVA (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={draft.vatRate}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      vatRate: +e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="wide">
                Personne prioritaire
                <select
                  value={draft.preferredWorkerId}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      preferredWorkerId: e.target.value,
                    }))
                  }
                >
                  <option value="">
                    Automatique : employé disponible, puis ST référent
                  </option>
                  {data.workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {workerName(worker)} ·{" "}
                      {worker.kind === "employee" ? "Employé" : "ST"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                Notes et consignes
                <textarea
                  value={draft.notes}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      notes: e.target.value,
                    }))
                  }
                />
              </label>
              <button disabled={busy}>
                {busy
                  ? "Enregistrement…"
                  : editingId
                    ? "Enregistrer les modifications"
                    : "Créer et générer les missions"}
              </button>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

function AdminBilling({
  data,
  reload,
  onSelectMission,
  onNewQuote,
  onNewInvoice,
  guidedQuestionnaireRequest,
  onGuidedQuestionnaireRequestConsumed,
  invoiceRequest,
  onInvoiceRequestConsumed,
  servicePricingRequest,
  onServicePricingRequestConsumed,
  onReturnToProfitability,
  onOpenStockItem,
  catalogOpenRequest,
  onCatalogOpenRequestConsumed,
}: {
  data: DataState;
  reload: () => void;
  onSelectMission: (mission: Mission) => void;
  onNewQuote: () => void;
  onNewInvoice: () => void;
  guidedQuestionnaireRequest?: string | null;
  onGuidedQuestionnaireRequestConsumed?: () => void;
  invoiceRequest?: string | null;
  onInvoiceRequestConsumed?: () => void;
  servicePricingRequest?: BillingCatalogFocus | null;
  onServicePricingRequestConsumed?: () => void;
  onReturnToProfitability?: () => void;
  onOpenStockItem?: (itemId: string) => void;
  catalogOpenRequest?: number | null;
  onCatalogOpenRequestConsumed?: () => void;
}) {
  const [view, setView] = useState<
    | "quotes"
    | "projects"
    | "receivables"
    | "paid"
    | "missions"
    | "commissions"
    | "referral_commissions"
    | "payments"
    | "catalog"
  >("quotes");
  const [selection, setSelection] = useState<{
    kind: "quote" | "invoice";
    id: string;
  } | null>(null);
  const [status, setStatus] = useState("");
  const [paymentTermsMode, setPaymentTermsMode] =
    useState<PaymentTermsMode>("days_after_issue");
  const [paymentTermsDays, setPaymentTermsDays] = useState(30);
  const [documentDueOn, setDocumentDueOn] = useState("");
  const [quoteValidUntil, setQuoteValidUntil] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentLabel, setAttachmentLabel] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(parisDay(new Date()));
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  const [paymentReference, setPaymentReference] = useState("");
  const [reminderDate, setReminderDate] = useState(parisDay(new Date()));
  const [reminderSubject, setReminderSubject] = useState("Relance de facture");
  const [reminderBody, setReminderBody] = useState("");
  const [collectionEditor, setCollectionEditor] = useState<{
    invoice_id: string;
    track: NonNullable<Invoice["collection_track"]>;
    next_review_on: string;
    notes: string;
  } | null>(null);
  const [derivedKind, setDerivedKind] = useState<
    "deposit" | "progress" | "credit"
  >("deposit");
  const [derivedPercent, setDerivedPercent] = useState(30);
  const [creditReason, setCreditReason] = useState("");
  const [selectedPaymentCandidate, setSelectedPaymentCandidate] =
    useState<BillingPaymentCandidate | null>(null);
  const [paymentCandidateAllocations, setPaymentCandidateAllocations] =
    useState<Array<{ invoice_id: string; amount: string }>>([]);
  const [paymentCandidateMethod, setPaymentCandidateMethod] =
    useState("transfer");
  const [paymentCandidateReference, setPaymentCandidateReference] =
    useState("");
  const [paymentCandidateNote, setPaymentCandidateNote] = useState("");
  const [referralReviewNotes, setReferralReviewNotes] = useState<
    Record<string, string>
  >({});
  const [referralPaymentReferences, setReferralPaymentReferences] = useState<
    Record<string, string>
  >({});
  const [paymentSyncBusy, setPaymentSyncBusy] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogEditor, setCatalogEditor] = useState<{
    id?: string;
    scope: "client" | "property";
    client_id: string;
    property_id: string;
    name: string;
    billing_mode: string;
    unit_price_ex_vat: number;
    vat_rate: number;
    default_duration_hours: number | null;
    notes: string;
  } | null>(null);
  const [catalogPricingAnalysis, setCatalogPricingAnalysis] = useState<
    NonNullable<BillingCatalogFocus["pricingAnalysis"]> | null
  >(null);
  const billingContentRef = useRef<HTMLDivElement>(null);
  const [catalogModelEditor, setCatalogModelEditor] = useState<{
    id?: string;
    item_code: string;
    name: string;
    description: string;
    item_kind: ServiceCatalogItem["item_kind"];
    default_billing_mode: string;
    default_unit_label: string;
    default_unit_price_ex_vat: number;
    default_vat_rate: number;
    default_duration_hours: number | null;
    target_margin_rate: number | null;
    change_reason: string;
  } | null>(null);
  const [catalogCreationFlow, setCatalogCreationFlow] = useState<
    "catalog" | "guided"
  >("catalog");
  const [catalogCreationTarget, setCatalogCreationTarget] = useState<{
    questionIndex: number;
    ruleIndex: number;
  } | null>(null);
  const [missionChecklistEditor, setMissionChecklistEditor] = useState<{
    catalog_item_id: string;
    item_name: string;
    name: string;
    instructions: string;
    change_reason: string;
    latest_version: number | null;
    latest_status: "draft" | "published" | "archived" | null;
    items: Array<{
      item_key: string;
      label: string;
      instructions: string;
      is_required: boolean;
      minimum_photo_count: number;
      zone_key: string;
      zone_label: string;
      zone_repeatable: boolean;
    }>;
  } | null>(null);
  const [catalogApplyEditor, setCatalogApplyEditor] = useState<{
    catalog_item_id: string;
    client_id: string;
    property_id: string;
    name: string;
    billing_mode: string;
    unit_price_ex_vat: number;
    vat_rate: number;
    default_duration_hours: number | null;
    notes: string;
  } | null>(null);
  const [qualificationEditor, setQualificationEditor] = useState<{
    catalog_item_id: string;
    name: string;
    description: string;
    change_reason: string;
    questions: Array<{
      id: string;
      question_key: string;
      label: string;
      help_text: string;
      response_type: ServiceQualificationQuestion["response_type"];
      required: boolean;
      unit_label: string;
      options: Array<{ value: string; label: string }>;
      costing_target: ServiceQualificationQuestion["costing_target"];
      pricing_rules: QualificationPricingRule[];
    }>;
  } | null>(null);
  const [costingProfileEditor, setCostingProfileEditor] = useState<{
    catalog_item_id: string;
    output_unit: ServiceCostingProfile["output_unit"];
    expected_output_per_labor_hour: string;
    default_team_size: number;
    default_labor_cost_per_hour: string;
    default_travel_cost_ex_vat: number;
    contingency_rate: number;
    target_mode: "margin" | "markup";
    target_rate: number;
    confidence: ServiceCostingProfile["confidence"];
    required_skills_text: string;
    assumptions_notes: string;
    components: Array<{
      id: string;
      component_type: "equipment" | "other";
      business_kind: "equipment" | "purchase" | "other";
      label: string;
      cost_basis: "per_job" | "per_elapsed_hour" | "per_output_unit";
      quantity: number;
      unit_label: string;
      unit_cost_ex_vat: number;
      supplier_id: string;
      purchase_reference: string;
      purchase_url: string;
      source_note: string;
    }>;
  } | null>(null);
  const [yieldMinimumSamples, setYieldMinimumSamples] = useState(3);
  const [yieldOutlierTolerance, setYieldOutlierTolerance] = useState(35);
  const [yieldDecisionNotes, setYieldDecisionNotes] = useState("");
  const [quoteCostingEditor, setQuoteCostingEditor] = useState<{
    quote_line_id: string;
    catalog_item_id: string;
    output_quantity: string;
    output_unit: ServiceCostingProfile["output_unit"];
    total_labor_hours: string;
    labor_cost_per_hour: string;
    team_size: number;
    travel_cost_ex_vat: number;
    other_cost_ex_vat: number;
    contingency_rate: number;
    target_mode: "margin" | "markup";
    target_rate: number;
    proposed_total_ex_vat: number;
    notes: string;
  } | null>(null);
  const [qualificationAnswers, setQualificationAnswers] = useState<Record<string, unknown>>({});
  const [qualificationRunId, setQualificationRunId] = useState("");
  const [qualificationNotes, setQualificationNotes] = useState("");
  const [qualificationPricingPreview, setQualificationPricingPreview] =
    useState<Record<string, unknown>>({});
  const [lineEditor, setLineEditor] = useState<{
    id?: string;
    description: string;
    quantity: number;
    unit_price_ex_vat: number;
    vat_rate: number;
    service_id: string;
  }>({
    description: "",
    quantity: 1,
    unit_price_ex_vat: 0,
    vat_rate: data.organization?.default_vat_rate ?? 20,
    service_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [henrriBusy, setHenrriBusy] = useState<"" | "preview" | "create_draft">("");
  const [henrriLifecycleBusy, setHenrriLifecycleBusy] = useState<
    "" | "preview" | "finalize_quote" | "transform_to_invoice"
  >("");
  const [henrriPdfBusy, setHenrriPdfBusy] = useState<"" | "pdf" | "facturx">("");
  const [henrriExport, setHenrriExport] =
    useState<BillingDocumentPreparation | null>(null);
  const [henrriLifecycle, setHenrriLifecycle] =
    useState<BillingQuoteLifecycle | null>(null);
  const billingProvider = supabase
    ? createHenrriBillingProvider(supabase, {
        privateDocumentsEnabled: hasDeploymentCapability(
          data,
          "henrri_documents",
        ),
        paymentReadEnabled: hasDeploymentCapability(
          data,
          "billing_payment_reconciliation",
        ),
        quoteTransformationEnabled: hasDeploymentCapability(
          data,
          "henrri_quote_conversion",
        ),
      })
    : null;
  const [sourcingDecisionNotes, setSourcingDecisionNotes] = useState("");
  const [sourcingFinalPrice, setSourcingFinalPrice] = useState("");
  const recordBillingOperation = async (lastError: string | null) => {
    if (!data.organization || !billingProvider) return;
    await recordIntegrationOperation(supabase!, {
      organizationId: data.organization.id,
      provider: billingProvider.id,
      error: lastError,
    });
  };
  const selectedQuote =
    selection?.kind === "quote"
      ? data.quotes.find((item) => item.id === selection.id) || null
      : null;
  const selectedInvoice =
    selection?.kind === "invoice"
      ? data.invoices.find((item) => item.id === selection.id) || null
      : null;
  const selectedDocument = selectedQuote || selectedInvoice;
  const selectedExternalLink = selection && billingProvider
    ? (data.integrationExternalLinks || []).find(
        (link) =>
          link.provider === billingProvider.id &&
          link.entity_type === selection.kind &&
          link.local_id === selection.id,
      ) || null
    : null;
  const selectedExternalMetadata = selectedExternalLink?.metadata || {};
  const externalState = String(selectedExternalMetadata.state || "linked");
  const externalStateLabel =
    ({
      building: "Création en cours",
      draft_ready: "Brouillon prêt à relire",
      finalized: "Document finalisé",
      line_error: "Erreur sur une ligne",
      linked: "Document lié",
    } as Record<string, string>)[externalState] || externalState;
  const selectedTransformation = selectedQuote
    ? (data.billingProviderDocumentTransformations || []).find(
        (item) => item.source_quote_id === selectedQuote.id,
      ) || null
    : null;
  const selectedSourcingWorkflow = selectedQuote
    ? (data.quoteSourcingWorkflows || []).find(
        (item) => item.quote_id === selectedQuote.id,
      ) || null
    : null;
  const selectedSourcingOffers = selectedSourcingWorkflow
    ? (data.quoteSourcingOffers || [])
        .filter((item) => item.workflow_id === selectedSourcingWorkflow.id)
        .sort((a, b) => b.version - a.version)
    : [];
  const latestSourcingOffer = selectedSourcingOffers[0] || null;
  const canEditLines = !!selectedDocument &&
    (selection?.kind === "quote"
      ? selectedDocument.status === "draft"
      : ["draft", "ready"].includes(selectedDocument.status));
  const allowedStatusOptions = selectedDocument
    ? Array.from(
        new Set([
          selectedDocument.status,
          ...(selection?.kind === "quote"
            ? ({
                draft: ["sent", "cancelled"],
                sent: ["accepted", "rejected", "expired", "cancelled"],
                accepted: ["cancelled"],
                rejected: [],
                expired: [],
                cancelled: [],
              }[selectedDocument.status] || [])
            : ({
                draft: ["ready", "cancelled"],
                ready: ["draft", "sent", "cancelled"],
                sent: [
                  "partially_paid",
                  "paid",
                  "overdue",
                  "cancelled",
                  "credited",
                ],
                partially_paid: ["paid", "overdue", "cancelled", "credited"],
                overdue: ["partially_paid", "paid", "cancelled", "credited"],
                paid: ["credited"],
                credited: [],
                cancelled: [],
              }[selectedDocument.status] || [])),
        ]),
      )
    : [];
  const lines = selectedQuote
    ? (data.quoteLines || []).filter(
        (line) => line.quote_id === selectedQuote.id,
      )
    : selectedInvoice
      ? (data.invoiceLines || []).filter(
          (line) => line.invoice_id === selectedInvoice.id,
        )
      : [];
  const attachments = (data.commercialAttachments || []).filter((item) =>
    selectedQuote
      ? item.quote_id === selectedQuote.id
      : selectedInvoice
        ? item.invoice_id === selectedInvoice.id
        : false,
  );
  const events = (data.commercialEvents || []).filter((item) =>
    selectedQuote
      ? item.quote_id === selectedQuote.id
      : selectedInvoice
        ? item.invoice_id === selectedInvoice.id
        : false,
  );
  const payments = selectedInvoice
    ? (data.invoicePayments || []).filter(
        (item) => item.invoice_id === selectedInvoice.id,
      )
    : [];
  const paymentCandidates = data.paymentReconciliationCandidates || [];
  const pendingPaymentCandidates = paymentCandidates.filter(
    (candidate) => candidate.review_status === "pending",
  );
  const referralCommissions = data.invoiceReferralCommissions || [];
  const referralCommissionsToReview = referralCommissions.filter(
    (item) => ["estimated", "pending_approval"].includes(item.status),
  );
  const referralCommissionDue = referralCommissions
    .filter((item) =>
      ["estimated", "pending_approval", "approved"].includes(item.status),
    )
    .reduce((sum, item) => sum + Number(item.commission_amount_ex_vat || 0), 0);
  const reminders = selectedInvoice
    ? (data.invoiceReminders || []).filter(
        (item) => item.invoice_id === selectedInvoice.id,
      )
    : [];
  const derivedInvoices = selectedInvoice
    ? data.invoices.filter(
        (item) => item.parent_invoice_id === selectedInvoice.id,
      )
    : [];
  const visibleInvoices = data.invoices.filter((invoice) =>
    view === "paid" ? invoice.status === "paid" : invoice.status !== "paid",
  );
  const receivableInvoices = data.invoices.filter(
      (invoice) =>
        ["sent", "partially_paid", "overdue"].includes(invoice.status) &&
        Number(invoice.total_inc_vat || 0) - Number(invoice.amount_paid || 0) > 0.005,
    ),
    today = parisDay(new Date()),
    dayDifference = (later: string, earlier: string) =>
      Math.round(
        (new Date(`${later}T12:00:00`).getTime() -
          new Date(`${earlier}T12:00:00`).getTime()) /
          86400000,
      ),
    remainingForInvoice = (invoice: Invoice) =>
      Math.max(
        0,
        Number(invoice.total_inc_vat || 0) - Number(invoice.amount_paid || 0),
      ),
    totalReceivables = receivableInvoices.reduce(
      (sum, invoice) => sum + remainingForInvoice(invoice),
      0,
    ),
    overdueReceivables = receivableInvoices
      .filter((invoice) => Boolean(invoice.due_on && invoice.due_on < today))
      .reduce((sum, invoice) => sum + remainingForInvoice(invoice), 0),
    legalReceivables = receivableInvoices
      .filter((invoice) => invoice.collection_track === "legal_procedure")
      .reduce((sum, invoice) => sum + remainingForInvoice(invoice), 0),
    reviewsDue = receivableInvoices.filter(
      (invoice) =>
        Boolean(
          invoice.collection_next_review_on &&
            invoice.collection_next_review_on <= today,
        ),
    ).length,
    collectionTrackLabels: Record<
      NonNullable<Invoice["collection_track"]>,
      string
    > = {
      expected: "Échéance normale",
      friendly: "Relance amiable",
      formal_notice: "Mise en demeure à préparer",
      legal_procedure: "Procédure en cours",
      disputed: "Litige à résoudre",
      payment_plan: "Échéancier convenu",
    },
    reminderMilestones = [1, 7, 15] as const,
    collectionInvoice = collectionEditor
      ? data.invoices.find((invoice) => invoice.id === collectionEditor.invoice_id) ||
        null
      : null;
  const normalizedCatalogSearch = catalogSearch.trim().toLocaleLowerCase("fr"),
    visibleCatalogModels = (data.serviceCatalogItems || []).filter((item) =>
      !normalizedCatalogSearch ||
      `${item.item_code} ${item.name} ${item.description || ""} ${item.item_kind}`
        .toLocaleLowerCase("fr")
        .includes(normalizedCatalogSearch),
    ),
    visibleClientServices = data.services.filter((service) => {
      if (service.active === false) return false;
      const client = data.clients.find((item) => item.id === service.client_id);
      return !normalizedCatalogSearch ||
        `${service.name} ${client?.name || ""} ${service.billing_mode}`
          .toLocaleLowerCase("fr")
          .includes(normalizedCatalogSearch);
    }),
    visiblePropertyServices = data.propertyServices.filter((service) => {
      if (service.active === false) return false;
      const property = data.properties.find((item) => item.id === service.property_id),
        client = data.clients.find((item) => item.id === property?.client_id);
      return !normalizedCatalogSearch ||
        `${service.name} ${client?.name || ""} ${property?.name || ""} ${service.billing_mode}`
          .toLocaleLowerCase("fr")
          .includes(normalizedCatalogSearch);
    }),
    visibleSaleItems = (data.inventoryItems || []).filter((item) => {
      if (!item.active || !item.sale_enabled) return false;
      return !normalizedCatalogSearch ||
        `${item.name} ${item.sale_label || ""} ${item.sku || ""}`
          .toLocaleLowerCase("fr")
          .includes(normalizedCatalogSearch);
    });
  const openBillingView = (
    nextView:
      | "quotes"
      | "projects"
      | "receivables"
      | "paid"
      | "missions"
      | "commissions"
      | "referral_commissions"
      | "payments"
      | "catalog",
  ) => {
    setView(nextView);
    window.requestAnimationFrame(() =>
      billingContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      }),
    );
  };
  const editCatalogModel = (
    item?: ServiceCatalogItem,
    flow: "catalog" | "guided" = "catalog",
  ) => {
    setCatalogCreationFlow(flow);
    setCatalogModelEditor(
      item
        ? {
            id: item.id,
            item_code: item.item_code,
            name: item.name,
            description: item.description || "",
            item_kind: item.item_kind,
            default_billing_mode: item.default_billing_mode,
            default_unit_label: item.default_unit_label,
            default_unit_price_ex_vat: Number(item.default_unit_price_ex_vat),
            default_vat_rate: Number(item.default_vat_rate),
            default_duration_hours: item.default_duration_hours == null
              ? null
              : Number(item.default_duration_hours),
            target_margin_rate: item.target_margin_rate == null
              ? null
              : Number(item.target_margin_rate),
            change_reason: "",
          }
        : {
            item_code: "",
            name: "",
            description: "",
            item_kind: "service",
            default_billing_mode: "fixed",
            default_unit_label: "forfait",
            default_unit_price_ex_vat: 0,
            default_vat_rate: data.organization?.default_vat_rate ?? 20,
            default_duration_hours: null,
            target_margin_rate: data.organization?.target_margin_rate ?? null,
            change_reason: "Création du modèle",
          },
    );
    setMessage("");
  };
  const createCatalogModelForRule = (
    questionIndex: number,
    ruleIndex: number,
    rule: QualificationPricingRule,
  ) => {
    setCatalogCreationTarget({ questionIndex, ruleIndex });
    setCatalogCreationFlow("catalog");
    setCatalogModelEditor({
      item_code: "",
      name: rule.line_item_name?.trim() || rule.label.trim(),
      description: "",
      item_kind: "service",
      default_billing_mode: "fixed",
      default_unit_label: "forfait",
      default_unit_price_ex_vat: Number(
        rule.line_item_unit_price_ex_vat || 0,
      ),
      default_vat_rate: Number(
        rule.line_item_vat_rate ?? data.organization?.default_vat_rate ?? 20,
      ),
      default_duration_hours: null,
      target_margin_rate: data.organization?.target_margin_rate ?? null,
      change_reason: "Création depuis une conséquence de devis guidé",
    });
    setMessage("");
  };
  const latestCostingProfile = (catalogItemId: string) =>
    (data.serviceCostingProfiles || [])
      .filter((profile) => profile.catalog_item_id === catalogItemId)
      .sort((a, b) => b.version - a.version)[0] || null;
  const latestQualification = (catalogItemId: string, publishedOnly = false) =>
    (data.serviceQualificationQuestionnaires || [])
      .filter(
        (questionnaire) =>
          questionnaire.catalog_item_id === catalogItemId &&
          (!publishedOnly || questionnaire.status === "published"),
      )
      .sort((a, b) => b.version - a.version)[0] || null;
  const qualificationQuestions = (questionnaireId: string) =>
    (data.serviceQualificationQuestions || [])
      .filter((question) => question.questionnaire_id === questionnaireId)
      .sort((a, b) => a.position - b.position);
  const openQualificationEditor = (item: ServiceCatalogItem) => {
    const current = latestQualification(item.id),
      questions = current ? qualificationQuestions(current.id) : [];
    setQualificationEditor({
      catalog_item_id: item.id,
      name: current?.name || `Qualification · ${item.name}`,
      description: current?.description || "",
      change_reason: current ? "" : "Création du questionnaire",
      questions: questions.map((question) => ({
        id: question.id,
        question_key: question.question_key,
        label: question.label,
        help_text: question.help_text || "",
        response_type: question.response_type,
        required: question.required,
        unit_label: question.unit_label || "",
        options: question.options || [],
        costing_target: question.costing_target,
        pricing_rules: question.pricing_rules || [],
      })),
    });
    setMessage("");
  };
  const openMissionChecklistEditor = async (item: ServiceCatalogItem) => {
    setBusy(true);
    setMessage("");
    const checklistResult = await supabase!
      .from("service_mission_checklists")
      .select("id,version,name,instructions,status,is_current_version,change_reason")
      .eq("catalog_item_id", item.id)
      .order("version", { ascending: false });
    if (checklistResult.error) {
      setBusy(false);
      setMessage(checklistResult.error.message);
      return;
    }
    const latest = checklistResult.data?.[0] || null;
    const itemsResult = latest
      ? await supabase!
          .from("service_mission_checklist_items")
          .select("item_key,label,instructions,is_required,minimum_photo_count,position,zone_key,zone_label,zone_repeatable")
          .eq("checklist_id", latest.id)
          .order("position")
      : { data: [], error: null };
    setBusy(false);
    if (itemsResult.error) {
      setMessage(itemsResult.error.message);
      return;
    }
    setMissionChecklistEditor({
      catalog_item_id: item.id,
      item_name: item.name,
      name: latest?.name || `Checklist · ${item.name}`,
      instructions: latest?.instructions || "Validez chaque étape obligatoire avant de clôturer la mission.",
      change_reason: latest ? "" : "Création de la checklist terrain",
      latest_version: latest?.version || null,
      latest_status: latest?.status || null,
      items: (itemsResult.data || []).length
        ? (itemsResult.data || []).map((entry) => ({
            item_key: entry.item_key,
            label: entry.label,
            instructions: entry.instructions || "",
            is_required: entry.is_required,
            minimum_photo_count: Number(entry.minimum_photo_count || 0),
            zone_key: entry.zone_key || "general",
            zone_label: entry.zone_label || "Général",
            zone_repeatable: Boolean(entry.zone_repeatable),
          }))
        : [
            {
              item_key: "step-1",
              label: "Contrôler et valider le résultat de la mission",
              instructions: "",
              is_required: true,
              minimum_photo_count: 0,
              zone_key: "general",
              zone_label: "Général",
              zone_repeatable: false,
            },
          ],
    });
  };
  const saveMissionChecklist = async (publish: boolean) => {
    if (!missionChecklistEditor) return;
    if (
      missionChecklistEditor.name.trim().length < 3 ||
      missionChecklistEditor.change_reason.trim().length < 3 ||
      !missionChecklistEditor.items.length ||
      missionChecklistEditor.items.some(
        (item) =>
          item.label.trim().length < 2 ||
          item.minimum_photo_count < 0 ||
          item.minimum_photo_count > 20,
      )
    ) {
      setMessage("Renseignez le nom, le motif de version et chaque étape (0 à 20 photos obligatoires).");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("save_service_mission_checklist", {
      p_catalog_item_id: missionChecklistEditor.catalog_item_id,
      p_name: missionChecklistEditor.name.trim(),
      p_instructions: missionChecklistEditor.instructions.trim() || null,
      p_change_reason: missionChecklistEditor.change_reason.trim(),
      p_publish: publish,
      p_items: missionChecklistEditor.items.map((item, index) => ({
        item_key: item.item_key || `step-${index + 1}`,
        label: item.label.trim(),
        instructions: item.instructions.trim() || null,
        is_required: item.is_required || item.minimum_photo_count > 0,
        minimum_photo_count: item.minimum_photo_count,
        zone_key: item.zone_key || null,
        zone_label: item.zone_label.trim() || "Général",
        zone_repeatable: item.zone_repeatable,
      })),
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : publish
          ? "Nouvelle version publiée. Les missions déjà commencées conservent leur checklist d’origine."
          : "Brouillon versionné enregistré. La checklist actuellement publiée reste active.",
    );
    if (!result.error) {
      setMissionChecklistEditor(null);
      reload();
    }
  };
  useEffect(() => {
    if (!guidedQuestionnaireRequest) return;
    openBillingView("catalog");
    if (guidedQuestionnaireRequest === "new")
      editCatalogModel(undefined, "guided");
    else {
      const item = (data.serviceCatalogItems || []).find(
        (candidate) => candidate.id === guidedQuestionnaireRequest,
      );
      if (item) openQualificationEditor(item);
      else setMessage("Le sujet de devis guidé demandé est introuvable ou archivé.");
    }
    onGuidedQuestionnaireRequestConsumed?.();
  }, [guidedQuestionnaireRequest]);
  useEffect(() => {
    if (!servicePricingRequest) return;
    openBillingView("catalog");
    const service =
      servicePricingRequest.scope === "property"
        ? data.propertyServices.find(
            (candidate) => candidate.id === servicePricingRequest.serviceId,
          )
        : data.services.find(
            (candidate) => candidate.id === servicePricingRequest.serviceId,
          );
    if (service)
      editCatalogService(service, servicePricingRequest.scope);
    else
      setMessage(
        `La prestation « ${servicePricingRequest.label} » n’est plus active ou n’est pas disponible dans ce contexte.`,
      );
    setCatalogPricingAnalysis(servicePricingRequest.pricingAnalysis || null);
    onServicePricingRequestConsumed?.();
  }, [servicePricingRequest]);
  useEffect(() => {
    if (!catalogOpenRequest) return;
    openBillingView("catalog");
    onCatalogOpenRequestConsumed?.();
  }, [catalogOpenRequest]);
  useEffect(() => {
    if (!invoiceRequest) return;
    const invoice = data.invoices.find((candidate) => candidate.id === invoiceRequest);
    if (invoice) {
      setView(invoice.status === "paid" ? "paid" : "receivables");
      setSelection({ kind: "invoice", id: invoice.id });
    }
    onInvoiceRequestConsumed?.();
  }, [invoiceRequest]);
  const prepareEndConstructionQualification = () => {
    if (!qualificationEditor) return;
    setQualificationEditor({
      ...qualificationEditor,
      name: "Qualification fin de chantier",
      description:
        "Collecte les éléments objectifs du chantier. Les critères peuvent piloter le tarif et le temps lorsque leurs règles ont été définies et validées.",
      change_reason:
        qualificationEditor.change_reason || "Ajout du modèle fin de chantier",
      questions: [
        { id: crypto.randomUUID(), question_key: "surface_m2", label: "Surface totale à traiter", help_text: "Surface utile estimée du chantier. Elle devient la quantité de base du calcul.", response_type: "number", required: true, unit_label: "m2", options: [], costing_target: "output_quantity", pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "etat_general", label: "État général du chantier", help_text: "Ajoutez à chaque niveau un supplément tarifaire et/ou un temps humain par m² validé.", response_type: "single_choice", required: true, unit_label: "", options: [{ value: "leger", label: "Léger" }, { value: "courant", label: "Courant" }, { value: "tres_impacte", label: "Très impacté" }], costing_target: null, pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "surface_vitree_m2", label: "Surface vitrée estimée", help_text: "Laissez vide si elle n’est pas encore mesurable.", response_type: "number", required: false, unit_label: "m2", options: [], costing_target: null, pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "residus_lourds", label: "Présence de résidus lourds ou collés", help_text: "Plâtre, colle, peinture ou protections difficiles à retirer. Une règle peut ajouter du temps ou un coût.", response_type: "boolean", required: true, unit_label: "", options: [], costing_target: null, pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "acces_contraint", label: "Accès, stationnement ou manutention contraints", help_text: "Une règle peut ajouter un forfait ou augmenter le temps estimé.", response_type: "boolean", required: true, unit_label: "", options: [], costing_target: null, pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "materiel_specifique", label: "Matériel ou location spécifique à prévoir", help_text: "Le coût réel reste à renseigner et valider par l’administrateur.", response_type: "boolean", required: true, unit_label: "", options: [], costing_target: null, pricing_rules: [] },
        { id: crypto.randomUUID(), question_key: "observations", label: "Observations complémentaires", help_text: "", response_type: "text", required: false, unit_label: "", options: [], costing_target: null, pricing_rules: [] },
      ],
    });
  };
  const addQualificationRule = (questionIndex: number) => {
    setQualificationEditor((current) => {
      if (!current) return current;
      const questions = current.questions.map((question, index) => {
        if (index !== questionIndex) return question;
        const comparisonValue: string | number | boolean =
          question.response_type === "boolean"
            ? true
            : ["number", "integer"].includes(question.response_type)
              ? 0
              : question.options[0]?.value || "";
        return {
          ...question,
          pricing_rules: [
            ...question.pricing_rules,
            {
              rule_id: questionnaireTechnicalId("r"),
              label: "",
              operator: (question.response_type === "multiple_choice"
                ? "contains"
                : "equals") as QualificationPricingRule["operator"],
              comparison_value: comparisonValue,
              comparison_value_max: null,
              unit_price_delta_ex_vat: null,
              unit_price_multiplier: null,
              labor_hours_per_output_unit: null,
              labor_time_multiplier: null,
              fixed_cost_delta_ex_vat: null,
              inventory_item_id: null,
              inventory_application_passes: 1,
              inventory_billing_mode: "included" as const,
              description_line: null,
              track_activity_time: false,
              activity_label: null,
              line_item_mode: null,
              line_item_catalog_id: null,
              line_item_name: null,
              line_item_quantity: 1,
              line_item_unit_price_ex_vat: null,
              line_item_vat_rate: 20,
            },
          ],
        };
      });
      return { ...current, questions };
    });
  };
  const updateQualificationRule = (
    questionIndex: number,
    ruleIndex: number,
    patch: Partial<QualificationPricingRule>,
  ) => {
    setQualificationEditor((current) =>
      current && ({
        ...current,
        questions: current.questions.map((question, index) =>
          index === questionIndex
            ? {
                ...question,
                pricing_rules: question.pricing_rules.map((rule, currentRuleIndex) =>
                  currentRuleIndex === ruleIndex ? { ...rule, ...patch } : rule,
                ),
              }
            : question,
        ),
      }),
    );
  };
  const removeQualificationRule = (questionIndex: number, ruleIndex: number) => {
    setQualificationEditor((current) =>
      current && ({
        ...current,
        questions: current.questions.map((question, index) =>
          index === questionIndex
            ? {
                ...question,
                pricing_rules: question.pricing_rules.filter(
                  (_, currentRuleIndex) => currentRuleIndex !== ruleIndex,
                ),
              }
            : question,
        ),
      }),
    );
  };
  const saveQualificationQuestionnaire = async (publish: boolean) => {
    if (!qualificationEditor) return;
    const keys = qualificationEditor.questions.map((question) => question.question_key.trim());
    if (!qualificationEditor.name.trim() || !qualificationEditor.change_reason.trim()) {
      setMessage("Le nom et le motif de cette nouvelle version sont obligatoires.");
      return;
    }
    if (
      !keys.length ||
      keys.some((key) => !/^[a-z][a-z0-9_]{0,62}$/.test(key)) ||
      new Set(keys).size !== keys.length ||
      qualificationEditor.questions.some(
        (question) =>
          !question.label.trim() ||
          (["single_choice", "multiple_choice"].includes(question.response_type) &&
            !question.options.length) ||
          question.pricing_rules.some(
            (rule) =>
              !rule.rule_id.trim() ||
              !/^[a-z][a-z0-9_]{0,62}$/.test(rule.rule_id) ||
              !rule.label.trim() ||
              [
                rule.unit_price_delta_ex_vat,
                rule.unit_price_multiplier,
                rule.labor_hours_per_output_unit,
                rule.labor_time_multiplier,
                rule.fixed_cost_delta_ex_vat,
                rule.inventory_item_id,
                rule.description_line,
                rule.line_item_catalog_id,
                rule.line_item_name,
              ].every((value) => value == null || value === "") ||
              (rule.line_item_mode === "catalog" &&
                !rule.line_item_catalog_id) ||
              (["client", "one_off"].includes(rule.line_item_mode || "") &&
                (!(rule.line_item_name || "").trim() ||
                  rule.line_item_unit_price_ex_vat == null)) ||
              (Boolean(rule.track_activity_time) &&
                (rule.activity_label || "").trim().length < 2),
          ),
      )
    ) {
      setMessage("Vérifiez les clés uniques, les libellés, les choix et les effets de chaque règle.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("save_service_qualification_questionnaire", {
      p_catalog_item_id: qualificationEditor.catalog_item_id,
      p_name: qualificationEditor.name.trim(),
      p_description: qualificationEditor.description.trim() || null,
      p_questions: qualificationEditor.questions.map(({ id: _id, ...question }) => ({
        ...question,
        question_key: question.question_key.trim(),
        label: question.label.trim(),
        help_text: question.help_text.trim() || null,
        unit_label: question.unit_label.trim() || null,
        options: ["single_choice", "multiple_choice"].includes(question.response_type)
          ? question.options
          : [],
        pricing_rules: question.pricing_rules.map((rule) => ({
          ...rule,
          rule_id: rule.rule_id.trim(),
          label: rule.label.trim(),
          fixed_cost_delta_ex_vat:
            rule.fixed_cost_delta_ex_vat == null &&
            (rule.inventory_item_id ||
              rule.description_line ||
              rule.line_item_mode)
              ? 0
              : rule.fixed_cost_delta_ex_vat,
          description_line: rule.description_line?.trim() || null,
          track_activity_time: Boolean(rule.track_activity_time),
          activity_label: rule.track_activity_time
            ? rule.activity_label?.trim() || null
            : null,
        })),
      })),
      p_publish: publish,
      p_change_reason: qualificationEditor.change_reason.trim(),
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : publish
          ? "Nouvelle version publiée. Les anciens devis conservent leur questionnaire d’origine."
          : "Brouillon versionné enregistré. La version publiée reste utilisée pour les devis.",
    );
    if (!result.error) {
      setQualificationEditor(null);
      reload();
    }
  };
  const openCostingProfile = (item: ServiceCatalogItem) => {
    const current = latestCostingProfile(item.id),
      components = current
        ? (data.serviceCostingComponents || [])
            .filter((component) => component.profile_id === current.id)
            .sort((a, b) => a.position - b.position)
        : [];
    setCostingProfileEditor({
      catalog_item_id: item.id,
      output_unit:
        current?.output_unit ||
        ({ "m²": "m2", "m³": "m3", unité: "unite" }[item.default_unit_label] as ServiceCostingProfile["output_unit"] | undefined) ||
        "forfait",
      expected_output_per_labor_hour:
        current?.expected_output_per_labor_hour == null
          ? ""
          : String(current.expected_output_per_labor_hour),
      default_team_size: Number(current?.default_team_size || 1),
      default_labor_cost_per_hour:
        current?.default_labor_cost_per_hour == null
          ? ""
          : String(current.default_labor_cost_per_hour),
      default_travel_cost_ex_vat: Number(current?.default_travel_cost_ex_vat || 0),
      contingency_rate: Number(current?.contingency_rate ?? 10),
      target_mode: current?.target_mode || "margin",
      target_rate: Number(current?.target_rate ?? item.target_margin_rate ?? data.organization?.target_margin_rate ?? 40),
      confidence: current?.confidence || "to_review",
      required_skills_text: (current?.required_skills || []).join(", "),
      assumptions_notes: current?.assumptions_notes || "",
      components: components.map((component) => ({
        id: component.id,
        component_type: component.component_type,
        business_kind: component.business_kind || (component.component_type === "equipment" ? "equipment" : "other"),
        label: component.label,
        cost_basis: component.cost_basis,
        quantity: Number(component.quantity),
        unit_label: component.unit_label,
        unit_cost_ex_vat: Number(component.unit_cost_ex_vat),
        supplier_id: component.supplier_id || "",
        purchase_reference: component.purchase_reference || "",
        purchase_url: component.purchase_url || "",
        source_note: component.source_note || "",
      })),
    });
    setYieldMinimumSamples(3);
    setYieldOutlierTolerance(35);
    setYieldDecisionNotes("");
    setMessage("");
  };
  const saveCostingProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!costingProfileEditor) return;
    if (
      costingProfileEditor.components.some(
        (component) => !component.label.trim() || component.quantity <= 0 || component.unit_cost_ex_vat < 0 || (component.business_kind === "purchase" && component.unit_cost_ex_vat <= 0),
      )
    ) {
      setMessage("Chaque coût doit avoir un libellé, une quantité positive et un coût valide. Un achat externe doit comporter une estimation supérieure à 0 €.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("save_service_costing_profile", {
      p_catalog_item_id: costingProfileEditor.catalog_item_id,
      p_output_unit: costingProfileEditor.output_unit,
      p_expected_output_per_labor_hour: costingProfileEditor.expected_output_per_labor_hour
        ? Number(costingProfileEditor.expected_output_per_labor_hour)
        : null,
      p_default_team_size: costingProfileEditor.default_team_size,
      p_default_labor_cost_per_hour: costingProfileEditor.default_labor_cost_per_hour
        ? Number(costingProfileEditor.default_labor_cost_per_hour)
        : null,
      p_default_travel_cost_ex_vat: costingProfileEditor.default_travel_cost_ex_vat,
      p_contingency_rate: costingProfileEditor.contingency_rate,
      p_target_mode: costingProfileEditor.target_mode,
      p_target_rate: costingProfileEditor.target_rate,
      p_source_kind: "manual",
      p_evidence_count: 0,
      p_confidence: costingProfileEditor.confidence,
      p_assumptions_notes: costingProfileEditor.assumptions_notes.trim() || null,
      p_components: costingProfileEditor.components.map((component, position) => ({
        ...component,
        component_type: component.business_kind === "equipment" ? "equipment" : "other",
        supplier_id: component.supplier_id || null,
        purchase_reference: component.purchase_reference.trim() || null,
        purchase_url: component.purchase_url.trim() || null,
        source_note: component.source_note.trim() || null,
        position,
      })),
      p_active: true,
      p_required_skills: Array.from(new Set(costingProfileEditor.required_skills_text.split(/[,;\n]/).map((skill) => skill.trim()).filter(Boolean))),
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Nouvelle version des hypothèses de chiffrage enregistrée. Les anciens calculs restent figés.",
    );
    if (!result.error) {
      setCostingProfileEditor(null);
      reload();
    }
  };
  const createYieldRecommendation = async () => {
    if (!costingProfileEditor) return;
    setBusy(true);
    const result = await supabase!.rpc(
      "create_service_costing_yield_recommendation",
      {
        p_catalog_item_id: costingProfileEditor.catalog_item_id,
        p_minimum_sample_size: yieldMinimumSamples,
        p_outlier_tolerance_percent: yieldOutlierTolerance,
      },
    );
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Analyse multi-missions créée. Aucun modèle n’a été modifié.",
    );
    if (!result.error) reload();
  };
  const decideYieldRecommendation = async (
    recommendation: CostingYieldRecommendation,
    decision: "approved" | "rejected",
  ) => {
    if (decision === "approved" && !yieldDecisionNotes.trim()) {
      setMessage("Expliquez les contrôles réalisés avant de publier le rendement consolidé.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc(
      "review_service_costing_yield_recommendation",
      {
        p_recommendation_id: recommendation.id,
        p_decision: decision,
        p_publish: decision === "approved",
        p_decision_notes: yieldDecisionNotes.trim() || null,
      },
    );
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : decision === "approved"
          ? "Rendement consolidé publié dans une nouvelle version du modèle."
          : "Recommandation refusée et conservée dans l’historique.",
    );
    if (!result.error) {
      setYieldDecisionNotes("");
      if (decision === "approved") setCostingProfileEditor(null);
      reload();
    }
  };
  const openQuoteCosting = (line: QuoteLine) => {
    const service = data.services.find((item) => item.id === line.service_id),
      catalogItemId = service?.catalog_item_id || "",
      profile = latestCostingProfile(catalogItemId),
      questionnaire = latestQualification(catalogItemId, true),
      previousQualification = questionnaire
        ? (data.quoteQualificationRuns || [])
            .filter(
              (run) =>
                run.quote_line_id === line.id &&
                run.questionnaire_id === questionnaire.id,
            )
            .sort((a, b) => b.run_number - a.run_number)[0] || null
        : null;
    setQuoteCostingEditor({
      quote_line_id: line.id,
      catalog_item_id: catalogItemId,
      output_quantity: profile?.output_unit !== "forfait" ? String(line.quantity) : "",
      output_unit: profile?.output_unit || "forfait",
      total_labor_hours: "",
      labor_cost_per_hour:
        profile?.default_labor_cost_per_hour == null
          ? ""
          : String(profile.default_labor_cost_per_hour),
      team_size: Number(profile?.default_team_size || 1),
      travel_cost_ex_vat: Number(profile?.default_travel_cost_ex_vat || 0),
      other_cost_ex_vat: 0,
      contingency_rate: Number(profile?.contingency_rate ?? 10),
      target_mode: profile?.target_mode || "margin",
      target_rate: Number(profile?.target_rate ?? data.organization?.target_margin_rate ?? 40),
      proposed_total_ex_vat: Number(line.quantity) * Number(line.unit_price_ex_vat),
      notes: "",
    });
    setQualificationAnswers(previousQualification?.answers || {});
    setQualificationRunId(previousQualification?.id || "");
    setQualificationNotes(previousQualification?.notes || "");
    setQualificationPricingPreview(previousQualification?.derived_inputs || {});
    setMessage("");
  };
  const saveQuoteQualification = async () => {
    if (!quoteCostingEditor) return;
    const questionnaire = latestQualification(quoteCostingEditor.catalog_item_id, true);
    if (!questionnaire) {
      setMessage("Aucun questionnaire publié n’est disponible pour cette prestation.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("evaluate_quote_line_qualification", {
      p_quote_line_id: quoteCostingEditor.quote_line_id,
      p_questionnaire_id: questionnaire.id,
      p_answers: qualificationAnswers,
      p_notes: qualificationNotes.trim() || null,
    });
    if (result.error) {
      setBusy(false);
      setMessage(result.error.message);
      return;
    }
    const runResult = await (supabase!.from as any)("quote_qualification_runs")
      .select("id,status,derived_inputs,missing_required")
      .eq("id", result.data)
      .single();
    setBusy(false);
    if (runResult.error) {
      setMessage(`Qualification enregistrée, mais sa relecture a échoué : ${runResult.error.message}`);
      reload();
      return;
    }
    const derived = (runResult.data?.derived_inputs || {}) as Record<string, unknown>;
    setQualificationRunId(String(result.data));
    setQualificationPricingPreview(derived);
    setQuoteCostingEditor((current) =>
      current
        ? {
            ...current,
            output_quantity:
              derived.output_quantity == null
                ? current.output_quantity
                : String(derived.output_quantity),
            output_unit:
              typeof derived.output_unit === "string" &&
              ["m2", "m3", "metre_lineaire", "unite", "forfait"].includes(derived.output_unit)
                ? (derived.output_unit as ServiceCostingProfile["output_unit"])
                : current.output_unit,
            total_labor_hours:
              derived.total_labor_hours == null
                ? current.total_labor_hours
                : String(derived.total_labor_hours),
            team_size:
              derived.team_size == null ? current.team_size : Number(derived.team_size),
            travel_cost_ex_vat:
              derived.travel_cost_ex_vat == null
                ? current.travel_cost_ex_vat
                : Number(derived.travel_cost_ex_vat),
            other_cost_ex_vat:
              derived.other_cost_ex_vat == null
                ? current.other_cost_ex_vat
                : Number(derived.other_cost_ex_vat),
            contingency_rate:
              derived.contingency_rate == null
                ? current.contingency_rate
                : Number(derived.contingency_rate),
            proposed_total_ex_vat:
              derived.recommended_total_sale_price_ex_vat == null
                ? current.proposed_total_ex_vat
                : Number(derived.recommended_total_sale_price_ex_vat),
          }
        : current,
    );
    setMessage(
      runResult.data?.status === "ready"
        ? "Qualification enregistrée. Les règles validées ont prérempli le tarif et le temps ; le prix reste à contrôler puis valider."
        : `Qualification enregistrée avec des réponses à compléter : ${(runResult.data?.missing_required || []).join(", ")}.`,
    );
    reload();
  };
  const createQuoteCostingRun = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!quoteCostingEditor) return;
    setBusy(true);
    const result = await supabase!.rpc("create_quote_costing_run", {
      p_quote_line_id: quoteCostingEditor.quote_line_id,
      p_catalog_item_id: quoteCostingEditor.catalog_item_id || null,
      p_output_quantity: quoteCostingEditor.output_quantity
        ? Number(quoteCostingEditor.output_quantity)
        : null,
      p_output_unit: quoteCostingEditor.output_quantity ? quoteCostingEditor.output_unit : null,
      p_total_labor_hours: quoteCostingEditor.total_labor_hours
        ? Number(quoteCostingEditor.total_labor_hours)
        : null,
      p_labor_cost_per_hour: quoteCostingEditor.labor_cost_per_hour
        ? Number(quoteCostingEditor.labor_cost_per_hour)
        : null,
      p_team_size: quoteCostingEditor.team_size,
      p_travel_cost_ex_vat: quoteCostingEditor.travel_cost_ex_vat,
      p_other_cost_ex_vat: quoteCostingEditor.other_cost_ex_vat,
      p_contingency_rate: quoteCostingEditor.contingency_rate,
      p_target_mode: quoteCostingEditor.target_mode,
      p_target_rate: quoteCostingEditor.target_rate,
      p_proposed_total_ex_vat: quoteCostingEditor.proposed_total_ex_vat,
      p_notes: quoteCostingEditor.notes.trim() || null,
    });
    const linkResult = !result.error && qualificationRunId
      ? await supabase!.rpc("link_quote_qualification_costing", {
          p_qualification_run_id: qualificationRunId,
          p_costing_run_id: result.data,
        })
      : null;
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : linkResult?.error
          ? `Chiffrage créé, mais le lien avec la qualification doit être contrôlé : ${linkResult.error.message}`
          : "Chiffrage calculé et figé avec sa qualification. Vérifiez son détail avant d’appliquer un prix au devis.",
    );
    if (!result.error) reload();
  };
  const applyQuoteCosting = async (run: QuoteCostingRun) => {
    if (!quoteCostingEditor) return;
    const total = Number(quoteCostingEditor.proposed_total_ex_vat);
    if (!window.confirm(`Appliquer ${money(total)} HT à cette ligne de devis ?`)) return;
    setBusy(true);
    const result = await supabase!.rpc("apply_quote_costing_run", {
      p_costing_run_id: run.id,
      p_total_sale_price_ex_vat: total,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Prix validé et appliqué au devis brouillon. Les éventuels besoins d’approvisionnement ont été préparés pour contrôle, sans commande ni envoi.",
    );
    if (!result.error) {
      setQuoteCostingEditor(null);
      reload();
    }
  };
  const prepareProcurementRequirements = async (run: QuoteCostingRun) => {
    setBusy(true);
    const result = await supabase!.rpc("prepare_procurement_requirements", {
      p_costing_run_id: run.id,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : `${Number(result.data || 0)} besoin(s) d’approvisionnement préparé(s) pour contrôle. Aucune commande ni aucun envoi n’a été effectué.`,
    );
    if (!result.error) reload();
  };
  const saveCatalogModel = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!catalogModelEditor) return;
    if (!catalogModelEditor.name.trim()) {
      setMessage("Le nom du modèle est obligatoire.");
      return;
    }
    if (catalogModelEditor.id && !catalogModelEditor.change_reason.trim()) {
      setMessage("Le motif de modification est obligatoire pour conserver une trace exploitable.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("save_service_catalog_item", {
      p_id: catalogModelEditor.id || null,
      p_item_code: catalogModelEditor.item_code.trim() || null,
      p_name: catalogModelEditor.name.trim(),
      p_description: catalogModelEditor.description.trim() || null,
      p_item_kind: catalogModelEditor.item_kind,
      p_default_billing_mode: catalogModelEditor.default_billing_mode,
      p_default_unit_label: catalogModelEditor.default_unit_label.trim(),
      p_default_unit_price_ex_vat: catalogModelEditor.default_unit_price_ex_vat,
      p_default_vat_rate: catalogModelEditor.default_vat_rate,
      p_default_duration_hours: catalogModelEditor.default_duration_hours,
      p_target_margin_rate: catalogModelEditor.target_margin_rate,
      p_active: true,
      p_change_reason: catalogModelEditor.change_reason.trim() || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : catalogModelEditor.id
          ? "Nouvelle version du modèle enregistrée. Les tarifs clients existants sont inchangés."
          : "Modèle ajouté à la bibliothèque centrale.",
    );
    if (!result.error) {
      const createdId = !catalogModelEditor.id && result.data
          ? String(result.data)
          : "",
        createdForRule = catalogCreationTarget && !!createdId,
        continueWithQuestionnaire =
          catalogCreationFlow === "guided" && !!createdId && !createdForRule,
        createdItem = continueWithQuestionnaire || createdForRule
          ? ({
              id: createdId,
              item_code: catalogModelEditor.item_code,
              name: catalogModelEditor.name.trim(),
              description: catalogModelEditor.description.trim() || null,
              item_kind: catalogModelEditor.item_kind,
              default_billing_mode: catalogModelEditor.default_billing_mode,
              default_unit_label: catalogModelEditor.default_unit_label,
              default_unit_price_ex_vat:
                catalogModelEditor.default_unit_price_ex_vat,
              default_vat_rate: catalogModelEditor.default_vat_rate,
              default_duration_hours:
                catalogModelEditor.default_duration_hours,
              target_margin_rate: catalogModelEditor.target_margin_rate,
              active: true,
              updated_at: new Date().toISOString(),
            } as ServiceCatalogItem)
          : null;
      setCatalogModelEditor(null);
      setCatalogCreationFlow("catalog");
      if (createdItem && createdForRule && catalogCreationTarget) {
        updateQualificationRule(
          catalogCreationTarget.questionIndex,
          catalogCreationTarget.ruleIndex,
          {
            line_item_mode: "catalog",
            line_item_catalog_id: createdItem.id,
            line_item_name: createdItem.name,
            line_item_unit_price_ex_vat:
              createdItem.default_unit_price_ex_vat,
            line_item_vat_rate: createdItem.default_vat_rate,
          },
        );
        setCatalogCreationTarget(null);
      } else if (createdItem) openQualificationEditor(createdItem);
      reload();
    }
  };
  const archiveCatalogModel = async (item: ServiceCatalogItem) => {
    if (!window.confirm(`Archiver « ${item.name} » sans modifier les tarifs déjà créés ?`)) return;
    setBusy(true);
    const result = await supabase!.rpc("save_service_catalog_item", {
      p_id: item.id,
      p_item_code: item.item_code,
      p_name: item.name,
      p_description: item.description,
      p_item_kind: item.item_kind,
      p_default_billing_mode: item.default_billing_mode,
      p_default_unit_label: item.default_unit_label,
      p_default_unit_price_ex_vat: item.default_unit_price_ex_vat,
      p_default_vat_rate: item.default_vat_rate,
      p_default_duration_hours: item.default_duration_hours,
      p_target_margin_rate: item.target_margin_rate,
      p_active: false,
      p_change_reason: "Archivage du modèle",
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Modèle archivé et version conservée.");
    if (!result.error) reload();
  };
  const applyCatalogModel = (item: ServiceCatalogItem) => {
    setCatalogApplyEditor({
      catalog_item_id: item.id,
      client_id: data.clients[0]?.id || "",
      property_id: "",
      name: item.name,
      billing_mode: item.default_billing_mode,
      unit_price_ex_vat: Number(item.default_unit_price_ex_vat),
      vat_rate: Number(item.default_vat_rate),
      default_duration_hours: item.default_duration_hours == null
        ? null
        : Number(item.default_duration_hours),
      notes: "",
    });
    setMessage("");
  };
  const createTariffFromCatalog = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!catalogApplyEditor) return;
    setBusy(true);
    const result = await supabase!.rpc("apply_service_catalog_item", {
      p_catalog_item_id: catalogApplyEditor.catalog_item_id,
      p_client_id: catalogApplyEditor.client_id,
      p_property_id: catalogApplyEditor.property_id || null,
      p_name: catalogApplyEditor.name.trim() || null,
      p_billing_mode: catalogApplyEditor.billing_mode,
      p_unit_price_ex_vat: catalogApplyEditor.unit_price_ex_vat,
      p_vat_rate: catalogApplyEditor.vat_rate,
      p_default_duration_hours: catalogApplyEditor.default_duration_hours,
      p_notes: catalogApplyEditor.notes.trim() || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : catalogApplyEditor.property_id
          ? "Tarif propre au bien créé depuis le modèle."
          : "Tarif propre au client créé depuis le modèle.",
    );
    if (!result.error) {
      setCatalogApplyEditor(null);
      reload();
    }
  };
  useEffect(() => {
    if (selectedDocument) setStatus(selectedDocument.status);
  }, [selectedDocument?.status]);
  useEffect(() => {
    setSourcingFinalPrice(
      selectedSourcingWorkflow?.suggested_client_price_ex_vat == null
        ? ""
        : String(selectedSourcingWorkflow.suggested_client_price_ex_vat),
    );
    setSourcingDecisionNotes("");
  }, [selectedSourcingWorkflow?.id, selectedSourcingWorkflow?.status]);
  const editCatalogService = (
    service?: ClientService | PropertyService,
    scope: "client" | "property" = "client",
  ) => {
    setCatalogPricingAnalysis(null);
    const property =
      service && scope === "property"
        ? data.properties.find(
            (item) => item.id === (service as PropertyService).property_id,
          )
        : null;
    setCatalogEditor(
      service
        ? {
            id: service.id,
            scope,
            client_id:
              scope === "property"
                ? property?.client_id || ""
                : (service as ClientService).client_id,
            property_id:
              scope === "property"
                ? (service as PropertyService).property_id
                : "",
            name: service.name,
            billing_mode: service.billing_mode,
            unit_price_ex_vat: Number(service.unit_price_ex_vat || 0),
            vat_rate: Number(service.vat_rate ?? 20),
            default_duration_hours:
              service.default_duration_hours == null
                ? null
                : Number(service.default_duration_hours),
            notes:
              scope === "property"
                ? (service as PropertyService).notes || ""
                : "",
          }
        : {
            scope: "client",
            client_id: data.clients[0]?.id || "",
            property_id: "",
            name: "",
            billing_mode: "fixed",
            unit_price_ex_vat: 0,
            vat_rate: data.organization?.default_vat_rate ?? 20,
            default_duration_hours: null,
            notes: "",
          },
    );
    setMessage("");
  };
  const saveCatalogService = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!catalogEditor || !data.organization) return;
    if (
      !catalogEditor.client_id ||
      !catalogEditor.name.trim() ||
      (catalogEditor.scope === "property" && !catalogEditor.property_id)
    ) {
      setMessage(
        catalogEditor.scope === "property"
          ? "Le client, le bien et le nom de la prestation sont obligatoires."
          : "Le client et le nom de la prestation sont obligatoires.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const table =
        catalogEditor.scope === "property"
          ? "property_services"
          : "client_services",
      payload =
        catalogEditor.scope === "property"
          ? {
              organization_id: data.organization.id,
              property_id: catalogEditor.property_id,
              name: catalogEditor.name.trim(),
              billing_mode: catalogEditor.billing_mode,
              unit_price_ex_vat: Number(catalogEditor.unit_price_ex_vat || 0),
              vat_rate: Number(catalogEditor.vat_rate || 0),
              default_duration_hours: catalogEditor.default_duration_hours,
              notes: catalogEditor.notes.trim() || null,
              active: true,
            }
          : {
              organization_id: data.organization.id,
              client_id: catalogEditor.client_id,
              name: catalogEditor.name.trim(),
              billing_mode: catalogEditor.billing_mode,
              unit_price_ex_vat: Number(catalogEditor.unit_price_ex_vat || 0),
              vat_rate: Number(catalogEditor.vat_rate || 0),
              default_duration_hours: catalogEditor.default_duration_hours,
              active: true,
            },
      serviceTable = supabase!.from(table) as any,
      result = catalogEditor.id
        ? await serviceTable.update(payload).eq("id", catalogEditor.id)
        : await serviceTable.insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : catalogEditor.id
          ? "Prestation modifiée. Les anciennes pièces conservent leur tarif figé."
          : catalogEditor.scope === "property"
            ? "Prestation ajoutée au catalogue tarifaire du bien."
            : "Prestation ajoutée au catalogue tarifaire du client.",
    );
    if (!result.error) {
      setCatalogEditor(null);
      setCatalogPricingAnalysis(null);
      reload();
    }
  };
  const archiveCatalogService = async (
    service: ClientService | PropertyService,
    scope: "client" | "property" = "client",
  ) => {
    if (
      !window.confirm(
        `Archiver « ${service.name} » sans modifier les devis, factures et missions existants ?`,
      )
    )
      return;
    setBusy(true);
    const result = await supabase!
      .from(scope === "property" ? "property_services" : "client_services")
      .update({ active: false })
      .eq("id", service.id);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Prestation archivée ; tout l’historique commercial est conservé.",
    );
    if (!result.error) reload();
  };
  const openDocument = (kind: "quote" | "invoice", id: string) => {
    const item =
      kind === "quote"
        ? data.quotes.find((quote) => quote.id === id)
        : data.invoices.find((invoice) => invoice.id === id);
    setSelection({ kind, id });
    setStatus(item?.status || "draft");
    const client = data.clients.find((candidate) => candidate.id === item?.client_id),
      terms = resolvePaymentTerms(data.organization, client, item);
    setPaymentTermsMode(terms.mode);
    setPaymentTermsDays(terms.days);
    setDocumentDueOn(kind === "invoice" ? (item as Invoice)?.due_on || "" : "");
    setQuoteValidUntil(kind === "quote" ? (item as Quote)?.valid_until || "" : "");
    setAttachmentFile(null);
    setAttachmentLabel("");
    setPaymentAmount(0);
    setPaymentDate(parisDay(new Date()));
    setPaymentReference("");
    setReminderDate(parisDay(new Date()));
    setReminderSubject("Relance de facture");
    setReminderBody("");
    setDerivedKind("deposit");
    setDerivedPercent(30);
    setCreditReason("");
    setHenrriBusy("");
    setHenrriExport(null);
    setHenrriLifecycleBusy("");
    setHenrriLifecycle(null);
    setLineEditor({
      description: "",
      quantity: 1,
      unit_price_ex_vat: 0,
      vat_rate: data.organization?.default_vat_rate ?? 20,
      service_id: "",
    });
    setMessage("");
  };
  const prepareHenrriDocument = async (
    action: "preview" | "create_draft",
  ) => {
    if (!selection || !selectedDocument) return;
    if (
      action === "create_draft" &&
      selectedSourcingWorkflow &&
      selectedSourcingWorkflow.status !== "approved_for_client"
    ) {
      setMessage(
        "Le chiffrage ST et le prix client doivent être validés manuellement par ARD avant la création dans Henrri.",
      );
      return;
    }
    setHenrriBusy(action);
    setMessage("");
    if (!billingProvider) {
      setMessage("Le fournisseur de facturation n’est pas initialisé.");
      return;
    }
    const result = await billingProvider.prepareDocument({
      action,
      documentType: selection.kind,
      documentId: selection.id,
      idempotencyKey: createBillingIdempotencyKey({
        provider: billingProvider.id,
        action,
        documentType: selection.kind,
        documentId: selection.id,
      }),
    });
    setHenrriBusy("");
    if (!result.ok) {
      await recordBillingOperation(result.error);
      setMessage(result.error);
      return;
    }
    await recordBillingOperation(null);
    setHenrriExport(result.data);
    setMessage(result.data.message);
    if (action === "create_draft") reload();
  };
  const manageHenrriQuote = async (
    action: "preview" | "finalize_quote" | "transform_to_invoice",
  ) => {
    if (!selectedQuote || !billingProvider) return;
    setHenrriLifecycleBusy(action);
    setMessage("");
    const result = await billingProvider.manageQuote({
      action,
      quoteId: selectedQuote.id,
      idempotencyKey: createBillingIdempotencyKey({
        provider: billingProvider.id,
        action,
        documentType: "quote",
        documentId: selectedQuote.id,
      }),
    });
    setHenrriLifecycleBusy("");
    if (!result.ok) {
      await recordBillingOperation(result.error);
      setMessage(result.error);
      return;
    }
    await recordBillingOperation(null);
    setHenrriLifecycle(result.data);
    setMessage(result.data.message);
    if (action !== "preview") reload();
    if (action === "transform_to_invoice" && result.data.invoice_id)
      setSelection({ kind: "invoice", id: result.data.invoice_id });
  };
  const reviewSourcingOffer = async (
    decision: "accepted" | "accepted_with_repricing" | "countered" | "rejected",
  ) => {
    if (!latestSourcingOffer) return;
    setBusy(true);
    setMessage("");
    const result = await supabase!.rpc("review_quote_sourcing_offer", {
      p_offer_id: latestSourcingOffer.id,
      p_decision: decision,
      p_decision_notes: sourcingDecisionNotes.trim() || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : decision === "countered"
          ? "Le budget maximum a été transmis au ST ; une nouvelle version est attendue."
          : decision === "accepted" || decision === "accepted_with_repricing"
            ? "Chiffrage ST retenu. Le prix client attend maintenant votre validation finale."
            : "Chiffrage ST refusé et décision historisée.",
    );
    if (!result.error) reload();
  };
  const approveSourcingForClient = async () => {
    if (!selectedSourcingWorkflow || !sourcingFinalPrice) return;
    setBusy(true);
    setMessage("");
    const result = await supabase!.rpc("approve_quote_sourcing_for_client", {
      p_workflow_id: selectedSourcingWorkflow.id,
      p_final_client_price_ex_vat: Number(sourcingFinalPrice),
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Prix client validé manuellement. Le devis peut maintenant être contrôlé puis préparé dans Henrri.",
    );
    if (!result.error) reload();
  };
  const resetLineEditor = () =>
    setLineEditor({
      description: "",
      quantity: 1,
      unit_price_ex_vat: 0,
      vat_rate: data.organization?.default_vat_rate ?? 20,
      service_id: "",
    });
  const recalculateDocumentTotals = async () => {
    if (!selection || !selectedDocument) return null;
    const lineTable = selection.kind === "quote" ? "quote_lines" : "invoice_lines",
      foreignKey = selection.kind === "quote" ? "quote_id" : "invoice_id",
      documentTable = selection.kind === "quote" ? "quotes" : "invoices",
      refreshed = await supabase!
        .from(lineTable)
        .select("quantity,unit_price_ex_vat,vat_rate")
        .eq(foreignKey, selection.id);
    if (refreshed.error) return refreshed.error;
    const discountFactor =
        selection.kind === "quote"
          ? 1 - Number(selectedQuote?.discount_rate || 0) / 100
          : 1,
      totals = (refreshed.data || []).reduce(
        (sum, line) => {
          const exVat =
            Number(line.quantity || 0) *
            Number(line.unit_price_ex_vat || 0) *
            discountFactor;
          sum.exVat += exVat;
          sum.vat += (exVat * Number(line.vat_rate || 0)) / 100;
          return sum;
        },
        { exVat: 0, vat: 0 },
      ),
      rounded = {
        total_ex_vat: Math.round(totals.exVat * 100) / 100,
        total_vat: Math.round(totals.vat * 100) / 100,
        total_inc_vat: Math.round((totals.exVat + totals.vat) * 100) / 100,
      },
      updated = await supabase!
        .from(documentTable)
        .update(rounded)
        .eq("id", selection.id);
    return updated.error;
  };
  const saveCommercialLine = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selection || !selectedDocument || !canEditLines) return;
    if (!lineEditor.description.trim() || lineEditor.quantity <= 0) {
      setMessage("La description et une quantité positive sont obligatoires.");
      return;
    }
    setBusy(true);
    setMessage("");
    const table = selection.kind === "quote" ? "quote_lines" : "invoice_lines",
      payload = {
        description: lineEditor.description.trim(),
        quantity: lineEditor.quantity,
        unit_price_ex_vat: lineEditor.unit_price_ex_vat,
        vat_rate: lineEditor.vat_rate,
        service_id: lineEditor.service_id || null,
      };
    const result = lineEditor.id
      ? await supabase!.from(table).update(payload).eq("id", lineEditor.id)
      : await supabase!.from(table).insert({
          ...payload,
          ...(selection.kind === "quote"
            ? { quote_id: selection.id }
            : { invoice_id: selection.id }),
          position: lines.length + 1,
        });
    const totalError = result.error ? null : await recalculateDocumentTotals();
    if (!result.error && !totalError && data.organization) {
      await supabase!.from("commercial_document_events").insert({
        organization_id: data.organization.id,
        quote_id: selection.kind === "quote" ? selection.id : null,
        invoice_id: selection.kind === "invoice" ? selection.id : null,
        event_type: lineEditor.id ? "line_updated" : "line_added",
        note: lineEditor.description.trim(),
      });
    }
    setBusy(false);
    const error = result.error || totalError;
    setMessage(
      error
        ? error.message
        : lineEditor.id
          ? "Ligne modifiée et totaux recalculés."
          : "Ligne ajoutée et totaux recalculés.",
    );
    if (!error) {
      resetLineEditor();
      reload();
    }
  };
  const deleteCommercialLine = async (line: QuoteLine | InvoiceLine) => {
    if (!selection || !selectedDocument || !canEditLines) return;
    setBusy(true);
    setMessage("");
    const table = selection.kind === "quote" ? "quote_lines" : "invoice_lines",
      removed = await supabase!.from(table).delete().eq("id", line.id),
      totalError = removed.error ? null : await recalculateDocumentTotals();
    if (
      !removed.error &&
      !totalError &&
      selection.kind === "invoice" &&
      "mission_id" in line &&
      line.mission_id
    ) {
      await supabase!
        .from("missions")
        .update({ billing_status: "to_invoice" })
        .eq("id", line.mission_id);
    }
    if (!removed.error && !totalError && data.organization) {
      await supabase!.from("commercial_document_events").insert({
        organization_id: data.organization.id,
        quote_id: selection.kind === "quote" ? selection.id : null,
        invoice_id: selection.kind === "invoice" ? selection.id : null,
        event_type: "line_deleted",
        note: line.description,
      });
    }
    setBusy(false);
    const error = removed.error || totalError;
    setMessage(
      error ? error.message : "Ligne supprimée et totaux recalculés.",
    );
    if (!error) {
      resetLineEditor();
      reload();
    }
  };
  const openAttachment = async (documentId: string) => {
    const document = data.documents.find((item) => item.id === documentId);
    if (!document?.storage_path) return;
    const { data: signed, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(document.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  };
  const openHenrriPdf = async (format: "pdf" | "facturx") => {
    if (!selectedInvoice) return;
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setHenrriPdfBusy(format);
    setMessage("");
    if (!billingProvider) {
      popup?.close();
      setHenrriPdfBusy("");
      setMessage("Le fournisseur de facturation n’est pas initialisé.");
      return;
    }
    const result = await billingProvider.getPrivateDocument({
      documentType: "invoice",
      documentId: selectedInvoice.id,
      format,
    });
    setHenrriPdfBusy("");
    if (!result.ok) {
      popup?.close();
      await recordBillingOperation(result.error);
      setMessage(result.error);
      return;
    }
    await recordBillingOperation(null);
    if (popup) popup.location.href = result.data.url;
    else window.open(result.data.url, "_blank", "noopener,noreferrer");
    setMessage(
      `${format === "facturx" ? "Factur-X" : "PDF"} Henrri ouvert via un lien ARD privé valable 5 minutes.`,
    );
  };
  const uploadAttachment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !selection ||
      !selectedDocument ||
      !attachmentFile ||
      !data.organization
    )
      return;
    setBusy(true);
    setMessage("");
    const client = data.clients.find(
        (item) => item.id === selectedDocument.client_id,
      ),
      safeName = attachmentFile.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      storagePath = `${data.organization.id}/admin/commercial/${selectedDocument.client_id}/${Date.now()}-${safeName}`,
      stored = await supabase!.storage
        .from("private-documents")
        .upload(storagePath, attachmentFile, { upsert: false });
    if (stored.error) {
      setBusy(false);
      setMessage(stored.error.message);
      return;
    }
    const document = await supabase!
      .from("documents")
      .insert({
        organization_id: data.organization.id,
        owner_profile_id: client?.profile_id || null,
        kind: selection.kind,
        storage_path: storagePath,
        original_name: attachmentFile.name,
        status: "validated",
      })
      .select("id")
      .single();
    if (document.error) {
      setBusy(false);
      setMessage(document.error.message);
      return;
    }
    const relation = await supabase!
      .from("commercial_document_attachments")
      .insert({
        organization_id: data.organization.id,
        quote_id: selection.kind === "quote" ? selection.id : null,
        invoice_id: selection.kind === "invoice" ? selection.id : null,
        document_id: document.data.id,
        label: attachmentLabel.trim() || attachmentFile.name,
      });
    if (!relation.error) {
      await supabase!.from("commercial_document_events").insert({
        organization_id: data.organization.id,
        quote_id: selection.kind === "quote" ? selection.id : null,
        invoice_id: selection.kind === "invoice" ? selection.id : null,
        event_type: "attachment_added",
        note: attachmentLabel.trim() || attachmentFile.name,
      });
    }
    setBusy(false);
    setMessage(
      relation.error
        ? `Fichier conservé mais liaison impossible : ${relation.error.message}`
        : "Pièce ajoutée au document commercial.",
    );
    if (!relation.error) {
      setAttachmentFile(null);
      setAttachmentLabel("");
      reload();
    }
  };
  const saveStatus = async () => {
    if (!selection || !selectedDocument) return;
    if (!allowedStatusOptions.includes(status)) {
      setMessage(
        "Cette transition d’état est interdite afin de conserver l’historique du document émis.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const table = selection.kind === "quote" ? "quotes" : "invoices",
      statusUpdate: Record<string, unknown> = { status };
    if (
      selection.kind === "invoice" &&
      status === "sent" &&
      selectedInvoice &&
      data.organization
    ) {
      const issueDate = selectedInvoice.issue_on || parisDay(new Date()),
        client = data.clients.find((item) => item.id === selectedInvoice.client_id),
        terms = resolvePaymentTerms(data.organization, client, selectedInvoice),
        dueOn = selectedInvoice.due_on || paymentDueOn(issueDate, terms.mode, terms.days),
        dueDate = new Date(`${dueOn}T12:00:00`);
      statusUpdate.issue_on = issueDate;
      statusUpdate.due_on = dueOn;
      statusUpdate.payment_terms_mode = terms.mode;
      statusUpdate.payment_terms_days = terms.days;
      statusUpdate.next_reminder_on =
        selectedInvoice.next_reminder_on ||
        parisDay(
          new Date(
            dueDate.getTime() +
              (data.organization.invoice_reminder_days_after_due ?? 7) *
                86400000,
          ),
        );
    }
    const { error } = await supabase!
      .from(table)
      .update(statusUpdate)
      .eq("id", selection.id);
    setBusy(false);
    if (error) setMessage(error.message);
    else {
      setMessage(
        "État enregistré dans ARD Manager. Aucun envoi externe n’a été effectué.",
      );
      reload();
    }
  };
  const savePaymentTerms = async () => {
    if (!selection || !selectedDocument || !data.organization || !canEditLines)
      return;
    setBusy(true);
    setMessage("");
    const issueOn = selectedQuote
        ? selectedQuote.issued_on
        : selectedInvoice?.issue_on || parisDay(new Date()),
      calculatedDueOn = paymentDueOn(issueOn, paymentTermsMode, paymentTermsDays),
      patch = selection.kind === "quote"
        ? {
            payment_terms_mode: paymentTermsMode,
            payment_terms_days: paymentTermsDays,
            valid_until: quoteValidUntil || null,
          }
        : {
            payment_terms_mode: paymentTermsMode,
            payment_terms_days: paymentTermsDays,
            due_on: documentDueOn || calculatedDueOn,
          },
      table = selection.kind === "quote" ? "quotes" : "invoices",
      result = await supabase!.from(table).update(patch).eq("id", selection.id);
    if (!result.error) {
      await supabase!.from("commercial_document_events").insert({
        organization_id: data.organization.id,
        quote_id: selection.kind === "quote" ? selection.id : null,
        invoice_id: selection.kind === "invoice" ? selection.id : null,
        event_type: "payment_terms_updated",
        note: `${paymentTermsLabel(paymentTermsMode, paymentTermsDays)}${selection.kind === "invoice" ? ` · échéance ${documentDueOn || calculatedDueOn}` : ""}`,
      });
    }
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Conditions de paiement enregistrées sur ce document.",
    );
    if (!result.error) reload();
  };
  const recordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice || !data.organization || paymentAmount <= 0) return;
    const remainingBefore = Math.max(
      0,
      Number(selectedInvoice.total_inc_vat || 0) -
        Number(selectedInvoice.amount_paid || 0),
    );
    if (paymentAmount > remainingBefore + 0.005) {
      setMessage(
        `Le règlement saisi (${money(paymentAmount)}) dépasse le solde restant (${money(remainingBefore)}). Vérifiez le montant ou traitez séparément le trop-perçu.`,
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.from("invoice_payments").insert({
      organization_id: data.organization.id,
      invoice_id: selectedInvoice.id,
      received_on: paymentDate,
      amount: paymentAmount,
      method: paymentMethod,
      reference: paymentReference.trim() || null,
      status: "confirmed",
      notes:
        "Règlement confirmé manuellement dans ARD Manager, à rapprocher avec Henrri.",
    });
    setBusy(false);
    const remainingAfter = Math.max(0, remainingBefore - paymentAmount);
    setMessage(
      error
        ? error.message
        : remainingAfter > 0.005
          ? `Règlement partiel enregistré : ${money(remainingAfter)} reste à encaisser. La facture passe en paiement partiel.`
          : "Règlement intégral enregistré. La facture passe à l’état payé.",
    );
    if (!error) {
      setPaymentAmount(0);
      setPaymentReference("");
      reload();
    }
  };
  const prepareReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice || !data.organization) return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.from("invoice_reminders").insert({
      organization_id: data.organization.id,
      invoice_id: selectedInvoice.id,
      channel: "internal",
      status: "prepared",
      subject: reminderSubject.trim() || "Relance de facture",
      body: reminderBody.trim() || null,
      scheduled_on: reminderDate,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Relance préparée dans ARD Manager. Aucun e-mail n’a été envoyé.",
    );
    if (!error) {
      setReminderBody("");
      reload();
    }
  };
  const openCollectionCase = (invoice: Invoice) => {
    setCollectionEditor({
      invoice_id: invoice.id,
      track: invoice.collection_track || "expected",
      next_review_on: invoice.collection_next_review_on || "",
      notes: invoice.collection_notes || "",
    });
    setMessage("");
  };
  const saveCollectionCase = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!collectionEditor) return;
    if (
      ["legal_procedure", "disputed", "payment_plan"].includes(
        collectionEditor.track,
      ) &&
      !collectionEditor.notes.trim()
    ) {
      setMessage(
        "Une note de suivi est obligatoire pour une procédure, un litige ou un échéancier.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const result = isShowcaseMode
      ? await supabase!
          .from("invoices")
          .update({
            collection_track: collectionEditor.track,
            collection_next_review_on: collectionEditor.next_review_on || null,
            collection_notes: collectionEditor.notes.trim() || null,
            collection_updated_at: new Date().toISOString(),
          })
          .eq("id", collectionEditor.invoice_id)
      : await supabase!.rpc("update_invoice_collection_case", {
          p_invoice_id: collectionEditor.invoice_id,
          p_track: collectionEditor.track,
          p_next_review_on: collectionEditor.next_review_on || null,
          p_notes: collectionEditor.notes.trim() || null,
        });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Suivi de la créance enregistré et daté. Aucun message externe n’a été envoyé.",
    );
    if (!result.error) {
      setCollectionEditor(null);
      reload();
    }
  };
  const prepareCollectionMilestone = async (
    invoice: Invoice,
    milestone: 1 | 7 | 15,
  ) => {
    if (!invoice.due_on || !data.organization) return;
    const scheduledOn = parisDay(
      new Date(
        new Date(`${invoice.due_on}T12:00:00`).getTime() +
          milestone * 86400000,
      ),
    );
    if (scheduledOn > today) {
      setMessage(
        `La relance J+${milestone} pourra être préparée à partir du ${frDate(scheduledOn)}.`,
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const client = data.clients.find((item) => item.id === invoice.client_id),
      reminder = {
        organization_id: data.organization.id,
        invoice_id: invoice.id,
        channel: "internal",
        status: "prepared",
        subject: `Relance J+${milestone} · facture ${invoice.number || "sans numéro"}`,
        body: `Relance à relire avant tout envoi. Client : ${client?.name || "Client"}. Facture : ${invoice.number || "sans numéro"}. Solde restant TTC : ${money(remainingForInvoice(invoice))}.`,
        scheduled_on: scheduledOn,
        milestone_days_after_due: milestone,
      },
      result = isShowcaseMode
        ? await supabase!.from("invoice_reminders").insert(reminder)
        : await supabase!.rpc("prepare_invoice_collection_reminder", {
            p_invoice_id: invoice.id,
            p_milestone_days_after_due: milestone,
          });
    setBusy(false);
    setMessage(
      result.error
        ? /duplicate|unique|23505/i.test(result.error.message)
          ? `La relance J+${milestone} est déjà préparée.`
          : result.error.message
        : `Relance J+${milestone} préparée dans ARD Manager. Aucun e-mail n’a été envoyé.`,
    );
    if (!result.error) reload();
  };
  const exportReceivablesPortfolio = () => {
    let popup: Window | null = null;
    try {
      popup = window.open("", "_blank");
    } catch {
      popup = null;
    }
    if (!popup) {
      setMessage(
        "Le navigateur a bloqué l’export. Autorisez les fenêtres contextuelles puis recommencez.",
      );
      return;
    }
    const organizationName = data.organization?.name || "ARD Manager",
      generatedAt = new Date().toLocaleString("fr-FR"),
      rows = [...receivableInvoices].sort((a, b) =>
        String(a.due_on || "9999").localeCompare(String(b.due_on || "9999")),
      );
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Portefeuille de créances ${escapeFinancialHtml(organizationName)}</title><style>@page{size:A4 landscape;margin:12mm}*{box-sizing:border-box}body{margin:0;color:#191b20;font:11px Arial,sans-serif}header{display:flex;justify-content:space-between;gap:24px;padding:18px;border-radius:16px;background:#191b20;color:white}header b{color:#c6a15b;letter-spacing:.08em}header h1{margin:5px 0 3px}.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:15px 0}.kpis div{padding:11px;border:1px solid #d8c28f;border-radius:11px;background:#fffdf7}.kpis span,.muted{display:block;color:#666}.kpis strong{display:block;margin-top:4px;font-size:16px}table{width:100%;border-collapse:collapse}th,td{padding:7px 6px;border-bottom:1px solid #e2e4e8;text-align:left;vertical-align:top}th{background:#f5f1e8}td.amount{text-align:right;font-weight:bold}.track{font-weight:bold}.note{max-width:260px}.notice{margin-top:16px;padding-top:10px;border-top:1px solid #ddd;color:#666;font-size:10px}</style></head><body><header><div><b>${escapeFinancialHtml(organizationName)}</b><h1>Portefeuille de créances</h1><p>Situation arrêtée au ${escapeFinancialHtml(generatedAt)}</p></div><div><strong>Document interne confidentiel</strong><p>Suivi hebdomadaire des encaissements</p></div></header><div class="kpis"><div><span>Reste total à encaisser</span><strong>${escapeFinancialHtml(money(totalReceivables))}</strong></div><div><span>Échu</span><strong>${escapeFinancialHtml(money(overdueReceivables))}</strong></div><div><span>En procédure</span><strong>${escapeFinancialHtml(money(legalReceivables))}</strong></div><div><span>Revues arrivées à échéance</span><strong>${reviewsDue}</strong></div></div><table><thead><tr><th>Facture / client</th><th>Émise</th><th>Échéance</th><th>Retard</th><th>Reste TTC</th><th>Parcours</th><th>Prochaine revue</th><th>Note</th></tr></thead><tbody>${rows.map((invoice) => { const client=data.clients.find((item)=>item.id===invoice.client_id); const late=invoice.due_on&&invoice.due_on<today?dayDifference(today,invoice.due_on):0; return `<tr><td><strong>${escapeFinancialHtml(invoice.number||"Sans numéro")}</strong><span class="muted">${escapeFinancialHtml(client?.name||"Client")}</span></td><td>${invoice.issue_on?escapeFinancialHtml(frDate(invoice.issue_on)):"—"}</td><td>${invoice.due_on?escapeFinancialHtml(frDate(invoice.due_on)):"—"}</td><td>${late?`${late} j`:"—"}</td><td class="amount">${escapeFinancialHtml(money(remainingForInvoice(invoice)))}</td><td class="track">${escapeFinancialHtml(collectionTrackLabels[invoice.collection_track||"expected"])}</td><td>${invoice.collection_next_review_on?escapeFinancialHtml(frDate(invoice.collection_next_review_on)):"—"}</td><td class="note">${escapeFinancialHtml(invoice.collection_notes||"—")}</td></tr>`; }).join("")||`<tr><td colspan="8">Aucune créance ouverte.</td></tr>`}</tbody></table><p class="notice">État de pilotage interne généré depuis les données connues dans ARD Manager. Ce document ne constitue ni une attestation comptable, ni une preuve de recouvrement, ni un acte juridique. Les relances indiquées comme préparées ne sont pas considérées comme envoyées.</p><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script></body></html>`);
    popup.document.close();
  };
  const createDerivedInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice || !data.organization) return;
    if (derivedKind === "credit" && !creditReason.trim()) {
      setMessage("Le motif de l’avoir est obligatoire.");
      return;
    }
    const ratio = derivedKind === "credit" ? 1 : derivedPercent / 100;
    if (ratio <= 0 || ratio > 1) {
      setMessage("Le pourcentage doit être compris entre 0 et 100 %.");
      return;
    }
    setBusy(true);
    setMessage("");
    const prefix =
        derivedKind === "credit"
          ? "AVO"
          : derivedKind === "deposit"
            ? "ACO"
            : "SIT",
      number = prefix + "-" + Date.now().toString().slice(-8),
      totalExVat = Math.round(selectedInvoice.total_ex_vat * ratio * 100) / 100,
      totalVat = Math.round(selectedInvoice.total_vat * ratio * 100) / 100,
      totalIncVat = Math.round((totalExVat + totalVat) * 100) / 100,
      issueOn = parisDay(new Date()),
      client = data.clients.find((item) => item.id === selectedInvoice.client_id),
      terms = resolvePaymentTerms(data.organization, client, selectedInvoice),
      dueOn = paymentDueOn(issueOn, terms.mode, terms.days);
    const created = await supabase!
      .from("invoices")
      .insert({
        organization_id: data.organization.id,
        client_id: selectedInvoice.client_id,
        quote_id: selectedInvoice.quote_id || null,
        number,
        grouping: "individual",
        status: "draft",
        issue_on: issueOn,
        due_on: dueOn,
        payment_terms_mode: terms.mode,
        payment_terms_days: terms.days,
        total_ex_vat: totalExVat,
        total_vat: totalVat,
        total_inc_vat: totalIncVat,
        document_kind: derivedKind,
        parent_invoice_id: selectedInvoice.id,
        progress_percent: derivedKind === "credit" ? null : derivedPercent,
        project_total_ex_vat:
          selectedInvoice.project_total_ex_vat || selectedInvoice.total_ex_vat,
        credit_reason: derivedKind === "credit" ? creditReason.trim() : null,
      })
      .select("id")
      .single();
    if (!created.error) {
      const sourceLines = (data.invoiceLines || []).filter(
          (line) => line.invoice_id === selectedInvoice.id,
        ),
        label =
          derivedKind === "credit"
            ? "Avoir"
            : derivedKind === "deposit"
              ? "Acompte"
              : "Avancement",
        derivedLines = sourceLines.length
          ? sourceLines.map((line) => {
              const hasBillingSnapshot = (data.commercialLineBillingSnapshots || []).some(
                (snapshot) => snapshot.invoice_line_id === line.id,
              );
              return {
                invoice_id: created.data.id,
                mission_id: null,
                service_id: line.service_id,
                ...(hasBillingSnapshot ? {
                  property_service_id: line.property_service_id || null,
                  source_invoice_line_id: line.id,
                } : {}),
                description: label + " — " + line.description,
                quantity: line.quantity,
                unit_price_ex_vat:
                  Math.round(line.unit_price_ex_vat * ratio * 100) / 100,
                vat_rate: line.vat_rate,
                position: line.position,
              };
            })
          : [
              {
                invoice_id: created.data.id,
                description:
                  label +
                  " lié à " +
                  (selectedInvoice.number || "la facture source"),
                quantity: 1,
                unit_price_ex_vat: totalExVat,
                vat_rate:
                  totalExVat > 0
                    ? Math.round((totalVat / totalExVat) * 10000) / 100
                    : 0,
                position: 1,
              },
            ];
      const lineResult = await supabase!
        .from("invoice_lines")
        .insert(derivedLines);
      if (!lineResult.error)
        await supabase!.from("commercial_document_events").insert({
          organization_id: data.organization.id,
          invoice_id: created.data.id,
          event_type: "created",
          status_to: "draft",
          note:
            "Créé depuis " +
            (selectedInvoice.number || "la facture source") +
            ".",
        });
    }
    setBusy(false);
    setMessage(
      created.error
        ? created.error.message
        : derivedKind === "credit"
          ? "Avoir créé en brouillon."
          : derivedKind === "deposit"
            ? "Facture d’acompte créée en brouillon."
            : "Facture d’avancement créée en brouillon.",
    );
    if (!created.error) {
      setCreditReason("");
      reload();
    }
  };
  const queueMissionForInvoice = async (mission: Mission) => {
    if (!data.organization) return;
    setBusy(true);
    setMessage("");
    const client = data.clients.find((item) => item.id === mission.client_id),
      grouped =
        mission.invoice_strategy === "grouped" ||
        (mission.invoice_strategy !== "individual" &&
          client?.billing_grouping !== "individual");
    let invoice = grouped
      ? data.invoices.find(
          (item) =>
            item.client_id === mission.client_id &&
            item.status === "draft" &&
            (item.document_kind || "standard") === "standard",
        )
      : undefined;
    if (!invoice) {
      const terms = resolvePaymentTerms(data.organization, client);
      const created = await supabase!
        .from("invoices")
        .insert({
          organization_id: data.organization.id,
          client_id: mission.client_id,
          billing_period: parisDay(mission.starts_at).slice(0, 7) + "-01",
          grouping: grouped ? "grouped" : "individual",
          document_kind: "standard",
          status: "draft",
          total_ex_vat: 0,
          total_vat: 0,
          total_inc_vat: 0,
          payment_terms_mode: terms.mode,
          payment_terms_days: terms.days,
        })
        .select(
          "id,client_id,number,billing_period,grouping,document_kind,status,total_ex_vat,total_vat,total_inc_vat,payment_terms_mode,payment_terms_days",
        )
        .single();
      if (created.error) {
        setBusy(false);
        setMessage(created.error.message);
        return;
      }
      invoice = created.data as Invoice;
      await supabase!.from("commercial_document_events").insert({
        organization_id: data.organization.id,
        invoice_id: invoice.id,
        event_type: "created",
        status_to: "draft",
        note: "Brouillon créé depuis une mission à facturer.",
      });
    }
    const quantity = Number(mission.billing_quantity || 1),
      amountExVat = Number(mission.amount_ex_vat || 0),
      vatRate = Number(data.organization.default_vat_rate ?? 20),
      firstPosition = (data.invoiceLines || []).filter(
        (line) => line.invoice_id === invoice!.id,
      ).length + 1,
      stockUsages = (data.missionStockUsages || []).filter(
        (usage) => usage.mission_id === mission.id && usage.billing_status === "ready" && usage.billable,
      ),
      lines = [
        {
          invoice_id: invoice.id,
          mission_id: mission.id,
          description:
            (mission.client?.name || mission.client_display_name || "Mission") +
            " — " +
            frDate(mission.starts_at),
          quantity,
          unit_price_ex_vat: quantity > 0 ? amountExVat / quantity : amountExVat,
          vat_rate: vatRate,
          position: firstPosition,
        },
        ...stockUsages.map((usage, index) => {
          const item = (data.inventoryItems || []).find((candidate) => candidate.id === usage.item_id);
          return {
            invoice_id: invoice!.id,
            mission_id: null,
            mission_stock_usage_id: usage.id,
            description: item?.sale_label || item?.name || "Produit utilisé en mission",
            quantity: usage.quantity,
            unit_price_ex_vat: Number(usage.unit_price_ex_vat || 0),
            vat_rate: usage.vat_rate,
            position: firstPosition + index + 1,
          };
        }),
      ],
      inserted = await supabase!.from("invoice_lines").insert(lines);
    if (inserted.error) {
      setBusy(false);
      setMessage(
        inserted.error.code === "23505"
          ? "Cette mission est déjà présente dans une facture."
          : inserted.error.message,
      );
      return;
    }
    if (stockUsages.length) {
      await supabase!
        .from("mission_stock_usages")
        .update({ billing_status: "queued" })
        .in("id", stockUsages.map((usage) => usage.id));
    }
    const refreshedLines = await supabase!
      .from("invoice_lines")
      .select("quantity,unit_price_ex_vat,vat_rate")
      .eq("invoice_id", invoice.id);
    const totals = (refreshedLines.data || []).reduce(
      (sum, line) => {
        const exVat = Number(line.quantity) * Number(line.unit_price_ex_vat);
        sum.exVat += exVat;
        sum.vat += (exVat * Number(line.vat_rate)) / 100;
        return sum;
      },
      { exVat: 0, vat: 0 },
    );
    const invoiceUpdate = await supabase!
      .from("invoices")
      .update({
        total_ex_vat: totals.exVat,
        total_vat: totals.vat,
        total_inc_vat: totals.exVat + totals.vat,
      })
      .eq("id", invoice.id);
    const missionUpdate = invoiceUpdate.error
      ? null
      : await supabase!
          .from("missions")
          .update({ billing_status: "queued" })
          .eq("id", mission.id);
    setBusy(false);
    const error = invoiceUpdate.error || missionUpdate?.error;
    setMessage(
      error
        ? error.message
        : "Mission ajoutée au brouillon du client. Ouvrez la liste des factures pour contrôler les lignes avant émission.",
    );
    if (!error) {
      setView("receivables");
      reload();
    }
  };
  const queueTurnoverCommission = async (
    declaration: ClientTurnoverDeclaration,
  ) => {
    setBusy(true);
    setMessage("");
    const error = await addTurnoverCommissionToInvoice(declaration, data);
    setBusy(false);
    setMessage(
      error || "Commission ajoutée au brouillon de facture correspondant.",
    );
    if (!error) reload();
  };
  const reviewReferralCommission = async (
    commission: InvoiceReferralCommission,
    nextStatus: "approved" | "paid" | "waived",
  ) => {
    const note = (referralReviewNotes[commission.id] || "").trim();
    const paymentReference = (
      referralPaymentReferences[commission.id] || ""
    ).trim();
    if (nextStatus === "waived" && !note) {
      setMessage("Expliquez pourquoi cette commission n’est finalement pas due.");
      return;
    }
    if (nextStatus === "paid" && !paymentReference) {
      setMessage("Renseignez la référence du règlement avant de déclarer la commission payée.");
      return;
    }
    setBusy(true);
    setMessage("");
    const result = isShowcaseMode
      ? await supabase!
          .from("invoice_referral_commissions")
          .update({
            status: nextStatus,
            review_note: note || null,
            approved_by:
              ["approved", "paid"].includes(nextStatus)
                ? "demo-admin"
                : commission.approved_by,
            approved_at:
              ["approved", "paid"].includes(nextStatus)
                ? new Date().toISOString()
                : commission.approved_at,
            paid_by: nextStatus === "paid" ? "demo-admin" : null,
            paid_at: nextStatus === "paid" ? new Date().toISOString() : null,
            payment_reference:
              nextStatus === "paid" ? paymentReference : null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", commission.id)
      : await supabase!.rpc("review_invoice_referral_commission", {
          p_commission_id: commission.id,
          p_decision:
            nextStatus === "approved"
              ? "approve"
              : nextStatus === "paid"
                ? "paid"
                : "waive",
          p_note: note || null,
          p_payment_reference:
            nextStatus === "paid" ? paymentReference : null,
        });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage(
      nextStatus === "approved"
        ? "Commission d’apporteur validée comme dette interne."
        : nextStatus === "paid"
          ? "Règlement de la commission enregistré avec sa référence."
          : "Commission écartée avec son motif ; elle ne pèse plus sur les coûts.",
    );
    reload();
  };
  const openPaymentCandidate = (candidate: BillingPaymentCandidate) => {
    const suggestions = candidate.suggested_allocations || [];
    setSelectedPaymentCandidate(candidate);
    setPaymentCandidateAllocations(
      suggestions.length
        ? suggestions.map((allocation, index) => ({
            invoice_id: allocation.invoice_id,
            amount: String(
              allocation.suggested_amount ??
                (suggestions.length === 1 && index === 0
                  ? candidate.amount
                  : ""),
            ),
          }))
        : [{ invoice_id: "", amount: String(candidate.amount) }],
    );
    setPaymentCandidateMethod("transfer");
    setPaymentCandidateReference("");
    setPaymentCandidateNote("");
    setMessage("");
  };
  const syncProviderPayments = async () => {
    if (!billingProvider) return;
    setPaymentSyncBusy(true);
    setMessage("");
    const result = await billingProvider.syncPayments();
    setPaymentSyncBusy(false);
    if (!result.ok) {
      await recordBillingOperation(result.error);
      setMessage(result.error);
      return;
    }
    await recordBillingOperation(null);
    setMessage(result.data.message);
    reload();
  };
  const confirmPaymentCandidate = async () => {
    if (!selectedPaymentCandidate) return;
    const allocations = paymentCandidateAllocations
      .filter((allocation) => allocation.invoice_id && Number(allocation.amount) > 0)
      .map((allocation) => ({
        invoice_id: allocation.invoice_id,
        amount: Math.round(Number(allocation.amount) * 100) / 100,
      }));
    const total = allocations.reduce((sum, allocation) => sum + allocation.amount, 0);
    if (!allocations.length || Math.abs(total - selectedPaymentCandidate.amount) > 0.01) {
      setMessage(
        `La répartition doit totaliser exactement ${money(selectedPaymentCandidate.amount)}.`,
      );
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("confirm_billing_payment_candidate", {
      p_provider: selectedPaymentCandidate.provider,
      p_candidate_id: selectedPaymentCandidate.id,
      p_allocations: allocations,
      p_method: paymentCandidateMethod,
      p_reference: paymentCandidateReference.trim() || null,
      p_resolution_note: paymentCandidateNote.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setSelectedPaymentCandidate(null);
    setMessage("Règlement rapproché après validation administrateur.");
    reload();
  };
  const ignorePaymentCandidate = async () => {
    if (!selectedPaymentCandidate) return;
    if (!paymentCandidateNote.trim()) {
      setMessage("Expliquez pourquoi ce règlement doit être ignoré.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("ignore_billing_payment_candidate", {
      p_provider: selectedPaymentCandidate.provider,
      p_candidate_id: selectedPaymentCandidate.id,
      p_resolution_note: paymentCandidateNote.trim(),
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setSelectedPaymentCandidate(null);
    setMessage("Règlement écarté avec son motif, sans modifier de facture.");
    reload();
  };
  const billingCaseLinkCandidates: BillingCaseLinkCandidate[] = [
    ...data.missions.map((item) => ({
      id: item.id,
      entity_type: "mission" as const,
      client_id: item.client_id,
      label: `Mission · ${item.location}`,
      detail: `${new Date(item.starts_at).toLocaleDateString("fr-FR")} · ${item.status}`,
    })),
    ...data.quotes.map((item) => ({
      id: item.id,
      entity_type: "quote" as const,
      client_id: item.client_id,
      label: item.number,
      detail: `${money(item.total_ex_vat)} HT · ${item.status}`,
    })),
    ...data.invoices.map((item) => ({
      id: item.id,
      entity_type: "invoice" as const,
      client_id: item.client_id,
      label: item.number || "Facture brouillon",
      detail: `${money(item.total_inc_vat)} TTC · ${item.status}`,
    })),
    ...data.expenses
      .filter((item) => Boolean(item.client_id))
      .map((item) => ({
        id: item.id,
        entity_type: "expense" as const,
        client_id: item.client_id!,
        label: item.description,
        detail: `${money(item.amount_inc_vat)} TTC · ${item.spent_on}`,
      })),
    ...(data.invoicePayments || []).flatMap((item) => {
      const invoice = data.invoices.find((value) => value.id === item.invoice_id);
      return invoice
        ? [{
            id: item.id,
            entity_type: "payment" as const,
            client_id: invoice.client_id,
            label: item.reference || `Règlement du ${item.received_on}`,
            detail: `${money(item.amount)} · ${invoice.number || "facture brouillon"}`,
          }]
        : [];
    }),
  ];
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Documents commerciaux</span>
          <h1>Devis et factures</h1>
        </div>
        <div className="row-actions">
          {onReturnToProfitability && (
            <button
              type="button"
              className="secondary"
              onClick={onReturnToProfitability}
            >
              ← Retour à la rentabilité
            </button>
          )}
          <button type="button" className="secondary" onClick={onNewQuote}>
            ＋ Nouveau devis
          </button>
          <button type="button" onClick={onNewInvoice}>
            ＋ Nouvelle facture
          </button>
        </div>
      </div>
      <div
        className="stats billing-kpis billing-kpis-primary"
        data-testid="billing-primary-actions"
      >
        <article className={view === "quotes" ? "active" : ""}>
          <button onClick={() => openBillingView("quotes")}>
            <span>Devis</span>
            <strong>{data.quotes.length}</strong>
            <small>Ouvrir la liste et agir ›</small>
          </button>
        </article>
        <article className={view === "receivables" ? "active" : ""}>
          <button onClick={() => openBillingView("receivables")}>
            <span>Factures à encaisser</span>
            <strong>
              {
                data.invoices.filter((i) =>
                  ["ready", "sent", "partially_paid", "overdue"].includes(
                    i.status,
                  ),
                ).length
              }
            </strong>
            <small>Suivre paiements et relances ›</small>
          </button>
        </article>
        <article className={view === "missions" ? "active" : ""}>
          <button onClick={() => openBillingView("missions")}>
            <span>Missions à facturer</span>
            <strong>
              {
                data.missions.filter((m) => m.billing_status === "to_invoice")
                  .length
              }
            </strong>
            <small>Préparer les brouillons ›</small>
          </button>
        </article>
        <article className={view === "payments" ? "active" : ""}>
          <button onClick={() => openBillingView("payments")}>
            <span>Règlements à rapprocher</span>
            <strong>{pendingPaymentCandidates.length}</strong>
            <small>Contrôler avant toute validation ›</small>
          </button>
        </article>
        <article className={view === "catalog" ? "active" : ""}>
          <button
            type="button"
            data-testid="billing-open-catalog"
            aria-controls="billing-catalog-panel"
            onClick={() => openBillingView("catalog")}
          >
            <span>Prestations & produits</span>
            <strong>
              {data.services.length +
                data.propertyServices.length +
                visibleSaleItems.length}
            </strong>
            <small>Rechercher et gérer les tarifs ›</small>
          </button>
        </article>
      </div>
      <details
        className="billing-more-views"
        data-testid="billing-secondary-actions"
        open={
          ["projects", "commissions", "referral_commissions", "paid"].includes(
            view,
          )
            ? true
            : undefined
        }
      >
        <summary>
          <span>
            <strong>Voir les autres espaces de facturation</strong>
            <small>
              Dossiers longs, commissions, apporteurs et archives payées
            </small>
          </span>
        </summary>
        <div className="stats billing-kpis billing-kpis-secondary">
          <article className={view === "projects" ? "active" : ""}>
            <button onClick={() => openBillingView("projects")}>
              <span>Dossiers / chantiers</span>
              <strong>{(data.billingCases || []).length}</strong>
              <small>Ouvrir la vue financière 360° ›</small>
            </button>
          </article>
          <article className={view === "commissions" ? "active" : ""}>
            <button onClick={() => openBillingView("commissions")}>
              <span>Commissions à facturer</span>
              <strong>
                {(data.turnoverDeclarations || []).filter(
                  (declaration) => declaration.status === "ready",
                ).length}
              </strong>
              <small>CA mensuels saisis à intégrer ›</small>
            </button>
          </article>
          <article className={view === "referral_commissions" ? "active" : ""}>
            <button onClick={() => openBillingView("referral_commissions")}>
              <span>Commissions apporteurs</span>
              <strong>{money(referralCommissionDue)}</strong>
              <small>
                {referralCommissionsToReview.length} à contrôler · coût interne ›
              </small>
            </button>
          </article>
          <article className={view === "paid" ? "active" : ""}>
            <button onClick={() => openBillingView("paid")}>
              <span>Factures payées</span>
              <strong>
                {data.invoices.filter((i) => i.status === "paid").length}
              </strong>
              <small>Consulter les pièces réglées ›</small>
            </button>
          </article>
        </div>
      </details>
      <details className="billing-kpi-guide" data-testid="billing-kpi-guide">
        <summary>Comprendre les compteurs de facturation</summary>
        <p className="section-intro">
          Chaque compteur ouvre la liste concernée. Il photographie uniquement
          les éléments actuellement enregistrés dans ARD Manager.
        </p>
        <div className="billing-kpi-guide-grid">
          <article><strong>Dossiers / chantiers</strong><span>Tous les dossiers de facturation simples ou longs, quel que soit leur état.</span></article>
          <article><strong>Devis</strong><span>Tous les devis enregistrés, du brouillon au devis accepté ou refusé.</span></article>
          <article><strong>Factures à encaisser</strong><span>Factures prêtes, envoyées, partiellement réglées ou en retard. Les brouillons et factures payées sont exclus.</span></article>
          <article><strong>Missions à facturer</strong><span>Missions dont le processus métier a confirmé l’état « à facturer ». Une mission seulement planifiée n’entre pas ici.</span></article>
          <article><strong>Commissions à facturer</strong><span>Déclarations mensuelles de chiffre d’affaires prêtes à être intégrées à une facturation.</span></article>
          <article><strong>Commissions apporteurs</strong><span>Montant interne des commissions à reverser, calculé depuis les dossiers concernés et encore à contrôler.</span></article>
          <article><strong>Factures payées</strong><span>Factures dont l’état enregistré est intégralement payé.</span></article>
          <article><strong>Règlements à rapprocher</strong><span>Lectures fournisseur encore en attente de contrôle humain et de correspondance avec une facture.</span></article>
          <article><strong>Prestations &amp; produits</strong><span>Prestations communes, tarifs propres aux biens et articles de stock autorisés à la vente, sans doublonner leurs historiques.</span></article>
        </div>
        <p className="billing-kpi-guide-limit">
          Limite : un document absent, non synchronisé ou non rapproché ne peut pas être compté. Ouvrez la rubrique pour contrôler le détail avant toute décision.
        </p>
      </details>
      <div
        ref={billingContentRef}
        className="billing-view-anchor"
        aria-hidden="true"
      />
      {view === "projects" && (
        <AdminBillingCases
          available={hasDeploymentCapability(data, "billing_cases")}
          governanceAvailable={hasDeploymentCapability(data, "billing_case_governance")}
          invoicingAvailable={hasDeploymentCapability(data, "billing_case_invoicing")}
          operationsAvailable={hasDeploymentCapability(data, "billing_case_operations")}
          cases={data.billingCases || []}
          links={data.billingCaseLinks || []}
          progressVersions={data.billingCaseProgress || []}
          ledgerEntries={data.billingCaseLedger || []}
          amendments={data.billingCaseAmendments || []}
          consistencyReviews={data.billingCaseConsistencyReviews || []}
          invoicePreparations={data.billingCaseInvoicePreparations || []}
          operationalSyncs={data.billingCaseOperationalSyncs || []}
          invoices={data.invoices.map(
            (invoice): BillingCaseInvoiceCandidate => ({
              id: invoice.id,
              client_id: invoice.client_id,
              number: invoice.number,
              document_kind: invoice.document_kind || "standard",
              parent_invoice_id: invoice.parent_invoice_id || null,
              status: invoice.status,
              total_ex_vat: invoice.total_ex_vat,
              total_inc_vat: invoice.total_inc_vat,
            }),
          )}
          costReviewCandidates={(data.missionCostingReviews || []).map(
            (review): BillingCaseCostReviewCandidate => ({
              id: review.id,
              mission_id: review.mission_id,
              review_number: review.review_number,
              status: review.status,
              actual_total_cost_ex_vat: review.actual_total_cost_ex_vat,
              decided_at: review.decided_at,
            }),
          )}
          linkCandidates={billingCaseLinkCandidates}
          clients={data.clients}
          properties={data.properties}
          reload={reload}
        />
      )}
      {view === "quotes" && (
        <section className="panel table-wrap">
          <h2>Devis</h2>
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Client</th>
                <th>Date</th>
                <th>Montant TTC</th>
                <th>État</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.quotes.map((q) => (
                <tr key={q.id}>
                  <td>{q.number}</td>
                  <td>
                    {data.clients.find((c) => c.id === q.client_id)?.name}
                  </td>
                  <td>{frDate(q.issued_on)}</td>
                  <td>{money(q.total_inc_vat)}</td>
                  <td>
                    <Status value={q.status} />
                  </td>
                  <td>
                    <button
                      className="secondary"
                      onClick={() => openDocument("quote", q.id)}
                    >
                      Ouvrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.quotes.length && <p className="empty">Aucun devis.</p>}
        </section>
      )}
      {view === "receivables" && (
        <section
          className="receivables-portfolio"
          data-testid="receivables-collection-portfolio"
        >
          <div className="panel receivables-portfolio-heading">
            <div>
              <span className="eyebrow">Suivi hebdomadaire</span>
              <h2>Portefeuille de créances</h2>
              <p className="section-intro">
                Le solde réellement restant est calculé après les paiements
                partiels. Les relances J+1, J+7 et J+15 sont préparées pour
                relecture : aucune n’est envoyée automatiquement.
              </p>
            </div>
            <button
              type="button"
              className="secondary"
              onClick={exportReceivablesPortfolio}
              data-testid="export-receivables-pdf"
            >
              Exporter la synthèse PDF
            </button>
          </div>
          <div className="receivables-summary-grid">
            <article>
              <span>Reste à encaisser</span>
              <strong>{money(totalReceivables)}</strong>
              <small>{receivableInvoices.length} facture(s) émise(s)</small>
            </article>
            <article className={overdueReceivables > 0 ? "warning" : ""}>
              <span>Montant échu</span>
              <strong>{money(overdueReceivables)}</strong>
              <small>À traiter lors de la revue hebdomadaire</small>
            </article>
            <article>
              <span>Procédures suivies</span>
              <strong>{money(legalReceivables)}</strong>
              <small>Uniquement les dossiers classés en procédure</small>
            </article>
            <article className={reviewsDue > 0 ? "warning" : ""}>
              <span>Actions arrivées à échéance</span>
              <strong>{reviewsDue}</strong>
              <small>Prochaines revues à faire aujourd’hui ou avant</small>
            </article>
          </div>
          <div className="panel table-wrap receivables-table">
            <table>
              <thead>
                <tr>
                  <th>Facture / client</th>
                  <th>Reste TTC</th>
                  <th>Échéance</th>
                  <th>Retard</th>
                  <th>Parcours</th>
                  <th>Jalons</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...receivableInvoices]
                  .sort((a, b) =>
                    String(a.due_on || "9999").localeCompare(
                      String(b.due_on || "9999"),
                    ),
                  )
                  .map((invoice) => {
                    const client = data.clients.find(
                        (item) => item.id === invoice.client_id,
                      ),
                      daysLate =
                        invoice.due_on && invoice.due_on < today
                          ? dayDifference(today, invoice.due_on)
                          : 0,
                      invoiceReminders = (data.invoiceReminders || []).filter(
                        (reminder) => reminder.invoice_id === invoice.id,
                      );
                    return (
                      <tr key={`collection-${invoice.id}`}>
                        <td>
                          <strong>{invoice.number || "Sans numéro"}</strong>
                          <small>{client?.name || "Client"}</small>
                        </td>
                        <td>
                          <strong>{money(remainingForInvoice(invoice))}</strong>
                          {Number(invoice.amount_paid || 0) > 0 && (
                            <small>
                              {money(Number(invoice.amount_paid || 0))} déjà
                              réglés
                            </small>
                          )}
                        </td>
                        <td>{invoice.due_on ? frDate(invoice.due_on) : "—"}</td>
                        <td>
                          {daysLate > 0 ? (
                            <span className="collection-late">{daysLate} j</span>
                          ) : (
                            <span className="collection-upcoming">À venir</span>
                          )}
                        </td>
                        <td>
                          <strong>
                            {collectionTrackLabels[
                              invoice.collection_track || "expected"
                            ]}
                          </strong>
                          <small>
                            {invoice.collection_next_review_on
                              ? `Revue ${frDate(invoice.collection_next_review_on)}`
                              : "Revue à planifier"}
                          </small>
                        </td>
                        <td>
                          <div className="collection-milestones">
                            {reminderMilestones.map((milestone) => {
                              const prepared = invoiceReminders.some(
                                (reminder) =>
                                  reminder.milestone_days_after_due === milestone,
                              ),
                                available = Boolean(
                                  invoice.due_on &&
                                    dayDifference(today, invoice.due_on) >=
                                      milestone,
                                );
                              return (
                                <button
                                  type="button"
                                  key={milestone}
                                  className={prepared ? "prepared" : "secondary"}
                                  disabled={busy || prepared || !available}
                                  title={
                                    prepared
                                      ? `J+${milestone} déjà préparée`
                                      : available
                                        ? `Préparer la relance J+${milestone}`
                                        : `Disponible à J+${milestone}`
                                  }
                                  onClick={() =>
                                    prepareCollectionMilestone(invoice, milestone)
                                  }
                                >
                                  {prepared ? "✓ " : ""}J+{milestone}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              type="button"
                              className="secondary compact"
                              onClick={() => openDocument("invoice", invoice.id)}
                            >
                              Facture
                            </button>
                            <button
                              type="button"
                              className="compact"
                              onClick={() => openCollectionCase(invoice)}
                              data-testid={`manage-receivable-${invoice.id}`}
                            >
                              Piloter
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {!receivableInvoices.length && (
              <p className="empty">Aucune facture émise avec un solde restant.</p>
            )}
          </div>
        </section>
      )}
      {(view === "receivables" || view === "paid") && (
        <section className="panel table-wrap">
          <h2>{view === "paid" ? "Factures payées" : "Factures à préparer et à encaisser"}</h2>
          <p className="section-intro">
            {view === "paid"
              ? "Chaque facture réglée reste consultable avec ses prestations, ses règlements et son historique complet."
              : "Les brouillons restent visibles jusqu’à leur contrôle ; les factures émises permettent ensuite de suivre échéances, paiements et relances."}
          </p>
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Client</th>
                <th>Émission</th>
                <th>Échéance</th>
                <th>Montant TTC</th>
                <th>État</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((i) => (
                  <tr key={i.id}>
                    <td>{i.number || "Brouillon"}</td>
                    <td>
                      {data.clients.find((c) => c.id === i.client_id)?.name}
                    </td>
                    <td>{i.issue_on ? frDate(i.issue_on) : "—"}</td>
                    <td>{i.due_on ? frDate(i.due_on) : "—"}</td>
                    <td>{money(i.total_inc_vat)}</td>
                    <td>
                      <Status value={i.status} />
                    </td>
                    <td>
                      <button
                        className="secondary"
                        onClick={() => openDocument("invoice", i.id)}
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!visibleInvoices.length && <p className="empty">Aucune facture dans cet état.</p>}
        </section>
      )}
      {view === "missions" && (
        <section className="panel table-wrap">
          <h2>Missions terminées à facturer</h2>
          <p className="section-intro">
            1. Ouvrez la mission pour contrôler son compte rendu. 2. Préparez
            la facture : ARD Manager crée ou complète le brouillon du client.
            3. Relisez ensuite les lignes dans « Factures à encaisser » avant
            toute préparation ou émission Henrri.
          </p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Mission</th>
                <th>Montant HT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.missions
                .filter((mission) => mission.billing_status === "to_invoice")
                .map((mission) => (
                  <tr key={mission.id}>
                    <td>{frDate(mission.starts_at)}</td>
                    <td>
                      {mission.client?.name ||
                        mission.client_display_name ||
                        "Client"}
                    </td>
                    <td>{mission.location || "Sans adresse"}</td>
                    <td>{money(mission.amount_ex_vat || 0)}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="secondary compact"
                          onClick={() => onSelectMission(mission)}
                        >
                          Ouvrir
                        </button>
                        <button
                          className="compact"
                          disabled={busy}
                          onClick={() => queueMissionForInvoice(mission)}
                        >
                          Préparer la facture
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!data.missions.some(
            (mission) => mission.billing_status === "to_invoice",
          ) && <p className="empty">Aucune mission à facturer.</p>}
          {message && <Notice tone="info">{message}</Notice>}
        </section>
      )}
      {view === "catalog" && (
        <section
          id="billing-catalog-panel"
          className="panel table-wrap"
          data-testid="billing-catalog-panel"
        >
          <div className="section-heading-row">
            <div>
              <h2>Prestations et produits commercialisables</h2>
              <p className="section-intro">
                La bibliothèque centrale sert de modèle réutilisable. Chaque
                application crée ensuite un tarif propre au client ou au bien :
                une évolution du modèle ne réécrit jamais les tarifs déjà créés.
              </p>
            </div>
            <div className="row-actions">
              {hasDeploymentCapability(data, "service_catalog") && (
                <button type="button" onClick={() => editCatalogModel()}>
                  ＋ Nouveau sujet / modèle
                </button>
              )}
              <button type="button" className="secondary" onClick={() => editCatalogService()}>
                ＋ Tarif client libre
              </button>
            </div>
          </div>
          <label className="compact-filter">
            Recherche rapide
            <input
              type="search"
              value={catalogSearch}
              onChange={(event) => setCatalogSearch(event.target.value)}
              placeholder="Client, prestation, produit, référence…"
            />
          </label>
          {!hasDeploymentCapability(data, "service_catalog") ? (
            <Notice>
              La bibliothèque centrale est validée sur la recette isolée mais
              n’est pas encore installée sur cette base. Les tarifs clients et
              biens existants restent entièrement opérationnels.
            </Notice>
          ) : (
            <>
              <h3>Bibliothèque centrale versionnée</h3>
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Modèle</th>
                    <th>Nature</th>
                    <th>Facturation</th>
                    <th>Prix conseillé HT</th>
                    <th>Marge cible</th>
                    <th>Versions</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCatalogModels.map((item) => {
                    const versions = (data.serviceCatalogVersions || []).filter(
                      (version) => version.catalog_item_id === item.id,
                    ), questionnaire = latestQualification(item.id);
                    return (
                      <tr key={item.id}>
                        <td>{item.item_code}</td>
                        <td>
                          <strong>{item.name}</strong>
                          {item.description && <small className="block">{item.description}</small>}
                          {!item.active && <small className="block">Archivé</small>}
                          {questionnaire && (
                            <small className="block">
                              Questionnaire v{questionnaire.version} · {questionnaire.status === "published" ? "publié" : questionnaire.status === "draft" ? "brouillon" : "archivé"}
                            </small>
                          )}
                        </td>
                        <td>{{ service: "Prestation", product: "Produit", rental: "Location", other: "Autre" }[item.item_kind]}</td>
                        <td>{item.default_billing_mode} · {item.default_unit_label}</td>
                        <td>{money(item.default_unit_price_ex_vat)}</td>
                        <td>{item.target_margin_rate == null ? "—" : `${item.target_margin_rate}%`}</td>
                        <td>{versions.length}</td>
                        <td>
                          <div className="row-actions">
                            {item.active && (
                              <button type="button" className="compact" onClick={() => applyCatalogModel(item)}>
                                Créer un tarif
                              </button>
                            )}
                            {item.active && hasDeploymentCapability(data, "guided_quote_costing") && (
                              <button type="button" className="secondary compact" onClick={() => openCostingProfile(item)}>
                                Rendement & coûts
                              </button>
                            )}
                            {item.active && hasDeploymentCapability(data, "service_qualification") && (
                              <button type="button" className="secondary compact" onClick={() => openQualificationEditor(item)}>
                                Questionnaire devis
                              </button>
                            )}
                            {item.active && hasDeploymentCapability(data, "mission_checklists") && (
                              <button type="button" className="secondary compact" onClick={() => openMissionChecklistEditor(item)}>
                                Checklist mission
                              </button>
                            )}
                            <button type="button" className="secondary compact" onClick={() => editCatalogModel(item)}>
                              Modifier
                            </button>
                            {item.active && (
                              <button type="button" className="danger compact" disabled={busy} onClick={() => archiveCatalogModel(item)}>
                                Archiver
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!visibleCatalogModels.length && (
                <p className="empty">Aucun modèle ne correspond à la recherche.</p>
              )}
            </>
          )}
          <h3>Tarifs et prestations par client et par bien</h3>
          <p className="section-intro">
            C’est ici que se pilotent les tarifs utilisés pour les futurs devis
            et missions. Une modification ne réécrit jamais l’historique déjà
            figé.
          </p>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Portée</th>
                <th>Prestation</th>
                <th>Facturation</th>
                <th>Tarif HT</th>
                <th>TVA</th>
                <th>Durée interne</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleClientServices.map((service) => (
                <tr key={service.id}>
                  <td>
                    {data.clients.find((client) => client.id === service.client_id)
                      ?.name || "Client"}
                  </td>
                  <td>Tous les biens du client</td>
                  <td>{service.name}</td>
                  <td>
                    {{
                      fixed: "Forfait",
                      hourly: "Horaire",
                      per_unit: "À l’unité",
                      monthly: "Mensuel",
                    }[service.billing_mode] || service.billing_mode}
                  </td>
                  <td>{money(service.unit_price_ex_vat)}</td>
                  <td>{service.vat_rate}%</td>
                  <td>
                    {service.default_duration_hours == null
                      ? "—"
                      : `${service.default_duration_hours} h`}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="secondary compact"
                        onClick={() => editCatalogService(service, "client")}
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="danger compact"
                        disabled={busy}
                        onClick={() => archiveCatalogService(service, "client")}
                      >
                        Archiver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visiblePropertyServices.map((service) => {
                const property = data.properties.find(
                    (item) => item.id === service.property_id,
                  ),
                  client = data.clients.find(
                    (item) => item.id === property?.client_id,
                  );
                return (
                  <tr key={`property-${service.id}`}>
                    <td>{client?.name || "Client"}</td>
                    <td>
                      <strong>{property?.name || "Bien"}</strong>
                      <small className="block">Tarif propre à ce bien</small>
                    </td>
                    <td>{service.name}</td>
                    <td>
                      {{
                        fixed: "Forfait",
                        hourly: "Horaire",
                        per_unit: "À l’unité",
                        monthly: "Mensuel",
                      }[service.billing_mode] || service.billing_mode}
                    </td>
                    <td>{money(service.unit_price_ex_vat)}</td>
                    <td>{service.vat_rate}%</td>
                    <td>
                      {service.default_duration_hours == null
                        ? "—"
                        : `${service.default_duration_hours} h`}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="secondary compact"
                          onClick={() => editCatalogService(service, "property")}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="danger compact"
                          disabled={busy}
                          onClick={() => archiveCatalogService(service, "property")}
                        >
                          Archiver
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!visibleClientServices.length && !visiblePropertyServices.length && (
            <p className="empty">Aucune prestation ne correspond à la recherche.</p>
          )}
          <h3>Produits issus du stock et autorisés à la vente</h3>
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Référence</th>
                <th>Stock</th>
                <th>Prix HT</th>
                <th>TVA</th>
                <th>Gestion</th>
              </tr>
            </thead>
            <tbody>
              {visibleSaleItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.sale_label || item.name}</td>
                  <td>{item.sku || "—"}</td>
                  <td>{item.current_quantity} {item.unit}</td>
                  <td>{money(item.sale_price_ex_vat || 0)}</td>
                  <td>{item.sale_vat_rate ?? 20}%</td>
                  <td>
                    {onOpenStockItem ? (
                      <button
                        type="button"
                        className="secondary compact"
                        onClick={() => onOpenStockItem(item.id)}
                      >
                        Modifier le produit →
                      </button>
                    ) : (
                      <small>Modifiable dans Stocks</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!visibleSaleItems.length && (
            <p className="empty">Aucun produit vendable ne correspond à la recherche.</p>
          )}
          {message && <Notice tone="info">{message}</Notice>}
        </section>
      )}
      {view === "commissions" && (
        <section className="panel table-wrap">
          <h2>Commissions sur chiffre d’affaires à facturer</h2>
          <p className="section-intro">
            La base mensuelle, le taux contractuel et le montant calculé restent
            visibles avant leur ajout au brouillon du client.
          </p>
          <table>
            <thead>
              <tr>
                <th>Mois</th>
                <th>Client</th>
                <th>CA déclaré</th>
                <th>Taux</th>
                <th>Commission HT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data.turnoverDeclarations || [])
                .filter((declaration) => declaration.status === "ready")
                .map((declaration) => (
                  <tr key={declaration.id}>
                    <td>
                      {new Intl.DateTimeFormat("fr-FR", {
                        month: "long",
                        year: "numeric",
                      }).format(new Date(`${declaration.period.slice(0, 10)}T12:00:00`))}
                    </td>
                    <td>
                      {data.clients.find((client) => client.id === declaration.client_id)?.name || "Client"}
                    </td>
                    <td>
                      {money(declaration.turnover_amount)} {declaration.turnover_basis === "inc_vat" ? "TTC" : "HT"}
                    </td>
                    <td>{declaration.commission_rate}%</td>
                    <td>{money(declaration.commission_amount_ex_vat)}</td>
                    <td>
                      <button
                        className="compact"
                        disabled={busy}
                        onClick={() => queueTurnoverCommission(declaration)}
                      >
                        Ajouter au brouillon
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!(data.turnoverDeclarations || []).some(
            (declaration) => declaration.status === "ready",
          ) && <p className="empty">Aucune commission prête à facturer.</p>}
          {message && <Notice tone="info">{message}</Notice>}
        </section>
      )}
      {view === "referral_commissions" && (
        <section className="panel table-wrap" data-testid="referral-commission-review">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Coût commercial interne</span>
              <h2>Commissions dues aux apporteurs d’affaires</h2>
              <p className="section-intro">
                Le montant est calculé sur le HT réellement facturé. Il ne
                figure jamais sur le devis ou la facture du client et n’est
                payé qu’après contrôle humain.
              </p>
            </div>
            <div className="stats compact-stats">
              <article>
                <span>À contrôler</span>
                <strong>{referralCommissionsToReview.length}</strong>
              </article>
              <article>
                <span>Dette estimée / validée</span>
                <strong>{money(referralCommissionDue)}</strong>
              </article>
            </div>
          </div>
          {!hasDeploymentCapability(data, "referral_commissions") && (
            <Notice>
              Le module est prêt dans l’application mais sa migration n’est pas
              encore installée sur cette base. Aucun coût n’est créé tant que
              la migration n’a pas été explicitement autorisée.
            </Notice>
          )}
          <table>
            <thead>
              <tr>
                <th>Facture</th>
                <th>Client</th>
                <th>Apporteur</th>
                <th>Base HT</th>
                <th>Taux</th>
                <th>Commission HT</th>
                <th>État</th>
                <th>Contrôle et règlement</th>
              </tr>
            </thead>
            <tbody>
              {referralCommissions.map((commission) => {
                const invoice = data.invoices.find(
                    (item) => item.id === commission.invoice_id,
                  ),
                  client = data.clients.find(
                    (item) => item.id === commission.client_id,
                  );
                return (
                  <tr key={commission.id}>
                    <td>{invoice?.number || "Brouillon"}</td>
                    <td>{client?.name || "Client"}</td>
                    <td>{commission.partner_name_snapshot}</td>
                    <td>{money(commission.commission_base_ex_vat)}</td>
                    <td>{Number(commission.commission_rate).toFixed(2)} %</td>
                    <td><strong>{money(commission.commission_amount_ex_vat)}</strong></td>
                    <td><Status value={commission.status} /></td>
                    <td>
                      {commission.status === "paid" ? (
                        <small>
                          Réglée le {commission.paid_at ? frDate(commission.paid_at) : "—"}
                          {commission.payment_reference
                            ? ` · ${commission.payment_reference}`
                            : ""}
                        </small>
                      ) : ["waived", "cancelled"].includes(commission.status) ? (
                        <small>{commission.review_note || labels[commission.status]}</small>
                      ) : (
                        <div className="referral-review-controls">
                          <input
                            aria-label={`Note de contrôle ${commission.partner_name_snapshot}`}
                            placeholder="Note de contrôle ou motif"
                            value={referralReviewNotes[commission.id] || ""}
                            onChange={(event) =>
                              setReferralReviewNotes((current) => ({
                                ...current,
                                [commission.id]: event.target.value,
                              }))
                            }
                          />
                          {commission.status === "approved" && (
                            <input
                              aria-label={`Référence de règlement ${commission.partner_name_snapshot}`}
                              placeholder="Référence du règlement"
                              value={referralPaymentReferences[commission.id] || ""}
                              onChange={(event) =>
                                setReferralPaymentReferences((current) => ({
                                  ...current,
                                  [commission.id]: event.target.value,
                                }))
                              }
                            />
                          )}
                          <div className="row-actions">
                            {["estimated", "pending_approval"].includes(
                              commission.status,
                            ) && (
                              <button
                                type="button"
                                className="compact"
                                disabled={busy}
                                onClick={() =>
                                  void reviewReferralCommission(commission, "approved")
                                }
                              >
                                Valider la dette
                              </button>
                            )}
                            {commission.status === "approved" && (
                              <button
                                type="button"
                                className="compact"
                                disabled={busy}
                                onClick={() =>
                                  void reviewReferralCommission(commission, "paid")
                                }
                              >
                                Marquer payée
                              </button>
                            )}
                            <button
                              type="button"
                              className="secondary compact"
                              disabled={busy}
                              onClick={() =>
                                void reviewReferralCommission(commission, "waived")
                              }
                            >
                              Écarter avec motif
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!referralCommissions.length && (
            <p className="empty">
              Aucune facture ne porte encore de commission d’apporteur.
            </p>
          )}
          {message && <Notice tone="info">{message}</Notice>}
        </section>
      )}
      {view === "payments" && (
        <section className="panel table-wrap">
          <div className="section-heading-row">
            <div>
              <h2>Règlements à rapprocher</h2>
              <p className="section-intro">
                Une lecture fournisseur ne modifie jamais une facture. Chaque
                répartition doit être contrôlée puis validée par un administrateur.
              </p>
            </div>
            {billingProvider?.capabilities.paymentRead && (
              <button
                type="button"
                disabled={paymentSyncBusy}
                onClick={syncProviderPayments}
              >
                {paymentSyncBusy ? "Lecture…" : "Lire les nouveaux règlements"}
              </button>
            )}
          </div>
          {!hasDeploymentCapability(data, "billing_payment_reconciliation") && (
            <Notice>
              Le rapprochement est validé sur la recette isolée mais n’est pas
              encore installé sur cette base. Aucun règlement fournisseur ne
              peut être importé ou validé ici.
            </Notice>
          )}
          {hasDeploymentCapability(data, "billing_payment_reconciliation") && (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Fournisseur</th>
                    <th>Référence externe</th>
                    <th>Montant</th>
                    <th>Correspondance</th>
                    <th>État</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paymentCandidates.map((candidate) => (
                    <tr key={`${candidate.provider}-${candidate.id}`}>
                      <td>{frDate(candidate.received_at)}</td>
                      <td>{candidate.provider}</td>
                      <td>{candidate.external_payment_id}</td>
                      <td>{money(candidate.amount)}</td>
                      <td><Status value={candidate.match_status} /></td>
                      <td><Status value={candidate.review_status} /></td>
                      <td>
                        {candidate.review_status === "pending" && (
                          <button
                            type="button"
                            className="secondary compact"
                            onClick={() => openPaymentCandidate(candidate)}
                          >
                            Vérifier
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!paymentCandidates.length && (
                <p className="empty">Aucun règlement en attente de rapprochement.</p>
              )}
            </>
          )}
          {message && <Notice tone="info">{message}</Notice>}
        </section>
      )}
      {qualificationEditor && (() => {
        const catalogItem = (data.serviceCatalogItems || []).find(
            (item) => item.id === qualificationEditor.catalog_item_id,
          ),
          history = (data.serviceQualificationQuestionnaires || [])
            .filter((item) => item.catalog_item_id === qualificationEditor.catalog_item_id)
            .sort((a, b) => b.version - a.version),
          guidanceMode = detectQuoteGuidanceMode(qualificationEditor.questions),
          guidanceModeCopy = {
            composition: {
              label: "Composition de forfaits connus",
              detail: "Les réponses assemblent des prestations communes ou propres au client : ménage, linge, kits et autres options identifiables.",
            },
            parametric: {
              label: "Base ajustée selon le chantier",
              detail: "Le prix, le temps, les produits et les coûts partent d’une base puis évoluent selon la surface, l’état et les attentes particulières.",
            },
            hybrid: {
              label: "Devis hybride",
              detail: "Le questionnaire combine une base ajustée par les constats et des forfaits ou options identifiables ajoutés au devis.",
            },
            informational: {
              label: "Questionnaire informatif",
              detail: "Aucune conséquence tarifaire n’est encore définie. Ajoutez une conséquence pour construire le calcul ou composer les prestations.",
            },
          }[guidanceMode];
        return (
          <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Questionnaire métier du devis">
            <div className="portal-detail create-modal">
              <div className="portal-detail-head">
                <div>
                  <span className="eyebrow">Qualification métier versionnée</span>
                  <h2>Questionnaire devis · {catalogItem?.name || qualificationEditor.name.replace(/^Qualification · /, "")}</h2>
                </div>
                <button type="button" className="icon-close" onClick={() => setQualificationEditor(null)} aria-label="Fermer">×</button>
              </div>
              <Notice>
                Les réponses qualitatives peuvent déclencher des règles explicites de prix ou de rendement. Chaque règle est versionnée et expliquée ; le résultat préremplit le chiffrage, mais aucun prix n’est appliqué, envoyé ou validé automatiquement.
              </Notice>
              <section className={`guided-pricing-strategy ${guidanceMode}`} data-testid="guided-pricing-strategy">
                <div><span className="eyebrow">Mécanique reconnue automatiquement</span><h3>{guidanceModeCopy.label}</h3></div>
                <p>{guidanceModeCopy.detail}</p>
                <small>Vous pouvez mélanger les deux mécaniques : ARD Manager conservera la base, les suppléments calculés et les prestations ajoutées comme des éléments distincts et analysables.</small>
              </section>
              <div className="form-grid">
                <label>Nom du questionnaire<input value={qualificationEditor.name} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, name: event.target.value }))} required /></label>
                <label className="wide">Description<textarea value={qualificationEditor.description} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, description: event.target.value }))} /></label>
                <label className="wide">Motif de cette nouvelle version<input value={qualificationEditor.change_reason} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, change_reason: event.target.value }))} placeholder="Pourquoi cette version évolue-t-elle ?" required /></label>
              </div>
              <section className="modal-subsection">
                <div className="section-heading-row">
                  <div><h3>Questions dans l’ordre d’affichage</h3><p className="section-intro">Écrivez simplement la question, choisissez la forme de réponse puis indiquez ce que chaque réponse change concrètement.</p></div>
                  <div className="row-actions">
                    <button type="button" className="secondary compact" onClick={() => setQualificationEditor((current) => current && ({ ...current, questions: [...current.questions, { id: crypto.randomUUID(), question_key: questionnaireTechnicalId("q"), label: "", help_text: "", response_type: "text", required: false, unit_label: "", options: [], costing_target: null, pricing_rules: [] }] }))}>＋ Ajouter une question</button>
                    {!qualificationEditor.questions.length && <button type="button" className="compact" onClick={prepareEndConstructionQualification}>Préparer « fin de chantier »</button>}
                  </div>
                </div>
                {qualificationEditor.questions.map((question, index) => (
                  <div className="form-grid costing-component-row" key={question.id} data-testid={`qualification-question-${index}`}>
                    <input type="hidden" value={question.question_key} readOnly />
                    <label className="wide">Votre question<input value={question.label} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) }))} placeholder="Ex. Faut-il évacuer des gravats ?" required /></label>
                    <label>Comment répondre ?<select value={question.response_type} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, response_type: event.target.value as ServiceQualificationQuestion["response_type"], options: ["single_choice", "multiple_choice"].includes(event.target.value) ? item.options : [], costing_target: ["number", "integer"].includes(event.target.value) ? item.costing_target : null, pricing_rules: [] } : item) }))}><option value="boolean">Oui ou non</option><option value="number">Un nombre avec décimales</option><option value="integer">Un nombre entier</option><option value="single_choice">Un choix dans une liste</option><option value="multiple_choice">Plusieurs cases à cocher</option><option value="text">Un texte libre</option></select></label>
                    <label>Unité affichée<input value={question.unit_label} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, unit_label: event.target.value } : item) }))} placeholder="m², heure, unité…" /></label>
                    <label>Ce nombre représente<select value={question.costing_target || ""} disabled={!['number','integer'].includes(question.response_type)} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, costing_target: (event.target.value || null) as ServiceQualificationQuestion["costing_target"] } : item) }))}><option value="">Une information utile seulement</option><option value="output_quantity">La surface ou quantité à traiter</option><option value="total_labor_hours">Le temps humain total déjà connu</option><option value="team_size">Le nombre de personnes nécessaires</option><option value="travel_cost_ex_vat">Le coût de déplacement prévu</option><option value="other_cost_ex_vat">Un autre coût direct</option><option value="contingency_rate">La réserve pour imprévus (%)</option></select></label>
                    <label className="checkbox-line"><input type="checkbox" checked={question.required} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, required: event.target.checked } : item) }))} /> Réponse obligatoire</label>
                    <label className="wide">Aide affichée<input value={question.help_text} onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, help_text: event.target.value } : item) }))} /></label>
                    {["single_choice", "multiple_choice"].includes(question.response_type) && (
                      <div className="wide qualification-choice-editor">
                        <strong>Réponses proposées</strong>
                        <small>Écrivez uniquement ce que l’utilisateur doit lire. La partie technique est créée automatiquement.</small>
                        {question.options.map((option, optionIndex) => (
                          <div className="qualification-choice-row" key={option.value}>
                            <input
                              aria-label={`Réponse ${optionIndex + 1}`}
                              value={option.label}
                              onChange={(event) => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, options: item.options.map((choice, choiceIndex) => choiceIndex === optionIndex ? { ...choice, label: event.target.value } : choice) } : item) }))}
                              placeholder="Ex. Gravats légers"
                            />
                            <button type="button" className="secondary compact" onClick={() => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, options: item.options.filter((_choice, choiceIndex) => choiceIndex !== optionIndex), pricing_rules: item.pricing_rules.filter((rule) => rule.comparison_value !== option.value) } : item) }))}>Retirer</button>
                          </div>
                        ))}
                        <button type="button" className="secondary compact" onClick={() => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, options: [...item.options, { value: questionnaireTechnicalId("q"), label: "" }] } : item) }))}>＋ Ajouter une réponse</button>
                      </div>
                    )}
                    {question.response_type !== "text" && (
                      <section className="wide qualification-rules-editor">
                        <div className="section-heading-row">
                          <div>
                            <h4>Que change cette réponse ?</h4>
                            <p className="section-intro">Une conséquence peut ajouter du temps, modifier le prix, prévoir un produit du stock et enrichir automatiquement le texte du devis.</p>
                          </div>
                          <button type="button" className="secondary compact" onClick={() => addQualificationRule(index)}>＋ Ajouter une conséquence</button>
                        </div>
                        {question.pricing_rules.map((rule, ruleIndex) => {
                          const evidence = (data.qualificationRuleEvidence || [])
                            .filter((item) => item.catalog_item_id === qualificationEditor.catalog_item_id && item.question_key === question.question_key && item.rule_id === rule.rule_id)
                            .sort((a, b) => b.evidence_count - a.evidence_count)[0],
                            evidencePricingProposals = buildGuidanceEvidencePricingProposals({
                              evidenceCount: Number(evidence?.evidence_count || 0),
                              averageCostExVatPerUnit: evidence?.average_actual_cost_ex_vat_per_output_unit,
                              targetMarginRate: catalogItem?.target_margin_rate,
                            });
                          return (
                            <div className="form-grid qualification-rule-row" key={`${question.id}-${ruleIndex}`} data-testid={`qualification-rule-${index}-${ruleIndex}`}>
                              <input type="hidden" value={rule.rule_id} readOnly />
                              <label className="wide">Nom de cette conséquence<input value={rule.label} onChange={(event) => updateQualificationRule(index, ruleIndex, { label: event.target.value })} placeholder="Ex. Évacuation de gravats lourds" required /></label>
                              <label>Cette conséquence s’applique quand la réponse<select value={rule.operator} onChange={(event) => updateQualificationRule(index, ruleIndex, { operator: event.target.value as QualificationPricingRule["operator"] })}>
                                <option value="equals">est égale à</option>
                                {question.response_type === "multiple_choice" && <option value="contains">Contient</option>}
                                {["number", "integer"].includes(question.response_type) && <><option value="gte">est supérieure ou égale à</option><option value="lte">est inférieure ou égale à</option><option value="between">est comprise entre</option></>}
                              </select></label>
                              {question.response_type === "boolean" ? (
                                <label>Quand l’utilisateur répond<select value={String(rule.comparison_value)} onChange={(event) => updateQualificationRule(index, ruleIndex, { comparison_value: event.target.value === "true" })}><option value="true">Oui</option><option value="false">Non</option></select></label>
                              ) : ["single_choice", "multiple_choice"].includes(question.response_type) ? (
                                <label>Réponse concernée<select value={String(rule.comparison_value)} onChange={(event) => updateQualificationRule(index, ruleIndex, { comparison_value: event.target.value })}><option value="">Choisir</option>{question.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                              ) : (
                                <label>Seuil qui déclenche cette conséquence<input type="number" step="0.001" value={Number(rule.comparison_value)} onChange={(event) => updateQualificationRule(index, ruleIndex, { comparison_value: Number(event.target.value) })} /></label>
                              )}
                              {rule.operator === "between" && <label>Borne haute<input type="number" step="0.001" value={rule.comparison_value_max ?? ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { comparison_value_max: event.target.value === "" ? null : Number(event.target.value) })} required /></label>}
                              <label>Ajouter au prix pour chaque {question.unit_label || "unité"} (€ HT)<input type="number" step="0.01" value={rule.unit_price_delta_ex_vat ?? ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { unit_price_delta_ex_vat: event.target.value === "" ? null : Number(event.target.value) })} placeholder="Ex. 2" /></label>
                              <label>Faire varier le prix de (%)<input type="number" min="-99" max="900" step="1" value={rule.unit_price_multiplier == null ? "" : Math.round((Number(rule.unit_price_multiplier) - 1) * 10000) / 100} onChange={(event) => updateQualificationRule(index, ruleIndex, { unit_price_multiplier: event.target.value === "" ? null : 1 + Number(event.target.value) / 100 })} placeholder="Ex. 10 pour ajouter 10 %" /></label>
                              <label>Temps humain nécessaire pour chaque {question.unit_label || "unité"} (heures)<input type="number" min="0.001" step="0.001" value={rule.labor_hours_per_output_unit ?? ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { labor_hours_per_output_unit: event.target.value === "" ? null : Number(event.target.value) })} placeholder="Ex. 0,10 h par m²" /></label>
                              <label>Faire varier le temps estimé de (%)<input type="number" min="-99" max="900" step="1" value={rule.labor_time_multiplier == null ? "" : Math.round((Number(rule.labor_time_multiplier) - 1) * 10000) / 100} onChange={(event) => updateQualificationRule(index, ruleIndex, { labor_time_multiplier: event.target.value === "" ? null : 1 + Number(event.target.value) / 100 })} placeholder="Ex. 20 pour ajouter 20 %" /></label>
                              <label>Ajouter un montant fixe au devis (€ HT)<input type="number" step="0.01" value={rule.fixed_cost_delta_ex_vat ?? ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { fixed_cost_delta_ex_vat: event.target.value === "" ? null : Number(event.target.value) })} placeholder="Ex. 50" /></label>
                              <label className="wide">Ajouter une prestation identifiable au devis<select value={rule.line_item_mode || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_mode: (event.target.value || null) as QualificationPricingRule["line_item_mode"], line_item_catalog_id: null, line_item_name: null, line_item_quantity: 1, line_item_unit_price_ex_vat: null, line_item_vat_rate: data.organization?.default_vat_rate ?? 20 })}><option value="">Non, cette conséquence modifie seulement le calcul principal</option><option value="catalog">Oui · prestation commune à tous les clients</option><option value="client">Oui · nouvelle prestation réservée au client de ce devis</option><option value="one_off">Oui · ligne exceptionnelle pour ce devis uniquement</option></select><small>Une ligne identifiable permet ensuite de mesurer séparément son chiffre d’affaires, son temps et sa rentabilité.</small></label>
                              {rule.line_item_mode === "catalog" && <>
                                <label className="wide">Prestation commune existante<select value={rule.line_item_catalog_id || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_catalog_id: event.target.value || null })}><option value="">Choisir dans la bibliothèque</option>{(data.serviceCatalogItems || []).filter((item) => item.active && item.item_kind === "service").map((item) => <option key={item.id} value={item.id}>{item.name} · {money(item.default_unit_price_ex_vat)} / {item.default_unit_label}</option>)}</select></label>
                                <div className="wide row-actions"><button type="button" className="secondary compact" onClick={() => createCatalogModelForRule(index, ruleIndex, rule)}>＋ La prestation n’existe pas encore</button><small>Elle sera créée dans la bibliothèque commune puis réutilisable pour tous les clients.</small></div>
                              </>}
                              {rule.line_item_mode && <label>Quantité de cette ligne<input type="number" min="0.001" step="0.001" value={rule.line_item_quantity ?? 1} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_quantity: Number(event.target.value) })} /></label>}
                              {(rule.line_item_mode === "client" || rule.line_item_mode === "one_off") && <>
                                <label className="wide">Nom lisible de la prestation<input value={rule.line_item_name || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_name: event.target.value })} placeholder={rule.line_item_mode === "client" ? "Ex. Supplément spécifique au client" : "Ex. Intervention exceptionnelle"} required /></label>
                                <label>Prix unitaire HT (€)<input type="number" min="0" step="0.01" value={rule.line_item_unit_price_ex_vat ?? ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_unit_price_ex_vat: event.target.value === "" ? null : Number(event.target.value) })} required /></label>
                                <label>TVA (%)<input type="number" min="0" max="100" step="0.01" value={rule.line_item_vat_rate ?? data.organization?.default_vat_rate ?? 20} onChange={(event) => updateQualificationRule(index, ruleIndex, { line_item_vat_rate: Number(event.target.value) })} required /></label>
                                <Notice tone="info">{rule.line_item_mode === "client" ? "Lors du premier brouillon, cette prestation sera ajoutée à la fiche du client et proposée dans ses prochains devis, sans l’ajouter aux autres clients." : "Cette ligne restera propre à ce devis. Elle n’encombrera ni la bibliothèque ni les tarifs futurs."}</Notice>
                              </>}
                              <label className="wide">Produit ou consommable à prévoir depuis le stock<select value={rule.inventory_item_id || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { inventory_item_id: event.target.value || null })}><option value="">Aucun produit spécifique</option>{(data.inventoryItems || []).filter((item) => item.active && item.item_kind !== "kit").map((item) => <option key={item.id} value={item.id}>{item.name}{item.yield_output_quantity ? ` · rendement renseigné` : " · rendement à compléter"}</option>)}</select></label>
                              {rule.inventory_item_id && <>
                                <label>Nombre de passages / applications<input type="number" min="0.001" step="0.1" value={rule.inventory_application_passes ?? 1} onChange={(event) => updateQualificationRule(index, ruleIndex, { inventory_application_passes: Number(event.target.value) })} /></label>
                                <label>Comment traiter son prix ?<select value={rule.inventory_billing_mode || "included"} onChange={(event) => updateQualificationRule(index, ruleIndex, { inventory_billing_mode: event.target.value as "included" | "separate" })}><option value="included">Coût absorbé dans la prestation</option><option value="separate">Ajouter son prix conseillé au devis</option></select></label>
                              </>}
                              <label className="checkbox-line wide"><input type="checkbox" checked={Boolean(rule.track_activity_time)} onChange={(event) => updateQualificationRule(index, ruleIndex, { track_activity_time: event.target.checked, activity_label: event.target.checked ? rule.activity_label || rule.label || "" : null })} /> Suivre le temps réel de cette étape après mission</label>
                              {rule.track_activity_time && <label className="wide">Nom court affiché à l’intervenant<input value={rule.activity_label || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { activity_label: event.target.value })} placeholder="Ex. Évacuation des gravats" required /><small>L’intervenant verra uniquement ce libellé métier, jamais les règles de prix ni les coûts.</small></label>}
                              <label className="wide">Phrase à ajouter au devis quand cette conséquence s’applique<textarea value={rule.description_line || ""} onChange={(event) => updateQualificationRule(index, ruleIndex, { description_line: event.target.value || null })} placeholder="Ex. Évacuation et traitement des gravats adaptés au volume constaté. Vous pouvez écrire {quantite} et {unite}." /></label>
                              <div className="wide rule-evidence-summary">
                                {evidence ? (
                                  <>
                                    <small><strong>{evidence.evidence_count} mission(s) validée(s) où cette conséquence était présente</strong> · médiane {evidence.median_actual_labor_hours_per_output_unit == null ? "à calculer" : `${Number(evidence.median_actual_labor_hours_per_output_unit).toFixed(3)} h/unité`} · estimation prudente {evidence.prudent_actual_labor_hours_per_output_unit == null ? "à calculer" : `${Number(evidence.prudent_actual_labor_hours_per_output_unit).toFixed(3)} h/unité`} · moyenne informative {evidence.average_actual_labor_hours_per_output_unit == null ? "à contrôler" : `${Number(evidence.average_actual_labor_hours_per_output_unit).toFixed(3)} h/unité`} · plage {evidence.minimum_actual_labor_hours_per_output_unit == null || evidence.maximum_actual_labor_hours_per_output_unit == null ? "à contrôler" : `${Number(evidence.minimum_actual_labor_hours_per_output_unit).toFixed(3)} à ${Number(evidence.maximum_actual_labor_hours_per_output_unit).toFixed(3)} h/unité`}.</small>
                                    <small>Le temps étant déclaré pour la mission entière, ces chiffres décrivent le scénario complet et non le temps causé par cette seule réponse. Ils ne sont jamais recopiés automatiquement dans la règle.</small>
                                    {evidencePricingProposals.length > 0 ? <>
                                      <div className="qualification-evidence-proposals" data-testid="qualification-evidence-pricing-proposals">
                                        {evidencePricingProposals.map((proposal) => <article key={proposal.key} data-pricing-proposal={proposal.key === "advised" ? "recommended" : proposal.key}><span>{proposal.label}</span><strong>{money(proposal.unitPriceExVat)} HT / unité</strong><small>Marge visée {proposal.marginRate.toFixed(0)} %</small></article>)}
                                      </div>
                                      <small><strong>Propositions pour le scénario complet observé.</strong> Elles utilisent le coût moyen réel et ne remplacent jamais automatiquement la base ni ce supplément. Une nouvelle version doit être relue et publiée par un administrateur.</small>
                                    </> : <small>Trois missions validées au minimum sont nécessaires avant de proposer une actualisation tarifaire fondée sur les coûts réels.</small>}
                                  </>
                                ) : <small>Aucune mission validée ne permet encore de confirmer cette règle.</small>}
                              </div>
                              <div className="row-actions wide"><button type="button" className="danger compact" onClick={() => removeQualificationRule(index, ruleIndex)}>Supprimer la règle</button></div>
                            </div>
                          );
                        })}
                        {!question.pricing_rules.length && <p className="empty">Aucune règle : la réponse reste informative jusqu’à ce que vous définissiez un effet.</p>}
                      </section>
                    )}
                    <div className="row-actions wide">
                      <button type="button" className="secondary compact" disabled={index === 0} onClick={() => setQualificationEditor((current) => { if (!current || index === 0) return current; const questions = [...current.questions]; [questions[index - 1], questions[index]] = [questions[index], questions[index - 1]]; return { ...current, questions }; })}>↑ Monter</button>
                      <button type="button" className="secondary compact" disabled={index === qualificationEditor.questions.length - 1} onClick={() => setQualificationEditor((current) => { if (!current || index >= current.questions.length - 1) return current; const questions = [...current.questions]; [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]]; return { ...current, questions }; })}>↓ Descendre</button>
                      <button type="button" className="danger compact" onClick={() => setQualificationEditor((current) => current && ({ ...current, questions: current.questions.filter((_, itemIndex) => itemIndex !== index) }))}>Supprimer</button>
                    </div>
                  </div>
                ))}
                {!qualificationEditor.questions.length && <p className="empty">Aucune question. Utilisez le modèle fin de chantier ou créez vos propres questions.</p>}
              </section>
              {!!history.length && <section className="modal-subsection"><h3>Historique immuable</h3>{history.map((item) => <p key={item.id}><strong>Version {item.version}</strong> · {item.status === "published" ? "Publiée" : item.status === "draft" ? "Brouillon" : "Archivée"} · {frDate(item.created_at, true)} · {item.change_reason}</p>)}</section>}
              {message && <Notice tone="info">{message}</Notice>}
              <div className="row-actions">
                <button type="button" className="secondary" onClick={() => setQualificationEditor(null)}>Annuler</button>
                <button type="button" className="secondary" disabled={busy} onClick={() => saveQualificationQuestionnaire(false)}>{busy ? "Enregistrement…" : "Enregistrer en brouillon"}</button>
                <button type="button" disabled={busy} onClick={() => saveQualificationQuestionnaire(true)}>{busy ? "Publication…" : "Publier cette version"}</button>
              </div>
            </div>
          </div>
        );
      })()}
      {costingProfileEditor && (() => {
        const catalogItem = (data.serviceCatalogItems || []).find(
          (item) => item.id === costingProfileEditor.catalog_item_id,
        ),
          yieldRecommendations = (data.costingYieldRecommendations || [])
            .filter((item) => item.catalog_item_id === costingProfileEditor.catalog_item_id)
            .sort((a, b) => b.recommendation_number - a.recommendation_number),
          latestYieldRecommendation = yieldRecommendations[0] || null;
        return (
          <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Rendement et coûts de la prestation">
            <form className="portal-detail create-modal" onSubmit={saveCostingProfile}>
              <div className="portal-detail-head">
                <div><span className="eyebrow">Hypothèses internes versionnées</span><h2>Rendement & coûts · {catalogItem?.name}</h2></div>
                <button type="button" className="icon-close" onClick={() => setCostingProfileEditor(null)} aria-label="Fermer">×</button>
              </div>
              <Notice>
                Les rendements issus du terrain ne deviendront des références qu’après validation administrative. Une valeur incertaine reste « À contrôler ».
              </Notice>
              {hasDeploymentCapability(data, "mission_costing_feedback") && (
                <section className="modal-subsection" data-testid="yield-consolidation">
                  <div className="section-heading-row">
                    <div>
                      <h3>Consolidation des missions validées</h3>
                      <p className="section-intro">
                        Une seule mission ne suffit pas à automatiser une référence. L’analyse retient la dernière comparaison complète de chaque mission, calcule une médiane, isole les valeurs atypiques puis pondère le rendement par les heures réellement approuvées.
                      </p>
                    </div>
                    <button type="button" className="secondary compact" disabled={busy} onClick={createYieldRecommendation}>
                      {busy ? "Analyse…" : "Analyser les missions validées"}
                    </button>
                  </div>
                  <div className="form-grid">
                    <label>
                      Missions cohérentes minimales
                      <input type="number" min="2" max="50" step="1" value={yieldMinimumSamples} onChange={(event) => setYieldMinimumSamples(Number(event.target.value))} />
                    </label>
                    <label>
                      Tolérance autour de la médiane (%)
                      <input type="number" min="0.1" max="200" step="0.1" value={yieldOutlierTolerance} onChange={(event) => setYieldOutlierTolerance(Number(event.target.value))} />
                    </label>
                  </div>
                  {latestYieldRecommendation ? (
                    <div className="validated-report">
                      <div className="detail-grid compact-grid">
                        <div><span>Analyse</span><strong>Version {latestYieldRecommendation.recommendation_number}</strong><small><Status value={latestYieldRecommendation.status} /></small></div>
                        <div><span>Missions trouvées</span><strong>{latestYieldRecommendation.candidate_review_count}</strong><small>Une seule comparaison complète par mission</small></div>
                        <div><span>Retenues</span><strong>{latestYieldRecommendation.included_review_count}</strong><small>Seuil demandé : {latestYieldRecommendation.minimum_sample_size}</small></div>
                        <div><span>Valeurs atypiques isolées</span><strong>{latestYieldRecommendation.excluded_review_count}</strong><small>Tolérance ± {latestYieldRecommendation.outlier_tolerance_percent}%</small></div>
                        <div><span>Rendement actuel</span><strong>{latestYieldRecommendation.current_yield_per_labor_hour == null ? "Non défini" : `${latestYieldRecommendation.current_yield_per_labor_hour} ${latestYieldRecommendation.output_unit}/h`}</strong></div>
                        <div><span>Rendement consolidé proposé</span><strong>{latestYieldRecommendation.recommended_yield_per_labor_hour == null ? "À contrôler" : `${latestYieldRecommendation.recommended_yield_per_labor_hour} ${latestYieldRecommendation.output_unit}/h`}</strong><small>{latestYieldRecommendation.total_output_quantity} {latestYieldRecommendation.output_unit} / {latestYieldRecommendation.total_labor_hours} h</small></div>
                      </div>
                      {latestYieldRecommendation.status === "needs_review" && (
                        <Notice tone="warning">
                          Échantillon insuffisant après exclusion des valeurs atypiques : cette analyse ne peut pas modifier le modèle.
                        </Notice>
                      )}
                      {(latestYieldRecommendation.status === "ready" || latestYieldRecommendation.status === "needs_review") && (
                        <>
                          <label className="wide">
                            Note de décision pour la consolidation
                            <textarea value={yieldDecisionNotes} onChange={(event) => setYieldDecisionNotes(event.target.value)} placeholder="Contrôle des missions retenues, explication des valeurs isolées…" />
                          </label>
                          <div className="row-actions">
                            <button type="button" className="danger compact" disabled={busy} onClick={() => decideYieldRecommendation(latestYieldRecommendation, "rejected")}>Refuser cette recommandation</button>
                            {latestYieldRecommendation.status === "ready" && (
                              <button type="button" className="success compact" disabled={busy} onClick={() => decideYieldRecommendation(latestYieldRecommendation, "approved")}>Valider et créer une nouvelle version</button>
                            )}
                          </div>
                        </>
                      )}
                      {latestYieldRecommendation.decision_notes && <small>Décision : {latestYieldRecommendation.decision_notes}</small>}
                    </div>
                  ) : (
                    <p className="empty">Aucune consolidation n’a encore été calculée pour cette prestation.</p>
                  )}
                </section>
              )}
              <div className="form-grid">
                <label>Unité produite<select value={costingProfileEditor.output_unit} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, output_unit: event.target.value as ServiceCostingProfile["output_unit"] }))}><option value="m2">m²</option><option value="m3">m³</option><option value="metre_lineaire">m linéaire</option><option value="unite">unité</option><option value="forfait">forfait</option></select></label>
                <label>Rendement humain / heure<input type="number" min="0.001" step="0.001" value={costingProfileEditor.expected_output_per_labor_hour} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, expected_output_per_labor_hour: event.target.value }))} placeholder="À contrôler si inconnu" /></label>
                <label>Taille d’équipe habituelle<input type="number" min="1" max="50" step="1" value={costingProfileEditor.default_team_size} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, default_team_size: Number(event.target.value) }))} required /></label>
                <label>Coût moyen main-d’œuvre / h<input type="number" min="0" step="0.01" value={costingProfileEditor.default_labor_cost_per_hour} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, default_labor_cost_per_hour: event.target.value }))} placeholder="À contrôler si inconnu" /></label>
                <label>Trajet prévu HT<input type="number" min="0" step="0.01" value={costingProfileEditor.default_travel_cost_ex_vat} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, default_travel_cost_ex_vat: Number(event.target.value) }))} /></label>
                <label>Imprévus (%)<input type="number" min="0" max="100" step="0.1" value={costingProfileEditor.contingency_rate} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, contingency_rate: Number(event.target.value) }))} required /></label>
                <label>Objectif<select value={costingProfileEditor.target_mode} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, target_mode: event.target.value as "margin" | "markup" }))}><option value="margin">Marge sur prix de vente</option><option value="markup">Majoration du coût</option></select></label>
                <label>Taux cible (%)<input type="number" min="0" max={costingProfileEditor.target_mode === "margin" ? 99.99 : 1000} step="0.1" value={costingProfileEditor.target_rate} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, target_rate: Number(event.target.value) }))} required /></label>
                <label>Niveau de confiance<select value={costingProfileEditor.confidence} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, confidence: event.target.value as ServiceCostingProfile["confidence"] }))}><option value="to_review">À contrôler</option><option value="low">Faible</option><option value="medium">Moyen</option><option value="high">Élevé</option><option value="verified">Vérifié</option></select></label>
                <label className="wide">Compétences indispensables<input aria-label="Compétences indispensables" value={costingProfileEditor.required_skills_text} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, required_skills_text: event.target.value }))} placeholder="Ex. traitement de sols, monobrosse, travail en hauteur" /><small>Séparez les compétences par une virgule. Elles servent à préparer l’affectation, mais ne sont jamais visibles sur le devis client.</small></label>
                <label className="wide">Hypothèses et source<textarea value={costingProfileEditor.assumptions_notes} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, assumptions_notes: event.target.value }))} placeholder="Expliquez d’où viennent les valeurs et ce qui reste à vérifier." /></label>
              </div>
              <section className="modal-subsection">
                <div className="section-heading-row"><div><h3>Moyens et achats propres à cette prestation</h3><p className="section-intro">Ajoutez les locations, achats ou prestations externes nécessaires. Les produits consommés du stock restent calculés séparément par leur rendement.</p></div><button type="button" className="secondary compact" onClick={() => setCostingProfileEditor((current) => current && ({ ...current, components: [...current.components, { id: crypto.randomUUID(), component_type: "equipment", business_kind: "equipment", label: "", cost_basis: "per_job", quantity: 1, unit_label: "forfait", unit_cost_ex_vat: 0, supplier_id: "", purchase_reference: "", purchase_url: "", source_note: "" }] }))}>＋ Ajouter un coût</button></div>
                {costingProfileEditor.components.map((component, index) => (
                  <div className="form-grid costing-component-row" data-business-kind={component.business_kind} key={component.id}>
                    <label>Nature du coût<select value={component.business_kind} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, business_kind: event.target.value as ServiceCostingComponent["business_kind"], component_type: event.target.value === "equipment" ? "equipment" : "other", supplier_id: event.target.value === "purchase" ? item.supplier_id : "", purchase_reference: event.target.value === "purchase" ? item.purchase_reference : "", purchase_url: event.target.value === "purchase" ? item.purchase_url : "" } : item) }))}><option value="equipment">Matériel / location</option><option value="purchase">Achat / prestation externe</option><option value="other">Autre coût</option></select></label>
                    <label>Libellé<input value={component.label} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) }))} required /></label>
                    <label>Calcul<select value={component.cost_basis} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, cost_basis: event.target.value as ServiceCostingComponent["cost_basis"] } : item) }))}><option value="per_job">Par chantier</option><option value="per_elapsed_hour">Par heure de présence</option><option value="per_output_unit">Par unité produite</option></select></label>
                    <label>Quantité<input type="number" min="0.001" step="0.001" value={component.quantity} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, quantity: Number(event.target.value) } : item) }))} required /></label>
                    <label>Unité<input value={component.unit_label} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, unit_label: event.target.value } : item) }))} required /></label>
                    <label>Coût unitaire HT<input type="number" min="0" step="0.01" value={component.unit_cost_ex_vat} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, unit_cost_ex_vat: Number(event.target.value) } : item) }))} required /></label>
                    {component.business_kind === "purchase" && <>
                      <label>Fournisseur<select value={component.supplier_id} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, supplier_id: event.target.value } : item) }))}><option value="">À sélectionner si connu</option>{(data.inventorySuppliers || []).filter((supplier) => supplier.active || supplier.id === component.supplier_id).map((supplier) => <option value={supplier.id} key={supplier.id}>{supplier.name}</option>)}</select></label>
                      <label>Référence / devis fournisseur<input value={component.purchase_reference} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, purchase_reference: event.target.value } : item) }))} placeholder="Ex. devis LOC-2026-18" /></label>
                      <label className="wide">Lien fournisseur<input type="url" value={component.purchase_url} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, purchase_url: event.target.value } : item) }))} placeholder="https://…" /></label>
                    </>}
                    <label className="wide">Source / note<input value={component.source_note} onChange={(event) => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.map((item, itemIndex) => itemIndex === index ? { ...item, source_note: event.target.value } : item) }))} /></label>
                    <button type="button" className="danger compact" onClick={() => setCostingProfileEditor((current) => current && ({ ...current, components: current.components.filter((_, itemIndex) => itemIndex !== index) }))}>Supprimer ce coût</button>
                  </div>
                ))}
                {!costingProfileEditor.components.length && <p className="empty">Aucun moyen ou achat propre à cette prestation.</p>}
              </section>
              {message && <Notice tone="info">{message}</Notice>}
              <div className="row-actions"><button type="button" className="secondary" onClick={() => setCostingProfileEditor(null)}>Annuler</button><button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer une nouvelle version"}</button></div>
            </form>
          </div>
        );
      })()}
      {quoteCostingEditor && (() => {
        const line = (data.quoteLines || []).find((item) => item.id === quoteCostingEditor.quote_line_id),
          catalogItem = (data.serviceCatalogItems || []).find((item) => item.id === quoteCostingEditor.catalog_item_id),
          activeCostingProfile = catalogItem ? latestCostingProfile(catalogItem.id) : null,
          questionnaire = latestQualification(quoteCostingEditor.catalog_item_id, true),
          questions = questionnaire ? qualificationQuestions(questionnaire.id) : [],
          qualificationRun = qualificationRunId
            ? (data.quoteQualificationRuns || []).find((item) => item.id === qualificationRunId) || null
            : null,
          runs = (data.quoteCostingRuns || []).filter((run) => run.quote_line_id === quoteCostingEditor.quote_line_id).sort((a, b) => b.run_number - a.run_number),
          latestRun = runs[0] || null,
          detailLines = latestRun ? (data.quoteCostingRunLines || []).filter((item) => item.costing_run_id === latestRun.id).sort((a, b) => a.position - b.position) : [];
        return (
          <div className="portal-overlay nested-modal-overlay" role="dialog" aria-modal="true" aria-label="Chiffrage guidé du devis">
            <form className="portal-detail create-modal" onSubmit={createQuoteCostingRun}>
              <div className="portal-detail-head"><div><span className="eyebrow">Calcul interne explicable</span><h2>Chiffrer · {line?.description}</h2></div><button type="button" className="icon-close" onClick={() => setQuoteCostingEditor(null)} aria-label="Fermer">×</button></div>
              <ChantierSavedSummary notes={runs.find(run => run.notes?.includes('"kind":"chantier"'))?.notes} currentPrice={line ? Number(line.quantity)*Number(line.unit_price_ex_vat) : undefined} />
              {!catalogItem && <Notice tone="warning">Cette ligne n’est pas reliée à un modèle central. Renseignez les heures et le coût horaire manuellement ; les valeurs absentes resteront « À contrôler ».</Notice>}
              {!!activeCostingProfile?.required_skills?.length && <Notice tone="info">Compétences à prévoir pour l’affectation : {activeCostingProfile.required_skills.join(" · ")}. Cette information reste interne et n’apparaît pas sur le devis client.</Notice>}
              {questionnaire && (
                <section className="modal-subsection" data-testid="quote-qualification-questionnaire">
                  <div className="section-heading-row">
                    <div><h3>{questionnaire.name}</h3><p className="section-intro">Version {questionnaire.version} publiée · les réponses sont figées avec le devis et restent modifiables par une nouvelle version.</p></div>
                    {qualificationRun && <Status value={qualificationRun.status} />}
                  </div>
                  <div className="form-grid">
                    {questions.map((question) => {
                      const answer = qualificationAnswers[question.question_key];
                      if (question.response_type === "boolean") return (
                        <label key={question.id}>{question.label}{question.required ? " *" : ""}<select value={answer === true ? "true" : answer === false ? "false" : ""} onChange={(event) => setQualificationAnswers((current) => ({ ...current, [question.question_key]: event.target.value === "" ? null : event.target.value === "true" }))}><option value="">À renseigner</option><option value="true">Oui</option><option value="false">Non</option></select>{question.help_text && <small>{question.help_text}</small>}</label>
                      );
                      if (question.response_type === "number" || question.response_type === "integer") return (
                        <label key={question.id}>{question.label}{question.required ? " *" : ""}<input type="number" min="0" step={question.response_type === "integer" ? "1" : "0.01"} value={answer == null ? "" : String(answer)} onChange={(event) => setQualificationAnswers((current) => ({ ...current, [question.question_key]: event.target.value === "" ? null : Number(event.target.value) }))} />{question.unit_label && <small>Unité : {question.unit_label}</small>}{question.help_text && <small>{question.help_text}</small>}</label>
                      );
                      if (question.response_type === "single_choice") return (
                        <label key={question.id}>{question.label}{question.required ? " *" : ""}<select value={typeof answer === "string" ? answer : ""} onChange={(event) => setQualificationAnswers((current) => ({ ...current, [question.question_key]: event.target.value || null }))}><option value="">À renseigner</option>{question.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>{question.help_text && <small>{question.help_text}</small>}</label>
                      );
                      if (question.response_type === "multiple_choice") return (
                        <label key={question.id}>{question.label}{question.required ? " *" : ""}<select multiple value={Array.isArray(answer) ? answer.map(String) : []} onChange={(event) => setQualificationAnswers((current) => ({ ...current, [question.question_key]: Array.from(event.target.selectedOptions, (option) => option.value) }))}>{question.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>{question.help_text && <small>{question.help_text}</small>}</label>
                      );
                      return (
                        <label className="wide" key={question.id}>{question.label}{question.required ? " *" : ""}<textarea value={typeof answer === "string" ? answer : ""} onChange={(event) => setQualificationAnswers((current) => ({ ...current, [question.question_key]: event.target.value }))} />{question.help_text && <small>{question.help_text}</small>}</label>
                      );
                    })}
                    <label className="wide">Note interne de qualification<textarea value={qualificationNotes} onChange={(event) => setQualificationNotes(event.target.value)} placeholder="Source des mesures, point restant à contrôler…" /></label>
                  </div>
                  <div className="row-actions"><button type="button" className="secondary" disabled={busy} onClick={saveQuoteQualification}>{busy ? "Enregistrement…" : qualificationRunId ? "Enregistrer une nouvelle version des réponses" : "Enregistrer la qualification"}</button></div>
                </section>
              )}
              {qualificationPricingPreview.recommended_total_sale_price_ex_vat != null && (() => {
                const appliedRules = Array.isArray(qualificationPricingPreview.applied_pricing_rules)
                    ? qualificationPricingPreview.applied_pricing_rules as Array<Record<string, unknown>>
                    : [],
                  unitLabel = String(qualificationPricingPreview.pricing_unit_label || "unité");
                return (
                  <section className="modal-subsection qualification-pricing-preview" data-testid="qualification-pricing-preview">
                    <h3>Préconisation issue du questionnaire</h3>
                    <div className="detail-grid">
                      <div><span>Base</span><strong>{money(Number(qualificationPricingPreview.base_unit_price_ex_vat || 0))} / {unitLabel}</strong></div>
                      <div><span>Après règles</span><strong>{money(Number(qualificationPricingPreview.recommended_unit_price_ex_vat || 0))} / {unitLabel}</strong><small>Supplément {money(Number(qualificationPricingPreview.unit_price_delta_ex_vat || 0))} · coefficient ×{Number(qualificationPricingPreview.unit_price_multiplier || 1).toFixed(2)}</small></div>
                      <div><span>Temps estimé</span><strong>{qualificationPricingPreview.total_labor_hours == null ? "À contrôler" : `${Number(qualificationPricingPreview.total_labor_hours).toFixed(2)} h humaines`}</strong><small>{qualificationPricingPreview.labor_hours_per_output_unit == null ? `Coefficient temps ×${Number(qualificationPricingPreview.labor_time_multiplier || 1).toFixed(2)}` : `${Number(qualificationPricingPreview.labor_hours_per_output_unit).toFixed(3)} h / ${unitLabel}`}</small></div>
                      <div><span>Prix prérempli</span><strong>{money(Number(qualificationPricingPreview.recommended_total_sale_price_ex_vat))} HT</strong><small>Validation manuelle obligatoire</small></div>
                    </div>
                    {!!appliedRules.length && <div className="table-wrap"><table><thead><tr><th>Règle appliquée</th><th>Critère</th><th>Réponse</th></tr></thead><tbody>{appliedRules.map((rule, index) => <tr key={`${String(rule.rule_id)}-${index}`}><td>{String(rule.rule_label || rule.rule_id)}</td><td>{String(rule.question_label || rule.question_key)}</td><td>{typeof rule.answer === "boolean" ? rule.answer ? "Oui" : "Non" : Array.isArray(rule.answer) ? rule.answer.join(", ") : String(rule.answer ?? "")}</td></tr>)}</tbody></table></div>}
                    <Notice>Ce montant est une préconisation croisant la quantité, le tarif de base et les règles versionnées. Le calcul de coût et la marge restent à contrôler avant d’appliquer le prix au devis.</Notice>
                  </section>
                );
              })()}
              {catalogItem && !questionnaire && hasDeploymentCapability(data, "service_qualification") && <Notice>Aucun questionnaire publié n’est rattaché à cette prestation. Le chiffrage manuel reste disponible.</Notice>}
              <div className="form-grid">
                <label>Modèle central<select value={quoteCostingEditor.catalog_item_id} onChange={(event) => { const profile = latestCostingProfile(event.target.value); setQualificationAnswers({}); setQualificationRunId(""); setQualificationNotes(""); setQualificationPricingPreview({}); setQuoteCostingEditor((current) => current && ({ ...current, catalog_item_id: event.target.value, output_unit: profile?.output_unit || current.output_unit, labor_cost_per_hour: profile?.default_labor_cost_per_hour == null ? current.labor_cost_per_hour : String(profile.default_labor_cost_per_hour), team_size: profile?.default_team_size || current.team_size, travel_cost_ex_vat: profile?.default_travel_cost_ex_vat ?? current.travel_cost_ex_vat, contingency_rate: profile?.contingency_rate ?? current.contingency_rate, target_mode: profile?.target_mode || current.target_mode, target_rate: profile?.target_rate ?? current.target_rate })); }}><option value="">Ligne libre</option>{(data.serviceCatalogItems || []).filter((item) => item.active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                <label>Quantité technique<input type="number" min="0.001" step="0.001" value={quoteCostingEditor.output_quantity} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, output_quantity: event.target.value }))} placeholder="Ex. 240" /></label>
                <label>Unité technique<select value={quoteCostingEditor.output_unit} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, output_unit: event.target.value as ServiceCostingProfile["output_unit"] }))}><option value="m2">m²</option><option value="m3">m³</option><option value="metre_lineaire">m linéaire</option><option value="unite">unité</option><option value="forfait">forfait</option></select></label>
                <label>Heures humaines totales<input type="number" min="0" step="0.25" value={quoteCostingEditor.total_labor_hours} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, total_labor_hours: event.target.value }))} placeholder="Automatique si rendement connu" /></label>
                <label>Coût main-d’œuvre / h<input type="number" min="0" step="0.01" value={quoteCostingEditor.labor_cost_per_hour} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, labor_cost_per_hour: event.target.value }))} placeholder="À contrôler" /></label>
                <label>Nombre d’intervenants<input type="number" min="1" max="50" step="1" value={quoteCostingEditor.team_size} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, team_size: Number(event.target.value) }))} required /><small>Ce nombre réduit la durée de présence, pas les heures humaines totales.</small></label>
                <label>Trajet HT<input type="number" min="0" step="0.01" value={quoteCostingEditor.travel_cost_ex_vat} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, travel_cost_ex_vat: Number(event.target.value) }))} /></label>
                <label>Autres coûts HT<input type="number" min="0" step="0.01" value={quoteCostingEditor.other_cost_ex_vat} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, other_cost_ex_vat: Number(event.target.value) }))} /></label>
                <label>Imprévus (%)<input type="number" min="0" max="100" step="0.1" value={quoteCostingEditor.contingency_rate} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, contingency_rate: Number(event.target.value) }))} /></label>
                <label>Objectif<select value={quoteCostingEditor.target_mode} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, target_mode: event.target.value as "margin" | "markup" }))}><option value="margin">Marge</option><option value="markup">Majoration</option></select></label>
                <label>Taux cible (%)<input type="number" min="0" max={quoteCostingEditor.target_mode === "margin" ? 99.99 : 1000} step="0.1" value={quoteCostingEditor.target_rate} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, target_rate: Number(event.target.value) }))} /></label>
                <label>Prix client envisagé HT<input type="number" min="0" step="0.01" value={quoteCostingEditor.proposed_total_ex_vat} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, proposed_total_ex_vat: Number(event.target.value) }))} /></label>
                <label className="wide">Note de calcul<textarea value={quoteCostingEditor.notes} onChange={(event) => setQuoteCostingEditor((current) => current && ({ ...current, notes: event.target.value }))} /></label>
              </div>
              <div className="row-actions"><button disabled={busy}>{busy ? "Calcul…" : "Calculer une nouvelle version"}</button></div>
              {latestRun && (
                <section className="modal-subsection">
                  <div className="section-heading-row"><div><h3>Dernier calcul · version {latestRun.run_number}</h3><p className="section-intro">{frDate(latestRun.created_at, true)} · <Status value={latestRun.status} /></p></div></div>
                  {latestRun.missing_inputs.length > 0 ? <Notice tone="warning">À contrôler avant validation : {latestRun.missing_inputs.map((item) => item.split(":")[0].replaceAll("_", " ")).join(", ")}.</Notice> : <Notice tone="success">Toutes les données indispensables sont présentes. Le prix reste à valider manuellement.</Notice>}
                  <div className="detail-grid">
                    <div><span>Coût total</span><strong>{money(latestRun.total_cost_ex_vat)} HT</strong><small>Main-d’œuvre {money(latestRun.labor_cost_ex_vat)} · matière {money(latestRun.material_cost_ex_vat)}</small></div>
                    <div><span>Durée</span><strong>{latestRun.total_labor_hours == null ? "À contrôler" : `${Number(latestRun.total_labor_hours).toFixed(2)} h humaines`}</strong><small>{latestRun.elapsed_hours == null ? "Présence à contrôler" : `${Number(latestRun.elapsed_hours).toFixed(2)} h sur place à ${latestRun.team_size}`}</small></div>
                    <div><span>Prix conseillé</span><strong>{latestRun.suggested_sale_price_ex_vat == null ? "À contrôler" : money(latestRun.suggested_sale_price_ex_vat)}</strong><small>Objectif {latestRun.target_rate}% ({latestRun.target_mode === "margin" ? "marge" : "majoration"})</small></div>
                    <div><span>Prix envisagé</span><strong>{money(latestRun.proposed_sale_price_ex_vat)}</strong><small className={latestRun.profitability_alert ? "data-warning" : ""}>{latestRun.projected_margin_rate == null ? "Marge à contrôler" : `Marge ${Number(latestRun.projected_margin_rate).toFixed(1)}%`}</small></div>
                  </div>
                  <div className="table-wrap"><table><thead><tr><th>Coût</th><th>Calcul</th><th>Montant HT</th><th>Source</th></tr></thead><tbody>{detailLines.map((detail) => <tr key={detail.id}><td>{detail.label}</td><td>{detail.quantity} {detail.unit_label} × {money(detail.unit_cost_ex_vat)}</td><td>{money(detail.amount_ex_vat)}</td><td>{detail.source_kind} · {detail.confidence}</td></tr>)}</tbody></table></div>
                  <div className="row-actions">
                    {latestRun.suggested_sale_price_ex_vat != null && <button type="button" className="secondary" onClick={() => setQuoteCostingEditor((current) => current && ({ ...current, proposed_total_ex_vat: Number(latestRun.suggested_sale_price_ex_vat) }))}>Reprendre le prix conseillé</button>}
                    <button type="button" disabled={busy || latestRun.missing_inputs.length > 0 || latestRun.status === "applied"} onClick={() => applyQuoteCosting(latestRun)}>Valider et appliquer le prix saisi</button>
                    {latestRun.status === "applied" && <button type="button" className="secondary" disabled={busy} onClick={() => prepareProcurementRequirements(latestRun)}>Préparer / actualiser les besoins</button>}
                  </div>
                  {latestRun.status === "applied" && <Notice tone="info">{(data.procurementRequirements || []).filter((item) => item.costing_run_id === latestRun.id && item.status !== "superseded").length} besoin(s) relié(s) à ce chiffrage. Cette préparation reste interne : elle ne commande et n’envoie rien.</Notice>}
                </section>
              )}
              {message && <Notice tone="info">{message}</Notice>}
              <div className="row-actions"><button type="button" className="secondary" onClick={() => setQuoteCostingEditor(null)}>Fermer</button></div>
            </form>
          </div>
        );
      })()}
      {missionChecklistEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Configurer la checklist de mission">
          <section className="portal-detail create-modal mission-checklist-editor">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Contrôle terrain · {missionChecklistEditor.item_name}</span>
                <h2>Checklist de fin de mission</h2>
                <p>
                  {missionChecklistEditor.latest_version
                    ? `La version ${missionChecklistEditor.latest_version} (${missionChecklistEditor.latest_status}) sert de base à cette nouvelle version.`
                    : "Première version : aucun réglage technique n’est nécessaire."}
                </p>
              </div>
              <button type="button" className="icon-close" onClick={() => setMissionChecklistEditor(null)} aria-label="Fermer">×</button>
            </div>
            <Notice>
              L’intervenant ne pourra clôturer la mission qu’après avoir coché toutes les étapes obligatoires et ajouté le nombre de photos demandé. Une publication ne modifie jamais les missions déjà commencées.
            </Notice>
            <div className="form-grid">
              <label>Nom visible par l’équipe<input value={missionChecklistEditor.name} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, name: event.target.value }))} required /></label>
              <label>Motif de cette version<input value={missionChecklistEditor.change_reason} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, change_reason: event.target.value }))} placeholder="Ex. ajout des photos après intervention" required /></label>
              <label className="wide">Consigne générale<textarea value={missionChecklistEditor.instructions} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, instructions: event.target.value }))} /></label>
            </div>
            <div className="mission-checklist-step-list">
              {missionChecklistEditor.items.map((item, index) => (
                <article className="mission-checklist-step-editor" key={`${item.item_key}-${index}`}>
                  <div className="section-heading-row">
                    <strong>Étape {index + 1}</strong>
                    {missionChecklistEditor.items.length > 1 && (
                      <button type="button" className="danger compact" onClick={() => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.filter((_entry, itemIndex) => itemIndex !== index) }))}>Supprimer</button>
                    )}
                  </div>
                  <div className="form-grid">
                    <label>Action à réaliser<input value={item.label} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, label: event.target.value } : entry) }))} placeholder="Ex. vérifier toutes les vitres" required /></label>
                    <label>Photos obligatoires<input type="number" min="0" max="20" step="1" value={item.minimum_photo_count} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, minimum_photo_count: Number(event.target.value), is_required: Number(event.target.value) > 0 ? true : entry.is_required } : entry) }))} /></label>
                    <label>Pièce ou zone<input value={item.zone_label} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, zone_label: event.target.value, zone_key: "" } : entry) }))} placeholder="Ex. Salle de bain" /></label>
                    <label className="wide">Précision utile<textarea value={item.instructions} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, instructions: event.target.value } : entry) }))} placeholder="Ce que l’intervenant doit contrôler ou photographier" /></label>
                    <label className="checkline wide"><input type="checkbox" checked={item.is_required} disabled={item.minimum_photo_count > 0} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, is_required: event.target.checked } : entry) }))} /> Obligatoire pour pouvoir terminer la mission</label>
                    <label className="checkline wide"><input type="checkbox" checked={item.zone_repeatable} onChange={(event) => setMissionChecklistEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, zone_repeatable: event.target.checked } : entry) }))} /> Cette pièce peut être ajoutée plusieurs fois sur place</label>
                  </div>
                </article>
              ))}
            </div>
            <button type="button" className="secondary" onClick={() => setMissionChecklistEditor((current) => current && ({ ...current, items: [...current.items, { item_key: `step-${current.items.length + 1}-${crypto.randomUUID().slice(0, 8)}`, label: "", instructions: "", is_required: true, minimum_photo_count: 0, zone_key: "general", zone_label: "Général", zone_repeatable: false }] }))}>＋ Ajouter une étape</button>
            <Notice>
              <strong>Validation métier.</strong> Le brouillon reste modifiable et sans effet sur les missions. « Valider et publier » enregistre automatiquement l’administrateur, la date et la nouvelle version ; aucune identité de validateur n’est à recopier depuis le document Word.
            </Notice>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="row-actions">
              <button type="button" className="secondary" onClick={() => setMissionChecklistEditor(null)}>Annuler</button>
              <button type="button" className="secondary" disabled={busy} onClick={() => saveMissionChecklist(false)}>{busy ? "Enregistrement…" : "Enregistrer en brouillon"}</button>
              <button type="button" disabled={busy} onClick={() => saveMissionChecklist(true)}>{busy ? "Publication…" : "Valider et publier"}</button>
            </div>
          </section>
        </div>
      )}
      {catalogModelEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Modèle de la bibliothèque centrale">
          <form className="portal-detail create-modal" onSubmit={saveCatalogModel}>
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Bibliothèque centrale</span>
                <h2>{catalogModelEditor.id ? "Modifier le modèle" : catalogCreationFlow === "guided" ? "Nouveau sujet guidé" : "Nouveau modèle"}</h2>
              </div>
              <button type="button" className="icon-close" onClick={() => { setCatalogModelEditor(null); setCatalogCreationTarget(null); }} aria-label="Fermer">×</button>
            </div>
            {catalogCreationFlow === "guided" && <Notice tone="info">Vous créez ici un <strong>modèle réutilisable</strong>, pas le devis d’un client. Une fois publié, ce sujet sera proposé dans « Créer un devis → Devis guidé ».</Notice>}
            <div className="form-grid">
              <input type="hidden" value={catalogModelEditor.item_code} readOnly />
              <label>{catalogCreationFlow === "guided" ? "Nom du sujet guidé" : "Nom du modèle"}<input value={catalogModelEditor.name} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, name: event.target.value } : current)} placeholder="Ex. Nettoyage fin de chantier" required /></label>
              <label>Nature<select value={catalogModelEditor.item_kind} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, item_kind: event.target.value as ServiceCatalogItem["item_kind"] } : current)}><option value="service">Prestation</option><option value="product">Produit</option><option value="rental">Location</option><option value="other">Autre</option></select></label>
              <label>Mode de facturation<select value={catalogModelEditor.default_billing_mode} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, default_billing_mode: event.target.value } : current)}><option value="fixed">Forfait</option><option value="hourly">Horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Mensuel</option></select></label>
              <label>Unité<input value={catalogModelEditor.default_unit_label} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, default_unit_label: event.target.value } : current)} required /></label>
              <label>Prix conseillé HT<input type="number" min="0" step="0.01" value={catalogModelEditor.default_unit_price_ex_vat} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, default_unit_price_ex_vat: Number(event.target.value) } : current)} required /></label>
              <label>TVA (%)<input type="number" min="0" max="100" step="0.01" value={catalogModelEditor.default_vat_rate} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, default_vat_rate: Number(event.target.value) } : current)} required /></label>
              <label>Durée indicative (h)<input type="number" min="0" step="0.25" value={catalogModelEditor.default_duration_hours ?? ""} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, default_duration_hours: event.target.value ? Number(event.target.value) : null } : current)} /></label>
              <label>Marge cible (%)<input type="number" min="0" max="99.99" step="0.01" value={catalogModelEditor.target_margin_rate ?? ""} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, target_margin_rate: event.target.value ? Number(event.target.value) : null } : current)} /></label>
              <label className="wide">
                Descriptif commercial type
                <textarea
                  value={catalogModelEditor.description}
                  onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, description: event.target.value } : current)}
                  placeholder="Ex. Nettoyage complet du logement, cuisine, sanitaires, sols et surfaces accessibles."
                />
                <small>Ce texte préremplit les futurs devis de cette prestation. Il reste modifiable avant chaque brouillon et les devis déjà créés conservent leur propre version.</small>
              </label>
              {catalogModelEditor.id && <label className="wide">Motif de la nouvelle version<input value={catalogModelEditor.change_reason} onChange={(event) => setCatalogModelEditor((current) => current ? { ...current, change_reason: event.target.value } : current)} required /></label>}
            </div>
            {!catalogModelEditor.id && catalogCreationFlow === "guided" && <Notice>Étape 1 sur 2 : renseignez seulement la base commerciale. Après l’enregistrement, ARD Manager ouvre automatiquement les questions, les réponses et leurs conséquences métier.</Notice>}
            {catalogModelEditor.id && (
              <section className="modal-subsection">
                <h3>Historique immuable</h3>
                {(data.serviceCatalogVersions || []).filter((version) => version.catalog_item_id === catalogModelEditor.id).sort((a, b) => b.version - a.version).map((version) => (
                  <p key={version.id}><strong>Version {version.version}</strong> · {frDate(version.created_at, true)} · {version.change_reason}</p>
                ))}
              </section>
            )}
            <Notice>Une modification crée une version. Elle ne modifie jamais les tarifs déjà appliqués à un client ou à un bien.</Notice>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="row-actions"><button type="button" className="secondary" onClick={() => { setCatalogModelEditor(null); setCatalogCreationTarget(null); }}>Annuler</button><button disabled={busy}>{busy ? "Enregistrement…" : catalogCreationTarget ? "Créer et utiliser cette prestation" : !catalogModelEditor.id && catalogCreationFlow === "guided" ? "Continuer vers les questions" : "Enregistrer cette version"}</button></div>
          </form>
        </div>
      )}
      {catalogApplyEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Créer un tarif depuis le modèle">
          <form className="portal-detail create-modal" onSubmit={createTariffFromCatalog}>
            <div className="portal-detail-head"><div><span className="eyebrow">Application contrôlée</span><h2>Créer un tarif depuis le modèle</h2></div><button type="button" className="icon-close" onClick={() => setCatalogApplyEditor(null)} aria-label="Fermer">×</button></div>
            <div className="form-grid">
              <label>Client<select value={catalogApplyEditor.client_id} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, client_id: event.target.value, property_id: "" } : current)} required><option value="">Choisir</option>{data.clients.filter((client) => client.active !== false).map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
              <label>Bien facultatif<select value={catalogApplyEditor.property_id} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, property_id: event.target.value } : current)}><option value="">Tarif général du client</option>{data.properties.filter((property) => property.client_id === catalogApplyEditor.client_id && property.active).map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}</select></label>
              <label>Nom appliqué<input value={catalogApplyEditor.name} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, name: event.target.value } : current)} required /></label>
              <label>Facturation<select value={catalogApplyEditor.billing_mode} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, billing_mode: event.target.value } : current)}><option value="fixed">Forfait</option><option value="hourly">Horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Mensuel</option></select></label>
              <label>Tarif HT<input type="number" min="0" step="0.01" value={catalogApplyEditor.unit_price_ex_vat} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, unit_price_ex_vat: Number(event.target.value) } : current)} required /></label>
              <label>TVA (%)<input type="number" min="0" max="100" step="0.01" value={catalogApplyEditor.vat_rate} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, vat_rate: Number(event.target.value) } : current)} required /></label>
              <label>Durée interne (h)<input type="number" min="0" step="0.25" value={catalogApplyEditor.default_duration_hours ?? ""} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, default_duration_hours: event.target.value ? Number(event.target.value) : null } : current)} /></label>
              <label className="wide">Notes propres au bien<textarea value={catalogApplyEditor.notes} disabled={!catalogApplyEditor.property_id} onChange={(event) => setCatalogApplyEditor((current) => current ? { ...current, notes: event.target.value } : current)} /></label>
            </div>
            <Notice>Ce tarif devient indépendant du modèle central dès sa création afin de protéger les devis, missions et factures futurs du client.</Notice>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="row-actions"><button type="button" className="secondary" onClick={() => setCatalogApplyEditor(null)}>Annuler</button><button disabled={busy}>{busy ? "Création…" : "Créer ce tarif"}</button></div>
          </form>
        </div>
      )}
      {catalogEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <form
            className="portal-detail create-modal"
            onSubmit={saveCatalogService}
          >
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Catalogue tarifaire</span>
                <h2>
                  {catalogEditor.id
                    ? "Modifier la prestation"
                    : "Nouvelle prestation"}
                </h2>
              </div>
              <button
                type="button"
                className="icon-close"
                onClick={() => {
                  setCatalogEditor(null);
                  setCatalogPricingAnalysis(null);
                }}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            {catalogPricingAnalysis && (
              <section
                className="pricing-decision-panel"
                data-testid="pricing-decision-panel"
              >
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Pilotage par les coûts observés</span>
                    <h3>Trois propositions pour les futurs devis</h3>
                  </div>
                  <span className="status">
                    {catalogPricingAnalysis.missionCount} mission
                    {catalogPricingAnalysis.missionCount > 1 ? "s" : ""} analysée
                    {catalogPricingAnalysis.missionCount > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="pricing-analysis-summary">
                  <span>
                    Tarif actuel <strong>{money(catalogPricingAnalysis.currentUnitPrice)}</strong>
                  </span>
                  <span>
                    CA moyen / mission <strong>{money(catalogPricingAnalysis.averageRevenue)}</strong>
                  </span>
                  <span>
                    Coût connu moyen <strong>{money(catalogPricingAnalysis.averageKnownCost)}</strong>
                  </span>
                  <span>
                    Marge observée <strong>{catalogPricingAnalysis.currentMarginRate.toFixed(1)} %</strong>
                  </span>
                </div>
                {catalogPricingAnalysis.missingCost && (
                  <Notice tone="error">
                    Des coûts sont incomplets. Les simulations restent visibles
                    pour l’analyse, mais leur application est bloquée tant que la
                    base de coût n’est pas fiabilisée.
                  </Notice>
                )}
                <div className="pricing-proposal-grid">
                  {[
                    {
                      key: "minimum",
                      title: "Minimum protégé",
                      rate: catalogPricingAnalysis.minimumMarginRate,
                      price: catalogPricingAnalysis.minimumUnitPrice,
                      detail: "Atteint le seuil de marge le plus exigeant déjà paramétré.",
                    },
                    {
                      key: "recommended",
                      title: "Conseillé",
                      rate: catalogPricingAnalysis.recommendedMarginRate,
                      price: catalogPricingAnalysis.recommendedUnitPrice,
                      detail: "Ajoute une première marge de sécurité aux coûts observés.",
                    },
                    {
                      key: "comfortable",
                      title: "Confortable",
                      rate: catalogPricingAnalysis.comfortableMarginRate,
                      price: catalogPricingAnalysis.comfortableUnitPrice,
                      detail: "Protège davantage les variations et les imprévus futurs.",
                    },
                  ].map((proposal) => (
                    <article key={proposal.key} data-pricing-proposal={proposal.key}>
                      <span>{proposal.title}</span>
                      <strong>{money(proposal.price)}</strong>
                      <small>Marge visée {proposal.rate.toFixed(1)} %</small>
                      <p>{proposal.detail}</p>
                      <button
                        type="button"
                        className={proposal.key === "recommended" ? "" : "secondary"}
                        disabled={catalogPricingAnalysis.missingCost}
                        onClick={() =>
                          setCatalogEditor((current) =>
                            current
                              ? { ...current, unit_price_ex_vat: proposal.price }
                              : current,
                          )
                        }
                      >
                        Retenir {proposal.title.toLocaleLowerCase("fr")}
                      </button>
                    </article>
                  ))}
                </div>
                <small className="block">
                  Simulation fondée sur les coûts directement connus et le
                  rendement tarifaire moyen observé. Le montant retenu reste
                  modifiable avant enregistrement et ne s’applique qu’aux
                  opérations futures.
                </small>
              </section>
            )}
            <div className="form-grid">
              <label>
                Portée du tarif
                <select
                  value={catalogEditor.scope}
                  disabled={!!catalogEditor.id}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? {
                            ...current,
                            scope: event.target.value as "client" | "property",
                            property_id: "",
                          }
                        : current,
                    )
                  }
                >
                  <option value="client">Tous les biens du client</option>
                  <option value="property">Un bien précis</option>
                </select>
              </label>
              <label>
                Client
                <select
                  value={catalogEditor.client_id}
                  disabled={!!catalogEditor.id}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? {
                            ...current,
                            client_id: event.target.value,
                            property_id: "",
                          }
                        : current,
                    )
                  }
                  required
                >
                  <option value="">Choisir</option>
                  {data.clients
                    .filter((client) => client.active !== false)
                    .map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                </select>
              </label>
              {catalogEditor.scope === "property" && (
                <label>
                  Bien concerné
                  <select
                    value={catalogEditor.property_id}
                    disabled={!!catalogEditor.id}
                    onChange={(event) =>
                      setCatalogEditor((current) =>
                        current
                          ? { ...current, property_id: event.target.value }
                          : current,
                      )
                    }
                    required
                  >
                    <option value="">Choisir le bien</option>
                    {data.properties
                      .filter(
                        (property) =>
                          property.client_id === catalogEditor.client_id &&
                          property.active,
                      )
                      .map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <label>
                Nom de la prestation
                <input
                  value={catalogEditor.name}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current ? { ...current, name: event.target.value } : current,
                    )
                  }
                  required
                />
              </label>
              <label>
                Mode de facturation
                <select
                  value={catalogEditor.billing_mode}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? { ...current, billing_mode: event.target.value }
                        : current,
                    )
                  }
                >
                  <option value="fixed">Forfait</option>
                  <option value="hourly">Horaire</option>
                  <option value="per_unit">À l’unité</option>
                  <option value="monthly">Mensuel</option>
                </select>
              </label>
              <label>
                Prix unitaire HT
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={catalogEditor.unit_price_ex_vat}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? {
                            ...current,
                            unit_price_ex_vat: Number(event.target.value),
                          }
                        : current,
                    )
                  }
                  required
                />
              </label>
              <label>
                TVA (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={catalogEditor.vat_rate}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? { ...current, vat_rate: Number(event.target.value) }
                        : current,
                    )
                  }
                  required
                />
              </label>
              <label>
                Durée interne indicative (h)
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={catalogEditor.default_duration_hours ?? ""}
                  onChange={(event) =>
                    setCatalogEditor((current) =>
                      current
                        ? {
                            ...current,
                            default_duration_hours: event.target.value
                              ? Number(event.target.value)
                              : null,
                          }
                        : current,
                    )
                  }
                />
              </label>
              {catalogEditor.scope === "property" && (
                <label className="wide">
                  Notes propres à ce bien
                  <textarea
                    value={catalogEditor.notes}
                    onChange={(event) =>
                      setCatalogEditor((current) =>
                        current
                          ? { ...current, notes: event.target.value }
                          : current,
                      )
                    }
                    placeholder="Accès, contraintes ou particularités utiles pour les futurs devis et missions."
                  />
                </label>
              )}
            </div>
            <Notice>
              Le tarif est modifiable pour les opérations futures. Les lignes
              déjà figées dans un devis, une facture ou une mission ne sont
              jamais réécrites.
            </Notice>
            <div className="row-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setCatalogEditor(null);
                  setCatalogPricingAnalysis(null);
                }}
              >
                Annuler
              </button>
              <button disabled={busy}>
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}
      {selectedPaymentCandidate && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail commercial-document-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Contrôle du règlement</span>
                <h2>{money(selectedPaymentCandidate.amount)}</h2>
                <p>
                  {selectedPaymentCandidate.provider} · référence {selectedPaymentCandidate.external_payment_id}
                </p>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedPaymentCandidate(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <Notice>
              Proposition {labels[selectedPaymentCandidate.match_status] || selectedPaymentCandidate.match_status}.
              Vérifiez la ou les factures et le total avant de confirmer.
            </Notice>
            <div className="form-grid">
              {paymentCandidateAllocations.map((allocation, index) => (
                <div className="row-actions wide" key={`${index}-${allocation.invoice_id}`}>
                  <label>
                    Facture
                    <select
                      value={allocation.invoice_id}
                      onChange={(event) =>
                        setPaymentCandidateAllocations((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, invoice_id: event.target.value }
                              : item,
                          ),
                        )
                      }
                    >
                      <option value="">Sélectionner…</option>
                      {data.invoices
                        .filter((invoice) => !["cancelled", "credited"].includes(invoice.status))
                        .map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            {invoice.number || "Brouillon"} · reste {money(
                              Math.max(0, invoice.total_inc_vat - (invoice.amount_paid || 0)),
                            )}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Montant affecté
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={allocation.amount}
                      onChange={(event) =>
                        setPaymentCandidateAllocations((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, amount: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                  </label>
                  {paymentCandidateAllocations.length > 1 && (
                    <button
                      type="button"
                      className="ghost compact"
                      onClick={() =>
                        setPaymentCandidateAllocations((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    >
                      Retirer
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="secondary"
                onClick={() =>
                  setPaymentCandidateAllocations((current) => [
                    ...current,
                    { invoice_id: "", amount: "" },
                  ])
                }
              >
                Ajouter une facture
              </button>
              <label>
                Mode de règlement
                <select
                  value={paymentCandidateMethod}
                  onChange={(event) => setPaymentCandidateMethod(event.target.value)}
                >
                  <option value="transfer">Virement</option>
                  <option value="direct_debit">Prélèvement</option>
                  <option value="card">Carte</option>
                  <option value="cash">Espèces</option>
                  <option value="cheque">Chèque</option>
                  <option value="other">Autre</option>
                </select>
              </label>
              <label>
                Référence interne
                <input
                  value={paymentCandidateReference}
                  onChange={(event) => setPaymentCandidateReference(event.target.value)}
                />
              </label>
              <label className="wide">
                Note de contrôle ou motif si ignoré
                <textarea
                  value={paymentCandidateNote}
                  onChange={(event) => setPaymentCandidateNote(event.target.value)}
                />
              </label>
              <div className="row-actions wide">
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={ignorePaymentCandidate}
                >
                  Ignorer avec motif
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={confirmPaymentCandidate}
                >
                  Confirmer la répartition
                </button>
              </div>
            </div>
            {message && <Notice tone="info">{message}</Notice>}
          </section>
        </div>
      )}
      {selection && selectedDocument && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail commercial-document-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">
                  {selection.kind === "quote" ? "Devis" : "Facture"}
                </span>
                <h2>{selectedDocument.number || "Brouillon sans numéro"}</h2>
                <p>
                  {data.clients.find(
                    (client) => client.id === selectedDocument.client_id,
                  )?.name || "Client introuvable"}
                </p>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelection(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="detail-grid">
              <div>
                <span>Émission</span>
                <strong>
                  {selectedQuote
                    ? frDate(selectedQuote.issued_on)
                    : selectedInvoice?.issue_on
                      ? frDate(selectedInvoice.issue_on)
                      : "Non émise"}
                </strong>
              </div>
              <div>
                <span>
                  {selectedQuote ? "Validité" : "Échéance de paiement"}
                </span>
                <strong>
                  {selectedQuote?.valid_until
                    ? frDate(selectedQuote.valid_until)
                    : selectedInvoice?.due_on
                      ? frDate(selectedInvoice.due_on)
                      : "Non renseignée"}
                </strong>
              </div>
              <div>
                <span>Total HT</span>
                <strong>{money(selectedDocument.total_ex_vat)}</strong>
              </div>
              <div>
                <span>TVA</span>
                <strong>{money(selectedDocument.total_vat)}</strong>
              </div>
              <div>
                <span>Total TTC</span>
                <strong>{money(selectedDocument.total_inc_vat)}</strong>
              </div>
              <div>
                <span>État actuel</span>
                <Status value={selectedDocument.status} />
              </div>
              <div>
                <span>Conditions de paiement</span>
                <strong>
                  {paymentTermsLabel(paymentTermsMode, paymentTermsDays)}
                </strong>
              </div>
              {selectedInvoice && (
                <>
                  <div>
                    <span>Déjà encaissé</span>
                    <strong>{money(selectedInvoice.amount_paid || 0)}</strong>
                  </div>
                  <div>
                    <span>Reste à encaisser</span>
                    <strong>
                      {money(
                        Math.max(
                          0,
                          selectedInvoice.total_inc_vat -
                            (selectedInvoice.amount_paid || 0),
                        ),
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Dernier règlement confirmé</span>
                    <strong>
                      {selectedInvoice.last_payment_on
                        ? frDate(selectedInvoice.last_payment_on)
                        : "Aucun règlement confirmé"}
                    </strong>
                  </div>
                </>
              )}
            </div>
            {selectedQuote &&
              ((selectedQuote.discount_rate || 0) > 0 ||
                (selectedQuote.deposit_rate || 0) > 0) && (
                <div className="detail-grid commercial-terms">
                  <div>
                    <span>Remise</span>
                    <strong>{selectedQuote.discount_rate || 0} %</strong>
                  </div>
                  <div>
                    <span>Acompte demandé</span>
                    <strong>{selectedQuote.deposit_rate || 0} %</strong>
                  </div>
                </div>
              )}
            {selectedQuote && selectedSourcingWorkflow && (
              <section className="detail-note quote-sourcing-workflow">
                <div className="section-heading-row">
                  <div>
                    <span className="eyebrow">Circuit facultatif ST → ARD → client</span>
                    <h3>Chiffrage du sous-traitant</h3>
                  </div>
                  <Status value={selectedSourcingWorkflow.status} />
                </div>
                <div className="detail-grid">
                  <div>
                    <span>Sous-traitant sollicité</span>
                    <strong>
                      {(() => {
                        const worker = data.workers.find(
                          (item) => item.id === selectedSourcingWorkflow.worker_id,
                        );
                        return worker ? workerName(worker) : "Intervenant introuvable";
                      })()}
                    </strong>
                  </div>
                  <div>
                    <span>Prix client envisagé</span>
                    <strong>
                      {selectedSourcingWorkflow.client_target_price_ex_vat == null
                        ? "À déterminer après l’offre ST"
                        : money(selectedSourcingWorkflow.client_target_price_ex_vat)}
                    </strong>
                  </div>
                  <div>
                    <span>Budget ST maximum</span>
                    <strong>
                      {selectedSourcingWorkflow.max_supplier_budget_ex_vat == null
                        ? "Aucun budget imposé"
                        : money(selectedSourcingWorkflow.max_supplier_budget_ex_vat)}
                    </strong>
                  </div>
                  <div>
                    <span>Objectif figé</span>
                    <strong>{selectedSourcingWorkflow.target_rate_snapshot} %</strong>
                    <small>
                      {selectedSourcingWorkflow.target_mode_snapshot === "markup"
                        ? "majoration sur coût"
                        : "marge sur vente"}
                    </small>
                  </div>
                  {latestSourcingOffer && (
                    <div>
                      <span>Dernière offre ST</span>
                      <strong>{money(latestSourcingOffer.supplier_total_ex_vat)}</strong>
                      <small>Version {latestSourcingOffer.version}</small>
                    </div>
                  )}
                  {selectedSourcingWorkflow.suggested_client_price_ex_vat != null && (
                    <div>
                      <span>Prix client minimal conseillé</span>
                      <strong>{money(selectedSourcingWorkflow.suggested_client_price_ex_vat)}</strong>
                    </div>
                  )}
                </div>
                {latestSourcingOffer?.status === "submitted" && (
                  <div className="form-grid quote-sourcing-decision">
                    <label className="wide">
                      Note de décision / message au ST
                      <textarea
                        value={sourcingDecisionNotes}
                        onChange={(event) => setSourcingDecisionNotes(event.target.value)}
                        placeholder={
                          selectedSourcingWorkflow.max_supplier_budget_ex_vat == null
                            ? "Précision interne ou motif de refus"
                            : `Budget maximum ${money(selectedSourcingWorkflow.max_supplier_budget_ex_vat)} HT possible, acceptez-vous ?`
                        }
                      />
                    </label>
                    {selectedSourcingWorkflow.max_supplier_budget_ex_vat != null &&
                      latestSourcingOffer.supplier_total_ex_vat >
                        selectedSourcingWorkflow.max_supplier_budget_ex_vat && (
                        <Notice tone="error">
                          L’offre dépasse le budget compatible avec le prix client actuel. Vous pouvez proposer le budget maximum au ST, refuser, ou retenir son coût puis augmenter le prix client avant toute présentation au client.
                        </Notice>
                      )}
                    <div className="row-actions wide">
                      <button
                        type="button"
                        className="success"
                        disabled={
                          busy ||
                          (selectedSourcingWorkflow.max_supplier_budget_ex_vat != null &&
                            latestSourcingOffer.supplier_total_ex_vat >
                              selectedSourcingWorkflow.max_supplier_budget_ex_vat)
                        }
                        onClick={() => reviewSourcingOffer("accepted")}
                      >
                        Retenir le chiffrage ST
                      </button>
                      {selectedSourcingWorkflow.max_supplier_budget_ex_vat != null &&
                        latestSourcingOffer.supplier_total_ex_vat >
                          selectedSourcingWorkflow.max_supplier_budget_ex_vat && (
                          <button
                            type="button"
                            className="secondary"
                            disabled={busy}
                            onClick={() => reviewSourcingOffer("accepted_with_repricing")}
                          >
                            Retenir le coût ST et recalculer le prix client
                          </button>
                        )}
                      {selectedSourcingWorkflow.max_supplier_budget_ex_vat != null && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => reviewSourcingOffer("countered")}
                        >
                          Proposer le budget maximum au ST
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => reviewSourcingOffer("rejected")}
                      >
                        Refuser
                      </button>
                    </div>
                  </div>
                )}
                {selectedSourcingWorkflow.status === "countered" && (
                  <Notice>
                    Une nouvelle version du ST est attendue. Le prix client et la marge restent invisibles dans son espace.
                  </Notice>
                )}
                {selectedSourcingWorkflow.status === "admin_review" && (
                  <div className="form-grid quote-sourcing-final-approval">
                    <label>
                      Prix client final HT
                      <input
                        type="number"
                        min={selectedSourcingWorkflow.suggested_client_price_ex_vat || 0}
                        step="0.01"
                        value={sourcingFinalPrice}
                        onChange={(event) => setSourcingFinalPrice(event.target.value)}
                      />
                    </label>
                    <div className="wide">
                      <Notice>
                        Même si le calcul est favorable, le devis ne part pas automatiquement : votre validation manuelle reste obligatoire.
                      </Notice>
                    </div>
                    <button
                      type="button"
                      disabled={busy || !sourcingFinalPrice}
                      onClick={approveSourcingForClient}
                    >
                      Valider le prix client et débloquer Henrri
                    </button>
                  </div>
                )}
                {selectedSourcingWorkflow.status === "approved_for_client" && (
                  <Notice tone="success">
                    Chiffrage ST et prix client validés par ARD. Le contrôle Henrri peut maintenant être lancé.
                  </Notice>
                )}
              </section>
            )}
            <section className="detail-note henrri-document-export">
              <div className="section-heading-row">
                <div>
                  <span className="eyebrow">Contrôle avant transmission</span>
                  <h3>Préparation Henrri</h3>
                </div>
                <Status
                  value={
                    henrriExport?.external_effect === "sandbox_draft_created" ||
                    henrriExport?.already_linked
                      ? "linked"
                      : "draft"
                  }
                />
              </div>
              <p>
                ARD Manager vérifie d’abord le client, ses coordonnées et les
                lignes. Le premier bouton n’envoie rien. En Sandbox seulement,
                le second crée un brouillon à relire dans Henrri, sans le
                finaliser et sans l’adresser au client.
              </p>
              <div className="row-actions wide">
                <button
                  type="button"
                  className="secondary"
                  disabled={Boolean(henrriBusy)}
                  onClick={() => prepareHenrriDocument("preview")}
                >
                  {henrriBusy === "preview"
                    ? "Contrôle…"
                    : "Contrôler avant export"}
                </button>
                {henrriExport &&
                  !henrriExport.already_linked &&
                  henrriExport.external_effect === "none" && (
                    <button
                      type="button"
                      disabled={
                        Boolean(henrriBusy) ||
                        Boolean(
                          selectedSourcingWorkflow &&
                            selectedSourcingWorkflow.status !== "approved_for_client",
                        )
                      }
                      onClick={() => prepareHenrriDocument("create_draft")}
                    >
                      {henrriBusy === "create_draft"
                        ? "Création…"
                        : "Créer le brouillon Henrri Sandbox"}
                    </button>
                  )}
              </div>
              {henrriExport && (
                <div className="detail-grid henrri-export-summary">
                  <div>
                    <span>Client contrôlé</span>
                    <strong>{henrriExport.client.name}</strong>
                    <small>{henrriExport.client.email}</small>
                  </div>
                  <div>
                    <span>Lignes</span>
                    <strong>{henrriExport.lines.length}</strong>
                    <small>Total contrôlé : {money(henrriExport.total_ex_vat)} HT</small>
                  </div>
                  <div>
                    <span>Environnement</span>
                    <strong>Sandbox</strong>
                    <small>Aucune production autorisée</small>
                  </div>
                  <div>
                    <span>Brouillon Henrri</span>
                    <strong>
                      {henrriExport.external_document?.identity ||
                        (henrriExport.external_document?.id
                          ? `ID ${henrriExport.external_document.id}`
                          : "Pas encore créé")}
                    </strong>
                    <small>
                      {henrriExport.external_effect === "sandbox_draft_created"
                        ? "Créé, non finalisé et non envoyé"
                        : henrriExport.already_linked
                          ? "Déjà lié : doublon bloqué"
                          : "Aucun effet externe"}
                    </small>
                  </div>
                </div>
              )}
              {selectedExternalLink && (
                <div className="detail-grid henrri-export-summary">
                  <div>
                    <span>Lien fournisseur enregistré</span>
                    <strong>
                      {String(selectedExternalMetadata.identity || "") ||
                        `ID ${selectedExternalLink.external_id}`}
                    </strong>
                    <small>{billingProvider?.label || selectedExternalLink.provider}</small>
                  </div>
                  <div>
                    <span>État externe connu</span>
                    <strong>{externalStateLabel}</strong>
                    <small>
                      Dernière synchronisation : {frDate(selectedExternalLink.last_synced_at)}
                    </small>
                  </div>
                </div>
              )}
              {selectedQuote && selectedExternalLink && (
                <div className="detail-note henrri-quote-lifecycle">
                  <span className="eyebrow">Après acceptation du devis</span>
                  <h4>Devis Henrri → facture brouillon</h4>
                  <p>
                    La finalisation verrouille définitivement le devis dans la
                    Sandbox. La transformation crée ensuite une facture encore
                    modifiable : elle n’est ni finalisée, ni envoyée au client.
                  </p>
                  {!hasDeploymentCapability(data, "henrri_quote_conversion") ? (
                    <Notice>
                      Cette base n’a pas encore reçu le module de transformation.
                      Aucun bouton irréversible n’est proposé.
                    </Notice>
                  ) : (
                    <div className="row-actions wide">
                      <button
                        type="button"
                        className="secondary"
                        disabled={Boolean(henrriLifecycleBusy)}
                        onClick={() => manageHenrriQuote("preview")}
                      >
                        {henrriLifecycleBusy === "preview"
                          ? "Lecture…"
                          : "Vérifier l’état Henrri"}
                      </button>
                      {(henrriLifecycle?.source_state || externalState) === "draft_ready" &&
                        selectedQuote.status === "accepted" && (
                          <button
                            type="button"
                            disabled={Boolean(henrriLifecycleBusy)}
                            onClick={() => manageHenrriQuote("finalize_quote")}
                          >
                            {henrriLifecycleBusy === "finalize_quote"
                              ? "Finalisation…"
                              : "Finaliser le devis Sandbox"}
                          </button>
                        )}
                      {(henrriLifecycle?.source_state || externalState) === "finalized" &&
                        selectedQuote.status === "accepted" &&
                        selectedTransformation?.status !== "needs_review" &&
                        selectedTransformation?.status !== "succeeded" && (
                          <button
                            type="button"
                            disabled={Boolean(henrriLifecycleBusy)}
                            onClick={() => manageHenrriQuote("transform_to_invoice")}
                          >
                            {henrriLifecycleBusy === "transform_to_invoice"
                              ? "Transformation…"
                              : "Créer la facture brouillon"}
                          </button>
                        )}
                    </div>
                  )}
                  {selectedQuote.status !== "accepted" && (
                    <Notice>
                      Le devis ARD doit d’abord être accepté. Aucune finalisation
                      ou transformation fournisseur n’est possible avant cela.
                    </Notice>
                  )}
                  {selectedTransformation?.status === "succeeded" && (
                    <Notice tone="success">
                      Facture brouillon créée et liée. Aucun doublon ne sera créé.
                    </Notice>
                  )}
                  {selectedTransformation?.status === "needs_review" && (
                    <Notice tone="error">
                      Résultat externe incertain : contrôlez Henrri manuellement.
                      ARD Manager bloque toute nouvelle tentative automatique.
                      {selectedTransformation.last_error
                        ? ` Détail : ${selectedTransformation.last_error}`
                        : ""}
                    </Notice>
                  )}
                </div>
              )}
            </section>
            {selectedQuote && (data.quoteCostingRuns || []).filter(run => run.quote_id === selectedQuote.id && run.notes?.includes('"kind":"chantier"')).map(run => {
              const line = lines.find(item => item.id === run.quote_line_id);
              return <ChantierSavedSummary key={run.id} notes={run.notes} currentPrice={line ? Number(line.quantity)*Number(line.unit_price_ex_vat)*(1-Number(selectedQuote.discount_rate||0)/100) : undefined} />;
            })}
            <section className="document-lines">
              <h3>Lignes du document</h3>
              {lines.length ? (
                <div className="table-wrap document-lines-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qté</th>
                        <th>Prix HT</th>
                        <th>TVA</th>
                        <th>Total TTC</th>
                        {canEditLines && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => {
                        const totalExVat =
                          Number(line.quantity) *
                          Number(line.unit_price_ex_vat),
                          billingSnapshot = (data.commercialLineBillingSnapshots || []).find(
                            (snapshot) => selection?.kind === "quote"
                              ? snapshot.quote_line_id === line.id
                              : snapshot.invoice_line_id === line.id,
                          );
                        return (
                          <tr key={line.id}>
                            <td>
                              <strong>{line.description}</strong>
                              {billingSnapshot && <small className={billingSnapshot.profitability_alert ? "data-warning" : ""}>
                                {labels[billingSnapshot.billing_treatment]}
                                {billingSnapshot.supplier_total_cost_ex_vat != null ? ` · coût ${money(billingSnapshot.supplier_total_cost_ex_vat)} HT` : ""}
                                {billingSnapshot.minimum_client_price_ex_vat != null ? ` · seuil ${money(billingSnapshot.minimum_client_price_ex_vat)} HT` : ""}
                              </small>}
                              {"mission_id" in line && line.mission_id && (() => {
                                const mission = data.missions.find(
                                  (item) => item.id === line.mission_id,
                                );
                                return mission ? (
                                  <small className="document-line-mission">
                                    Intervention du {frDate(mission.starts_at)} · {mission.location}
                                  </small>
                                ) : null;
                              })()}
                            </td>
                            <td>{line.quantity}</td>
                            <td>{money(line.unit_price_ex_vat)}</td>
                            <td>{line.vat_rate} %</td>
                            <td>
                              {money(totalExVat * (1 + line.vat_rate / 100))}
                            </td>
                            {canEditLines && (
                              <td>
                                <div className="row-actions">
                                  <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() =>
                                      setLineEditor({
                                        id: line.id,
                                        description: line.description,
                                        quantity: Number(line.quantity),
                                        unit_price_ex_vat: Number(
                                          line.unit_price_ex_vat,
                                        ),
                                        vat_rate: Number(line.vat_rate),
                                        service_id: line.service_id || "",
                                      })
                                    }
                                  >
                                    Modifier
                                  </button>
                                  {selection?.kind === "quote" && hasDeploymentCapability(data, "guided_quote_costing") && (
                                    <button
                                      type="button"
                                      className="compact"
                                      onClick={() => openQuoteCosting(line as QuoteLine)}
                                    >
                                      Chiffrer
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="danger compact"
                                    disabled={busy}
                                    onClick={() => deleteCommercialLine(line)}
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty">Aucune ligne enregistrée.</p>
              )}
              {canEditLines ? (
                <form
                  className="form-grid commercial-line-form"
                  onSubmit={saveCommercialLine}
                >
                  <h4 className="wide">
                    {lineEditor.id ? "Modifier la ligne" : "Ajouter une ligne"}
                  </h4>
                  <label>
                    Prestation du client (facultatif)
                    <select
                      value={lineEditor.service_id}
                      onChange={(event) => {
                        const service = data.services.find(
                          (item) => item.id === event.target.value,
                        );
                        setLineEditor((value) => ({
                          ...value,
                          service_id: event.target.value,
                          description: service?.name || value.description,
                          unit_price_ex_vat:
                            service?.unit_price_ex_vat ??
                            value.unit_price_ex_vat,
                          vat_rate: service?.vat_rate ?? value.vat_rate,
                        }));
                      }}
                    >
                      <option value="">Ligne libre</option>
                      {data.services
                        .filter(
                          (service) =>
                            service.client_id === selectedDocument.client_id,
                        )
                        .map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} · {money(service.unit_price_ex_vat)} HT
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="wide">
                    Description
                    <input
                      value={lineEditor.description}
                      onChange={(event) =>
                        setLineEditor((value) => ({
                          ...value,
                          description: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    Quantité
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={lineEditor.quantity}
                      onChange={(event) =>
                        setLineEditor((value) => ({
                          ...value,
                          quantity: +event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    Prix unitaire HT (€)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lineEditor.unit_price_ex_vat}
                      onChange={(event) =>
                        setLineEditor((value) => ({
                          ...value,
                          unit_price_ex_vat: +event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    TVA de la ligne (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={lineEditor.vat_rate}
                      onChange={(event) =>
                        setLineEditor((value) => ({
                          ...value,
                          vat_rate: +event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <div className="row-actions wide">
                    {lineEditor.id && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={resetLineEditor}
                      >
                        Annuler la modification
                      </button>
                    )}
                    <button disabled={busy}>
                      {busy
                        ? "Recalcul…"
                        : lineEditor.id
                          ? "Enregistrer la ligne"
                          : "Ajouter et recalculer"}
                    </button>
                  </div>
                </form>
              ) : (
                <Notice>
                  Les lignes sont verrouillées après l’envoi du document afin
                  de conserver la pièce transmise. Créez un avoir ou une
                  nouvelle version pour la corriger.
                </Notice>
              )}
            </section>
            {selectedQuote?.notes && (
              <div className="detail-note">
                <strong>Notes du devis</strong>
                <p>{selectedQuote.notes}</p>
              </div>
            )}
            {selectedInvoice?.henrri_id &&
              hasDeploymentCapability(data, "henrri_documents") && (
              <div className="detail-actions">
                <button
                  type="button"
                  className="secondary"
                  disabled={!!henrriPdfBusy}
                  onClick={() => openHenrriPdf("pdf")}
                >
                  {henrriPdfBusy === "pdf" ? "Préparation du PDF…" : "Afficher le PDF Henrri"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={!!henrriPdfBusy}
                  onClick={() => openHenrriPdf("facturx")}
                >
                  {henrriPdfBusy === "facturx" ? "Préparation Factur-X…" : "Télécharger Factur-X"}
                </button>
              </div>
            )}
            {selectedInvoice?.henrri_id &&
              !hasDeploymentCapability(data, "henrri_documents") && (
                <Notice>
                  Le PDF Henrri reste consultable dans Henrri. Son accès privé
                  depuis ARD Manager sera proposé après activation du module
                  documentaire sécurisé sur cette base.
                </Notice>
              )}
            <div className="commercial-support-grid">
              <section className="detail-note">
                <h3>Pièces jointes</h3>
                {attachments.length ? (
                  <div className="attachment-list">
                    {attachments.map((attachment) => {
                      const document = data.documents.find(
                        (item) => item.id === attachment.document_id,
                      );
                      return (
                        <button
                          key={attachment.id}
                          className="secondary"
                          onClick={() => openAttachment(attachment.document_id)}
                        >
                          {attachment.label ||
                            document?.original_name ||
                            "Ouvrir la pièce"}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty">Aucune pièce jointe.</p>
                )}
                <form className="attachment-form" onSubmit={uploadAttachment}>
                  <label>
                    Libellé
                    <input
                      value={attachmentLabel}
                      onChange={(e) => setAttachmentLabel(e.target.value)}
                      placeholder="Contrat, bon signé, justificatif…"
                    />
                  </label>
                  <label>
                    Fichier PDF ou image
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      onChange={(e) =>
                        setAttachmentFile(e.target.files?.[0] || null)
                      }
                      required
                    />
                  </label>
                  <button disabled={busy || !attachmentFile}>
                    {busy ? "Ajout…" : "Ajouter la pièce"}
                  </button>
                </form>
              </section>
              <section className="detail-note">
                <h3>Journal</h3>
                {events.length ? (
                  <ol className="commercial-timeline">
                    {events.map((event) => (
                      <li key={event.id}>
                        <strong>
                          {commercialEventLabel(event)}
                        </strong>
                        <span>{frDate(event.created_at, true)}</span>
                        {event.note && <small>{event.note}</small>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="empty">Aucun événement enregistré.</p>
                )}
              </section>
            </div>
            {selectedInvoice && (
              <div className="commercial-support-grid billing-actions-grid">
                <section className="detail-note">
                  <h3>Règlements</h3>
                  {payments.length ? (
                    <div className="payment-list">
                      {payments.map((payment) => (
                        <div key={payment.id}>
                          <strong>{money(payment.amount)}</strong>
                          <span>
                            Reçu le {frDate(payment.received_on)} · {payment.method}
                          </span>
                          <small>
                            Validé dans ARD Manager le {frDate(payment.created_at, true)}
                            {payment.reference ? ` · Réf. ${payment.reference}` : ""}
                          </small>
                          <Status value={payment.status} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty">Aucun règlement rapproché.</p>
                  )}
                  <form className="attachment-form" onSubmit={recordPayment}>
                    <label>
                      Montant TTC reçu
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={paymentAmount || ""}
                        onChange={(e) => setPaymentAmount(+e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Date de réception
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Mode
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="transfer">Virement</option>
                        <option value="direct_debit">Prélèvement</option>
                        <option value="card">Carte</option>
                        <option value="cash">Espèces</option>
                        <option value="cheque">Chèque</option>
                        <option value="other">Autre</option>
                      </select>
                    </label>
                    <label>
                      Référence
                      <input
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                      />
                    </label>
                    <button disabled={busy || paymentAmount <= 0}>
                      Enregistrer le règlement interne
                    </button>
                  </form>
                  <small>
                    Henrri autorise la lecture des règlements mais pas leur
                    écriture. Cette saisie sert au rapprochement interne.
                  </small>
                </section>
                <section className="detail-note">
                  <h3>Relances</h3>
                  {reminders.length ? (
                    <div className="payment-list">
                      {reminders.map((reminder) => (
                        <div key={reminder.id}>
                          <strong>{reminder.subject || "Relance"}</strong>
                          <span>
                            {reminder.scheduled_on
                              ? frDate(reminder.scheduled_on)
                              : frDate(reminder.created_at)}
                          </span>
                          <Status value={reminder.status} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty">Aucune relance préparée.</p>
                  )}
                  <form className="attachment-form" onSubmit={prepareReminder}>
                    <label>
                      Date prévue
                      <input
                        type="date"
                        value={reminderDate}
                        onChange={(e) => setReminderDate(e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Objet
                      <input
                        value={reminderSubject}
                        onChange={(e) => setReminderSubject(e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Message préparé
                      <textarea
                        rows={3}
                        value={reminderBody}
                        onChange={(e) => setReminderBody(e.target.value)}
                      />
                    </label>
                    <button disabled={busy}>Préparer la relance</button>
                  </form>
                  <small>
                    La relance reste en brouillon tant que l’envoi e-mail ou
                    Henrri n’est pas connecté.
                  </small>
                </section>
              </div>
            )}
            {selectedInvoice && selectedInvoice.document_kind !== "credit" && (
              <section className="detail-note derived-documents">
                <h3>Acomptes, avancements et avoirs</h3>
                {derivedInvoices.length ? (
                  <div className="payment-list">
                    {derivedInvoices.map((invoice) => (
                      <div key={invoice.id}>
                        <strong>{invoice.number || "Brouillon"}</strong>
                        <span>
                          {invoice.document_kind === "credit"
                            ? "Avoir"
                            : invoice.document_kind === "deposit"
                              ? "Acompte " +
                                (invoice.progress_percent || "") +
                                " %"
                              : "Avancement " +
                                (invoice.progress_percent || "") +
                                " %"}{" "}
                          · {money(invoice.total_inc_vat)}
                        </span>
                        <Status value={invoice.status} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty">Aucun document dérivé.</p>
                )}
                <form
                  className="derived-document-form"
                  onSubmit={createDerivedInvoice}
                >
                  <label>
                    Type
                    <select
                      value={derivedKind}
                      onChange={(e) =>
                        setDerivedKind(
                          e.target.value as "deposit" | "progress" | "credit",
                        )
                      }
                    >
                      <option value="deposit">Facture d’acompte</option>
                      <option value="progress">Facture d’avancement</option>
                      <option value="credit">Avoir</option>
                    </select>
                  </label>
                  {derivedKind !== "credit" ? (
                    <label>
                      Pourcentage du montant source
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={derivedPercent}
                        onChange={(e) => setDerivedPercent(+e.target.value)}
                        required
                      />
                    </label>
                  ) : (
                    <label>
                      Motif de l’avoir
                      <input
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                        required
                      />
                    </label>
                  )}
                  <button disabled={busy}>
                    Créer le document en brouillon
                  </button>
                </form>
                <small>
                  Le document reste modifiable et ne reçoit une numérotation
                  Henrri officielle qu’à sa finalisation via l’API.
                </small>
              </section>
            )}
            <section className="detail-note">
              <h3>Conditions et délais</h3>
              <p>
                La condition est figée sur ce document. Une modification future
                de la fiche client ne changera pas ce devis ou cette facture.
              </p>
              <div className="form-grid">
                <label>
                  Condition de paiement
                  <select
                    value={paymentTermsMode}
                    disabled={!canEditLines}
                    onChange={(event) =>
                      setPaymentTermsMode(event.target.value as PaymentTermsMode)
                    }
                  >
                    <option value="immediate">Paiement comptant</option>
                    <option value="days_after_issue">Délai après émission</option>
                    <option value="end_of_month">Fin de mois</option>
                  </select>
                </label>
                {paymentTermsMode === "days_after_issue" && (
                  <label>
                    Délai (jours)
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={paymentTermsDays}
                      disabled={!canEditLines}
                      onChange={(event) => setPaymentTermsDays(+event.target.value)}
                    />
                  </label>
                )}
                {selectedQuote ? (
                  <label>
                    Devis valable jusqu’au
                    <input
                      type="date"
                      value={quoteValidUntil}
                      disabled={!canEditLines}
                      onChange={(event) => setQuoteValidUntil(event.target.value)}
                    />
                  </label>
                ) : (
                  <label>
                    Échéance exacte
                    <input
                      type="date"
                      value={documentDueOn}
                      disabled={!canEditLines}
                      onChange={(event) => setDocumentDueOn(event.target.value)}
                    />
                    {!documentDueOn && (
                      <small>
                        Calculée automatiquement à l’enregistrement selon la condition choisie.
                      </small>
                    )}
                  </label>
                )}
                {canEditLines && (
                  <button type="button" disabled={busy} onClick={savePaymentTerms}>
                    Enregistrer les conditions
                  </button>
                )}
              </div>
            </section>
            <div className="document-status-editor">
              <label>
                État dans ARD Manager
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {allowedStatusOptions.map((value) => (
                    <option key={value} value={value}>
                      {labels[value] || value}
                    </option>
                  ))}
                </select>
              </label>
              <button disabled={busy} onClick={saveStatus}>
                {busy ? "Enregistrement…" : "Enregistrer l’état"}
              </button>
            </div>
            <p className="section-intro">
              Le changement d’état est interne : l’envoi au client et la
              synchronisation Henrri seront activés après connexion de l’API.
            </p>
            {message && <Notice tone="info">{message}</Notice>}
          </section>
        </div>
      )}
      {collectionEditor && collectionInvoice && (
        <div className="modal-backdrop">
          <form
            className="modal-card portal-detail collection-case-dialog"
            onSubmit={saveCollectionCase}
            data-testid="collection-case-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Piloter la créance"
          >
            <button
              type="button"
              className="icon-close"
              aria-label="Fermer"
              onClick={() => setCollectionEditor(null)}
            >
              ×
            </button>
            <span className="eyebrow">Suivi de créance</span>
            <h2>{collectionInvoice.number || "Facture sans numéro"}</h2>
            <div className="collection-case-summary">
              <span>
                Client
                <strong>
                  {data.clients.find(
                    (client) => client.id === collectionInvoice.client_id,
                  )?.name || "Client"}
                </strong>
              </span>
              <span>
                Reste TTC
                <strong>{money(remainingForInvoice(collectionInvoice))}</strong>
              </span>
              <span>
                Échéance
                <strong>
                  {collectionInvoice.due_on
                    ? frDate(collectionInvoice.due_on)
                    : "Non renseignée"}
                </strong>
              </span>
            </div>
            <label>
              Situation actuelle
              <select
                value={collectionEditor.track}
                onChange={(event) =>
                  setCollectionEditor((value) =>
                    value
                      ? {
                          ...value,
                          track: event.target.value as NonNullable<
                            Invoice["collection_track"]
                          >,
                        }
                      : value,
                  )
                }
              >
                {Object.entries(collectionTrackLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Prochaine revue / action
              <input
                type="date"
                value={collectionEditor.next_review_on}
                onChange={(event) =>
                  setCollectionEditor((value) =>
                    value
                      ? { ...value, next_review_on: event.target.value }
                      : value,
                  )
                }
              />
            </label>
            <label>
              Note de suivi
              <textarea
                rows={4}
                placeholder="Dernier contact, engagement du client, référence de procédure ou échéancier…"
                value={collectionEditor.notes}
                onChange={(event) =>
                  setCollectionEditor((value) =>
                    value ? { ...value, notes: event.target.value } : value,
                  )
                }
              />
            </label>
            <section className="modal-subsection">
              <h3>Relances formalisées</h3>
              <p>
                Préparer un jalon crée un brouillon interne daté. Il ne prouve
                pas l’envoi et ne transmet rien au client.
              </p>
              <div className="collection-milestones dialog-milestones">
                {reminderMilestones.map((milestone) => {
                  const prepared = (data.invoiceReminders || []).some(
                      (reminder) =>
                        reminder.invoice_id === collectionInvoice.id &&
                        reminder.milestone_days_after_due === milestone,
                    ),
                    available = Boolean(
                      collectionInvoice.due_on &&
                        dayDifference(today, collectionInvoice.due_on) >=
                          milestone,
                    );
                  return (
                    <button
                      type="button"
                      key={milestone}
                      className={prepared ? "prepared" : "secondary"}
                      disabled={busy || prepared || !available}
                      onClick={() =>
                        prepareCollectionMilestone(collectionInvoice, milestone)
                      }
                    >
                      {prepared ? "✓ Préparée" : "Préparer"} J+{milestone}
                    </button>
                  );
                })}
              </div>
            </section>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="modal-actions">
              <button disabled={busy}>
                {busy ? "Enregistrement…" : "Enregistrer le suivi"}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setCollectionEditor(null)}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function AdminInventory({
  profile,
  data,
  reload,
  focusItemId,
  onFocusConsumed,
  onReturnToCatalog,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  focusItemId?: string | null;
  onFocusConsumed?: () => void;
  onReturnToCatalog?: () => void;
}) {
  const items = data.inventoryItems || [],
    movements = data.inventoryMovements || [],
    suppliers = data.inventorySuppliers || [],
    kitComponents = data.inventoryKitComponents || [],
    procurementRequirements = data.procurementRequirements || [],
    pendingProcurement = procurementRequirements.filter((item) => item.status === "to_review"),
    approvedProcurement = procurementRequirements.filter((item) => item.status === "approved"),
    activeItems = items.filter((item) => item.active),
    stockItems = activeItems.filter((item) => (item.item_kind || "stock") === "stock"),
    kits = activeItems.filter((item) => item.item_kind === "kit"),
    kitCapacity = (kitId: string) => {
      const parts = kitComponents.filter((part) => part.kit_item_id === kitId);
      return parts.length
        ? Math.max(
            0,
            Math.floor(
              Math.min(
                ...parts.map((part) => {
                  const item = items.find((candidate) => candidate.id === part.component_item_id);
                  return Number(item?.current_quantity || 0) / Number(part.quantity || 1);
                }),
              ),
            ),
          )
        : 0;
    },
    lowItems = activeItems.filter(
      (item) =>
        item.minimum_quantity > 0 &&
        item.current_quantity <= item.minimum_quantity,
    ),
    inventoryValue = stockItems.reduce(
      (sum, item) =>
        sum + Math.max(0, item.current_quantity) * item.average_unit_cost,
      0,
    ),
    blankItem: Partial<InventoryItem> = {
      name: "",
      category: "",
      sku: "",
      unit: "unité",
      minimum_quantity: 0,
      preferred_supplier: "",
      item_kind: "stock",
      supplier_id: null,
      supplier_product_url: "",
      sale_enabled: false,
      sale_price_ex_vat: 0,
      sale_vat_rate: data.organization?.default_vat_rate ?? 20,
      sale_label: "",
      purchase_package_quantity: null,
      purchase_package_unit: "L",
      purchase_package_price_ex_vat: null,
      yield_material_quantity: null,
      yield_material_unit: "L",
      yield_output_quantity: null,
      yield_output_unit: "m2",
      default_application_passes: 1,
      loss_rate_percent: 0,
      target_margin_rate: null,
      active: true,
    },
    [itemDraft, setItemDraft] = useState<Partial<InventoryItem>>(blankItem),
    [movementItem, setMovementItem] = useState(activeItems[0]?.id || ""),
    [movementType, setMovementType] = useState("usage"),
    [movementQuantity, setMovementQuantity] = useState(1),
    [movementCost, setMovementCost] = useState(0),
    [movementDate, setMovementDate] = useState(parisDay(new Date())),
    [movementNotes, setMovementNotes] = useState(""),
    [supplierDraft, setSupplierDraft] = useState<Partial<InventorySupplier>>({ name: "", contact_name: "", email: "", phone: "", website: "", address: "", notes: "", active: true }),
    [kitItemId, setKitItemId] = useState(kits[0]?.id || ""),
    [componentItemId, setComponentItemId] = useState(stockItems[0]?.id || ""),
    [componentQuantity, setComponentQuantity] = useState(1),
    [kitModalOpen, setKitModalOpen] = useState(false),
    [kitQuantityDrafts, setKitQuantityDrafts] = useState<Record<string, number>>({}),
    [estimateItemId, setEstimateItemId] = useState(""),
    [estimateTargetQuantity, setEstimateTargetQuantity] = useState(240),
    [estimateTargetUnit, setEstimateTargetUnit] = useState("m2"),
    [estimatePasses, setEstimatePasses] = useState(1),
    [activeInventoryWorkspace, setActiveInventoryWorkspace] = useState<
      "article" | "estimate" | "suppliers" | "history" | ""
    >(""),
    [procurementEditor, setProcurementEditor] = useState<ProcurementRequirement | null>(null),
    [procurementDraft, setProcurementDraft] = useState({
      supplier_id: "",
      needed_on: "",
      quantity: 1,
      unit_cost: 0,
      purchase_reference: "",
      purchase_url: "",
      review_note: "",
    }),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const inventoryEditorRef = useRef<HTMLElement>(null);
  const yieldItems = stockItems.filter(
      (item) =>
        item.purchase_package_quantity &&
        item.purchase_package_price_ex_vat != null &&
        item.yield_material_quantity &&
        item.yield_output_quantity &&
        item.yield_output_unit,
    ),
    estimateItem = yieldItems.find((item) => item.id === estimateItemId) || yieldItems[0],
    estimatePreview = calculateMaterialEstimate(
      estimateItem,
      estimateTargetQuantity,
      estimateTargetUnit,
      estimatePasses,
    );
  const movementLabels: Record<string, string> = {
    purchase: "Achat / entrée",
    usage: "Utilisation",
    adjustment_in: "Correction positive",
    adjustment_out: "Correction négative",
    return: "Retour en stock",
    waste: "Perte / casse",
  };
  useEffect(() => {
    if (!focusItemId) return;
    const item = items.find((candidate) => candidate.id === focusItemId);
    if (item) {
      setItemDraft({ ...item });
      setActiveInventoryWorkspace("article");
      window.requestAnimationFrame(() =>
        inventoryEditorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      );
    } else setMessage("Le produit demandé n’est plus disponible dans le stock actif.");
    onFocusConsumed?.();
  }, [focusItemId]);
  function toggleInventoryWorkspace(
    workspace: "article" | "estimate" | "suppliers" | "history",
    open: boolean,
  ) {
    setActiveInventoryWorkspace((current) =>
      open ? workspace : current === workspace ? "" : current,
    );
  }
  function editInventoryItem(item: InventoryItem) {
    setItemDraft({ ...item });
    setActiveInventoryWorkspace("article");
    window.requestAnimationFrame(() =>
      inventoryEditorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      }),
    );
  }
  async function saveItem(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = {
        organization_id: profile.organization_id,
        name: itemDraft.name?.trim(),
        category: itemDraft.category?.trim() || null,
        sku: itemDraft.sku?.trim() || null,
        unit: itemDraft.unit?.trim() || "unité",
        minimum_quantity: Number(itemDraft.minimum_quantity || 0),
        preferred_supplier: itemDraft.preferred_supplier?.trim() || null,
        item_kind: itemDraft.item_kind || "stock",
        supplier_id: itemDraft.supplier_id || null,
        supplier_product_url: itemDraft.supplier_product_url?.trim() || null,
        sale_enabled: !!itemDraft.sale_enabled,
        sale_price_ex_vat: itemDraft.sale_enabled ? Number(itemDraft.sale_price_ex_vat || 0) : null,
        sale_vat_rate: Number(itemDraft.sale_vat_rate ?? data.organization?.default_vat_rate ?? 20),
        sale_label: itemDraft.sale_label?.trim() || null,
        purchase_package_quantity:
          itemDraft.item_kind === "stock" && itemDraft.purchase_package_quantity
            ? Number(itemDraft.purchase_package_quantity)
            : null,
        purchase_package_unit:
          itemDraft.item_kind === "stock" && itemDraft.purchase_package_quantity
            ? itemDraft.purchase_package_unit || "L"
            : null,
        purchase_package_price_ex_vat:
          itemDraft.item_kind === "stock" && itemDraft.purchase_package_price_ex_vat != null
            ? Number(itemDraft.purchase_package_price_ex_vat)
            : null,
        yield_material_quantity:
          itemDraft.item_kind === "stock" && itemDraft.yield_material_quantity
            ? Number(itemDraft.yield_material_quantity)
            : null,
        yield_material_unit:
          itemDraft.item_kind === "stock" && itemDraft.yield_material_quantity
            ? itemDraft.yield_material_unit || itemDraft.purchase_package_unit || "L"
            : null,
        yield_output_quantity:
          itemDraft.item_kind === "stock" && itemDraft.yield_output_quantity
            ? Number(itemDraft.yield_output_quantity)
            : null,
        yield_output_unit:
          itemDraft.item_kind === "stock" && itemDraft.yield_output_quantity
            ? itemDraft.yield_output_unit || "m2"
            : null,
        default_application_passes: Math.max(
          1,
          Number(itemDraft.default_application_passes || 1),
        ),
        loss_rate_percent: Number(itemDraft.loss_rate_percent || 0),
        target_margin_rate:
          itemDraft.item_kind === "stock" && itemDraft.target_margin_rate != null
            ? Number(itemDraft.target_margin_rate)
            : null,
        active: itemDraft.active !== false,
      },
      result = itemDraft.id
        ? await supabase!
            .from("inventory_items")
            .update(payload)
            .eq("id", itemDraft.id)
        : await supabase!.from("inventory_items").insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : itemDraft.id
          ? "Article mis à jour."
          : "Article créé.",
    );
    if (!result.error) {
      if (itemDraft.item_kind === "kit") setKitModalOpen(false);
      setItemDraft(blankItem);
      reload();
    }
  }
  function openKitEditor(item: InventoryItem) {
    const quantities: Record<string, number> = {};
    kitComponents
      .filter((component) => component.kit_item_id === item.id)
      .forEach((component) => {
        quantities[component.id] = Number(component.quantity);
      });
    setItemDraft({ ...item });
    setKitItemId(item.id);
    setComponentItemId(stockItems[0]?.id || "");
    setComponentQuantity(1);
    setKitQuantityDrafts(quantities);
    setMessage("");
    setKitModalOpen(true);
  }
  async function addMovement(e: React.FormEvent) {
    e.preventDefault();
    if (!movementItem) return setMessage("Choisissez un article.");
    setBusy(true);
    const { error } = await supabase!.from("inventory_movements").insert({
      organization_id: profile.organization_id,
      item_id: movementItem,
      movement_type: movementType,
      quantity: movementQuantity,
      unit_cost: movementType === "purchase" ? movementCost : null,
      occurred_on: movementDate,
      notes: movementNotes.trim() || null,
    });
    setBusy(false);
    setMessage(
      error ? error.message : "Mouvement enregistré et stock recalculé.",
    );
    if (!error) {
      setMovementNotes("");
      reload();
    }
  }
  async function toggleItem(item: InventoryItem) {
    const { error } = await supabase!
      .from("inventory_items")
      .update({ active: !item.active })
      .eq("id", item.id);
    setMessage(
      error
        ? error.message
        : item.active
          ? "Article archivé."
          : "Article réactivé.",
    );
    if (!error) reload();
  }
  async function saveSupplier(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = {
        organization_id: profile.organization_id,
        name: supplierDraft.name?.trim(),
        contact_name: supplierDraft.contact_name?.trim() || null,
        email: supplierDraft.email?.trim().toLowerCase() || null,
        phone: supplierDraft.phone?.trim() || null,
        website: supplierDraft.website?.trim() || null,
        address: supplierDraft.address?.trim() || null,
        notes: supplierDraft.notes?.trim() || null,
        active: supplierDraft.active !== false,
      },
      result = supplierDraft.id
        ? await supabase!.from("inventory_suppliers").update(payload).eq("id", supplierDraft.id)
        : await supabase!.from("inventory_suppliers").insert(payload);
    setBusy(false);
    setMessage(result.error ? result.error.message : supplierDraft.id ? "Fournisseur mis à jour." : "Fournisseur ajouté.");
    if (!result.error) {
      setSupplierDraft({ name: "", contact_name: "", email: "", phone: "", website: "", address: "", notes: "", active: true });
      reload();
    }
  }
  function openProcurement(requirement: ProcurementRequirement) {
    setProcurementEditor(requirement);
    setProcurementDraft({
      supplier_id: requirement.supplier_id || "",
      needed_on: requirement.needed_on || "",
      quantity: Number(requirement.quantity),
      unit_cost: Number(requirement.estimated_unit_cost_ex_vat),
      purchase_reference: requirement.purchase_reference || "",
      purchase_url: requirement.purchase_url || "",
      review_note: requirement.review_note || "",
    });
    setMessage("");
  }
  async function reviewProcurement(decision: "approve" | "cancel") {
    if (!procurementEditor) return;
    if (decision === "approve" && !procurementDraft.supplier_id) {
      setMessage("Choisissez le fournisseur avant de valider ce besoin.");
      return;
    }
    if (decision === "cancel" && procurementDraft.review_note.trim().length < 5) {
      setMessage("Expliquez pourquoi ce besoin doit être écarté.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("review_procurement_requirement", {
      p_requirement_id: procurementEditor.id,
      p_decision: decision,
      p_supplier_id: procurementDraft.supplier_id || null,
      p_needed_on: procurementDraft.needed_on || null,
      p_quantity: procurementDraft.quantity,
      p_estimated_unit_cost_ex_vat: procurementDraft.unit_cost,
      p_purchase_reference: procurementDraft.purchase_reference.trim() || null,
      p_purchase_url: procurementDraft.purchase_url.trim() || null,
      p_review_note: procurementDraft.review_note.trim() || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : decision === "approve"
          ? "Besoin validé. Aucune commande n’a été envoyée : il est prêt pour le futur circuit de commande."
          : "Besoin écarté avec sa justification.",
    );
    if (!result.error) {
      setProcurementEditor(null);
      reload();
    }
  }
  async function saveKitComponent(e: React.FormEvent) {
    e.preventDefault();
    if (!kitItemId || !componentItemId) return setMessage("Choisissez un kit et un article composant.");
    setBusy(true);
    const { error } = await supabase!.from("inventory_kit_components").upsert({
      organization_id: profile.organization_id,
      kit_item_id: kitItemId,
      component_item_id: componentItemId,
      quantity: componentQuantity,
    }, { onConflict: "kit_item_id,component_item_id" });
    setBusy(false);
    setMessage(error ? error.message : "Composition du kit mise à jour.");
    if (!error) reload();
  }
  async function removeKitComponent(component: InventoryKitComponent) {
    const { error } = await supabase!.from("inventory_kit_components").delete().eq("id", component.id);
    setMessage(error ? error.message : "Composant retiré du kit.");
    if (!error) reload();
  }
  async function updateKitComponentQuantity(component: InventoryKitComponent) {
    const quantity = Number(kitQuantityDrafts[component.id] ?? component.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setMessage("La quantité du composant doit être supérieure à zéro.");
      return;
    }
    const { error } = await supabase!
      .from("inventory_kit_components")
      .update({ quantity })
      .eq("id", component.id);
    setMessage(error ? error.message : "Quantité du composant mise à jour.");
    if (!error) reload();
  }
  async function toggleKitFromModal() {
    if (!itemDraft.id) return;
    const nextActive = itemDraft.active === false;
    const { error } = await supabase!
      .from("inventory_items")
      .update({ active: nextActive })
      .eq("id", itemDraft.id);
    setMessage(error ? error.message : nextActive ? "Kit réactivé." : "Kit archivé. Son historique reste conservé.");
    if (!error) {
      setItemDraft((current) => ({ ...current, active: nextActive }));
      reload();
    }
  }
  async function deleteUnusedKit(item: InventoryItem) {
    if (item.item_kind !== "kit") return;
    if (item.active) {
      setMessage("Archivez d’abord le kit avant de demander sa suppression.");
      return;
    }
    if ((data.missionStockUsages || []).some((usage) => usage.item_id === item.id)) {
      setMessage("Ce kit a déjà été utilisé : il reste archivé pour préserver l’historique des missions et factures.");
      return;
    }
    if (!window.confirm(`Supprimer définitivement le kit « ${item.name} » jamais utilisé ?`)) return;
    setBusy(true);
    const itemResult = await supabase!.rpc("delete_unused_inventory_kit", {
      p_item_id: item.id,
    });
    setBusy(false);
    setMessage(itemResult.error ? itemResult.error.message : "Kit jamais utilisé supprimé.");
    if (!itemResult.error) {
      setItemDraft(blankItem);
      setKitItemId("");
      setKitModalOpen(false);
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Achats et consommables</span>
          <h1>Stocks</h1>
        </div>
        {onReturnToCatalog && (
          <button type="button" className="secondary" onClick={onReturnToCatalog}>
            ← Retour aux prestations et produits
          </button>
        )}
      </div>
      <div className="stats inventory-stats">
        <article>
          <span>Articles actifs</span>
          <strong>{activeItems.length}</strong>
        </article>
        <article>
          <span>Alertes de réapprovisionnement</span>
          <strong>{lowItems.length}</strong>
        </article>
        <article>
          <span>Valeur estimée du stock</span>
          <strong>{money(inventoryValue)}</strong>
          <small>quantité × coût moyen HT</small>
        </article>
        <article>
          <span>Besoins à contrôler</span>
          <strong>{pendingProcurement.length}</strong>
          <small>{approvedProcurement.length} validé(s), aucune commande automatique</small>
        </article>
      </div>
      <details className="billing-kpi-guide inventory-kpi-guide" data-testid="inventory-kpi-guide">
        <summary>Comprendre les compteurs de stock</summary>
        <p className="section-intro">
          Les quantités viennent du stock ARD Manager. Les besoins issus des devis
          restent séparés tant qu’un administrateur ne les a pas contrôlés.
        </p>
        <div className="billing-kpi-guide-grid">
          <article>
            <strong>Articles actifs</strong>
            <span>Nombre d’articles de stock et de kits non archivés. Un kit compte comme référence, sans doubler la valeur de ses composants.</span>
          </article>
          <article>
            <strong>Alertes de réapprovisionnement</strong>
            <span>Références actives dont la quantité est inférieure ou égale au seuil défini. Un seuil à zéro ne crée aucune alerte.</span>
          </article>
          <article>
            <strong>Valeur estimée du stock</strong>
            <span>Pour chaque article physique actif : quantité positive × coût moyen HT. Les kits sont exclus pour ne pas compter deux fois leurs composants.</span>
          </article>
          <article>
            <strong>Besoins à contrôler</strong>
            <span>Besoins issus d’un devis ou d’un manque de stock encore à examiner. Les besoins validés restent internes et ne constituent jamais une commande.</span>
          </article>
        </div>
        <p className="billing-kpi-guide-limit">
          Limite : une quantité ou un coût moyen non mis à jour fausse la lecture. Toute commande fournisseur reste une décision humaine distincte.
        </p>
      </details>
      {lowItems.length > 0 && (
        <Notice tone="error">
          À réapprovisionner : {lowItems.map((item) => item.name).join(", ")}.
        </Notice>
      )}
      <section className="panel" data-testid="procurement-requirements">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Devis validé → préparation interne</span>
            <h2>Besoins d’approvisionnement</h2>
            <p className="section-intro">
              Les achats externes et manques de stock issus d’un chiffrage appliqué arrivent ici.
              Valider un besoin ne commande rien : il prépare une décision traçable.
            </p>
          </div>
          <strong>{money([...pendingProcurement, ...approvedProcurement].reduce((sum, item) => sum + Number(item.estimated_total_cost_ex_vat || 0), 0))} HT suivis</strong>
        </div>
        <div className="compact-record-list">
          {procurementRequirements
            .filter((requirement) => requirement.status !== "superseded")
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .map((requirement) => {
              const quote = data.quotes.find((item) => item.id === requirement.quote_id),
                client = quote ? data.clients.find((item) => item.id === quote.client_id) : null,
                mission = requirement.mission_id
                  ? data.missions.find((item) => item.id === requirement.mission_id)
                  : null,
                supplier = suppliers.find((item) => item.id === requirement.supplier_id);
              return (
                <article key={requirement.id}>
                  <div>
                    <strong>{requirement.label}</strong>
                    <small>
                      {requirement.source_kind === "stock_shortage" ? "Stock insuffisant" : "Achat / prestation externe"}
                      {quote ? ` · ${quote.number}` : ""}
                      {client ? ` · ${client.name}` : ""}
                      {mission ? ` · mission du ${frDate(mission.starts_at)}` : ""}
                    </small>
                    <small>{requirement.quantity} {requirement.unit_label} × {money(requirement.estimated_unit_cost_ex_vat)} · {supplier?.name || "fournisseur à choisir"}</small>
                  </div>
                  <div>
                    <strong>{money(requirement.estimated_total_cost_ex_vat)}</strong>
                    <small>{requirement.status === "to_review" ? "À contrôler" : requirement.status === "approved" ? "Validé · non commandé" : "Écarté"}</small>
                  </div>
                  <button type="button" className="secondary compact" onClick={() => openProcurement(requirement)}>
                    {requirement.status === "to_review" ? "Examiner" : "Consulter"}
                  </button>
                </article>
              );
            })}
          {!procurementRequirements.some((item) => item.status !== "superseded") && (
            <p className="empty">Aucun besoin préparé. Ils apparaîtront après validation d’un chiffrage contenant un achat ou un manque de stock.</p>
          )}
        </div>
      </section>
      <section className="panel table-wrap inventory-catalog-table">
        <h2>Catalogue et niveaux</h2>
        <table>
          <thead>
            <tr>
              <th>Article</th>
              <th>Stock</th>
              <th>Seuil</th>
              <th>Coût moyen HT</th>
              <th>Valeur</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className={!item.active ? "muted-row" : ""}>
                <td>
                  <strong>{item.name}</strong>
                  <small>{item.item_kind === "kit" ? "Kit composé" : item.category || "Sans catégorie"}</small>
                  {item.item_kind === "kit" && (
                    <small>
                      {kitComponents.filter((part) => part.kit_item_id === item.id).map((part) => {
                        const component = items.find((candidate) => candidate.id === part.component_item_id);
                        return `${part.quantity} × ${component?.name || "article"}`;
                      }).join(" · ") || "Composition à compléter"}
                    </small>
                  )}
                </td>
                <td>
                  {item.item_kind === "kit" ? `${kitCapacity(item.id)} kit(s) réalisable(s)` : `${item.current_quantity} ${item.unit}`}
                </td>
                <td>{item.minimum_quantity}</td>
                <td>{money(item.average_unit_cost)}</td>
                <td>
                  {item.item_kind === "kit" ? "Composants" : money(Math.max(0, item.current_quantity) * item.average_unit_cost)}
                </td>
                <td className="row-actions">
                  <button
                    className="secondary compact"
                    onClick={() => item.item_kind === "kit" ? openKitEditor(item) : editInventoryItem(item)}
                  >
                    Modifier
                  </button>
                  <button
                    className="secondary compact"
                    onClick={() => toggleItem(item)}
                  >
                    {item.active ? "Archiver" : "Réactiver"}
                  </button>
                  {item.item_kind === "kit" && (
                    <button
                      className="secondary compact"
                      onClick={() => openKitEditor(item)}
                    >
                      Composition
                    </button>
                  )}
                  {item.item_kind === "kit" && !item.active && (
                    <button
                      className="danger compact"
                      disabled={busy}
                      onClick={() => deleteUnusedKit(item)}
                    >
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="empty">Aucun article en stock.</p>}
      </section>
      <details
        className="inventory-workspace"
        data-testid="inventory-workspace-article"
        open={activeInventoryWorkspace === "article"}
        onToggle={(event) => toggleInventoryWorkspace("article", event.currentTarget.open)}
      >
        <summary>
          <span><strong>Gérer un article ou un mouvement</strong><small>Créer, modifier, archiver, entrer ou corriger le stock</small></span>
          <span className="inventory-workspace-state">{itemDraft.id ? `Article sélectionné : ${itemDraft.name}` : "Outils de gestion"}</span>
        </summary>
        <div className="inventory-workspace-body inventory-forms">
        <section className="panel" ref={inventoryEditorRef}>
          <h2>{itemDraft.id ? "Modifier l’article" : "Nouvel article"}</h2>
          <form className="form-grid" onSubmit={saveItem}>
            <label>
              Type
              <select value={itemDraft.item_kind || "stock"} onChange={(e) => setItemDraft((item) => ({ ...item, item_kind: e.target.value as InventoryItem["item_kind"] }))}>
                <option value="stock">Article unitaire / consommable</option>
                <option value="kit">Kit composé</option>
              </select>
            </label>
            <label>
              Nom
              <input
                value={itemDraft.name || ""}
                onChange={(e) =>
                  setItemDraft((item) => ({ ...item, name: e.target.value }))
                }
                required
              />
            </label>
            <label>
              Catégorie
              <input
                value={itemDraft.category || ""}
                onChange={(e) =>
                  setItemDraft((item) => ({
                    ...item,
                    category: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Référence interne
              <input
                value={itemDraft.sku || ""}
                onChange={(e) =>
                  setItemDraft((item) => ({ ...item, sku: e.target.value }))
                }
              />
            </label>
            <label>
              Unité
              <input
                value={itemDraft.unit || ""}
                onChange={(e) =>
                  setItemDraft((item) => ({ ...item, unit: e.target.value }))
                }
              />
            </label>
            <label>
              Seuil d’alerte
              <input
                type="number"
                min="0"
                step="0.001"
                value={itemDraft.minimum_quantity || 0}
                onChange={(e) =>
                  setItemDraft((item) => ({
                    ...item,
                    minimum_quantity: +e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Fournisseur préféré
              <input
                value={itemDraft.preferred_supplier || ""}
                onChange={(e) =>
                  setItemDraft((item) => ({
                    ...item,
                    preferred_supplier: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Fiche fournisseur
              <select value={itemDraft.supplier_id || ""} onChange={(e) => setItemDraft((item) => ({ ...item, supplier_id: e.target.value || null }))}>
                <option value="">Aucun fournisseur relié</option>
                {suppliers.filter((supplier) => supplier.active).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
              </select>
            </label>
            <label className="wide">
              Lien direct de commande
              <input type="url" value={itemDraft.supplier_product_url || ""} onChange={(e) => setItemDraft((item) => ({ ...item, supplier_product_url: e.target.value }))} placeholder="https://fournisseur.fr/produit" />
            </label>
            <label className="check wide">
              <input type="checkbox" checked={!!itemDraft.sale_enabled} onChange={(e) => setItemDraft((item) => ({ ...item, sale_enabled: e.target.checked }))} />
              Facturable au client lorsqu’il est utilisé en mission
            </label>
            {itemDraft.sale_enabled && <>
              <label>Prix de vente HT<input type="number" min="0" step="0.01" value={itemDraft.sale_price_ex_vat || 0} onChange={(e) => setItemDraft((item) => ({ ...item, sale_price_ex_vat: +e.target.value }))} /></label>
              <label>TVA (%)<input type="number" min="0" max="100" step="0.1" value={itemDraft.sale_vat_rate ?? 20} onChange={(e) => setItemDraft((item) => ({ ...item, sale_vat_rate: +e.target.value }))} /></label>
              <label className="wide">Libellé de facturation<input value={itemDraft.sale_label || ""} onChange={(e) => setItemDraft((item) => ({ ...item, sale_label: e.target.value }))} placeholder={itemDraft.name || "Kit d’accueil"} /></label>
            </>}
            {itemDraft.item_kind !== "kit" && <>
              <div className="wide form-subsection-heading">
                <strong>Conditionnement et rendement technique</strong>
                <small>Facultatif. À compléter pour estimer automatiquement les besoins matière dans les devis.</small>
              </div>
              <label>Quantité par conditionnement<input type="number" min="0.001" step="0.001" value={itemDraft.purchase_package_quantity ?? ""} onChange={(e) => setItemDraft((item) => ({ ...item, purchase_package_quantity: e.target.value ? +e.target.value : null }))} placeholder="5" /></label>
              <label>Unité du conditionnement<select value={itemDraft.purchase_package_unit || "L"} onChange={(e) => setItemDraft((item) => ({ ...item, purchase_package_unit: e.target.value as InventoryItem["purchase_package_unit"] }))}><option value="L">L</option><option value="mL">mL</option><option value="kg">kg</option><option value="g">g</option><option value="unite">unité</option></select></label>
              <label>Prix du conditionnement HT<input type="number" min="0" step="0.01" value={itemDraft.purchase_package_price_ex_vat ?? ""} onChange={(e) => setItemDraft((item) => ({ ...item, purchase_package_price_ex_vat: e.target.value ? +e.target.value : null }))} placeholder="48" /></label>
              <label>Quantité matière de référence<input type="number" min="0.001" step="0.001" value={itemDraft.yield_material_quantity ?? ""} onChange={(e) => setItemDraft((item) => ({ ...item, yield_material_quantity: e.target.value ? +e.target.value : null }))} placeholder="1" /></label>
              <label>Unité matière du rendement<select value={itemDraft.yield_material_unit || itemDraft.purchase_package_unit || "L"} onChange={(e) => setItemDraft((item) => ({ ...item, yield_material_unit: e.target.value as InventoryItem["yield_material_unit"] }))}><option value="L">L</option><option value="mL">mL</option><option value="kg">kg</option><option value="g">g</option><option value="unite">unité</option></select></label>
              <label>Cette quantité traite<input type="number" min="0.001" step="0.001" value={itemDraft.yield_output_quantity ?? ""} onChange={(e) => setItemDraft((item) => ({ ...item, yield_output_quantity: e.target.value ? +e.target.value : null }))} placeholder="20" /></label>
              <label>Unité traitée<select value={itemDraft.yield_output_unit || "m2"} onChange={(e) => setItemDraft((item) => ({ ...item, yield_output_unit: e.target.value as InventoryItem["yield_output_unit"] }))}><option value="m2">m²</option><option value="m3">m³</option><option value="metre_lineaire">m linéaire</option><option value="unite">unité</option></select></label>
              <label>Nombre de couches / passages<input type="number" min="1" step="1" value={itemDraft.default_application_passes || 1} onChange={(e) => setItemDraft((item) => ({ ...item, default_application_passes: +e.target.value }))} /></label>
              <label>Pertes prévues (%)<input type="number" min="0" max="100" step="0.1" value={itemDraft.loss_rate_percent || 0} onChange={(e) => setItemDraft((item) => ({ ...item, loss_rate_percent: +e.target.value }))} /></label>
              <label>Marge matière minimale (%)<input type="number" min="0" max="99.9" step="0.1" value={itemDraft.target_margin_rate ?? ""} onChange={(e) => setItemDraft((item) => ({ ...item, target_margin_rate: e.target.value ? +e.target.value : null }))} placeholder="60" /></label>
            </>}
            <button disabled={busy}>
              {itemDraft.id ? "Enregistrer l’article" : "Créer l’article"}
            </button>
          </form>
        </section>
        <section className="panel">
          <h2>Entrée, utilisation ou correction</h2>
          <form className="form-grid" onSubmit={addMovement}>
            <label>
              Article
              <select
                value={movementItem}
                onChange={(e) => setMovementItem(e.target.value)}
                required
              >
                <option value="">Choisir</option>
                {activeItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Mouvement
              <select
                value={movementType}
                onChange={(e) => setMovementType(e.target.value)}
              >
                {Object.entries(movementLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Quantité
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={movementQuantity}
                onChange={(e) => setMovementQuantity(+e.target.value)}
                required
              />
            </label>
            {movementType === "purchase" && (
              <label>
                Coût unitaire HT
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={movementCost}
                  onChange={(e) => setMovementCost(+e.target.value)}
                />
              </label>
            )}
            <label>
              Date
              <input
                type="date"
                value={movementDate}
                onChange={(e) => setMovementDate(e.target.value)}
              />
            </label>
            <label className="wide">
              Motif / notes
              <input
                value={movementNotes}
                onChange={(e) => setMovementNotes(e.target.value)}
              />
            </label>
            <button disabled={busy}>Enregistrer le mouvement</button>
          </form>
        </section>
        </div>
      </details>
      <details
        className="inventory-workspace"
        data-testid="inventory-workspace-estimate"
        open={activeInventoryWorkspace === "estimate"}
        onToggle={(event) => toggleInventoryWorkspace("estimate", event.currentTarget.open)}
      >
        <summary>
          <span><strong>Préparer un besoin matière</strong><small>Estimer quantité, achat, coût et vente minimale pour un devis</small></span>
          <span className="inventory-workspace-state">{yieldItems.length} produit(s) exploitable(s)</span>
        </summary>
        <div className="inventory-workspace-body">
      <section className="panel material-estimator">
        <div className="section-heading"><div><span className="eyebrow">Préparation des devis</span><h2>Estimateur de besoin matière</h2></div></div>
        <p className="section-intro">Calcule les quantités théoriques, les achats à prévoir et le seuil de vente lié à la matière. La main-d’œuvre, les trajets et les autres charges restent à ajouter au prix final.</p>
        {yieldItems.length ? <>
          <div className="form-grid">
            <label>Produit<select value={estimateItem?.id || ""} onChange={(event) => { const selected = yieldItems.find((item) => item.id === event.target.value); setEstimateItemId(event.target.value); if (selected?.yield_output_unit) setEstimateTargetUnit(selected.yield_output_unit); setEstimatePasses(Number(selected?.default_application_passes || 1)); }}>{yieldItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Surface / quantité à traiter<input type="number" min="0.001" step="0.001" value={estimateTargetQuantity} onChange={(event) => setEstimateTargetQuantity(+event.target.value)} /></label>
            <label>Unité<select value={estimateTargetUnit} onChange={(event) => setEstimateTargetUnit(event.target.value)}><option value="m2">m²</option><option value="m3">m³</option><option value="metre_lineaire">m linéaire</option><option value="unite">unité</option></select></label>
            <label>Couches / passages<input type="number" min="1" step="1" value={estimatePasses} onChange={(event) => setEstimatePasses(+event.target.value)} /></label>
          </div>
          {estimatePreview ? <div className="material-estimate-grid" role="status">
            <article><span>Besoin théorique</span><strong>{estimatePreview.theoreticalMaterialQuantity.toFixed(2)} {materialUnitLabels[estimateItem?.yield_material_unit || ""]}</strong></article>
            <article><span>Conditionnements nécessaires</span><strong>{estimatePreview.packagesRequired}</strong></article>
            <article><span>À acheter maintenant</span><strong>{estimatePreview.packagesToPurchase}</strong><small>selon le stock actuel</small></article>
            <article><span>Reste projeté</span><strong>{estimatePreview.projectedRemainder.toFixed(2)} {materialUnitLabels[estimateItem?.yield_material_unit || ""]}</strong></article>
            <article><span>Coût matière théorique</span><strong>{money(estimatePreview.theoreticalMaterialCost)}</strong></article>
            <article><span>Décaissement achat</span><strong>{money(estimatePreview.purchaseCost)}</strong></article>
            <article><span>Vente minimale matière</span><strong>{estimatePreview.minimumMaterialSalePrice == null ? "Marge à définir" : money(estimatePreview.minimumMaterialSalePrice)}</strong><small>hors main-d’œuvre et autres coûts</small></article>
          </div> : <Notice tone="error">L’unité choisie ne correspond pas au rendement du produit.</Notice>}
        </> : <p className="empty">Aucun produit n’a encore un conditionnement et un rendement complets.</p>}
      </section>
        </div>
      </details>
      <details
        className="inventory-workspace"
        data-testid="inventory-workspace-suppliers"
        open={activeInventoryWorkspace === "suppliers"}
        onToggle={(event) => toggleInventoryWorkspace("suppliers", event.currentTarget.open)}
      >
        <summary>
          <span><strong>Fournisseurs</strong><small>Coordonnées, sources d’achat et informations utiles</small></span>
          <span className="inventory-workspace-state">{suppliers.length} fournisseur(s)</span>
        </summary>
        <div className="inventory-workspace-body inventory-forms inventory-advanced-forms">
        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">Réapprovisionnement</span><h2>Fournisseurs</h2></div></div>
          <div className="compact-record-list">
            {suppliers.map((supplier) => <article key={supplier.id}>
              <div><strong>{supplier.name}</strong><small>{[supplier.contact_name, supplier.email, supplier.phone].filter(Boolean).join(" · ") || "Coordonnées à compléter"}</small>{supplier.website && <a href={supplier.website} target="_blank" rel="noreferrer">Ouvrir le site</a>}</div>
              <button className="secondary compact" onClick={() => setSupplierDraft({ ...supplier })}>Modifier</button>
            </article>)}
          </div>
          <form className="form-grid" onSubmit={saveSupplier}>
            <label>Nom du fournisseur<input value={supplierDraft.name || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, name: e.target.value }))} required /></label>
            <label>Contact<input value={supplierDraft.contact_name || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, contact_name: e.target.value }))} /></label>
            <label>E-mail<input type="email" value={supplierDraft.email || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, email: e.target.value }))} /></label>
            <label>Téléphone<input type="tel" value={supplierDraft.phone || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, phone: e.target.value }))} /></label>
            <label className="wide">Site internet<input type="url" value={supplierDraft.website || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, website: e.target.value }))} /></label>
            <label className="wide">Adresse<input value={supplierDraft.address || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, address: e.target.value }))} /></label>
            <label className="wide">Notes<textarea value={supplierDraft.notes || ""} onChange={(e) => setSupplierDraft((value) => ({ ...value, notes: e.target.value }))} /></label>
            <button disabled={busy}>{supplierDraft.id ? "Enregistrer le fournisseur" : "Ajouter le fournisseur"}</button>
          </form>
        </section>
        </div>
      </details>
      <details
        className="inventory-workspace"
        data-testid="inventory-workspace-history"
        open={activeInventoryWorkspace === "history"}
        onToggle={(event) => toggleInventoryWorkspace("history", event.currentTarget.open)}
      >
        <summary>
          <span><strong>Historique des mouvements</strong><small>Entrées, utilisations, corrections, retours et pertes</small></span>
          <span className="inventory-workspace-state">{movements.length} mouvement(s)</span>
        </summary>
        <div className="inventory-workspace-body">
      <section className="panel compact-record-list">
        <h2>Derniers mouvements</h2>
        {movements.slice(0, 30).map((movement) => {
          const item = items.find(
              (candidate) => candidate.id === movement.item_id,
            ),
            inbound = ["purchase", "adjustment_in", "return"].includes(
              movement.movement_type,
            );
          return (
            <article key={movement.id}>
              <div>
                <strong>{item?.name || "Article supprimé"}</strong>
                <small>
                  {frDate(movement.occurred_on)} ·{" "}
                  {movementLabels[movement.movement_type]}
                  {movement.expense_report_id
                    ? " · depuis une note de frais"
                    : ""}
                </small>
              </div>
              <b>
                {inbound ? "+" : "−"}
                {movement.quantity} {item?.unit || ""}
              </b>
            </article>
          );
        })}
        {!movements.length && <p className="empty">Aucun mouvement.</p>}
      </section>
        </div>
      </details>
      {procurementEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Contrôler le besoin d’approvisionnement">
          <section className="portal-detail create-modal" data-testid="procurement-review-dialog">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Approvisionnement interne · aucune commande envoyée</span>
                <h2>{procurementEditor.label}</h2>
              </div>
              <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setProcurementEditor(null)}>×</button>
            </div>
            <div className="detail-grid">
              <div><span>Origine</span><strong>{procurementEditor.source_kind === "stock_shortage" ? "Manque de stock calculé" : "Achat ou prestation externe"}</strong></div>
              <div><span>État</span><strong>{procurementEditor.status === "to_review" ? "À contrôler" : procurementEditor.status === "approved" ? "Validé · non commandé" : procurementEditor.status === "cancelled" ? "Écarté" : "Remplacé"}</strong></div>
              <div><span>Devis</span><strong>{data.quotes.find((item) => item.id === procurementEditor.quote_id)?.number || "Brouillon"}</strong></div>
              <div><span>Mission</span><strong>{procurementEditor.mission_id ? data.missions.find((item) => item.id === procurementEditor.mission_id)?.starts_at ? frDate(data.missions.find((item) => item.id === procurementEditor.mission_id)!.starts_at) : "Mission reliée" : "Pas encore rattachée"}</strong></div>
            </div>
            <div className="form-grid">
              <label>Fournisseur<select disabled={procurementEditor.status !== "to_review"} value={procurementDraft.supplier_id} onChange={(event) => setProcurementDraft((current) => ({ ...current, supplier_id: event.target.value }))}><option value="">À choisir</option>{suppliers.filter((supplier) => supplier.active).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
              <label>Date nécessaire<input disabled={procurementEditor.status !== "to_review"} type="date" value={procurementDraft.needed_on} onChange={(event) => setProcurementDraft((current) => ({ ...current, needed_on: event.target.value }))} /></label>
              <label>Quantité<input disabled={procurementEditor.status !== "to_review"} type="number" min="0.001" step="0.001" value={procurementDraft.quantity} onChange={(event) => setProcurementDraft((current) => ({ ...current, quantity: Number(event.target.value) }))} /></label>
              <label>Unité<input value={procurementEditor.unit_label} disabled /></label>
              <label>Coût unitaire HT estimé<input disabled={procurementEditor.status !== "to_review"} type="number" min="0" step="0.01" value={procurementDraft.unit_cost} onChange={(event) => setProcurementDraft((current) => ({ ...current, unit_cost: Number(event.target.value) }))} /></label>
              <label>Total HT recalculé<input value={money(procurementDraft.quantity * procurementDraft.unit_cost)} disabled /></label>
              <label>Référence fournisseur<input disabled={procurementEditor.status !== "to_review"} value={procurementDraft.purchase_reference} onChange={(event) => setProcurementDraft((current) => ({ ...current, purchase_reference: event.target.value }))} /></label>
              <label className="wide">Lien utile<input disabled={procurementEditor.status !== "to_review"} type="url" value={procurementDraft.purchase_url} onChange={(event) => setProcurementDraft((current) => ({ ...current, purchase_url: event.target.value }))} /></label>
              <label className="wide">Contrôle / justification<textarea disabled={procurementEditor.status !== "to_review"} value={procurementDraft.review_note} onChange={(event) => setProcurementDraft((current) => ({ ...current, review_note: event.target.value }))} placeholder="Contrôle du tarif, disponibilité, ou motif pour écarter le besoin…" /></label>
            </div>
            {procurementEditor.purchase_url && <a href={procurementEditor.purchase_url} target="_blank" rel="noreferrer">Consulter la source fournisseur</a>}
            <Notice tone="info">Cette étape valide uniquement un besoin interne. Aucun e-mail, bon de commande ou paiement n’est produit.</Notice>
            {message && <Notice tone={/choisissez|expliquez|invalide|refus/i.test(message) ? "error" : "info"}>{message}</Notice>}
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setProcurementEditor(null)}>Fermer</button>
              {procurementEditor.status === "to_review" && <>
                <button type="button" className="danger" disabled={busy} onClick={() => reviewProcurement("cancel")}>Écarter avec justification</button>
                <button type="button" disabled={busy} onClick={() => reviewProcurement("approve")}>{busy ? "Validation…" : "Valider le besoin"}</button>
              </>}
            </div>
          </section>
        </div>
      )}
      {kitModalOpen && itemDraft.id && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Modifier le kit">
          <section className="portal-detail kit-editor-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Stock · kit composé</span>
                <h2>{itemDraft.name || "Kit"}</h2>
              </div>
              <button type="button" className="icon-close" aria-label="Fermer" onClick={() => { setKitModalOpen(false); setItemDraft(blankItem); }}>×</button>
            </div>
            <form className="form-grid" onSubmit={saveItem}>
              <label>Nom du kit<input value={itemDraft.name || ""} onChange={(event) => setItemDraft((current) => ({ ...current, name: event.target.value }))} required /></label>
              <label>Catégorie<input value={itemDraft.category || ""} onChange={(event) => setItemDraft((current) => ({ ...current, category: event.target.value }))} /></label>
              <label>Référence interne<input value={itemDraft.sku || ""} onChange={(event) => setItemDraft((current) => ({ ...current, sku: event.target.value }))} /></label>
              <label>Unité<input value={itemDraft.unit || "kit"} onChange={(event) => setItemDraft((current) => ({ ...current, unit: event.target.value }))} /></label>
              <label>Seuil d’alerte<input type="number" min="0" step="0.001" value={itemDraft.minimum_quantity || 0} onChange={(event) => setItemDraft((current) => ({ ...current, minimum_quantity: +event.target.value }))} /></label>
              <label>Fournisseur<select value={itemDraft.supplier_id || ""} onChange={(event) => setItemDraft((current) => ({ ...current, supplier_id: event.target.value || null }))}><option value="">Aucun fournisseur relié</option>{suppliers.filter((supplier) => supplier.active).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
              <label className="wide">Lien direct de commande<input type="url" value={itemDraft.supplier_product_url || ""} onChange={(event) => setItemDraft((current) => ({ ...current, supplier_product_url: event.target.value }))} /></label>
              <label className="check wide"><input type="checkbox" checked={!!itemDraft.sale_enabled} onChange={(event) => setItemDraft((current) => ({ ...current, sale_enabled: event.target.checked }))} /> Facturable au client lorsqu’il est utilisé en mission</label>
              {itemDraft.sale_enabled && <>
                <label>Prix de vente HT<input type="number" min="0" step="0.01" value={itemDraft.sale_price_ex_vat || 0} onChange={(event) => setItemDraft((current) => ({ ...current, sale_price_ex_vat: +event.target.value }))} /></label>
                <label>TVA (%)<input type="number" min="0" max="100" step="0.1" value={itemDraft.sale_vat_rate ?? 20} onChange={(event) => setItemDraft((current) => ({ ...current, sale_vat_rate: +event.target.value }))} /></label>
                <label className="wide">Libellé de facturation<input value={itemDraft.sale_label || ""} onChange={(event) => setItemDraft((current) => ({ ...current, sale_label: event.target.value }))} /></label>
              </>}
              <button disabled={busy}>Enregistrer les informations du kit</button>
            </form>

            <section className="panel kit-composition-editor">
              <div className="section-heading"><div><span className="eyebrow">Assemblage automatique</span><h3>Composition du kit</h3></div><strong>{kitCapacity(itemDraft.id)} kit(s) réalisable(s)</strong></div>
              <p className="section-intro">Modifiez les quantités, retirez un composant ou ajoutez un article sans quitter cette fenêtre. Une utilisation en mission retire automatiquement chaque composant.</p>
              <div className="compact-record-list">
                {kitComponents.filter((component) => component.kit_item_id === itemDraft.id).map((component) => {
                  const part = items.find((item) => item.id === component.component_item_id);
                  return <article key={component.id}>
                    <div><strong>{part?.name || "Article"}</strong><small>{part ? `${part.current_quantity} ${part.unit} en stock` : "Article indisponible"}</small></div>
                    <label>Quantité / kit<input aria-label={`Quantité de ${part?.name || "composant"}`} type="number" min="0.001" step="0.001" value={kitQuantityDrafts[component.id] ?? component.quantity} onChange={(event) => setKitQuantityDrafts((current) => ({ ...current, [component.id]: +event.target.value }))} /></label>
                    <div className="row-actions"><button type="button" className="secondary compact" onClick={() => updateKitComponentQuantity(component)}>Enregistrer</button><button type="button" className="danger compact" onClick={() => removeKitComponent(component)}>Retirer</button></div>
                  </article>;
                })}
                {!kitComponents.some((component) => component.kit_item_id === itemDraft.id) && <p className="empty">Aucun composant : ajoutez le premier article ci-dessous.</p>}
              </div>
              <form className="form-grid" onSubmit={saveKitComponent}>
                <label>Article à ajouter<select value={componentItemId} onChange={(event) => setComponentItemId(event.target.value)} required><option value="">Choisir</option>{stockItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                <label>Quantité par kit<input type="number" min="0.001" step="0.001" value={componentQuantity} onChange={(event) => setComponentQuantity(+event.target.value)} required /></label>
                <button disabled={busy}>Ajouter à la composition</button>
              </form>
            </section>

            <div className="detail-actions">
              <button type="button" className={itemDraft.active === false ? "secondary" : "danger"} onClick={toggleKitFromModal}>{itemDraft.active === false ? "Réactiver le kit" : "Archiver le kit"}</button>
              {itemDraft.active === false && <button type="button" className="danger" disabled={busy} onClick={() => deleteUnusedKit(itemDraft as InventoryItem)}>Supprimer définitivement si aucun historique</button>}
            </div>
            {message && <Notice tone={message.includes("mise à jour") || message.includes("ajouté") || message.includes("archivé") || message.includes("réactivé") ? "success" : "info"}>{message}</Notice>}
          </section>
        </div>
      )}
      {message && (
        <Notice tone={message.includes(".") ? "success" : "error"}>
          {message}
        </Notice>
      )}
    </>
  );
}

function AdminDirector({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const leaders = (data.organizationLeaders || []).filter((item) => item.active),
    currentPeriod = parisDay(new Date()).slice(0, 7),
    [scopeLeaderId, setScopeLeaderId] = useState("all"),
    [leaderId, setLeaderId] = useState(leaders[0]?.id || ""),
    [period, setPeriod] = useState(currentPeriod),
    [netSalary, setNetSalary] = useState(0),
    [contributions, setContributions] = useState(0),
    [otherCosts, setOtherCosts] = useState(0),
    [dividends, setDividends] = useState(0),
    [status, setStatus] = useState<DirectorCompensation["status"]>("planned"),
    [paidOn, setPaidOn] = useState(""),
    [notes, setNotes] = useState(""),
    [timeLeaderId, setTimeLeaderId] = useState(leaders[0]?.id || ""),
    [timeDate, setTimeDate] = useState(parisDay(new Date())),
    [timeCategory, setTimeCategory] = useState<DirectorTimeEntry["category"]>("commercial_networking"),
    [timeHours, setTimeHours] = useState(1),
    [timeNote, setTimeNote] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [activeDirectorView, setActiveDirectorView] = useState<
      "overview" | "time" | "compensation" | "governance"
    >("overview");
  const selectedLeader = leaders.find((item) => item.id === leaderId),
    scopeLeader = leaders.find((item) => item.id === scopeLeaderId),
    leaderLabel = scopeLeaderId === "all"
      ? `Tous les dirigeants · ${leaders.length} mandat(s) actif(s)`
      : scopeLeader
        ? `${[scopeLeader.first_name, scopeLeader.last_name].filter(Boolean).join(" ")} · ${scopeLeader.title}`
        : "Dirigeant à définir",
    entries = (data.directorCompensations || []).filter(
      (entry) => scopeLeaderId === "all" || entry.leader_id === scopeLeaderId,
    ),
    year = period.slice(0, 4),
    yearEntries = entries.filter((entry) => entry.period.startsWith(year)),
    directorTimeEntries = (data.directorTimeEntries || []).filter(
      (entry) => entry.work_date.startsWith(year) && (scopeLeaderId === "all" || entry.leader_id === scopeLeaderId),
    ),
    directorTimeMinutes = directorTimeEntries.reduce((sum, entry) => sum + Number(entry.minutes || 0), 0),
    commercialTimeMinutes = directorTimeEntries.filter((entry) => entry.category === "commercial_networking").reduce((sum, entry) => sum + Number(entry.minutes || 0), 0),
    operationsTimeMinutes = directorTimeEntries.filter((entry) => entry.category === "operations").reduce((sum, entry) => sum + Number(entry.minutes || 0), 0),
    commercialTimeShare = directorTimeMinutes > 0 ? Math.round(commercialTimeMinutes / directorTimeMinutes * 100) : null,
    monthlyTimeAllocation = Array.from({ length: 12 }, (_, monthIndex) => {
      const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
        monthRows = directorTimeEntries.filter((entry) => entry.work_date.startsWith(monthKey)),
        total = monthRows.reduce((sum, entry) => sum + Number(entry.minutes || 0), 0),
        commercial = monthRows.filter((entry) => entry.category === "commercial_networking").reduce((sum, entry) => sum + Number(entry.minutes || 0), 0);
      return { monthKey, total, commercial, share: total > 0 ? Math.round(commercial / total * 100) : null };
    }),
    yearNet = yearEntries.reduce((sum, entry) => sum + entry.net_salary, 0),
    yearContributions = yearEntries.reduce(
      (sum, entry) => sum + entry.social_contributions + entry.other_costs,
      0,
    ),
    yearDividends = yearEntries.reduce(
      (sum, entry) => sum + entry.dividends,
      0,
    ),
    scopedProfileIds = leaders
      .filter((leader) => scopeLeaderId === "all" || leader.id === scopeLeaderId)
      .map((leader) => leader.profile_id)
      .filter(Boolean),
    leaderMileage = data.mileages.filter(
      (entry) =>
        !entry.worker_id &&
        Boolean(
          entry.owner_profile_id && scopedProfileIds.includes(entry.owner_profile_id),
        ) &&
        entry.travel_date.startsWith(year) &&
        entry.status === "approved",
    ),
    leaderKm = leaderMileage.reduce(
      (sum, entry) => sum + Number(entry.kilometers || 0),
      0,
    ),
    leaderMileageValue = leaderMileage.every(
      (entry) => entry.calculated_amount != null,
    )
      ? leaderMileage.reduce(
          (sum, entry) => sum + Number(entry.calculated_amount || 0),
          0,
        )
      : null,
    leaderIndicators = leaders.map((leader) => {
      const leaderEntries = (data.directorCompensations || []).filter(
          (entry) =>
            entry.leader_id === leader.id && entry.period.startsWith(year),
        ),
        mileageEntries = data.mileages.filter(
          (entry) =>
            !entry.worker_id &&
            Boolean(leader.profile_id) &&
            entry.owner_profile_id === leader.profile_id &&
            entry.travel_date.startsWith(year) &&
            entry.status === "approved",
        ),
        timeRows = (data.directorTimeEntries || []).filter((entry) => entry.leader_id === leader.id && entry.work_date.startsWith(year)),
        totalTime = timeRows.reduce((sum, entry) => sum + Number(entry.minutes || 0), 0),
        commercialTime = timeRows.filter((entry) => entry.category === "commercial_networking").reduce((sum, entry) => sum + Number(entry.minutes || 0), 0);
      return {
        leader,
        net: leaderEntries.reduce((sum, entry) => sum + entry.net_salary, 0),
        costs: leaderEntries.reduce(
          (sum, entry) =>
            sum + entry.social_contributions + entry.other_costs,
          0,
        ),
        dividends: leaderEntries.reduce((sum, entry) => sum + entry.dividends, 0),
        kilometers: mileageEntries.reduce(
          (sum, entry) => sum + Number(entry.kilometers || 0),
          0,
        ),
        months: new Set(leaderEntries.map((entry) => entry.period.slice(0, 7)))
          .size,
        totalTime,
        commercialTime,
        commercialShare: totalTime > 0 ? Math.round(commercialTime / totalTime * 100) : null,
      };
    });
  function loadPeriod(value: string) {
    setPeriod(value);
    const entry = (data.directorCompensations || []).find(
      (item) =>
        item.leader_id === leaderId && item.period.startsWith(value),
    );
    setNetSalary(entry?.net_salary || 0);
    setContributions(entry?.social_contributions || 0);
    setOtherCosts(entry?.other_costs || 0);
    setDividends(entry?.dividends || 0);
    setStatus(entry?.status || "planned");
    setPaidOn(entry?.paid_on || "");
    setNotes(entry?.notes || "");
    setMessage("");
  }
  function loadLeader(value: string) {
    setLeaderId(value);
    const entry = (data.directorCompensations || []).find(
      (item) => item.leader_id === value && item.period.startsWith(period),
    );
    setNetSalary(entry?.net_salary || 0);
    setContributions(entry?.social_contributions || 0);
    setOtherCosts(entry?.other_costs || 0);
    setDividends(entry?.dividends || 0);
    setStatus(entry?.status || "planned");
    setPaidOn(entry?.paid_on || "");
    setNotes(entry?.notes || "");
    setMessage("");
  }
  function loadEntry(entry: DirectorCompensation) {
    setLeaderId(entry.leader_id || "");
    setPeriod(entry.period.slice(0, 7));
    setNetSalary(entry.net_salary || 0);
    setContributions(entry.social_contributions || 0);
    setOtherCosts(entry.other_costs || 0);
    setDividends(entry.dividends || 0);
    setStatus(entry.status || "planned");
    setPaidOn(entry.paid_on || "");
    setNotes(entry.notes || "");
    setMessage("");
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedLeader) {
      setMessage("Définissez d’abord le dirigeant concerné.");
      return;
    }
    setBusy(true);
    const { error } = await supabase!.from("director_compensations").upsert(
      {
        organization_id: profile.organization_id,
        profile_id: selectedLeader.profile_id,
        leader_id: selectedLeader.id,
        period: `${period}-01`,
        net_salary: netSalary,
        social_contributions: contributions,
        other_costs: otherCosts,
        dividends,
        status,
        paid_on: status === "paid" ? paidOn || parisDay(new Date()) : null,
        notes: notes.trim() || null,
      },
      { onConflict: "organization_id,leader_id,period" },
    );
    setBusy(false);
    setMessage(error ? error.message : "Rémunération dirigeant enregistrée.");
    if (!error) reload();
  }
  async function saveDirectorTime(event: React.FormEvent) {
    event.preventDefault();
    if (!timeLeaderId) { setMessage("Choisissez le dirigeant concerné."); return; }
    setBusy(true);
    const { error } = await supabase!.rpc("record_director_time_entry", {
      p_leader_id: timeLeaderId,
      p_work_date: timeDate,
      p_category: timeCategory,
      p_minutes: Math.round(timeHours * 60),
      p_note: timeNote.trim(),
    });
    setBusy(false);
    setMessage(error ? error.message : "Temps dirigeant enregistré à partir de la saisie réelle.");
    if (!error) { setTimeNote(""); reload(); }
  }
  const timeCategoryLabels: Record<DirectorTimeEntry["category"], string> = {
    commercial_networking: "Commercial et networking",
    operations: "Opérations terrain",
    management: "Pilotage et direction",
    administration_finance: "Administration et finance",
    team: "Équipe et RH",
    other: "Autre temps",
  };
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Pilotage personnel</span>
          <h1>Dirigeant</h1>
        </div>
        <label className="director-scope-selector">
          Vue affichée
          <select
            aria-label="Dirigeant affiché"
            value={scopeLeaderId}
            onChange={(event) => {
              const value = event.target.value;
              setScopeLeaderId(value);
              if (value !== "all") loadLeader(value);
            }}
          >
            <option value="all">Tous les dirigeants</option>
            {leaders.map((leader) => (
              <option key={leader.id} value={leader.id}>
                {[leader.first_name, leader.last_name].filter(Boolean).join(" ")} · {leader.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="stats director-stats">
        <article>
          <span>Rémunération nette {year}</span>
          <strong>{money(yearNet)}</strong>
          <small>{leaderLabel}</small>
        </article>
        <article>
          <span>Charges et autres coûts {year}</span>
          <strong>{money(yearContributions)}</strong>
        </article>
        <article>
          <span>Dividendes suivis {year}</span>
          <strong>{money(yearDividends)}</strong>
          <small>présentés à part des charges d’exploitation</small>
        </article>
        <article>
          <span>IK dirigeant {year}</span>
          <strong>
            {leaderMileageValue == null
              ? `${leaderKm} km`
              : money(leaderMileageValue)}
          </strong>
          <small>
            {leaderMileageValue == null
              ? "véhicule ou calcul IK à compléter"
              : `${leaderKm} km validés`}
          </small>
        </article>
        <article>
          <span>Temps mesuré {year}</span>
          <strong>{directorTimeMinutes > 0 ? `${(directorTimeMinutes / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h` : "À renseigner"}</strong>
          <small>{directorTimeMinutes > 0 ? `${directorTimeEntries.length} saisie(s) réelle(s)` : "aucune durée saisie sur ce périmètre"}</small>
        </article>
        <article>
          <span>Commercial & networking</span>
          <strong>{commercialTimeShare == null ? "À renseigner" : `${commercialTimeShare} %`}</strong>
          <small>{commercialTimeShare == null ? "aucun calcul sans temps saisi" : `${(commercialTimeMinutes / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h commerciales · ${(operationsTimeMinutes / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h terrain`}</small>
        </article>
      </div>
      <details className="billing-kpi-guide director-kpi-guide" data-testid="director-kpi-guide">
        <summary>
          <span>Comprendre les indicateurs dirigeant</span>
          <small>Définitions, sources et limites de la vue sélectionnée</small>
        </summary>
        <div className="billing-kpi-guide-grid">
          <article><span>Rémunération nette</span><strong>Ce qui a été décidé pour le dirigeant</strong><p>Somme des rémunérations nettes enregistrées sur l’année et sur le périmètre affiché. Une période absente reste absente du total.</p></article>
          <article><span>Charges et autres coûts</span><strong>Coût suivi hors rémunération nette</strong><p>Cotisations et autres coûts rattachés aux rémunérations dirigeant. Ils restent séparés des dépenses d’exploitation et de la comptabilité officielle.</p></article>
          <article><span>Dividendes suivis</span><strong>Distribution présentée à part</strong><p>Somme des dividendes saisis pour la période. Elle n’est jamais ajoutée aux charges d’exploitation ni confondue avec une rémunération mensuelle.</p></article>
          <article><span>IK dirigeant</span><strong>Déplacements validés uniquement</strong><p>Kilomètres approuvés pour le ou les dirigeants affichés. Le montant en euros apparaît seulement lorsqu’un véhicule et une règle de calcul exploitables existent.</p></article>
          <article><span>Temps mesuré</span><strong>Durées réellement renseignées</strong><p>Somme des saisies datées de temps dirigeant. L’absence de saisie affiche « À renseigner » et ne signifie jamais zéro heure travaillée.</p></article>
          <article><span>Commercial &amp; networking</span><strong>Part du temps mesuré</strong><p>Temps commercial et networking divisé par tout le temps dirigeant saisi sur la période. Cette part n’est calculée que si le total mesuré est supérieur à zéro.</p></article>
        </div>
        <p className="billing-kpi-guide-limit"><strong>Limite :</strong> cette page suit les décisions internes et le temps déclaré. Elle ne remplace ni la paie, ni les comptes validés par l’expert-comptable, ni un relevé exhaustif du temps lorsque les saisies sont incomplètes.</p>
      </details>
      <div
        className="decision-view-switcher director-view-switcher"
        aria-label="Choisir une vue dirigeant"
        role="navigation"
        data-testid="director-view-switcher"
      >
        {[
          {
            id: "overview" as const,
            label: "Synthèse",
            description: "Comparer Romain, Camille ou l’ensemble des dirigeants",
          },
          {
            id: "time" as const,
            label: "Temps & activité",
            description: "Suivre le temps commercial, le terrain et le pilotage",
          },
          {
            id: "compensation" as const,
            label: "Rémunération",
            description: "Décider un mois et consulter l’historique financier",
          },
          {
            id: "governance" as const,
            label: "Mandats & gouvernance",
            description: "Gérer les dirigeants, leurs fonctions et leurs mandats",
          },
        ].map((view) => (
          <button
            type="button"
            key={view.id}
            className={activeDirectorView === view.id ? "active" : ""}
            aria-pressed={activeDirectorView === view.id}
            onClick={() => setActiveDirectorView(view.id)}
          >
            <strong>{view.label}</strong>
            <span>{view.description}</span>
          </button>
        ))}
      </div>
      {activeDirectorView === "overview" && (
      <section className="panel director-comparison" data-testid="director-comparison">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Lecture comparative {year}</span>
            <h2>Indicateurs par dirigeant</h2>
          </div>
          <small>Les montants restent séparés des charges d’exploitation et des dividendes.</small>
        </div>
        <div className="director-comparison-grid">
          {leaderIndicators.map(({ leader, net, costs, dividends: leaderDividends, kilometers, months, totalTime, commercialTime, commercialShare }) => (
            <button
              type="button"
              key={leader.id}
              className={scopeLeaderId === leader.id ? "active" : ""}
              onClick={() => {
                setScopeLeaderId(leader.id);
                loadLeader(leader.id);
              }}
            >
              <span>
                <strong>{[leader.first_name, leader.last_name].filter(Boolean).join(" ")}</strong>
                <small>{leader.title}</small>
              </span>
              <dl>
                <div><dt>Net suivi</dt><dd>{money(net)}</dd></div>
                <div><dt>Charges</dt><dd>{money(costs)}</dd></div>
                <div><dt>Dividendes</dt><dd>{money(leaderDividends)}</dd></div>
                <div><dt>Mois renseignés</dt><dd>{months}</dd></div>
                <div><dt>IK validés</dt><dd>{kilometers} km</dd></div>
                <div><dt>Temps mesuré</dt><dd>{totalTime > 0 ? `${(totalTime / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h` : "À renseigner"}</dd></div>
                <div><dt>Commercial</dt><dd>{commercialShare == null ? "À renseigner" : `${commercialShare} % · ${(commercialTime / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h`}</dd></div>
              </dl>
            </button>
          ))}
        </div>
      </section>
      )}
      {activeDirectorView === "time" && (
      <section className="panel director-time-allocation" data-testid="director-time-allocation">
        <div className="section-heading"><div><span className="eyebrow">Temps réellement consacré · {year}</span><h2>Commercial, networking et opérations</h2><p className="section-intro">Chaque durée vient d’une saisie datée. Une période vide reste « à renseigner » et n’est jamais interprétée comme zéro.</p></div><strong>{commercialTimeShare == null ? "À renseigner" : `${commercialTimeShare} % commercial`}</strong></div>
        <div className="director-time-months" aria-label="Répartition mensuelle du temps dirigeant">
          {monthlyTimeAllocation.map((month) => <article key={month.monthKey} className={month.total > 0 ? "measured" : "missing"}><small>{new Date(`${month.monthKey}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "short" })}</small><strong>{month.total > 0 ? `${(month.total / 60).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h` : "—"}</strong><span>{month.share == null ? "À renseigner" : `${month.share} % commercial`}</span></article>)}
        </div>
        <form className="form-grid director-time-form" onSubmit={saveDirectorTime}>
          <label>Dirigeant<select required value={timeLeaderId} onChange={(event) => setTimeLeaderId(event.target.value)}><option value="">Choisir</option>{leaders.map((leader) => <option key={leader.id} value={leader.id}>{[leader.first_name,leader.last_name].filter(Boolean).join(" ")} · {leader.title}</option>)}</select></label>
          <label>Date<input required type="date" max={parisDay(new Date())} value={timeDate} onChange={(event) => setTimeDate(event.target.value)} /></label>
          <label>Nature du temps<select value={timeCategory} onChange={(event) => setTimeCategory(event.target.value as DirectorTimeEntry["category"])}>{Object.entries(timeCategoryLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Durée réelle (heures)<input required type="number" min="0.25" max="24" step="0.25" value={timeHours} onChange={(event) => setTimeHours(Number(event.target.value))} /></label>
          <label className="wide">Précision<textarea required minLength={3} maxLength={500} value={timeNote} onChange={(event) => setTimeNote(event.target.value)} placeholder="Ex. rendez-vous avec un maître d’œuvre et relance des deux devis transmis." /></label>
          <button disabled={busy}>{busy ? "Enregistrement…" : "Ajouter ce temps réel"}</button>
        </form>
        <div className="compact-record-list director-time-history">
          {directorTimeEntries.slice(0,12).map((entry) => { const leader = leaders.find((item) => item.id === entry.leader_id); return <article key={entry.id}><div><strong>{timeCategoryLabels[entry.category]}</strong><small>{frDate(entry.work_date)} · {leader ? [leader.first_name,leader.last_name].filter(Boolean).join(" ") : "Dirigeant"}</small></div><span><strong>{(entry.minutes / 60).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong><small>{entry.note}</small></span></article>; })}
          {!directorTimeEntries.length && <p className="empty">Aucune durée réelle saisie pour ce périmètre.</p>}
        </div>
      </section>
      )}
      {activeDirectorView === "compensation" && (
        <>
      <section className="panel">
        <h2>Décider et suivre la rémunération mensuelle</h2>
        <form className="form-grid" onSubmit={save}>
          <label>
            Dirigeant concerné
            <select value={leaderId} onChange={(e) => loadLeader(e.target.value)} required>
              <option value="">Définir un dirigeant</option>
              {leaders.map((leader) => (
                <option key={leader.id} value={leader.id}>
                  {[leader.first_name, leader.last_name].filter(Boolean).join(" ")} · {leader.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Mois
            <input
              type="month"
              value={period}
              onChange={(e) => loadPeriod(e.target.value)}
            />
          </label>
          <label>
            État
            <select
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as DirectorCompensation["status"])
              }
            >
              <option value="planned">Prévue</option>
              <option value="approved">Validée</option>
              <option value="paid">Versée</option>
            </select>
          </label>
          <label>
            Rémunération nette (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={netSalary}
              onChange={(e) => setNetSalary(+e.target.value)}
            />
          </label>
          <label>
            Cotisations / charges (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={contributions}
              onChange={(e) => setContributions(+e.target.value)}
            />
          </label>
          <label>
            Autres coûts associés (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={otherCosts}
              onChange={(e) => setOtherCosts(+e.target.value)}
            />
          </label>
          <label>
            Dividendes (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={dividends}
              onChange={(e) => setDividends(+e.target.value)}
            />
          </label>
          {status === "paid" && (
            <label>
              Date de versement
              <input
                type="date"
                value={paidOn}
                onChange={(e) => setPaidOn(e.target.value)}
              />
            </label>
          )}
          <label className="wide">
            Notes
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <button disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer le mois"}
          </button>
        </form>
        {message && (
          <Notice tone={message.includes("enregistr") ? "success" : "error"}>
            {message}
          </Notice>
        )}
      </section>
      <section className="panel table-wrap">
        <h2>Historique</h2>
        <table>
          <thead>
            <tr>
              <th>Mois</th>
              <th>Dirigeant</th>
              <th>Net</th>
              <th>Charges</th>
              <th>Autres coûts</th>
              <th>Dividendes</th>
              <th>État</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{frDate(entry.period)}</td>
                <td>
                  {(() => {
                    const leader = leaders.find((item) => item.id === entry.leader_id);
                    return leader
                      ? [leader.first_name, leader.last_name].filter(Boolean).join(" ")
                      : "Dirigeant non rattaché";
                  })()}
                </td>
                <td>{money(entry.net_salary)}</td>
                <td>{money(entry.social_contributions)}</td>
                <td>{money(entry.other_costs)}</td>
                <td>{money(entry.dividends)}</td>
                <td>
                  <Status value={entry.status} />
                </td>
                <td>
                  <button
                    className="secondary compact"
                    onClick={() => loadEntry(entry)}
                  >
                    Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!entries.length && (
          <p className="empty">Aucune rémunération dirigeant enregistrée.</p>
        )}
      </section>
        </>
      )}
      {activeDirectorView === "governance" && (
        <OrganizationLeaderManager profile={profile} data={data} reload={reload} />
      )}
    </>
  );
}

function AdminAccounting({
  profile,
  data,
  reload,
  focusExpenseId,
  onFocusConsumed,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  focusExpenseId?: string | null;
  onFocusConsumed?: () => void;
}) {
  const month = parisDay(new Date()).slice(0, 7),
    expenses = data.expenses.filter(
      (e) =>
        e.spent_on.startsWith(month) &&
        !["cancelled", "rejected"].includes(e.status),
    ),
    today = parisDay(new Date()),
    fixedCharges = data.fixedCharges || [],
    activeFixedCharges = fixedCharges.filter(
      (charge) =>
        charge.active &&
        charge.starts_on <= today &&
        (!charge.ends_on || charge.ends_on >= today),
    ),
    monthlyEquivalent = (charge: FixedCharge) =>
      charge.frequency === "weekly"
        ? (charge.amount_ex_vat * 52) / 12
        : charge.frequency === "quarterly"
          ? charge.amount_ex_vat / 3
          : charge.frequency === "annual"
            ? charge.amount_ex_vat / 12
            : charge.amount_ex_vat,
    monthlyFixedTotal = activeFixedCharges.reduce(
      (sum, charge) => sum + monthlyEquivalent(charge),
      0,
    ),
    employees = data.workers.filter(
      (worker) => worker.kind === "employee" && worker.active,
    ),
    monthlyPayroll = employees.reduce(
      (sum, worker) =>
        sum +
        Number(worker.employer_cost_monthly ?? worker.net_salary ?? 0) +
        Number(worker.ticket_count || 0) * Number(worker.ticket_value || 0),
      0,
    ),
    monthlyFixedTotalIncVat = activeFixedCharges.reduce(
      (sum, charge) =>
        sum +
        monthlyEquivalent(charge) *
          (1 + Number(charge.vat_rate || 0) / 100),
      0,
    ),
    observedReceiptMonths = (() => {
      const cutoff = new Date(`${today}T12:00:00Z`);
      cutoff.setUTCMonth(cutoff.getUTCMonth() - 11, 1);
      const byMonth = new Map<string, number>();
      (data.invoicePayments || [])
        .filter(
          (payment) =>
            payment.status === "confirmed" &&
            payment.received_on >= cutoff.toISOString().slice(0, 10) &&
            payment.received_on <= today,
        )
        .forEach((payment) => {
          const key = payment.received_on.slice(0, 7);
          byMonth.set(key, (byMonth.get(key) || 0) + Number(payment.amount || 0));
        });
      return byMonth;
    })(),
    observedMonthlyReceipts = observedReceiptMonths.size
      ? Array.from(observedReceiptMonths.values()).reduce(
          (sum, amount) => sum + amount,
          0,
        ) / observedReceiptMonths.size
      : 0,
    initialCashPlanSettings: CashPlanSettings = {
      baseline_monthly_receipts:
        data.organization?.cash_plan_settings?.baseline_monthly_receipts ?? null,
      safety_months: data.organization?.cash_plan_settings?.safety_months ?? 3,
      low_season_reserve:
        data.organization?.cash_plan_settings?.low_season_reserve ?? 0,
      significant_spend_threshold:
        data.organization?.cash_plan_settings?.significant_spend_threshold ?? 0,
      high_season_factor:
        data.organization?.cash_plan_settings?.high_season_factor ?? 1,
      low_season_factor:
        data.organization?.cash_plan_settings?.low_season_factor ?? 1,
      monthly_adjustments:
        data.organization?.cash_plan_settings?.monthly_adjustments || {},
      notes: data.organization?.cash_plan_settings?.notes || "",
    },
    blankCharge: Partial<FixedCharge> = {
      name: "",
      vendor: "",
      category: "subscription",
      amount_ex_vat: 0,
      vat_rate: data.organization?.default_vat_rate ?? 20,
      frequency: "monthly",
      starts_on: today,
      ends_on: null,
      next_due_on: today,
      payment_method: "",
      notes: "",
      active: true,
    },
    [balance, setBalance] = useState(
      data.organization?.current_account_balance?.toString() || "",
    ),
    [notes, setNotes] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [exportFrom, setExportFrom] = useState(`${today.slice(0, 4)}-01-01`),
    [exportTo, setExportTo] = useState(today),
    [analysisYear, setAnalysisYear] = useState(today.slice(0, 4)),
    [selectedAccountingMonth, setSelectedAccountingMonth] = useState(month),
    [accountingView, setAccountingView] = useState<
      "month" | "forecast" | "settings" | "expenses"
    >("month"),
    [editingExpense, setEditingExpense] = useState<Expense | null>(null),
    [expenseMessage, setExpenseMessage] = useState(""),
    [reimbursementDate, setReimbursementDate] = useState(today),
    [reimbursementReference, setReimbursementReference] = useState(""),
    [cashPlanSettings, setCashPlanSettings] =
      useState<CashPlanSettings>(initialCashPlanSettings),
    [cashPlanDraft, setCashPlanDraft] =
      useState<CashPlanSettings>(initialCashPlanSettings),
    [cashPlanOpen, setCashPlanOpen] = useState(false),
    [cashPlanMessage, setCashPlanMessage] = useState(""),
    [cashPlanBusy, setCashPlanBusy] = useState(false),
    [editingCharge, setEditingCharge] =
      useState<Partial<FixedCharge>>(blankCharge);
  useEffect(() => {
    if (!focusExpenseId) return;
    const expense = data.expenses.find((item) => item.id === focusExpenseId);
    if (!expense) return;
    setExpenseMessage("");
    setAccountingView("expenses");
    setEditingExpense({ ...expense });
    setReimbursementDate(expense.reimbursed_on || today);
    setReimbursementReference(expense.reimbursement_reference || "");
    onFocusConsumed?.();
  }, [focusExpenseId, data.expenses, onFocusConsumed, today]);
  const openCashPlanSettings = () => {
      setCashPlanDraft({
        ...cashPlanSettings,
        monthly_adjustments: { ...(cashPlanSettings.monthly_adjustments || {}) },
      });
      setCashPlanMessage("");
      setCashPlanOpen(true);
    },
    updateCashPlanMonth = (
      key: string,
      field: keyof CashPlanMonthAdjustment,
      value: string | number,
    ) =>
      setCashPlanDraft((current) => ({
        ...current,
        monthly_adjustments: {
          ...(current.monthly_adjustments || {}),
          [key]: {
            ...(current.monthly_adjustments?.[key] || {}),
            [field]: value,
          },
        },
      })),
    saveCashPlanSettings = async () => {
      const safeNumber = (value: unknown) =>
          Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0,
        normalized: CashPlanSettings = {
          baseline_monthly_receipts:
            cashPlanDraft.baseline_monthly_receipts == null
              ? null
              : safeNumber(cashPlanDraft.baseline_monthly_receipts),
          safety_months: Math.min(
            12,
            Math.max(1, Math.round(safeNumber(cashPlanDraft.safety_months || 3))),
          ),
          low_season_reserve: safeNumber(cashPlanDraft.low_season_reserve),
          significant_spend_threshold: safeNumber(
            cashPlanDraft.significant_spend_threshold,
          ),
          high_season_factor: Math.min(
            5,
            safeNumber(cashPlanDraft.high_season_factor ?? 1),
          ),
          low_season_factor: Math.min(
            5,
            safeNumber(cashPlanDraft.low_season_factor ?? 1),
          ),
          monthly_adjustments: Object.fromEntries(
            Object.entries(cashPlanDraft.monthly_adjustments || {}).map(
              ([key, adjustment]) => [
                key,
                {
                  season: adjustment.season || "normal",
                  additional_receipts: safeNumber(
                    adjustment.additional_receipts,
                  ),
                  planned_hiring_cost: safeNumber(
                    adjustment.planned_hiring_cost,
                  ),
                  planned_investment: safeNumber(
                    adjustment.planned_investment,
                  ),
                  other_outflows: safeNumber(adjustment.other_outflows),
                  note: adjustment.note?.trim() || "",
                },
              ],
            ),
          ),
          notes: cashPlanDraft.notes?.trim() || "",
        };
      setCashPlanBusy(true);
      setCashPlanMessage("");
      const result = isShowcaseMode
        ? await supabase!
            .from("organizations")
            .update({ cash_plan_settings: normalized })
            .eq("id", profile.organization_id)
        : await supabase!.rpc("save_cash_plan_settings", {
            p_settings: normalized,
          });
      setCashPlanBusy(false);
      if (result.error) {
        setCashPlanMessage(result.error.message);
        return;
      }
      setCashPlanSettings(normalized);
      setCashPlanDraft(normalized);
      setCashPlanMessage(
        isShowcaseMode
          ? "Hypothèses enregistrées dans le Showcase, sans effet bancaire."
          : "Hypothèses du plan de trésorerie enregistrées et auditées.",
      );
      if (!isShowcaseMode) reload();
    };
  const previousSnapshot = data.cashBalances[0] || null,
    reconciliationFrom = previousSnapshot?.recorded_at || null,
    reconciliationStartDay = reconciliationFrom
      ? (() => {
          const value = new Date(reconciliationFrom);
          value.setDate(value.getDate() + 1);
          return parisDay(value);
        })()
      : null,
    knownReceipts = reconciliationStartDay
      ? (data.invoicePayments || [])
          .filter(
            (payment) =>
              payment.status === "confirmed" &&
              payment.received_on >= reconciliationStartDay &&
              payment.received_on <= today,
          )
          .reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
      : 0,
    knownExpenseOutflows = reconciliationStartDay
      ? data.expenses
          .filter(
            (expense) =>
              expense.status === "approved" &&
              expense.spent_on >= reconciliationStartDay &&
              expense.spent_on <= today,
          )
          .reduce(
            (sum, expense) => sum + Number(expense.amount_inc_vat || 0),
            0,
          )
      : 0,
    knownDirectorOutflows = reconciliationStartDay
      ? (data.directorCompensations || [])
          .filter(
            (entry) =>
              entry.status === "paid" &&
              !!entry.paid_on &&
              entry.paid_on >= reconciliationStartDay &&
              entry.paid_on <= today,
          )
          .reduce(
            (sum, entry) =>
              sum +
              Number(entry.net_salary || 0) +
              Number(entry.social_contributions || 0) +
              Number(entry.other_costs || 0) +
              Number(entry.dividends || 0),
            0,
          )
      : 0,
    knownOutflows = knownExpenseOutflows + knownDirectorOutflows,
    expectedNetChange = knownReceipts - knownOutflows,
    enteredBalance = balance === "" ? null : Number(balance),
    actualBalanceChange =
      enteredBalance == null || data.organization?.current_account_balance == null
        ? null
        : enteredBalance - Number(data.organization.current_account_balance),
    unexplainedDifference =
      actualBalanceChange == null || !previousSnapshot
        ? null
        : actualBalanceChange - expectedNetChange,
    projectionStart = new Date(`${today}T12:00:00`),
    addProjectionDays = (days: number) => {
      const value = new Date(projectionStart);
      value.setDate(value.getDate() + days);
      return value;
    },
    monthEnd = (value: Date) =>
      new Date(value.getFullYear(), value.getMonth() + 1, 0, 12),
    addChargePeriod = (value: Date, frequency: FixedCharge["frequency"]) => {
      const next = new Date(value);
      if (frequency === "weekly") next.setDate(next.getDate() + 7);
      else if (frequency === "quarterly") next.setMonth(next.getMonth() + 3);
      else if (frequency === "annual") next.setFullYear(next.getFullYear() + 1);
      else next.setMonth(next.getMonth() + 1);
      return next;
    },
    projectedFixedCharges = (until: Date) =>
      fixedCharges.reduce((sum, charge) => {
        if (!charge.active) return sum;
        const end = charge.ends_on
            ? new Date(`${charge.ends_on}T23:59:59`)
            : null,
          incVat =
            Number(charge.amount_ex_vat || 0) *
            (1 + Number(charge.vat_rate || 0) / 100);
        let occurrence = new Date(
            `${charge.next_due_on || charge.starts_on}T12:00:00`,
          ),
          guard = 0;
        while (occurrence < projectionStart && guard < 1000) {
          occurrence = addChargePeriod(occurrence, charge.frequency);
          guard += 1;
        }
        let total = 0;
        while (
          occurrence <= until &&
          (!end || occurrence <= end) &&
          guard < 1000
        ) {
          total += incVat;
          occurrence = addChargePeriod(occurrence, charge.frequency);
          guard += 1;
        }
        return sum + total;
      }, 0),
    payrollOccurrences = (until: Date) => {
      let occurrence = monthEnd(projectionStart),
        count = 0;
      while (occurrence <= until && count < 24) {
        count += 1;
        occurrence = monthEnd(
          new Date(occurrence.getFullYear(), occurrence.getMonth() + 1, 1, 12),
        );
      }
      return count;
    },
    expectedReceipts = (until: Date) =>
      data.invoices
        .filter((invoice) => {
          if (
            !["ready", "sent", "partially_paid", "overdue"].includes(
              invoice.status,
            )
          )
            return false;
          const expectedOn = invoice.due_on || invoice.issue_on || today;
          return new Date(`${expectedOn}T12:00:00`) <= until;
        })
        .reduce(
          (sum, invoice) =>
            sum +
            Math.max(
              0,
              Number(invoice.total_inc_vat || 0) -
                Number(invoice.amount_paid || 0),
            ),
          0,
        ),
    projectedDirectorCosts = (until: Date) =>
      (data.directorCompensations || [])
        .filter((entry) => {
          if (entry.status === "paid") return false;
          const period = new Date(`${entry.period.slice(0, 7)}-01T12:00:00`),
            due = monthEnd(period);
          return due >= projectionStart && due <= until;
        })
        .reduce(
          (sum, entry) =>
            sum +
            Number(entry.net_salary || 0) +
            Number(entry.social_contributions || 0) +
            Number(entry.other_costs || 0) +
            Number(entry.dividends || 0),
          0,
        ),
    projectedMissionCosts = (until: Date) => {
      const missionIds = new Set(
          data.missions
            .filter((mission) => {
              const starts = new Date(mission.starts_at);
              return (
                starts >= projectionStart &&
                starts <= until &&
                mission.status !== "cancelled"
              );
            })
            .map((mission) => mission.id),
        ),
        monthlyWorkerMonths = new Set<string>();
      return data.assignments
        .filter(
          (assignment) =>
            missionIds.has(assignment.mission_id) &&
            !["cancelled", "refused"].includes(assignment.status),
        )
        .reduce((sum, assignment) => {
          const worker = data.workers.find(
            (item) =>
              item.id === assignment.worker_id && item.kind === "contractor",
          );
          if (!worker) return sum;
          if (
            assignment.pricing_mode === "monthly" ||
            worker.pricing_mode === "monthly"
          ) {
            const mission = data.missions.find(
                (item) => item.id === assignment.mission_id,
              ),
              workerMonth =
                worker.id +
                "-" +
                (mission ? parisDay(mission.starts_at).slice(0, 7) : "");
            if (monthlyWorkerMonths.has(workerMonth)) return sum;
            monthlyWorkerMonths.add(workerMonth);
            return (
              sum + Number(worker.monthly_fee || assignment.agreed_amount || 0)
            );
          }
          if (assignment.agreed_amount != null)
            return sum + Number(assignment.agreed_amount);
          if (
            assignment.pricing_mode === "fixed" ||
            worker.pricing_mode === "fixed"
          )
            return (
              sum + Number(assignment.agreed_rate ?? worker.fixed_fee ?? 0)
            );
          return (
            sum +
            Number(assignment.assigned_hours || 0) *
              Number(assignment.agreed_rate ?? worker.hourly_rate ?? 0)
          );
        }, 0);
    },
    committedFutureMissionIds = new Set(
      data.assignments
        .filter((assignment) =>
          ["assigned", "accepted"].includes(assignment.status),
        )
        .map((assignment) => assignment.mission_id),
    ),
    projectedMissionReceipts = (until: Date) =>
      data.missions
        .filter((mission) => {
          const starts = new Date(mission.starts_at);
          return (
            starts >= projectionStart &&
            starts <= until &&
            mission.status !== "cancelled" &&
            mission.billing_status === "planned" &&
            committedFutureMissionIds.has(mission.id)
          );
        })
        .reduce((sum, mission) => {
          const contractualService = mission.property_service_id
              ? data.propertyServices.find(
                  (service) => service.id === mission.property_service_id,
                )
              : data.services.find((service) => service.id === mission.service_id),
            vatRate = Number(
              contractualService?.vat_rate ??
                data.organization?.default_vat_rate ??
                20,
            );
          return (
            sum +
            Number(mission.amount_ex_vat || 0) * (1 + vatRate / 100)
          );
        }, 0),
    cashProjections = [30, 60, 90].map((days) => {
      const until = addProjectionDays(days),
        invoicedReceipts = expectedReceipts(until),
        missionReceipts = projectedMissionReceipts(until),
        receipts = invoicedReceipts + missionReceipts,
        fixed = projectedFixedCharges(until),
        payroll = monthlyPayroll * payrollOccurrences(until),
        director = projectedDirectorCosts(until),
        missions = projectedMissionCosts(until),
        outflows = fixed + payroll + director + missions,
        startingBalance = data.organization?.current_account_balance;
      return {
        days,
        until,
        receipts,
        invoicedReceipts,
        missionReceipts,
        fixed,
        payroll,
        director,
        missions,
        outflows,
        balance:
          startingBalance == null
            ? null
            : Number(startingBalance) + receipts - outflows,
      };
    }),
    cashPlanBaseline =
      cashPlanSettings.baseline_monthly_receipts ?? observedMonthlyReceipts,
    cashPlanSafetyMonths = Math.min(
      12,
      Math.max(1, Number(cashPlanSettings.safety_months || 3)),
    ),
    cashPlanStructuralMonthlyCosts = monthlyFixedTotalIncVat + monthlyPayroll,
    cashPlanRedLine =
      cashPlanStructuralMonthlyCosts * cashPlanSafetyMonths,
    cashPlanSafetyFloor =
      cashPlanRedLine + Number(cashPlanSettings.low_season_reserve || 0),
    cashPlanRows = (() => {
      let runningBalance =
          data.organization?.current_account_balance == null
            ? null
            : Number(data.organization.current_account_balance),
        recurringHiringCost = 0;
      return Array.from({ length: 12 }, (_, index) => {
        const monthStart = new Date(
            projectionStart.getFullYear(),
            projectionStart.getMonth() + index,
            1,
            12,
          ),
          monthUntil = monthEnd(monthStart),
          previousMonthEnd = new Date(
            monthStart.getFullYear(),
            monthStart.getMonth(),
            0,
            12,
          ),
          key = parisDay(monthStart).slice(0, 7),
          adjustment = cashPlanSettings.monthly_adjustments?.[key] || {},
          season = adjustment.season || "normal",
          seasonFactor =
            season === "high"
              ? Number(cashPlanSettings.high_season_factor ?? 1)
              : season === "low"
                ? Number(cashPlanSettings.low_season_factor ?? 1)
                : 1,
          monthDays = monthUntil.getDate(),
          remainingDays =
            index === 0
              ? Math.max(1, monthDays - projectionStart.getDate() + 1)
              : monthDays,
          baselineReceipts =
            cashPlanBaseline * seasonFactor * (remainingDays / monthDays),
          receiptsBefore = index === 0 ? 0 : expectedReceipts(previousMonthEnd),
          missionReceiptsBefore =
            index === 0 ? 0 : projectedMissionReceipts(previousMonthEnd),
          missionReceipts = Math.max(
            0,
            projectedMissionReceipts(monthUntil) - missionReceiptsBefore,
          ),
          knownReceipts = Math.max(
            0,
            expectedReceipts(monthUntil) - receiptsBefore + missionReceipts,
          ),
          receipts = Math.max(
            knownReceipts,
            baselineReceipts + Number(adjustment.additional_receipts || 0),
          ),
          fixedBefore =
            index === 0 ? 0 : projectedFixedCharges(previousMonthEnd),
          fixed = Math.max(
            0,
            projectedFixedCharges(monthUntil) - fixedBefore,
          ),
          directorBefore =
            index === 0 ? 0 : projectedDirectorCosts(previousMonthEnd),
          director = Math.max(
            0,
            projectedDirectorCosts(monthUntil) - directorBefore,
          ),
          missionsBefore =
            index === 0 ? 0 : projectedMissionCosts(previousMonthEnd),
          missions = Math.max(
            0,
            projectedMissionCosts(monthUntil) - missionsBefore,
          );
        recurringHiringCost += Number(adjustment.planned_hiring_cost || 0);
        const payroll = monthlyPayroll + recurringHiringCost,
          investment = Number(adjustment.planned_investment || 0),
          otherOutflows = Number(adjustment.other_outflows || 0),
          outflows =
            fixed + payroll + director + missions + investment + otherOutflows;
        if (runningBalance != null) runningBalance += receipts - outflows;
        const significantInvestment =
            investment > 0 &&
            investment >=
              Number(cashPlanSettings.significant_spend_threshold || 0),
          cashBeforeSpendPassed =
            !significantInvestment ||
            (runningBalance != null && runningBalance >= cashPlanSafetyFloor);
        return {
          key,
          label: new Intl.DateTimeFormat("fr-FR", {
            month: "short",
            year: "2-digit",
          }).format(monthStart),
          season,
          receipts,
          knownReceipts,
          missionReceipts,
          fixed,
          payroll,
          director,
          missions,
          investment,
          otherOutflows,
          outflows,
          balance: runningBalance,
          significantInvestment,
          cashBeforeSpendPassed,
          note: adjustment.note || "",
        };
      });
    })(),
    cashPlanBalances = cashPlanRows
      .map((row) => row.balance)
      .filter((value): value is number => value != null),
    cashPlanMinimumBalance = cashPlanBalances.length
      ? Math.min(...cashPlanBalances)
      : null,
    cashPlanFirstBreach = cashPlanRows.find(
      (row) => row.balance != null && row.balance < cashPlanSafetyFloor,
    ),
    cashPlanInvestmentBlocks = cashPlanRows.filter(
      (row) => row.significantInvestment && !row.cashBeforeSpendPassed,
    ),
    availableAnalysisYears = Array.from(
      new Set([
        today.slice(0, 4),
        ...data.invoices
          .map((invoice) => invoice.issue_on?.slice(0, 4))
          .filter(Boolean),
        ...data.expenses
          .map((expense) => expense.spent_on.slice(0, 4))
          .filter(Boolean),
      ] as string[]),
    ).sort((a, b) => b.localeCompare(a)),
    contractorCostForMonth = (key: string) => {
      const missionIds = new Set(
          data.missions
            .filter(
              (mission) =>
                parisDay(mission.starts_at).startsWith(key) &&
                mission.status !== "cancelled",
            )
            .map((mission) => mission.id),
        ),
        monthlyWorkers = new Set<string>();
      return data.assignments
        .filter(
          (assignment) =>
            missionIds.has(assignment.mission_id) &&
            !["cancelled", "refused"].includes(assignment.status),
        )
        .reduce((sum, assignment) => {
          const worker = data.workers.find(
            (item) =>
              item.id === assignment.worker_id && item.kind === "contractor",
          );
          if (!worker) return sum;
          if (
            assignment.pricing_mode === "monthly" ||
            worker.pricing_mode === "monthly"
          ) {
            if (monthlyWorkers.has(worker.id)) return sum;
            monthlyWorkers.add(worker.id);
            return (
              sum + Number(worker.monthly_fee || assignment.agreed_amount || 0)
            );
          }
          if (assignment.agreed_amount != null)
            return sum + Number(assignment.agreed_amount);
          if (
            assignment.pricing_mode === "fixed" ||
            worker.pricing_mode === "fixed"
          )
            return (
              sum + Number(assignment.agreed_rate ?? worker.fixed_fee ?? 0)
            );
          return (
            sum +
            Number(assignment.assigned_hours || 0) *
              Number(assignment.agreed_rate ?? worker.hourly_rate ?? 0)
          );
        }, 0);
    },
    selectedMonthStart = `${selectedAccountingMonth}-01`,
    selectedMonthEnd = parisDay(
      new Date(
        Number(selectedAccountingMonth.slice(0, 4)),
        Number(selectedAccountingMonth.slice(5, 7)),
        0,
        12,
      ),
    ),
    chargeOccurrencesForMonth = (charge: FixedCharge) => {
      if (!charge.active || charge.starts_on > selectedMonthEnd)
        return 0;
      const contractEnd = charge.ends_on || "9999-12-31";
      if (contractEnd < selectedMonthStart) return 0;
      let occurrence = new Date(`${charge.starts_on}T12:00:00`),
        count = 0,
        guard = 0;
      const monthStartDate = new Date(`${selectedMonthStart}T12:00:00`),
        monthEndDate = new Date(`${selectedMonthEnd}T12:00:00`);
      while (occurrence < monthStartDate && guard < 2000) {
        occurrence = addChargePeriod(occurrence, charge.frequency);
        guard += 1;
      }
      while (
        occurrence <= monthEndDate &&
        parisDay(occurrence) <= contractEnd &&
        guard < 2000
      ) {
        count += 1;
        occurrence = addChargePeriod(occurrence, charge.frequency);
        guard += 1;
      }
      return count;
    },
    selectedMonthPayments = (data.invoicePayments || []).filter(
      (payment) =>
        payment.status === "confirmed" &&
        payment.received_on.startsWith(selectedAccountingMonth),
    ),
    selectedMonthExpenses = data.expenses.filter(
      (expense) =>
        expense.spent_on.startsWith(selectedAccountingMonth) &&
        !["cancelled", "rejected"].includes(expense.status),
    ),
    selectedMonthDirectorPaid = (data.directorCompensations || []).filter(
      (entry) =>
        entry.status === "paid" &&
        !!entry.paid_on &&
        entry.paid_on.startsWith(selectedAccountingMonth),
    ),
    selectedMonthActualReceipts = selectedMonthPayments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0,
    ),
    selectedMonthActualOutflows =
      selectedMonthExpenses
        .filter((expense) => expense.status === "approved")
        .reduce(
          (sum, expense) => sum + Number(expense.amount_inc_vat || 0),
          0,
        ) +
      selectedMonthDirectorPaid.reduce(
        (sum, entry) =>
          sum +
          Number(entry.net_salary || 0) +
          Number(entry.social_contributions || 0) +
          Number(entry.other_costs || 0) +
          Number(entry.dividends || 0),
        0,
      ),
    selectedMonthFixedForecast = fixedCharges.reduce(
      (sum, charge) =>
        sum +
        chargeOccurrencesForMonth(charge) *
          Number(charge.amount_ex_vat || 0) *
          (1 + Number(charge.vat_rate || 0) / 100),
      0,
    ),
    selectedMonthContractorForecast = contractorCostForMonth(
      selectedAccountingMonth,
    ),
    selectedMonthDirectorForecast = (data.directorCompensations || [])
      .filter(
        (entry) =>
          entry.period.startsWith(selectedAccountingMonth) &&
          entry.status !== "paid",
      )
      .reduce(
        (sum, entry) =>
          sum +
          Number(entry.net_salary || 0) +
          Number(entry.social_contributions || 0) +
          Number(entry.other_costs || 0) +
          Number(entry.dividends || 0),
        0,
      ),
    selectedMonthExpectedReceipts = data.invoices
      .filter(
        (invoice) =>
          !["paid", "cancelled", "credited"].includes(invoice.status) &&
          (invoice.due_on || invoice.issue_on || invoice.billing_period || "").startsWith(
            selectedAccountingMonth,
          ),
      )
      .reduce(
        (sum, invoice) =>
          sum +
          Math.max(
            0,
            Number(invoice.total_inc_vat || 0) -
              Number(invoice.amount_paid || 0),
          ),
        0,
      ),
    selectedMonthForecastOutflows =
      selectedMonthFixedForecast +
      monthlyPayroll +
      selectedMonthContractorForecast +
      selectedMonthDirectorForecast,
    selectedMonthLedger = [
      ...selectedMonthPayments.map((payment) => {
        const invoice = data.invoices.find(
            (item) => item.id === payment.invoice_id,
          ),
          client = data.clients.find((item) => item.id === invoice?.client_id);
        return {
          date: payment.received_on,
          direction: "Encaissement",
          category: "Règlement client",
          label: `${client?.name || "Client"} · ${invoice?.number || "Facture brouillon"}`,
          actual: Number(payment.amount || 0),
          forecast: 0,
          status: "Confirmé",
        };
      }),
      ...selectedMonthExpenses.map((expense) => ({
        date: expense.spent_on,
        direction: "Décaissement",
        category: expense.category,
        label: `${expense.merchant || "Fournisseur"} · ${expense.description}`,
        actual:
          expense.status === "approved"
            ? -Number(expense.amount_inc_vat || 0)
            : 0,
        forecast:
          expense.status === "approved"
            ? 0
            : -Number(expense.amount_inc_vat || 0),
        status: expense.status === "approved" ? "Comptabilisé" : "À valider",
      })),
      ...selectedMonthDirectorPaid.map((entry) => ({
        date: entry.paid_on || entry.period,
        direction: "Décaissement",
        category: "Dirigeant",
        label: "Rémunération et coûts associés",
        actual: -(
          Number(entry.net_salary || 0) +
          Number(entry.social_contributions || 0) +
          Number(entry.other_costs || 0) +
          Number(entry.dividends || 0)
        ),
        forecast: 0,
        status: "Versé",
      })),
      ...fixedCharges
        .filter((charge) => chargeOccurrencesForMonth(charge) > 0)
        .map((charge) => ({
          date: selectedMonthStart,
          direction: "Décaissement",
          category: "Charge fixe",
          label: `${charge.vendor || "Fournisseur"} · ${charge.name} · ${chargeOccurrencesForMonth(charge)} échéance(s)`,
          actual: 0,
          forecast: -(
            chargeOccurrencesForMonth(charge) *
            Number(charge.amount_ex_vat || 0) *
            (1 + Number(charge.vat_rate || 0) / 100)
          ),
          status: "Prévisionnel",
        })),
      ...(monthlyPayroll > 0
        ? [
            {
              date: selectedMonthEnd,
              direction: "Décaissement",
              category: "Salaires",
              label: "Salaires, coûts employeur et tickets restaurant",
              actual: 0,
              forecast: -monthlyPayroll,
              status: "Prévisionnel",
            },
          ]
        : []),
      ...(selectedMonthContractorForecast > 0
        ? [
            {
              date: selectedMonthEnd,
              direction: "Décaissement",
              category: "Sous-traitance",
              label: "Missions et forfaits ST affectés",
              actual: 0,
              forecast: -selectedMonthContractorForecast,
              status: "Estimation opérationnelle",
            },
          ]
        : []),
      ...(selectedMonthDirectorForecast > 0
        ? [
            {
              date: selectedMonthEnd,
              direction: "Décaissement",
              category: "Dirigeant",
              label: "Rémunération dirigeant prévue ou validée",
              actual: 0,
              forecast: -selectedMonthDirectorForecast,
              status: "Prévisionnel",
            },
          ]
        : []),
      ...data.invoices
        .filter(
          (invoice) =>
            !["paid", "cancelled", "credited"].includes(invoice.status) &&
            (invoice.due_on || invoice.issue_on || invoice.billing_period || "").startsWith(
              selectedAccountingMonth,
            ),
        )
        .map((invoice) => ({
          date:
            invoice.due_on || invoice.issue_on || invoice.billing_period || selectedMonthEnd,
          direction: "Encaissement",
          category: "Facture attendue",
          label: `${data.clients.find((client) => client.id === invoice.client_id)?.name || "Client"} · ${invoice.number || "Brouillon"}`,
          actual: 0,
          forecast: Math.max(
            0,
            Number(invoice.total_inc_vat || 0) -
              Number(invoice.amount_paid || 0),
          ),
          status: "À encaisser",
        })),
    ].sort((a, b) => a.date.localeCompare(b.date)),
    financialMonths = Array.from({ length: 12 }, (_, monthIndex) => {
      const key = analysisYear + "-" + String(monthIndex + 1).padStart(2, "0"),
        monthStart = key + "-01",
        monthStarted = monthStart <= today,
        monthEndDate = parisDay(
          new Date(Number(analysisYear), monthIndex + 1, 0, 12),
        ),
        revenue = data.invoices
          .filter(
            (invoice) =>
              (invoice.issue_on || invoice.billing_period || "").startsWith(
                key,
              ) && invoice.status !== "cancelled",
          )
          .reduce(
            (sum, invoice) =>
              sum +
              (invoice.document_kind === "credit" ? -1 : 1) *
                Number(invoice.total_ex_vat || 0),
            0,
          ),
        operating = data.expenses
          .filter(
            (expense) =>
              expense.spent_on.startsWith(key) &&
              !["cancelled", "rejected"].includes(expense.status),
          )
          .reduce(
            (sum, expense) => sum + Number(expense.amount_ex_vat || 0),
            0,
          ),
        recurring = monthStarted
          ? fixedCharges
              .filter(
                (charge) =>
                  charge.starts_on <= monthEndDate &&
                  (!charge.ends_on || charge.ends_on >= monthStart),
              )
              .reduce((sum, charge) => sum + monthlyEquivalent(charge), 0)
          : 0,
        leadership = monthStarted
          ? (data.directorCompensations || [])
              .filter((entry) => entry.period.startsWith(key))
              .reduce(
                (sum, entry) =>
                  sum +
                  Number(entry.net_salary || 0) +
                  Number(entry.social_contributions || 0) +
                  Number(entry.other_costs || 0),
                0,
              )
          : 0,
        contractor = monthStarted ? contractorCostForMonth(key) : 0,
        costs =
          operating +
          recurring +
          (monthStarted ? monthlyPayroll : 0) +
          leadership +
          contractor;
      return {
        key,
        label: new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
          new Date(Number(analysisYear), monthIndex, 1),
        ),
        revenue,
        costs,
        margin: revenue - costs,
      };
    }),
    financialYearRevenue = financialMonths.reduce(
      (sum, row) => sum + row.revenue,
      0,
    ),
    financialYearCosts = financialMonths.reduce(
      (sum, row) => sum + row.costs,
      0,
    ),
    financialChartMax = Math.max(
      1,
      ...financialMonths.flatMap((row) => [row.revenue, row.costs]),
    ),
    overdueBalance = data.invoices
      .filter(
        (invoice) =>
          invoice.status === "overdue" ||
          (invoice.status === "sent" &&
            !!invoice.due_on &&
            invoice.due_on < today),
      )
      .reduce(
        (sum, invoice) =>
          sum +
          Math.max(
            0,
            Number(invoice.total_inc_vat || 0) -
              Number(invoice.amount_paid || 0),
          ),
        0,
      ),
    invoicesInExport = data.invoices.filter((invoice) => {
      const date = invoice.issue_on || invoice.billing_period;
      return (
        !!date &&
        date >= exportFrom &&
        date <= exportTo &&
        invoice.status !== "cancelled"
      );
    }),
    paymentsInExport = (data.invoicePayments || []).filter(
      (payment) =>
        payment.received_on >= exportFrom &&
        payment.received_on <= exportTo &&
        payment.status !== "cancelled",
    ),
    expensesInExport = data.expenses.filter(
      (expense) =>
        expense.spent_on >= exportFrom &&
        expense.spent_on <= exportTo &&
        !["cancelled", "rejected"].includes(expense.status),
    ),
    directorInExport = (data.directorCompensations || []).filter(
      (entry) =>
        entry.status === "paid" &&
        !!entry.paid_on &&
        entry.paid_on >= exportFrom &&
        entry.paid_on <= exportTo,
    ),
    exportVatCollected = invoicesInExport.reduce((sum, invoice) => {
      const sign = invoice.document_kind === "credit" ? -1 : 1;
      return sum + sign * Number(invoice.total_vat || 0);
    }, 0),
    exportVatDeductible = expensesInExport.reduce(
      (sum, expense) => sum + Number(expense.vat_amount || 0),
      0,
    );
  const downloadAccountingExport = () => {
    if (!exportFrom || !exportTo || exportFrom > exportTo) {
      setMessage("La période d’export est invalide.");
      return;
    }
    const columns = [
        "Date",
        "Type",
        "Référence",
        "Tiers",
        "Libellé",
        "Montant HT",
        "TVA",
        "Montant TTC",
        "Encaissement",
        "Décaissement",
        "Statut",
        "Pièces jointes",
        "Identifiant ARD",
      ],
      rows: Array<Array<string | number>> = [],
      clientName = (clientId: string) =>
        data.clients.find((client) => client.id === clientId)?.name ||
        "Client non identifié",
      invoiceById = new Map(
        data.invoices.map((invoice) => [invoice.id, invoice]),
      ),
      attachmentNames = (invoiceId: string) =>
        (data.commercialAttachments || [])
          .filter((attachment) => attachment.invoice_id === invoiceId)
          .map((attachment) => {
            const document = data.documents.find(
              (item) => item.id === attachment.document_id,
            );
            return (
              attachment.label || document?.original_name || "Pièce jointe"
            );
          })
          .join(" | ");
    invoicesInExport.forEach((invoice) => {
      const sign = invoice.document_kind === "credit" ? -1 : 1;
      rows.push([
        invoice.issue_on || invoice.billing_period || "",
        invoice.document_kind === "credit" ? "Avoir" : "Facture",
        invoice.number || "Brouillon",
        clientName(invoice.client_id),
        invoice.document_kind === "credit"
          ? invoice.credit_reason || "Avoir client"
          : "Vente / prestation",
        sign * Number(invoice.total_ex_vat || 0),
        sign * Number(invoice.total_vat || 0),
        sign * Number(invoice.total_inc_vat || 0),
        0,
        0,
        invoice.status,
        attachmentNames(invoice.id),
        invoice.id,
      ]);
    });
    paymentsInExport.forEach((payment) => {
      const invoice = invoiceById.get(payment.invoice_id);
      rows.push([
        payment.received_on,
        "Règlement client",
        payment.reference || invoice?.number || "",
        invoice ? clientName(invoice.client_id) : "Client non identifié",
        `Règlement${payment.method ? ` · ${payment.method}` : ""}`,
        0,
        0,
        Number(payment.amount || 0),
        Number(payment.amount || 0),
        0,
        payment.status,
        invoice ? attachmentNames(invoice.id) : "",
        payment.id,
      ]);
    });
    expensesInExport.forEach((expense) => {
      rows.push([
        expense.spent_on,
        "Dépense",
        "",
        expense.merchant || "Fournisseur non renseigné",
        `${expense.category} · ${expense.description}`,
        Number(expense.amount_ex_vat || 0),
        Number(expense.vat_amount || 0),
        Number(expense.amount_inc_vat || 0),
        0,
        Number(expense.amount_inc_vat || 0),
        expense.status,
        expense.receipt_path || "",
        expense.id,
      ]);
    });
    directorInExport.forEach((entry) => {
      const amount =
        Number(entry.net_salary || 0) +
        Number(entry.social_contributions || 0) +
        Number(entry.other_costs || 0) +
        Number(entry.dividends || 0);
      rows.push([
        entry.paid_on || entry.period,
        "Rémunération dirigeant",
        entry.period.slice(0, 7),
        profile.display_name,
        "Net, cotisations, autres coûts et dividendes versés",
        amount,
        0,
        amount,
        0,
        amount,
        entry.status,
        "",
        entry.id,
      ]);
    });
    rows.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    const decimal = (value: string | number) =>
        typeof value === "number" ? value.toFixed(2).replace(".", ",") : value,
      escapeCsv = (value: string | number) =>
        `"${String(decimal(value)).replaceAll('"', '""')}"`,
      summary = [
        ["EXPORT COMPTABLE ARD MANAGER"],
        [
          "Société",
          data.organization?.legal_name || data.organization?.name || "",
        ],
        ["Période", exportFrom, exportTo],
        ["TVA collectée", exportVatCollected],
        ["TVA déductible saisie", exportVatDeductible],
        ["TVA nette indicative", exportVatCollected - exportVatDeductible],
        [
          "Avertissement",
          "Export de préparation à contrôler par le cabinet comptable ; les pièces privées restent accessibles dans ARD Manager.",
        ],
        [],
      ],
      csv = [...summary, columns, ...rows]
        .map((row) => row.map(escapeCsv).join(";"))
        .join("\r\n"),
      blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
      url = URL.createObjectURL(blob),
      link = document.createElement("a");
    link.href = url;
    link.download = `export-comptable-${exportFrom}-${exportTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setMessage(
      `Export comptable généré : ${rows.length} ligne(s), TVA nette indicative ${money(exportVatCollected - exportVatDeductible)}.`,
    );
  };
  const selectedMonthFlowBars: ExecutiveBarItem[] = [
      {
        label: "Encaissé",
        value: selectedMonthActualReceipts,
        display: money(selectedMonthActualReceipts),
        tone: "positive",
        detail: "Règlements confirmés",
      },
      {
        label: "Décaissé",
        value: selectedMonthActualOutflows,
        display: money(selectedMonthActualOutflows),
        tone: "slate",
        detail: "Sorties comptabilisées",
      },
      {
        label: "À encaisser",
        value: selectedMonthExpectedReceipts,
        display: money(selectedMonthExpectedReceipts),
        tone: "gold",
        detail: "Échéances connues",
      },
      {
        label: "À décaisser",
        value: selectedMonthForecastOutflows,
        display: money(selectedMonthForecastOutflows),
        tone: "danger",
        detail: "Prévisionnel connu",
      },
    ],
    selectedMonthOutflowSlices: ExecutiveDonutSlice[] = [
      {
        label: "Charges fixes",
        value: selectedMonthFixedForecast,
        color: "var(--ard-gold)",
      },
      {
        label: "Salaires",
        value: monthlyPayroll,
        color: "#667085",
      },
      {
        label: "Sous-traitance",
        value: selectedMonthContractorForecast,
        color: "#b6743d",
      },
      {
        label: "Dirigeant",
        value: selectedMonthDirectorForecast,
        color: "#a7463f",
      },
    ];
  const saveExpense = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingExpense) return;
    const amountIncVat = Number(editingExpense.amount_inc_vat || 0),
      vatRate = Number(editingExpense.vat_rate || 0),
      amountExVat = amountIncVat / (1 + Math.max(0, vatRate) / 100),
      vatAmount = amountIncVat - amountExVat;
    setBusy(true);
    setExpenseMessage("");
    const originalExpense = data.expenses.find(
        (item) => item.id === editingExpense.id,
      ),
      decisionRecorded =
        originalExpense?.status !== editingExpense.status &&
        ["approved", "rejected", "cancelled"].includes(editingExpense.status);
    const { error } = await supabase!
      .from("expense_reports")
      .update({
        spent_on: editingExpense.spent_on,
        category: editingExpense.category,
        description: editingExpense.description.trim(),
        merchant: editingExpense.merchant?.trim() || null,
        payment_method: editingExpense.payment_method,
        amount_inc_vat: amountIncVat,
        amount_ex_vat: amountExVat,
        vat_rate: vatRate,
        vat_amount: vatAmount,
        status: editingExpense.status,
        ...(decisionRecorded
          ? { reviewed_by: profile.id, reviewed_at: new Date().toISOString() }
          : {}),
      })
      .eq("id", editingExpense.id);
    setBusy(false);
    setExpenseMessage(error ? error.message : "Dépense mise à jour.");
    if (!error) {
      reload();
      setTimeout(() => setEditingExpense(null), 450);
    }
  };
  const recordExpenseReimbursement = async () => {
    if (!editingExpense) return;
    if (!reimbursementDate || !reimbursementReference.trim()) {
      setExpenseMessage(
        "Renseignez la date réelle et la référence du remboursement.",
      );
      return;
    }
    setBusy(true);
    setExpenseMessage("");
    const { error } = await supabase!.rpc("record_expense_reimbursement", {
      p_expense_report_id: editingExpense.id,
      p_reimbursed_on: reimbursementDate,
      p_reimbursement_reference: reimbursementReference.trim(),
    });
    setBusy(false);
    setExpenseMessage(
      error
        ? error.message
        : "Remboursement réel enregistré. Aucun virement n’a été déclenché.",
    );
    if (!error) {
      reload();
      setEditingExpense((value) =>
        value
          ? {
              ...value,
              reimbursement_status: "paid",
              reimbursed_on: reimbursementDate,
              reimbursement_reference: reimbursementReference.trim(),
            }
          : value,
      );
    }
  };
  const saveFixedCharge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCharge.name?.trim()) return;
    setBusy(true);
    setMessage("");
    const payload = {
      organization_id: profile.organization_id,
      name: editingCharge.name.trim(),
      vendor: editingCharge.vendor?.trim() || null,
      category: editingCharge.category || "other",
      amount_ex_vat: Number(editingCharge.amount_ex_vat || 0),
      vat_rate: Number(editingCharge.vat_rate || 0),
      frequency: editingCharge.frequency || "monthly",
      starts_on: editingCharge.starts_on || today,
      ends_on: editingCharge.ends_on || null,
      next_due_on: editingCharge.next_due_on || null,
      payment_method: editingCharge.payment_method?.trim() || null,
      notes: editingCharge.notes?.trim() || null,
      active: editingCharge.active !== false,
    };
    const result = editingCharge.id
      ? await supabase!
          .from("recurring_fixed_charges")
          .update(payload)
          .eq("id", editingCharge.id)
      : await supabase!.from("recurring_fixed_charges").insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editingCharge.id
          ? "Charge fixe mise à jour."
          : "Charge fixe créée.",
    );
    if (!result.error) {
      setEditingCharge({ ...blankCharge });
      reload();
    }
  };
  const toggleFixedCharge = async (charge: FixedCharge) => {
    setBusy(true);
    const { error } = await supabase!
      .from("recurring_fixed_charges")
      .update({ active: !charge.active })
      .eq("id", charge.id);
    setBusy(false);
    setMessage(
      error
        ? error.message
        : charge.active
          ? "Charge fixe arrêtée."
          : "Charge fixe réactivée.",
    );
    if (!error) reload();
  };
  const cashPlanChartValues = cashPlanBalances.length
      ? [...cashPlanBalances, cashPlanSafetyFloor]
      : [0, cashPlanSafetyFloor],
    cashPlanChartMin = Math.min(...cashPlanChartValues),
    cashPlanChartMax = Math.max(...cashPlanChartValues),
    cashPlanChartRange = Math.max(1, cashPlanChartMax - cashPlanChartMin),
    cashPlanBalancePath = smoothChartPath(
      cashPlanRows
        .filter((row) => row.balance != null)
        .map((row, index, values) => ({
          x: values.length === 1 ? 50 : (index / (values.length - 1)) * 100,
          y:
            94 -
            ((Number(row.balance) - cashPlanChartMin) / cashPlanChartRange) * 88,
        })),
    ),
    cashPlanSafetyY =
      94 -
      ((cashPlanSafetyFloor - cashPlanChartMin) / cashPlanChartRange) * 88;
  async function updateBalance(e: React.FormEvent) {
    e.preventDefault();
    const next = +balance,
      previous = data.organization?.current_account_balance ?? null,
      difference = previous == null ? null : next - previous;
    setBusy(true);
    const snapshot = await supabase!.from("cash_balance_snapshots").insert({
      organization_id: profile.organization_id,
      balance: next,
      previous_balance: previous,
      difference,
      known_receipts: previousSnapshot ? knownReceipts : null,
      known_outflows: previousSnapshot ? knownOutflows : null,
      expected_net_change: previousSnapshot ? expectedNetChange : null,
      unexplained_difference:
        previousSnapshot && difference != null
          ? difference - expectedNetChange
          : null,
      reconciliation_from: reconciliationFrom,
      notes: notes || null,
      created_by: profile.id,
    });
    const updated = snapshot.error
      ? null
      : await supabase!
          .from("organizations")
          .update({
            current_account_balance: next,
            current_account_updated_at: new Date().toISOString(),
          })
          .eq("id", profile.organization_id);
    setBusy(false);
    const error = snapshot.error || updated?.error;
    setMessage(
      error
        ? error.message
        : difference == null
          ? "Solde initial enregistré."
          : `Solde mis à jour : variation ${money(difference)}, dont ${money(difference - expectedNetChange)} reste à expliquer avec les opérations actuellement renseignées.`,
    );
    if (!error) {
      setNotes("");
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Pilotage financier</span>
          <h1>Comptabilité & trésorerie</h1>
        </div>
      </div>
      <Notice>
        Henrri permet de lire l’état des factures et paiements, mais pas le
        solde du compte bancaire. Le solde courant nécessite une connexion
        bancaire dédiée ou une saisie contrôlée ici.
      </Notice>
      <div className="stats">
        <article>
          <span>Solde bancaire renseigné</span>
          <strong>
            {data.organization?.current_account_balance == null
              ? "Non renseigné"
              : money(data.organization.current_account_balance)}
          </strong>
          <small>
            {data.organization?.current_account_updated_at
              ? `Mis à jour le ${frDate(data.organization.current_account_updated_at, true)}`
              : "Solde initial à saisir"}
          </small>
        </article>
        <article>
          <span>Dépenses du mois</span>
          <strong>
            {money(expenses.reduce((s, e) => s + e.amount_inc_vat, 0))}
          </strong>
        </article>
        <article>
          <span>Charges fixes mensuelles HT</span>
          <strong>{money(monthlyFixedTotal)}</strong>
          <small>{activeFixedCharges.length} charge(s) active(s)</small>
        </article>
        <article>
          <span>Salaires et tickets mensuels</span>
          <strong>{money(monthlyPayroll)}</strong>
          <small>
            {employees.some((worker) => worker.employer_cost_monthly == null)
              ? "Lecture incomplète : certains coûts employeur manquent"
              : "Coûts employeur configurés"}
          </small>
        </article>
        <article>
          <span>TVA déductible saisie</span>
          <strong>
            {money(expenses.reduce((s, e) => s + e.vat_amount, 0))}
          </strong>
        </article>
        <article>
          <span>Factures impayées</span>
          <strong>
            {money(
              data.invoices
                .filter((i) =>
                  ["sent", "partially_paid", "overdue"].includes(i.status),
                )
                .reduce(
                  (s, i) =>
                    s + Math.max(0, i.total_inc_vat - (i.amount_paid || 0)),
                  0,
                ),
            )}
          </strong>
        </article>
      </div>
      <details className="billing-kpi-guide accounting-kpi-guide" data-testid="accounting-kpi-guide">
        <summary>Comprendre les chiffres comptables affichés</summary>
        <p className="section-intro">
          Ces montants servent au pilotage courant. Ils restent séparés entre
          données réellement saisies et équivalents mensuels de prévision.
        </p>
        <div className="billing-kpi-guide-grid">
          <article>
            <strong>Solde bancaire renseigné</strong>
            <span>Dernier solde saisi et daté dans ARD Manager. Henrri ne fournit pas le solde du compte bancaire.</span>
          </article>
          <article>
            <strong>Dépenses du mois</strong>
            <span>Somme TTC des dépenses datées du mois courant, hors dépenses annulées ou refusées.</span>
          </article>
          <article>
            <strong>Charges fixes mensuelles HT</strong>
            <span>Charges actives ramenées à un mois : hebdomadaire × 52 ÷ 12, trimestrielle ÷ 3 ou annuelle ÷ 12.</span>
          </article>
          <article>
            <strong>Salaires et tickets mensuels</strong>
            <span>Coût employeur mensuel des salariés actifs, ou salaire net si ce coût manque, augmenté des tickets renseignés.</span>
          </article>
          <article>
            <strong>TVA déductible saisie</strong>
            <span>Somme de la TVA enregistrée sur les dépenses retenues du mois courant. Une pièce absente reste hors calcul.</span>
          </article>
          <article>
            <strong>Factures impayées</strong>
            <span>Reste TTC des factures envoyées, partiellement payées ou en retard : total TTC − montant déjà réglé.</span>
          </article>
        </div>
        <p className="billing-kpi-guide-limit">
          Limite : cette synthèse ne remplace ni le relevé bancaire ni la validation du cabinet comptable. Les données manquantes restent signalées et ne sont pas inventées.
        </p>
      </details>
      <div
        className="decision-view-switcher accounting-view-switcher"
        aria-label="Choisir une vue comptable"
        role="navigation"
        data-testid="accounting-view-switcher"
      >
        {[
          {
            id: "month" as const,
            label: "Mois en cours",
            description: "Flux réels, prévisions et mouvements du mois choisi",
          },
          {
            id: "forecast" as const,
            label: "Prévision & trésorerie",
            description: "Santé annuelle, plan sur 12 mois et projection à 90 jours",
          },
          {
            id: "settings" as const,
            label: "Paramètres & charges",
            description: "Solde, charges fixes, salaires et export comptable",
          },
          {
            id: "expenses" as const,
            label: "Dépenses",
            description: `${data.expenses.length} dépense(s) et note(s) de frais`,
          },
        ].map((view) => (
          <button
            type="button"
            key={view.id}
            className={accountingView === view.id ? "active" : ""}
            aria-pressed={accountingView === view.id}
            onClick={() => setAccountingView(view.id)}
          >
            <strong>{view.label}</strong>
            <span>{view.description}</span>
          </button>
        ))}
      </div>
      {accountingView === "month" && (
      <section className="panel table-wrap accounting-month-ledger">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Lecture mensuelle</span>
            <h2>Encaissements, décaissements et prévisionnel</h2>
            <p className="section-intro">
              Les montants réellement confirmés restent séparés des échéances
              prévues. Ainsi, une charge fixe attendue n’est jamais présentée
              comme déjà débitée et un brouillon n’est jamais assimilé à un
              encaissement.
            </p>
          </div>
          <label className="compact-filter">
            Mois analysé
            <input
              type="month"
              value={selectedAccountingMonth}
              onChange={(event) => setSelectedAccountingMonth(event.target.value)}
            />
          </label>
        </div>
        <div className="health-indicators">
          <article>
            <span>Encaissements confirmés</span>
            <strong>{money(selectedMonthActualReceipts)}</strong>
          </article>
          <article>
            <span>Décaissements comptabilisés</span>
            <strong>{money(selectedMonthActualOutflows)}</strong>
          </article>
          <article>
            <span>Solde réel renseigné du mois</span>
            <strong
              className={
                selectedMonthActualReceipts - selectedMonthActualOutflows < 0
                  ? "negative"
                  : ""
              }
            >
              {money(selectedMonthActualReceipts - selectedMonthActualOutflows)}
            </strong>
          </article>
          <article>
            <span>Reste à encaisser prévu</span>
            <strong>{money(selectedMonthExpectedReceipts)}</strong>
          </article>
          <article>
            <span>Sorties prévisionnelles</span>
            <strong>{money(selectedMonthForecastOutflows)}</strong>
          </article>
          <article>
            <span>Solde mensuel après prévisions</span>
            <strong
              className={
                selectedMonthActualReceipts -
                    selectedMonthActualOutflows +
                    selectedMonthExpectedReceipts -
                    selectedMonthForecastOutflows <
                0
                  ? "negative"
                  : ""
              }
            >
              {money(
                selectedMonthActualReceipts -
                  selectedMonthActualOutflows +
                  selectedMonthExpectedReceipts -
                  selectedMonthForecastOutflows,
              )}
            </strong>
          </article>
        </div>
        <div className="executive-reading-grid">
          <article className="executive-reading-card">
            <div className="executive-reading-heading">
              <div>
                <span className="eyebrow">Flux du mois</span>
                <h3>Réel et prévisionnel comparés</h3>
              </div>
            </div>
            <ExecutiveBars
              label={`Comparaison des encaissements et décaissements pour ${selectedAccountingMonth}`}
              items={selectedMonthFlowBars}
            />
          </article>
          <article className="executive-reading-card">
            <div className="executive-reading-heading">
              <div>
                <span className="eyebrow">Sorties à venir</span>
                <h3>Composition du prévisionnel</h3>
              </div>
            </div>
            <ExecutiveDonut
              label={`Répartition des sorties prévisionnelles pour ${selectedAccountingMonth}`}
              slices={selectedMonthOutflowSlices}
              centerLabel="à décaisser"
              centerValue={compactMoney(selectedMonthForecastOutflows)}
            />
          </article>
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Sens</th>
              <th>Type</th>
              <th>Libellé</th>
              <th>Réel</th>
              <th>Prévisionnel</th>
              <th>État</th>
            </tr>
          </thead>
          <tbody>
            {selectedMonthLedger.map((row, index) => (
              <tr key={`${row.date}-${row.category}-${index}`}>
                <td>{frDate(row.date)}</td>
                <td>{row.direction}</td>
                <td>{row.category}</td>
                <td>{row.label}</td>
                <td>{row.actual ? money(row.actual) : "—"}</td>
                <td>{row.forecast ? money(row.forecast) : "—"}</td>
                <td>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!selectedMonthLedger.length && (
          <p className="empty">
            Aucun flux réel ou prévisionnel connu pour ce mois.
          </p>
        )}
        <p className="projection-note">
          Cette lecture est un outil de pilotage, pas un relevé bancaire. Les
          paiements Henrri sont lus puis rapprochés après contrôle ; les charges
          fixes, salaires et coûts ST restent prévisionnels tant qu’aucun flux
          réel n’est confirmé.
        </p>
      </section>
      )}
      {accountingView === "forecast" && (
        <>
      <section className="panel financial-health-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Santé de l’entreprise</span>
            <h2>CA et coûts connus mois par mois</h2>
          </div>
          <label className="compact-filter">
            Exercice
            <select
              value={analysisYear}
              onChange={(event) => setAnalysisYear(event.target.value)}
            >
              {availableAnalysisYears.map((year) => (
                <option key={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="health-indicators">
          <article>
            <span>CA HT</span>
            <strong>{money(financialYearRevenue)}</strong>
          </article>
          <article>
            <span>Coûts connus</span>
            <strong>{money(financialYearCosts)}</strong>
          </article>
          <article>
            <span>Marge de gestion</span>
            <strong
              className={
                financialYearRevenue - financialYearCosts < 0 ? "negative" : ""
              }
            >
              {money(financialYearRevenue - financialYearCosts)}
            </strong>
          </article>
          <article>
            <span>Retards à encaisser</span>
            <strong className={overdueBalance > 0 ? "negative" : ""}>
              {money(overdueBalance)}
            </strong>
          </article>
        </div>
        <div
          className="financial-chart"
          role="img"
          aria-label={
            "Comparaison mensuelle du chiffre d’affaires et des coûts " +
            analysisYear
          }
        >
          {financialMonths.map((row) => (
            <div className="financial-month" key={row.key}>
              <div className="financial-bars">
                <span
                  className="revenue-bar"
                  style={{
                    height:
                      Math.max(2, (row.revenue / financialChartMax) * 100) +
                      "%",
                  }}
                  title={"CA " + money(row.revenue)}
                />
                <span
                  className="cost-bar"
                  style={{
                    height:
                      Math.max(2, (row.costs / financialChartMax) * 100) + "%",
                  }}
                  title={"Coûts " + money(row.costs)}
                />
              </div>
              <small>{row.label}</small>
            </div>
          ))}
        </div>
        <div className="chart-legend">
          <span>
            <i className="revenue-dot" /> CA HT facturé
          </span>
          <span>
            <i className="cost-dot" /> Coûts connus
          </span>
        </div>
        <p className="projection-note">
          Les coûts connus regroupent dépenses saisies, charges récurrentes,
          salaires et tickets, dirigeant et sous-traitance affectée. Les mois
          futurs de l’exercice ne sont pas comptés dans cette lecture
          historique.
        </p>
      </section>
      <section className="panel cash-plan-12m" data-testid="cash-plan-12m">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Décider avant de dépenser</span>
            <h2>Plan de trésorerie glissant sur 12 mois</h2>
            <p>
              Encaissements prudents, saisonnalité, charges, recrutements et
              investissements sont séparés pour rendre chaque hypothèse lisible.
            </p>
          </div>
          <button className="secondary" type="button" onClick={openCashPlanSettings}>
            Régler les hypothèses
          </button>
        </div>
        <div className="cash-plan-summary">
          <article>
            <span>Base mensuelle d’encaissements</span>
            <strong>{money(cashPlanBaseline)}</strong>
            <small>
              {cashPlanSettings.baseline_monthly_receipts == null
                ? `${observedReceiptMonths.size} mois actif(s) confirmé(s)`
                : "Hypothèse validée par l’administrateur"}
            </small>
          </article>
          <article>
            <span>Ligne rouge de sécurité</span>
            <strong>{money(cashPlanRedLine)}</strong>
            <small>
              {cashPlanSafetyMonths} mois de charges fixes TTC et salariés
            </small>
          </article>
          <article>
            <span>Réserve basse saison</span>
            <strong>{money(Number(cashPlanSettings.low_season_reserve || 0))}</strong>
            <small>Ajoutée à la ligne rouge, jamais confondue avec le solde</small>
          </article>
          <article className={cashPlanFirstBreach ? "is-warning" : "is-positive"}>
            <span>Point bas projeté</span>
            <strong>
              {cashPlanMinimumBalance == null
                ? "Solde à renseigner"
                : money(cashPlanMinimumBalance)}
            </strong>
            <small>
              {cashPlanFirstBreach
                ? `Sous le seuil dès ${cashPlanFirstBreach.label}`
                : "Au-dessus du seuil sur la période"}
            </small>
          </article>
        </div>
        {data.organization?.current_account_balance == null ? (
          <Notice tone="info">
            Renseignez le solde bancaire actuel pour calculer la trajectoire et
            appliquer la règle « cash avant dépense ».
          </Notice>
        ) : cashPlanFirstBreach ? (
          <Notice tone="warning">
            La projection passe sous le seuil de sécurité en {cashPlanFirstBreach.label}.
            Les dépenses et hypothèses de ce mois doivent être revues avant engagement.
          </Notice>
        ) : (
          <Notice tone="success">
            Selon les hypothèses actuelles, la trésorerie reste au-dessus de la
            ligne rouge augmentée de la réserve basse saison.
          </Notice>
        )}
        <div className="cash-plan-chart-card">
          <div className="cash-plan-chart-heading">
            <div>
              <h3>Trajectoire du solde disponible</h3>
              <small>Courbe projetée et seuil de sécurité à ne pas franchir</small>
            </div>
            <div className="cash-plan-chart-legend" aria-label="Légende du plan de trésorerie">
              <span><i className="balance" /> Solde projeté</span>
              <span><i className="safety" /> Ligne rouge + réserve</span>
            </div>
          </div>
          <svg
            className="cash-plan-chart"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-label="Projection du solde de trésorerie sur douze mois"
          >
            <line
              className="cash-plan-safety-line"
              x1="0"
              x2="100"
              y1={cashPlanSafetyY}
              y2={cashPlanSafetyY}
              vectorEffect="non-scaling-stroke"
            />
            {cashPlanBalancePath && (
              <path
                className="cash-plan-balance-line"
                d={cashPlanBalancePath}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        </div>
        <div className="cash-plan-months" aria-label="Prévisions mensuelles détaillées">
          {cashPlanRows.map((row) => (
            <article
              key={row.key}
              className={`${row.balance != null && row.balance < cashPlanSafetyFloor ? "is-warning" : ""} ${row.significantInvestment && !row.cashBeforeSpendPassed ? "is-blocked" : ""}`}
            >
              <div className="cash-plan-month-head">
                <strong>{row.label}</strong>
                <span className={`season-${row.season}`}>
                  {row.season === "high"
                    ? "Haute saison"
                    : row.season === "low"
                      ? "Basse saison"
                      : "Saison normale"}
                </span>
              </div>
              <dl>
                <div className="positive"><dt>Encaissements</dt><dd>+ {money(row.receipts)}</dd></div>
                <div className="positive"><dt>Dont missions engagées</dt><dd>+ {money(row.missionReceipts)}</dd></div>
                <div><dt>Sorties prévues</dt><dd>− {money(row.outflows)}</dd></div>
                <div className="cash-plan-month-balance"><dt>Solde projeté</dt><dd>{row.balance == null ? "À renseigner" : money(row.balance)}</dd></div>
              </dl>
              {row.investment > 0 && (
                <p className={row.cashBeforeSpendPassed ? "cash-approved" : "cash-blocked"}>
                  Investissement {money(row.investment)} · {row.cashBeforeSpendPassed
                    ? "compatible avec le seuil"
                    : "à reporter, réduire ou financer"}
                </p>
              )}
              {row.note && <small>{row.note}</small>}
            </article>
          ))}
        </div>
        <p className="projection-note">
          Les échéances clients et les missions futures déjà affectées alimentent
          le prévisionnel. Leur chiffre d’affaires TTC estimé et leurs coûts ST
          restent séparés du réalisé jusqu’à la validation administrative. Le coût
          d’un recrutement est récurrent à partir de son mois de démarrage. Ce plan
          reste une hypothèse de gestion : il ne vaut ni relevé bancaire ni
          prévision comptable certifiée.
        </p>
        {!!cashPlanInvestmentBlocks.length && (
          <p className="data-warning">
            {cashPlanInvestmentBlocks.length} investissement(s) significatif(s)
            ne respecte(nt) pas actuellement la règle « cash avant dépense ».
          </p>
        )}
      </section>
      <section className="panel cash-projection-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Vision prévisionnelle</span>
            <h2>Trésorerie projetée à 30, 60 et 90 jours</h2>
          </div>
          <small>Calcul arrêté au {frDate(today)}</small>
        </div>
        {data.organization?.current_account_balance == null && (
          <Notice tone="info">
            Renseignez le solde courant pour obtenir les soldes projetés. Les
            entrées et sorties prévisionnelles restent consultables ci-dessous.
          </Notice>
        )}
        <div className="cash-projection-grid">
          {cashProjections.map((projection) => (
            <article key={projection.days}>
              <div className="projection-heading">
                <div>
                  <span>À {projection.days} jours</span>
                  <small>
                    jusqu’au {frDate(projection.until.toISOString())}
                  </small>
                </div>
                <strong
                  className={
                    projection.balance != null && projection.balance < 0
                      ? "negative"
                      : ""
                  }
                >
                  {projection.balance == null
                    ? "Solde à renseigner"
                    : money(projection.balance)}
                </strong>
              </div>
              <dl className="projection-breakdown">
                <div className="positive">
                  <dt>Encaissements attendus</dt>
                  <dd>+ {money(projection.receipts)}</dd>
                </div>
                <div>
                  <dt>Charges fixes TTC</dt>
                  <dd>− {money(projection.fixed)}</dd>
                </div>
                <div>
                  <dt>Salaires et tickets</dt>
                  <dd>− {money(projection.payroll)}</dd>
                </div>
                <div>
                  <dt>Dirigeant prévu</dt>
                  <dd>− {money(projection.director)}</dd>
                </div>
                <div>
                  <dt>Missions / sous-traitance</dt>
                  <dd>− {money(projection.missions)}</dd>
                </div>
                <div className="projection-total">
                  <dt>Sorties prévues</dt>
                  <dd>− {money(projection.outflows)}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        <p className="projection-note">
          Hypothèses : factures à leur échéance, paie en fin de mois, charges
          selon leur prochaine échéance et coûts ST des missions planifiées. Les
          achats futurs non saisis, impôts et aléas ne sont pas estimés.
        </p>
      </section>
      {cashPlanOpen && (
        <div className="modal-backdrop">
          <form
            className="modal-card portal-detail cash-plan-settings-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Régler le plan de trésorerie"
            onSubmit={(event) => {
              event.preventDefault();
              void saveCashPlanSettings();
            }}
          >
            <button
              type="button"
              className="icon-close"
              aria-label="Fermer"
              onClick={() => setCashPlanOpen(false)}
            >
              ×
            </button>
            <span className="eyebrow">Hypothèses contrôlées</span>
            <h2>Régler le plan de trésorerie</h2>
            <p>
              Aucun montant saisi ici n’est un flux bancaire réel. Les
              hypothèses servent uniquement à comparer les décisions avant de
              les engager.
            </p>
            <div className="cash-plan-global-settings">
              <label>
                Encaissements habituels par mois (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={money(observedMonthlyReceipts)}
                  value={cashPlanDraft.baseline_monthly_receipts ?? ""}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      baseline_monthly_receipts:
                        event.target.value === "" ? null : +event.target.value,
                    }))
                  }
                />
                <small>Laisser vide pour utiliser la moyenne des mois actifs confirmés.</small>
              </label>
              <label>
                Nombre de mois protégés
                <input
                  type="number"
                  min="1"
                  max="12"
                  step="1"
                  value={cashPlanDraft.safety_months ?? 3}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      safety_months: +event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Réserve dédiée à la basse saison (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashPlanDraft.low_season_reserve ?? 0}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      low_season_reserve: +event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Dépense significative à partir de (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashPlanDraft.significant_spend_threshold ?? 0}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      significant_spend_threshold: +event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Encaissements en haute saison (% d’un mois normal)
                <input
                  type="number"
                  min="0"
                  max="500"
                  step="1"
                  value={Math.round(Number(cashPlanDraft.high_season_factor ?? 1) * 100)}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      high_season_factor: +event.target.value / 100,
                    }))
                  }
                />
              </label>
              <label>
                Encaissements en basse saison (% d’un mois normal)
                <input
                  type="number"
                  min="0"
                  max="500"
                  step="1"
                  value={Math.round(Number(cashPlanDraft.low_season_factor ?? 1) * 100)}
                  onChange={(event) =>
                    setCashPlanDraft((current) => ({
                      ...current,
                      low_season_factor: +event.target.value / 100,
                    }))
                  }
                />
              </label>
            </div>
            <h3>Mois par mois</h3>
            <div className="cash-plan-month-settings">
              {cashPlanRows.map((row) => {
                const adjustment =
                  cashPlanDraft.monthly_adjustments?.[row.key] || {};
                return (
                  <article key={row.key}>
                    <h4>{row.label}</h4>
                    <label>
                      Niveau d’activité
                      <select
                        value={adjustment.season || "normal"}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "season",
                            event.target.value,
                          )
                        }
                      >
                        <option value="low">Basse saison</option>
                        <option value="normal">Saison normale</option>
                        <option value="high">Haute saison</option>
                      </select>
                    </label>
                    <label>
                      Encaissements supplémentaires (€)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={adjustment.additional_receipts || 0}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "additional_receipts",
                            +event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      Coût mensuel des recrutements démarrant ici (€)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={adjustment.planned_hiring_cost || 0}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "planned_hiring_cost",
                            +event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      Investissement ponctuel (€)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={adjustment.planned_investment || 0}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "planned_investment",
                            +event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      Autres sorties ponctuelles (€)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={adjustment.other_outflows || 0}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "other_outflows",
                            +event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      Explication
                      <input
                        value={adjustment.note || ""}
                        onChange={(event) =>
                          updateCashPlanMonth(
                            row.key,
                            "note",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  </article>
                );
              })}
            </div>
            <label className="cash-plan-notes">
              Hypothèses générales et décisions à valider
              <textarea
                value={cashPlanDraft.notes || ""}
                onChange={(event) =>
                  setCashPlanDraft((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
              />
            </label>
            {cashPlanMessage && (
              <Notice tone={cashPlanMessage.includes("enregistr") ? "success" : "error"}>
                {cashPlanMessage}
              </Notice>
            )}
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setCashPlanOpen(false)}>
                Fermer
              </button>
              <button disabled={cashPlanBusy}>
                {cashPlanBusy ? "Enregistrement…" : "Enregistrer les hypothèses"}
              </button>
            </div>
          </form>
        </div>
      )}
        </>
      )}
      {accountingView === "settings" && (
        <>
      <section className="panel accounting-export-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Transmission au cabinet</span>
            <h2>Export comptable Novantys</h2>
          </div>
          <strong>
            {invoicesInExport.length +
              paymentsInExport.length +
              expensesInExport.length +
              directorInExport.length}{" "}
            ligne(s)
          </strong>
        </div>
        <p className="section-intro">
          Prépare un journal CSV contrôlable dans Excel : factures, avoirs,
          règlements, dépenses, rémunération dirigeant versée, TVA et références
          des pièces. Le format final pourra être adapté aux consignes
          techniques de Novantys.
        </p>
        <div className="export-controls">
          <label>
            Du
            <input
              type="date"
              value={exportFrom}
              max={exportTo}
              onChange={(event) => setExportFrom(event.target.value)}
            />
          </label>
          <label>
            Au
            <input
              type="date"
              value={exportTo}
              min={exportFrom}
              onChange={(event) => setExportTo(event.target.value)}
            />
          </label>
          <button onClick={downloadAccountingExport}>
            Télécharger l’export CSV
          </button>
        </div>
        <div className="export-summary">
          <span>{invoicesInExport.length} facture(s) / avoir(s)</span>
          <span>{paymentsInExport.length} règlement(s)</span>
          <span>{expensesInExport.length} dépense(s)</span>
          <span>TVA collectée {money(exportVatCollected)}</span>
          <span>TVA déductible {money(exportVatDeductible)}</span>
        </div>
        <Notice>
          Les salaires estimés et charges fixes prévisionnelles ne sont pas
          comptabilisés comme des paiements réels. Les bulletins et
          justificatifs restent consultables dans les fiches concernées.
        </Notice>
      </section>
      <section className="panel">
        <h2>Mettre à jour le solde courant</h2>
        <form className="form-grid" onSubmit={updateBalance}>
          <label>
            Nouveau solde bancaire (€)
            <input
              type="number"
              step="0.01"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              required
            />
          </label>
          <label>
            Commentaire de rapprochement
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Écart identifié, opération en attente…"
            />
          </label>
          <button disabled={busy}>
            {busy ? "Mise à jour…" : "Comparer et enregistrer le solde"}
          </button>
        </form>
        {previousSnapshot && actualBalanceChange != null && (
          <div className="reconciliation-preview">
            <div>
              <span>Variation du solde saisi</span>
              <strong>{money(actualBalanceChange)}</strong>
            </div>
            <div>
              <span>Encaissements confirmés connus</span>
              <strong>+ {money(knownReceipts)}</strong>
            </div>
            <div>
              <span>Décaissements saisis et validés</span>
              <strong>− {money(knownOutflows)}</strong>
            </div>
            <div>
              <span>Variation attendue</span>
              <strong>{money(expectedNetChange)}</strong>
            </div>
            <div className={Math.abs(unexplainedDifference || 0) > 0.01 ? "warning" : "ok"}>
              <span>Écart restant à identifier</span>
              <strong>{money(unexplainedDifference || 0)}</strong>
            </div>
            <small>
              Calcul depuis le lendemain du dernier relevé ({frDate(
                previousSnapshot.recorded_at,
              )}). Il utilise uniquement les règlements confirmés, dépenses
              approuvées et rémunérations dirigeant marquées comme versées. Les
              mouvements bancaires absents doivent être ajoutés ou expliqués
              dans le commentaire.
            </small>
          </div>
        )}
        {message && (
          <Notice
            tone={
              message.startsWith("Solde") ||
              message.includes("généré") ||
              message.includes("créée") ||
              message.includes("mise à jour") ||
              message.includes("arrêtée") ||
              message.includes("réactivée")
                ? "success"
                : "error"
            }
          >
            {message}
          </Notice>
        )}
        <div className="balance-history">
          {data.cashBalances.slice(0, 8).map((s) => (
            <div key={s.id}>
              <span>
                {frDate(s.recorded_at, true)} · {money(s.balance)}
              </span>
              <strong>
                {s.difference == null
                  ? "Solde initial"
                  : `Écart ${money(s.difference)}`}
              </strong>
              <small>{s.notes || "Sans commentaire"}</small>
              {s.unexplained_difference != null && (
                <small>
                  Connu : +{money(s.known_receipts || 0)} / −
                  {money(s.known_outflows || 0)} · non expliqué {money(
                    s.unexplained_difference,
                  )}
                </small>
              )}
            </div>
          ))}
        </div>
      </section>
      <section className="panel fixed-charge-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Charges récurrentes</span>
            <h2>Frais fixes courants</h2>
          </div>
          <strong>{money(monthlyFixedTotal)} HT / mois</strong>
        </div>
        <p className="section-intro">
          Banque, loyer, assurances, abonnements et autres contrats récurrents.
          La date de fin permet d’arrêter automatiquement leur projection.
        </p>
        <div className="fixed-charge-layout">
          <form className="form-grid" onSubmit={saveFixedCharge}>
            <label>
              Intitulé
              <input
                value={editingCharge.name || ""}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    name: e.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Fournisseur
              <input
                value={editingCharge.vendor || ""}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    vendor: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Catégorie
              <select
                value={editingCharge.category || "other"}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    category: e.target.value,
                  }))
                }
              >
                <option value="bank">Banque</option>
                <option value="rent">Loyer</option>
                <option value="insurance">Assurance</option>
                <option value="subscription">Abonnement</option>
                <option value="vehicle">Véhicule</option>
                <option value="energy">Énergie</option>
                <option value="telecom">Télécom</option>
                <option value="accounting">Comptable</option>
                <option value="tax">Taxe</option>
                <option value="other">Autre</option>
              </select>
            </label>
            <label>
              Montant HT par échéance
              <input
                type="number"
                min="0"
                step="0.01"
                value={editingCharge.amount_ex_vat || 0}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    amount_ex_vat: +e.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              TVA (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={editingCharge.vat_rate ?? 20}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    vat_rate: +e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Fréquence
              <select
                value={editingCharge.frequency || "monthly"}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    frequency: e.target.value as FixedCharge["frequency"],
                  }))
                }
              >
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuelle</option>
                <option value="quarterly">Trimestrielle</option>
                <option value="annual">Annuelle</option>
              </select>
            </label>
            <label>
              Début
              <input
                type="date"
                value={editingCharge.starts_on || today}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    starts_on: e.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Fin de récurrence
              <input
                type="date"
                value={editingCharge.ends_on || ""}
                min={editingCharge.starts_on || today}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    ends_on: e.target.value || null,
                  }))
                }
              />
            </label>
            <label>
              Prochaine échéance
              <input
                type="date"
                value={editingCharge.next_due_on || ""}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    next_due_on: e.target.value || null,
                  }))
                }
              />
            </label>
            <label>
              Mode de paiement
              <input
                value={editingCharge.payment_method || ""}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    payment_method: e.target.value,
                  }))
                }
              />
            </label>
            <label className="wide">
              Notes
              <input
                value={editingCharge.notes || ""}
                onChange={(e) =>
                  setEditingCharge((value) => ({
                    ...value,
                    notes: e.target.value,
                  }))
                }
              />
            </label>
            <div className="row-actions wide">
              {editingCharge.id && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setEditingCharge({ ...blankCharge })}
                >
                  Annuler la modification
                </button>
              )}
              <button disabled={busy}>
                {editingCharge.id
                  ? "Enregistrer la charge"
                  : "Ajouter la charge fixe"}
              </button>
            </div>
          </form>
          <div className="recurrence-list">
            {fixedCharges.map((charge) => (
              <article
                key={charge.id}
                className={!charge.active ? "muted" : ""}
              >
                <div>
                  <strong>{charge.name}</strong>
                  <small>
                    {charge.vendor || "Sans fournisseur"} ·{" "}
                    {charge.frequency === "weekly"
                      ? "hebdomadaire"
                      : charge.frequency === "quarterly"
                        ? "trimestrielle"
                        : charge.frequency === "annual"
                          ? "annuelle"
                          : "mensuelle"}{" "}
                    · {money(charge.amount_ex_vat)} HT
                  </small>
                  <small>
                    Du {frDate(charge.starts_on)}
                    {charge.ends_on
                      ? " au " + frDate(charge.ends_on)
                      : " sans date de fin"}
                  </small>
                </div>
                <div className="row-actions">
                  <button
                    className="secondary compact"
                    onClick={() => setEditingCharge({ ...charge })}
                  >
                    Modifier
                  </button>
                  <button
                    className="secondary compact"
                    disabled={busy}
                    onClick={() => toggleFixedCharge(charge)}
                  >
                    {charge.active ? "Arrêter" : "Réactiver"}
                  </button>
                </div>
              </article>
            ))}
            {!fixedCharges.length && (
              <p className="empty">Aucune charge fixe définie.</p>
            )}
          </div>
        </div>
      </section>
      <section className="panel table-wrap">
        <h2>Salaires et avantages mensuels</h2>
        <table>
          <thead>
            <tr>
              <th>Employé</th>
              <th>Salaire net</th>
              <th>Coût employeur</th>
              <th>Tickets restaurant</th>
              <th>Coût retenu</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((worker) => (
              <tr key={worker.id}>
                <td>{workerName(worker)}</td>
                <td>{money(worker.net_salary || 0)}</td>
                <td>
                  {worker.employer_cost_monthly == null
                    ? "À renseigner"
                    : money(worker.employer_cost_monthly)}
                </td>
                <td>
                  {worker.ticket_count || 0} × {money(worker.ticket_value || 0)}
                </td>
                <td>
                  {money(
                    Number(
                      worker.employer_cost_monthly ?? worker.net_salary ?? 0,
                    ) +
                      Number(worker.ticket_count || 0) *
                        Number(worker.ticket_value || 0),
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!employees.length && <p className="empty">Aucun salarié actif.</p>}
      </section>
        </>
      )}
      {accountingView === "expenses" && (
      <section className="panel table-wrap">
        <h2>Dépenses et notes de frais</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Catégorie</th>
              <th>HT</th>
              <th>TVA</th>
              <th>TTC</th>
              <th>État</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.expenses.map((e) => (
              <tr key={e.id}>
                <td>{frDate(e.spent_on)}</td>
                <td>{e.description}</td>
                <td>{e.category}</td>
                <td>{money(e.amount_ex_vat)}</td>
                <td>{money(e.vat_amount)}</td>
                <td>{money(e.amount_inc_vat)}</td>
                <td>
                  <Status value={e.status} />
                </td>
                <td>
                  <button
                    className="secondary compact"
                    onClick={() => {
                      setExpenseMessage("");
                      setEditingExpense({ ...e });
                      setReimbursementDate(e.reimbursed_on || today);
                      setReimbursementReference(
                        e.reimbursement_reference || "",
                      );
                    }}
                  >
                    Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.expenses.length && (
          <p className="empty">Aucune dépense enregistrée.</p>
        )}
      </section>
      )}
      {editingExpense && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Contrôle comptable</span>
                <h2>Modifier la dépense</h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setEditingExpense(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <form className="form-grid modal-form" onSubmit={saveExpense}>
              <label>
                Date
                <input
                  type="date"
                  value={editingExpense.spent_on}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, spent_on: event.target.value }
                        : value,
                    )
                  }
                  required
                />
              </label>
              <label>
                Catégorie
                <select
                  value={editingExpense.category}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, category: event.target.value }
                        : value,
                    )
                  }
                >
                  <option value="meal">Repas</option>
                  <option value="purchase">Achat</option>
                  <option value="travel">Déplacement</option>
                  <option value="fixed">Charge fixe</option>
                  <option value="subcontracting">Sous-traitance</option>
                  <option value="tax">Taxe</option>
                  <option value="other">Autre</option>
                </select>
              </label>
              <label className="wide">
                Description
                <input
                  value={editingExpense.description}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, description: event.target.value }
                        : value,
                    )
                  }
                  required
                />
              </label>
              <label>
                Fournisseur
                <input
                  value={editingExpense.merchant || ""}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, merchant: event.target.value }
                        : value,
                    )
                  }
                />
              </label>
              <label>
                Paiement
                <select
                  value={editingExpense.payment_method}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, payment_method: event.target.value }
                        : value,
                    )
                  }
                >
                  <option value="company_card">Carte société</option>
                  <option value="personal_card">Carte personnelle</option>
                  <option value="cash">Espèces</option>
                  <option value="transfer">Virement</option>
                  <option value="direct_debit">Prélèvement</option>
                </select>
              </label>
              <label>
                Montant TTC (€)
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editingExpense.amount_inc_vat}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, amount_inc_vat: +event.target.value }
                        : value,
                    )
                  }
                  required
                />
              </label>
              <label>
                TVA (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editingExpense.vat_rate}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value
                        ? { ...value, vat_rate: +event.target.value }
                        : value,
                    )
                  }
                />
              </label>
              <label>
                État
                <select
                  value={editingExpense.status}
                  onChange={(event) =>
                    setEditingExpense((value) =>
                      value ? { ...value, status: event.target.value } : value,
                    )
                  }
                >
                  {(data.expenses.find(
                    (item) => item.id === editingExpense.id,
                  )?.status === "pending" ||
                    editingExpense.status === "pending") && (
                    <option value="pending">À valider</option>
                  )}
                  <option value="approved">Validée</option>
                  <option value="rejected">Refusée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </label>
              {editingExpense.status === "approved" &&
                editingExpense.payment_method === "personal_card" && (
                  <section className="detail-note wide">
                    <h3>Remboursement de la dépense personnelle</h3>
                    {editingExpense.reimbursement_status === "paid" ? (
                      <Notice tone="success">
                        Remboursée le {frDate(editingExpense.reimbursed_on || "")} ·
                        référence {editingExpense.reimbursement_reference || "—"}
                      </Notice>
                    ) : (
                      <>
                        <Notice tone="info">
                          ARD Manager enregistre uniquement un remboursement déjà
                          effectué. Aucun virement n’est déclenché ici.
                        </Notice>
                        <div className="form-grid compact-form">
                          <label>
                            Date réelle
                            <input
                              type="date"
                              value={reimbursementDate}
                              onChange={(event) =>
                                setReimbursementDate(event.target.value)
                              }
                            />
                          </label>
                          <label>
                            Référence du paiement
                            <input
                              value={reimbursementReference}
                              onChange={(event) =>
                                setReimbursementReference(event.target.value)
                              }
                              placeholder="Ex. VIR-2026-104"
                            />
                          </label>
                        </div>
                        <button
                          type="button"
                          className="secondary"
                          disabled={busy}
                          onClick={recordExpenseReimbursement}
                        >
                          Enregistrer le remboursement réel
                        </button>
                      </>
                    )}
                  </section>
                )}
              <div className="row-actions wide">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setEditingExpense(null)}
                >
                  Annuler
                </button>
                <button disabled={busy}>
                  {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </form>
            {expenseMessage && (
              <Notice
                tone={
                  expenseMessage.includes("mise à jour") ? "success" : "error"
                }
              >
                {expenseMessage}
              </Notice>
            )}
          </section>
        </div>
      )}
    </>
  );
}

type ClientScore = {
  revenue: number;
  costs: number;
  margin: number;
  marginRate: number;
  overdue: number;
  recurrences: number;
  score: number;
  components: { label: string; value: number; weight: number }[];
  reasons: string[];
};

type ProfitabilityLevel = "client" | "property" | "service" | "mission";
type ProfitabilityRow = {
  id: string;
  label: string;
  clientName: string;
  missions: number;
  revenue: number;
  contractorCost: number;
  employeeCost: number;
  mileageCost: number;
  stockCost: number;
  expenses: number;
  referralCost: number;
  totalCost: number;
  margin: number;
  marginRate: number;
  missingCost: boolean;
  serviceScope?: "client" | "property";
  serviceId?: string;
  clientId?: string;
  propertyId?: string | null;
};

const financialGlossary = [
  {
    term: "Marge brute réelle",
    formula: "CA HT − coûts directs connus",
    definition:
      "Ce qu’il reste après les coûts directement rattachables à la prestation : intervenants, produits, IK, achats mission et apport commercial facturé.",
  },
  {
    term: "Taux de marge",
    formula: "Marge brute réelle ÷ CA HT",
    definition:
      "La part du chiffre d’affaires conservée après les coûts directs. Elle permet de comparer des prestations de tailles différentes.",
  },
  {
    term: "Marge plancher",
    formula: "Seuil minimal défini par l’entreprise",
    definition:
      "Le niveau sous lequel une prestation doit être corrigée, renégociée ou refusée avant sa réalisation.",
  },
  {
    term: "Marge nette",
    formula: "Résultat net ÷ CA HT",
    definition:
      "La rentabilité finale après toutes les charges, impôts et éléments comptables. Elle doit être validée avec l’expert-comptable.",
  },
  {
    term: "Résultat provisoire",
    formula: "Produits − charges enregistrées",
    definition:
      "Une estimation de pilotage à l’instant T, distincte du résultat comptable officiel tant que les écritures ne sont pas clôturées.",
  },
  {
    term: "BFR",
    formula: "Stocks + créances − dettes d’exploitation",
    definition:
      "Le besoin de trésorerie créé par le décalage entre les dépenses engagées et les encaissements reçus.",
  },
];

function AdminProfitability({
  data,
  onOpenServicePricing,
}: {
  data: DataState;
  onOpenServicePricing?: (focus: BillingCatalogFocus) => void;
}) {
  const today = parisDay(new Date()),
    fiscalStartMonth = Math.min(
      12,
      Math.max(1, Number(data.organization?.fiscal_year_start_month || 1)),
    ),
    fiscalRange = (offset = 0, closeAtToday = false) => {
      const todayYear = Number(today.slice(0, 4)),
        todayMonth = Number(today.slice(5, 7)),
        startYear =
          todayYear + (todayMonth >= fiscalStartMonth ? 0 : -1) + offset,
        from = `${startYear}-${String(fiscalStartMonth).padStart(2, "0")}-01`,
        fiscalEnd = new Date(
          Date.UTC(startYear + 1, fiscalStartMonth - 1, 0),
        ),
        fullTo = fiscalEnd.toISOString().slice(0, 10);
      return { from, to: closeAtToday && fullTo > today ? today : fullTo };
    },
    currentFiscal = fiscalRange(0, true),
    [from, setFrom] = useState(currentFiscal.from),
    [to, setTo] = useState(today),
    [periodPreset, setPeriodPreset] = useState<
      "current" | "previous" | "rolling" | "custom"
    >("current"),
    [level, setLevel] = useState<ProfitabilityLevel>("client"),
    [clientFilter, setClientFilter] = useState(""),
    [sort, setSort] = useState("margin-asc"),
    [activeProfitabilityView, setActiveProfitabilityView] = useState<
      "overview" | "analysis"
    >("overview");
  const applyPeriod = (preset: "current" | "previous" | "rolling") => {
    if (preset === "rolling") {
      const rollingStart = new Date(`${today}T12:00:00Z`);
      rollingStart.setUTCMonth(rollingStart.getUTCMonth() - 11, 1);
      setFrom(rollingStart.toISOString().slice(0, 10));
      setTo(today);
    } else {
      const range = fiscalRange(preset === "previous" ? -1 : 0, true);
      setFrom(range.from);
      setTo(preset === "previous" ? fiscalRange(-1).to : range.to);
    }
    setPeriodPreset(preset);
  };
  const assignmentCost = (assignment: Assignment) => {
      const worker = data.workers.find(
        (item) => item.id === assignment.worker_id,
      );
      if (!worker) return { contractor: 0, employee: 0, missing: true };
      if (worker.kind === "contractor") {
        if (
          assignment.pricing_mode === "monthly" ||
          worker.pricing_mode === "monthly"
        ) {
          const mission = data.missions.find(
              (item) => item.id === assignment.mission_id,
            ),
            monthKey = mission ? parisDay(mission.starts_at).slice(0, 7) : "",
            monthlyAssignments = data.assignments.filter((item) => {
              if (
                item.worker_id !== worker.id ||
                ["cancelled", "refused"].includes(item.status)
              )
                return false;
              const assignedMission = data.missions.find(
                (candidate) => candidate.id === item.mission_id,
              );
              return (
                !!assignedMission &&
                assignedMission.status !== "cancelled" &&
                parisDay(assignedMission.starts_at).startsWith(monthKey)
              );
            }).length;
          return {
            contractor:
              Number(worker.monthly_fee || assignment.agreed_amount || 0) /
              Math.max(1, monthlyAssignments),
            employee: 0,
            missing:
              worker.monthly_fee == null && assignment.agreed_amount == null,
          };
        }
        if (assignment.agreed_amount != null)
          return {
            contractor: Number(assignment.agreed_amount),
            employee: 0,
            missing: false,
          };
        if (
          assignment.pricing_mode === "fixed" ||
          worker.pricing_mode === "fixed"
        )
          return {
            contractor: Number(assignment.agreed_rate ?? worker.fixed_fee ?? 0),
            employee: 0,
            missing: assignment.agreed_rate == null && worker.fixed_fee == null,
          };
        const rate = assignment.agreed_rate ?? worker.hourly_rate;
        return {
          contractor:
            Number(assignment.assigned_hours || 0) * Number(rate || 0),
          employee: 0,
          missing: rate == null,
        };
      }
      const monthlyHours = Number(worker.monthly_hours || 0),
        employerCost = worker.employer_cost_monthly,
        ticketCost =
          Number(worker.ticket_count || 0) * Number(worker.ticket_value || 0),
        hourlyKnownCost =
          monthlyHours > 0
            ? (Number(employerCost ?? worker.net_salary ?? 0) + ticketCost) /
              monthlyHours
            : 0;
      return {
        contractor: 0,
        employee: Number(assignment.assigned_hours || 0) * hourlyKnownCost,
        missing: monthlyHours <= 0 || employerCost == null,
      };
    },
    missions = data.missions.filter((mission) => {
      const date = parisDay(mission.starts_at);
      return (
        date >= from &&
        date <= to &&
        mission.status !== "cancelled" &&
        mission.admin_validated_at != null &&
        (!clientFilter || mission.client_id === clientFilter)
      );
    }),
    missionRows = missions.map((mission) => {
      const client = data.clients.find((item) => item.id === mission.client_id),
        property = data.properties.find(
          (item) => item.id === mission.property_id,
        ),
        propertyService = data.propertyServices.find(
          (item) => item.id === mission.property_service_id,
        ),
        service = data.services.find((item) => item.id === mission.service_id),
        assignments = data.assignments.filter(
          (item) =>
            item.mission_id === mission.id &&
            !["cancelled", "refused"].includes(item.status),
        ),
        labor = assignments.reduce(
          (total, assignment) => {
            const cost = assignmentCost(assignment);
            return {
              contractor: total.contractor + cost.contractor,
              employee: total.employee + cost.employee,
              missing: total.missing || cost.missing,
            };
          },
          { contractor: 0, employee: 0, missing: false },
        ),
        directExpenses = data.expenses
          .filter(
            (expense) =>
              expense.mission_id === mission.id &&
              !["cancelled", "rejected"].includes(expense.status),
          )
          .reduce(
            (sum, expense) => sum + Number(expense.amount_ex_vat || 0),
            0,
          ),
        missionMileage = data.mileages.filter(
          (entry) =>
            entry.mission_id === mission.id &&
            entry.status === "approved",
        ),
        mileageCost = missionMileage.reduce(
          (sum, entry) => sum + Number(entry.calculated_amount || 0),
          0,
        ),
        mileageIncomplete = missionMileage.some(
          (entry) => entry.calculated_amount == null,
        ),
        stockMovements = (data.inventoryMovements || []).filter(
          (movement) =>
            movement.mission_id === mission.id &&
            movement.movement_type === "usage",
        ),
        stockCost = stockMovements.reduce(
          (sum, movement) =>
            sum + Number(movement.quantity || 0) * Number(movement.unit_cost || 0),
          0,
        ),
        stockCostIncomplete = stockMovements.some(
          (movement) => movement.unit_cost == null,
        ),
        invoiceRevenue = (data.invoiceLines || [])
          .filter((line) => line.mission_id === mission.id)
          .reduce(
            (sum, line) =>
              sum +
              Number(line.quantity || 0) * Number(line.unit_price_ex_vat || 0),
            0,
          ),
        referralCost = (data.invoiceLines || [])
          .filter((line) => line.mission_id === mission.id)
          .reduce((sum, line) => {
            const referral = (data.invoiceReferralCommissions || []).find(
              (item) =>
                item.invoice_id === line.invoice_id &&
                !["waived", "cancelled"].includes(item.status),
            );
            if (!referral) return sum;
            const lineRevenue =
              Number(line.quantity || 0) * Number(line.unit_price_ex_vat || 0);
            return sum + (lineRevenue * Number(referral.commission_rate || 0)) / 100;
          }, 0),
        revenue = Number(mission.amount_ex_vat || invoiceRevenue || 0),
        totalCost =
          labor.contractor +
          labor.employee +
          directExpenses +
          mileageCost +
          stockCost +
          referralCost,
        margin = revenue - totalCost;
      return {
        mission,
        clientName:
          client?.name || mission.client_display_name || "Client non identifié",
        propertyName: property?.name || "Sans bien rattaché",
        serviceName:
          propertyService?.name || service?.name || "Prestation non renseignée",
        serviceScope: propertyService
          ? ("property" as const)
          : service
            ? ("client" as const)
            : undefined,
        serviceId: propertyService?.id || service?.id,
        clientId: mission.client_id,
        propertyId: mission.property_id,
        revenue,
        contractorCost: labor.contractor,
        employeeCost: labor.employee,
        mileageCost,
        stockCost,
        expenses: directExpenses,
        referralCost,
        totalCost,
        margin,
        marginRate: revenue > 0 ? (margin / revenue) * 100 : 0,
        missingCost: labor.missing || mileageIncomplete || stockCostIncomplete,
      };
    }),
    grouped = new Map<string, ProfitabilityRow>();
  missionRows.forEach((row) => {
    const group =
        level === "client"
          ? row.mission.client_id
          : level === "property"
            ? row.mission.property_id || "no-property-" + row.mission.client_id
            : level === "service"
              ? row.mission.property_service_id ||
                row.mission.service_id ||
                "no-service-" + row.mission.client_id
              : row.mission.id,
      label =
        level === "client"
          ? row.clientName
          : level === "property"
            ? row.propertyName
            : level === "service"
              ? row.serviceName
              : frDate(row.mission.starts_at, true) + " · " + row.clientName,
      current = grouped.get(group) || {
        id: group,
        label,
        clientName: row.clientName,
        missions: 0,
        revenue: 0,
        contractorCost: 0,
        employeeCost: 0,
        mileageCost: 0,
        stockCost: 0,
        expenses: 0,
        referralCost: 0,
        totalCost: 0,
        margin: 0,
        marginRate: 0,
        missingCost: false,
        serviceScope: row.serviceScope,
        serviceId: row.serviceId,
        clientId: row.clientId,
        propertyId: row.propertyId,
      };
    current.missions += 1;
    current.revenue += row.revenue;
    current.contractorCost += row.contractorCost;
    current.employeeCost += row.employeeCost;
    current.mileageCost += row.mileageCost;
    current.stockCost += row.stockCost;
    current.expenses += row.expenses;
    current.referralCost += row.referralCost;
    current.totalCost += row.totalCost;
    current.margin += row.margin;
    current.marginRate =
      current.revenue > 0 ? (current.margin / current.revenue) * 100 : 0;
    current.missingCost = current.missingCost || row.missingCost;
    grouped.set(group, current);
  });
  const clientBasketGroups = new Map<string, ProfitabilityRow>();
  missionRows.forEach((row) => {
    const group = row.mission.client_id,
      current = clientBasketGroups.get(group) || {
        id: group,
        label: row.clientName,
        clientName: row.clientName,
        missions: 0,
        revenue: 0,
        contractorCost: 0,
        employeeCost: 0,
        mileageCost: 0,
        stockCost: 0,
        expenses: 0,
        referralCost: 0,
        totalCost: 0,
        margin: 0,
        marginRate: 0,
        missingCost: false,
      };
    current.missions += 1;
    current.revenue += row.revenue;
    current.contractorCost += row.contractorCost;
    current.employeeCost += row.employeeCost;
    current.mileageCost += row.mileageCost;
    current.stockCost += row.stockCost;
    current.expenses += row.expenses;
    current.referralCost += row.referralCost;
    current.totalCost += row.totalCost;
    current.margin += row.margin;
    current.marginRate =
      current.revenue > 0 ? (current.margin / current.revenue) * 100 : 0;
    current.missingCost = current.missingCost || row.missingCost;
    clientBasketGroups.set(group, current);
  });
  const rows = Array.from(grouped.values()).sort((a, b) =>
      sort === "revenue-desc"
        ? b.revenue - a.revenue
        : sort === "margin-desc"
          ? b.marginRate - a.marginRate
          : sort === "name"
            ? a.label.localeCompare(b.label, "fr")
            : a.marginRate - b.marginRate,
    ),
    clientBasketRows = Array.from(clientBasketGroups.values()).sort(
      (a, b) =>
        b.revenue / Math.max(1, b.missions) -
        a.revenue / Math.max(1, a.missions),
    ),
    totalMissions = missionRows.length,
    totalRevenue = missionRows.reduce((sum, row) => sum + row.revenue, 0),
    totalCost = missionRows.reduce((sum, row) => sum + row.totalCost, 0),
    averageBasket = totalRevenue / Math.max(1, totalMissions),
    averageMarginPerBasket =
      (totalRevenue - totalCost) / Math.max(1, totalMissions),
    targetMargin = data.organization?.target_margin_rate ?? 30,
    underTarget = rows.filter(
      (row) => row.revenue <= 0 || row.marginRate < targetMargin,
    ).length,
    contractorCostTotal = missionRows.reduce(
      (sum, row) => sum + row.contractorCost,
      0,
    ),
    employeeCostTotal = missionRows.reduce(
      (sum, row) => sum + row.employeeCost,
      0,
    ),
    mileageCostTotal = missionRows.reduce(
      (sum, row) => sum + row.mileageCost,
      0,
    ),
    stockCostTotal = missionRows.reduce(
      (sum, row) => sum + row.stockCost,
      0,
    ),
    expenseCostTotal = missionRows.reduce(
      (sum, row) => sum + row.expenses,
      0,
    ),
    referralCostTotal = missionRows.reduce(
      (sum, row) => sum + row.referralCost,
      0,
    ),
    profitabilityCostSlices: ExecutiveDonutSlice[] = [
      { label: "Sous-traitance", value: contractorCostTotal, color: "var(--ard-gold)" },
      { label: "Salariés", value: employeeCostTotal, color: "#667085" },
      { label: "IK mission", value: mileageCostTotal, color: "#8b6f47" },
      { label: "Produits / stock", value: stockCostTotal, color: "#b6743d" },
      { label: "Achats / frais", value: expenseCostTotal, color: "#a7463f" },
      { label: "Apport commercial", value: referralCostTotal, color: "#6f5f9b" },
    ],
    topClientBasketBars: ExecutiveBarItem[] = clientBasketRows
      .slice(0, 5)
      .map((row) => {
        const basket = row.revenue / Math.max(1, row.missions);
        return {
          label: row.label,
          value: basket,
          display: money(basket),
          tone: row.marginRate < targetMargin ? "danger" : "gold",
          detail: `${row.missions} mission${row.missions > 1 ? "s" : ""} · marge ${row.marginRate.toFixed(1)} %`,
        };
      });
  const buildPricingAnalysis = (
    row: ProfitabilityRow,
  ): NonNullable<BillingCatalogFocus["pricingAnalysis"]> | undefined => {
    if (!row.serviceScope || !row.serviceId || row.missions <= 0) return undefined;
    const sourceService =
        row.serviceScope === "property"
          ? data.propertyServices.find((item) => item.id === row.serviceId)
          : data.services.find((item) => item.id === row.serviceId),
      client = data.clients.find((item) => item.id === row.clientId),
      catalogItem = sourceService?.catalog_item_id
        ? (data.serviceCatalogItems || []).find(
            (item) => item.id === sourceService.catalog_item_id,
          )
        : null,
      configuredMode = data.organization?.profitability_target_mode || "margin",
      configuredRate = Number(
        data.organization?.profitability_target_rate ??
          data.organization?.target_margin_rate ??
          30,
      ),
      operationalTarget =
        configuredMode === "markup"
          ? (100 * configuredRate) / (100 + configuredRate)
          : configuredRate,
      minimumMarginRate = Math.min(
        85,
        Math.max(
          0,
          Number(data.organization?.target_margin_rate ?? 30),
          Number(client?.target_margin_rate ?? 0),
          Number(catalogItem?.target_margin_rate ?? 0),
        ),
      ),
      recommendedMarginRate = Math.min(
        90,
        Math.max(minimumMarginRate + 5, operationalTarget),
      ),
      comfortableMarginRate = Math.min(
        92,
        Math.max(minimumMarginRate + 10, recommendedMarginRate + 5),
      ),
      averageRevenue = row.revenue / row.missions,
      averageKnownCost = row.totalCost / row.missions,
      currentUnitPrice = Number(sourceService?.unit_price_ex_vat || 0),
      unitScale =
        averageRevenue > 0 && currentUnitPrice > 0
          ? currentUnitPrice / averageRevenue
          : 1,
      priceForMargin = (rate: number) =>
        Math.round(
          ((averageKnownCost / Math.max(0.01, 1 - rate / 100)) * unitScale +
            Number.EPSILON) *
            100,
        ) / 100;
    return {
      missionCount: row.missions,
      averageRevenue,
      averageKnownCost,
      currentMarginRate: row.marginRate,
      currentUnitPrice,
      billingMode: sourceService?.billing_mode || "fixed",
      minimumMarginRate,
      recommendedMarginRate,
      comfortableMarginRate,
      minimumUnitPrice: priceForMargin(minimumMarginRate),
      recommendedUnitPrice: priceForMargin(recommendedMarginRate),
      comfortableUnitPrice: priceForMargin(comfortableMarginRate),
      missingCost: row.missingCost,
    };
  };
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Pilotage de la marge</span>
          <h1>Rentabilité</h1>
        </div>
      </div>
      <details className="panel financial-glossary" data-testid="financial-glossary">
        <summary>Comprendre le vocabulaire financier</summary>
        <div className="section-heading">
          <div>
            <span className="eyebrow">Définitions communes</span>
            <h2>Le même vocabulaire pour décider</h2>
          </div>
          <small>
            Les indicateurs de gestion restent provisoires jusqu’à leur
            rapprochement avec la comptabilité et leur validation par
            l’expert-comptable.
          </small>
        </div>
        <div className="financial-glossary-grid">
          {financialGlossary.map((item) => (
            <article key={item.term}>
              <h3>{item.term}</h3>
              <strong>{item.formula}</strong>
              <p>{item.definition}</p>
            </article>
          ))}
        </div>
      </details>
      <Notice>
        Cette lecture utilise uniquement les coûts rattachables : prix convenus
        des ST, coût horaire salarié connu et dépenses liées à une mission. Les
        IK rattachées à une mission sont intégrées selon leur montant fiscal
        calculé. Les produits réellement consommés sont valorisés au coût figé
        dans leur mouvement de stock. Les commissions d’apporteur sont déduites
        ligne par ligne lorsqu’une facture est rattachée à la mission. Les
        déplacements généraux, temps de trajet et commissions sans mission
        identifiable ne sont pas répartis artificiellement.
      </Notice>
      <section className="panel">
        <div className="period-presets" aria-label="Périodes rapides">
          <button
            type="button"
            className={periodPreset === "current" ? "active" : ""}
            onClick={() => applyPeriod("current")}
          >
            Exercice en cours
          </button>
          <button
            type="button"
            className={periodPreset === "previous" ? "active" : ""}
            onClick={() => applyPeriod("previous")}
          >
            Exercice précédent
          </button>
          <button
            type="button"
            className={periodPreset === "rolling" ? "active" : ""}
            onClick={() => applyPeriod("rolling")}
          >
            12 derniers mois
          </button>
        </div>
        <div className="profitability-filters">
          <label>
            Du
            <input
              type="date"
              value={from}
              max={to}
              onChange={(event) => {
                setFrom(event.target.value);
                setPeriodPreset("custom");
              }}
            />
          </label>
          <label>
            Au
            <input
              type="date"
              value={to}
              min={from}
              onChange={(event) => {
                setTo(event.target.value);
                setPeriodPreset("custom");
              }}
            />
          </label>
          <label>
            Niveau de lecture
            <select
              value={level}
              onChange={(event) =>
                setLevel(event.target.value as ProfitabilityLevel)
              }
            >
              <option value="client">Par client</option>
              <option value="property">Par bien</option>
              <option value="service">Par prestation</option>
              <option value="mission">Par mission</option>
            </select>
          </label>
          <label>
            Client
            <select
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
            >
              <option value="">Tous les clients</option>
              {data.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Trier
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              <option value="margin-asc">Marge la plus faible</option>
              <option value="margin-desc">Marge la plus forte</option>
              <option value="revenue-desc">CA le plus élevé</option>
              <option value="name">Nom</option>
            </select>
          </label>
        </div>
      </section>
      <div
        className="decision-view-switcher profitability-view-switcher"
        aria-label="Choisir une vue de rentabilité"
        role="navigation"
        data-testid="profitability-view-switcher"
      >
        <button
          type="button"
          className={activeProfitabilityView === "overview" ? "active" : ""}
          aria-pressed={activeProfitabilityView === "overview"}
          onClick={() => setActiveProfitabilityView("overview")}
        >
          <strong>Vue dirigeant</strong>
          <span>Décider rapidement à partir des marges, coûts et alertes</span>
        </button>
        <button
          type="button"
          className={activeProfitabilityView === "analysis" ? "active" : ""}
          aria-pressed={activeProfitabilityView === "analysis"}
          onClick={() => setActiveProfitabilityView("analysis")}
        >
          <strong>Analyse détaillée</strong>
          <span>Comparer les clients, biens, prestations ou missions et agir</span>
        </button>
      </div>
      {activeProfitabilityView === "overview" && (
        <>
      <details
        className="billing-kpi-guide profitability-kpi-guide"
        data-testid="profitability-kpi-guide"
      >
        <summary>Comprendre les indicateurs de rentabilité</summary>
        <p>
          Ces chiffres portent uniquement sur les missions validées et non
          annulées de la période sélectionnée. Ils utilisent les revenus et les
          coûts directement rattachables connus dans ARD Manager.
        </p>
        <div className="billing-kpi-guide-grid">
          <article>
            <strong>CA HT retenu</strong>
            <span>
              Somme du montant HT des missions retenues. Lorsqu’un montant de
              mission manque, ARD utilise uniquement les lignes de facture liées
              disponibles.
            </span>
          </article>
          <article>
            <strong>Coûts directement connus</strong>
            <span>
              Sous-traitance, coût salarié estimé, IK approuvées, produits
              consommés, achats ou frais et commissions d’apporteur rattachés aux
              mêmes missions.
            </span>
          </article>
          <article>
            <strong>Marge de gestion</strong>
            <span>
              CA HT retenu moins coûts directement connus. Elle reste provisoire
              tant que tous les coûts ne sont pas complets et rapprochés.
            </span>
          </article>
          <article>
            <strong>Panier moyen global HT</strong>
            <span>
              CA HT retenu divisé par le nombre de missions non annulées de la
              période, sans inventer de valeur lorsqu’aucune mission n’existe.
            </span>
          </article>
          <article>
            <strong>Marge moyenne par panier</strong>
            <span>
              Marge de gestion divisée par ce même nombre de missions. Elle aide
              à comparer les prestations sans remplacer l’analyse ligne par ligne.
            </span>
          </article>
          <article>
            <strong>Sous la cible</strong>
            <span>
              Nombre de lignes du niveau choisi dont le CA est nul ou dont le
              taux de marge est inférieur à la cible définie par la société.
            </span>
          </article>
        </div>
        <p className="billing-kpi-guide-limit">
          <strong>Limite :</strong> une ligne « Coût incomplet » ne doit jamais
          servir seule à modifier un tarif. Cette lecture pilote la décision ; le
          résultat comptable officiel reste celui validé avec l’expert-comptable.
        </p>
      </details>
      <div className="stats">
        <article>
          <span>CA HT retenu</span>
          <strong>{money(totalRevenue)}</strong>
        </article>
        <article>
          <span>Coûts directement connus</span>
          <strong>{money(totalCost)}</strong>
        </article>
        <article>
          <span>Marge de gestion</span>
          <strong>{money(totalRevenue - totalCost)}</strong>
          <small>
            {totalRevenue > 0
              ? (((totalRevenue - totalCost) / totalRevenue) * 100).toFixed(1) +
                " %"
              : "Aucun CA sur la période"}
          </small>
        </article>
        <article>
          <span>Panier moyen global HT</span>
          <strong>{money(averageBasket)}</strong>
          <small>
            {totalMissions} mission{totalMissions > 1 ? "s" : ""} non annulée
            {totalMissions > 1 ? "s" : ""}
          </small>
        </article>
        <article>
          <span>Marge moyenne par panier</span>
          <strong>{money(averageMarginPerBasket)}</strong>
          <small>
            {totalRevenue > 0
              ? (((totalRevenue - totalCost) / totalRevenue) * 100).toFixed(1) +
                " % du CA HT"
              : "Aucun CA sur la période"}
          </small>
        </article>
        <article>
          <span>Sous la cible de {targetMargin} %</span>
          <strong>{underTarget}</strong>
          <small>ligne(s) à analyser ou compléter</small>
        </article>
      </div>
      <section className="executive-reading-grid profitability-visuals">
        <article className="panel executive-reading-card">
          <div className="executive-reading-heading">
            <div>
              <span className="eyebrow">Structure des coûts</span>
              <h2>Où part l’argent des missions</h2>
            </div>
          </div>
          <ExecutiveDonut
            label={`Répartition des ${money(totalCost)} de coûts directement connus`}
            slices={profitabilityCostSlices}
            centerLabel="coûts connus"
            centerValue={compactMoney(totalCost)}
          />
        </article>
        <article className="panel executive-reading-card">
          <div className="executive-reading-heading">
            <div>
              <span className="eyebrow">Portefeuille clients</span>
              <h2>Top 5 des paniers moyens</h2>
            </div>
          </div>
          <ExecutiveBars
            label="Comparaison des cinq paniers moyens clients les plus élevés"
            items={topClientBasketBars}
          />
          <p className="executive-chart-note">
            Une barre rouge signale une marge inférieure à la cible de {targetMargin} %.
          </p>
        </article>
      </section>
        </>
      )}
      {activeProfitabilityView === "analysis" && (
        <>
      <section className="panel table-wrap">
        <h2>Panier moyen par client</h2>
        <p>
          Le panier moyen correspond au CA HT retenu divisé par le nombre de
          missions non annulées sur la période. La marge moyenne associée
          déduit les coûts directement connus de ces mêmes missions.
        </p>
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Missions</th>
              <th>CA HT</th>
              <th>Panier moyen HT</th>
              <th>Marge moyenne / panier</th>
              <th>Taux de marge</th>
              <th>Qualité</th>
            </tr>
          </thead>
          <tbody>
            {clientBasketRows.map((row) => (
              <tr key={row.id}>
                <td>{row.label}</td>
                <td>{row.missions}</td>
                <td>{money(row.revenue)}</td>
                <td>{money(row.revenue / Math.max(1, row.missions))}</td>
                <td>{money(row.margin / Math.max(1, row.missions))}</td>
                <td>{row.marginRate.toFixed(1)} %</td>
                <td>
                  {row.missingCost ? (
                    <span className="data-warning">Coût incomplet</span>
                  ) : row.marginRate < targetMargin ? (
                    <span className="data-warning">Sous la cible</span>
                  ) : (
                    <span className="data-ok">Complet</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!clientBasketRows.length && (
          <p className="empty">Aucune mission sur cette période.</p>
        )}
      </section>
      <section className="panel table-wrap">
        <h2>
          {level === "client"
            ? "Rentabilité par client"
            : level === "property"
              ? "Rentabilité par bien"
              : level === "service"
                ? "Rentabilité par prestation"
                : "Rentabilité par mission"}
        </h2>
        <table>
          <thead>
            <tr>
              <th>Élément</th>
              {level !== "client" && <th>Client</th>}
              <th>Missions</th>
              <th>CA HT</th>
              <th>Coût ST</th>
              <th>Coût salarié</th>
              <th>IK mission</th>
              <th>Produits / stock</th>
              <th>Achats / frais</th>
              <th>Apport commercial</th>
              <th>Marge</th>
              <th>Taux</th>
              <th>Qualité</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {level === "service" &&
                  row.serviceScope &&
                  row.serviceId &&
                  row.clientId &&
                  onOpenServicePricing ? (
                    <button
                      type="button"
                      className="profitability-service-link"
                      data-testid="profitability-open-service-pricing"
                      onClick={() =>
                        onOpenServicePricing({
                          scope: row.serviceScope!,
                          serviceId: row.serviceId!,
                          clientId: row.clientId!,
                          propertyId: row.propertyId,
                          label: row.label,
                          pricingAnalysis: buildPricingAnalysis(row),
                        })
                      }
                    >
                      <strong>{row.label}</strong>
                      <small>Ouvrir et adapter le tarif futur →</small>
                    </button>
                  ) : (
                    row.label
                  )}
                </td>
                {level !== "client" && <td>{row.clientName}</td>}
                <td>{row.missions}</td>
                <td>{money(row.revenue)}</td>
                <td>{money(row.contractorCost)}</td>
                <td>{money(row.employeeCost)}</td>
                <td>{money(row.mileageCost)}</td>
                <td>{money(row.stockCost)}</td>
                <td>{money(row.expenses)}</td>
                <td>{money(row.referralCost)}</td>
                <td>{money(row.margin)}</td>
                <td>{row.marginRate.toFixed(1)} %</td>
                <td>
                  {row.missingCost ? (
                    <span className="data-warning">Coût incomplet</span>
                  ) : row.marginRate < targetMargin ? (
                    <span className="data-warning">Sous la cible</span>
                  ) : (
                    <span className="data-ok">Complet</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <p className="empty">Aucune mission sur cette période.</p>
        )}
      </section>
        </>
      )}
    </>
  );
}

const defaultScoring: Required<ScoringPreferences> = {
  margin_weight: 30,
  revenue_weight: 15,
  payment_weight: 15,
  recurrence_weight: 10,
  travel_weight: 10,
  admin_weight: 10,
  incident_weight: 10,
  max_round_trip_minutes: 120,
  max_monthly_admin_minutes: 240,
};
const boundedScore = (value: number) => Math.max(0, Math.min(100, value));

function scoreClients(data: DataState) {
  const preferences = {
      ...defaultScoring,
      ...(data.organization?.scoring_preferences || {}),
    },
    revenues = new Map(
      data.clients.map((client) => [
        client.id,
        data.invoices
          .filter(
            (invoice) =>
              invoice.client_id === client.id &&
              !["cancelled", "credited"].includes(invoice.status),
          )
          .reduce((sum, invoice) => sum + invoice.total_ex_vat, 0),
      ]),
    ),
    maxRevenue = Math.max(0, ...revenues.values());
  return new Map<string, ClientScore>(
    data.clients.map((client) => {
      const missions = data.missions.filter(
          (mission) => mission.client_id === client.id,
        ),
        validatedMissionIds = new Set(
          missions
            .filter((mission) => mission.admin_validated_at != null)
            .map((mission) => mission.id),
        ),
        assignments = data.assignments.filter(
          (assignment) =>
            validatedMissionIds.has(assignment.mission_id) &&
            !["cancelled", "refused"].includes(assignment.status),
        ),
        directExpenses = data.expenses
          .filter(
            (expense) =>
              expense.client_id === client.id &&
              expense.status === "approved" &&
              (!expense.mission_id || validatedMissionIds.has(expense.mission_id)),
          )
          .reduce((sum, expense) => sum + expense.amount_ex_vat, 0),
        assignmentCosts = assignments.reduce((sum, assignment) => {
          if (assignment.agreed_amount != null)
            return sum + assignment.agreed_amount;
          if (assignment.agreed_rate != null)
            return sum + assignment.agreed_rate * assignment.assigned_hours;
          const worker = data.workers.find(
            (item) => item.id === assignment.worker_id,
          );
          if (worker?.kind === "contractor") {
            if (worker.pricing_mode === "hourly" && worker.hourly_rate != null)
              return sum + worker.hourly_rate * assignment.assigned_hours;
            if (worker.pricing_mode === "fixed" && worker.fixed_fee != null)
              return sum + worker.fixed_fee;
          }
          return sum;
        }, 0),
        referralCosts = (data.invoiceReferralCommissions || [])
          .filter(
            (item) =>
              item.client_id === client.id &&
              !["waived", "cancelled"].includes(item.status),
          )
          .reduce(
            (sum, item) => sum + Number(item.commission_amount_ex_vat || 0),
            0,
          ),
        revenue = revenues.get(client.id) || 0,
        costs = assignmentCosts + directExpenses + referralCosts,
        margin = revenue - costs,
        marginRate = revenue > 0 ? (margin / revenue) * 100 : 0,
        target =
          client.target_margin_rate ??
          data.organization?.target_margin_rate ??
          30,
        overdue = data.invoices.filter(
          (invoice) =>
            invoice.client_id === client.id &&
            (invoice.status === "overdue" ||
              (invoice.status === "sent" &&
                !!invoice.due_on &&
                invoice.due_on < parisDay(new Date()))),
        ).length,
        recurrences = (data.recurrences || []).filter(
          (item) => item.client_id === client.id && item.active,
        ).length,
        incidents = client.incident_count || 0,
        marginValue =
          revenue > 0
            ? boundedScore((marginRate / Math.max(target, 1)) * 80)
            : 25,
        revenueValue =
          maxRevenue > 0 ? boundedScore((revenue / maxRevenue) * 100) : 50,
        paymentValue = boundedScore(100 - overdue * 30),
        recurrenceValue = recurrences
          ? boundedScore(60 + recurrences * 10)
          : 30,
        travelValue =
          client.average_round_trip_minutes == null
            ? 50
            : boundedScore(
                100 -
                  (client.average_round_trip_minutes /
                    Math.max(preferences.max_round_trip_minutes, 1)) *
                    100,
              ),
        adminValue =
          client.monthly_admin_minutes == null
            ? 50
            : boundedScore(
                100 -
                  (client.monthly_admin_minutes /
                    Math.max(preferences.max_monthly_admin_minutes, 1)) *
                    100,
              ),
        incidentValue = boundedScore(100 - incidents * 20),
        components = [
          {
            label: "Marge",
            value: marginValue,
            weight: preferences.margin_weight,
          },
          {
            label: "Chiffre d’affaires",
            value: revenueValue,
            weight: preferences.revenue_weight,
          },
          {
            label: "Paiements",
            value: paymentValue,
            weight: preferences.payment_weight,
          },
          {
            label: "Récurrence",
            value: recurrenceValue,
            weight: preferences.recurrence_weight,
          },
          {
            label: "Trajets",
            value: travelValue,
            weight: preferences.travel_weight,
          },
          {
            label: "Temps administratif",
            value: adminValue,
            weight: preferences.admin_weight,
          },
          {
            label: "Incidents",
            value: incidentValue,
            weight: preferences.incident_weight,
          },
        ],
        totalWeight = components.reduce((sum, item) => sum + item.weight, 0),
        score = totalWeight
          ? Math.round(
              components.reduce(
                (sum, item) => sum + item.value * item.weight,
                0,
              ) / totalWeight,
            )
          : 0,
        reasons: string[] = [];
      if (revenue === 0) reasons.push("Aucun CA facturé enregistré");
      if (revenue > 0 && marginRate < target)
        reasons.push(
          `Marge ${marginRate.toFixed(1)} % sous la cible de ${target} %`,
        );
      if (overdue) reasons.push(`${overdue} facture(s) en retard`);
      if (!recurrences) reasons.push("Aucune prestation récurrente active");
      if (client.average_round_trip_minutes == null)
        reasons.push("Temps de trajet à renseigner");
      if (client.monthly_admin_minutes == null)
        reasons.push("Temps administratif à renseigner");
      if (incidents) reasons.push(`${incidents} incident(s) déclaré(s)`);
      if (!reasons.length) reasons.push("Aucun point faible détecté");
      return [
        client.id,
        {
          revenue,
          costs,
          margin,
          marginRate,
          overdue,
          recurrences,
          score,
          components,
          reasons,
        },
      ];
    }),
  );
}

type PortfolioClass = "A" | "B" | "C";
const portfolioDecisionLabels: Record<ClientPortfolioReview["decision"], string> = {
  develop: "Développer la relation",
  maintain: "Conserver et suivre",
  renegotiate: "Renégocier",
  stop: "Préparer un arrêt de relation",
};

function suggestClientPortfolioClass(
  client: Client,
  score: ClientScore,
  targetMargin: number,
) {
  const incidents = client.incident_count || 0;
  let suggested: PortfolioClass = "B";
  if (
    score.revenue === 0 ||
    score.marginRate < targetMargin ||
    score.overdue >= 2 ||
    incidents >= 3
  )
    suggested = "C";
  else if (
    score.marginRate >= targetMargin &&
    score.overdue === 0 &&
    incidents <= 1 &&
    (score.recurrences > 0 || client.strategic_priority)
  )
    suggested = "A";
  const causes = [...score.reasons];
  if (client.strategic_priority) causes.push("Client identifié comme stratégique");
  return { suggested, causes: [...new Set(causes)] };
}

function ClientPortfolioReviewPanel({
  client,
  score,
  data,
  reload,
  setMessage,
}: {
  client: Client;
  score: ClientScore;
  data: DataState;
  reload: () => void;
  setMessage: (message: string) => void;
}) {
  const today = parisDay(new Date()),
    year = today.slice(0, 4),
    reviews = (data.clientPortfolioReviews || [])
      .filter((item) => item.client_id === client.id)
      .sort((a, b) => b.reviewed_at.localeCompare(a.reviewed_at)),
    active = reviews.find((item) => item.status === "active"),
    targetMargin = Number(
      client.target_margin_rate ?? data.organization?.target_margin_rate ?? 30,
    ),
    liveSuggestion = suggestClientPortfolioClass(client, score, targetMargin),
    knownMinimum =
      score.costs > 0 && targetMargin < 100
        ? score.costs / (1 - targetMargin / 100)
        : null,
    [periodStart, setPeriodStart] = useState(`${year}-01-01`),
    [periodEnd, setPeriodEnd] = useState(today),
    [decidedClass, setDecidedClass] = useState<PortfolioClass>(
      active?.decided_class || liveSuggestion.suggested,
    ),
    [decision, setDecision] = useState<ClientPortfolioReview["decision"]>(
      active?.decision || "maintain",
    ),
    [rationale, setRationale] = useState(active?.rationale || ""),
    [nextAction, setNextAction] = useState(active?.next_action || ""),
    [nextReviewOn, setNextReviewOn] = useState(
      active?.next_review_on ||
        parisDay(new Date(Date.now() + 90 * 86400000)),
    ),
    [busy, setBusy] = useState(false);
  async function saveReview(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    const result = await supabase!.rpc("record_client_portfolio_review", {
      p_client_id: client.id,
      p_period_start: periodStart,
      p_period_end: periodEnd,
      p_decided_class: decidedClass,
      p_decision: decision,
      p_rationale: rationale.trim(),
      p_next_action: nextAction.trim(),
      p_next_review_on: nextReviewOn,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Revue A/B/C enregistrée. La décision reste humaine et aucune action commerciale n’a été exécutée automatiquement.",
    );
    if (!result.error) reload();
  }
  return (
    <section className="client-score-card client-portfolio-card">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Arbitrage commercial</span>
          <h3>Portefeuille clients A/B/C</h3>
        </div>
        <span className={`portfolio-class class-${active?.decided_class || liveSuggestion.suggested}`}>
          {active ? `Classe décidée ${active.decided_class}` : `Proposition ${liveSuggestion.suggested}`}
        </span>
      </div>
      <div className="admin-kpis client-file-kpis">
        <article>
          <span>Classe proposée</span>
          <strong>{active?.suggested_class || liveSuggestion.suggested}</strong>
        </article>
        <article>
          <span>Marge connue</span>
          <strong>{score.marginRate.toFixed(1)} %</strong>
          <small>Cible {targetMargin.toFixed(0)} %</small>
        </article>
        <article>
          <span>CA HT minimum connu</span>
          <strong>{money(active?.minimum_revenue_ex_vat ?? knownMinimum ?? 0)}</strong>
          <small>Coûts directs connus uniquement</small>
        </article>
        <article>
          <span>Prochaine revue</span>
          <strong>{active ? frDate(active.next_review_on) : "À planifier"}</strong>
        </article>
      </div>
      <ul>
        {(active?.causes?.length ? active.causes : liveSuggestion.causes).map(
          (cause) => <li key={cause}>{cause}</li>,
        )}
      </ul>
      {active && (
        <Notice>
          <strong>{portfolioDecisionLabels[active.decision]}</strong> — {active.next_action}
          <br />Motif : {active.rationale}
        </Notice>
      )}
      <form className="form-grid" onSubmit={saveReview}>
        <label>Période analysée — début<input type="date" required value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
        <label>Fin<input type="date" required value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
        <label>Classe retenue<select value={decidedClass} onChange={(event) => setDecidedClass(event.target.value as PortfolioClass)}><option value="A">A — développer</option><option value="B">B — optimiser / conserver</option><option value="C">C — décider / corriger</option></select></label>
        <label>Décision humaine<select value={decision} onChange={(event) => setDecision(event.target.value as ClientPortfolioReview["decision"])}>{Object.entries(portfolioDecisionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="wide">Pourquoi cette décision ?<textarea required minLength={10} value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="Expliquez les faits et le choix retenu." /></label>
        <label className="wide">Prochaine action<input required minLength={3} value={nextAction} onChange={(event) => setNextAction(event.target.value)} placeholder="Ex. préparer une renégociation avec preuves de marge." /></label>
        <label>Prochaine date de revue<input type="date" min={today} required value={nextReviewOn} onChange={(event) => setNextReviewOn(event.target.value)} /></label>
        <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer la revue"}</button>
      </form>
      <small>
        La classe est une aide à la décision. ARD Manager ne modifie jamais seul le tarif,
        le contrat, le statut ou la relation avec le client.
      </small>
    </section>
  );
}

type OrganizationAccessSummary = {
  label: string;
  detail: string;
  tone: "success" | "warning" | "danger" | "neutral";
};

function organizationAccessSummary(
  targetType: "client" | "worker",
  targetId: string,
  hasAccount: boolean,
  invitations: OrganizationUserInvitation[] = [],
  accessStatus?: OrgProfile["access_status"],
): OrganizationAccessSummary {
  if (hasAccount && accessStatus === "suspended") return {
    label: "Accès suspendu",
    detail: "Dossier et historique conservés",
    tone: "danger",
  };
  if (hasAccount) return {
    label: "Espace actif",
    detail: "Compte créé et rattaché",
    tone: "success",
  };
  const latest = invitations.find(
    (item) => item.target_type === targetType && item.target_id === targetId,
  );
  if (!latest) return {
    label: "Lien non envoyé",
    detail: "Aucune invitation préparée",
    tone: "neutral",
  };
  if (latest.status === "accepted") return {
    label: "Activation à rattacher",
    detail: "Invitation acceptée, fiche à contrôler",
    tone: "warning",
  };
  if (latest.status === "sent") return {
    label: "Activation attendue",
    detail: `Lien envoyé à ${latest.email}`,
    tone: "warning",
  };
  if (latest.status === "pending") return {
    label: "Lien préparé",
    detail: "Aucun e-mail envoyé",
    tone: "warning",
  };
  if (latest.status === "failed") return {
    label: "Envoi en échec",
    detail: latest.last_error || "Invitation à reprendre",
    tone: "danger",
  };
  return {
    label: latest.status === "expired" ? "Lien expiré" : "Lien révoqué",
    detail: "Nouvelle invitation nécessaire",
    tone: "danger",
  };
}

function accessToneClass(tone: OrganizationAccessSummary["tone"]) {
  return tone === "success" ? "success" : tone === "danger" ? "danger" : tone === "warning" ? "warning" : "";
}

function AccountInvitationPanel({targetType,targetId,displayName,hasAccount,accountEmail,accountProfile,onChanged}:{
  targetType:"client"|"worker"; targetId:string; displayName:string;
  hasAccount:boolean; accountEmail?:string|null; accountProfile?:OrgProfile;
  onChanged?:()=>void|Promise<void>;
}) {
  const [invitations,setInvitations]=useState<OrganizationUserInvitation[]>([]),
    [name,setName]=useState(displayName),[email,setEmail]=useState(accountEmail||""),
    [loginEmail,setLoginEmail]=useState(accountEmail||""),
    [message,setMessage]=useState(""),[busy,setBusy]=useState(false);
  const load=useCallback(async()=>{
    const result=await supabase!.from("organization_user_invitations").select("*").eq("target_type",targetType).eq("target_id",targetId).order("created_at",{ascending:false});
    if(result.error) setMessage(result.error.message);
    else setInvitations((result.data||[]) as OrganizationUserInvitation[]);
  },[targetId,targetType]);
  useEffect(()=>{setName(displayName);setEmail(accountEmail||"");setLoginEmail(accountEmail||"");setMessage("");void load();},[displayName,accountEmail,load]);
  const openInvitation=invitations.find((item)=>["pending","sent"].includes(item.status)), accepted=invitations.find((item)=>item.status==="accepted");
  async function invokeInvitation(body:Record<string,unknown>){
    setBusy(true);setMessage("");
    const result=await supabase!.functions.invoke("invite-organization-user",{body:{...body,return_to:location.origin}});
    setBusy(false);
    if(result.error){
      let detail=result.error.message;
      try{const payload=await (result.error as typeof result.error&{context?:Response}).context?.clone().json();if(payload?.error)detail=payload.error;}catch{/* Message Supabase conservé. */}
      setMessage(detail);
      return;
    }
    setMessage(isShowcaseMode?"Invitation simulée : aucun e-mail réel n’a été envoyé.":"Invitation sécurisée envoyée.");
    await load();
  }
  async function invite(event:React.FormEvent){
    event.preventDefault();
    await invokeInvitation({target_type:targetType,target_id:targetId,display_name:name,email});
  }
  async function sendPrepared(){
    if(!openInvitation||openInvitation.status!=="pending")return;
    if(!window.confirm(`Envoyer maintenant l’invitation à ${openInvitation.email} ?`))return;
    await invokeInvitation({invitation_id:openInvitation.id});
  }
  async function revoke(){
    if(!openInvitation||!window.confirm(`Révoquer l’invitation envoyée à ${openInvitation.email} ?`))return;
    const result=await supabase!.rpc("revoke_organization_user_invitation",{p_invitation_id:openInvitation.id});
    setMessage(result.error?result.error.message:"Invitation révoquée.");if(!result.error)await load();
  }
  async function manageAccess(action:"suspend"|"reactivate"|"change_email"){
    if(action==="suspend"&&!window.confirm(`Suspendre l’accès de ${displayName} ? Ses données et son historique seront conservés.`))return;
    if(action==="reactivate"&&!window.confirm(`Réactiver l’accès de ${displayName} ?`))return;
    if(action==="change_email"&&loginEmail.trim().toLowerCase()===(accountEmail||"").trim().toLowerCase())return;
    setBusy(true);setMessage("");
    const result=await supabase!.functions.invoke("manage-organization-user-access",{body:{target_type:targetType,target_id:targetId,action,email:action==="change_email"?loginEmail.trim().toLowerCase():undefined}});
    setBusy(false);
    if(result.error){
      let detail=result.error.message;
      try{const payload=await (result.error as typeof result.error&{context?:Response}).context?.clone().json();if(payload?.error)detail=payload.error;}catch{/* Message Supabase conservé. */}
      setMessage(detail);return;
    }
    setMessage(action==="suspend"?"Accès suspendu. Le dossier et l’historique sont conservés.":action==="reactivate"?"Accès réactivé.":"Adresse de connexion mise à jour sans changer de compte.");
    await onChanged?.();
  }
  const accountSuspended=accountProfile?.access_status==="suspended";
  return (
    <section className="account-invitation-panel">
      <span className="eyebrow">Compte de connexion</span>
      <h4>Accès personnel à ARD Manager</h4>
      {hasAccount ? (
        <div className="account-access-active">
          <div>
            <span className={`status-badge ${accountSuspended ? "danger" : "success"}`}>
              {accountSuspended ? "Accès suspendu" : "Espace actif"}
            </span>
            <small>{accountEmail || "adresse rattachée"} · les données métier restent conservées</small>
          </div>
          {accountProfile && (
            <>
              <button
                type="button"
                className={accountSuspended ? "secondary compact" : "danger compact"}
                disabled={busy}
                onClick={() => void manageAccess(accountSuspended ? "reactivate" : "suspend")}
              >
                {accountSuspended ? "Réactiver l’accès" : "Suspendre l’accès"}
              </button>
              <form
                className="account-login-email"
                onSubmit={(event) => {
                  event.preventDefault();
                  void manageAccess("change_email");
                }}
              >
                <label>
                  Adresse de connexion
                  <input
                    required
                    type="email"
                    value={loginEmail}
                    onChange={(event) => setLoginEmail(event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="secondary compact"
                  disabled={busy || loginEmail.trim().toLowerCase() === (accountEmail || "").trim().toLowerCase()}
                >
                  {busy ? "Enregistrement…" : "Modifier l’adresse d’accès"}
                </button>
                <small>Le compte, les missions, documents et historiques restent rattachés au même profil.</small>
              </form>
            </>
          )}
        </div>
      ) : accepted ? (
        <div className="account-invitation-status">
          <strong>Activation à rattacher</strong>
          <small>{accepted.display_name} · {accepted.email}</small>
          <small>Le lien a été accepté mais la fiche doit encore être contrôlée avant d’afficher un espace actif.</small>
        </div>
      ) : openInvitation ? (
        <div className="account-invitation-status">
          <strong>Invitation {openInvitation.status === "sent" ? "envoyée" : "en préparation"}</strong>
          <small>{openInvitation.display_name} · {openInvitation.email} · valable jusqu’au {frDate(openInvitation.expires_at)}</small>
          {openInvitation.status === "pending" && <small>Aucun e-mail n’est encore parti. L’envoi reste une décision manuelle depuis cette fiche.</small>}
          <div className="row-actions">
            {openInvitation.status === "pending" && <button type="button" className="compact" disabled={busy} onClick={sendPrepared}>{busy ? "Envoi…" : "Envoyer l’invitation"}</button>}
            <button type="button" className="danger compact" disabled={busy} onClick={revoke}>Révoquer l’invitation</button>
          </div>
        </div>
      ) : (
        <div className="account-invitation-empty">
          <div>
            <strong>Lien non envoyé</strong>
            <small>Une fiche importée reste sans compte, sans e-mail et sans accès tant que vous n’agissez pas ici.</small>
          </div>
          <form className="form-grid" onSubmit={invite}>
            <label>Nom affiché<input required minLength={2} value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label>E-mail de connexion<input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            <button disabled={busy}>{busy ? "Envoi…" : "Créer et envoyer le lien"}</button>
            <small className="wide">Le destinataire choisira lui-même son mot de passe. Son rôle et sa société sont verrouillés par le serveur.</small>
          </form>
        </div>
      )}
      {message && <Notice tone={/envoyée|simulée|révoquée|suspendu|réactivé|mise à jour/.test(message) ? "success" : "error"}>{message}</Notice>}
    </section>
  );
}

function AdminClients({
  data,
  onSelect,
  reload,
}: {
  data: DataState;
  onSelect: (mission: Mission) => void;
  reload: () => void;
}) {
  const [selected, setSelected] = useState<Client | null>(null),
    [financialClientOpen, setFinancialClientOpen] = useState(false),
    [readinessFilter, setReadinessFilter] = useState<
      "all" | "ready" | "attention"
    >("all"),
    [message, setMessage] = useState(""),
    [clientDraft, setClientDraft] = useState<Partial<Client>>({}),
    [documentKind, setDocumentKind] = useState("other"),
    [documentContractId, setDocumentContractId] = useState(""),
    [documentFile, setDocumentFile] = useState<File | null>(null),
    [turnoverPeriod, setTurnoverPeriod] = useState(parisDay(new Date()).slice(0, 7)),
    [turnoverAmount, setTurnoverAmount] = useState(""),
    [turnoverReference, setTurnoverReference] = useState(""),
    [turnoverNotes, setTurnoverNotes] = useState(""),
    [propertyEditor, setPropertyEditor] = useState<{
      id?: string;
      name: string;
      address: string;
      contactName: string;
      accessNotes: string;
      propertyTypeId: string;
    } | null>(null),
    [propertyTypes, setPropertyTypes] = useState<ClientPropertyType[]>([]),
    [propertyArchive, setPropertyArchive] = useState<{
      property: ClientProperty;
      reason: string;
    } | null>(null),
    [serviceEditor, setServiceEditor] = useState<{
      id?: string;
      scope: "client" | "property";
      propertyId: string;
      name: string;
      billingMode: string;
      price: string;
      vat: string;
      duration: string;
      notes: string;
      treatment: BillingTreatment;
      supplierCost: string;
      targetMode: "margin" | "markup";
      targetRate: string;
      disbursementPrincipal: string;
      disbursementMandate: string;
      fiscalWarningAcknowledged: boolean;
    } | null>(null),
    [recurringContractEditor, setRecurringContractEditor] = useState<{
      contractKind: "recurring_professional" | "recurring_municipality";
      templateId: string;
      quoteId: string;
      offerName: string;
      serviceScope: string;
      billingFrequency: "monthly" | "quarterly" | "annual";
      billingAmount: string;
      vatRate: string;
      paymentTermsDays: string;
      startsOn: string;
      endsOn: string;
      notes: string;
    } | null>(null),
    [busy, setBusy] = useState(false),
    scores = scoreClients(data);
  useEffect(() => {
    let cancelled = false;
    if (!selected) {
      setPropertyTypes([]);
      return () => { cancelled = true; };
    }
    void supabase!
      .from("client_property_types")
      .select("id,client_id,name,site_code,site_label,base_service_name,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours,active")
      .eq("client_id", selected.id)
      .eq("active", true)
      .order("name")
      .then((result) => {
        if (!cancelled && !result.error) setPropertyTypes((result.data || []) as ClientPropertyType[]);
      });
    return () => { cancelled = true; };
  }, [selected?.id]);
  function openClient(client: Client) {
    setSelected(client);
    setFinancialClientOpen(false);
    setClientDraft(client);
    setMessage("");
    setDocumentKind("other");
    setDocumentContractId("");
    setDocumentFile(null);
    setTurnoverPeriod(parisDay(new Date()).slice(0, 7));
    setTurnoverAmount("");
    setTurnoverReference("");
    setTurnoverNotes("");
    setPropertyEditor(null);
    setPropertyArchive(null);
    setServiceEditor(null);
    setRecurringContractEditor(null);
  }
  async function openDocument(document: DocumentRow) {
    if (!document.storage_path) return;
    const { data: signed, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(document.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  }
  async function advanceContract(contract: ClientContract) {
    const next =
      contract.status === "draft"
        ? "sent"
        : contract.status === "sent"
          ? "signed"
          : contract.status === "signed"
            ? "active"
            : null;
    if (!next) return;
    const now = new Date(),
      patch: Record<string, string | null> = { status: next };
    if (next === "sent") {
      patch.sent_at = now.toISOString();
      patch.next_reminder_at = new Date(
        now.getTime() + 7 * 86400000,
      ).toISOString();
    }
    if (next === "signed") {
      patch.signed_at = now.toISOString();
      patch.next_reminder_at = null;
    }
    const { error } = await supabase!
      .from("client_contracts")
      .update(patch)
      .eq("id", contract.id);
    setMessage(
      error
        ? error.message
        : "Statut du contrat enregistré. Aucun envoi externe n’a été simulé.",
    );
    if (!error) reload();
  }
  async function prepareClientSignature(contract: ClientContract) {
    setBusy(true);
    const { error } = await supabase!.rpc("prepare_tenant_contract_signature", {
      p_source_type: "client_contract",
      p_source_id: contract.id,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Dossier de signature électronique préparé. Il sera envoyé par le prestataire de signature une fois celui-ci connecté.",
    );
    if (!error) reload();
  }
  function openRecurringContractPreparation() {
    const template = (data.contractTemplates || []).find(
        (item) => item.audience === "client" && item.active,
      ),
      acceptedQuote = selected
        ? data.quotes.find(
            (item) => item.client_id === selected.id && item.status === "accepted",
          )
        : null,
      today = parisDay(new Date());
    setRecurringContractEditor({
      contractKind: "recurring_professional",
      templateId: template?.id || "",
      quoteId: acceptedQuote?.id || "",
      offerName: "",
      serviceScope: "",
      billingFrequency: "monthly",
      billingAmount: acceptedQuote ? String(acceptedQuote.total_ex_vat) : "",
      vatRate: String(data.organization?.default_vat_rate ?? 20),
      paymentTermsDays: String(selected?.payment_terms_days ?? 15),
      startsOn: today,
      endsOn: "",
      notes: "",
    });
    setMessage("");
  }
  async function prepareRecurringContract(event: React.FormEvent) {
    event.preventDefault();
    if (!selected || !recurringContractEditor) return;
    setBusy(true);
    const result = await supabase!.rpc("prepare_recurring_client_contract", {
      p_client_id: selected.id,
      p_contract_kind: recurringContractEditor.contractKind,
      p_template_id: recurringContractEditor.templateId,
      p_quote_id: recurringContractEditor.quoteId,
      p_offer_name: recurringContractEditor.offerName.trim(),
      p_service_scope: recurringContractEditor.serviceScope.trim(),
      p_billing_frequency: recurringContractEditor.billingFrequency,
      p_billing_amount_ex_vat: Number(recurringContractEditor.billingAmount),
      p_vat_rate: Number(recurringContractEditor.vatRate),
      p_payment_terms_days: Number(recurringContractEditor.paymentTermsDays),
      p_starts_on: recurringContractEditor.startsOn,
      p_ends_on: recurringContractEditor.endsOn || null,
      p_notes: recurringContractEditor.notes.trim() || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Contrat récurrent préparé depuis le devis accepté. Aucun mandat ni prélèvement n’a été créé par ARD Manager.",
    );
    if (!result.error) {
      setRecurringContractEditor(null);
      reload();
    }
  }
  async function saveClient(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    const payload = {
      name: clientDraft.name?.trim(),
      category: clientDraft.category?.trim() || null,
      client_type: clientDraft.client_type || "standard",
      address: clientDraft.address?.trim() || null,
      siret: clientDraft.siret?.trim() || null,
      vat_number: clientDraft.vat_number?.trim() || null,
      contact_first_name: clientDraft.contact_first_name?.trim() || null,
      contact_last_name: clientDraft.contact_last_name?.trim() || null,
      contact_title: clientDraft.contact_title?.trim() || null,
      contact_email: clientDraft.contact_email?.trim().toLowerCase() || null,
      contact_phone: clientDraft.contact_phone?.trim() || null,
      billing_emission: clientDraft.billing_emission || "month_end",
      billing_grouping: clientDraft.billing_grouping || "grouped",
      payment_terms_mode: clientDraft.payment_terms_mode || null,
      payment_terms_days:
        clientDraft.payment_terms_mode === "days_after_issue"
          ? clientDraft.payment_terms_days ?? data.organization?.default_invoice_due_days ?? 30
          : null,
      target_margin_rate: clientDraft.target_margin_rate ?? null,
      average_round_trip_minutes:
        clientDraft.average_round_trip_minutes ?? null,
      monthly_admin_minutes: clientDraft.monthly_admin_minutes ?? null,
      incident_count: clientDraft.incident_count || 0,
      strategic_priority: !!clientDraft.strategic_priority,
      notes: clientDraft.notes?.trim() || null,
      scoring_notes: clientDraft.scoring_notes?.trim() || null,
      commission_enabled: !!clientDraft.commission_enabled,
      commission_rate: clientDraft.commission_enabled
        ? clientDraft.commission_rate ?? null
        : null,
      commission_basis: clientDraft.commission_basis || "ex_vat",
      commission_vat_rate: clientDraft.commission_vat_rate ?? 20,
      commission_label:
        clientDraft.commission_label?.trim() || "Commission de gestion",
    };
    let paymentTermsDeferred = false;
    const primarySave = await supabase!
      .from("clients")
      .update(payload)
      .eq("id", selected.id)
      .select(
        "id,name,profile_id,category,client_type,address,siret,vat_number,contact_first_name,contact_last_name,contact_title,contact_email,contact_phone,notes,calendar_enabled,billing_emission,billing_grouping,payment_terms_mode,payment_terms_days,target_margin_rate,average_round_trip_minutes,monthly_admin_minutes,incident_count,strategic_priority,scoring_notes,commission_enabled,commission_rate,commission_basis,commission_vat_rate,commission_label",
      )
      .single();
    let saved: Client | null = primarySave.data as Client | null,
      error = primarySave.error;
    if (error && /payment_terms_|schema cache|column/i.test(error.message)) {
      const {
          payment_terms_mode: _paymentTermsMode,
          payment_terms_days: _paymentTermsDays,
          ...legacyPayload
        } = payload,
        retry = await supabase!
          .from("clients")
          .update(legacyPayload)
          .eq("id", selected.id)
          .select(
            "id,name,profile_id,category,client_type,address,siret,vat_number,contact_first_name,contact_last_name,contact_title,contact_email,contact_phone,notes,calendar_enabled,billing_emission,billing_grouping,target_margin_rate,average_round_trip_minutes,monthly_admin_minutes,incident_count,strategic_priority,scoring_notes,commission_enabled,commission_rate,commission_basis,commission_vat_rate,commission_label",
          )
          .single();
      saved = retry.data as Client | null;
      error = retry.error;
      paymentTermsDeferred = !error;
    }
    setBusy(false);
    setMessage(
      error
        ? error.message
        : paymentTermsDeferred
          ? "Fiche client enregistrée. Les conditions de paiement seront disponibles après activation de la migration préparée."
          : "Fiche client enregistrée.",
    );
    if (!error) {
      setSelected(saved as Client);
      setClientDraft(saved as Client);
      reload();
    }
  }
  function editProperty(property?: ClientProperty) {
    setPropertyEditor(
      property
        ? {
            id: property.id,
            name: property.name,
            address: property.address,
            contactName: property.contact_name || "",
            accessNotes: property.access_notes || "",
            propertyTypeId: property.property_type_id || "",
          }
        : { name: "", address: "", contactName: "", accessNotes: "", propertyTypeId: "" },
    );
    setMessage("");
  }
  async function saveProperty(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !propertyEditor || !data.organization) return;
    setBusy(true);
    const result = await supabase!.rpc("save_client_property_with_type", {
      p_property_id: propertyEditor.id || null,
      p_client_id: selected.id,
      p_name: propertyEditor.name.trim(),
      p_address: propertyEditor.address.trim(),
      p_contact_name: propertyEditor.contactName.trim() || null,
      p_access_notes: propertyEditor.accessNotes.trim() || null,
      p_property_type_id: propertyEditor.propertyTypeId || null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : propertyEditor.id
          ? "Bien modifié dans la fiche client."
          : "Bien ajouté à la fiche client.",
    );
    if (!result.error) {
      setPropertyEditor(null);
      reload();
    }
  }
  function archiveProperty(property: ClientProperty) {
    const hasFutureMission = data.missions.some(
        (mission) =>
          mission.property_id === property.id &&
          new Date(mission.starts_at) >= new Date() &&
          !["cancelled", "completed"].includes(mission.status),
      ),
      hasRecurrence = (data.recurrences || []).some(
        (recurrence) => recurrence.property_id === property.id && recurrence.active,
      );
    if (hasFutureMission || hasRecurrence) {
      setMessage(
        "Ce bien possède encore des missions futures ou une récurrence active. Traitez-les avant de l’archiver.",
      );
      return;
    }
    setPropertyArchive({ property, reason: "" });
    setMessage("");
  }
  async function confirmPropertyArchive(e: React.FormEvent) {
    e.preventDefault();
    if (!propertyArchive) return;
    setBusy(true);
    let { error } = await supabase!.rpc("archive_client_property", {
      p_property_id: propertyArchive.property.id,
      p_reason: propertyArchive.reason.trim(),
    });
    let legacyArchive = false;
    if (
      error &&
      /could not find the function|schema cache|does not exist/i.test(error.message)
    ) {
      const propertyResult = await supabase!
        .from("client_properties")
        .update({ active: false })
        .eq("id", propertyArchive.property.id);
      const serviceResult = propertyResult.error
        ? { error: null }
        : await supabase!
            .from("property_services")
            .update({ active: false })
            .eq("property_id", propertyArchive.property.id);
      error = propertyResult.error || serviceResult.error;
      legacyArchive = !error;
    }
    setBusy(false);
    setMessage(
      error
        ? error.message
        : legacyArchive
          ? "Bien et prestations archivés. L’historique est conservé ; le journal renforcé sera actif après déploiement de la migration préparée."
          : "Bien et prestations archivés en une seule opération. Le motif et les valeurs précédentes sont conservés dans le journal.",
    );
    if (!error) {
      setPropertyArchive(null);
      reload();
    }
  }
  function editCommercialService(
    service?: ClientService | PropertyService,
    scope: "client" | "property" = "property",
    propertyId = "",
  ) {
    const rule = service
      ? (data.serviceBillingRules || []).find((item) =>
          scope === "property"
            ? item.property_service_id === service.id
            : item.client_service_id === service.id,
        )
      : null;
    setServiceEditor(
      service
        ? {
            id: service.id,
            scope,
            propertyId:
              scope === "property"
                ? (service as PropertyService).property_id
                : "",
            name: service.name,
            billingMode: service.billing_mode,
            price: String(service.unit_price_ex_vat),
            vat: String((service as PropertyService).vat_rate ?? 20),
            duration: String((service as PropertyService).default_duration_hours ?? ""),
            notes: (service as PropertyService).notes || "",
            treatment: rule?.billing_treatment || "standard",
            supplierCost: String(rule?.supplier_unit_cost_ex_vat ?? ""),
            targetMode: rule?.target_mode || data.organization?.profitability_target_mode || "margin",
            targetRate: String(rule?.target_rate ?? data.organization?.profitability_target_rate ?? 30),
            disbursementPrincipal: rule?.disbursement_principal_name || "",
            disbursementMandate: rule?.disbursement_mandate_reference || "",
            fiscalWarningAcknowledged: !!rule?.fiscal_warning_acknowledged_at,
          }
        : {
            scope: propertyId ? "property" : scope,
            propertyId,
            name: "",
            billingMode: "fixed",
            price: "",
            vat: String(data.organization?.default_vat_rate ?? 20),
            duration: "",
            notes: "",
            treatment: "standard",
            supplierCost: "",
            targetMode: data.organization?.profitability_target_mode || "margin",
            targetRate: String(data.organization?.profitability_target_rate ?? 30),
            disbursementPrincipal: "",
            disbursementMandate: "",
            fiscalWarningAcknowledged: false,
          },
    );
    setMessage("");
  }
  async function saveCommercialService(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !serviceEditor || !data.organization) return;
    if (serviceEditor.scope === "property" && !serviceEditor.propertyId) {
      setMessage("Choisissez le bien concerné.");
      return;
    }
    setBusy(true);
    let result = await supabase!.rpc("save_service_definition_with_billing_rule", {
      p_scope: serviceEditor.scope,
      p_service_id: serviceEditor.id || null,
      p_client_id: selected.id,
      p_property_id: serviceEditor.scope === "property" ? serviceEditor.propertyId : null,
      p_name: serviceEditor.name.trim(),
      p_billing_mode: serviceEditor.billingMode,
      p_unit_price_ex_vat: Number(serviceEditor.price),
      p_vat_rate: Number(serviceEditor.vat),
      p_default_duration_hours: serviceEditor.duration ? Number(serviceEditor.duration) : null,
      p_notes: serviceEditor.notes.trim() || null,
      p_billing_treatment: serviceEditor.treatment,
      p_supplier_unit_cost_ex_vat: serviceEditor.treatment === "standard" ? null : Number(serviceEditor.supplierCost),
      p_target_mode: serviceEditor.treatment === "recharge" ? serviceEditor.targetMode : null,
      p_target_rate: serviceEditor.treatment === "recharge" ? Number(serviceEditor.targetRate) : null,
      p_disbursement_principal_name: serviceEditor.treatment === "disbursement" ? serviceEditor.disbursementPrincipal.trim() : null,
      p_disbursement_mandate_reference: serviceEditor.treatment === "disbursement" ? serviceEditor.disbursementMandate.trim() : null,
      p_fiscal_warning_acknowledged: serviceEditor.treatment === "disbursement" && serviceEditor.fiscalWarningAcknowledged,
    });
    if (
      result.error &&
      serviceEditor.treatment === "standard" &&
      /could not find the function|schema cache|does not exist/i.test(result.error.message)
    ) {
      const table = serviceEditor.scope === "property" ? "property_services" : "client_services",
        payload = serviceEditor.scope === "property"
          ? { organization_id:data.organization.id,property_id:serviceEditor.propertyId,name:serviceEditor.name.trim(),billing_mode:serviceEditor.billingMode,unit_price_ex_vat:Number(serviceEditor.price),vat_rate:Number(serviceEditor.vat),default_duration_hours:serviceEditor.duration?Number(serviceEditor.duration):null,notes:serviceEditor.notes.trim()||null,active:true }
          : { organization_id:data.organization.id,client_id:selected.id,name:serviceEditor.name.trim(),billing_mode:serviceEditor.billingMode,unit_price_ex_vat:Number(serviceEditor.price),vat_rate:Number(serviceEditor.vat),default_duration_hours:serviceEditor.duration?Number(serviceEditor.duration):null,active:true };
      const serviceTable = supabase!.from(table) as any;
      result = serviceEditor.id
        ? await serviceTable.update(payload).eq("id", serviceEditor.id)
        : await serviceTable.insert(payload);
    }
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : serviceEditor.id
          ? "Prestation modifiée. Les anciennes missions et pièces commerciales conservent leur tarif figé."
          : "Prestation ajoutée à la fiche client.",
    );
    if (!result.error) {
      setServiceEditor(null);
      reload();
    }
  }
  async function archiveCommercialService(
    service: ClientService | PropertyService,
    scope: "client" | "property",
  ) {
    if (!window.confirm(`Archiver la prestation « ${service.name} » ?`)) return;
    setBusy(true);
    const { error } = await supabase!
      .from(scope === "property" ? "property_services" : "client_services")
      .update({ active: false })
      .eq("id", service.id);
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Prestation archivée sans modifier les missions, devis ou factures existants.",
    );
    if (!error) reload();
  }
  async function saveTurnoverDeclaration(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !data.organization) return;
    const amount = Number(turnoverAmount), rate = Number(selected.commission_rate || 0);
    if (!selected.commission_enabled || rate <= 0) {
      setMessage("Activez d’abord la commission et renseignez son taux dans la fiche client.");
      return;
    }
    if (!turnoverPeriod || !Number.isFinite(amount) || amount < 0) {
      setMessage("Renseignez un mois et un chiffre d’affaires valide.");
      return;
    }
    setBusy(true);
    const result = await supabase!.from("client_turnover_declarations").upsert(
      {
        organization_id: data.organization.id,
        client_id: selected.id,
        period: `${turnoverPeriod}-01`,
        turnover_amount: amount,
        turnover_basis: selected.commission_basis || "ex_vat",
        commission_rate: rate,
        commission_amount_ex_vat: 0,
        vat_rate: selected.commission_vat_rate ?? 20,
        label: selected.commission_label || "Commission de gestion",
        source_reference: turnoverReference.trim() || null,
        notes: turnoverNotes.trim() || null,
        status: "ready",
      },
      { onConflict: "client_id,period" },
    );
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Chiffre d’affaires enregistré et commission calculée. Elle reste à valider avant facturation.",
    );
    if (!result.error) {
      setTurnoverAmount("");
      setTurnoverReference("");
      setTurnoverNotes("");
      reload();
    }
  }
  async function invoiceTurnoverDeclaration(declaration: ClientTurnoverDeclaration) {
    setBusy(true);
    const error = await addTurnoverCommissionToInvoice(declaration, data);
    setBusy(false);
    setMessage(
      error || "Commission ajoutée au brouillon de facture du mois, avec sa base et son taux.",
    );
    if (!error) reload();
  }
  async function uploadClientDocument(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !documentFile || !data.organization) return;
    setBusy(true);
    const safeName = documentFile.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      ownerFolder = selected.profile_id || `admin/clients/${selected.id}`,
      storagePath = `${data.organization.id}/${ownerFolder}/${Date.now()}-${safeName}`,
      stored = await supabase!.storage
        .from("private-documents")
        .upload(storagePath, documentFile, { upsert: false });
    if (stored.error) {
      setBusy(false);
      setMessage(stored.error.message);
      return;
    }
    const saved = await supabase!
      .from("documents")
      .insert({
        organization_id: data.organization.id,
        owner_profile_id: selected.profile_id || null,
        kind: documentKind,
        storage_path: storagePath,
        original_name: documentFile.name,
        status: "validated",
      })
      .select("id")
      .single();
    if (!saved.error && documentContractId) {
      const linked = await supabase!
        .from("client_contracts")
        .update({ document_id: saved.data.id })
        .eq("id", documentContractId)
        .eq("client_id", selected.id);
      if (linked.error) {
        setBusy(false);
        setMessage(
          `Pièce déposée mais non reliée au contrat : ${linked.error.message}`,
        );
        reload();
        return;
      }
    }
    setBusy(false);
    setMessage(
      saved.error
        ? saved.error.message
        : "Pièce ajoutée à la fiche client et conservée dans les documents privés.",
    );
    if (!saved.error) {
      setDocumentFile(null);
      setDocumentContractId("");
      reload();
    }
  }
  const operationalReadinessFor = (clientId: string) => {
    const client = data.clients.find((item) => item.id === clientId),
      activeProperties = data.properties.filter(
        (property) => property.client_id === clientId && property.active,
      ),
      activeServices = data.services.filter(
        (service) =>
          service.client_id === clientId && service.active !== false,
      ),
      activePropertyIds = new Set(
        activeProperties.map((property) => property.id),
      ),
      activePropertyServices = data.propertyServices.filter(
        (service) =>
          service.active && activePropertyIds.has(service.property_id),
      ),
      baseServiceCountByProperty = activePropertyServices.reduce(
        (counts, service) => {
          counts.set(
            service.property_id,
            (counts.get(service.property_id) || 0) + 1,
          );
          return counts;
        },
        new Map<string, number>(),
      ),
      propertiesWithoutAddress = activeProperties.filter(
        (property) => !property.address?.trim(),
      ).length,
      propertiesWithoutType = activeProperties.filter(
        (property) => !property.property_type_id,
      ).length,
      propertiesWithoutSingleBase = activeProperties.filter(
        (property) =>
          (baseServiceCountByProperty.get(property.id) || 0) !== 1,
      ).length,
      baseServicesWithoutPrice = activePropertyServices.filter(
        (service) =>
          service.unit_price_ex_vat == null ||
          !Number.isFinite(Number(service.unit_price_ex_vat)) ||
          Number(service.unit_price_ex_vat) < 0,
      ).length,
      baseServicesWithoutDuration = activePropertyServices.filter(
        (service) =>
          service.default_duration_hours == null ||
          !Number.isFinite(Number(service.default_duration_hours)) ||
          Number(service.default_duration_hours) <= 0,
      ).length,
      supplementsWithoutPrice = activeServices.filter(
        (service) =>
          service.unit_price_ex_vat == null ||
          !Number.isFinite(Number(service.unit_price_ex_vat)) ||
          Number(service.unit_price_ex_vat) < 0,
      ).length,
      supplementsWithoutDuration = activeServices.filter(
        (service) =>
          service.default_duration_hours == null ||
          !Number.isFinite(Number(service.default_duration_hours)) ||
          Number(service.default_duration_hours) <= 0,
      ).length,
      issues = [
        activeProperties.length === 0 && activeServices.length === 0
          ? "Aucun bien ni prestation client active pour préparer une mission."
          : null,
        activeProperties.length === 0 &&
        activeServices.length > 0 &&
        !client?.address?.trim()
          ? "Adresse client manquante pour les missions sans bien."
          : null,
        propertiesWithoutAddress > 0
          ? `${propertiesWithoutAddress} bien(s) sans adresse exploitable.`
          : null,
        propertiesWithoutType > 0
          ? `${propertiesWithoutType} bien(s) sans type de bien.`
          : null,
        propertiesWithoutSingleBase > 0
          ? `${propertiesWithoutSingleBase} bien(s) sans tarif de base unique.`
          : null,
        baseServicesWithoutPrice > 0
          ? `${baseServicesWithoutPrice} tarif(s) de base sans prix exploitable.`
          : null,
        baseServicesWithoutDuration > 0
          ? `${baseServicesWithoutDuration} tarif(s) de base sans durée prévisionnelle.`
          : null,
        supplementsWithoutPrice > 0
          ? `${supplementsWithoutPrice} supplément(s) client sans prix exploitable.`
          : null,
        supplementsWithoutDuration > 0
          ? `${supplementsWithoutDuration} supplément(s) client sans durée prévisionnelle.`
          : null,
      ].filter((issue): issue is string => Boolean(issue));
    return {
      activePropertyCount: activeProperties.length,
      distinctPropertyTypeCount: new Set(
        activeProperties
          .map((property) => property.property_type_id)
          .filter(Boolean),
      ).size,
      activeBaseServiceCount: activePropertyServices.length,
      activeSupplementCount: activeServices.length,
      issues,
      ready:
        (activeProperties.length > 0 || activeServices.length > 0) &&
        issues.length === 0,
    };
  };
  const detail = selected
    ? (() => {
        const missions = data.missions.filter(
            (mission) => mission.client_id === selected.id,
          ),
          missionIds = new Set(missions.map((mission) => mission.id)),
          validatedMissionIds = new Set(
            missions
              .filter((mission) => mission.admin_validated_at != null)
              .map((mission) => mission.id),
          ),
          assignments = data.assignments.filter(
            (assignment) =>
              validatedMissionIds.has(assignment.mission_id) &&
              !["cancelled", "refused"].includes(assignment.status),
          ),
          invoices = data.invoices.filter(
            (invoice) => invoice.client_id === selected.id,
          ),
          invoiceIds = new Set(invoices.map((invoice) => invoice.id)),
          payments = (data.invoicePayments || []).filter(
            (payment) =>
              invoiceIds.has(payment.invoice_id) &&
              payment.status === "confirmed",
          ),
          quotes = data.quotes.filter(
            (quote) => quote.client_id === selected.id,
          ),
          expenses = data.expenses.filter(
            (expense) => expense.client_id === selected.id,
          ),
          properties = data.properties.filter(
            (property) => property.client_id === selected.id,
          ),
          propertyIds = new Set(properties.map((property) => property.id)),
          services = data.services.filter(
            (service) => service.client_id === selected.id,
          ),
          propertyServices = data.propertyServices.filter((service) =>
            propertyIds.has(service.property_id),
          ),
          preferred = data.preferredWorkers.filter(
            (item) => item.client_id === selected.id && item.active,
          ),
          recurrences = (data.recurrences || []).filter(
            (item) => item.client_id === selected.id,
          ),
          contact = (data.profiles || []).find(
            (profile) => profile.id === selected.profile_id,
          ),
          documents = data.documents.filter(
            (document) => document.owner_profile_id === selected.profile_id,
          ),
          clientContracts = (data.clientContracts || []).filter(
            (contract) => contract.client_id === selected.id,
          ),
          clientRequirements = (data.clientRequirements || []).filter(
            (requirement) => requirement.client_id === selected.id,
          ),
          turnoverDeclarations = (data.turnoverDeclarations || []).filter(
            (declaration) => declaration.client_id === selected.id,
          ),
          invoicedRevenue = invoices
            .filter(
              (invoice) =>
                !["cancelled", "credited", "draft"].includes(invoice.status),
            )
            .reduce((sum, invoice) => sum + invoice.total_ex_vat, 0),
          toInvoice = missions
            .filter((mission) => mission.billing_status === "to_invoice")
            .reduce((sum, mission) => sum + (mission.amount_ex_vat || 0), 0),
          monthlyWorkerMonths = new Set<string>(),
          laborCosts = assignments.reduce(
            (total, assignment) => {
            const worker = data.workers.find(
              (item) => item.id === assignment.worker_id,
              ),
              mission = missions.find(
                (item) => item.id === assignment.mission_id,
              );
            if (!worker)
              return { ...total, incomplete: true };
            if (worker?.kind === "contractor") {
              if (
                assignment.pricing_mode === "monthly" ||
                worker.pricing_mode === "monthly"
              ) {
                const key = `${worker.id}-${mission ? parisDay(mission.starts_at).slice(0, 7) : "unknown"}`;
                if (monthlyWorkerMonths.has(key)) return total;
                monthlyWorkerMonths.add(key);
                const amount = worker.monthly_fee ?? assignment.agreed_amount;
                return {
                  ...total,
                  contractor: total.contractor + Number(amount || 0),
                  incomplete: total.incomplete || amount == null,
                };
              }
              if (assignment.agreed_amount != null)
                return {
                  ...total,
                  contractor:
                    total.contractor + Number(assignment.agreed_amount),
                };
              if (
                (assignment.pricing_mode === "hourly" ||
                  worker.pricing_mode === "hourly") &&
                (assignment.agreed_rate != null || worker.hourly_rate != null)
              )
                return {
                  ...total,
                  contractor:
                    total.contractor +
                    Number(assignment.assigned_hours || 0) *
                      Number(assignment.agreed_rate ?? worker.hourly_rate ?? 0),
                };
              const fixed = assignment.agreed_rate ?? worker.fixed_fee;
              return {
                ...total,
                contractor: total.contractor + Number(fixed || 0),
                incomplete: total.incomplete || fixed == null,
              };
            }
            const monthlyHours = Number(worker.monthly_hours || 0),
              knownMonthlyCost =
                Number(worker.employer_cost_monthly ?? worker.net_salary ?? 0) +
                Number(worker.ticket_count || 0) * Number(worker.ticket_value || 0),
              cost =
                monthlyHours > 0
                  ? (knownMonthlyCost / monthlyHours) *
                    Number(assignment.assigned_hours || 0)
                  : 0;
            return {
              ...total,
              employee: total.employee + cost,
              incomplete:
                total.incomplete ||
                monthlyHours <= 0 ||
                worker.employer_cost_monthly == null,
            };
          },
            { contractor: 0, employee: 0, incomplete: false },
          ),
          directExpenses = expenses.reduce(
            (sum, expense) =>
              expense.status !== "approved" ||
              (expense.mission_id != null &&
                !validatedMissionIds.has(expense.mission_id))
                ? sum
                : sum + expense.amount_ex_vat,
            0,
          ),
          mileageCosts = data.mileages
            .filter(
              (entry) =>
                !!entry.mission_id &&
                validatedMissionIds.has(entry.mission_id) &&
                entry.status === "approved",
            )
            .reduce(
              (sum, entry) => sum + Number(entry.calculated_amount || 0),
              0,
            ),
          documentedCosts =
            laborCosts.contractor +
            laborCosts.employee +
            directExpenses +
            mileageCosts,
          collected = payments.reduce(
            (sum, payment) => sum + Number(payment.amount || 0),
            0,
          ),
          remainingToCollect = invoices
            .filter(
              (invoice) =>
                !["cancelled", "credited", "draft"].includes(invoice.status),
            )
            .reduce(
              (sum, invoice) =>
                sum +
                Math.max(
                  0,
                  Number(invoice.total_inc_vat || 0) -
                    Number(invoice.amount_paid || 0),
                ),
              0,
            ),
          provisionalMargin = invoicedRevenue - documentedCosts,
          operationalReadiness = operationalReadinessFor(selected.id);
        return {
          missions,
          invoices,
          payments,
          quotes,
          expenses,
          properties,
          services,
          propertyServices,
          preferred,
          recurrences,
          contact,
          documents,
          clientContracts,
          clientRequirements,
          turnoverDeclarations,
          invoicedRevenue,
          toInvoice,
          documentedCosts,
          laborCosts,
          directExpenses,
          mileageCosts,
          collected,
          remainingToCollect,
          provisionalMargin,
          operationalReadiness,
        };
      })()
    : null;
  const rankedClients = data.clients.map((client) => ({
      client,
      metrics: scores.get(client.id)!,
    })),
    activePortfolioReviews = new Map(
      (data.clientPortfolioReviews || [])
        .filter((item) => item.status === "active")
        .map((item) => [item.client_id, item]),
    ),
    portfolioClassFor = (client: Client) => {
      const review = activePortfolioReviews.get(client.id);
      if (review) return { value: review.decided_class, reviewed: true };
      const score = scores.get(client.id)!;
      return {
        value: suggestClientPortfolioClass(
          client,
          score,
          Number(client.target_margin_rate ?? data.organization?.target_margin_rate ?? 30),
        ).suggested,
        reviewed: false,
      };
    },
    portfolioCounts = (["A", "B", "C"] as PortfolioClass[]).map((value) => ({
      value,
      count: data.clients.filter((client) => portfolioClassFor(client).value === value).length,
      reviewed: data.clients.filter((client) => {
        const classification = portfolioClassFor(client);
        return classification.value === value && classification.reviewed;
      }).length,
    })),
    operationalReadinessByClient = new Map(
      data.clients.map((client) => [
        client.id,
        operationalReadinessFor(client.id),
      ]),
    ),
    readyClientCount = data.clients.filter(
      (client) => operationalReadinessByClient.get(client.id)?.ready,
    ).length,
    attentionClientCount = data.clients.length - readyClientCount,
    displayedClients = data.clients.filter((client) => {
      if (readinessFilter === "all") return true;
      const ready = operationalReadinessByClient.get(client.id)?.ready === true;
      return readinessFilter === "ready" ? ready : !ready;
    }),
    rankings = [
      {
        title: "Top CA",
        items: [...rankedClients].sort(
          (a, b) => b.metrics.revenue - a.metrics.revenue,
        ),
        value: (item: (typeof rankedClients)[number]) =>
          money(item.metrics.revenue),
      },
      {
        title: "Top marge documentée",
        items: [...rankedClients].sort(
          (a, b) => b.metrics.margin - a.metrics.margin,
        ),
        value: (item: (typeof rankedClients)[number]) =>
          money(item.metrics.margin),
      },
      {
        title: "Top scoring",
        items: [...rankedClients].sort(
          (a, b) => b.metrics.score - a.metrics.score,
        ),
        value: (item: (typeof rankedClients)[number]) =>
          `${item.metrics.score}/100`,
      },
    ];
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Portefeuille</span>
          <h1>Clients</h1>
        </div>
      </div>
      <section className="admin-kpis portfolio-overview" aria-label="Répartition du portefeuille clients">
        {portfolioCounts.map((item) => (
          <article key={item.value}>
            <span>Classe {item.value}</span>
            <strong>{item.count}</strong>
            <small>{item.reviewed} décision(s) validée(s) · le reste est proposé</small>
          </article>
        ))}
      </section>
      <Notice>
        A = développer, B = optimiser ou conserver, C = décider et corriger. Les
        classes non encore revues sont clairement marquées comme propositions.
      </Notice>
      <details
        className="billing-kpi-guide client-portfolio-analysis"
        data-testid="client-portfolio-analysis"
      >
        <summary>Analyser les classements du portefeuille</summary>
        <div className="client-portfolio-analysis-body">
          <p>
            Ouvrez ce chapitre uniquement pour comparer le chiffre d’affaires,
            la marge documentée et le scoring. La liste opérationnelle des
            clients reste immédiatement accessible dessous.
          </p>
          <div className="client-ranking-grid">
            {rankings.map((ranking) => (
              <section className="panel" key={ranking.title}>
                <h2>{ranking.title}</h2>
                {ranking.items.slice(0, 10).map((item, index) => (
                  <button
                    className="ranking-row"
                    key={item.client.id}
                    onClick={() => openClient(item.client)}
                  >
                    <b>{index + 1}</b>
                    <span>{item.client.name}</span>
                    <strong>{ranking.value(item)}</strong>
                  </button>
                ))}
              </section>
            ))}
          </div>
          <p className="billing-kpi-guide-limit">
            La marge est limitée aux coûts connus et validés. Le scoring aide à
            préparer une décision ; il ne classe jamais définitivement un
            client sans revue humaine.
          </p>
        </div>
      </details>
      <section className="client-readiness-toolbar" aria-label="Pilotage de la préparation des missions">
        <div>
          <span className="eyebrow">Préparation opérationnelle</span>
          <h2>Quels clients sont prêts pour planifier ?</h2>
          <p>
            Le contrôle rapproche automatiquement les biens, adresses, types,
            tarifs de base, suppléments, prix et durées.
          </p>
        </div>
        <div className="segmented client-readiness-filters" role="group" aria-label="Filtrer la préparation des missions par client">
          <button
            type="button"
            className={readinessFilter === "all" ? "active" : ""}
            onClick={() => setReadinessFilter("all")}
          >
            Tous ({data.clients.length})
          </button>
          <button
            type="button"
            className={readinessFilter === "ready" ? "active" : ""}
            onClick={() => setReadinessFilter("ready")}
          >
            Missions prêtes ({readyClientCount})
          </button>
          <button
            type="button"
            className={readinessFilter === "attention" ? "active" : ""}
            onClick={() => setReadinessFilter("attention")}
          >
            À compléter ({attentionClientCount})
          </button>
        </div>
      </section>
      <section className="panel table-wrap client-list-table">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Adresse</th>
              <th>Missions</th>
              <th>À facturer</th>
              <th>Score</th>
              <th>Portefeuille</th>
              <th>Accès &amp; conformité</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayedClients.map((c) => {
              const missions = data.missions.filter(
                (m) => m.client_id === c.id,
              ),
                access = organizationAccessSummary(
                  "client",
                  c.id,
                  Boolean(c.profile_id),
                  data.organizationUserInvitations,
                  data.profiles?.find((profile) => profile.id === c.profile_id)?.access_status,
                ),
                activeContract = (data.clientContracts || []).find(
                  (contract) => contract.client_id === c.id && ["signed", "active"].includes(contract.status),
                ),
                pendingDocuments = (data.clientRequirements || []).filter(
                  (requirement) => requirement.client_id === c.id && !["validated", "waived"].includes(requirement.status),
                ).length,
                operationalReadiness = operationalReadinessFor(c.id);
              return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <small>{c.category || ""}</small>
                  </td>
                  <td>
                    {c.client_type === "concierge"
                      ? "Conciergerie"
                      : "Standard"}
                  </td>
                  <td>{c.address || "—"}</td>
                  <td>{missions.length}</td>
                  <td>
                    {money(
                      missions
                        .filter((m) => m.billing_status === "to_invoice")
                        .reduce((s, m) => s + (m.amount_ex_vat || 0), 0),
                    )}
                  </td>
                  <td>
                    <strong>{scores.get(c.id)?.score || 0}/100</strong>
                  </td>
                  <td>
                    {(() => {
                      const classification = portfolioClassFor(c);
                      return (
                        <span className={`portfolio-class class-${classification.value}`}>
                          {classification.value}{classification.reviewed ? " · décidée" : " · proposée"}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="directory-readiness">
                      <span className={`status-badge ${accessToneClass(access.tone)}`}>{access.label}</span>
                      <small>{access.detail}</small>
                      <small>{activeContract ? "Contrat à jour" : "Contrat à contrôler"} · {pendingDocuments ? `${pendingDocuments} document(s) à traiter` : "documents à jour"}</small>
                      <small data-testid="client-directory-operational-readiness">
                        {operationalReadiness.ready
                          ? `Missions prêtes · ${operationalReadiness.activePropertyCount} bien(s) · ${operationalReadiness.activeBaseServiceCount + operationalReadiness.activeSupplementCount} tarif(s)`
                          : `Référentiel missions à compléter · ${operationalReadiness.issues.length} point(s)`}
                      </small>
                    </div>
                  </td>
                  <td>
                    <button
                      className="secondary compact"
                      onClick={() => openClient(c)}
                    >
                      Ouvrir la fiche
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!displayedClients.length && (
          <p className="empty">
            {data.clients.length
              ? "Aucun client ne correspond à ce filtre."
              : "Aucun client. Utilisez le bouton Créer pour ajouter le premier."}
          </p>
        )}
      </section>
      {selected && detail && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail client-control-file">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Fiche client complète</span>
                <h2>{selected.name}</h2>
                <small>
                  {selected.client_type === "concierge"
                    ? "Conciergerie"
                    : "Client standard"}
                  {selected.category ? ` · ${selected.category}` : ""}
                </small>
              </div>
              <div className="row-actions">
                <button type="button" className="secondary compact" onClick={() => setFinancialClientOpen(true)}>
                  Synthèse financière
                </button>
                <button className="icon-close" onClick={() => setSelected(null)} aria-label="Fermer">
                  ×
                </button>
              </div>
            </div>
            {message && (
              <Notice
                tone={
                  message.includes("enregistrée") ||
                  message.includes("ajoutée") ||
                  message.includes("enregistré")
                    ? "success"
                    : "error"
                }
              >
                {message}
              </Notice>
            )}
            <div className="admin-kpis client-file-kpis">
              <article>
                <span>CA facturé HT</span>
                <strong>{money(detail.invoicedRevenue)}</strong>
              </article>
              <article>
                <span>Règlements encaissés</span>
                <strong>{money(detail.collected)}</strong>
                <small>Règlements confirmés</small>
              </article>
              <article>
                <span>Reste à encaisser TTC</span>
                <strong>{money(detail.remainingToCollect)}</strong>
              </article>
              <article>
                <span>À facturer</span>
                <strong>{money(detail.toInvoice)}</strong>
              </article>
              <article>
                <span>Coûts directs validés</span>
                <strong>{money(detail.documentedCosts)}</strong>
                <small>Uniquement après validation administrative de la mission</small>
              </article>
              <article>
                <span>Marge provisoire documentée</span>
                <strong>{money(detail.provisionalMargin)}</strong>
              </article>
            </div>
            <details
              className="billing-kpi-guide"
              data-testid="client-kpi-guide"
            >
              <summary>Comprendre la synthèse financière du client</summary>
              <div className="billing-kpi-guide-grid">
                <article>
                  <strong>CA facturé HT</strong>
                  <span>
                    Total HT des factures émises pour ce client. Les brouillons,
                    avoirs et factures annulées sont exclus.
                  </span>
                </article>
                <article>
                  <strong>Règlements encaissés</strong>
                  <span>
                    Somme des règlements confirmés et rapprochés des factures du
                    client.
                  </span>
                </article>
                <article>
                  <strong>Reste à encaisser TTC</strong>
                  <span>
                    Solde TTC encore dû sur les factures émises, hors brouillons,
                    avoirs et annulations.
                  </span>
                </article>
                <article>
                  <strong>À facturer</strong>
                  <span>
                    Montant HT des missions validées placées dans la file de
                    facturation, avant création de la facture.
                  </span>
                </article>
                <article>
                  <strong>Coûts directs validés</strong>
                  <span>
                    Coûts ST ou salariés, IK et dépenses approuvées rattachés aux
                    seules missions validées par l’administration.
                  </span>
                </article>
                <article>
                  <strong>Marge provisoire documentée</strong>
                  <span>
                    CA facturé HT moins les coûts directs validés et documentés.
                    Elle reste provisoire tant que tous les coûts ne sont pas
                    renseignés.
                  </span>
                </article>
              </div>
              <p className="billing-kpi-guide-limit">
                Cette vue sert à décider et à contrôler. Elle ne transforme pas
                une mission en facture et ne remplace ni la comptabilité ni le
                rapprochement bancaire.
              </p>
            </details>
            <details
              className="file-chapter client-operational-readiness"
              data-testid="client-operational-readiness"
            >
              <summary>
                <span>
                  <strong>Référentiel missions et tarifs</strong>
                  <small>
                    Biens, types, tarifs de base et suppléments disponibles pour
                    préparer une mission
                  </small>
                </span>
                <span className="file-chapter-summary-value">
                  {detail.operationalReadiness.ready
                    ? "Prêt pour la planification"
                    : `${detail.operationalReadiness.issues.length} point(s) à compléter`}
                </span>
              </summary>
              <div className="file-chapter-body">
                <div className="admin-kpis client-file-kpis">
                  <article>
                    <span>Biens actifs</span>
                    <strong>{detail.operationalReadiness.activePropertyCount}</strong>
                  </article>
                  <article>
                    <span>Types de bien</span>
                    <strong>{detail.operationalReadiness.distinctPropertyTypeCount}</strong>
                  </article>
                  <article>
                    <span>Tarifs de base</span>
                    <strong>{detail.operationalReadiness.activeBaseServiceCount}</strong>
                  </article>
                  <article>
                    <span>Suppléments client</span>
                    <strong>{detail.operationalReadiness.activeSupplementCount}</strong>
                  </article>
                </div>
                {detail.operationalReadiness.ready ? (
                  <Notice tone="success">
                    Le référentiel est complet pour créer et planifier une mission
                    liée à un bien sans ressaisie des tarifs et des temps prévus.
                  </Notice>
                ) : (
                  <Notice tone="warning">
                    {detail.operationalReadiness.issues.join(" ")}
                  </Notice>
                )}
                <small>
                  Les tarifs propres à ce client ou à ses biens restent distincts
                  du catalogue général : ils ne modifient pas les prix des autres
                  clients.
                </small>
                <button
                  type="button"
                  className="secondary compact"
                  onClick={() => {
                    const chapter = document.getElementById(
                      "client-properties-services-chapter",
                    );
                    if (!(chapter instanceof HTMLDetailsElement)) return;
                    chapter.open = true;
                    chapter.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Voir et modifier les biens et tarifs
                </button>
              </div>
            </details>
            <Notice>
              La marge reprend uniquement les missions validées par
              l’administration : coûts ST, coût salarié ventilé selon les heures
              affectées, IK approuvées et dépenses approuvées. Une mission
              simplement marquée « Terminée » reste hors des coûts acquis et de
              la facturation jusqu’à la validation de son compte rendu. Les temps
              de trajet sans calcul API ne sont pas inventés.
              {detail.laborCosts.incomplete &&
                " Au moins un coût employeur ou tarif manque : la lecture reste incomplète."}
            </Notice>
            <details className="file-chapter client-financial-chapter">
              <summary>
                <span>
                  <strong>Lecture financière et arbitrage commercial</strong>
                  <small>Scoring, rentabilité, commission, coûts et règlements</small>
                </span>
                <span className="file-chapter-summary-value">
                  Marge {money(detail.provisionalMargin)}
                </span>
              </summary>
              <div className="file-chapter-body">
            {scores.get(selected.id) && (
              <section className="client-score-card">
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Rentabilité explicable</span>
                    <h3>Scoring client</h3>
                  </div>
                  <strong className="score-total">
                    {scores.get(selected.id)!.score}/100
                  </strong>
                </div>
                <div className="score-components">
                  {scores.get(selected.id)!.components.map((component) => (
                    <div key={component.label}>
                      <span>
                        {component.label} ·{" "}
                        {component.weight >= 25
                          ? "impact principal"
                          : component.weight >= 15
                            ? "impact important"
                            : "impact complémentaire"}
                      </span>
                      <progress max="100" value={component.value} />
                      <b>{Math.round(component.value)}</b>
                    </div>
                  ))}
                </div>
                <ul>
                  {scores.get(selected.id)!.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </section>
            )}
            {scores.get(selected.id) &&
              hasDeploymentCapability(data, "client_portfolio_reviews") && (
                <ClientPortfolioReviewPanel
                  key={`${selected.id}-${activePortfolioReviews.get(selected.id)?.id || "new"}`}
                  client={selected}
                  score={scores.get(selected.id)!}
                  data={data}
                  reload={reload}
                  setMessage={setMessage}
                />
              )}
            <section className="client-turnover-panel">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Facturation mixte</span>
                  <h3>Commission sur chiffre d’affaires</h3>
                </div>
                {selected.commission_enabled && selected.commission_rate ? (
                  <strong>
                    {selected.commission_rate}% du CA {selected.commission_basis === "inc_vat" ? "TTC" : "HT"}
                  </strong>
                ) : (
                  <span className="status">Non activée</span>
                )}
              </div>
              {selected.commission_enabled && selected.commission_rate ? (
                <>
                  <form className="form-grid turnover-entry-form" onSubmit={saveTurnoverDeclaration}>
                    <label>
                      Mois concerné
                      <input
                        type="month"
                        value={turnoverPeriod}
                        onChange={(e) => setTurnoverPeriod(e.target.value)}
                        required
                      />
                    </label>
                    <label>
                      CA réalisé {selected.commission_basis === "inc_vat" ? "TTC" : "HT"}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={turnoverAmount}
                        onChange={(e) => setTurnoverAmount(e.target.value)}
                        placeholder="Ex. 10 000"
                        required
                      />
                    </label>
                    <label>
                      Justificatif / référence
                      <input
                        value={turnoverReference}
                        onChange={(e) => setTurnoverReference(e.target.value)}
                        placeholder="Relevé mensuel, extranet…"
                      />
                    </label>
                    <label className="wide">
                      Note interne
                      <input
                        value={turnoverNotes}
                        onChange={(e) => setTurnoverNotes(e.target.value)}
                      />
                    </label>
                    <div className="turnover-calculation">
                      <span>Commission HT calculée</span>
                      <strong>
                        {money(Number(turnoverAmount || 0) * Number(selected.commission_rate) / 100)}
                      </strong>
                      <small>
                        TVA {selected.commission_vat_rate ?? 20}% appliquée sur la ligne de facture
                      </small>
                    </div>
                    <button disabled={busy}>
                      Enregistrer le CA et calculer
                    </button>
                  </form>
                  <div className="turnover-history">
                    {detail.turnoverDeclarations.map((declaration) => {
                      const invoice = data.invoices.find(
                        (item) => item.id === declaration.invoice_id,
                      );
                      return (
                        <article key={declaration.id}>
                          <div>
                            <strong>
                              {new Intl.DateTimeFormat("fr-FR", {
                                month: "long",
                                year: "numeric",
                              }).format(new Date(`${declaration.period.slice(0, 10)}T12:00:00`))}
                              {" · "}{money(declaration.commission_amount_ex_vat)} HT
                            </strong>
                            <small>
                              Base {money(declaration.turnover_amount)} {declaration.turnover_basis === "inc_vat" ? "TTC" : "HT"}
                              {" · "}{declaration.commission_rate}%
                              {declaration.source_reference ? ` · ${declaration.source_reference}` : ""}
                              {invoice ? ` · ${invoice.number || "brouillon de facture"}` : ""}
                            </small>
                          </div>
                          <div className="row-actions">
                            <Status value={declaration.status} />
                            {declaration.status === "ready" && (
                              <>
                                <button
                                  type="button"
                                  className="secondary compact"
                                  onClick={() => {
                                    setTurnoverPeriod(declaration.period.slice(0, 7));
                                    setTurnoverAmount(String(declaration.turnover_amount));
                                    setTurnoverReference(declaration.source_reference || "");
                                    setTurnoverNotes(declaration.notes || "");
                                  }}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="compact"
                                  disabled={busy}
                                  onClick={() => invoiceTurnoverDeclaration(declaration)}
                                >
                                  Ajouter à la facture
                                </button>
                              </>
                            )}
                          </div>
                        </article>
                      );
                    })}
                    {!detail.turnoverDeclarations.length && (
                      <p className="empty">Aucun chiffre d’affaires mensuel renseigné.</p>
                    )}
                  </div>
                </>
              ) : (
                <Notice>
                  Activez cette règle dans « Modifier la fiche client », puis indiquez le taux contractuel et si la base est le CA HT ou TTC.
                </Notice>
              )}
            </section>
            <div className="client-file-grid client-finance-details">
              <section>
                <h3>Détail des coûts rattachés</h3>
                <dl>
                  <div>
                    <dt>Sous-traitants</dt>
                    <dd>{money(detail.laborCosts.contractor)}</dd>
                  </div>
                  <div>
                    <dt>Salariés ventilés</dt>
                    <dd>{money(detail.laborCosts.employee)}</dd>
                  </div>
                  <div>
                    <dt>IK des missions</dt>
                    <dd>{money(detail.mileageCosts)}</dd>
                  </div>
                  <div>
                    <dt>Achats et frais directs</dt>
                    <dd>{money(detail.directExpenses)}</dd>
                  </div>
                  <div>
                    <dt>Total documenté</dt>
                    <dd>{money(detail.documentedCosts)}</dd>
                  </div>
                </dl>
                {detail.laborCosts.incomplete && (
                  <small className="data-warning">
                    Coût incomplet : renseigner les coûts employeur ou tarifs
                    manquants dans Équipe & RH.
                  </small>
                )}
              </section>
              <section>
                <h3>Règlements confirmés</h3>
                {detail.payments.map((payment) => {
                  const invoice = detail.invoices.find(
                    (item) => item.id === payment.invoice_id,
                  );
                  return (
                    <article className="mini-record" key={payment.id}>
                      <strong>
                        {money(payment.amount)} · {invoice?.number || "Facture"}
                      </strong>
                      <small>
                        {frDate(payment.received_on)}
                        {payment.method ? ` · ${payment.method}` : ""}
                        {payment.reference ? ` · ${payment.reference}` : ""}
                      </small>
                    </article>
                  );
                })}
                {!detail.payments.length && (
                  <p className="empty">Aucun règlement confirmé.</p>
                )}
              </section>
            </div>
              </div>
            </details>
            <div className="client-file-grid">
              <details className="file-chapter">
                <summary>
                  <span>
                    <strong>Identité, contact et accès</strong>
                    <small>
                      {selected.contact_email || detail.contact?.email || "E-mail à compléter"}
                    </small>
                  </span>
                  <span className="file-chapter-summary-value">
                    {selected.profile_id ? "Espace relié" : "Accès à préparer"}
                  </span>
                </summary>
                <div className="file-chapter-body">
                <dl>
                  <div>
                    <dt>Contact</dt>
                    <dd>
                      {[selected.contact_first_name, selected.contact_last_name]
                        .filter(Boolean)
                        .join(" ") || detail.contact?.display_name || "Non renseigné"}
                      {selected.contact_title ? ` · ${selected.contact_title}` : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{selected.contact_email || detail.contact?.email || "—"}</dd>
                  </div>
                  <div>
                    <dt>Téléphone</dt>
                    <dd>{selected.contact_phone || detail.contact?.phone || "—"}</dd>
                  </div>
                  <div>
                    <dt>Adresse</dt>
                    <dd>{selected.address || "—"}</dd>
                  </div>
                  <div>
                    <dt>SIRET / TVA</dt>
                    <dd>
                      {selected.siret || "—"} · {selected.vat_number || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Facturation</dt>
                    <dd>
                      {labels[selected.billing_emission || ""] || "À définir"} ·{" "}
                      {labels[selected.billing_grouping || ""] || "À définir"}
                      {selected.commission_enabled && selected.commission_rate
                        ? ` · ${selected.commission_rate}% du CA ${selected.commission_basis === "inc_vat" ? "TTC" : "HT"}`
                        : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>Conditions de paiement</dt>
                    <dd>
                      {(() => {
                        const terms = resolvePaymentTerms(data.organization, selected);
                        return `${paymentTermsLabel(terms.mode, terms.days)}${selected.payment_terms_mode ? " · spécifique au client" : " · règle société"}`;
                      })()}
                    </dd>
                  </div>
                </dl>
                <AccountInvitationPanel
                  targetType="client"
                  targetId={selected.id}
                  displayName={
                    [selected.contact_first_name, selected.contact_last_name]
                      .filter(Boolean)
                      .join(" ") || detail.contact?.display_name || selected.name
                  }
                  hasAccount={!!selected.profile_id}
                  accountEmail={detail.contact?.email || selected.contact_email}
                  accountProfile={detail.contact}
                  onChanged={reload}
                />
                <details className="client-edit-details">
                  <summary>
                    <strong>Modifier la fiche client</strong>
                  </summary>
                  <form className="form-grid" onSubmit={saveClient}>
                    <label>
                      Nom
                      <input
                        value={clientDraft.name || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            name: e.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label>
                      Catégorie
                      <input
                        value={clientDraft.category || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            category: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Profil
                      <select
                        value={clientDraft.client_type || "standard"}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            client_type: e.target
                              .value as Client["client_type"],
                          }))
                        }
                      >
                        <option value="standard">Client standard</option>
                        <option value="concierge">Conciergerie</option>
                      </select>
                    </label>
                    <label className="wide">
                      Adresse
                      <input
                        value={clientDraft.address || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            address: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      SIRET
                      <input
                        value={clientDraft.siret || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            siret: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      N° de TVA
                      <input
                        value={clientDraft.vat_number || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            vat_number: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <div className="form-section-title wide">
                      <strong>Contact principal</strong>
                      <small>Utilisé pour les contrats, devis, factures, invitations et notifications.</small>
                    </div>
                    <label>
                      Prénom du contact
                      <input
                        value={clientDraft.contact_first_name || ""}
                        onChange={(e) => setClientDraft((current) => ({ ...current, contact_first_name: e.target.value }))}
                      />
                    </label>
                    <label>
                      Nom du contact
                      <input
                        value={clientDraft.contact_last_name || ""}
                        onChange={(e) => setClientDraft((current) => ({ ...current, contact_last_name: e.target.value }))}
                      />
                    </label>
                    <label>
                      Fonction
                      <input
                        value={clientDraft.contact_title || ""}
                        onChange={(e) => setClientDraft((current) => ({ ...current, contact_title: e.target.value }))}
                        placeholder="Gérant, responsable exploitation…"
                      />
                    </label>
                    <label>
                      E-mail du contact
                      <input
                        type="email"
                        value={clientDraft.contact_email || ""}
                        onChange={(e) => setClientDraft((current) => ({ ...current, contact_email: e.target.value }))}
                      />
                    </label>
                    <label>
                      Téléphone du contact
                      <input
                        type="tel"
                        value={clientDraft.contact_phone || ""}
                        onChange={(e) => setClientDraft((current) => ({ ...current, contact_phone: e.target.value }))}
                      />
                    </label>
                    <label>
                      Émission des factures
                      <select
                        value={clientDraft.billing_emission || "month_end"}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            billing_emission: e.target.value,
                          }))
                        }
                      >
                        <option value="month_end">Fin de mois</option>
                        <option value="immediate">Immédiate</option>
                      </select>
                    </label>
                    <label>
                      Regroupement
                      <select
                        value={clientDraft.billing_grouping || "grouped"}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            billing_grouping: e.target.value,
                          }))
                        }
                      >
                        <option value="grouped">Regroupée</option>
                        <option value="individual">Individuelle</option>
                      </select>
                    </label>
                    <label>
                      Conditions de paiement
                      <select
                        value={clientDraft.payment_terms_mode || "inherit"}
                        onChange={(event) =>
                          setClientDraft((current) => ({
                            ...current,
                            payment_terms_mode:
                              event.target.value === "inherit"
                                ? null
                                : (event.target.value as PaymentTermsMode),
                          }))
                        }
                      >
                        <option value="inherit">Hériter de la règle société</option>
                        <option value="immediate">Paiement comptant</option>
                        <option value="days_after_issue">Délai après émission</option>
                        <option value="end_of_month">Fin de mois</option>
                      </select>
                    </label>
                    {clientDraft.payment_terms_mode === "days_after_issue" && (
                      <label>
                        Délai client (jours)
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={clientDraft.payment_terms_days ?? data.organization?.default_invoice_due_days ?? 30}
                          onChange={(event) =>
                            setClientDraft((current) => ({
                              ...current,
                              payment_terms_days: +event.target.value,
                            }))
                          }
                        />
                      </label>
                    )}
                    <label className="check wide">
                      <input
                        type="checkbox"
                        checked={!!clientDraft.commission_enabled}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            commission_enabled: e.target.checked,
                            commission_rate:
                              e.target.checked && current.commission_rate == null
                                ? 20
                                : current.commission_rate,
                          }))
                        }
                      />
                      Ajouter une commission mensuelle sur le chiffre d’affaires du client
                    </label>
                    {clientDraft.commission_enabled && (
                      <>
                        <label>
                          Taux contractuel (%)
                          <input
                            type="number"
                            min="0.01"
                            max="100"
                            step="0.01"
                            value={clientDraft.commission_rate ?? ""}
                            onChange={(e) =>
                              setClientDraft((current) => ({
                                ...current,
                                commission_rate: e.target.value ? +e.target.value : null,
                              }))
                            }
                            required
                          />
                        </label>
                        <label>
                          Base prévue au contrat
                          <select
                            value={clientDraft.commission_basis || "ex_vat"}
                            onChange={(e) =>
                              setClientDraft((current) => ({
                                ...current,
                                commission_basis: e.target.value as "ex_vat" | "inc_vat",
                              }))
                            }
                          >
                            <option value="ex_vat">Chiffre d’affaires HT</option>
                            <option value="inc_vat">Chiffre d’affaires TTC</option>
                          </select>
                        </label>
                        <label>
                          TVA sur la commission (%)
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={clientDraft.commission_vat_rate ?? 20}
                            onChange={(e) =>
                              setClientDraft((current) => ({
                                ...current,
                                commission_vat_rate: +e.target.value,
                              }))
                            }
                          />
                        </label>
                        <label>
                          Libellé de facturation
                          <input
                            value={clientDraft.commission_label || "Commission de gestion"}
                            onChange={(e) =>
                              setClientDraft((current) => ({
                                ...current,
                                commission_label: e.target.value,
                              }))
                            }
                          />
                        </label>
                      </>
                    )}
                    <label>
                      Marge cible (%)
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={clientDraft.target_margin_rate ?? ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            target_margin_rate: e.target.value
                              ? +e.target.value
                              : null,
                          }))
                        }
                        placeholder={String(
                          data.organization?.target_margin_rate || 30,
                        )}
                      />
                    </label>
                    <label>
                      Trajet moyen A/R (min)
                      <input
                        type="number"
                        min="0"
                        value={clientDraft.average_round_trip_minutes ?? ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            average_round_trip_minutes: e.target.value
                              ? +e.target.value
                              : null,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Temps administratif mensuel (min)
                      <input
                        type="number"
                        min="0"
                        value={clientDraft.monthly_admin_minutes ?? ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            monthly_admin_minutes: e.target.value
                              ? +e.target.value
                              : null,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Incidents constatés
                      <input
                        type="number"
                        min="0"
                        value={clientDraft.incident_count || 0}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            incident_count: +e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="check wide">
                      <input
                        type="checkbox"
                        checked={!!clientDraft.strategic_priority}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            strategic_priority: e.target.checked,
                          }))
                        }
                      />
                      Client prioritaire pour la stratégie commerciale
                    </label>
                    <label className="wide">
                      Notes internes
                      <textarea
                        value={clientDraft.notes || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            notes: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="wide">
                      Notes expliquant le scoring
                      <textarea
                        value={clientDraft.scoring_notes || ""}
                        onChange={(e) =>
                          setClientDraft((current) => ({
                            ...current,
                            scoring_notes: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <button disabled={busy}>
                      {busy ? "Enregistrement…" : "Enregistrer la fiche"}
                    </button>
                  </form>
                </details>
                </div>
              </details>
              <details className="file-chapter">
                <summary>
                  <span>
                    <strong>Dossier contractuel et pièces</strong>
                    <small>Dossier contractuel, signatures et pièces jointes</small>
                  </span>
                  <span className="file-chapter-summary-value">
                    {detail.clientContracts.length} contrat(s) · {detail.documents.length} pièce(s)
                  </span>
                </summary>
                <div className="file-chapter-body">
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Dossier contractuel</span>
                    <h3>Contrats et documents</h3>
                  </div>
                  {hasDeploymentCapability(data, "recurring_client_contracts") && (
                    <button className="secondary compact" onClick={openRecurringContractPreparation}>
                      Préparer un contrat récurrent
                    </button>
                  )}
                </div>
                {detail.clientContracts.map((contract) => (
                  <article className="mini-record" key={contract.id}>
                    {(() => {
                      const envelope = (data.signatureEnvelopes || []).find(
                        (item) =>
                          item.source_type === "client_contract" &&
                          item.source_id === contract.id,
                      );
                      return envelope ? (
                        <small>
                          Signature électronique : {labels[envelope.status] || envelope.status}
                          {envelope.provider ? ` · ${envelope.provider}` : " · prestataire à connecter"}
                        </small>
                      ) : null;
                    })()}
                    <strong>
                      {contract.title} · version{" "}
                      {contract.template_version || "—"}
                    </strong>
                    <small>
                      {labels[contract.status] || contract.status}
                      {contract.ends_on
                        ? ` · échéance ${frDate(contract.ends_on)}`
                        : " · sans échéance renseignée"}
                    </small>
                    {contract.contract_kind && contract.contract_kind !== "standard" && (
                      <div className="contract-commercial-terms">
                        <span className="portfolio-class class-A">
                          {contract.contract_kind === "recurring_municipality" ? "Commune" : "Professionnel"}
                        </span>
                        <strong>{contract.offer_name}</strong>
                        <small>{contract.service_scope}</small>
                        <small>
                          {contract.billing_frequency === "monthly" ? "Mensuel" : contract.billing_frequency === "quarterly" ? "Trimestriel" : "Annuel"}
                          {` · ${money(contract.billing_amount_ex_vat || 0)} HT · TVA ${contract.vat_rate ?? 0} % · paiement à ${contract.payment_terms_days ?? 0} jours`}
                        </small>
                        <small>
                          Facturation : Henrri · aucun mandat ni prélèvement initié par ARD Manager
                        </small>
                      </div>
                    )}
                    {contract.rendered_content && (
                      <details className="contract-preview">
                        <summary>Voir le contrat prérempli</summary>
                        <div>{contract.rendered_content}</div>
                      </details>
                    )}
                    {contract.status === "draft" && (
                      <div className="row-actions">
                        <button
                          className="secondary compact"
                          onClick={() => advanceContract(contract)}
                        >
                          Enregistrer comme envoyé hors application
                        </button>
                        <button
                          className="secondary compact"
                          disabled={busy || (data.signatureEnvelopes || []).some((item) => item.source_type === "client_contract" && item.source_id === contract.id && ["draft","queued","sent","viewed"].includes(item.status))}
                          onClick={() => prepareClientSignature(contract)}
                        >
                          Préparer la signature électronique
                        </button>
                      </div>
                    )}
                    {contract.status === "sent" && (
                      <button
                        className="secondary compact"
                        onClick={() => advanceContract(contract)}
                      >
                        Enregistrer la signature reçue
                      </button>
                    )}
                    {contract.status === "signed" && (
                      <button
                        className="secondary compact"
                        onClick={() => advanceContract(contract)}
                      >
                        Activer le contrat
                      </button>
                    )}
                  </article>
                ))}
                {!detail.clientContracts.length && (
                  <p className="empty">Aucun contrat préparé.</p>
                )}
                {detail.clientRequirements.map((requirement) => (
                  <article className="mini-record" key={requirement.id}>
                    <strong>{requirement.label}</strong>
                    <small>
                      {labels[requirement.status] || requirement.status}
                    </small>
                  </article>
                ))}
                <div className="attachment-list">
                  {detail.documents.map((document) => (
                    <button
                      key={document.id}
                      className="secondary compact"
                      onClick={() => openDocument(document)}
                    >
                      {document.original_name}
                    </button>
                  ))}
                </div>
                {!detail.documents.length && (
                  <p className="empty">Aucun document client déposé.</p>
                )}
                <details className="client-edit-details">
                  <summary>
                    <strong>Ajouter une pièce jointe</strong>
                  </summary>
                  <form className="form-grid" onSubmit={uploadClientDocument}>
                    <label>
                      Type de document
                      <select
                        value={documentKind}
                        onChange={(e) => setDocumentKind(e.target.value)}
                      >
                        <option value="signed_contract">Contrat</option>
                        <option value="quote">Devis</option>
                        <option value="invoice">Facture</option>
                        <option value="report">Rapport</option>
                        <option value="other">Autre pièce</option>
                      </select>
                    </label>
                    <label>
                      Relier à un contrat
                      <select
                        value={documentContractId}
                        onChange={(e) => setDocumentContractId(e.target.value)}
                      >
                        <option value="">Aucun</option>
                        {detail.clientContracts.map((contract) => (
                          <option key={contract.id} value={contract.id}>
                            {contract.title} · v
                            {contract.template_version || "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="wide">
                      PDF ou image
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={(e) =>
                          setDocumentFile(e.target.files?.[0] || null)
                        }
                        required
                      />
                    </label>
                    <button disabled={busy || !documentFile}>
                      {busy ? "Dépôt…" : "Ajouter à la fiche client"}
                    </button>
                  </form>
                </details>
                </div>
              </details>
            </div>
            <details
              id="client-properties-services-chapter"
              className="file-chapter"
              data-testid="client-properties-services-chapter"
            >
              <summary>
                <span>
                  <strong>Adresses, biens et prestations</strong>
                  <small>Sites d’intervention, services actifs et conditions tarifaires</small>
                </span>
                <span className="file-chapter-summary-value">
                  {detail.properties.length} bien(s) · {detail.services.length + detail.propertyServices.length} prestation(s)
                </span>
              </summary>
              <div className="file-chapter-body client-file-grid">
                <section>
                  <div className="section-heading">
                    <h3>Biens</h3>
                    <button type="button" className="secondary compact" onClick={() => editProperty()}>
                      Ajouter un bien
                    </button>
                  </div>
                  {detail.properties.map((property) => (
                    <article className="mini-record" key={property.id}>
                      <div>
                        <strong>{property.name}</strong>
                        <small>{property.address || "Adresse à renseigner avant la première mission"}</small>
                        {property.property_type_id && <small>Type : {propertyTypes.find((type) => type.id === property.property_type_id)?.name || "Type tarifaire rattaché"}</small>}
                        {property.access_notes && <small>{property.access_notes}</small>}
                      </div>
                      <div className="row-actions">
                        <button type="button" className="secondary compact" onClick={() => editProperty(property)}>Modifier</button>
                        {property.active && (
                          <button type="button" className="danger compact" onClick={() => archiveProperty(property)}>Archiver</button>
                        )}
                      </div>
                    </article>
                  ))}
                  {!detail.properties.length && (
                    <p className="empty">Aucun bien distinct.</p>
                  )}
                </section>
                <section>
                  <div className="section-heading">
                    <h3>Prestations et tarifs</h3>
                    <button type="button" className="secondary compact" onClick={() => editCommercialService()}>
                      Ajouter une prestation
                    </button>
                  </div>
                  {detail.services.map((service) => (
                    <article className="mini-record" key={service.id}>
                      <div>
                        <strong>{service.name}</strong>
                        <small>Client entier · {service.billing_mode} · {money(service.unit_price_ex_vat)} HT · TVA {service.vat_rate ?? 20} %</small>
                        {(() => { const rule=(data.serviceBillingRules||[]).find((item)=>item.client_service_id===service.id); return rule ? <small>{labels[rule.billing_treatment]}{rule.billing_treatment !== "standard" ? ` · coût fournisseur ${money(rule.supplier_unit_cost_ex_vat || 0)} HT` : ""}</small> : null; })()}
                      </div>
                      <div className="row-actions">
                        <button type="button" className="secondary compact" onClick={() => editCommercialService(service, "client")}>Modifier</button>
                        <button type="button" className="danger compact" onClick={() => archiveCommercialService(service, "client")}>Archiver</button>
                      </div>
                    </article>
                  ))}
                  {detail.propertyServices.map((service) => (
                    <article className="mini-record" key={service.id}>
                      <div>
                        <strong>{service.name}</strong>
                        <small>{detail.properties.find((property) => property.id === service.property_id)?.name || "Bien"} · {service.billing_mode} · {money(service.unit_price_ex_vat)} HT · TVA {service.vat_rate ?? 20} %</small>
                        {(() => { const rule=(data.serviceBillingRules||[]).find((item)=>item.property_service_id===service.id); return rule ? <small>{labels[rule.billing_treatment]}{rule.billing_treatment !== "standard" ? ` · coût fournisseur ${money(rule.supplier_unit_cost_ex_vat || 0)} HT` : ""}</small> : null; })()}
                      </div>
                      <div className="row-actions">
                        <button type="button" className="secondary compact" onClick={() => editCommercialService(service, "property")}>Modifier</button>
                        <button type="button" className="danger compact" onClick={() => archiveCommercialService(service, "property")}>Archiver</button>
                      </div>
                    </article>
                  ))}
                  {!detail.services.length &&
                    !detail.propertyServices.length && (
                      <p className="empty">Aucune prestation active.</p>
                    )}
                </section>
              </div>
            </details>
            <details className="file-chapter">
              <summary>
                <span>
                  <strong>Missions</strong>
                  <small>Historique, statuts et accès au dossier mission</small>
                </span>
                <span className="file-chapter-summary-value">{detail.missions.length}</span>
              </summary>
              <div className="file-chapter-body compact-record-list">
                {[...detail.missions]
                  .sort((a, b) => b.starts_at.localeCompare(a.starts_at))
                  .map((mission) => (
                    <article key={mission.id}>
                      <div>
                        <strong>{frDate(mission.starts_at, true)}</strong>
                        <small>
                          {mission.location} ·{" "}
                          {money(mission.amount_ex_vat || 0)} HT
                        </small>
                      </div>
                      <div className="row-actions">
                        <Status value={mission.status} />
                        <button
                          className="secondary compact"
                          onClick={() => {
                            setSelected(null);
                            onSelect(mission);
                          }}
                        >
                          Ouvrir
                        </button>
                      </div>
                    </article>
                  ))}
              </div>
            </details>
            <div className="client-file-grid">
              <details className="file-chapter">
                <summary>
                  <span><strong>Devis</strong><small>Propositions commerciales</small></span>
                  <span className="file-chapter-summary-value">{detail.quotes.length}</span>
                </summary>
                <div className="file-chapter-body">
                {detail.quotes.map((quote) => (
                  <article className="mini-record" key={quote.id}>
                    <strong>{quote.number}</strong>
                    <small>
                      {frDate(quote.issued_on)} · {money(quote.total_inc_vat)}{" "}
                      TTC · {labels[quote.status] || quote.status}
                    </small>
                  </article>
                ))}
                {!detail.quotes.length && <p className="empty">Aucun devis.</p>}
                </div>
              </details>
              <details className="file-chapter">
                <summary>
                  <span><strong>Factures</strong><small>Émission, encaissement et échéances</small></span>
                  <span className="file-chapter-summary-value">{detail.invoices.length}</span>
                </summary>
                <div className="file-chapter-body">
                {detail.invoices.map((invoice) => (
                  <article className="mini-record" key={invoice.id}>
                    <strong>{invoice.number || "Brouillon"}</strong>
                    <small>
                      {invoice.issue_on
                        ? frDate(invoice.issue_on)
                        : "Non émise"}{" "}
                      · {money(invoice.total_inc_vat)} TTC ·{" "}
                      {labels[invoice.status] || invoice.status}
                    </small>
                    <small>
                      Encaissé {money(invoice.amount_paid || 0)} · reste{" "}
                      {money(
                        Math.max(
                          0,
                          Number(invoice.total_inc_vat || 0) -
                            Number(invoice.amount_paid || 0),
                        ),
                      )}
                      {invoice.due_on
                        ? ` · échéance ${frDate(invoice.due_on)}`
                        : ""}
                    </small>
                  </article>
                ))}
                {!detail.invoices.length && (
                  <p className="empty">Aucune facture.</p>
                )}
                </div>
              </details>
              <details className="file-chapter">
                <summary>
                  <span><strong>Organisation automatique</strong><small>Récurrences et intervenants référents</small></span>
                  <span className="file-chapter-summary-value">{detail.recurrences.length} récurrence(s)</span>
                </summary>
                <div className="file-chapter-body">
                <p>
                  {detail.recurrences.length} récurrence(s) ·{" "}
                  {detail.preferred.length} intervenant(s) référent(s)
                </p>
                {detail.preferred.map((item) => {
                  const worker = data.workers.find(
                    (candidate) => candidate.id === item.worker_id,
                  );
                  return (
                    <article className="mini-record" key={item.id}>
                      <strong>
                        Priorité {item.priority} ·{" "}
                        {worker ? workerName(worker) : "Intervenant"}
                      </strong>
                      <small>
                        {item.property_id
                          ? detail.properties.find(
                              (property) => property.id === item.property_id,
                            )?.name || "Bien"
                          : "Client entier"}
                      </small>
                    </article>
                  );
                })}
                </div>
              </details>
            </div>
            {selected.notes && (
              <details className="file-chapter client-note-block">
                <summary><span><strong>Notes internes</strong><small>Informations réservées à l’équipe</small></span></summary>
                <div className="file-chapter-body"><p>{selected.notes}</p></div>
              </details>
            )}
          </section>
        </div>
      )}
      {selected && recurringContractEditor && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setRecurringContractEditor(null);
        }}>
          <form className="portal-detail recurring-contract-dialog" role="dialog" aria-modal="true" aria-label="Préparer un contrat récurrent" onSubmit={prepareRecurringContract}>
            <div className="portal-detail-head">
              <div><span className="eyebrow">Offre récurrente · {selected.name}</span><h2>Préparer le contrat</h2></div>
              <button type="button" className="icon-close" onClick={() => setRecurringContractEditor(null)}>×</button>
            </div>
            <Notice>
              Le contrat est préparé depuis un devis accepté pour relecture. La facturation reste gérée dans Henrri ; ARD Manager ne crée aucun mandat SEPA et n’initie aucun prélèvement.
            </Notice>
            <div className="form-grid">
              <label>Type de relation<select value={recurringContractEditor.contractKind} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,contractKind:event.target.value as "recurring_professional"|"recurring_municipality"} : current)}><option value="recurring_professional">Professionnel</option><option value="recurring_municipality">Commune / collectivité</option></select></label>
              <label>Modèle de contrat<select required value={recurringContractEditor.templateId} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,templateId:event.target.value} : current)}><option value="">Choisir</option>{(data.contractTemplates || []).filter((item) => item.audience === "client" && item.active).map((item) => <option key={item.id} value={item.id}>{item.name} · v{item.version}</option>)}</select></label>
              <label className="wide">Devis accepté<select required value={recurringContractEditor.quoteId} onChange={(event) => {const quote=data.quotes.find((item)=>item.id===event.target.value);setRecurringContractEditor((current) => current ? {...current,quoteId:event.target.value,billingAmount:quote?String(quote.total_ex_vat):current.billingAmount} : current);}}><option value="">Choisir</option>{data.quotes.filter((item) => item.client_id === selected.id && item.status === "accepted").map((item) => <option key={item.id} value={item.id}>{item.number} · {money(item.total_ex_vat)} HT</option>)}</select></label>
              <label className="wide">Nom de l’offre<input required minLength={3} value={recurringContractEditor.offerName} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,offerName:event.target.value} : current)} placeholder="Ex. Entretien mensuel des locaux" /></label>
              <label className="wide">Prestations et périmètre<textarea required minLength={10} value={recurringContractEditor.serviceScope} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,serviceScope:event.target.value} : current)} placeholder="Décrivez les prestations, sites et limites de l’offre." /></label>
              <label>Facturation<select value={recurringContractEditor.billingFrequency} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,billingFrequency:event.target.value as "monthly"|"quarterly"|"annual"} : current)}><option value="monthly">Mensuelle</option><option value="quarterly">Trimestrielle</option><option value="annual">Annuelle</option></select></label>
              <label>Montant par échéance HT<input required type="number" min="0" step="0.01" value={recurringContractEditor.billingAmount} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,billingAmount:event.target.value} : current)} /></label>
              <label>TVA (%)<input required type="number" min="0" max="100" step="0.01" value={recurringContractEditor.vatRate} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,vatRate:event.target.value} : current)} /></label>
              <label>Délai de paiement (jours)<input required type="number" min="0" max="365" value={recurringContractEditor.paymentTermsDays} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,paymentTermsDays:event.target.value} : current)} /></label>
              <label>Début<input required type="date" value={recurringContractEditor.startsOn} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,startsOn:event.target.value} : current)} /></label>
              <label>Fin facultative<input type="date" min={recurringContractEditor.startsOn} value={recurringContractEditor.endsOn} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,endsOn:event.target.value} : current)} /></label>
              <label className="wide">Notes internes<textarea value={recurringContractEditor.notes} onChange={(event) => setRecurringContractEditor((current) => current ? {...current,notes:event.target.value} : current)} /></label>
            </div>
            {!data.quotes.some((item) => item.client_id === selected.id && item.status === "accepted") && <Notice tone="warning">Aucun devis accepté n’est disponible pour ce client. Faites accepter le devis avant de préparer le contrat.</Notice>}
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setRecurringContractEditor(null)}>Annuler</button>
              <button disabled={busy || !recurringContractEditor.templateId || !recurringContractEditor.quoteId}>{busy ? "Préparation…" : "Créer le brouillon à relire"}</button>
            </div>
          </form>
        </div>
      )}
      {selected && propertyEditor && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPropertyEditor(null);
        }}>
          <form className="portal-detail" role="dialog" aria-modal="true" aria-label={propertyEditor.id ? "Modifier le bien" : "Ajouter un bien"} onSubmit={saveProperty}>
            <div className="portal-detail-head">
              <div><span className="eyebrow">Fiche client · {selected.name}</span><h2>{propertyEditor.id ? "Modifier le bien" : "Ajouter un bien"}</h2></div>
              <button type="button" className="icon-close" onClick={() => setPropertyEditor(null)}>×</button>
            </div>
            <div className="form-grid">
              <label>Nom du bien<input required value={propertyEditor.name} onChange={(event) => setPropertyEditor((current) => current ? {...current,name:event.target.value} : current)} /></label>
              <label>Contact sur place<input value={propertyEditor.contactName} onChange={(event) => setPropertyEditor((current) => current ? {...current,contactName:event.target.value} : current)} /></label>
              <label className="wide">Type de bien et tarif de base<select required={propertyTypes.length > 0} value={propertyEditor.propertyTypeId} onChange={(event) => setPropertyEditor((current) => current ? {...current,propertyTypeId:event.target.value} : current)}><option value="">{propertyTypes.length > 0 ? "Choisir un type" : "Aucun type tarifaire disponible"}</option>{propertyTypes.map((type) => <option key={type.id} value={type.id}>{type.name} · {money(type.unit_price_ex_vat)} HT · {type.default_duration_hours ?? "—"} h</option>)}</select><small>{propertyTypes.length > 0 ? "Le tarif et le temps de base sont repris automatiquement." : "Vous pouvez conserver ce bien sans type et créer ses conditions tarifaires plus tard."}</small></label>
              <label className="wide">Adresse complète<input required value={propertyEditor.address} onChange={(event) => setPropertyEditor((current) => current ? {...current,address:event.target.value} : current)} /></label>
              <label className="wide">Consignes d’accès<textarea value={propertyEditor.accessNotes} onChange={(event) => setPropertyEditor((current) => current ? {...current,accessNotes:event.target.value} : current)} /></label>
            </div>
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setPropertyEditor(null)}>Annuler</button>
              <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer le bien"}</button>
            </div>
          </form>
        </div>
      )}
      {selected && propertyArchive && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPropertyArchive(null);
        }}>
          <form
            className="portal-detail property-change-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Archiver le bien"
            onSubmit={confirmPropertyArchive}
          >
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Fiche client · {selected.name}</span>
                <h2>Archiver « {propertyArchive.property.name} »</h2>
              </div>
              <button type="button" className="icon-close" onClick={() => setPropertyArchive(null)}>×</button>
            </div>
            <Notice tone="warning">
              Le bien et ses prestations actives seront archivés ensemble. Les missions, devis, contrats, factures et anciennes conditions resteront consultables.
            </Notice>
            <label className="modal-field">
              Motif de l’archivage
              <textarea
                required
                minLength={5}
                value={propertyArchive.reason}
                onChange={(event) => setPropertyArchive((current) => current ? {...current,reason:event.target.value} : current)}
                placeholder="Ex. fin de gestion du bien confirmée par le client"
              />
            </label>
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setPropertyArchive(null)}>Conserver le bien</button>
              <button className="danger" disabled={busy || propertyArchive.reason.trim().length < 5}>
                {busy ? "Archivage…" : "Archiver sans effacer l’historique"}
              </button>
            </div>
          </form>
        </div>
      )}
      {selected && serviceEditor && detail && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setServiceEditor(null);
        }}>
          <form className="portal-detail" role="dialog" aria-modal="true" aria-label={serviceEditor.id ? "Modifier la prestation" : "Ajouter une prestation"} onSubmit={saveCommercialService}>
            <div className="portal-detail-head">
              <div><span className="eyebrow">Tarification · {selected.name}</span><h2>{serviceEditor.id ? "Modifier la prestation" : "Ajouter une prestation"}</h2></div>
              <button type="button" className="icon-close" onClick={() => setServiceEditor(null)}>×</button>
            </div>
            <div className="form-grid">
              <label>Portée<select value={serviceEditor.scope} disabled={!!serviceEditor.id} onChange={(event) => setServiceEditor((current) => current ? {...current,scope:event.target.value as "client"|"property",propertyId:event.target.value==="client"?"":current.propertyId} : current)}><option value="client">Client entier</option><option value="property">Un bien précis</option></select></label>
              {serviceEditor.scope === "property" && <label>Bien<select required value={serviceEditor.propertyId} disabled={!!serviceEditor.id} onChange={(event) => setServiceEditor((current) => current ? {...current,propertyId:event.target.value} : current)}><option value="">Choisir</option>{detail.properties.filter((property) => property.active).map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}</select></label>}
              <label>Nom de la prestation<input required value={serviceEditor.name} onChange={(event) => setServiceEditor((current) => current ? {...current,name:event.target.value} : current)} /></label>
              <label>Facturation<select value={serviceEditor.billingMode} onChange={(event) => setServiceEditor((current) => current ? {...current,billingMode:event.target.value} : current)}><option value="fixed">Forfait</option><option value="hourly">Horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Mensuel</option></select></label>
              <label>Prix HT<input required disabled={serviceEditor.treatment === "disbursement"} type="number" min="0" step="0.01" value={serviceEditor.price} onChange={(event) => setServiceEditor((current) => current ? {...current,price:event.target.value} : current)} />{serviceEditor.treatment === "disbursement" && <small>Identique au coût fournisseur, sans marge.</small>}</label>
              <label>TVA (%)<input required type="number" min="0" max="100" step="0.01" value={serviceEditor.vat} onChange={(event) => setServiceEditor((current) => current ? {...current,vat:event.target.value} : current)} /></label>
              <label>Durée interne indicative (h)<input type="number" min="0.25" step="0.25" value={serviceEditor.duration} onChange={(event) => setServiceEditor((current) => current ? {...current,duration:event.target.value} : current)} /></label>
              <label>
                Traitement commercial
                <select value={serviceEditor.treatment} onChange={(event) => setServiceEditor((current) => current ? {...current,treatment:event.target.value as BillingTreatment,fiscalWarningAcknowledged:event.target.value === "disbursement" ? current.fiscalWarningAcknowledged : false} : current)}>
                  <option value="standard">Prestation standard</option>
                  <option value="recharge">Frais à refacturer avec marge</option>
                  <option value="disbursement">Débours remboursé à l’euro près</option>
                </select>
              </label>
              {serviceEditor.treatment !== "standard" && <label>Coût fournisseur unitaire HT<input required type="number" min="0" step="0.01" value={serviceEditor.supplierCost} onChange={(event) => setServiceEditor((current) => current ? {...current,supplierCost:event.target.value,price:current.treatment === "disbursement" ? event.target.value : current.price} : current)} /></label>}
              {serviceEditor.treatment === "recharge" && <>
                <label>Objectif<select value={serviceEditor.targetMode} onChange={(event) => setServiceEditor((current) => current ? {...current,targetMode:event.target.value as "margin"|"markup"} : current)}><option value="margin">Marge sur prix de vente</option><option value="markup">Majoration du coût</option></select></label>
                <label>Taux cible (%)<input required type="number" min="0" max={serviceEditor.targetMode === "margin" ? 99.99 : 999.99} step="0.1" value={serviceEditor.targetRate} onChange={(event) => setServiceEditor((current) => current ? {...current,targetRate:event.target.value} : current)} /></label>
              </>}
              {serviceEditor.treatment === "disbursement" && <>
                <label>Donneur d’ordre / personne remboursant<input required value={serviceEditor.disbursementPrincipal} onChange={(event) => setServiceEditor((current) => current ? {...current,disbursementPrincipal:event.target.value} : current)} /></label>
                <label>Référence du mandat écrit<input required value={serviceEditor.disbursementMandate} onChange={(event) => setServiceEditor((current) => current ? {...current,disbursementMandate:event.target.value} : current)} /></label>
                <Notice tone="warning">Le mode « débours » n’est pas une simple refacturation : la dépense doit être engagée au nom et pour le compte du client, justifiée et remboursée exactement. ARD Manager ne décide pas de l’éligibilité fiscale ; faites valider ce paramétrage par l’expert-comptable.</Notice>
                <label className="wide checkbox-line"><input required type="checkbox" checked={serviceEditor.fiscalWarningAcknowledged} onChange={(event) => setServiceEditor((current) => current ? {...current,fiscalWarningAcknowledged:event.target.checked} : current)} /> J’ai vérifié le mandat et je demande l’activation de ce mode.</label>
              </>}
              {serviceEditor.scope === "property" && <label className="wide">Notes internes<textarea value={serviceEditor.notes} onChange={(event) => setServiceEditor((current) => current ? {...current,notes:event.target.value} : current)} /></label>}
            </div>
            <Notice>Les nouvelles conditions s’appliquent aux prochaines opérations. Les missions, devis et factures déjà créés gardent leurs montants historiques.</Notice>
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setServiceEditor(null)}>Annuler</button>
              <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer la prestation"}</button>
            </div>
          </form>
        </div>
      )}
      {selected && financialClientOpen && (
        <AdminEntityFinancialSummary
          data={data}
          subject={{ type: "client", client: selected }}
          onClose={() => setFinancialClientOpen(false)}
        />
      )}
    </>
  );
}

function AdminCompanyProfile({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const org = data.organization,
    [form, setForm] = useState<Partial<Organization>>(org || {}),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => setForm(org || {}), [org]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!org) return;
    setBusy(true);
    const { error } = await supabase!
      .from("organizations")
      .update({
        name: form.name,
        legal_name: form.legal_name || null,
        siret: form.siret || null,
        vat_number: form.vat_number || null,
        address: form.address || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        country: form.country || "France",
        iban: form.iban || null,
        bic: form.bic || null,
        bank_name: form.bank_name || null,
        representative_first_name: form.representative_first_name || null,
        representative_last_name: form.representative_last_name || null,
        representative_title: form.representative_title || null,
        email: form.email || null,
        phone: form.phone || null,
        website: form.website || null,
      })
      .eq("id", profile.organization_id);
    setBusy(false);
    setMessage(
      error ? error.message : "Informations de la société enregistrées.",
    );
    if (!error) reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Identité de l’entreprise</span>
          <h1>Société & dirigeant</h1>
        </div>
      </div>
      <section className="panel company-profile-panel">
        <p className="section-intro">
          Ouvrez uniquement le chapitre utile. Les informations enregistrées
          alimentent les documents, les tournées et les communications de la
          société.
        </p>
        <form
          className="form-grid company-profile-form"
          data-testid="company-profile-form"
          onSubmit={save}
        >
          <details className="settings-chapter company-profile-chapter" open>
            <summary>
              <span>
                <strong>Identité et siège</strong>
                <small>Nom, SIRET, TVA et adresse de départ des tournées</small>
              </span>
            </summary>
            <div className="settings-chapter-body company-profile-fields">
          <label>
            Nom commercial
            <input
              value={form.name || ""}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
              required
            />
          </label>
          <label>
            Raison sociale
            <input
              value={form.legal_name || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, legal_name: e.target.value }))
              }
            />
          </label>
          <label>
            SIRET
            <input
              value={form.siret || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, siret: e.target.value }))
              }
            />
          </label>
          <label>
            N° TVA
            <input
              value={form.vat_number || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, vat_number: e.target.value }))
              }
            />
          </label>
          <label className="wide">
            Adresse du siège et départ des tournées
            <input
              value={form.address || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, address: e.target.value }))
              }
            />
          </label>
          <label>
            Code postal
            <input
              value={form.postal_code || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, postal_code: e.target.value }))
              }
            />
          </label>
          <label>
            Ville
            <input
              value={form.city || ""}
              onChange={(e) => setForm((v) => ({ ...v, city: e.target.value }))}
            />
          </label>
            </div>
          </details>
          <details className="settings-chapter company-profile-chapter">
            <summary>
              <span>
                <strong>Représentant et contacts</strong>
                <small>Identité du signataire et coordonnées publiques</small>
              </span>
            </summary>
            <div className="settings-chapter-body company-profile-fields">
          <label>
            Prénom du représentant
            <input
              value={form.representative_first_name || ""}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  representative_first_name: e.target.value,
                }))
              }
            />
          </label>
          <label>
            Nom du représentant
            <input
              value={form.representative_last_name || ""}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  representative_last_name: e.target.value,
                }))
              }
            />
          </label>
          <label>
            Fonction
            <input
              value={form.representative_title || ""}
              placeholder="Président"
              onChange={(e) =>
                setForm((v) => ({ ...v, representative_title: e.target.value }))
              }
            />
          </label>
          <label>
            Email société
            <input
              type="email"
              value={form.email || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, email: e.target.value }))
              }
            />
          </label>
          <label>
            Téléphone
            <input
              value={form.phone || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, phone: e.target.value }))
              }
            />
          </label>
          <label>
            Site internet
            <input
              value={form.website || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, website: e.target.value }))
              }
            />
          </label>
            </div>
          </details>
          <details className="settings-chapter company-profile-chapter">
            <summary>
              <span>
                <strong>Coordonnées bancaires</strong>
                <small>Banque, IBAN et BIC utilisés sur les documents concernés</small>
              </span>
            </summary>
            <div className="settings-chapter-body company-profile-fields">
          <label>
            Banque
            <input
              value={form.bank_name || ""}
              onChange={(e) =>
                setForm((v) => ({ ...v, bank_name: e.target.value }))
              }
            />
          </label>
          <label>
            IBAN
            <input
              value={form.iban || ""}
              onChange={(e) => setForm((v) => ({ ...v, iban: e.target.value }))}
            />
          </label>
          <label>
            BIC
            <input
              value={form.bic || ""}
              onChange={(e) => setForm((v) => ({ ...v, bic: e.target.value }))}
            />
          </label>
            </div>
          </details>
          <button disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer la société"}
          </button>
        </form>
        {message && (
          <Notice
            tone={message.startsWith("Informations") ? "success" : "error"}
          >
            {message}
          </Notice>
        )}
      </section>
    </>
  );
}

function ProcessDefinitionManager({
  profile,
  data,
  reload,
  onManageGuidedQuote,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  onManageGuidedQuote: (catalogItemId: string | "new") => void;
}) {
  const definitions = [...(data.processDefinitions || [])].sort(
      (left, right) =>
        left.process_key.localeCompare(right.process_key) ||
        right.version - left.version,
    ),
    [editing, setEditing] = useState<ProcessDefinitionRow | null>(null),
    [simulating, setSimulating] = useState<ProcessDefinitionRow | null>(null),
    [simulationForm, setSimulationForm] = useState({
      eventType: "mission.report_submitted",
      actorRole: "contractor",
      subjectType: "mission",
      subjectId: "mission-exemple",
      clientName: "Client exemple",
      workerName: "Intervenant exemple",
      reportId: "compte-rendu-exemple",
      actualHours: "2",
    }),
    [simulationResult, setSimulationResult] = useState<{
      event: BusinessEventEnvelope;
      active: ProcessSimulationResult | null;
      draft: ProcessSimulationResult;
    } | null>(null),
    [publicationRequestNote, setPublicationRequestNote] = useState(""),
    [reviewingPublication, setReviewingPublication] =
      useState<ProcessDefinitionPublicationReview | null>(null),
    [publicationReviewNote, setPublicationReviewNote] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [retireReason, setRetireReason] = useState("");

  if (!hasDeploymentCapability(data, "process_definition_editor"))
    return (
      <section className="panel process-definition-manager">
        <h2>Processus et automatisations</h2>
        <Notice tone="info">
          L’éditeur contrôlé est disponible dans la recette isolée, mais pas
          encore sur cet environnement.
        </Notice>
      </section>
    );

  async function cloneDefinition(definition: ProcessDefinitionRow) {
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc("create_process_definition_draft", {
      p_source_definition_id: definition.id,
      p_name: null,
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage(
      isShowcaseMode
        ? "Version de travail créée dans le Showcase, sans effet opérationnel."
        : "Version de travail créée sans effet opérationnel.",
    );
    reload();
  }

  function openEditor(definition: ProcessDefinitionRow) {
    setRetireReason("");
    setMessage("");
    setEditing(structuredClone(definition));
  }

  function engineDefinition(definition: ProcessDefinitionRow): ProcessDefinition {
    return {
      id: definition.id,
      key: definition.process_key,
      name: definition.name,
      version: definition.version,
      status: definition.status,
      trigger: {
        eventTypes: [...definition.trigger_event_types],
        conditions: structuredClone(definition.trigger_conditions),
      },
      steps: structuredClone(definition.steps) as ProcessDefinition["steps"],
      publishedAt: definition.published_at,
      effectiveFrom: definition.effective_from,
      effectiveUntil: definition.effective_until,
    };
  }

  function openSimulation(definition: ProcessDefinitionRow) {
    const subjectCondition = definition.trigger_conditions.find(
      (condition) => condition.field === "subject.type" && condition.operator === "equals",
    );
    setSimulationResult(null);
    setPublicationRequestNote("");
    setSimulationForm((current) => ({
      ...current,
      eventType: definition.trigger_event_types[0] || current.eventType,
      subjectType:
        typeof subjectCondition?.value === "string"
          ? subjectCondition.value
          : current.subjectType,
    }));
    setSimulating(definition);
  }

  async function requestPublication() {
    if (!simulating || !simulationResult) return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc(
      "request_process_definition_publication",
      {
        p_definition_id: simulating.id,
        p_request_note: publicationRequestNote,
        p_simulation_evidence: simulationResult,
      },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage(
      "Demande de publication enregistrée. Le brouillon reste sans effet jusqu’à sa revue explicite.",
    );
    setSimulating(null);
    setSimulationResult(null);
    setPublicationRequestNote("");
    reload();
  }

  async function reviewPublication(decision: "approved" | "rejected") {
    if (!reviewingPublication) return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc(
      "review_process_definition_publication",
      {
        p_review_id: reviewingPublication.id,
        p_decision: decision,
        p_review_note: publicationReviewNote,
      },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage(
      decision === "approved"
        ? "Version publiée et activée. La version société précédente est conservée comme retirée."
        : "Publication refusée. Le brouillon reste disponible pour correction.",
    );
    setReviewingPublication(null);
    setPublicationReviewNote("");
    reload();
  }

  function runSimulation(event: React.FormEvent) {
    event.preventDefault();
    if (!simulating) return;
    const occurredAt = new Date().toISOString(),
      businessEvent: BusinessEventEnvelope = {
        id: `simulation-${crypto.randomUUID()}`,
        organizationId: profile.organization_id,
        type: simulationForm.eventType.trim(),
        schemaVersion: 1,
        occurredAt,
        actor: {
          profileId: profile.id,
          role: simulationForm.actorRole as BusinessEventEnvelope["actor"]["role"],
        },
        subject: {
          type: simulationForm.subjectType.trim(),
          id: simulationForm.subjectId.trim(),
        },
        context: {
          client_name: simulationForm.clientName.trim(),
          worker_name: simulationForm.workerName.trim(),
          report_id: simulationForm.reportId.trim() || null,
          actual_hours: Number(simulationForm.actualHours),
        },
      },
      activeDefinition = definitions
        .filter(
          (definition) =>
            definition.process_key === simulating.process_key &&
            definition.status === "active",
        )
        .sort((left, right) => right.version - left.version)[0],
      draftResult = simulateProcessDefinition(
        businessEvent,
        engineDefinition(simulating),
      );
    setSimulationResult({
      event: businessEvent,
      active: activeDefinition
        ? simulateProcessDefinition(
            businessEvent,
            engineDefinition(activeDefinition),
          )
        : null,
      draft: draftResult,
    });
  }

  function simulationSummary(
    label: string,
    result: ProcessSimulationResult | null,
    event: BusinessEventEnvelope,
  ) {
    if (!result)
      return (
        <article className="process-simulation-column">
          <h3>{label}</h3>
          <Notice>Aucune version active comparable.</Notice>
        </article>
      );
    const firstAction = result.plan?.actions[0],
      delayHours = firstAction
        ? Math.round(
            ((new Date(firstAction.dueAt).getTime() -
              new Date(event.occurredAt).getTime()) /
              3600000) *
              10,
          ) / 10
        : null;
    return (
      <article className="process-simulation-column">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Version {result.processVersion}</span>
            <h3>{label}</h3>
          </div>
          <span className={`status ${result.matches ? "active" : "warning"}`}>
            {result.matches ? "Se déclencherait" : "Ne se déclencherait pas"}
          </span>
        </div>
        {result.validationErrors.length > 0 && (
          <Notice tone="error">{result.validationErrors.join(" ")}</Notice>
        )}
        <dl className="process-simulation-metrics">
          <div><dt>Actions requises</dt><dd>{result.plan?.actions.length || 0}</dd></div>
          <div><dt>Informations / alertes</dt><dd>{result.plan?.notifications.length || 0}</dd></div>
          <div><dt>Délai de première action</dt><dd>{delayHours == null ? "—" : `${delayHours} h`}</dd></div>
        </dl>
        <div className="process-simulation-rules">
          <strong>Lecture des règles</strong>
          <p className={result.eventTypeMatches ? "rule-match" : "rule-miss"}>
            Événement : {result.eventTypeMatches ? "correspond" : "ne correspond pas"}
          </p>
          {result.conditionResults.map((condition) => (
            <p className={condition.matches ? "rule-match" : "rule-miss"} key={`${condition.field}-${condition.operator}`}>
              {condition.matches ? "✓" : "×"} {condition.field} : {condition.actual == null ? "non renseigné" : String(condition.actual)}
            </p>
          ))}
        </div>
        {result.plan && (
          <div className="compact-record-list">
            {result.plan.notifications.map((notification) => (
              <article key={notification.idempotencyKey}>
                <div><strong>{notification.title}</strong><small>{notification.channelIntents.join(", ")}</small></div>
              </article>
            ))}
            {result.plan.actions.map((action) => (
              <article key={action.idempotencyKey}>
                <div><strong>{action.title}</strong><small>Échéance simulée : {frDate(action.dueAt, true)}</small></div>
              </article>
            ))}
          </div>
        )}
      </article>
    );
  }

  function updateCondition(
    index: number,
    patch: Partial<ProcessDefinitionRow["trigger_conditions"][number]>,
  ) {
    setEditing((current) => {
      if (!current) return current;
      const triggerConditions = [...current.trigger_conditions];
      triggerConditions[index] = { ...triggerConditions[index], ...patch };
      return { ...current, trigger_conditions: triggerConditions };
    });
  }

  function updateStep(index: number, patch: Record<string, unknown>) {
    setEditing((current) => {
      if (!current) return current;
      const steps = [...current.steps];
      steps[index] = { ...steps[index], ...patch };
      return { ...current, steps };
    });
  }

  function toggleStepChannel(index: number, channel: string) {
    const step = editing?.steps[index],
      current = Array.isArray(step?.channelIntents)
        ? (step!.channelIntents as string[])
        : [],
      next = current.includes(channel)
        ? current.filter((item) => item !== channel)
        : [...current, channel];
    if (next.length) updateStep(index, { channelIntents: next });
  }

  function addCondition() {
    setEditing((current) =>
      current
        ? {
            ...current,
            trigger_conditions: [
              ...current.trigger_conditions,
              { field: "context.status", operator: "equals", value: "" },
            ],
          }
        : current,
    );
  }

  function addStep(kind: "notification" | "required_action") {
    setEditing((current) => {
      if (!current) return current;
      const suffix = current.steps.length + 1,
        step =
          kind === "notification"
            ? {
                id: `notification-${suffix}`,
                kind,
                level: "information",
                titleTemplate: "Nouvelle information",
                bodyTemplate: "Un événement métier nécessite votre attention.",
                recipient: { kind: "role", role: "admin" },
                channelIntents: ["internal"],
              }
            : {
                id: `action-${suffix}`,
                kind,
                titleTemplate: "Action requise",
                descriptionTemplate: "Contrôler cet événement métier.",
                assignee: { kind: "role", role: "admin" },
                dueAfterMinutes: 1440,
                remindersBeforeDueMinutes: [240],
                escalateAfterMinutes: 240,
                requiresHumanDecision: true,
                allowedDecisions: ["approve", "request_changes"],
                directLinkTemplate: "/",
              };
      return { ...current, steps: [...current.steps, step] };
    });
  }

  async function saveDraft(event: React.FormEvent) {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc("update_process_definition_draft", {
      p_definition_id: editing.id,
      p_name: editing.name,
      p_trigger_event_types: editing.trigger_event_types,
      p_trigger_conditions: editing.trigger_conditions,
      p_steps: editing.steps,
      p_draft_notes: editing.draft_notes || null,
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage(
      isShowcaseMode
        ? "Brouillon enregistré dans le Showcase, sans publication ni effet externe."
        : "Brouillon enregistré sans publication ni effet externe.",
    );
    setEditing(null);
    reload();
  }

  async function retireDraft() {
    if (!editing) return;
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc("retire_process_definition_draft", {
      p_definition_id: editing.id,
      p_reason: retireReason,
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage("Brouillon retiré et conservé dans l’historique.");
    setEditing(null);
    setRetireReason("");
    reload();
  }

  return (
    <section className="panel process-definition-manager">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Automatisations versionnées</span>
          <h2>Processus et automatisations</h2>
          <p>
            Une version active reste immuable. Toute évolution commence par une
            copie de travail propre à votre société.
          </p>
        </div>
      </div>
      <Notice tone="info">
        Un brouillon reste sans effet tant qu’il n’a pas passé une simulation
        positive, une demande motivée puis une revue explicite. Publier active
        uniquement les actions internes prévues : aucun fournisseur externe
        n’est connecté par cette décision.
      </Notice>
      {hasDeploymentCapability(data, "service_qualification") && (
        <section className="guided-process-center" data-testid="guided-process-center">
          <div className="section-heading-row">
            <div>
              <span className="eyebrow">Sujets guidés sans code</span>
              <h3>Modèles réutilisables pour préparer les devis guidés</h3>
              <p className="section-intro">
                Créez vos sujets métier avec des questions simples, puis indiquez
                l’impact concret des réponses sur le temps, le prix, les produits,
                le matériel et le texte du devis. Les identifiants techniques sont
                générés et conservés automatiquement.
              </p>
            </div>
            <button type="button" onClick={() => onManageGuidedQuote("new")}>
              ＋ Nouveau sujet guidé
            </button>
          </div>
          <Notice>
            Les observations terrain ne changent jamais un tarif seules. ARD Manager
            prépare une proposition robuste en isolant les valeurs atypiques ; vous
            la relisez, la corrigez ou la refusez avant toute nouvelle version.
          </Notice>
          <div className="guided-process-grid">
            {(data.serviceCatalogItems || [])
              .filter((item) => item.active)
              .sort((left, right) => left.name.localeCompare(right.name, "fr"))
              .map((item) => {
                const questionnaires = (data.serviceQualificationQuestionnaires || [])
                    .filter((entry) => entry.catalog_item_id === item.id)
                    .sort((left, right) => right.version - left.version),
                  current = questionnaires[0],
                  evidence = (data.qualificationRuleEvidence || []).filter(
                    (entry) => entry.catalog_item_id === item.id,
                  ),
                  evidenceCount = evidence.reduce(
                    (maximum, entry) => Math.max(maximum, Number(entry.evidence_count || 0)),
                    0,
                  );
                return (
                  <article className="guided-process-card" key={item.id}>
                    <div>
                      <strong>{item.name}</strong>
                      <small>{item.default_unit_price_ex_vat > 0 ? `${money(item.default_unit_price_ex_vat)} HT / ${item.default_unit_label}` : "Tarif de base à renseigner"}</small>
                    </div>
                    <div className="guided-process-statuses">
                      <span className={`status ${current?.status === "published" ? "active" : "warning"}`}>
                        {current ? `Questionnaire v${current.version} · ${current.status === "published" ? "publié" : "brouillon"}` : "Questionnaire à créer"}
                      </span>
                      <small>{evidenceCount ? `${evidenceCount} mission(s) au maximum par règle pour contrôler les données` : "Pas encore de retour terrain exploitable"}</small>
                    </div>
                    <button type="button" className="secondary compact" onClick={() => onManageGuidedQuote(item.id)}>
                      {current ? "Modifier par une nouvelle version" : "Créer le questionnaire"}
                    </button>
                  </article>
                );
              })}
          </div>
        </section>
      )}
      {message && (
        <Notice
          tone={/créée|enregistré|retiré|publiée|refusée/.test(message) ? "success" : "error"}
        >
          {message}
        </Notice>
      )}
      <div className="process-definition-list">
        {definitions.map((definition) => {
          const publicationReview = (data.processDefinitionPublicationReviews || []).find(
            (review) => review.definition_id === definition.id && review.status === "pending",
          );
          return (
          <article className={`process-definition-row ${definition.status}`} key={definition.id}>
            <div>
              <strong>{definition.name}</strong>
              <small>
                {definition.process_key} · version {definition.version} · {definition.organization_id ? "votre société" : "modèle système"}
              </small>
              <small>
                Déclencheur : {definition.trigger_event_types.join(", ")}
                {` · ${definition.steps.length} étape(s)`}
              </small>
            </div>
            <div className="row-actions">
              <span className={`status ${definition.status}`}>
                {definition.status === "active"
                  ? "Active"
                  : definition.status === "draft"
                    ? "Brouillon sans effet"
                    : "Retirée"}
              </span>
              {definition.status === "active" && (
                <button
                  type="button"
                  className="secondary compact"
                  disabled={busy}
                  onClick={() => cloneDefinition(definition)}
                >
                  Créer une version de travail
                </button>
              )}
              {definition.status === "draft" &&
                definition.organization_id === profile.organization_id && (
                  <>
                    {publicationReview ? (
                      <button
                        type="button"
                        className="success compact"
                        onClick={() => {
                          setPublicationReviewNote("");
                          setReviewingPublication(publicationReview);
                        }}
                      >
                        Contrôler la publication
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="secondary compact"
                        onClick={() => openSimulation(definition)}
                      >
                        Simuler avant publication
                      </button>
                    )}
                    <button
                      type="button"
                      className="secondary compact"
                      onClick={() => openEditor(definition)}
                    >
                      Modifier le brouillon
                    </button>
                  </>
                )}
            </div>
          </article>
          );
        })}
      </div>
      {editing && (
        <div className="modal-backdrop">
          <form
            className="modal-card portal-detail process-definition-editor"
            role="dialog"
            aria-label="Modifier le brouillon de processus"
            onSubmit={saveDraft}
          >
            <button
              type="button"
              className="icon-close"
              aria-label="Fermer"
              onClick={() => setEditing(null)}
            >
              ×
            </button>
            <span className="eyebrow">Version {editing.version} · sans effet</span>
            <h2>Modifier le brouillon de processus</h2>
            <label>
              Nom du processus
              <input
                required
                maxLength={160}
                value={editing.name}
                onChange={(event) =>
                  setEditing((current) =>
                    current ? { ...current, name: event.target.value } : current,
                  )
                }
              />
            </label>
            <label>
              Événements déclencheurs
              <input
                required
                value={editing.trigger_event_types.join(", ")}
                onChange={(event) =>
                  setEditing((current) =>
                    current
                      ? {
                          ...current,
                          trigger_event_types: event.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean),
                        }
                      : current,
                  )
                }
              />
              <small>Exemple : mission.report_submitted</small>
            </label>
            <div className="process-editor-section">
              <div className="section-heading">
                <div><h3>Conditions</h3><small>Toutes les conditions doivent être vraies.</small></div>
                <button type="button" className="secondary compact" onClick={addCondition}>Ajouter une condition</button>
              </div>
              {editing.trigger_conditions.map((condition, index) => (
                <div className="process-condition-row" key={`${condition.field}-${index}`}>
                  <label>
                    Information observée
                    <input value={condition.field} onChange={(event) => updateCondition(index, { field: event.target.value })} />
                  </label>
                  <label>
                    Comparaison
                    <select value={condition.operator} onChange={(event) => updateCondition(index, { operator: event.target.value as ProcessDefinitionRow["trigger_conditions"][number]["operator"] })}>
                      <option value="equals">est égal à</option>
                      <option value="not_equals">est différent de</option>
                      <option value="in">fait partie de</option>
                      <option value="exists">est renseigné</option>
                      <option value="greater_than">est supérieur à</option>
                      <option value="less_than">est inférieur à</option>
                    </select>
                  </label>
                  {condition.operator === "exists" ? (
                    <label>
                      Attendu
                      <select value={condition.value === false ? "false" : "true"} onChange={(event) => updateCondition(index, { value: event.target.value === "true" })}>
                        <option value="true">Oui, renseigné</option>
                        <option value="false">Non renseigné</option>
                      </select>
                    </label>
                  ) : (
                    <label>
                      Valeur attendue
                      <input value={condition.value == null ? "" : String(condition.value)} onChange={(event) => updateCondition(index, { value: event.target.value })} />
                    </label>
                  )}
                  <button type="button" className="secondary compact" onClick={() => setEditing((current) => current ? { ...current, trigger_conditions: current.trigger_conditions.filter((_item, itemIndex) => itemIndex !== index) } : current)}>Retirer</button>
                </div>
              ))}
            </div>
            <div className="process-editor-section">
              <div className="section-heading">
                <div><h3>Étapes</h3><small>Ordre préparé pour la future publication contrôlée.</small></div>
                <div className="row-actions">
                  <button type="button" className="secondary compact" onClick={() => addStep("notification")}>Ajouter une information</button>
                  <button type="button" className="secondary compact" onClick={() => addStep("required_action")}>Ajouter une action</button>
                </div>
              </div>
              {editing.steps.map((step, index) => {
                const kind = String(step.kind || ""),
                  channels = Array.isArray(step.channelIntents) ? step.channelIntents as string[] : [];
                return (
                  <article className="process-step-editor" key={`${String(step.id)}-${index}`}>
                    <div className="section-heading">
                      <div><strong>{kind === "notification" ? "Information ou alerte" : "Action requise"}</strong><small>{String(step.id)}</small></div>
                      {editing.steps.length > 1 && <button type="button" className="secondary compact" onClick={() => setEditing((current) => current ? { ...current, steps: current.steps.filter((_item, itemIndex) => itemIndex !== index) } : current)}>Retirer</button>}
                    </div>
                    {kind === "notification" ? (
                      <>
                        <label>Niveau<select value={String(step.level || "information")} onChange={(event) => updateStep(index, { level: event.target.value })}><option value="information">Information</option><option value="alert">Alerte</option></select></label>
                        <fieldset><legend>Canaux envisagés</legend><div className="process-channel-checks">{["internal", "push", "email", "sms"].map((channel) => <label key={channel}><input type="checkbox" checked={channels.includes(channel)} onChange={() => toggleStepChannel(index, channel)} />{channel === "internal" ? "ARD Manager" : channel === "email" ? "E-mail" : channel.toUpperCase()}</label>)}</div></fieldset>
                      </>
                    ) : (
                      <div className="form-grid">
                        <label>Délai (heures)<input type="number" min="0" step="0.5" value={Number(step.dueAfterMinutes || 0) / 60} onChange={(event) => updateStep(index, { dueAfterMinutes: Math.round(Number(event.target.value) * 60) })} /></label>
                        <label>Rappels avant échéance (heures)<input value={(Array.isArray(step.remindersBeforeDueMinutes) ? step.remindersBeforeDueMinutes as number[] : []).map((minutes) => minutes / 60).join(", ")} onChange={(event) => updateStep(index, { remindersBeforeDueMinutes: event.target.value.split(",").map((value) => Math.round(Number(value.trim()) * 60)).filter((value) => Number.isFinite(value) && value >= 0) })} /></label>
                        <label>Escalade après échéance (heures)<input type="number" min="0" step="0.5" value={step.escalateAfterMinutes == null ? "" : Number(step.escalateAfterMinutes) / 60} onChange={(event) => updateStep(index, { escalateAfterMinutes: event.target.value === "" ? null : Math.round(Number(event.target.value) * 60) })} /></label>
                        <label>Décisions permises<input value={(Array.isArray(step.allowedDecisions) ? step.allowedDecisions as string[] : []).join(", ")} onChange={(event) => updateStep(index, { allowedDecisions: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} /></label>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            <label>
              Note de travail
              <textarea rows={3} maxLength={2000} value={editing.draft_notes || ""} onChange={(event) => setEditing((current) => current ? { ...current, draft_notes: event.target.value } : current)} />
            </label>
            <Notice tone="info">Enregistrer ne publie rien. Une future étape de recette et d’autorisation sera obligatoire avant activation.</Notice>
            <div className="modal-actions">
              <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer le brouillon"}</button>
              <button type="button" className="secondary" onClick={() => setEditing(null)}>Annuler</button>
            </div>
            <details className="danger-zone">
              <summary>Retirer ce brouillon</summary>
              <label>Motif<input minLength={5} value={retireReason} onChange={(event) => setRetireReason(event.target.value)} /></label>
              <button type="button" className="danger" disabled={busy || retireReason.trim().length < 5} onClick={retireDraft}>Retirer sans supprimer l’historique</button>
            </details>
          </form>
        </div>
      )}
      {simulating && (
        <div className="modal-backdrop">
          <form
            className="modal-card portal-detail process-simulation-dialog"
            role="dialog"
            aria-label="Simuler un brouillon de processus"
            onSubmit={runSimulation}
          >
            <button
              type="button"
              className="icon-close"
              aria-label="Fermer"
              onClick={() => setSimulating(null)}
            >
              ×
            </button>
            <span className="eyebrow">Comparaison sans effet</span>
            <h2>Simuler le brouillon</h2>
            <p>
              Ces informations sont fictives. Le calcul ne crée aucune action,
              notification, mission ou écriture dans la base.
            </p>
            <div className="form-grid process-simulation-form">
              <label>
                Événement métier
                <input
                  required
                  value={simulationForm.eventType}
                  onChange={(event) => setSimulationForm((value) => ({ ...value, eventType: event.target.value }))}
                />
              </label>
              <label>
                Profil à l’origine
                <select
                  value={simulationForm.actorRole}
                  onChange={(event) => setSimulationForm((value) => ({ ...value, actorRole: event.target.value }))}
                >
                  <option value="contractor">Sous-traitant</option>
                  <option value="employee">Employé</option>
                  <option value="client">Client</option>
                  <option value="admin">Administrateur</option>
                  <option value="system">Système</option>
                </select>
              </label>
              <label>
                Type d’objet
                <input required value={simulationForm.subjectType} onChange={(event) => setSimulationForm((value) => ({ ...value, subjectType: event.target.value }))} />
              </label>
              <label>
                Identifiant fictif
                <input required value={simulationForm.subjectId} onChange={(event) => setSimulationForm((value) => ({ ...value, subjectId: event.target.value }))} />
              </label>
              <label>
                Client fictif
                <input value={simulationForm.clientName} onChange={(event) => setSimulationForm((value) => ({ ...value, clientName: event.target.value }))} />
              </label>
              <label>
                Intervenant fictif
                <input value={simulationForm.workerName} onChange={(event) => setSimulationForm((value) => ({ ...value, workerName: event.target.value }))} />
              </label>
              <label>
                Compte rendu fictif
                <input value={simulationForm.reportId} onChange={(event) => setSimulationForm((value) => ({ ...value, reportId: event.target.value }))} />
              </label>
              <label>
                Heures fictives
                <input type="number" min="0" step="0.25" value={simulationForm.actualHours} onChange={(event) => setSimulationForm((value) => ({ ...value, actualHours: event.target.value }))} />
              </label>
            </div>
            <div className="modal-actions">
              <button>Lancer la simulation</button>
              <button type="button" className="secondary" onClick={() => setSimulating(null)}>Fermer</button>
            </div>
            {simulationResult && (
              <>
                <div className="process-simulation-results">
                  {simulationSummary("Version active", simulationResult.active, simulationResult.event)}
                  {simulationSummary("Brouillon préparé", simulationResult.draft, simulationResult.event)}
                </div>
                <section className="process-publication-request">
                  <div>
                    <span className="eyebrow">Étape suivante sous contrôle</span>
                    <h3>Demander la publication</h3>
                    <p>
                      La demande fige cette simulation et le contenu du brouillon.
                      Une seconde décision restera obligatoire avant activation.
                    </p>
                  </div>
                  {simulationResult.draft.matches &&
                  simulationResult.draft.validationErrors.length === 0 ? (
                    <>
                      <label>
                        Pourquoi publier cette version ?
                        <textarea
                          rows={3}
                          minLength={10}
                          maxLength={2000}
                          value={publicationRequestNote}
                          onChange={(event) => setPublicationRequestNote(event.target.value)}
                          placeholder="Ex. Délai et destinataires relus après la simulation."
                        />
                      </label>
                      <button
                        type="button"
                        disabled={busy || publicationRequestNote.trim().length < 10}
                        onClick={requestPublication}
                      >
                        Enregistrer la demande de publication
                      </button>
                    </>
                  ) : (
                    <Notice tone="error">
                      Ce scénario ne valide pas encore le brouillon. Corrigez les
                      données fictives ou le processus avant de demander sa publication.
                    </Notice>
                  )}
                </section>
              </>
            )}
          </form>
        </div>
      )}
      {reviewingPublication && (
        <div className="modal-backdrop">
          <section
            className="modal-card portal-detail process-publication-review"
            role="dialog"
            aria-label="Contrôler la publication du processus"
          >
            <button
              type="button"
              className="icon-close"
              aria-label="Fermer"
              onClick={() => setReviewingPublication(null)}
            >
              ×
            </button>
            <span className="eyebrow">Décision humaine obligatoire</span>
            <h2>Contrôler la publication</h2>
            <Notice tone="info">
              La demande a figé la version {reviewingPublication.version_snapshot}.
              Si le brouillon a changé depuis, le serveur refusera l’activation.
            </Notice>
            <dl className="process-publication-summary">
              <div><dt>Processus</dt><dd>{String(reviewingPublication.definition_snapshot.name || reviewingPublication.process_key_snapshot)}</dd></div>
              <div><dt>Déclencheurs</dt><dd>{Array.isArray(reviewingPublication.definition_snapshot.trigger_event_types) ? reviewingPublication.definition_snapshot.trigger_event_types.join(", ") : "—"}</dd></div>
              <div><dt>Étapes</dt><dd>{Array.isArray(reviewingPublication.definition_snapshot.steps) ? reviewingPublication.definition_snapshot.steps.length : 0}</dd></div>
              <div><dt>Simulation</dt><dd>{reviewingPublication.simulation_evidence.draft?.matches ? "Positive" : "À contrôler"}</dd></div>
              <div><dt>Demandée le</dt><dd>{frDate(reviewingPublication.requested_at, true)}</dd></div>
            </dl>
            <div className="process-publication-note">
              <strong>Motif de la demande</strong>
              <p>{reviewingPublication.request_note}</p>
            </div>
            <label>
              Note de décision obligatoire
              <textarea
                rows={3}
                minLength={10}
                maxLength={2000}
                value={publicationReviewNote}
                onChange={(event) => setPublicationReviewNote(event.target.value)}
                placeholder="Indiquez les contrôles réalisés avant votre décision."
              />
            </label>
            <Notice>
              L’approbation active cette version pour les prochains événements
              de votre société et retire l’ancienne version société sans la
              supprimer. Elle n’envoie aucun message déjà passé et ne branche
              aucun fournisseur externe.
            </Notice>
            <div className="modal-actions">
              <button
                type="button"
                disabled={busy || publicationReviewNote.trim().length < 10}
                onClick={() => reviewPublication("approved")}
              >
                Approuver et activer cette version
              </button>
              <button
                type="button"
                className="danger"
                disabled={busy || publicationReviewNote.trim().length < 10}
                onClick={() => reviewPublication("rejected")}
              >
                Refuser la publication
              </button>
              <button type="button" className="secondary" onClick={() => setReviewingPublication(null)}>
                Annuler
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

type OperatingProcedureVersion = {
  id: string;
  organization_id: string;
  procedure_key: string;
  title: string;
  version: number;
  status: "draft" | "published" | "superseded" | "retired";
  summary: string;
  steps: string[];
  content_format: "one_page" | "pdf" | "image" | "video";
  resource_url: string | null;
  audience_roles: Role[];
  owner_profile_id: string | null;
  revision_on: string | null;
  requires_acknowledgement: boolean;
  change_reason: string;
  published_at: string | null;
  created_at: string;
};
type OperatingProcedureAcknowledgement = {
  id: string;
  procedure_id: string;
  profile_id: string;
  acknowledged_at: string;
};
type OperatingProcedureApplication = {
  id: string;
  procedure_id: string;
  audit_log_id: number | string;
  explanation: string;
  applied_at: string;
  validated_by: string;
  validated_at: string;
};
type ProcedureAuditCandidate = {
  id: number | string;
  actor_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  created_at: string;
};

const criticalProcedureCatalog = [
  ["quote", "Devis", "Créer, contrôler et envoyer un devis fiable."],
  ["site_visit", "Visite sur site", "Collecter les informations et preuves utiles au chiffrage."],
  ["planning", "Planning", "Planifier, affecter et confirmer les interventions."],
  ["emergencies", "Urgences", "Qualifier, prendre en charge et escalader une urgence."],
  ["billing_collection", "Facturation et relances", "Émettre, suivre et relancer sans perdre la preuve."],
  ["recruitment", "Recrutement", "Sélectionner une personne et sécuriser la décision."],
  ["onboarding", "Intégration", "Donner les accès, documents et consignes nécessaires."],
  ["replacement", "Remplacement", "Remplacer une absence sans perdre l'information terrain."],
  ["complaint", "Réclamation", "Qualifier, répondre et suivre une réclamation client."],
  ["product_order", "Commande de produits", "Contrôler le besoin, commander et tracer la réception."],
] as const;

function OperatingProcedureLibrary({
  profile,
  data,
}: {
  profile: Profile;
  data: DataState;
}) {
  const [versions, setVersions] = useState<OperatingProcedureVersion[]>([]),
    [acknowledgements, setAcknowledgements] = useState<OperatingProcedureAcknowledgement[]>([]),
    [applications, setApplications] = useState<OperatingProcedureApplication[]>([]),
    [auditCandidates, setAuditCandidates] = useState<ProcedureAuditCandidate[]>([]),
    [available, setAvailable] = useState(true),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [editor, setEditor] = useState<{
      procedureKey: string;
      title: string;
      summary: string;
      stepsText: string;
      contentFormat: OperatingProcedureVersion["content_format"];
      resourceUrl: string;
      audienceRoles: Role[];
      ownerProfileId: string;
      revisionOn: string;
      requiresAcknowledgement: boolean;
      changeReason: string;
    } | null>(null),
    [applicationEditor, setApplicationEditor] = useState<{
      procedure: OperatingProcedureVersion;
      auditLogId: string;
      explanation: string;
    } | null>(null);
  const loadProcedures = useCallback(async () => {
    const [result,ackResult,applicationResult,auditResult] = await Promise.all([
      supabase!.from("operating_procedure_versions").select("id,organization_id,procedure_key,title,version,status,summary,steps,content_format,resource_url,audience_roles,owner_profile_id,revision_on,requires_acknowledgement,change_reason,published_at,created_at").order("procedure_key").order("version", { ascending: false }),
      supabase!.from("operating_procedure_acknowledgements").select("id,procedure_id,profile_id,acknowledged_at").order("acknowledged_at", { ascending: false }),
      supabase!.from("operating_procedure_applications").select("id,procedure_id,audit_log_id,explanation,applied_at,validated_by,validated_at").order("validated_at", { ascending: false }),
      supabase!.from("audit_logs").select("id,actor_id,action,entity_type,entity_id,created_at").order("created_at", { ascending: false }).limit(80),
    ]);
    setLoading(false);
    if (result.error && missingDeploymentObject(result.error.message)) {
      setAvailable(false);
      return;
    }
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setVersions((result.data || []) as OperatingProcedureVersion[]);
    if (!ackResult.error) setAcknowledgements((ackResult.data || []) as OperatingProcedureAcknowledgement[]);
    if (!applicationResult.error) setApplications((applicationResult.data || []) as OperatingProcedureApplication[]);
    if (!auditResult.error) setAuditCandidates(((auditResult.data || []) as ProcedureAuditCandidate[]).filter((item) => !item.action.startsWith("operating_procedure_")));
  }, []);
  useEffect(() => { void loadProcedures(); }, [loadProcedures]);
  const editableProfiles = (data.profiles || []).filter((item) =>
      ["admin", "employee", "contractor"].includes(item.role),
    ),
    publishedCount = criticalProcedureCatalog.filter(([key]) =>
      versions.some((item) => item.procedure_key === key && item.status === "published"),
    ).length;
  function openProcedure(procedureKey: string) {
    const catalog = criticalProcedureCatalog.find(([key]) => key === procedureKey),
      rows = versions.filter((item) => item.procedure_key === procedureKey),
      source = rows.find((item) => item.status === "draft") || rows.find((item) => item.status === "published");
    setEditor({
      procedureKey,
      title: source?.title || catalog?.[1] || "",
      summary: source?.summary || catalog?.[2] || "",
      stepsText: (source?.steps || []).join("\n"),
      contentFormat: source?.content_format || "one_page",
      resourceUrl: source?.resource_url || "",
      audienceRoles: source?.audience_roles || ["admin"],
      ownerProfileId: source?.owner_profile_id || profile.id,
      revisionOn: source?.revision_on || parisDay(new Date(Date.now() + 90 * 86400000)),
      requiresAcknowledgement: source?.requires_acknowledgement || false,
      changeReason: source?.status === "draft" ? source.change_reason : "Nouvelle version de la procédure",
    });
    setMessage("");
  }
  async function saveProcedure(event: React.FormEvent) {
    event.preventDefault();
    if (!editor) return;
    const steps = editor.stepsText.split("\n").map((item) => item.trim()).filter(Boolean);
    setBusy(true);
    const { error } = await supabase!.rpc("save_operating_procedure_draft", {
      p_procedure_key: editor.procedureKey,
      p_title: editor.title.trim(),
      p_summary: editor.summary.trim(),
      p_steps: steps,
      p_content_format: editor.contentFormat,
      p_resource_url: editor.resourceUrl.trim() || null,
      p_audience_roles: editor.audienceRoles,
      p_owner_profile_id: editor.ownerProfileId || null,
      p_revision_on: editor.revisionOn || null,
      p_requires_acknowledgement: editor.requiresAcknowledgement,
      p_change_reason: editor.changeReason.trim(),
    });
    setBusy(false);
    setMessage(error ? error.message : "Brouillon enregistré. Aucun contenu n’a été publié automatiquement.");
    if (!error) { setEditor(null); await loadProcedures(); }
  }
  async function publishProcedure(procedure: OperatingProcedureVersion) {
    setBusy(true);
    const { error } = await supabase!.rpc("publish_operating_procedure", {
      p_procedure_id: procedure.id,
    });
    setBusy(false);
    setMessage(error ? error.message : "Procédure publiée. La version précédente reste dans l’historique.");
    if (!error) await loadProcedures();
  }
  async function registerApplication(event: React.FormEvent) {
    event.preventDefault();
    if (!applicationEditor) return;
    const parsedAuditId = /^\d+$/.test(applicationEditor.auditLogId)
      ? Number(applicationEditor.auditLogId)
      : applicationEditor.auditLogId;
    setBusy(true);
    const { error } = await supabase!.rpc("register_operating_procedure_application", {
      p_procedure_id: applicationEditor.procedure.id,
      p_audit_log_id: parsedAuditId,
      p_explanation: applicationEditor.explanation.trim(),
    });
    setBusy(false);
    setMessage(error ? error.message : "Application prouvée par une trace opérationnelle existante.");
    if (!error) { setApplicationEditor(null); await loadProcedures(); }
  }
  const auditLabel = (item: ProcedureAuditCandidate) => {
    const actor = (data.profiles || []).find((profileItem) => profileItem.id === item.actor_id)?.display_name || "Système";
    return `${new Date(item.created_at).toLocaleString("fr-FR")} · ${item.action.replaceAll("_", " ")} · ${item.entity_type} · ${actor}`;
  };
  function toggleAudience(role: Role) {
    setEditor((current) => {
      if (!current) return current;
      const audienceRoles = current.audienceRoles.includes(role)
        ? current.audienceRoles.filter((item) => item !== role)
        : [...current.audienceRoles, role];
      return { ...current, audienceRoles };
    });
  }
  return (
    <section className="panel operating-procedure-library" data-testid="operating-procedure-library">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Savoir-faire opérationnel</span>
          <h2>Procédures critiques</h2>
          <p className="section-intro">Une page simple ou un support court, versionné et relu. L’ancien contenu publié n’est jamais réécrit.</p>
        </div>
        <div className="procedure-progress"><strong>{publishedCount}/10</strong><small>publiées</small></div>
      </div>
      {!available ? <Notice>En cours de développement sur cet environnement.</Notice> : loading ? <p>Chargement des procédures…</p> : (
        <div className="procedure-catalog-grid">
          {criticalProcedureCatalog.map(([key, title, description]) => {
            const rows = versions.filter((item) => item.procedure_key === key),
              draft = rows.find((item) => item.status === "draft"),
              published = rows.find((item) => item.status === "published"),
              owner = (data.profiles || []).find((item) => item.id === (draft || published)?.owner_profile_id);
            const acknowledgementCount = published ? acknowledgements.filter((item) => item.procedure_id === published.id).length : 0,
              applicationCount = published ? applications.filter((item) => item.procedure_id === published.id).length : 0;
            return <article className="procedure-card" key={key}>
              <div className="procedure-card-head"><strong>{title}</strong><span className={`status ${published ? "active" : draft ? "pending" : "draft"}`}>{published ? `Publiée · v${published.version}` : draft ? `Brouillon · v${draft.version}` : "À créer"}</span></div>
              <p>{(draft || published)?.summary || description}</p>
              <small>{owner ? `Responsable : ${owner.display_name}` : "Responsable à définir"}{(draft || published)?.revision_on ? ` · révision ${frDate((draft || published)!.revision_on!)}` : " · révision à définir"}{published?.requires_acknowledgement ? ` · ${acknowledgementCount} lecture(s) confirmée(s)` : ""}{published ? ` · ${applicationCount} application(s) prouvée(s)` : ""}</small>
              <div className="row-actions">
                <button type="button" className="secondary compact" onClick={() => openProcedure(key)}>{draft ? "Modifier le brouillon" : published ? "Préparer une nouvelle version" : "Créer"}</button>
                {published && <button type="button" className="secondary compact" disabled={!auditCandidates.length} onClick={() => setApplicationEditor({ procedure: published, auditLogId: String(auditCandidates[0]?.id || ""), explanation: "" })}>Prouver une application</button>}
                {draft && <button type="button" className="compact" disabled={busy} onClick={() => publishProcedure(draft)}>Publier</button>}
              </div>
            </article>;
          })}
        </div>
      )}
      {message && <Notice tone={message.startsWith("Brouillon") || message.startsWith("Procédure publiée") || message.startsWith("Application prouvée") ? "success" : "error"}>{message}</Notice>}
      {editor && <div className="portal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setEditor(null); }}>
        <form className="portal-detail procedure-editor" role="dialog" aria-modal="true" aria-label="Modifier la procédure" onSubmit={saveProcedure}>
          <div className="portal-detail-head"><div><span className="eyebrow">Procédure versionnée</span><h2>{editor.title}</h2></div><button type="button" className="icon-close" onClick={() => setEditor(null)}>×</button></div>
          <div className="form-grid">
            <label className="wide">Titre<input required minLength={3} maxLength={160} value={editor.title} onChange={(event) => setEditor((current) => current ? {...current,title:event.target.value} : current)} /></label>
            <label className="wide">Objectif et périmètre<textarea maxLength={2000} value={editor.summary} onChange={(event) => setEditor((current) => current ? {...current,summary:event.target.value} : current)} /></label>
            <label className="wide">Étapes — une instruction simple par ligne<textarea required rows={8} value={editor.stepsText} onChange={(event) => setEditor((current) => current ? {...current,stepsText:event.target.value} : current)} placeholder={'1. Vérifier…\n2. Décider…\n3. Conserver la preuve…'} /></label>
            <label>Support<select value={editor.contentFormat} onChange={(event) => setEditor((current) => current ? {...current,contentFormat:event.target.value as OperatingProcedureVersion["content_format"]} : current)}><option value="one_page">Fiche une page</option><option value="pdf">PDF</option><option value="image">Image</option><option value="video">Vidéo courte</option></select></label>
            {editor.contentFormat !== "one_page" && <label>Lien du support<input required type="url" value={editor.resourceUrl} onChange={(event) => setEditor((current) => current ? {...current,resourceUrl:event.target.value} : current)} /></label>}
            <fieldset className="wide procedure-audience"><legend>Profils concernés</legend>{(["admin","client","contractor","employee"] as Role[]).map((role) => <label key={role}><input type="checkbox" checked={editor.audienceRoles.includes(role)} onChange={() => toggleAudience(role)} /> {role === "admin" ? "Administrateur" : role === "client" ? "Client" : role === "contractor" ? "Sous-traitant" : "Employé"}</label>)}</fieldset>
            <label>Responsable<select required value={editor.ownerProfileId} onChange={(event) => setEditor((current) => current ? {...current,ownerProfileId:event.target.value} : current)}><option value="">Choisir</option>{editableProfiles.map((item) => <option key={item.id} value={item.id}>{item.display_name}</option>)}</select></label>
            <label>Prochaine révision<input required type="date" value={editor.revisionOn} onChange={(event) => setEditor((current) => current ? {...current,revisionOn:event.target.value} : current)} /></label>
            <label className="wide checkbox-line"><input type="checkbox" checked={editor.requiresAcknowledgement} onChange={(event) => setEditor((current) => current ? {...current,requiresAcknowledgement:event.target.checked} : current)} /> Demander un accusé de lecture aux profils concernés</label>
            <label className="wide">Pourquoi cette version ?<textarea required minLength={5} value={editor.changeReason} onChange={(event) => setEditor((current) => current ? {...current,changeReason:event.target.value} : current)} /></label>
          </div>
          <Notice>Enregistrer crée ou met à jour uniquement le brouillon. La publication reste une action séparée et explicite.</Notice>
          <div className="detail-actions"><button type="button" className="secondary" onClick={() => setEditor(null)}>Annuler</button><button disabled={busy || !editor.audienceRoles.length}>{busy ? "Enregistrement…" : "Enregistrer le brouillon"}</button></div>
        </form>
      </div>}
      {applicationEditor && <div className="portal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setApplicationEditor(null); }}>
        <form className="portal-detail procedure-application-editor" role="dialog" aria-modal="true" aria-label="Prouver l’application de la procédure" onSubmit={registerApplication}>
          <div className="portal-detail-head"><div><span className="eyebrow">Preuve d’application · version {applicationEditor.procedure.version}</span><h2>{applicationEditor.procedure.title}</h2></div><button type="button" className="icon-close" onClick={() => setApplicationEditor(null)}>×</button></div>
          <p>Choisissez une action déjà inscrite au journal. ARD Manager conservera le lien vers cette trace au lieu d’accepter une simple déclaration.</p>
          <div className="form-grid">
            <label className="wide">Trace opérationnelle<select required value={applicationEditor.auditLogId} onChange={(event) => setApplicationEditor((current) => current ? {...current,auditLogId:event.target.value} : current)}>{auditCandidates.map((item) => <option key={String(item.id)} value={String(item.id)}>{auditLabel(item)}</option>)}</select></label>
            <label className="wide">Pourquoi cette trace démontre-t-elle l’application ?<textarea required minLength={10} maxLength={2000} rows={5} value={applicationEditor.explanation} onChange={(event) => setApplicationEditor((current) => current ? {...current,explanation:event.target.value} : current)} placeholder="Ex. l’urgence a été qualifiée, prise en charge en N1 puis résolue avec cause et compte rendu horodatés." /></label>
          </div>
          <Notice>La lecture seule n’est jamais comptée comme application. Une action liée à la procédure elle-même ne peut pas servir de preuve.</Notice>
          <div className="detail-actions"><button type="button" className="secondary" onClick={() => setApplicationEditor(null)}>Annuler</button><button disabled={busy || !applicationEditor.auditLogId}>{busy ? "Validation…" : "Valider cette preuve"}</button></div>
        </form>
      </div>}
    </section>
  );
}

type EmergencyRotation = {
  id: string; name: string; starts_at: string; ends_at: string;
  n1_profile_id: string; backup_profile_id?: string | null; contact_phone?: string | null;
  status: "scheduled" | "active" | "closed" | "cancelled"; notes?: string | null;
};
type OperationalEmergency = {
  id: string; mission_id?: string | null; property_id?: string | null; title: string; description: string;
  severity: "low" | "moderate" | "high" | "critical";
  status: "reported" | "acknowledged_n1" | "escalated_n2" | "resolved" | "closed";
  rotation_id?: string | null; n1_profile_id?: string | null; n2_profile_id?: string | null;
  emergency_kind?: string; current_escalation_level?: number | null;
  current_assignee_profile_id?: string | null; escalation_trigger?: "n1_request" | "n1_timeout" | "direct_rule" | null;
  reported_by: string; occurred_at: string; acknowledgement_due_at: string;
  reported_origin?: "terrain" | "client_relay" | "admin_call" | "responsible_manager" | "other";
  client_id?: string | null; intervention_commitment?: string | null; intervention_target_at?: string | null;
  site_address?:string|null;site_contact_name?:string|null;site_contact_phone?:string|null;
  access_instructions?:string|null;expected_action?:string|null;equipment_notes?:string|null;
  critical_billing_accepted_at?: string | null;
  emergency_billing_status?: "not_assessed" | "eligible_for_review" | "awaiting_acknowledgement" | "draft_requested" | "waived" | "invoiced";
  emergency_billing_triggered_at?: string | null; emergency_billing_review_note?: string | null;
  acknowledged_at?: string | null; escalated_at?: string | null; root_cause?: string | null;
  resolution_summary?: string | null; recurrence_key?: string | null; resolved_at?: string | null;
};
type EmergencyReporterAuthorization={profile_id:string;enabled:boolean;reason:string;valid_from:string;valid_until?:string|null};
const emergencySeverityLabels: Record<OperationalEmergency["severity"], string> = {
  low: "Faible", moderate: "Modérée", high: "Élevée", critical: "Critique",
};
const emergencyKindLabels: Record<string, string> = {
  general: "Incident général", access: "Accès / clés", absence: "Absence", safety: "Sécurité",
  water_damage: "Dégât des eaux", equipment: "Matériel", client: "Demande client urgente",
};
const emergencyBillingLabels:Record<string,string>={
  eligible_for_review:"Facturation urgence à décider",
  awaiting_acknowledgement:"Facturation critique en attente de prise en charge",
  draft_requested:"Préparation de facturation ouverte",
  waived:"Facturation urgence écartée",
  invoiced:"Intervention d’urgence facturée",
};

function EmergencyOperationsCenter({
  profile,
  data,
  mode = "full",
  onOpenMissions,
}: {
  profile: Profile;
  data: DataState;
  mode?: "full" | "priority";
  onOpenMissions?: () => void;
}) {
  const [rotations, setRotations] = useState<EmergencyRotation[]>([]),
    [emergencies, setEmergencies] = useState<OperationalEmergency[]>([]),
    [emergencyInterventions,setEmergencyInterventions]=useState<any[]>([]),
    [emergencyTasks,setEmergencyTasks]=useState<any[]>([]),
    [emergencyEvidence,setEmergencyEvidence]=useState<any[]>([]),
    [emergencyEvents,setEmergencyEvents]=useState<any[]>([]),
    [emergencyCommercialSnapshots,setEmergencyCommercialSnapshots]=useState<any[]>([]),
    [reporterAuthorizations,setReporterAuthorizations]=useState<EmergencyReporterAuthorization[]>([]),
    [available, setAvailable] = useState(true), [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false), [message, setMessage] = useState(""),
    [month, setMonth] = useState(parisDay(new Date()).slice(0, 7)),
    [reportEditor, setReportEditor] = useState<{clientId:string;propertyId:string;missionId:string;title:string;description:string;severity:OperationalEmergency["severity"];emergencyKind:string;occurredAt:string;recurrenceKey:string;siteAddress:string;siteContactName:string;siteContactPhone:string;accessInstructions:string;expectedAction:string;equipmentNotes:string}|null>(null),
    [authorizationEditor,setAuthorizationEditor]=useState<{profileId:string;enabled:boolean;reason:string}|null>(null),
    [billingEditor,setBillingEditor]=useState<{emergency:OperationalEmergency;decision:"request_draft"|"waive";note:string}|null>(null),
    [selectedEmergencyId,setSelectedEmergencyId]=useState<string|null>(null),
    [reviewEditor,setReviewEditor]=useState<{intervention:any;clientId:string;approve:boolean;shareReport:boolean;billable:boolean;billingMode:"monthly_grouped"|"standalone";note:string}|null>(null),
    [actionEditor, setActionEditor] = useState<{ emergency: OperationalEmergency; kind: "escalate" | "resolve"; note: string; rootCause: string; recurrenceKey: string; nextReviewOn: string } | null>(null);
  const load = useCallback(async () => {
    const [rotationResult, emergencyResult,authorizationResult,interventionResult,taskResult,evidenceResult,eventResult,snapshotResult] = await Promise.all([
      supabase!.rpc("list_emergency_on_call_rotations_safe"),
      supabase!.from("operational_emergencies").select("id,mission_id,property_id,title,description,severity,emergency_kind,status,rotation_id,current_escalation_level,current_assignee_profile_id,escalation_trigger,n1_profile_id,n2_profile_id,reported_by,reported_origin,client_id,occurred_at,acknowledgement_due_at,intervention_commitment,intervention_target_at,critical_billing_accepted_at,emergency_billing_status,emergency_billing_triggered_at,emergency_billing_review_note,acknowledged_at,escalated_at,root_cause,resolution_summary,recurrence_key,resolved_at,site_address,site_contact_name,site_contact_phone,access_instructions,expected_action,equipment_notes").order("occurred_at", { ascending: false }),
      supabase!.from("operational_emergency_reporter_authorizations").select("profile_id,enabled,reason,valid_from,valid_until").order("created_at"),
      supabase!.from("operational_emergency_interventions").select("id,emergency_id,assignee_profile_id,origin_kind,origin_address,destination_address,status,departed_at,arrived_at,site_departed_at,return_arrived_at,action_summary,remaining_actions,actual_minutes,employee_payable_minutes,completed_at,admin_review_status,admin_review_note,admin_reviewed_by,admin_reviewed_at,client_report_visible,billing_mode,invoice_id").order("created_at",{ascending:false}),
      supabase!.from("operational_emergency_intervention_tasks").select("id,intervention_id,label,position,is_required,minimum_photo_count,completed_at,notes").order("position"),
      supabase!.from("operational_emergency_intervention_evidence").select("id,intervention_id,task_id,stage,original_name,captured_at,client_visible").order("captured_at"),
      supabase!.from("operational_emergency_events").select("id,emergency_id,event_type,note,previous_status,new_status,actor_profile_id,created_at").order("created_at"),
      supabase!.from("operational_emergency_commercial_snapshots").select("id,emergency_id,client_hourly_rate_ex_vat,client_vat_rate,billing_rounding_mode,client_billable_hours,calculated_client_amount_ex_vat"),
    ]);
    setLoading(false);
    const error = rotationResult.error || emergencyResult.error || authorizationResult.error;
    if (error && missingDeploymentObject(error.message)) { setAvailable(false); return; }
    if (error) { setMessage(error.message); return; }
    setRotations((rotationResult.data || []) as EmergencyRotation[]);
    setEmergencies((emergencyResult.data || []) as OperationalEmergency[]);
    setReporterAuthorizations((authorizationResult.data||[]) as EmergencyReporterAuthorization[]);
    setEmergencyInterventions(interventionResult.data||[]);
    setEmergencyTasks(taskResult.data||[]);
    setEmergencyEvidence(evidenceResult.data||[]);
    setEmergencyEvents(eventResult.data||[]);
    setEmergencyCommercialSnapshots(snapshotResult.data||[]);
  }, []);
  useEffect(() => { void load(); }, [load]);
  const profiles = (data.profiles || []).filter((item) => ["admin", "employee", "contractor"].includes(item.role)),
    profileName = (id?: string | null) => profiles.find((item) => item.id === id)?.display_name || "Non défini",
    monthRows = emergencies.filter((item) => item.occurred_at.slice(0, 7) === month),
    resolvedN1 = monthRows.filter((item) => item.resolved_at && !item.escalated_at).length,
    escalatedN2 = monthRows.filter((item) => item.escalated_at).length,
    responseRows = monthRows.filter((item) => item.acknowledged_at),
    averageResponse = responseRows.length ? Math.round(responseRows.reduce((sum, item) => sum + (new Date(item.acknowledged_at!).getTime() - new Date(item.occurred_at).getTime()) / 60000, 0) / responseRows.length) : null,
    activeRotation = rotations.find((item) => item.status !== "cancelled" && new Date(item.starts_at) <= new Date() && new Date(item.ends_at) > new Date()),
    datetimeInput = (value: Date) => new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    employeeProfiles=(data.profiles||[]).filter((item)=>item.role==="employee"),
    selectedClientProperties=reportEditor?.clientId?data.properties.filter((item)=>item.client_id===reportEditor.clientId&&item.active):[],
    selectedClientMissions=reportEditor?.clientId?data.missions.filter((item)=>item.client_id===reportEditor.clientId):[],
    selectedEmergency=selectedEmergencyId?emergencies.find((item)=>item.id===selectedEmergencyId)||null:null,
    selectedIntervention=selectedEmergency?emergencyInterventions.find((item)=>item.emergency_id===selectedEmergency.id)||null:null,
    selectedTasks=selectedIntervention?emergencyTasks.filter((item)=>item.intervention_id===selectedIntervention.id):[],
    selectedEvidence=selectedIntervention?emergencyEvidence.filter((item)=>item.intervention_id===selectedIntervention.id):[],
    selectedEvents=selectedEmergency?emergencyEvents.filter((item)=>item.emergency_id===selectedEmergency.id):[],
    selectedSnapshot=selectedEmergency?emergencyCommercialSnapshots.find((item)=>item.emergency_id===selectedEmergency.id)||null:null;
  async function reportEmergency(event: React.FormEvent) {
    event.preventDefault(); if (!reportEditor) return; setBusy(true);
    const { error } = await supabase!.rpc("report_operational_emergency_v3", {p_client_id:reportEditor.clientId||null,p_property_id:reportEditor.propertyId||null,p_mission_id:reportEditor.missionId||null,p_title:reportEditor.title,p_description:reportEditor.description,p_severity:reportEditor.severity,p_emergency_kind:reportEditor.emergencyKind,p_occurred_at:new Date(reportEditor.occurredAt).toISOString(),p_recurrence_key:reportEditor.recurrenceKey||null,p_site_address:reportEditor.siteAddress||null,p_site_contact_name:reportEditor.siteContactName||null,p_site_contact_phone:reportEditor.siteContactPhone||null,p_access_instructions:reportEditor.accessInstructions||null,p_expected_action:reportEditor.expectedAction||null,p_equipment_notes:reportEditor.equipmentNotes||null});
    setBusy(false); setMessage(error ? error.message : "Urgence déclarée : alertes prioritaires de l’astreinte planifiées jusqu’à prise en charge ou escalade. Aucun effet financier automatique.");
    if (!error) { setReportEditor(null); await load(); }
  }
  async function saveReporterAuthorization(event:React.FormEvent){
    event.preventDefault();if(!authorizationEditor)return;setBusy(true);
    const {error}=await supabase!.rpc("set_operational_emergency_reporter_authorization",{p_profile_id:authorizationEditor.profileId,p_enabled:authorizationEditor.enabled,p_reason:authorizationEditor.reason,p_valid_from:parisDay(new Date()),p_valid_until:null});
    setBusy(false);setMessage(error?error.message:authorizationEditor.enabled?"Responsable habilité à décider du déclenchement d’une urgence.":"Habilitation de déclaration retirée.");
    if(!error){setAuthorizationEditor(null);await load();}
  }
  async function acknowledge(item: OperationalEmergency) {
    setBusy(true); const { error } = await supabase!.rpc("acknowledge_operational_emergency", { p_emergency_id: item.id, p_note: "Prise en charge depuis le centre d’urgences." });
    setBusy(false); setMessage(error ? error.message : item.reported_origin==="client_relay"&&item.severity==="critical"&&item.critical_billing_accepted_at ? `Prise en charge N${item.current_escalation_level||1} horodatée : la préparation de facturation d’urgence est ouverte.` : `Prise en charge N${item.current_escalation_level || 1} horodatée.`); if (!error) await load();
  }
  async function submitBillingDecision(event:React.FormEvent){
    event.preventDefault();if(!billingEditor)return;setBusy(true);
    const {error}=await supabase!.rpc("decide_operational_emergency_billing",{p_emergency_id:billingEditor.emergency.id,p_decision:billingEditor.decision,p_note:billingEditor.note});
    setBusy(false);setMessage(error?error.message:billingEditor.decision==="request_draft"?"Préparation de facturation d’urgence demandée ; le tarif reste à valider.":"Facturation d’urgence écartée avec motif.");
    if(!error){setBillingEditor(null);await load();}
  }
  async function submitEmergencyReview(event:React.FormEvent){
    event.preventDefault();if(!reviewEditor)return;setBusy(true);
    const {error}=await supabase!.rpc("review_operational_emergency_intervention",{p_intervention_id:reviewEditor.intervention.id,p_approve:reviewEditor.approve,p_client_id:reviewEditor.clientId||null,p_share_report_with_client:reviewEditor.shareReport,p_billable:reviewEditor.billable,p_billing_mode:reviewEditor.billable?reviewEditor.billingMode:null,p_review_note:reviewEditor.note});
    setBusy(false);
    setMessage(error?error.message:reviewEditor.approve?(reviewEditor.billable?`Compte rendu validé : ${reviewEditor.billingMode==="monthly_grouped"?"ligne ajoutée au brouillon mensuel":"facture indépendante créée en brouillon"}. Aucun envoi Henrri.`:"Compte rendu validé comme non facturable."):"Corrections demandées à l’intervenant.");
    if(!error){setReviewEditor(null);await load();}
  }
  function openEmergencyReview(intervention:any,emergency:OperationalEmergency){
    setReviewEditor({intervention,clientId:emergency.client_id||"",approve:true,shareReport:false,billable:false,billingMode:"monthly_grouped",note:""});
  }
  async function completeAction(event: React.FormEvent) {
    event.preventDefault(); if (!actionEditor) return; setBusy(true);
    const result = actionEditor.kind === "escalate"
      ? await supabase!.rpc("escalate_operational_emergency", { p_emergency_id: actionEditor.emergency.id, p_reason: actionEditor.note })
      : await supabase!.rpc("resolve_operational_emergency", { p_emergency_id: actionEditor.emergency.id, p_resolution_summary: actionEditor.note, p_root_cause: actionEditor.rootCause, p_recurrence_key: actionEditor.recurrenceKey || null, p_next_review_on: actionEditor.nextReviewOn || null });
    setBusy(false); setMessage(result.error ? result.error.message : actionEditor.kind === "escalate" ? "Urgence escaladée au N2 avec motif." : "Urgence résolue avec cause et trace de résolution.");
    if (!result.error) { setActionEditor(null); await load(); }
  }
  function openAction(emergency: OperationalEmergency, kind: "escalate" | "resolve") {
    setActionEditor({ emergency, kind, note: "", rootCause: emergency.root_cause || "", recurrenceKey: emergency.recurrence_key || "", nextReviewOn: parisDay(new Date(Date.now() + 30 * 86400000)) }); setMessage("");
  }
  const openEmergencies = emergencies
      .filter((item) => !["resolved", "closed"].includes(item.status))
      .sort((left, right) => {
        const severityRank: Record<OperationalEmergency["severity"], number> = {
          low: 0,
          moderate: 1,
          high: 2,
          critical: 3,
        };
        const severityDifference = severityRank[right.severity] - severityRank[left.severity];
        return severityDifference || new Date(left.acknowledgement_due_at).getTime() - new Date(right.acknowledgement_due_at).getTime();
      }),
    criticalEmergencies = openEmergencies.filter((item) => item.severity === "critical"),
    lateEmergencies = openEmergencies.filter(
      (item) => item.status === "reported" && new Date(item.acknowledgement_due_at).getTime() < Date.now(),
    ),
    originLabel = (item: OperationalEmergency) =>
      item.reported_origin === "client_relay"
        ? "Client · appel d’un locataire"
        : item.reported_origin === "admin_call"
          ? "Administrateur · appel reçu"
          : item.reported_origin === "responsible_manager"
            ? "Responsable salarié habilité"
          : item.reported_origin === "terrain"
            ? "Équipe terrain"
            : "Autre origine";
  if (mode === "priority") {
    return <section className={`panel admin-emergency-priority${criticalEmergencies.length || lateEmergencies.length ? " has-critical" : ""}`} data-testid="admin-emergency-priority">
      <div className="admin-emergency-priority-head">
        <div>
          <span className="eyebrow">Priorité opérationnelle</span>
          <h2>Urgences en cours</h2>
          <p>Les appels client, signalements administratifs et incidents terrain sont regroupés ici avant les indicateurs de pilotage.</p>
        </div>
        <div className="admin-emergency-priority-counts" aria-label="Synthèse des urgences">
          <span><strong>{openEmergencies.length}</strong> ouverte(s)</span>
          <span><strong>{criticalEmergencies.length}</strong> critique(s)</span>
          <span><strong>{lateEmergencies.length}</strong> en retard</span>
        </div>
      </div>
      {!available ? <Notice>En cours de développement sur cet environnement.</Notice> : loading ? <p>Chargement des urgences prioritaires…</p> : <>
        {openEmergencies.length ? <div className="admin-emergency-priority-list">
          {openEmergencies.slice(0, 3).map((item) => {
            const overdue = item.status === "reported" && new Date(item.acknowledgement_due_at).getTime() < Date.now();
            return <article className={`admin-emergency-priority-row severity-${item.severity}`} key={item.id}>
              <div>
                <span className="eyebrow">{emergencySeverityLabels[item.severity]} · {emergencyKindLabels[item.emergency_kind || "general"] || item.emergency_kind} · {labels[item.status] || item.status}</span>
                <h3>{item.title}</h3>
                <small>{originLabel(item)} · {frDate(item.occurred_at, true)}{item.intervention_commitment?` · intervention ${item.intervention_commitment.toLowerCase()}`:""}</small>
                {item.emergency_billing_status&&item.emergency_billing_status!=="not_assessed"&&<small className="emergency-billing-state">{emergencyBillingLabels[item.emergency_billing_status]||item.emergency_billing_status}</small>}
              </div>
              <div className={`admin-emergency-priority-deadline${overdue ? " overdue" : ""}`}>
                <span>{overdue ? "Prise en charge en retard" : "Échéance N1"}</span>
                <strong>{frDate(item.acknowledgement_due_at, true)}</strong>
              </div>
            </article>;
          })}
        </div> : <p className="admin-emergency-empty">Aucune urgence ouverte. Le centre reste accessible pour déclarer ou consulter un incident.</p>}
        <div className="admin-emergency-priority-footer">
          <span>{activeRotation ? <>Astreinte : <strong>{profileName(activeRotation.n1_profile_id)}</strong>{activeRotation.contact_phone ? ` · ${activeRotation.contact_phone}` : ""}</> : "Aucune astreinte active"}</span>
          <button type="button" onClick={onOpenMissions}>Ouvrir le centre d’urgences</button>
        </div>
      </>}
    </section>;
  }
  return <section className="panel emergency-operations-center" data-testid="emergency-operations-center">
    <div className="section-heading"><div><span className="eyebrow">Délégation opérationnelle</span><h2>Urgences & astreinte N1/N2</h2><p className="section-intro">Le N1 prend en charge, le N2 intervient selon la gravité ou l’absence de réponse. Chaque étape reste horodatée.</p></div><label>Mois analysé<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label></div>
    {!available ? <Notice>En cours de développement sur cet environnement.</Notice> : loading ? <p>Chargement du circuit d’urgence…</p> : <>
      <div className="emergency-kpis"><article><span>Incidents</span><strong>{monthRows.length}</strong></article><article><span>Résolus en N1</span><strong>{resolvedN1}</strong></article><article><span>Escaladés N2</span><strong>{escalatedN2}</strong></article><article><span>Réponse moyenne</span><strong>{averageResponse == null ? "—" : `${averageResponse} min`}</strong></article></div>
      <article className="on-call-summary"><div><span className="eyebrow">Astreinte actuelle</span><h3>{activeRotation?.name || "Aucune astreinte active"}</h3>{activeRotation ? <p>N1 : <strong>{profileName(activeRotation.n1_profile_id)}</strong>{activeRotation.backup_profile_id ? ` · renfort : ${profileName(activeRotation.backup_profile_id)}` : ""} · jusqu’au {frDate(activeRotation.ends_at, true)}</p> : <p>La rotation, les niveaux et la rémunération se règlent dans Équipe &amp; RH.</p>}</div><div>{activeRotation?.contact_phone && <strong>{activeRotation.contact_phone}</strong>}<small>Numéro repris automatiquement de la fiche RH du titulaire.</small></div><span className="status-badge">Planning : Équipe &amp; RH</span><button type="button" onClick={() => setReportEditor({clientId:"",propertyId:"",missionId:"",title:"",description:"",severity:"moderate",emergencyKind:"general",occurredAt:datetimeInput(new Date()),recurrenceKey:"",siteAddress:"",siteContactName:"",siteContactPhone:"",accessInstructions:"",expectedAction:"",equipmentNotes:""})}>Déclarer une urgence</button></article>
      <details className="emergency-reporter-permissions"><summary><strong>Qui peut décider de déclencher une urgence ?</strong></summary><Notice>Les clients et administrateurs peuvent déclarer. Un salarié ne le peut que s’il reçoit cette habilitation de responsable. Un sous-traitant ne peut jamais être habilité. Être d’astreinte permet de traiter une urgence, pas de la créer.</Notice><div className="permission-set-list">{employeeProfiles.map((employee)=>{const authorization=reporterAuthorizations.find((item)=>item.profile_id===employee.id),enabled=!!authorization?.enabled;return <article key={employee.id}><div><strong>{employee.display_name}</strong><small>{enabled?`Responsable habilité · ${authorization?.reason}`:"Salarié terrain · doit prévenir son responsable"}</small></div><button type="button" className={enabled?"secondary compact":"compact"} onClick={()=>setAuthorizationEditor({profileId:employee.id,enabled:!enabled,reason:enabled?"Habilitation retirée par l’administrateur.":"Responsable de secteur autorisé à décider du déclenchement d’une urgence."})}>{enabled?"Retirer l’habilitation":"Habiliter"}</button></article>;})}{!employeeProfiles.length&&<p className="empty">Aucun salarié actif.</p>}</div></details>
      <div className="emergency-list">{emergencies.length ? emergencies.map((item) => {
        const origin = originLabel(item);
        const overdue = item.status === "reported" && new Date(item.acknowledgement_due_at).getTime() < Date.now();
        const intervention=emergencyInterventions.find((entry)=>entry.emergency_id===item.id);
        return <article className={`emergency-row severity-${item.severity}`} key={item.id}><div><span className="eyebrow">{emergencySeverityLabels[item.severity]} · {emergencyKindLabels[item.emergency_kind || "general"] || item.emergency_kind} · {labels[item.status] || item.status}</span><h3>{item.title}</h3><p>{item.description}</p><small>{origin} · {frDate(item.occurred_at, true)} · responsable actuel : {profileName(item.current_assignee_profile_id || item.n1_profile_id)} (N{item.current_escalation_level || 1}) · N2 : {profileName(item.n2_profile_id)}</small>{item.intervention_commitment&&<small>Engagement d’intervention : <strong>{item.intervention_commitment}</strong>{item.intervention_target_at?` · avant le ${frDate(item.intervention_target_at,true)}`:""}</small>}{item.emergency_billing_status&&item.emergency_billing_status!=="not_assessed"&&<small className="emergency-billing-state">{emergencyBillingLabels[item.emergency_billing_status]||item.emergency_billing_status}</small>}</div><div className={`emergency-timing${overdue ? " overdue" : ""}`}><span>{overdue ? "Prise en charge en retard" : "Prise en charge attendue"}</span><strong>{frDate(item.acknowledgement_due_at, true)}</strong>{item.resolved_at && <small>Résolue le {frDate(item.resolved_at, true)}</small>}</div><div className="row-actions"><button type="button" className="secondary" onClick={()=>setSelectedEmergencyId(item.id)}>Ouvrir la fiche</button>{item.status === "reported" && <button type="button" disabled={busy} onClick={() => void acknowledge(item)}>Prendre en charge N{item.current_escalation_level || 1}</button>}{item.current_escalation_level !== 2 && ["reported","acknowledged_n1"].includes(item.status) && <button type="button" className="secondary" onClick={() => openAction(item, "escalate")}>Demander le renfort N2</button>}{!intervention&&["acknowledged_n1","escalated_n2"].includes(item.status) && <button type="button" onClick={() => openAction(item, "resolve")}>Résoudre</button>}{!intervention&&item.reported_origin==="client_relay"&&item.emergency_billing_status==="eligible_for_review"&&<><button type="button" className="secondary" onClick={()=>setBillingEditor({emergency:item,decision:"waive",note:""})}>Ne pas facturer l’urgence</button><button type="button" onClick={()=>setBillingEditor({emergency:item,decision:"request_draft",note:""})}>Préparer la facturation</button></>}{!intervention&&item.reported_origin==="client_relay"&&item.emergency_billing_status==="draft_requested"&&<button type="button" className="secondary" onClick={()=>setBillingEditor({emergency:item,decision:"waive",note:""})}>Écarter avant facturation</button>}{intervention?.status==="completed"&&intervention.admin_review_status!=="validated"&&<button type="button" onClick={()=>openEmergencyReview(intervention,item)}>Contrôler et décider</button>}</div></article>;
      }) : <p className="empty">Aucune urgence déclarée.</p>}</div>
    </>}
    {message && <Notice tone={/enregistrée|déclarée|horodatée|escaladée|résolue|Préparation|Facturation/.test(message) ? "success" : "error"}>{message}</Notice>}
    {reportEditor && <div className="portal-overlay"><form className="portal-detail emergency-dialog" role="dialog" aria-modal="true" aria-label="Déclarer une urgence" onSubmit={reportEmergency}><div className="portal-detail-head"><div><span className="eyebrow">Traçabilité opérationnelle</span><h2>Déclarer une urgence</h2></div><button type="button" className="icon-close" onClick={() => setReportEditor(null)}>×</button></div><div className="form-grid"><label className="wide">Client concerné<select value={reportEditor.clientId} onChange={(event)=>{const client=data.clients.find((item)=>item.id===event.target.value);setReportEditor({...reportEditor,clientId:event.target.value,propertyId:"",missionId:"",siteAddress:client?.address||"",siteContactName:[client?.contact_first_name,client?.contact_last_name].filter(Boolean).join(" "),siteContactPhone:client?.contact_phone||"",accessInstructions:""});}}><option value="">Urgence interne / aucun client</option>{data.clients.map((client)=><option key={client.id} value={client.id}>{client.name}</option>)}</select></label><label className="wide">Bien concerné<select value={reportEditor.propertyId} disabled={!reportEditor.clientId} onChange={(event)=>{const property=selectedClientProperties.find((item)=>item.id===event.target.value),mission=selectedClientMissions.find((item)=>item.id===reportEditor.missionId);setReportEditor({...reportEditor,propertyId:event.target.value,missionId:mission?.property_id&&mission.property_id!==event.target.value?"":reportEditor.missionId,siteAddress:property?.address||reportEditor.siteAddress,siteContactName:property?.contact_name||reportEditor.siteContactName,accessInstructions:property?.access_notes||reportEditor.accessInstructions});}}><option value="">Autre adresse / aucun bien enregistré</option>{selectedClientProperties.map((property)=><option key={property.id} value={property.id}>{property.name} · {property.address}</option>)}</select></label><label className="wide">Mission concernée<select value={reportEditor.missionId} disabled={!reportEditor.clientId} onChange={(event)=>{const mission=selectedClientMissions.find((item)=>item.id===event.target.value),property=data.properties.find((item)=>item.id===mission?.property_id);setReportEditor({...reportEditor,missionId:event.target.value,propertyId:property?.id||reportEditor.propertyId,siteAddress:property?.address||mission?.location||reportEditor.siteAddress,siteContactName:property?.contact_name||reportEditor.siteContactName,accessInstructions:property?.access_notes||reportEditor.accessInstructions});}}><option value="">Aucune mission précise</option>{selectedClientMissions.filter((mission)=>!reportEditor.propertyId||!mission.property_id||mission.property_id===reportEditor.propertyId).map((mission)=><option key={mission.id} value={mission.id}>{frDate(mission.starts_at,true)} · {mission.location}</option>)}</select></label><label className="wide">Titre<input required minLength={3} value={reportEditor.title} onChange={(event) => setReportEditor({...reportEditor,title:event.target.value})} /></label><label>Catégorie<select value={reportEditor.emergencyKind} onChange={(event) => setReportEditor({...reportEditor,emergencyKind:event.target.value})}>{Object.entries(emergencyKindLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>Gravité<select value={reportEditor.severity} onChange={(event) => setReportEditor({...reportEditor,severity:event.target.value as OperationalEmergency["severity"]})}>{Object.entries(emergencySeverityLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>Date et heure<input required type="datetime-local" value={reportEditor.occurredAt} onChange={(event) => setReportEditor({...reportEditor,occurredAt:event.target.value})} /></label><label className="wide">Adresse précise<input required minLength={3} value={reportEditor.siteAddress} onChange={(event)=>setReportEditor({...reportEditor,siteAddress:event.target.value})}/></label><label>Contact sur place<input value={reportEditor.siteContactName} onChange={(event)=>setReportEditor({...reportEditor,siteContactName:event.target.value})}/></label><label>Téléphone du contact<input type="tel" value={reportEditor.siteContactPhone} onChange={(event)=>setReportEditor({...reportEditor,siteContactPhone:event.target.value})}/></label><label className="wide">Description<textarea required minLength={5} rows={5} value={reportEditor.description} onChange={(event) => setReportEditor({...reportEditor,description:event.target.value})} /></label><label className="wide">Ce qui est attendu<textarea required minLength={3} rows={3} value={reportEditor.expectedAction} onChange={(event)=>setReportEditor({...reportEditor,expectedAction:event.target.value})}/></label><label className="wide">Accès au site<textarea rows={2} value={reportEditor.accessInstructions} onChange={(event)=>setReportEditor({...reportEditor,accessInstructions:event.target.value})}/></label><label className="wide">Matériel à prévoir<textarea rows={2} value={reportEditor.equipmentNotes} onChange={(event)=>setReportEditor({...reportEditor,equipmentNotes:event.target.value})}/></label><label className="wide">Cause récurrente connue (facultatif)<input value={reportEditor.recurrenceKey} onChange={(event) => setReportEditor({...reportEditor,recurrenceKey:event.target.value})} placeholder="Ex. accès, fuite, absence" /></label></div><Notice>Le choix d’une mission renseigne automatiquement son client, son bien et l’adresse la plus précise disponible. La catégorie et la gravité appliquent ensuite les règles N1/N2.</Notice><div className="detail-actions"><button type="button" className="secondary" onClick={() => setReportEditor(null)}>Annuler</button><button disabled={busy}>{busy ? "Déclaration…" : "Déclarer et alerter l’astreinte"}</button></div></form></div>}
    {selectedEmergency&&<div className="portal-overlay"><section className="portal-detail emergency-dialog emergency-case-dialog" role="dialog" aria-modal="true" aria-label="Fiche de suivi de l’urgence"><div className="portal-detail-head"><div><span className="eyebrow">Suivi administrateur en temps réel</span><h2>{selectedEmergency.title}</h2></div><button type="button" className="icon-close" onClick={()=>setSelectedEmergencyId(null)}>×</button></div><div className="emergency-case-grid"><article><span>État</span><strong>{labels[selectedEmergency.status]||selectedEmergency.status}</strong><small>{emergencySeverityLabels[selectedEmergency.severity]} · {frDate(selectedEmergency.occurred_at,true)}</small></article><article><span>Lieu</span><strong>{selectedEmergency.site_address||"À confirmer"}</strong><small>{selectedEmergency.site_contact_name||"Aucun contact sur place"}</small></article><article><span>Intervenant</span><strong>{profileName(selectedIntervention?.assignee_profile_id||selectedEmergency.current_assignee_profile_id)}</strong><small>{selectedIntervention?.actual_minutes?`${selectedIntervention.actual_minutes} min réelles`:`N${selectedEmergency.current_escalation_level||1}`}</small></article><article><span>Contrôle</span><strong>{selectedIntervention?.admin_review_status==="validated"?"Validé":selectedIntervention?.admin_review_status==="changes_requested"?"Corrections demandées":"À contrôler"}</strong><small>{selectedIntervention?.client_report_visible?"Rapport partagé au client":"Rapport interne"}</small></article></div><div className="emergency-case-body"><article className="emergency-case-brief"><h3>Brief d’intervention</h3><p>{selectedEmergency.description}</p>{selectedEmergency.expected_action&&<p><strong>Attendu :</strong> {selectedEmergency.expected_action}</p>}{selectedEmergency.access_instructions&&<p><strong>Accès :</strong> {selectedEmergency.access_instructions}</p>}{selectedEmergency.equipment_notes&&<p><strong>Matériel :</strong> {selectedEmergency.equipment_notes}</p>}</article><article><h3>Progression terrain</h3>{selectedIntervention?<><div className="emergency-journey-status"><span className={selectedIntervention.departed_at?"done":""}>Départ</span><span className={selectedIntervention.arrived_at?"done":""}>Arrivée</span><span className={selectedIntervention.site_departed_at?"done":""}>Départ du site</span><span className={selectedIntervention.return_arrived_at?"done":""}>Retour</span></div><ol className="emergency-case-steps">{selectedTasks.map((task)=>{const photoCount=selectedEvidence.filter((item)=>item.task_id===task.id).length;return <li key={task.id} className={task.completed_at?"done":""}><strong>{task.label}</strong><span>{task.completed_at?"Validée":"En attente"}{task.minimum_photo_count?` · ${photoCount}/${task.minimum_photo_count} photo(s)`:""}</span>{task.notes&&<p>{task.notes}</p>}</li>;})}</ol></>:<p>Aucun départ terrain enregistré.</p>}</article>{selectedIntervention?.action_summary&&<article><h3>Compte rendu</h3><p><strong>Actions réalisées :</strong> {selectedIntervention.action_summary}</p><p><strong>Cause :</strong> {selectedEmergency.root_cause||"À confirmer"}</p><p><strong>Reste à faire :</strong> {selectedIntervention.remaining_actions||"Rien de signalé"}</p></article>}{selectedSnapshot&&<article><h3>Lecture commerciale réservée à l’administration</h3><p>{selectedSnapshot.client_billable_hours} h client · {money(selectedSnapshot.client_hourly_rate_ex_vat)} / h · <strong>{money(selectedSnapshot.calculated_client_amount_ex_vat)} HT</strong></p><small>Temps salarié : {selectedIntervention?.employee_payable_minutes||0} minutes réelles. Le client est arrondi selon la règle contractuelle.</small></article>}<article><h3>Historique</h3><ul className="emergency-case-events">{selectedEvents.slice().reverse().map((event)=><li key={event.id}><strong>{frDate(event.created_at,true)}</strong><span>{event.note||event.event_type}</span></li>)}</ul></article></div><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setSelectedEmergencyId(null)}>Fermer</button>{selectedIntervention?.status==="completed"&&selectedIntervention.admin_review_status!=="validated"&&<button type="button" onClick={()=>openEmergencyReview(selectedIntervention,selectedEmergency)}>Contrôler et décider</button>}</div></section></div>}
    {reviewEditor&&<div className="portal-overlay"><form className="portal-detail emergency-dialog" role="dialog" aria-modal="true" aria-label="Valider le compte rendu d’urgence" onSubmit={submitEmergencyReview}><div className="portal-detail-head"><div><span className="eyebrow">Décision humaine obligatoire</span><h2>Valider le compte rendu</h2></div><button type="button" className="icon-close" onClick={()=>setReviewEditor(null)}>×</button></div><div className="form-grid"><label className="wide">Décision<select value={reviewEditor.approve?"approve":"changes"} onChange={(event)=>setReviewEditor({...reviewEditor,approve:event.target.value==="approve",billable:event.target.value==="approve"?reviewEditor.billable:false,shareReport:event.target.value==="approve"?reviewEditor.shareReport:false})}><option value="approve">Valider le compte rendu</option><option value="changes">Demander des corrections</option></select></label>{reviewEditor.approve&&<><label className="wide">Client concerné<select value={reviewEditor.clientId} onChange={(event)=>setReviewEditor({...reviewEditor,clientId:event.target.value})}><option value="">Urgence interne / aucun client</option>{data.clients.filter((client)=>client.active).map((client)=><option key={client.id} value={client.id}>{client.name}</option>)}</select></label><label className="checkline wide"><input type="checkbox" checked={reviewEditor.shareReport} disabled={!reviewEditor.clientId} onChange={(event)=>setReviewEditor({...reviewEditor,shareReport:event.target.checked})}/> Rendre le rapport visible au client confirmé</label><label className="checkline wide"><input type="checkbox" checked={reviewEditor.billable} disabled={!reviewEditor.clientId} onChange={(event)=>setReviewEditor({...reviewEditor,billable:event.target.checked})}/> Intervention facturable au client</label>{reviewEditor.billable&&<label className="wide">Choix de facturation<select value={reviewEditor.billingMode} onChange={(event)=>setReviewEditor({...reviewEditor,billingMode:event.target.value as "monthly_grouped"|"standalone"})}><option value="monthly_grouped">Ajouter une ligne au brouillon mensuel en cours</option><option value="standalone">Créer une facture indépendante en brouillon</option></select><small>Dans les deux cas, aucun envoi Henrri n’est déclenché.</small></label>}</>}<label className="wide">Commentaire de contrôle<textarea required minLength={5} rows={4} value={reviewEditor.note} onChange={(event)=>setReviewEditor({...reviewEditor,note:event.target.value})}/></label></div><Notice>{reviewEditor.billable?reviewEditor.billingMode==="monthly_grouped"?"La ligne sera ajoutée au brouillon groupé du client pour le mois en cours.":"Une facture individuelle séparée sera créée au statut brouillon.":"La décision sera tracée sans créer de ligne de facture."}</Notice><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setReviewEditor(null)}>Annuler</button><button disabled={busy}>{busy?"Validation…":reviewEditor.approve?"Valider la décision":"Demander les corrections"}</button></div></form></div>}
    {authorizationEditor&&<div className="portal-overlay"><form className="portal-detail emergency-dialog" role="dialog" aria-modal="true" aria-label="Gérer l’habilitation de déclaration" onSubmit={saveReporterAuthorization}><div className="portal-detail-head"><div><span className="eyebrow">Droit sensible</span><h2>{authorizationEditor.enabled?"Habiliter ce responsable":"Retirer l’habilitation"}</h2></div><button type="button" className="icon-close" onClick={()=>setAuthorizationEditor(null)}>×</button></div><p>Cette autorisation permet au salarié de décider qu’un incident doit réellement déclencher l’astreinte. Elle ne découle jamais automatiquement de son planning d’astreinte.</p><div className="form-grid"><label className="wide">Motif de la décision<textarea required minLength={5} rows={4} value={authorizationEditor.reason} onChange={(event)=>setAuthorizationEditor({...authorizationEditor,reason:event.target.value})}/></label></div><Notice>Les sous-traitants restent exclus de cette habilitation côté serveur.</Notice><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setAuthorizationEditor(null)}>Annuler</button><button disabled={busy}>{busy?"Enregistrement…":authorizationEditor.enabled?"Confirmer l’habilitation":"Confirmer le retrait"}</button></div></form></div>}
    {billingEditor&&<div className="portal-overlay"><form className="portal-detail emergency-dialog" role="dialog" aria-modal="true" aria-label="Décider la facturation d’urgence" onSubmit={submitBillingDecision}><div className="portal-detail-head"><div><span className="eyebrow">Décision humaine · {billingEditor.emergency.title}</span><h2>{billingEditor.decision==="request_draft"?"Préparer la facturation d’urgence":"Écarter la facturation d’urgence"}</h2></div><button type="button" className="icon-close" onClick={()=>setBillingEditor(null)}>×</button></div><p>{billingEditor.decision==="request_draft"?"Cette action ouvre la préparation de facturation. Le tarif contractuel doit encore être contrôlé avant création ou envoi dans Henrri.":"Cette action conserve la demande et l’intervention, mais indique qu’aucune facturation d’urgence ne doit être préparée."}</p><div className="form-grid"><label className="wide">Motif de la décision<textarea required minLength={5} rows={4} value={billingEditor.note} onChange={(event)=>setBillingEditor({...billingEditor,note:event.target.value})}/></label></div><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setBillingEditor(null)}>Annuler</button><button disabled={busy}>{busy?"Enregistrement…":"Confirmer la décision"}</button></div></form></div>}
    {actionEditor && <div className="portal-overlay"><form className="portal-detail emergency-dialog" role="dialog" aria-modal="true" aria-label={actionEditor.kind === "escalate" ? "Escalader l’urgence au N2" : "Résoudre l’urgence"} onSubmit={completeAction}><div className="portal-detail-head"><div><span className="eyebrow">{actionEditor.emergency.title}</span><h2>{actionEditor.kind === "escalate" ? "Escalader au N2" : "Résoudre et capitaliser"}</h2></div><button type="button" className="icon-close" onClick={() => setActionEditor(null)}>×</button></div><div className="form-grid"><label className="wide">{actionEditor.kind === "escalate" ? "Motif de l’escalade" : "Résolution appliquée"}<textarea required minLength={5} rows={4} value={actionEditor.note} onChange={(event) => setActionEditor({...actionEditor,note:event.target.value})} /></label>{actionEditor.kind === "resolve" && <><label className="wide">Cause racine<input required minLength={3} value={actionEditor.rootCause} onChange={(event) => setActionEditor({...actionEditor,rootCause:event.target.value})} /></label><label>Clé de récidive<input value={actionEditor.recurrenceKey} onChange={(event) => setActionEditor({...actionEditor,recurrenceKey:event.target.value})} /></label><label>Revue suivante<input type="date" value={actionEditor.nextReviewOn} onChange={(event) => setActionEditor({...actionEditor,nextReviewOn:event.target.value})} /></label></>}</div><div className="detail-actions"><button type="button" className="secondary" onClick={() => setActionEditor(null)}>Annuler</button><button disabled={busy}>{busy ? "Enregistrement…" : actionEditor.kind === "escalate" ? "Confirmer l’escalade N2" : "Confirmer la résolution"}</button></div></form></div>}
  </section>;
}

function AdminSettings({
  profile,
  data,
  reload,
  onManageGuidedQuote,
  appearance,
  onAppearanceChange,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  onManageGuidedQuote: (catalogItemId: string | "new") => void;
  appearance: AppearanceMode;
  onAppearanceChange: (value: AppearanceMode) => void;
}) {
  const org = data.organization,
    [form, setForm] = useState<Organization>({
      ...(org || ({ id: profile.organization_id, name: "" } as Organization)),
      dashboard_preferences:
        profile.dashboard_preferences || org?.dashboard_preferences || {},
    }),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [scoringStrategy, setScoringStrategy] = useState("balanced"),
    [reviewSettings, setReviewSettings] = useState({
      enabled: false,
      google_review_url: "",
      cooldown_days: 90,
      request_delay_days: 0,
      four_month_goal: 15,
    }),
    [reviewSettingsMessage, setReviewSettingsMessage] = useState(""),
    [reviewSettingsBusy, setReviewSettingsBusy] = useState(false),
    [reviewSettingsAvailable, setReviewSettingsAvailable] = useState(true),
    [settingsSearch, setSettingsSearch] = useState(""),
    [activeSettingsSection, setActiveSettingsSection] = useState("");
  useEffect(() => {
    if (org)
      setForm({
        ...org,
        dashboard_preferences:
          profile.dashboard_preferences || org.dashboard_preferences || {},
      });
  }, [org, profile.dashboard_preferences]);
  useEffect(() => {
    let active = true;
    void supabase!
      .from("organization_review_settings")
      .select("enabled,google_review_url,cooldown_days,request_delay_days,four_month_goal")
      .eq("organization_id", profile.organization_id)
      .maybeSingle()
      .then(({ data: settings, error }) => {
        if (!active) return;
        if (error && missingDeploymentObject(error.message)) {
          setReviewSettingsAvailable(false);
          return;
        }
        if (settings) {
          setReviewSettings({
            enabled: Boolean(settings.enabled),
            google_review_url: settings.google_review_url || "",
            cooldown_days: Number(settings.cooldown_days || 90),
            request_delay_days: Number(settings.request_delay_days || 0),
            four_month_goal: Number(settings.four_month_goal || 15),
          });
        }
      });
    return () => { active = false; };
  }, [profile.organization_id]);
  const widgets: [DashboardWidgetKey, string][] = [
    ["monthly_revenue", "CA mensuel"],
    ["monthly_profit", "Bénéfice provisoire mensuel"],
    ["monthly_costs", "Coûts mensuels"],
    ["annual_revenue", "CA annuel"],
    ["annual_profit", "Bénéfice provisoire annuel"],
    ["annual_costs", "Coûts annuels"],
    ["collected", "Encaissements"],
    ["bank_balance", "Solde bancaire"],
  ];
  const defaultWidgetOrder = widgets.map(([key]) => key),
    widgetOrder = [
      ...(form.dashboard_preferences?.order || []).filter((key) =>
        defaultWidgetOrder.includes(key),
      ),
      ...defaultWidgetOrder.filter(
        (key) => !(form.dashboard_preferences?.order || []).includes(key),
      ),
    ];
  function toggle(key: DashboardWidgetKey) {
    setForm((v) => ({
      ...v,
      dashboard_preferences: {
        ...v.dashboard_preferences,
        [key]: v.dashboard_preferences?.[key] === false,
      },
    }));
  }
  function moveWidget(key: DashboardWidgetKey, direction: -1 | 1) {
    const index = widgetOrder.indexOf(key),
      target = index + direction;
    if (index < 0 || target < 0 || target >= widgetOrder.length) return;
    const next = [...widgetOrder];
    [next[index], next[target]] = [next[target], next[index]];
    setForm((value) => ({
      ...value,
      dashboard_preferences: {
        ...(value.dashboard_preferences || {}),
        order: next,
      },
    }));
  }
  function updateScoring(key: keyof ScoringPreferences, value: number) {
    setForm((current) => ({
      ...current,
      scoring_preferences: {
        ...defaultScoring,
        ...(current.scoring_preferences || {}),
        [key]: value,
      },
    }));
  }
  function applyScoringStrategy(value: string) {
    setScoringStrategy(value);
    const current = {
        ...defaultScoring,
        ...(form.scoring_preferences || {}),
      },
      presets: Record<string, Partial<ScoringPreferences>> = {
        balanced: {
          margin_weight: 30,
          revenue_weight: 15,
          payment_weight: 15,
          recurrence_weight: 10,
          travel_weight: 10,
          admin_weight: 10,
          incident_weight: 10,
        },
        profitability: {
          margin_weight: 40,
          revenue_weight: 15,
          payment_weight: 15,
          recurrence_weight: 5,
          travel_weight: 10,
          admin_weight: 10,
          incident_weight: 5,
        },
        growth: {
          margin_weight: 25,
          revenue_weight: 30,
          payment_weight: 10,
          recurrence_weight: 15,
          travel_weight: 5,
          admin_weight: 5,
          incident_weight: 10,
        },
        reliability: {
          margin_weight: 25,
          revenue_weight: 10,
          payment_weight: 25,
          recurrence_weight: 15,
          travel_weight: 5,
          admin_weight: 10,
          incident_weight: 10,
        },
        simplicity: {
          margin_weight: 25,
          revenue_weight: 10,
          payment_weight: 15,
          recurrence_weight: 5,
          travel_weight: 20,
          admin_weight: 20,
          incident_weight: 5,
        },
      };
    setForm((organization) => ({
      ...organization,
      scoring_preferences: { ...current, ...(presets[value] || {}) },
    }));
  }
  const settingsSections = [
    {
      id: "settings-display",
      label: "Affichage et démonstration",
      description: "Thème clair ou sombre, présentation commerciale et imports historiques.",
      keywords: "thème sombre clair showcase démonstration import csv historique",
    },
    {
      id: "settings-business",
      label: "Règles de gestion",
      description: "TVA, marges, facturation, paiements, alertes et centre de contrôle.",
      keywords: "tva marge facture paiement relance stock tableau de bord avis google",
    },
    {
      id: "settings-contracts",
      label: "Contrats et documents",
      description: "Modèles clients, salariés et sous-traitants, versions et documents associés.",
      keywords: "contrat avenant modèle document client employé salarié sous-traitant st",
    },
    {
      id: "settings-access",
      label: "Accès et comptes",
      description: "Adresses de connexion, invitations, suspensions, rôles et accès Coach.",
      keywords: "email mail compte connexion invitation suspendre réactiver rôle utilisateur coach",
    },
    {
      id: "settings-processes",
      label: "Processus métier",
      description: "Procédures, automatisations, checklists, devis guidés et urgences.",
      keywords: "processus automatisation procédure checklist devis guidé urgence astreinte",
    },
    {
      id: "settings-connections",
      label: "Communications et connexions",
      description: "E-mails, signatures, Henrri, calendriers et autres intégrations.",
      keywords: "communication email signature henrri calendrier intégration fournisseur",
    },
    {
      id: "settings-security",
      label: "Sécurité et fiabilité",
      description: "Confidentialité, supervision, sauvegardes et accès support temporaires.",
      keywords: "sécurité confidentialité supervision sauvegarde pra support technique",
    },
  ];
  const normalizedSettingsSearch = settingsSearch.trim().toLocaleLowerCase("fr-FR"),
    matchingSettingsSections = normalizedSettingsSearch
      ? settingsSections.filter((section) =>
          `${section.label} ${section.description} ${section.keywords}`
            .toLocaleLowerCase("fr-FR")
            .includes(normalizedSettingsSearch),
        )
      : settingsSections;
  function openSettingsSection(id: string) {
    setActiveSettingsSection(id);
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const organizationPayload: Record<string, unknown> = {
      default_vat_rate: form.default_vat_rate,
      target_margin_rate: form.target_margin_rate,
      fiscal_year_start_month: form.fiscal_year_start_month,
      route_buffer_minutes: form.route_buffer_minutes,
      mileage_return_to_headquarters:
        form.mileage_return_to_headquarters === true,
      default_invoice_due_days: form.default_invoice_due_days,
      default_payment_terms_mode:
        form.default_payment_terms_mode || "days_after_issue",
      invoice_reminder_days_after_due:
        form.invoice_reminder_days_after_due,
      document_alert_days: form.document_alert_days,
      contract_alert_days: form.contract_alert_days,
      low_stock_alerts_enabled: form.low_stock_alerts_enabled,
      default_billing_emission: form.default_billing_emission,
      default_billing_grouping: form.default_billing_grouping,
      use_preferred_workers: form.use_preferred_workers,
      prioritize_available_employees:
        form.prioritize_available_employees,
      scoring_preferences: form.scoring_preferences || defaultScoring,
    };
    if (form.profitability_target_mode !== undefined) {
      organizationPayload.profitability_target_mode =
        form.profitability_target_mode || "margin";
      organizationPayload.profitability_target_rate =
        form.profitability_target_rate ?? 30;
    }
    let paymentTermsDeferred = false;
    let organizationUpdate = await supabase!
      .from("organizations")
      .update(organizationPayload)
      .eq("id", profile.organization_id);
    if (
      organizationUpdate.error &&
      /default_payment_terms_mode|schema cache|column/i.test(
        organizationUpdate.error.message,
      )
    ) {
      const { default_payment_terms_mode: _paymentTermsMode, ...legacyPayload } =
        organizationPayload;
      organizationUpdate = await supabase!
        .from("organizations")
        .update(legacyPayload)
        .eq("id", profile.organization_id);
      paymentTermsDeferred = !organizationUpdate.error;
    }
    const profileUpdate = organizationUpdate.error
      ? null
      : await supabase!
          .from("profiles")
          .update({ dashboard_preferences: form.dashboard_preferences || {} })
          .eq("id", profile.id);
    setBusy(false);
    const error = organizationUpdate.error || profileUpdate?.error;
    setMessage(
      error
        ? error.message
        : paymentTermsDeferred
          ? "Réglages enregistrés. Le mode de paiement sera disponible après activation de la migration préparée."
          : "Réglages enregistrés.",
    );
    if (!error) reload();
  }
  async function saveReviewSettings(e: React.FormEvent) {
    e.preventDefault();
    setReviewSettingsBusy(true);
    setReviewSettingsMessage("");
    const { error } = await supabase!.rpc("save_google_review_settings", {
      p_enabled: reviewSettings.enabled,
      p_google_review_url: reviewSettings.google_review_url,
      p_cooldown_days: reviewSettings.cooldown_days,
      p_request_delay_days: reviewSettings.request_delay_days,
      p_four_month_goal: reviewSettings.four_month_goal,
    });
    setReviewSettingsBusy(false);
    if (error && missingDeploymentObject(error.message)) {
      setReviewSettingsAvailable(false);
      setReviewSettingsMessage("En cours de développement sur cet environnement.");
      return;
    }
    setReviewSettingsMessage(
      error
        ? error.message
        : "Collecte d’avis enregistrée. Elle restera neutre et limitée selon cette fréquence.",
    );
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Configuration</span>
          <h1>Réglages</h1>
          <p>Choisissez un sujet : les réglages techniques restent rangés et fermés tant que vous n’en avez pas besoin.</p>
        </div>
      </div>
      <section className="panel settings-index" data-testid="settings-index">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Accès rapide</span>
            <h2>Que voulez-vous régler ?</h2>
            <p>Recherchez avec vos mots. Chaque chapitre explique son utilité avant d’afficher ses outils.</p>
          </div>
        </div>
        <label className="settings-search">
          Rechercher dans les réglages
          <input
            type="search"
            value={settingsSearch}
            onChange={(event) => setSettingsSearch(event.target.value)}
            placeholder="Ex. contrat, e-mail de connexion, Henrri, marge…"
          />
        </label>
        <div className="settings-index-grid">
          {matchingSettingsSections.map((section) => (
            <button
              type="button"
              key={section.id}
              className={activeSettingsSection === section.id ? "active" : ""}
              aria-pressed={activeSettingsSection === section.id}
              onClick={() => openSettingsSection(section.id)}
            >
              <strong>{section.label}</strong>
              <span>{section.description}</span>
              <small>Ouvrir ce chapitre →</small>
            </button>
          ))}
          {!matchingSettingsSections.length && (
            <p className="empty">Aucun chapitre ne correspond. Essayez un mot plus simple comme « contrat », « accès » ou « facture ».</p>
          )}
        </div>
      </section>
      {activeSettingsSection && (
        <div className="settings-current-topic" role="status">
          <span>
            Sujet affiché : <strong>{settingsSections.find((section) => section.id === activeSettingsSection)?.label}</strong>
          </span>
          <button type="button" className="secondary compact" onClick={() => setActiveSettingsSection("")}>Fermer ce sujet</button>
        </div>
      )}
      <details id="settings-display" className="settings-chapter" hidden={activeSettingsSection !== "settings-display"} open={activeSettingsSection === "settings-display"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Affichage et démonstration</strong><small>Thème, présentation commerciale et reprise de l’historique</small></span>
        </summary>
        <div className="settings-chapter-body">
          <AppearanceSettings value={appearance} onChange={onAppearanceChange} />
          <DemoPresentationLauncher />
          <AdminHistoricalImport reload={reload} />
        </div>
      </details>
      <details id="settings-business" className="settings-chapter" hidden={activeSettingsSection !== "settings-business"} open={activeSettingsSection === "settings-business"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Règles de gestion</strong><small>Tableau de bord, avis Google, TVA, marges, facturation et échéances</small></span>
        </summary>
        <div className="settings-chapter-body">
      <section className="panel">
        <h2>Centre de contrôle</h2>
        <p className="section-intro">
          Chaque administrateur choisit les indicateurs visibles et leur ordre
          sans modifier le centre de contrôle de ses collègues.
        </p>
        <div className="widget-checks widget-order-list">
          {widgetOrder.map((key, index) => {
            const label = widgets.find(([item]) => item === key)?.[1] || key;
            return (
              <div key={key} className="widget-order-row">
                <label>
                  <input
                    type="checkbox"
                    checked={form.dashboard_preferences?.[key] !== false}
                    onChange={() => toggle(key)}
                  />
                  {label}
                </label>
                <div className="row-actions">
                  <button
                    type="button"
                    className="secondary compact"
                    disabled={index === 0}
                    onClick={() => moveWidget(key, -1)}
                    aria-label={`Monter ${label}`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="secondary compact"
                    disabled={index === widgetOrder.length - 1}
                    onClick={() => moveWidget(key, 1)}
                    aria-label={`Descendre ${label}`}
                  >
                    ↓
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="panel">
        <h2>Avis Google après mission contrôlée</h2>
        <p className="section-intro">
          ARD peut proposer le lien public Google après validation administrative d’une mission.
          L’invitation est facultative, identique pour tous les clients et ne dépend jamais de leur satisfaction.
        </p>
        {!reviewSettingsAvailable ? (
          <Notice tone="info">En cours de développement sur cet environnement.</Notice>
        ) : (
          <form className="form-grid" onSubmit={saveReviewSettings}>
            <label className="check-line">
              <input
                type="checkbox"
                checked={reviewSettings.enabled}
                onChange={(event) => setReviewSettings((value) => ({ ...value, enabled: event.target.checked }))}
              />
              Activer l’invitation après validation d’une mission
            </label>
            <label className="span-2">
              Lien public « Donner un avis » de Google Business Profile
              <input
                type="url"
                placeholder="https://g.page/r/.../review"
                value={reviewSettings.google_review_url}
                onChange={(event) => setReviewSettings((value) => ({ ...value, google_review_url: event.target.value }))}
                required={reviewSettings.enabled}
              />
              <small>Copiez ici le lien public fourni par votre fiche d’établissement Google.</small>
            </label>
            <label>
              Délai avant affichage (jours)
              <input
                type="number" min="0" max="30"
                value={reviewSettings.request_delay_days}
                onChange={(event) => setReviewSettings((value) => ({ ...value, request_delay_days: Number(event.target.value) }))}
              />
            </label>
            <label>
              Nouvelle invitation au même client après (jours)
              <input
                type="number" min="30" max="365"
                value={reviewSettings.cooldown_days}
                onChange={(event) => setReviewSettings((value) => ({ ...value, cooldown_days: Number(event.target.value) }))}
              />
            </label>
            <label>
              Objectif sur 4 mois
              <input
                type="number" min="1" max="100"
                value={reviewSettings.four_month_goal}
                onChange={(event) => setReviewSettings((value) => ({ ...value, four_month_goal: Number(event.target.value) }))}
              />
            </label>
            {reviewSettings.google_review_url && (
              <div className="span-2">
                <ReviewQrCode
                  url={reviewSettings.google_review_url}
                  label="Aperçu du QR code à remettre aux clients"
                  downloadable
                />
              </div>
            )}
            <div className="form-actions span-2">
              <button type="submit" disabled={reviewSettingsBusy}>
                {reviewSettingsBusy ? "Enregistrement…" : "Enregistrer la collecte d’avis"}
              </button>
            </div>
            {reviewSettingsMessage && <Notice tone={reviewSettingsMessage.startsWith("Collecte") ? "success" : "error"}>{reviewSettingsMessage}</Notice>}
          </form>
        )}
      </section>
      <section className="panel">
        <h2>Règles de gestion</h2>
        <form className="form-grid" onSubmit={save}>
          <label>
            TVA par défaut (%)
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.default_vat_rate ?? 20}
              onChange={(e) =>
                setForm((v) => ({ ...v, default_vat_rate: +e.target.value }))
              }
            />
          </label>
          <label>
            Marge cible (%)
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.target_margin_rate ?? 30}
              onChange={(e) =>
                setForm((v) => ({ ...v, target_margin_rate: +e.target.value }))
              }
            />
          </label>
          {form.profitability_target_mode !== undefined && (
            <>
              <label>
                Calcul des propositions ST
                <select
                  value={form.profitability_target_mode || "margin"}
                  onChange={(event) =>
                    setForm((value) => ({
                      ...value,
                      profitability_target_mode: event.target.value as "margin" | "markup",
                    }))
                  }
                >
                  <option value="margin">Marge sur le prix de vente</option>
                  <option value="markup">Majoration du coût</option>
                </select>
              </label>
              <label>
                Objectif propositions ST (%)
                <input
                  type="number"
                  min="0"
                  max={form.profitability_target_mode === "margin" ? 99.99 : 999.99}
                  step="0.1"
                  value={form.profitability_target_rate ?? 30}
                  onChange={(event) =>
                    setForm((value) => ({
                      ...value,
                      profitability_target_rate: Number(event.target.value),
                    }))
                  }
                />
                <small>
                  La marge compare le bénéfice au prix vendu ; la majoration compare
                  le bénéfice au coût du sous-traitant.
                </small>
              </label>
            </>
          )}
          <label>
            Mois de début d’exercice
            <select
              value={form.fiscal_year_start_month ?? 1}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  fiscal_year_start_month: +e.target.value,
                }))
              }
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2026, i, 1).toLocaleDateString("fr-FR", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>
          </label>
          <label>
            Marge entre deux missions (minutes)
            <input
              type="number"
              min="0"
              max="180"
              value={form.route_buffer_minutes ?? 15}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  route_buffer_minutes: +e.target.value,
                }))
              }
            />
          </label>
          <label className="inline-check wide">
            <input
              type="checkbox"
              checked={form.mileage_return_to_headquarters === true}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  mileage_return_to_headquarters: event.target.checked,
                }))
              }
            />
            Proposer par défaut le retour au siège — uniquement s’il est réellement effectué
          </label>
          <label>
            Facturation par défaut des nouveaux clients
            <select
              value={form.default_billing_emission ?? "month_end"}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  default_billing_emission: e.target
                    .value as Organization["default_billing_emission"],
                }))
              }
            >
              <option value="month_end">En fin de mois</option>
              <option value="immediate">Dès la mission terminée</option>
              <option value="manual">Décision manuelle</option>
            </select>
          </label>
          <label>
            Regroupement par défaut
            <select
              value={form.default_billing_grouping ?? "grouped"}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  default_billing_grouping: e.target
                    .value as Organization["default_billing_grouping"],
                }))
              }
            >
              <option value="grouped">Une facture regroupée</option>
              <option value="individual">Une facture par prestation</option>
            </select>
          </label>
          <label>
            Conditions de paiement par défaut
            <select
              value={form.default_payment_terms_mode ?? "days_after_issue"}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  default_payment_terms_mode: e.target.value as PaymentTermsMode,
                }))
              }
            >
              <option value="immediate">Paiement comptant</option>
              <option value="days_after_issue">Délai après émission</option>
              <option value="end_of_month">Fin de mois</option>
            </select>
          </label>
          {form.default_payment_terms_mode !== "immediate" &&
            form.default_payment_terms_mode !== "end_of_month" && (
          <label>
            Délai par défaut des factures (jours)
            <input
              type="number"
              min="0"
              max="365"
              value={form.default_invoice_due_days ?? 30}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  default_invoice_due_days: +e.target.value,
                }))
              }
            />
          </label>
          )}
          <label>
            Première relance après échéance (jours)
            <input
              type="number"
              min="0"
              max="90"
              value={form.invoice_reminder_days_after_due ?? 7}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  invoice_reminder_days_after_due: +e.target.value,
                }))
              }
            />
          </label>
          <label>
            Alerte avant échéance d’un document (jours)
            <input
              type="number"
              min="0"
              max="365"
              value={form.document_alert_days ?? 30}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  document_alert_days: +e.target.value,
                }))
              }
            />
          </label>
          <label>
            Alerte avant renouvellement d’un contrat (jours)
            <input
              type="number"
              min="0"
              max="365"
              value={form.contract_alert_days ?? 30}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  contract_alert_days: +e.target.value,
                }))
              }
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.low_stock_alerts_enabled !== false}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  low_stock_alerts_enabled: e.target.checked,
                }))
              }
            />
            Alerter lorsque le stock atteint son seuil minimum
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.prioritize_available_employees !== false}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  prioritize_available_employees: e.target.checked,
                }))
              }
            />
            Placer en priorité les employés disponibles avec des heures restantes
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.use_preferred_workers !== false}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  use_preferred_workers: e.target.checked,
                }))
              }
            />
            Utiliser ensuite les ST référents du client ou du bien
          </label>
          <Notice>
            Les IK ne reposent plus sur un prix fixe au kilomètre. Elles sont
            calculées automatiquement selon le barème fiscal officiel, le type
            de véhicule, sa puissance fiscale et le cumul annuel. Les véhicules
            et cartes grises se gèrent dans Équipe & RH.
          </Notice>
          <details className="wide scoring-settings">
            <summary>
              <strong>Configurer simplement le classement des clients</strong>
            </summary>
            <p className="section-intro">
              Le score sur 100 sert uniquement à comparer les clients.
              Choisissez ce qui compte le plus pour votre société ;
              l’application règle automatiquement l’importance de chaque
              critère.
            </p>
            <div className="form-grid">
              <label>
                Priorité de votre classement
                <select
                  value={scoringStrategy}
                  onChange={(e) => applyScoringStrategy(e.target.value)}
                >
                  <option value="balanced">Équilibre recommandé</option>
                  <option value="profitability">Rentabilité avant tout</option>
                  <option value="growth">
                    Développer le chiffre d’affaires
                  </option>
                  <option value="reliability">
                    Clients fiables et réguliers
                  </option>
                  <option value="simplicity">
                    Organisation simple et proche
                  </option>
                  <option value="custom" disabled>
                    Réglage personnalisé
                  </option>
                </select>
              </label>
              <label>
                Trajet aller-retour considéré trop long (minutes)
                <input
                  type="number"
                  min="1"
                  value={
                    form.scoring_preferences?.max_round_trip_minutes ??
                    defaultScoring.max_round_trip_minutes
                  }
                  onChange={(e) =>
                    updateScoring("max_round_trip_minutes", +e.target.value)
                  }
                />
              </label>
              <label>
                Temps administratif mensuel considéré trop lourd (minutes)
                <input
                  type="number"
                  min="1"
                  value={
                    form.scoring_preferences?.max_monthly_admin_minutes ??
                    defaultScoring.max_monthly_admin_minutes
                  }
                  onChange={(e) =>
                    updateScoring("max_monthly_admin_minutes", +e.target.value)
                  }
                />
              </label>
              <Notice>
                Exemple : avec « Rentabilité avant tout », la marge influence
                davantage le classement. Le CA, les paiements, les incidents, la
                récurrence, les trajets et le temps administratif restent tous
                pris en compte.
              </Notice>
            </div>
            <details className="scoring-advanced">
              <summary>Réglages avancés facultatifs</summary>
              <p className="section-intro">
                Une importance de 30 signifie que ce critère compte pour environ
                30 % du score final. Vous pouvez ignorer cette partie : les
                profils ci-dessus la configurent automatiquement.
              </p>
              <div className="form-grid">
                {(
                  [
                    ["margin_weight", "Importance de la marge"],
                    ["revenue_weight", "Importance du CA"],
                    ["payment_weight", "Importance des paiements"],
                    ["recurrence_weight", "Importance de la récurrence"],
                    ["travel_weight", "Importance des trajets"],
                    ["admin_weight", "Importance du temps administratif"],
                    ["incident_weight", "Importance des incidents"],
                  ] as [keyof ScoringPreferences, string][]
                ).map(([key, label]) => (
                  <label key={key}>
                    {label}
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={
                        form.scoring_preferences?.[key] ?? defaultScoring[key]
                      }
                      onChange={(e) => {
                        setScoringStrategy("custom");
                        updateScoring(key, +e.target.value);
                      }}
                    />
                  </label>
                ))}
              </div>
            </details>
          </details>
          <button disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer les réglages"}
          </button>
        </form>
        {message && (
          <Notice tone={message.startsWith("Réglages") ? "success" : "error"}>
            {message}
          </Notice>
        )}
      </section>
        </div>
      </details>
      <details id="settings-contracts" className="settings-chapter" hidden={activeSettingsSection !== "settings-contracts"} open={activeSettingsSection === "settings-contracts"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Contrats et documents</strong><small>Retrouvez les modèles clients, salariés et sous-traitants sans parcourir toute la page</small></span>
        </summary>
        <div className="settings-chapter-body">
          <ContractTemplateManager
            profile={profile}
            templates={data.contractTemplates || []}
            clientContracts={data.clientContracts || []}
            workerContracts={data.contracts}
            reload={reload}
          />
        </div>
      </details>
      <details id="settings-access" className="settings-chapter" hidden={activeSettingsSection !== "settings-access"} open={activeSettingsSection === "settings-access"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Accès et comptes</strong><small>Invitations, e-mails de connexion, suspensions, rôles administratifs et Coach</small></span>
        </summary>
        <div className="settings-chapter-body">
          <AccessManager profile={profile} data={data} reload={reload} />
        </div>
      </details>
      <details id="settings-processes" className="settings-chapter" hidden={activeSettingsSection !== "settings-processes"} open={activeSettingsSection === "settings-processes"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Processus métier</strong><small>Procédures, automatisations, checklists, devis guidés et urgences</small></span>
        </summary>
        <div className="settings-chapter-body">
          <OperatingProcedureLibrary profile={profile} data={data} />
          <ProcessDefinitionManager
            profile={profile}
            data={data}
            reload={reload}
            onManageGuidedQuote={onManageGuidedQuote}
          />
          <EmergencyOperationsCenter profile={profile} data={data} />
        </div>
      </details>
      <details id="settings-connections" className="settings-chapter" hidden={activeSettingsSection !== "settings-connections"} open={activeSettingsSection === "settings-connections"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Communications et connexions</strong><small>E-mails, signatures, Henrri, calendriers et autres fournisseurs</small></span>
        </summary>
        <div className="settings-chapter-body">
          <CommunicationManager profile={profile} />
          <IntegrationSettings
            profile={profile}
            integrations={data.integrations || []}
            cursors={data.integrationCursors || []}
            reload={reload}
          />
        </div>
      </details>
      <details id="settings-security" className="settings-chapter" hidden={activeSettingsSection !== "settings-security"} open={activeSettingsSection === "settings-security"}>
        <summary onClick={(event) => event.preventDefault()}>
          <span><strong>Sécurité et fiabilité</strong><small>Confidentialité, supervision et accès support temporaires</small></span>
        </summary>
        <div className="settings-chapter-body">
          <AdminPrivacyCenter profile={profile} />
          <AdminReliabilityCenter profile={profile} />
          <SupportAccessManager grants={data.supportAccessGrants || []} reload={reload} />
        </div>
      </details>
    </>
  );
}

type CommunicationSettingsRow = {
  organization_id: string;
  outbound_email_enabled: boolean;
  email_provider: string | null;
  email_provider_status: string;
  sender_name: string | null;
  reply_to_email: string | null;
  signature_enabled: boolean;
  signature_provider: string | null;
  signature_environment: "test" | "production";
  signature_provider_status: string;
  signature_expiry_days: number;
  signature_reminder_days: number[];
  last_error: string | null;
};

type CommunicationTemplateRow = {
  id: string;
  event_key: string;
  audience: string;
  name: string;
  version: number;
  subject_template: string;
  body_template: string;
  active: boolean;
};

type OutboundMessageRow = {
  id: string;
  event_key: string;
  recipient_name: string | null;
  recipient_email: string;
  subject: string;
  status: string;
  provider: string | null;
  attempt_count: number;
  sent_at: string | null;
  delivered_at: string | null;
  last_error: string | null;
  created_at: string;
};

function CommunicationManager({ profile }: { profile: Profile }) {
  const blank: CommunicationSettingsRow = {
      organization_id: profile.organization_id,
      outbound_email_enabled: false,
      email_provider: null,
      email_provider_status: "not_configured",
      sender_name: "",
      reply_to_email: "",
      signature_enabled: false,
      signature_provider: null,
      signature_environment: "test",
      signature_provider_status: "not_configured",
      signature_expiry_days: 30,
      signature_reminder_days: [3, 7],
      last_error: null,
    },
    [settings, setSettings] = useState<CommunicationSettingsRow>(blank),
    [templates, setTemplates] = useState<CommunicationTemplateRow[]>([]),
    [messages, setMessages] = useState<OutboundMessageRow[]>([]),
    [editing, setEditing] = useState<CommunicationTemplateRow | null>(null),
    [loading, setLoading] = useState(true),
    [available, setAvailable] = useState(true),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");

  async function loadCommunications() {
    setLoading(true);
    const [settingsResult, templatesResult, messagesResult] = await Promise.all([
      supabase!.from("organization_communication_settings").select("*").maybeSingle(),
      supabase!.from("communication_templates").select("id,event_key,audience,name,version,subject_template,body_template,active").eq("active", true).order("name"),
      supabase!.from("outbound_messages").select("id,event_key,recipient_name,recipient_email,subject,status,provider,attempt_count,sent_at,delivered_at,last_error,created_at").order("created_at", { ascending: false }).limit(30),
    ]);
    const firstError = settingsResult.error || templatesResult.error || messagesResult.error;
    if (firstError && /does not exist|schema cache|could not find the table|relation/i.test(firstError.message)) {
      setAvailable(false);
    } else if (firstError) {
      setMessage(firstError.message);
    } else {
      setAvailable(true);
      setSettings((settingsResult.data as CommunicationSettingsRow | null) || blank);
      setTemplates((templatesResult.data || []) as CommunicationTemplateRow[]);
      setMessages((messagesResult.data || []) as OutboundMessageRow[]);
    }
    setLoading(false);
  }

  useEffect(() => { void loadCommunications(); }, [profile.organization_id]);

  async function saveSettings(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    const result = await supabase!.rpc("save_communication_settings", {
      p_sender_name: settings.sender_name || null,
      p_reply_to_email: settings.reply_to_email || null,
      p_email_provider: settings.email_provider || null,
      p_outbound_email_enabled: settings.outbound_email_enabled,
      p_signature_provider: settings.signature_provider || null,
      p_signature_environment: settings.signature_environment,
      p_signature_enabled: settings.signature_enabled,
      p_signature_expiry_days: settings.signature_expiry_days,
      p_signature_reminder_days: settings.signature_reminder_days,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Paramètres de communication enregistrés.");
    if (!result.error) await loadCommunications();
  }

  async function saveTemplate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    const result = await supabase!.rpc("create_communication_template_version", {
      p_event_key: editing.event_key,
      p_audience: editing.audience,
      p_name: editing.name,
      p_subject_template: editing.subject_template,
      p_body_template: editing.body_template,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : `Nouvelle version du modèle « ${editing.name} » enregistrée.`);
    if (!result.error) { setEditing(null); await loadCommunications(); }
  }

  const providerState = (status: string) => ({
    not_configured: "À choisir",
    testing: "Test en cours",
    connected: "Connecté",
    error: "À corriger",
    disabled: "Désactivé",
  })[status] || status;
  const messageState = (status: string) => ({
    draft: "Brouillon", queued: "En attente", sending: "Envoi en cours",
    sent: "Envoyé", delivered: "Remis", bounced: "Rejeté",
    failed: "Échec", cancelled: "Annulé",
  })[status] || status;

  return (
    <section className="panel communication-manager">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Communications et preuves</span>
          <h2>E-mails automatiques & signatures</h2>
        </div>
      </div>
      {!available ? (
        <Notice>
          Le centre de communication est prêt dans la prochaine migration. Aucun e-mail
          externe n’est activé tant que cette migration et un prestataire ne sont pas validés.
        </Notice>
      ) : loading ? <p>Chargement du centre de communication…</p> : <>
        <div className="sync-state-grid">
          <div><strong>E-mails sortants</strong><span>{providerState(settings.email_provider_status)} · {settings.email_provider || "prestataire à définir"}</span></div>
          <div><strong>Signature électronique</strong><span>{providerState(settings.signature_provider_status)} · {settings.signature_provider || "prestataire à définir"}</span></div>
          <div><strong>Journal récent</strong><span>{messages.length} message(s) · {messages.filter((item) => ["failed","bounced"].includes(item.status)).length} à contrôler</span></div>
        </div>
        <details>
          <summary><strong>Régler les prestataires et l’expéditeur</strong></summary>
          <form className="form-grid details-content" onSubmit={saveSettings}>
            <label>Nom de l’expéditeur<input value={settings.sender_name || ""} onChange={(event) => setSettings((value) => ({ ...value, sender_name: event.target.value }))} placeholder="ARD Clean & Manage" /></label>
            <label>Adresse de réponse<input type="email" value={settings.reply_to_email || ""} onChange={(event) => setSettings((value) => ({ ...value, reply_to_email: event.target.value }))} /></label>
            <label>Prestataire e-mail<input value={settings.email_provider || ""} onChange={(event) => setSettings((value) => ({ ...value, email_provider: event.target.value }))} placeholder="À choisir" /></label>
            <label>Prestataire de signature<input value={settings.signature_provider || ""} onChange={(event) => setSettings((value) => ({ ...value, signature_provider: event.target.value }))} placeholder="À choisir" /></label>
            <label>Environnement de signature<select value={settings.signature_environment} onChange={(event) => setSettings((value) => ({ ...value, signature_environment: event.target.value as "test" | "production" }))}><option value="test">Test</option><option value="production">Production</option></select></label>
            <label>Expiration d’une demande de signature (jours)<input type="number" min="1" max="365" value={settings.signature_expiry_days} onChange={(event) => setSettings((value) => ({ ...value, signature_expiry_days: Number(event.target.value) }))} /></label>
            <label className="checkbox-label"><input type="checkbox" checked={settings.outbound_email_enabled} disabled={settings.email_provider_status !== "connected"} onChange={(event) => setSettings((value) => ({ ...value, outbound_email_enabled: event.target.checked }))} />Activer les envois réels</label>
            <label className="checkbox-label"><input type="checkbox" checked={settings.signature_enabled} disabled={settings.signature_provider_status !== "connected"} onChange={(event) => setSettings((value) => ({ ...value, signature_enabled: event.target.checked }))} />Activer les signatures réelles</label>
            <Notice>Renseigner un nom de prestataire ne le connecte pas. Les boutons restent verrouillés tant qu’une fonction serveur n’a pas vérifié ses clés et son environnement de test.</Notice>
            <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer sans activer"}</button>
          </form>
        </details>
        <details>
          <summary><strong>Modifier les messages automatiques ({templates.length})</strong></summary>
          <div className="permission-set-list details-content">
            {templates.map((template) => <article key={template.id}><div><strong>{template.name}</strong><small>{template.audience} · version {template.version}<br />{template.subject_template}</small></div><button type="button" className="secondary compact" onClick={() => setEditing({ ...template })}>Modifier</button></article>)}
          </div>
        </details>
        <details>
          <summary><strong>Journal des envois ({messages.length})</strong></summary>
          <div className="permission-set-list details-content">
            {messages.map((item) => <article key={item.id}><div><strong>{item.subject}</strong><small>{item.recipient_name || item.recipient_email} · {frDate(item.created_at, true)}{item.last_error ? ` · ${item.last_error}` : ""}</small></div><Status value={item.status} /><small>{messageState(item.status)}{item.provider ? ` · ${item.provider}` : ""}</small></article>)}
            {!messages.length && <p className="empty">Aucun envoi. Les futures communications apparaîtront ici avec leur état de remise.</p>}
          </div>
        </details>
      </>}
      {message && <Notice tone={message.includes("enregistr") ? "success" : "error"}>{message}</Notice>}
      {editing && <div className="modal-backdrop"><form className="modal-card portal-detail" role="dialog" aria-label="Modifier un message automatique" onSubmit={saveTemplate}><button type="button" className="icon-close" aria-label="Fermer" onClick={() => setEditing(null)}>×</button><span className="eyebrow">Nouvelle version</span><h2>{editing.name}</h2><p>La version déjà utilisée reste figée dans l’historique.</p><label>Nom<input required value={editing.name} onChange={(event) => setEditing((value) => value && ({ ...value, name: event.target.value }))} /></label><label>Objet<input required maxLength={250} value={editing.subject_template} onChange={(event) => setEditing((value) => value && ({ ...value, subject_template: event.target.value }))} /></label><label>Message<textarea required rows={10} value={editing.body_template} onChange={(event) => setEditing((value) => value && ({ ...value, body_template: event.target.value }))} /></label><small>Les variables entre doubles accolades seront remplacées au moment de préparer le message.</small><div className="row-actions"><button disabled={busy}>Enregistrer la nouvelle version</button><button type="button" className="secondary" onClick={() => setEditing(null)}>Annuler</button></div></form></div>}
    </section>
  );
}

function SupportAccessManager({
  grants,
  reload,
}: {
  grants: SupportAccessGrant[];
  reload: () => void;
}) {
  const [busy, setBusy] = useState(""),
    [message, setMessage] = useState("");
  const statusLabels: Record<string, string> = {
    requested: "À décider",
    approved: "Autorisée",
    rejected: "Refusée",
    revoked: "Révoquée",
    expired: "Expirée",
  };
  async function respond(grant: SupportAccessGrant, action: "approve" | "reject" | "revoke") {
    setBusy(grant.id);
    setMessage("");
    const result = await supabase!.rpc("respond_support_access_request", {
      p_grant_id: grant.id,
      p_action: action,
    });
    setBusy("");
    setMessage(
      result.error
        ? result.error.message
        : action === "approve"
          ? "Demande d’assistance autorisée jusqu’à son échéance."
          : action === "reject"
            ? "Demande d’assistance refusée."
            : "Autorisation d’assistance révoquée immédiatement.",
    );
    if (!result.error) reload();
  }
  return (
    <section className="panel support-access-manager">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Assistance sécurisée</span>
          <h2>Demandes du support ARD Manager</h2>
        </div>
      </div>
      <Notice>
        Le support doit indiquer un motif et une durée. Votre accord est requis
        et peut être révoqué. Il ne donne jamais, à lui seul, un accès aux
        données métier protégées de votre société.
      </Notice>
      <div className="permission-set-list">
        {grants.map((grant) => (
          <article key={grant.id}>
            <div>
              <strong>{grant.reason}</strong>
              <small>
                Demandée le {frDate(grant.created_at, true)} · expiration {frDate(grant.expires_at, true)}
              </small>
            </div>
            <Status value={grant.status} />
            <div className="row-actions">
              {grant.status === "requested" && (
                <>
                  <button
                    type="button"
                    className="secondary compact"
                    disabled={busy === grant.id}
                    onClick={() => respond(grant, "approve")}
                  >
                    Autoriser
                  </button>
                  <button
                    type="button"
                    className="danger compact"
                    disabled={busy === grant.id}
                    onClick={() => respond(grant, "reject")}
                  >
                    Refuser
                  </button>
                </>
              )}
              {grant.status === "approved" && (
                <button
                  type="button"
                  className="danger compact"
                  disabled={busy === grant.id}
                  onClick={() => respond(grant, "revoke")}
                >
                  Révoquer
                </button>
              )}
            </div>
            <small>{statusLabels[grant.status] || grant.status}</small>
          </article>
        ))}
        {!grants.length && (
          <p className="empty">Aucune demande d’assistance pour votre société.</p>
        )}
      </div>
      {message && <Notice tone={message.includes(".") ? "success" : "error"}>{message}</Notice>}
    </section>
  );
}

type HenrriPreviewResource = {
  ok: boolean;
  total?: number;
  returned?: number;
  status?: number;
  error?: string;
  preview?: Array<{ id: string | null; title: string | null; subtitle: string | null }>;
};

type HenrriImportPreview = {
  environment: "sandbox";
  imported: false;
  resources: Record<"customers" | "items" | "documents", HenrriPreviewResource>;
};

type HenrriSelection = Record<"customers" | "items" | "documents", string[]>;

type HenrriSandboxTest = {
  mode: "health" | "payments" | "quote";
  health: {
    authenticated: boolean;
    environment: "sandbox";
    resources: {
      customers: number;
      items: number;
      document_types: number;
      line_types: number;
    };
    credits: { used: string | null; limit: string | null; remaining: string | null };
  };
  reused?: boolean;
  run_key?: string;
  quote?: { id: string | number; identity?: string | null; finalized?: boolean };
  payments?: {
    permission: "Payment_Read";
    readable: boolean;
    returned: number;
    highest_id?: number | null;
    imported: false;
    preview: Array<{
      id: number | null;
      amount: number;
      date: string | null;
      payment_mode_id: number | null;
    }>;
  };
};

function IntegrationSettings({
  profile,
  integrations,
  cursors,
  reload,
}: {
  profile: Profile;
  integrations: OrganizationIntegration[];
  cursors: IntegrationSyncCursor[];
  reload: () => void;
}) {
  const defaults: Record<
      OrganizationIntegration["provider"],
      Omit<OrganizationIntegration, "id" | "organization_id">
    > = {
      henrri: {
        provider: "henrri",
        enabled: false,
        environment: "sandbox",
        status: "not_configured",
        account_identifier: "",
        provider_name: "Henrri",
        settings: {
          import_customers: true,
          import_items: true,
          import_documents: true,
          export_documents: true,
        },
        last_sync_at: null,
        last_error: null,
      },
      bank: {
        provider: "bank",
        enabled: false,
        environment: "production",
        status: "not_configured",
        account_identifier: "",
        provider_name: "",
        settings: { sync_balance: true, sync_transactions: true },
        last_sync_at: null,
        last_error: null,
      },
      google_calendar: {
        provider: "google_calendar",
        enabled: false,
        environment: "production",
        status: "not_configured",
        account_identifier: "",
        provider_name: "Google Agenda",
        settings: {
          admin_calendar: true,
          client_calendars: true,
          worker_calendars: true,
        },
        last_sync_at: null,
        last_error: null,
      },
      google_maps: {
        provider: "google_maps",
        enabled: false,
        environment: "production",
        status: "not_configured",
        account_identifier: "",
        provider_name: "Google Maps Platform",
        settings: {
          geocoding: true,
          route_optimization: true,
          travel_times: true,
        },
        last_sync_at: null,
        last_error: null,
      },
    },
    buildForm = () =>
      (["henrri", "bank", "google_calendar", "google_maps"] as const).reduce(
        (result, provider) => {
          const stored = integrations.find((item) => item.provider === provider);
          result[provider] = {
            ...defaults[provider],
            ...(stored || {}),
            settings: {
              ...defaults[provider].settings,
              ...(stored?.settings || {}),
            },
          } as OrganizationIntegration;
          return result;
        },
        {} as Record<OrganizationIntegration["provider"], OrganizationIntegration>,
      ),
    [form, setForm] = useState(buildForm),
    [busy, setBusy] = useState<OrganizationIntegration["provider"] | "">(""),
    [message, setMessage] = useState(""),
    [henrriClientId, setHenrriClientId] = useState(""),
    [henrriClientSecret, setHenrriClientSecret] = useState(""),
    [henrriPreview, setHenrriPreview] = useState<HenrriImportPreview | null>(null),
    [previewingHenrri, setPreviewingHenrri] = useState(false),
    [stagingHenrri, setStagingHenrri] = useState(false),
    [testingHenrri, setTestingHenrri] = useState<"" | "health" | "payments" | "quote">(""),
    [henrriTest, setHenrriTest] = useState<HenrriSandboxTest | null>(null),
    [henrriSelection, setHenrriSelection] = useState<HenrriSelection>({
      customers: [],
      items: [],
      documents: [],
    });
  useEffect(() => setForm(buildForm()), [integrations]);
  function update(
    provider: OrganizationIntegration["provider"],
    changes: Partial<OrganizationIntegration>,
  ) {
    setForm((current) => ({
      ...current,
      [provider]: { ...current[provider], ...changes },
    }));
  }
  function updateSetting(
    provider: OrganizationIntegration["provider"],
    key: keyof OrganizationIntegration["settings"],
    checked: boolean,
  ) {
    update(provider, {
      settings: { ...form[provider].settings, [key]: checked },
    });
  }
  async function persistHenrriFailure(detail: string) {
    const organizationId = form.henrri.organization_id || profile.organization_id;
    await supabase!
      .from("organization_integrations")
      .update({ last_error: detail.slice(0, 500) })
      .eq("organization_id", organizationId)
      .eq("provider", "henrri");
    setForm((current) => ({
      ...current,
      henrri: { ...current.henrri, last_error: detail.slice(0, 500) },
    }));
  }
  async function clearHenrriFailure() {
    const organizationId = form.henrri.organization_id || profile.organization_id;
    await supabase!
      .from("organization_integrations")
      .update({ last_error: null })
      .eq("organization_id", organizationId)
      .eq("provider", "henrri");
    setForm((current) => ({
      ...current,
      henrri: { ...current.henrri, last_error: null },
    }));
  }
  async function save(provider: OrganizationIntegration["provider"]) {
    setBusy(provider);
    setMessage("");
    const value = form[provider],
      status = value.enabled
        ? value.status === "connected"
          ? "connected"
          : "pending"
        : "disabled",
      payload = {
        organization_id: profile.organization_id,
        provider,
        enabled: value.enabled,
        environment: value.environment,
        status,
        account_identifier: value.account_identifier?.trim() || null,
        provider_name: value.provider_name?.trim() || null,
        settings: value.settings,
      };
    const result = await supabase!
      .from("organization_integrations")
      .upsert(payload, { onConflict: "organization_id,provider" });
    setBusy("");
    setMessage(
      result.error
        ? result.error.message
        : `${provider === "henrri" ? "Henrri" : provider === "bank" ? "Banque" : provider === "google_calendar" ? "Google Agenda" : "Google Maps"} : préférences enregistrées.`,
    );
    if (!result.error) reload();
  }
  async function connectHenrri(event: React.FormEvent) {
    event.preventDefault();
    setBusy("henrri");
    setMessage("");
    const preference = form.henrri,
      saved = await supabase!.from("organization_integrations").upsert(
        {
          organization_id: profile.organization_id,
          provider: "henrri",
          enabled: true,
          environment: preference.environment,
          status: preference.status === "connected" ? "connected" : "pending",
          account_identifier: preference.account_identifier?.trim() || null,
          provider_name: "Henrri",
          settings: preference.settings,
        },
        { onConflict: "organization_id,provider" },
      );
    if (saved.error) {
      setBusy("");
      setMessage(saved.error.message);
      return;
    }
    const result = await supabase!.functions.invoke("henrri-connect", {
      body: {
        environment: preference.environment,
        client_id: henrriClientId,
        client_secret: henrriClientSecret,
      },
    });
    setBusy("");
    if (result.error) {
      let detail = result.error.message;
      try {
        const payload = await (result.error as typeof result.error & { context?: Response }).context
          ?.clone()
          .json();
        if (payload?.error) detail = payload.error;
      } catch {
        // Le message Supabase est conservé.
      }
      await persistHenrriFailure(detail);
      setMessage(detail);
      reload();
      return;
    }
    await clearHenrriFailure();
    setHenrriClientId("");
    setHenrriClientSecret("");
    setMessage(
      isShowcaseMode
        ? "Connexion Henrri simulée : aucun identifiant n’a quitté le Showcase."
        : "Sandbox Henrri connectée et identifiants chiffrés dans le coffre sécurisé.",
    );
    reload();
  }
  async function previewHenrriImport() {
    setPreviewingHenrri(true);
    setMessage("");
    const result = await supabase!.functions.invoke<HenrriImportPreview>(
      "henrri-preview-import",
      { body: {} },
    );
    setPreviewingHenrri(false);
    if (result.error) {
      let detail = result.error.message;
      try {
        const payload = await (result.error as typeof result.error & { context?: Response }).context
          ?.clone()
          .json();
        if (payload?.error) detail = payload.error;
      } catch {
        // Le message Supabase est conservé.
      }
      await persistHenrriFailure(detail);
      setMessage(detail);
      reload();
      return;
    }
    if (!result.data) {
      setMessage("Henrri n’a retourné aucun aperçu exploitable.");
      return;
    }
    const preview = result.data;
    await clearHenrriFailure();
    setHenrriPreview(preview);
    setHenrriSelection({
      customers: form.henrri.settings.import_customers === false
        ? []
        : (preview.resources.customers.preview || []).flatMap((row) => row.id ? [row.id] : []),
      items: form.henrri.settings.import_items === false
        ? []
        : (preview.resources.items.preview || []).flatMap((row) => row.id ? [row.id] : []),
      documents: form.henrri.settings.import_documents === false
        ? []
        : (preview.resources.documents.preview || []).flatMap((row) => row.id ? [row.id] : []),
    });
    setMessage("Aperçu Henrri actualisé. Aucune donnée n’a été importée.");
  }
  function toggleHenrriCandidate(
    resource: keyof HenrriSelection,
    id: string,
    checked: boolean,
  ) {
    setHenrriSelection((current) => ({
      ...current,
      [resource]: checked
        ? [...new Set([...current[resource], id])]
        : current[resource].filter((value) => value !== id),
    }));
  }
  async function stageHenrriImport() {
    setStagingHenrri(true);
    setMessage("");
    const result = await supabase!.functions.invoke<{
      staged: Record<keyof HenrriSelection, number>;
      imported: false;
    }>("henrri-stage-import", { body: { selection: henrriSelection } });
    setStagingHenrri(false);
    if (result.error) {
      let detail = result.error.message;
      try {
        const payload = await (result.error as typeof result.error & { context?: Response }).context
          ?.clone()
          .json();
        if (payload?.error) detail = payload.error;
      } catch {
        // Le message Supabase est conservé.
      }
      await persistHenrriFailure(detail);
      setMessage(detail);
      reload();
      return;
    }
    if (!result.data) {
      setMessage("Henrri n’a retourné aucun élément à préparer.");
      return;
    }
    const total = Object.values(result.data.staged).reduce((sum, value) => sum + value, 0);
    await clearHenrriFailure();
    setMessage(
      `${total} élément(s) placé(s) dans le sas de rapprochement. Aucun client, service, devis ou facture n’a encore été créé.`,
    );
  }
  async function runHenrriSandboxTest(mode: "health" | "payments" | "quote") {
    setTestingHenrri(mode);
    setMessage("");
    const runKey = `ui_${new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 14)}_${crypto.randomUUID().slice(0, 8)}`,
      result = await supabase!.functions.invoke<HenrriSandboxTest>("henrri-sandbox-test", {
        body: {
          mode,
          run_key: runKey,
          confirmation: mode === "quote" ? "TEST ARD MANAGER SANDBOX" : undefined,
          finalize: false,
        },
      });
    setTestingHenrri("");
    if (result.error) {
      let detail = result.error.message;
      try {
        const payload = await (result.error as typeof result.error & { context?: Response }).context
          ?.clone()
          .json();
        if (payload?.error) detail = payload.error;
      } catch {
        // Le message Supabase est conservé.
      }
      await persistHenrriFailure(detail);
      setMessage(detail);
      reload();
      return;
    }
    if (!result.data) {
      setMessage("La Sandbox Henrri n’a retourné aucun résultat.");
      return;
    }
    setHenrriTest(result.data);
    await clearHenrriFailure();
    setMessage(
      mode === "health"
        ? "Connexion et droits de lecture Sandbox vérifiés."
        : mode === "payments"
          ? `Droit Payment_Read vérifié : ${result.data.payments?.returned || 0} règlement(s) lisible(s). Aucun règlement n’a été importé ni modifié.`
          : `Devis brouillon fictif créé dans Henrri Sandbox${result.data.quote?.identity ? ` (${result.data.quote.identity})` : ""}. Aucun envoi client n’a été réalisé.`,
    );
    reload();
  }
  const stateLabel = (item: OrganizationIntegration) =>
    item.last_error
      ? "À contrôler"
      : item.status === "connected"
      ? "Connecté"
      : item.enabled
        ? "Configuration en attente"
        : "Désactivé";
  const henrriCursors = cursors.filter((cursor) => cursor.provider === "henrri"),
    henrriFailedFlows = henrriCursors.filter((cursor) => cursor.status === "error").length,
    henrriRunningFlows = henrriCursors.filter((cursor) => cursor.status === "running").length;
  return (
    <section className="panel">
      <h2>Connexions par société</h2>
      <p className="section-intro">
        Chaque société dispose de ses propres connexions. Les clés API et jetons
        sont saisis une seule fois, transmis au serveur puis chiffrés dans son
        coffre sécurisé. Ils ne sont ni conservés ni réaffichés dans le navigateur.
      </p>
      <div className="connection-settings">
        <details open>
          <summary>
            <strong>Henrri</strong>
            <span>{stateLabel(form.henrri)}</span>
          </summary>
          <div className="details-content">
            <label className="check">
              <input
                type="checkbox"
                checked={form.henrri.enabled}
                onChange={(event) =>
                  update("henrri", { enabled: event.target.checked })
                }
              />
              Préparer la connexion Henrri
            </label>
            <div className="form-grid">
              <label>
                Environnement
                <select
                  value={form.henrri.environment}
                  onChange={(event) =>
                    update("henrri", {
                      environment: event.target.value as "sandbox" | "production",
                    })
                  }
                >
                  <option value="sandbox">Test gratuit (sandbox)</option>
                  <option value="production">Production réelle</option>
                </select>
              </label>
              <label>
                Compte Henrri (email, sans mot de passe)
                <input
                  type="email"
                  value={form.henrri.account_identifier || ""}
                  placeholder="adresse du compte Henrri"
                  onChange={(event) =>
                    update("henrri", { account_identifier: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="widget-checks">
              {(
                [
                  ["import_customers", "Importer les clients Henrri"],
                  ["import_items", "Importer services et articles"],
                  ["import_documents", "Importer devis et factures"],
                  ["export_documents", "Créer/finaliser les pièces depuis ARD Manager"],
                ] as [keyof OrganizationIntegration["settings"], string][]
              ).map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={form.henrri.settings[key] !== false}
                    onChange={(event) =>
                      updateSetting("henrri", key, event.target.checked)
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <Notice>
              La lecture des règlements Henrri est prévue. Leur saisie restera
              faite dans Henrri, car l’API ne permet pas actuellement de les
              enregistrer depuis ARD Manager. Les modifications seront reçues
              par webhooks dès leur disponibilité.
            </Notice>
            {form.henrri.last_error && (
              <Notice tone="error">
                Dernière erreur conservée : {form.henrri.last_error}. Relancez
                d’abord « Contrôler la connexion ». Une réussite effacera
                automatiquement cette alerte ; les correspondances existantes
                empêchent de recréer silencieusement un doublon.
              </Notice>
            )}
            {form.henrri.environment === "sandbox" ? (
              <form className="henrri-credentials" onSubmit={connectHenrri}>
                <div>
                  <span className="eyebrow">Connexion sécurisée</span>
                  <h4>Tester la clé Sandbox</h4>
                  <small>
                    Le Client ID et le Secret sont vérifiés directement auprès de
                    Henrri puis chiffrés côté serveur. Ils ne sont jamais conservés
                    dans le navigateur ni réaffichés.
                  </small>
                </div>
                <label>
                  Client ID Sandbox
                  <input
                    required
                    autoComplete="off"
                    value={henrriClientId}
                    onChange={(event) => setHenrriClientId(event.target.value)}
                  />
                </label>
                <label>
                  Secret API Sandbox
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    value={henrriClientSecret}
                    onChange={(event) => setHenrriClientSecret(event.target.value)}
                  />
                </label>
                <button type="submit" disabled={busy === "henrri"}>
                  {busy === "henrri" ? "Vérification…" : "Tester et connecter la Sandbox"}
                </button>
              </form>
            ) : (
              <Notice>
                La production restera verrouillée jusqu’à la validation de la
                sandbox et à la remise des paramètres de production par Henrri.
              </Notice>
            )}
            {form.henrri.status === "connected" && (
              <div className="henrri-preview-panel">
                <div className="section-title-row">
                  <div>
                    <span className="eyebrow">Recette isolée</span>
                    <h4>Tester la chaîne Henrri Sandbox</h4>
                    <small>
                      Le contrôle lit les droits réels. Le test d’écriture crée uniquement un client,
                      une prestation et un devis brouillon portant la mention « TEST ARD MANAGER SANDBOX ».
                      Aucun e-mail ni document réel n’est envoyé.
                    </small>
                  </div>
                  <div className="inline-actions">
                    <button
                      type="button"
                      disabled={Boolean(testingHenrri)}
                      onClick={() => runHenrriSandboxTest("health")}
                    >
                      {testingHenrri === "health" ? "Contrôle…" : "Contrôler la connexion"}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(testingHenrri)}
                      onClick={() => runHenrriSandboxTest("payments")}
                    >
                      {testingHenrri === "payments" ? "Lecture…" : "Tester les règlements"}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(testingHenrri)}
                      onClick={() => runHenrriSandboxTest("quote")}
                    >
                      {testingHenrri === "quote" ? "Création du test…" : "Créer un devis test brouillon"}
                    </button>
                  </div>
                </div>
                {henrriTest && (
                  <div className="sync-state-grid">
                    <div>
                      <strong>Authentification</strong>
                      <span>{henrriTest.health.authenticated ? "Valide · Sandbox" : "À contrôler"}</span>
                    </div>
                    <div>
                      <strong>Données lisibles</strong>
                      <span>{henrriTest.health.resources.customers} client(s) · {henrriTest.health.resources.items} article(s)</span>
                    </div>
                    <div>
                      <strong>Crédits Sandbox</strong>
                      <span>{henrriTest.health.credits.limit || "Information non retournée"}</span>
                    </div>
                    {henrriTest.quote && (
                      <div>
                        <strong>Dernier devis test</strong>
                        <span>{henrriTest.quote.identity || `ID ${henrriTest.quote.id}`} · brouillon non envoyé</span>
                      </div>
                    )}
                    {henrriTest.payments && (
                      <div>
                        <strong>Droit Payment_Read</strong>
                        <span>
                          Valide · {henrriTest.payments.returned} règlement(s) lu(s) · aucun import
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="section-title-row">
                  <div>
                    <span className="eyebrow">Avant import</span>
                    <h4>Contrôler les données disponibles</h4>
                    <small>
                      Lecture de la Sandbox uniquement. Cette étape n’ajoute et ne
                      modifie encore aucune donnée dans ARD Manager.
                    </small>
                  </div>
                  <button
                    type="button"
                    disabled={previewingHenrri}
                    onClick={previewHenrriImport}
                  >
                    {previewingHenrri
                      ? "Analyse en cours…"
                      : henrriPreview
                        ? "Actualiser l’aperçu"
                        : "Analyser ce qui serait importé"}
                  </button>
                </div>
                {henrriPreview && (
                  <div className="henrri-preview-grid">
                    {(
                      [
                        ["customers", "Clients"],
                        ["items", "Services / articles"],
                        ["documents", "Devis / factures"],
                      ] as const
                    ).map(([resource, label]) => {
                      const content = henrriPreview.resources[resource];
                      return (
                        <article key={resource}>
                          <header>
                            <strong>{label}</strong>
                            <span className={content.ok ? "status completed" : "status pending"}>
                              {content.ok ? `${content.total ?? 0} trouvé(s)` : "Accès à vérifier"}
                            </span>
                          </header>
                          {content.ok ? (
                            content.preview?.length ? (
                              <ul>
                                {content.preview.map((row, index) => (
                                  <li key={row.id || `${resource}-${index}`}>
                                    <label>
                                      <input
                                        type="checkbox"
                                        disabled={!row.id}
                                        checked={Boolean(row.id && henrriSelection[resource].includes(row.id))}
                                        onChange={(event) => row.id && toggleHenrriCandidate(
                                          resource,
                                          row.id,
                                          event.target.checked,
                                        )}
                                      />
                                      <span>
                                        <strong>{row.title || `Élément ${index + 1}`}</strong>
                                        {row.subtitle && <small>{row.subtitle}</small>}
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="empty">Aucun élément dans la Sandbox.</p>
                            )
                          ) : (
                            <p className="empty">
                              {content.status ? `Erreur ${content.status} · ` : ""}
                              {content.error || "Lecture refusée par Henrri."}
                            </p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
                {henrriPreview && (
                  <div className="henrri-stage-actions">
                    <small>
                      Le sas conserve une copie de travail réservée à l’administrateur.
                      Le rapprochement évitera ensuite les doublons avant toute création.
                    </small>
                    <button
                      type="button"
                      disabled={
                        stagingHenrri ||
                        Object.values(henrriSelection).every((values) => values.length === 0)
                      }
                      onClick={stageHenrriImport}
                    >
                      {stagingHenrri
                        ? "Préparation…"
                        : `Préparer le rapprochement (${Object.values(henrriSelection).reduce((sum, values) => sum + values.length, 0)})`}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="section-title-row">
              <div>
                <span className="eyebrow">État d’exploitation</span>
                <h4>Journal de synchronisation Henrri</h4>
                <small>
                  Chaque flux reprend après le dernier identifiant traité. Une erreur
                  reste visible jusqu’à la prochaine réussite, sans recréer les
                  correspondances déjà enregistrées.
                </small>
              </div>
            </div>
            <div className="sync-state-grid">
              <div>
                <strong>Connexion</strong>
                <span>{stateLabel(form.henrri)}</span>
                <small>
                  {form.henrri.last_sync_at
                    ? `Dernier échange ${frDate(form.henrri.last_sync_at, true)}`
                    : "Aucun échange global horodaté"}
                </small>
              </div>
              <div>
                <strong>Flux en cours</strong>
                <span>{henrriRunningFlows}</span>
                <small>{henrriRunningFlows ? "Traitement à surveiller" : "Aucun traitement bloqué en cours"}</small>
              </div>
              <div>
                <strong>Flux à contrôler</strong>
                <span>{henrriFailedFlows}</span>
                <small>{henrriFailedFlows ? "Le détail figure ci-dessous" : "Aucune erreur de flux conservée"}</small>
              </div>
              <div>
                <strong>Reprise protégée</strong>
                <span>Curseur + anti-doublon</span>
                <small>La reprise continue après le dernier élément confirmé.</small>
              </div>
              {henrriCursors.map((cursor) => (
                  <div key={cursor.id}>
                    <strong>
                      {{
                        customers: "Clients",
                        items: "Services / articles",
                        documents: "Devis / factures",
                        payments: "Règlements",
                        calendar_events: "Événements",
                        transactions: "Mouvements",
                        geocoding: "Adresses",
                        routes: "Tournées",
                      }[cursor.resource]}
                    </strong>
                    <span>
                      {{
                        idle: "En attente",
                        running: "En cours",
                        success: "Réussi",
                        error: "À contrôler",
                      }[cursor.status]}
                      {" · "}
                      {cursor.last_success_at
                        ? `Dernier succès ${frDate(cursor.last_success_at, true)}`
                        : "Aucune synchronisation"}
                    </span>
                    <small>
                      Reprise après l’identifiant externe {cursor.min_external_id || 0}
                    </small>
                    {cursor.status === "error" && cursor.last_error && (
                      <small>{cursor.last_error}</small>
                    )}
                  </div>
                ))}
            </div>
            <button
              type="button"
              disabled={busy === "henrri"}
              onClick={() => save("henrri")}
            >
              Enregistrer Henrri
            </button>
          </div>
        </details>
        <details>
          <summary>
            <strong>Banque</strong>
            <span>{stateLabel(form.bank)}</span>
          </summary>
          <div className="details-content">
            <label className="check">
              <input
                type="checkbox"
                checked={form.bank.enabled}
                onChange={(event) =>
                  update("bank", { enabled: event.target.checked })
                }
              />
              Préparer la connexion bancaire
            </label>
            <div className="form-grid">
              <label>
                Fournisseur de connexion
                <input
                  value={form.bank.provider_name || ""}
                  placeholder="À choisir (ex. agrégateur bancaire)"
                  onChange={(event) =>
                    update("bank", { provider_name: event.target.value })
                  }
                />
              </label>
              <label>
                Libellé du compte (sans identifiant bancaire)
                <input
                  value={form.bank.account_identifier || ""}
                  placeholder="Compte courant société"
                  onChange={(event) =>
                    update("bank", { account_identifier: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="widget-checks">
              <label>
                <input
                  type="checkbox"
                  checked={form.bank.settings.sync_balance !== false}
                  onChange={(event) =>
                    updateSetting("bank", "sync_balance", event.target.checked)
                  }
                />
                Lire le solde
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.bank.settings.sync_transactions !== false}
                  onChange={(event) =>
                    updateSetting("bank", "sync_transactions", event.target.checked)
                  }
                />
                Préparer le rapprochement des mouvements
              </label>
            </div>
            <Notice>
              Aucun fournisseur bancaire n’est encore sélectionné : ce réglage
              prépare le besoin sans afficher un faux solde connecté.
            </Notice>
            <button
              type="button"
              disabled={busy === "bank"}
              onClick={() => save("bank")}
            >
              Enregistrer la banque
            </button>
          </div>
        </details>
        <details>
          <summary>
            <strong>Google Agenda</strong>
            <span>{stateLabel(form.google_calendar)}</span>
          </summary>
          <div className="details-content">
            <label className="check">
              <input
                type="checkbox"
                checked={form.google_calendar.enabled}
                onChange={(event) =>
                  update("google_calendar", { enabled: event.target.checked })
                }
              />
              Préparer la synchronisation Google Agenda
            </label>
            <label>
              Compte Google de la société (email, sans mot de passe)
              <input
                type="email"
                value={form.google_calendar.account_identifier || ""}
                placeholder="agenda principal de la société"
                onChange={(event) =>
                  update("google_calendar", {
                    account_identifier: event.target.value,
                  })
                }
              />
            </label>
            <div className="widget-checks">
              {(
                [
                  ["admin_calendar", "Agenda administrateur"],
                  ["client_calendars", "Agendas clients qui l’autorisent"],
                  ["worker_calendars", "Agendas ST/employés qui l’autorisent"],
                ] as [keyof OrganizationIntegration["settings"], string][]
              ).map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={form.google_calendar.settings[key] !== false}
                    onChange={(event) =>
                      updateSetting("google_calendar", key, event.target.checked)
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <Notice>
              L’autorisation Google sera demandée séparément à chaque personne
              qui active son agenda. Aucun mot de passe Google ne sera demandé.
            </Notice>
            <button
              type="button"
              disabled={busy === "google_calendar"}
              onClick={() => save("google_calendar")}
            >
              Enregistrer Google Agenda
            </button>
          </div>
        </details>
        <details>
          <summary>
            <strong>Google Maps — tournées</strong>
            <span>{stateLabel(form.google_maps)}</span>
          </summary>
          <div className="details-content">
            <label className="check">
              <input
                type="checkbox"
                checked={form.google_maps.enabled}
                onChange={(event) =>
                  update("google_maps", { enabled: event.target.checked })
                }
              />
              Préparer Google Maps Platform
            </label>
            <div className="widget-checks">
              {(
                [
                  ["geocoding", "Vérifier les adresses et coordonnées"],
                  ["route_optimization", "Optimiser l’ordre des tournées"],
                  ["travel_times", "Calculer distances et temps de route"],
                ] as [keyof OrganizationIntegration["settings"], string][]
              ).map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={form.google_maps.settings[key] !== false}
                    onChange={(event) =>
                      updateSetting("google_maps", key, event.target.checked)
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <Notice>
              Les feuilles de route partent déjà du siège et s’ouvrent dans
              Google Maps. L’activation de Routes API et Geocoding API ajoutera
              les durées réelles, la distance et l’ordre optimisé. La clé restera
              exclusivement dans le coffre du serveur.
            </Notice>
            <div className="sync-state-grid">
              {cursors
                .filter((cursor) => cursor.provider === "google_maps")
                .map((cursor) => (
                  <div key={cursor.id}>
                    <strong>
                      {cursor.resource === "geocoding"
                        ? "Adresses"
                        : "Tournées"}
                    </strong>
                    <span>
                      {cursor.last_success_at
                        ? `Dernier succès ${frDate(cursor.last_success_at, true)}`
                        : "Aucun calcul API"}
                    </span>
                  </div>
                ))}
            </div>
            <button
              type="button"
              disabled={busy === "google_maps"}
              onClick={() => save("google_maps")}
            >
              Enregistrer Google Maps
            </button>
          </div>
        </details>
      </div>
      {message && (
        <Notice tone={message.includes("enregistrées") ? "success" : "error"}>
          {message}
        </Notice>
      )}
    </section>
  );
}

function ContractTemplateManager({
  profile,
  templates,
  clientContracts,
  workerContracts,
  reload,
}: {
  profile: Profile;
  templates: ContractTemplate[];
  clientContracts: ClientContract[];
  workerContracts: WorkerContract[];
  reload: () => void;
}) {
  const [audience, setAudience] =
      useState<ContractTemplate["audience"]>("client"),
    [name, setName] = useState("Contrat de prestation"),
    [content, setContent] = useState(""),
    [renewalDays, setRenewalDays] = useState(30),
    [documents, setDocuments] = useState<string[]>(["signed_contract"]),
    [editingId, setEditingId] = useState<string | null>(null),
    [deleteId, setDeleteId] = useState<string | null>(null),
    [formOpen, setFormOpen] = useState(false),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const documentChoices = [
    ["signed_contract", "Contrat signé"],
    ["urssaf_vigilance", "Vigilance URSSAF"],
    ["professional_liability", "RC professionnelle"],
    ["bank_details", "RIB"],
  ];
  function chooseAudience(value: ContractTemplate["audience"]) {
    setAudience(value);
    setName(
      value === "client"
        ? "Contrat de prestation"
        : "Contrat de sous-traitance",
    );
    setDocuments(
      value === "contractor"
        ? ["signed_contract", "urssaf_vigilance", "professional_liability"]
        : ["signed_contract"],
    );
  }
  function resetForm() {
    setEditingId(null);
    setAudience("client");
    setName("Contrat de prestation");
    setContent("");
    setRenewalDays(30);
    setDocuments(["signed_contract"]);
    setDeleteId(null);
    setMessage("");
  }
  function editTemplate(template: ContractTemplate) {
    if (template.audience === "employee") return;
    setEditingId(template.id);
    setAudience(template.audience);
    setName(template.name);
    setContent(template.content);
    setRenewalDays(template.renewal_notice_days);
    setDocuments(template.required_document_kinds || []);
    setDeleteId(null);
    setMessage("");
    setFormOpen(true);
  }
  function duplicateTemplateAsNewVersion(template: ContractTemplate) {
    if (template.audience === "employee") return;
    setEditingId(null);
    setAudience(template.audience);
    setName(template.name);
    setContent(template.content);
    setRenewalDays(template.renewal_notice_days);
    setDocuments(template.required_document_kinds || []);
    setDeleteId(null);
    setMessage(
      `La version ${template.version} a été recopiée. Modifiez uniquement ce qui évolue puis enregistrez : les contrats déjà générés resteront inchangés.`,
    );
    setFormOpen(true);
  }
  function templateUsage(templateId: string) {
    const linked = [
      ...clientContracts.filter((item) => item.template_id === templateId),
      ...workerContracts.filter((item) => item.template_id === templateId),
    ];
    return {
      linked: linked.length,
      sent: linked.filter((item) => item.status !== "draft").length,
    };
  }
  function toggleDocument(kind: string) {
    setDocuments((current) =>
      current.includes(kind)
        ? current.filter((item) => item !== kind)
        : [...current, kind],
    );
  }
  async function saveTemplate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !content.trim()) {
      setMessage("Le nom et le contenu du modèle sont obligatoires.");
      return;
    }
    setBusy(true);
    if (editingId) {
      const { error } = await supabase!
        .from("contract_templates")
        .update({
          name: name.trim(),
          content: content.trim(),
          required_document_kinds: documents,
          renewal_notice_days: renewalDays,
        })
        .eq("id", editingId);
      setBusy(false);
      setMessage(
        error
          ? error.message
          : "Modèle mis à jour. Les contrats déjà envoyés restent inchangés.",
      );
      if (!error) {
        resetForm();
        setMessage(
          "Modèle mis à jour. Les contrats déjà envoyés restent inchangés.",
        );
        setFormOpen(false);
        reload();
      }
      return;
    }
    const version =
      Math.max(
        0,
        ...templates
          .filter((item) => item.audience === audience)
          .map((item) => item.version),
      ) + 1;
    const deactivate = await supabase!
      .from("contract_templates")
      .update({ active: false })
      .eq("audience", audience)
      .eq("active", true);
    if (deactivate.error) {
      setBusy(false);
      setMessage(deactivate.error.message);
      return;
    }
    const { error } = await supabase!.from("contract_templates").insert({
      organization_id: profile.organization_id,
      audience,
      name: name.trim(),
      version,
      content: content.trim(),
      required_document_kinds: documents,
      renewal_notice_days: renewalDays,
      active: true,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : `Version ${version} enregistrée. Elle sera utilisée pour les prochaines fiches.`,
    );
    if (!error) {
      resetForm();
      setMessage(
        `Version ${version} enregistrée. Elle sera utilisée pour les prochaines fiches.`,
      );
      setFormOpen(false);
      reload();
    }
  }
  async function deleteTemplate(template: ContractTemplate) {
    const usage = templateUsage(template.id);
    if (usage.sent) {
      setMessage(
        "Ce modèle est protégé car il a déjà servi à un contrat envoyé.",
      );
      return;
    }
    setBusy(true);
    const { error } = await supabase!
      .from("contract_templates")
      .delete()
      .eq("id", template.id);
    setBusy(false);
    setDeleteId(null);
    setMessage(
      error
        ? error.message
        : usage.linked
          ? "Modèle supprimé. Les brouillons existants conservent leur texte généré."
          : "Modèle supprimé.",
    );
    if (!error) {
      if (editingId === template.id) resetForm();
      reload();
    }
  }
  return (
    <section className="panel contract-settings">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Automatisation documentaire</span>
          <h2>Modèles de contrats</h2>
        </div>
      </div>
      <Notice>
        Les modèles clients et sous-traitants remplissent automatiquement les
        informations connues lors de la création du dossier. Un modèle déjà
        envoyé est figé : il faut alors créer une nouvelle version. Les contrats
        de travail salariés sont déposés depuis la paie après rédaction par le
        comptable.
      </Notice>
      <div className="contract-template-list">
        {templates
          .filter((template) => template.audience !== "employee")
          .map((template) => {
            const usage = templateUsage(template.id),
              locked = usage.sent > 0;
            return (
              <article key={template.id}>
                <div>
                  <strong>{template.name}</strong>
                  <small>
                    {template.audience === "client"
                      ? "Client"
                      : "Sous-traitant"}
                    {" · version "}
                    {template.version}
                    {usage.linked
                      ? ` · ${usage.linked} dossier(s)`
                      : " · jamais utilisé"}
                  </small>
                </div>
                <Status value={template.active ? "active" : "expired"} />
                <div className="template-actions">
                  <button
                    className="secondary compact"
                    onClick={() =>
                      locked
                        ? duplicateTemplateAsNewVersion(template)
                        : editTemplate(template)
                    }
                  >
                    {locked ? "Créer une nouvelle version" : "Modifier"}
                  </button>
                  <button
                    className="danger compact"
                    disabled={locked}
                    onClick={() => setDeleteId(template.id)}
                  >
                    Supprimer
                  </button>
                </div>
                {locked && (
                  <small className="template-lock">
                    Protégé : le bouton recopie son contenu dans une nouvelle
                    version modifiable.
                  </small>
                )}
                {deleteId === template.id && (
                  <div className="template-delete-confirm">
                    <small>Supprimer définitivement ce modèle ?</small>
                    <button
                      className="danger compact"
                      disabled={busy}
                      onClick={() => deleteTemplate(template)}
                    >
                      Confirmer
                    </button>
                    <button
                      className="secondary compact"
                      onClick={() => setDeleteId(null)}
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </article>
            );
          })}
      </div>
      <details
        open={formOpen}
        onToggle={(event) => setFormOpen(event.currentTarget.open)}
      >
        <summary>
          <strong>
            {editingId ? "Modifier le modèle" : "Créer une nouvelle version"}
          </strong>
        </summary>
        <form className="form-grid" onSubmit={saveTemplate}>
          <label>
            Destinataire
            <select
              value={audience}
              onChange={(e) =>
                chooseAudience(e.target.value as ContractTemplate["audience"])
              }
            >
              <option value="client">Client</option>
              <option value="contractor">Sous-traitant</option>
            </select>
          </label>
          <label>
            Nom du modèle
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Alerte avant échéance (jours)
            <input
              type="number"
              min="0"
              max="365"
              value={renewalDays}
              onChange={(e) => setRenewalDays(+e.target.value)}
            />
          </label>
          <fieldset className="contract-document-checks">
            <legend>Pièces à demander automatiquement</legend>
            {documentChoices.map(([kind, label]) => (
              <label key={kind}>
                <input
                  type="checkbox"
                  checked={documents.includes(kind)}
                  onChange={() => toggleDocument(kind)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <label className="wide">
            Contenu du modèle
            <textarea
              rows={8}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Collez ici le texte contractuel validé."
            />
            <small>
              Champs automatiques : {"{{company_name}}"}, {"{{company_siret}}"},{" "}
              {"{{company_address}}"}, {"{{company_representative}}"},{" "}
              {audience === "client"
                ? "{{client_name}}, {{client_address}}, {{client_siret}}, {{client_vat_number}}"
                : "{{worker_name}}, {{worker_business_name}}, {{worker_siret}}, {{worker_address}}"}
              , {"{{contract_date}}"}.
            </small>
          </label>
          <button disabled={busy}>
            {busy
              ? "Enregistrement…"
              : editingId
                ? "Enregistrer les modifications"
                : "Enregistrer la nouvelle version"}
          </button>
          {editingId && (
            <button
              type="button"
              className="secondary"
              onClick={() => {
                resetForm();
                setFormOpen(false);
              }}
            >
              Annuler
            </button>
          )}
        </form>
      </details>
      {message && (
        <Notice tone={message.includes("enregistrée") ? "success" : "error"}>
          {message}
        </Notice>
      )}
    </section>
  );
}

function CoachAccessPanel({ data, reload }: { data: DataState; reload: () => void }) {
  const coachSet=(data.permissionSets||[]).find((set)=>set.access_mode==="coach_read_only"),
    coachInvitations=(data.organizationUserInvitations||[]).filter((item)=>item.target_type==="coach"),
    openInvitation=coachInvitations.find((item)=>["pending","sent"].includes(item.status)),
    coachProfiles=(data.profiles||[]).filter((item)=>item.role==="admin"&&item.permission_set_id===coachSet?.id),
    [displayName,setDisplayName]=useState(""),[email,setEmail]=useState(""),[busy,setBusy]=useState(false),[message,setMessage]=useState("");
  async function invite(event:React.FormEvent){
    event.preventDefault();if(!coachSet)return;setBusy(true);setMessage("");
    const result=await supabase!.functions.invoke("invite-organization-user",{body:{target_type:"coach",target_id:coachSet.id,display_name:displayName,email,return_to:location.origin}});
    setBusy(false);
    if(result.error){let detail=result.error.message;try{const payload=await (result.error as typeof result.error&{context?:Response}).context?.clone().json();if(payload?.error)detail=payload.error;}catch{/* message conservé */}setMessage(detail);return;}
    setMessage(isShowcaseMode?"Invitation Coach simulée. Aucun e-mail réel n’a été envoyé.":"Invitation Coach envoyée de façon sécurisée.");setDisplayName("");setEmail("");reload();
  }
  async function resend(){
    if(!openInvitation||!["pending","sent"].includes(openInvitation.status))return;setBusy(true);setMessage("");
    const wasSent=openInvitation.status==="sent";
    const result=await supabase!.functions.invoke("invite-organization-user",{body:{invitation_id:openInvitation.id,return_to:location.origin}});setBusy(false);setMessage(result.error?result.error.message:wasSent?"Nouveau lien d’activation Coach envoyé.":"Invitation Coach envoyée.");if(!result.error)reload();
  }
  async function revoke(){
    if(!openInvitation||!window.confirm(`Révoquer l’invitation Coach de ${openInvitation.email} ?`))return;setBusy(true);
    const result=await supabase!.rpc("revoke_organization_user_invitation",{p_invitation_id:openInvitation.id});setBusy(false);setMessage(result.error?result.error.message:"Invitation Coach révoquée.");if(!result.error)reload();
  }
  return <section className="coach-access-panel" data-testid="coach-access-panel"><div className="section-heading"><div><span className="eyebrow">Accès externe contrôlé</span><h3>Coach de l’entreprise</h3><p>Le Coach voit les écrans administrateur, ne modifie aucune donnée métier et peut uniquement rédiger ses notes dans l’espace Coaching.</p></div><span className="status-badge success">Lecture seule serveur</span></div>
    {!coachSet?<p className="notice warning">La migration Coach doit être appliquée avant d’envoyer un accès réel.</p>:<>
      {coachProfiles.map((coach)=><article className="coach-access-account" key={coach.id}><div><strong>{coach.display_name}</strong><small>{coach.email||"Adresse non renseignée"}</small></div><span className={`status-badge ${coach.access_status==="suspended"?"danger":"success"}`}>{coach.access_status==="suspended"?"Suspendu":"Actif · lecture seule"}</span></article>)}
      {openInvitation?<article className="coach-access-account"><div><strong>{openInvitation.display_name}</strong><small>{openInvitation.email} · {openInvitation.status==="sent"?"activation attendue":"invitation préparée"}</small></div><div className="detail-actions"><button type="button" className="secondary compact" disabled={busy} onClick={()=>void resend()}>{openInvitation.status==="sent"?"Renvoyer le lien":"Envoyer"}</button><button type="button" className="danger compact" disabled={busy} onClick={()=>void revoke()}>Révoquer</button></div></article>:<form className="form-grid" onSubmit={invite}><label>Nom du coach<input required minLength={2} value={displayName} onChange={(event)=>setDisplayName(event.target.value)} placeholder="Prénom et nom" /></label><label>Adresse de connexion<input required type="email" value={email} onChange={(event)=>setEmail(event.target.value)} placeholder="coach@exemple.fr" /></label><button disabled={busy}>{busy?"Préparation…":"Inviter le coach"}</button></form>}
    </>}
    {message&&<p className={`notice ${/envoyée|simulée|révoquée/.test(message)?"success":"error"}`}>{message}</p>}
  </section>;
}

function AccessManager({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const permissionOptions: [string, string][] = [
      ["dashboard", "Centre de contrôle"],
      ["agenda", "Agenda"],
      ["routes", "Tournées"],
      ["missions", "Missions"],
      ["clients", "Clients"],
      ["crm", "CRM & acquisition"],
      ["action_plan", "Plan d’action 4 mois"],
      ["billing", "Devis & factures"],
      ["accounting", "Comptabilité"],
      ["profitability", "Rentabilité"],
      ["stocks", "Stocks"],
      ["director", "Dirigeant"],
      ["team", "Équipe & RH"],
      ["company", "Société"],
      ["settings", "Réglages et utilisateurs"],
    ],
    allPermissions = permissionOptions.map(([key]) => key),
    [editingId, setEditingId] = useState(""),
    [name, setName] = useState(""),
    [description, setDescription] = useState(""),
    [permissions, setPermissions] = useState<string[]>(allPermissions),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [selectedAccessProfileId, setSelectedAccessProfileId] = useState(""),
    [selectedPendingAccessTarget, setSelectedPendingAccessTarget] = useState<{
      targetType: "client" | "worker";
      targetId: string;
      displayName: string;
      email: string;
    } | null>(null),
    [accountSearch, setAccountSearch] = useState("");
  function accessTargetFor(user: OrgProfile) {
    const client = data.clients.find((item) => item.profile_id === user.id);
    if (client) return { targetType: "client" as const, targetId: client.id, displayName: client.name };
    const worker = data.workers.find((item) => item.profile_id === user.id);
    if (worker) return {
      targetType: "worker" as const,
      targetId: worker.id,
      displayName:
        [worker.first_name, worker.last_name].filter(Boolean).join(" ") ||
        worker.business_name ||
        user.display_name,
    };
    return null;
  }
  const selectedAccessProfile = (data.profiles || []).find((item) => item.id === selectedAccessProfileId),
    selectedAccessTarget = selectedAccessProfile ? accessTargetFor(selectedAccessProfile) : null,
    normalizedAccountSearch = accountSearch.trim().toLocaleLowerCase("fr-FR"),
    visibleProfiles = (data.profiles || []).filter((user) => {
      if (!normalizedAccountSearch) return true;
      const target = accessTargetFor(user);
      return [
        user.display_name,
        user.email,
        labels[user.role] || user.role,
        target?.displayName,
      ].filter(Boolean).join(" ").toLocaleLowerCase("fr-FR").includes(normalizedAccountSearch);
    }),
    pendingAccessTargets = [
      ...data.clients
        .filter((client) => !client.profile_id && client.active !== false)
        .map((client) => ({
          targetType: "client" as const,
          targetId: client.id,
          displayName: client.name,
          email: client.contact_email || "",
          kindLabel: "Client",
        })),
      ...data.workers
        .filter((worker) => !worker.profile_id && worker.active !== false)
        .map((worker) => ({
          targetType: "worker" as const,
          targetId: worker.id,
          displayName:
            [worker.first_name, worker.last_name].filter(Boolean).join(" ") ||
            worker.business_name ||
            "Intervenant sans nom",
          email: worker.contact_email || "",
          kindLabel: worker.kind === "contractor" ? "Sous-traitant" : "Employé",
        })),
    ].filter((target) =>
      !normalizedAccountSearch ||
      [target.displayName, target.email, target.kindLabel].join(" ").toLocaleLowerCase("fr-FR").includes(normalizedAccountSearch),
    );
  function selectSet(set?: PermissionSet) {
    setEditingId(set?.id || "");
    setName(set?.name || "");
    setDescription(set?.description || "");
    setPermissions(set?.permissions || allPermissions);
  }
  function togglePermission(key: string) {
    setPermissions((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  }
  async function saveSet(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = {
        organization_id: profile.organization_id,
        name: name.trim(),
        description: description.trim() || null,
        permissions,
        active: true,
      },
      result = editingId
        ? await supabase!
            .from("permission_sets")
            .update(payload)
            .eq("id", editingId)
        : await supabase!.from("permission_sets").insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editingId
          ? "Type d’accès mis à jour."
          : "Type d’accès créé.",
    );
    if (!result.error) {
      selectSet();
      reload();
    }
  }
  async function updateProfileAccess(
    userId: string,
    values: { permission_set_id?: string | null; access_status?: string },
  ) {
    setBusy(true);
    const { error } = await supabase!
      .from("profiles")
      .update(values)
      .eq("id", userId);
    setBusy(false);
    setMessage(error ? error.message : "Accès utilisateur mis à jour.");
    if (!error) reload();
  }
  return (
    <section className="panel access-manager">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Organisation des accès</span>
          <h2>Comptes et autorisations</h2>
          <p>Les rôles définissent ce qui est visible. Le bouton « Gérer » permet de modifier l’adresse de connexion ou de suspendre réellement un espace.</p>
        </div>
        <button className="secondary compact" onClick={() => selectSet()}>
          Nouveau type
        </button>
      </div>
      <Notice>
        Ces types réduisent les écrans visibles pour les comptes administratifs
        délégués. Les clients, salariés et ST conservent leurs espaces dédiés.
        L’audit final vérifiera aussi chaque règle de sécurité Supabase.
      </Notice>
      <CoachAccessPanel data={data} reload={reload} />
      <div className="permission-set-list">
        {(data.permissionSets || []).map((set) => (
          <article key={set.id}>
            <div>
              <strong>{set.name}</strong>
              <small>
                {set.permissions.length} accès ·{" "}
                {set.description || "Sans description"}
              </small>
              {set.access_mode === "coach_read_only" && <span className="status-badge success">Coach · lecture seule</span>}
            </div>
            <button
              className="secondary compact"
              onClick={() => selectSet(set)}
              disabled={set.access_mode === "coach_read_only"}
            >
              {set.access_mode === "coach_read_only" ? "Protégé" : "Modifier"}
            </button>
          </article>
        ))}
        {!(data.permissionSets || []).length && (
          <p className="empty">Aucun type personnalisé.</p>
        )}
      </div>
      <details>
        <summary>
          <strong>
            {editingId ? "Modifier le type" : "Créer un type d’accès"}
          </strong>
        </summary>
        <form className="form-grid" onSubmit={saveSet}>
          <label>
            Nom
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <label>
            Description
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <fieldset className="permission-checks wide">
            <legend>Écrans autorisés</legend>
            {permissionOptions.map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={permissions.includes(key)}
                  onChange={() => togglePermission(key)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <button disabled={busy}>
            {editingId ? "Enregistrer le type" : "Créer le type"}
          </button>
        </form>
      </details>
      <div className="table-wrap access-table">
        <div className="section-heading-row access-directory-heading">
          <div>
            <h3>Comptes de la société</h3>
            <p className="section-intro">Une modification d’e-mail conserve les missions, documents, factures et historiques du même compte.</p>
          </div>
          <label className="access-directory-search">
            Rechercher une personne
            <input
              type="search"
              value={accountSearch}
              onChange={(event) => setAccountSearch(event.target.value)}
              placeholder="Nom, e-mail ou rôle"
            />
          </label>
        </div>
        <table>
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>E-mail de connexion</th>
              <th>Rôle</th>
              <th>Type d’accès admin</th>
              <th>État du compte</th>
              <th>Gestion</th>
            </tr>
          </thead>
          <tbody>
            {visibleProfiles.map((user) => {
              const accessTarget = accessTargetFor(user);
              return (
              <tr key={user.id}>
                <td>{user.display_name}</td>
                <td>{user.email || "Non renseigné"}</td>
                <td>{labels[user.role] || user.role}</td>
                <td>
                  {user.is_primary_admin || user.id === profile.id ? (
                    <strong>Administrateur principal · accès complet</strong>
                  ) : user.role === "admin" ? (
                    <select
                      value={user.permission_set_id || ""}
                      onChange={(e) =>
                        updateProfileAccess(user.id, {
                          permission_set_id: e.target.value || null,
                        })
                      }
                    >
                      <option value="">Administrateur complet</option>
                      {(data.permissionSets || [])
                        .filter((set) => set.active)
                        .map((set) => (
                          <option key={set.id} value={set.id}>
                            {set.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    "Espace dédié"
                  )}
                </td>
                <td>
                  <span className={`status-badge ${user.access_status === "suspended" ? "danger" : user.access_status === "invited" ? "warning" : "success"}`}>
                    {user.access_status === "suspended" ? "Suspendu" : user.access_status === "invited" ? "Invité" : "Actif"}
                  </span>
                </td>
                <td>
                  {accessTarget ? (
                    <button type="button" className="secondary compact" onClick={() => setSelectedAccessProfileId(user.id)}>
                      Gérer
                    </button>
                  ) : (
                    <small>{user.role === "admin" ? "Compte administrateur protégé" : "Fiche métier à rattacher"}</small>
                  )}
                </td>
              </tr>
              );
            })}
            {!visibleProfiles.length && (
              <tr><td colSpan={6} className="empty">Aucun compte ne correspond à cette recherche.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <section className="pending-access-directory" data-testid="pending-access-directory">
        <div className="section-heading-row">
          <div>
            <h3>Fiches sans compte actif</h3>
            <p className="section-intro">Les imports restent ici sans accès. Vous décidez individuellement quand créer et envoyer manuellement le lien de connexion.</p>
          </div>
          <span className="status-badge warning">{pendingAccessTargets.length} à préparer</span>
        </div>
        <div className="pending-access-grid">
          {pendingAccessTargets.map((target) => {
            const access = organizationAccessSummary(
              target.targetType,
              target.targetId,
              false,
              data.organizationUserInvitations,
            );
            return (
              <article key={`${target.targetType}-${target.targetId}`}>
                <div>
                  <span className="eyebrow">{target.kindLabel}</span>
                  <strong>{target.displayName}</strong>
                  <small>{target.email || "E-mail de connexion à renseigner"}</small>
                </div>
                <div className="pending-access-actions">
                  <span className={`status-badge ${accessToneClass(access.tone)}`}>{access.label}</span>
                  <button
                    type="button"
                    className="secondary compact"
                    onClick={() => setSelectedPendingAccessTarget(target)}
                  >
                    Préparer l’accès
                  </button>
                </div>
              </article>
            );
          })}
          {!pendingAccessTargets.length && (
            <p className="empty">Aucune fiche sans compte ne correspond à cette recherche.</p>
          )}
        </div>
      </section>
      <AccessAuditTable data={data} />
      {message && (
        <Notice tone={message.includes(".") ? "success" : "error"}>
          {message}
        </Notice>
      )}
      {selectedAccessProfile && selectedAccessTarget && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedAccessProfileId(""); }}>
          <section className="portal-detail access-account-dialog" role="dialog" aria-modal="true" aria-label={`Gérer le compte de ${selectedAccessProfile.display_name}`}>
            <div className="portal-detail-head">
              <div><span className="eyebrow">Compte de connexion</span><h2>{selectedAccessProfile.display_name}</h2></div>
              <button type="button" className="icon-close" onClick={() => setSelectedAccessProfileId("")}>×</button>
            </div>
            <AccountInvitationPanel
              targetType={selectedAccessTarget.targetType}
              targetId={selectedAccessTarget.targetId}
              displayName={selectedAccessTarget.displayName}
              hasAccount
              accountEmail={selectedAccessProfile.email}
              accountProfile={selectedAccessProfile}
              onChanged={async () => { await reload(); setSelectedAccessProfileId(""); }}
            />
            <div className="detail-actions"><button type="button" className="secondary" onClick={() => setSelectedAccessProfileId("")}>Fermer</button></div>
          </section>
        </div>
      )}
      {selectedPendingAccessTarget && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedPendingAccessTarget(null); }}>
          <section className="portal-detail access-account-dialog" role="dialog" aria-modal="true" aria-label={`Préparer le compte de ${selectedPendingAccessTarget.displayName}`}>
            <div className="portal-detail-head">
              <div><span className="eyebrow">Accès non encore activé</span><h2>{selectedPendingAccessTarget.displayName}</h2></div>
              <button type="button" className="icon-close" onClick={() => setSelectedPendingAccessTarget(null)}>×</button>
            </div>
            <AccountInvitationPanel
              targetType={selectedPendingAccessTarget.targetType}
              targetId={selectedPendingAccessTarget.targetId}
              displayName={selectedPendingAccessTarget.displayName}
              hasAccount={false}
              accountEmail={selectedPendingAccessTarget.email}
              onChanged={reload}
            />
            <div className="detail-actions"><button type="button" className="secondary" onClick={() => setSelectedPendingAccessTarget(null)}>Fermer</button></div>
          </section>
        </div>
      )}
    </section>
  );
}

function AccessAuditTable({ data }: { data: DataState }) {
  const accessLabels: Record<string, string> = {
    invited: "Invité",
    active: "Actif",
    suspended: "Suspendu",
  };
  return (
    <details className="access-audit">
      <summary>
        <strong>Historique des changements d’accès</strong>
      </summary>
      <p className="section-intro">
        Les changements sont horodatés et conservés : compte concerné, type
        d’accès et administrateur à l’origine de l’action.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Action</th>
              <th>Élément concerné</th>
              <th>Réalisée par</th>
            </tr>
          </thead>
          <tbody>
            {(data.accessAuditLogs || []).map((entry) => {
              const target = (data.profiles || []).find(
                  (item) => item.id === entry.entity_id,
                ),
                actor = (data.profiles || []).find(
                  (item) => item.id === entry.actor_id,
                ),
                oldStatus = String(entry.old_value?.access_status || ""),
                newStatus = String(entry.new_value?.access_status || ""),
                setName = String(
                  entry.new_value?.name || entry.old_value?.name || "",
                );
              return (
                <tr key={entry.id}>
                  <td>{frDate(entry.created_at, true)}</td>
                  <td>
                    {entry.action === "profile_access_updated"
                      ? oldStatus && newStatus && oldStatus !== newStatus
                        ? `${accessLabels[oldStatus] || oldStatus} → ${accessLabels[newStatus] || newStatus}`
                        : "Type d’accès modifié"
                      : entry.action === "permission_set_created"
                        ? "Type créé"
                        : entry.action === "permission_set_deleted"
                          ? "Type supprimé"
                          : "Type modifié"}
                  </td>
                  <td>{target?.display_name || setName || "Élément archivé"}</td>
                  <td>{actor?.display_name || "Action système"}</td>
                </tr>
              );
            })}
            {!(data.accessAuditLogs || []).length && (
              <tr>
                <td colSpan={4} className="empty">
                  Aucun changement d’accès enregistré.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </details>
  );
}

type CreateKind = "menu" | "client" | "quote" | "invoice" | "expense";
type QuoteCreationMode = "choice" | "free" | "guided" | "chantier";
type QuickQuoteLineDraft = {
  key: string;
  catalogItemId: string;
  reuseScope?: "catalog" | "client" | "one_off";
  description: string;
  quantity: number;
  unitPriceExVat: number;
  vatRate: number;
};
type ClientReferralAgreement = {
  id: string;
  client_id: string;
  referral_partner_id: string;
  commission_rate: number;
  pricing_protection_rate: number;
  active: boolean;
};
function AdminCreateModal({
  profile,
  data,
  initialKind = "menu",
  onClose,
  onMission,
  reload,
}: {
  profile: Profile;
  data: DataState;
  initialKind?: CreateKind;
  onClose: () => void;
  onMission: () => void;
  reload: () => void;
}) {
  const [chantier, setChantier] = useState(() => ({...chantierDefaults("milieu"), sourceAddress: [data.organization?.address, data.organization?.postal_code, data.organization?.city].filter(Boolean).join(" ")}));
  const [chantierReason, setChantierReason] = useState("");
  const [chantierSavedQuote, setChantierSavedQuote] = useState("");
  const [chantierPendingCost, setChantierPendingCost] = useState<Record<string, unknown> | null>(null);
  let chantierResult: ReturnType<typeof calculateChantier> | null = null;
  try { chantierResult = calculateChantier(chantier); } catch { /* Displayed in the wizard. */ }
  const [kind, setKind] = useState<CreateKind>(initialKind),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [quoteCreationMode, setQuoteCreationMode] =
      useState<QuoteCreationMode>("choice"),
    [clientName, setClientName] = useState(""),
    [clientCategory, setClientCategory] = useState(""),
    [clientAddress, setClientAddress] = useState(""),
    [clientContactFirstName, setClientContactFirstName] = useState(""),
    [clientContactLastName, setClientContactLastName] = useState(""),
    [clientContactEmail, setClientContactEmail] = useState(""),
    [clientContactPhone, setClientContactPhone] = useState(""),
    [quoteClient, setQuoteClient] = useState(data.clients[0]?.id || ""),
    [quoteNewClientName, setQuoteNewClientName] = useState(""),
    [quoteNewClientAddress, setQuoteNewClientAddress] = useState(""),
    [quoteNewClientFirstName, setQuoteNewClientFirstName] = useState(""),
    [quoteNewClientLastName, setQuoteNewClientLastName] = useState(""),
    [quoteNewClientEmail, setQuoteNewClientEmail] = useState(""),
    [quoteNewClientPhone, setQuoteNewClientPhone] = useState(""),
    [quotePrepareClientInvitation, setQuotePrepareClientInvitation] = useState(true),
    [quoteGuidedAnswers, setQuoteGuidedAnswers] = useState<Record<string, unknown>>({}),
    [quoteGuidedNotes, setQuoteGuidedNotes] = useState(""),
    [quoteGuidedDescriptionOverride, setQuoteGuidedDescriptionOverride] = useState(""),
    [quoteGuidedPriceOverride, setQuoteGuidedPriceOverride] = useState(""),
    [quoteGuidedOverrideReason, setQuoteGuidedOverrideReason] = useState(""),
    [quoteCatalogSearch, setQuoteCatalogSearch] = useState(""),
    [quoteCatalogItemId, setQuoteCatalogItemId] = useState(""),
    [quoteDescription, setQuoteDescription] = useState(""),
    [quoteQty, setQuoteQty] = useState(1),
    [quotePrice, setQuotePrice] = useState(0),
    [quoteAdditionalLines, setQuoteAdditionalLines] = useState<QuickQuoteLineDraft[]>([]),
    [quoteDiscountRate, setQuoteDiscountRate] = useState(0),
    [quoteDepositRate, setQuoteDepositRate] = useState(0),
    [quoteHasReferral, setQuoteHasReferral] = useState(false),
    [quoteReferralPartnerId, setQuoteReferralPartnerId] = useState(""),
    [quoteNewReferralPartnerName, setQuoteNewReferralPartnerName] = useState(""),
    [quoteReferralCommissionRate, setQuoteReferralCommissionRate] = useState(
      DEFAULT_REFERRAL_COMMISSION_RATE,
    ),
    [quoteReferralProtectionRate, setQuoteReferralProtectionRate] = useState(
      DEFAULT_REFERRAL_PRICING_PROTECTION_RATE,
    ),
    [quoteReferralPartners, setQuoteReferralPartners] = useState<ReferralPartner[]>([]),
    [quoteReferralAgreements, setQuoteReferralAgreements] = useState<ClientReferralAgreement[]>([]),
    [quoteReferralFeatureAvailable, setQuoteReferralFeatureAvailable] = useState(true),
    [quoteUseSourcing, setQuoteUseSourcing] = useState(false),
    [quoteSourcingWorker, setQuoteSourcingWorker] = useState(
      data.workers.find((worker) => worker.kind === "contractor" && worker.active)?.id || "",
    ),
    [quoteVat, setQuoteVat] = useState(
      data.organization?.default_vat_rate ?? 20,
    ),
    [quoteValidityDays, setQuoteValidityDays] = useState(30),
    [quotePaymentTermsMode, setQuotePaymentTermsMode] = useState<PaymentTermsMode>(
      resolvePaymentTerms(data.organization, data.clients[0]).mode,
    ),
    [quotePaymentTermsDays, setQuotePaymentTermsDays] = useState(
      resolvePaymentTerms(data.organization, data.clients[0]).days,
    ),
    [invoiceClient, setInvoiceClient] = useState(data.clients[0]?.id || ""),
    [invoiceDescription, setInvoiceDescription] = useState(""),
    [invoiceQty, setInvoiceQty] = useState(1),
    [invoicePrice, setInvoicePrice] = useState(0),
    [invoiceVat, setInvoiceVat] = useState(
      data.organization?.default_vat_rate ?? 20,
    ),
    [invoiceIssueOn, setInvoiceIssueOn] = useState(parisDay(new Date())),
    [invoicePaymentTermsMode, setInvoicePaymentTermsMode] =
      useState<PaymentTermsMode>(
        resolvePaymentTerms(data.organization, data.clients[0]).mode,
      ),
    [invoicePaymentTermsDays, setInvoicePaymentTermsDays] = useState(
      resolvePaymentTerms(data.organization, data.clients[0]).days,
    ),
    [quoteMaterialItemId, setQuoteMaterialItemId] = useState(""),
    [quoteMaterialTarget, setQuoteMaterialTarget] = useState(0),
    [quoteMaterialUnit, setQuoteMaterialUnit] = useState("m2"),
    [quoteMaterialPasses, setQuoteMaterialPasses] = useState(1),
    [spentOn, setSpentOn] = useState(parisDay(new Date())),
    [expenseDescription, setExpenseDescription] = useState(""),
    [expenseAmount, setExpenseAmount] = useState(0),
    [expenseVat, setExpenseVat] = useState(
      data.organization?.default_vat_rate ?? 20,
    ),
    [expenseCategory, setExpenseCategory] = useState("purchase"),
    [addToStock, setAddToStock] = useState(false),
    [stockItemId, setStockItemId] = useState(""),
    [newStockName, setNewStockName] = useState(""),
    [stockQuantity, setStockQuantity] = useState(1),
    [stockUnit, setStockUnit] = useState("unité");
  useEffect(() => {
    if (!supabase || kind !== "quote") return;
    let active = true;
    void Promise.all([
      supabase.from("referral_partners").select("*").eq("active", true).order("display_name"),
      supabase.from("client_referral_agreements").select("*").eq("active", true),
    ]).then(([partners, agreements]) => {
      if (!active) return;
      const unavailable = [partners.error, agreements.error].some(
        (error) => error && /does not exist|schema cache|relation/i.test(error.message),
      );
      setQuoteReferralFeatureAvailable(!unavailable);
      setQuoteReferralPartners(unavailable ? [] : ((partners.data || []) as ReferralPartner[]));
      setQuoteReferralAgreements(unavailable ? [] : ((agreements.data || []) as ClientReferralAgreement[]));
    });
    return () => { active = false; };
  }, [kind]);
  useEffect(() => {
    if (!quoteClient || !quoteReferralFeatureAvailable) return;
    const agreement = quoteReferralAgreements.find(
      (item) => item.client_id === quoteClient && item.active,
    );
    if (!agreement) {
      setQuoteHasReferral(false);
      setQuoteReferralPartnerId("");
      setQuoteNewReferralPartnerName("");
      setQuoteReferralCommissionRate(DEFAULT_REFERRAL_COMMISSION_RATE);
      setQuoteReferralProtectionRate(DEFAULT_REFERRAL_PRICING_PROTECTION_RATE);
      return;
    }
    setQuoteHasReferral(true);
    setQuoteReferralPartnerId(agreement.referral_partner_id);
    setQuoteNewReferralPartnerName("");
    setQuoteReferralCommissionRate(Number(agreement.commission_rate));
    setQuoteReferralProtectionRate(Number(agreement.pricing_protection_rate));
  }, [quoteClient, quoteReferralAgreements, quoteReferralFeatureAvailable]);
  const quoteCatalogItems = (data.serviceCatalogItems || [])
      .filter((item) => item.active)
      .filter((item) => {
        const query = quoteCatalogSearch.trim().toLocaleLowerCase("fr");
        return !query || `${item.item_code} ${item.name} ${item.description || ""}`.toLocaleLowerCase("fr").includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    publishedGuidedQuestionnaires = (data.serviceQualificationQuestionnaires || [])
      .filter((item) => item.status === "published")
      .sort((left, right) => right.version - left.version),
    guidedCatalogItems = quoteCatalogItems.filter((item) =>
      publishedGuidedQuestionnaires.some(
        (questionnaire) => questionnaire.catalog_item_id === item.id,
      ),
    ),
    guidedQuestionnaire = quoteCatalogItemId
      ? publishedGuidedQuestionnaires.find(
          (questionnaire) => questionnaire.catalog_item_id === quoteCatalogItemId,
        ) || null
      : null,
    guidedQuestions = guidedQuestionnaire
      ? (data.serviceQualificationQuestions || [])
          .filter(
            (question) => question.questionnaire_id === guidedQuestionnaire.id,
          )
          .sort((left, right) => left.position - right.position)
      : [],
    guidedCostingProfile = quoteCatalogItemId
      ? (data.serviceCostingProfiles || [])
          .filter(
            (item) =>
              item.catalog_item_id === quoteCatalogItemId && item.active,
          )
          .sort((left, right) => right.version - left.version)[0] || null
      : null,
    guidedCostComponents = guidedCostingProfile
      ? (data.serviceCostingComponents || []).filter(
          (item) => item.profile_id === guidedCostingProfile.id,
        )
      : [],
    guidedCatalogItem = guidedCatalogItems.find(
      (item) => item.id === quoteCatalogItemId,
    ),
    guidedEvaluation: QuoteGuidanceEvaluation | null =
      quoteCreationMode === "guided" && guidedCatalogItem && guidedQuestionnaire
        ? evaluateQuoteGuidance({
            catalogItem: guidedCatalogItem,
            questions: guidedQuestions,
            answers: quoteGuidedAnswers,
            costingProfile: guidedCostingProfile,
            costComponents: guidedCostComponents,
            inventoryItems: data.inventoryItems || [],
            catalogItems: data.serviceCatalogItems || [],
            catalogPriceOverrides: Object.fromEntries(
              data.services
                .filter(
                  (service) =>
                    service.client_id === quoteClient &&
                    service.catalog_item_id &&
                    service.active !== false,
                )
                .map((service) => [
                  String(service.catalog_item_id),
                  Number(service.unit_price_ex_vat),
                ]),
            ),
            catalogVatOverrides: Object.fromEntries(
              data.services
                .filter(
                  (service) =>
                    service.client_id === quoteClient &&
                    service.catalog_item_id &&
                    service.active !== false,
                )
                .map((service) => [
                  String(service.catalog_item_id),
                  Number(service.vat_rate),
                ]),
            ),
            clientServiceOverrides: Object.fromEntries(
              data.services
                .filter(
                  (service) =>
                    service.client_id === quoteClient &&
                    service.active !== false,
                )
                .map((service) => [
                  service.name.trim().toLocaleLowerCase("fr"),
                  {
                    unitPriceExVat: Number(service.unit_price_ex_vat),
                    vatRate: Number(service.vat_rate),
                  },
                ]),
            ),
            defaultTargetMode:
              data.organization?.profitability_target_mode || "margin",
            defaultTargetRate:
              data.organization?.profitability_target_rate ??
              data.organization?.target_margin_rate ??
              40,
          })
        : null,
    guidedRecommendedPrice = guidedEvaluation?.recommendedTotalExVat ?? null,
    guidedSuggestedDescription = guidedCatalogItem
      ? [
          guidedCatalogItem.description || guidedCatalogItem.name,
          ...(guidedEvaluation?.descriptionLines || []),
        ]
          .filter(Boolean)
          .join("\n")
      : "",
    guidedRetainedDescription =
      quoteGuidedDescriptionOverride.trim() || guidedSuggestedDescription,
    guidedRetainedPrice = quoteGuidedPriceOverride.trim()
      ? Number(quoteGuidedPriceOverride)
      : guidedRecommendedPrice,
    guidedPriceChanged =
      guidedRecommendedPrice != null &&
      guidedRetainedPrice != null &&
      Math.abs(guidedRetainedPrice - guidedRecommendedPrice) > 0.005,
    guidedLineQuantity =
      guidedCatalogItem?.default_billing_mode === "per_unit"
        ? guidedEvaluation?.outputQuantity || 1
        : 1,
    guidedUnitPrice =
      guidedRetainedPrice == null || guidedLineQuantity <= 0
        ? 0
        : guidedRetainedPrice / guidedLineQuantity,
    guidedComponentLines: QuickQuoteLineDraft[] =
      guidedEvaluation?.lineItems.map((item) => ({
        key: `guided-component-${item.sourceRuleId}`,
        catalogItemId: item.catalogItemId || "",
        reuseScope: item.mode,
        description: item.name,
        quantity: item.quantity,
        unitPriceExVat: item.unitPriceExVat,
        vatRate: item.vatRate,
      })) || [],
    guidedComponentTotal = guidedComponentLines.reduce(
      (total, line) => total + line.quantity * line.unitPriceExVat,
      0,
    ),
    guidedPrimaryTotal = Math.max(
      0,
      Number(guidedRetainedPrice || 0) - guidedComponentTotal,
    ),
    quoteBaseDraftLines: QuickQuoteLineDraft[] = quoteCreationMode === "chantier" ? [{
      key: "primary", catalogItemId: "", reuseScope: "one_off",
      description: chantierResult ? chantierDescription(chantier) : "Chantier à compléter",
      quantity: 1, unitPriceExVat: chantierResult?.price || 0, vatRate: quoteVat,
    }] : [
      {
        key: "primary",
        catalogItemId:
          quoteCreationMode === "guided" ? "" : quoteCatalogItemId,
        reuseScope: quoteCreationMode === "guided" ? "one_off" : undefined,
        description:
          quoteCreationMode === "guided"
            ? guidedRetainedDescription
            : quoteDescription,
        quantity:
          quoteCreationMode === "guided" ? guidedLineQuantity : quoteQty,
        unitPriceExVat:
          quoteCreationMode === "guided"
            ? guidedPrimaryTotal / Math.max(guidedLineQuantity, 1)
            : quotePrice,
        vatRate: quoteVat,
      },
      ...(quoteCreationMode === "guided" ? guidedComponentLines : []),
      ...(quoteCreationMode === "free" ? quoteAdditionalLines : []),
    ],
    quoteBaseGrossTotal = quoteBaseDraftLines.reduce(
      (total, line) => total + line.quantity * line.unitPriceExVat,
      0,
    ),
    quoteReferralSummary = calculateReferralCommission({
      baseTotalExVat: quoteBaseGrossTotal,
      discountRate: quoteCreationMode === "chantier" ? 0 : quoteDiscountRate,
      commissionRate: quoteReferralCommissionRate,
      pricingProtectionRate: quoteReferralProtectionRate,
    }),
    quoteReferralFactor = quoteHasReferral
      ? 1 + quoteReferralProtectionRate / 100
      : 1,
    quoteDraftLines: QuickQuoteLineDraft[] = quoteBaseDraftLines.map((line) => ({
      ...line,
      unitPriceExVat: quoteCreationMode === "chantier"
        ? Math.round(
            (line.unitPriceExVat * quoteReferralFactor + Number.EPSILON) * 100,
          ) / 100
        : line.unitPriceExVat * quoteReferralFactor,
    })),
    quoteYieldItems = (data.inventoryItems || []).filter(
      (item) =>
        item.active &&
        item.item_kind !== "kit" &&
        item.purchase_package_quantity &&
        item.purchase_package_price_ex_vat != null &&
        item.yield_material_quantity &&
        item.yield_output_quantity &&
        item.yield_output_unit,
    ),
    quoteMaterialItem = quoteYieldItems.find((item) => item.id === quoteMaterialItemId),
    quoteMaterialEstimate = calculateMaterialEstimate(
      quoteMaterialItem,
      quoteMaterialTarget,
      quoteMaterialUnit,
      quoteMaterialPasses,
    ),
    quoteGrossTotal = quoteDraftLines.reduce(
      (total, line) => total + line.quantity * line.unitPriceExVat,
      0,
    ),
    quoteDiscountAmount = quoteCreationMode === "chantier" ? 0 : quoteGrossTotal * quoteDiscountRate / 100,
    quoteLineTotal = quoteGrossTotal - quoteDiscountAmount,
    quoteVatAmount = quoteDraftLines.reduce(
      (total, line) =>
        total +
        line.quantity *
          line.unitPriceExVat *
          (1 - (quoteCreationMode === "chantier" ? 0 : quoteDiscountRate) / 100) *
          (line.vatRate / 100),
      0,
    ),
    quoteDepositAmount = (quoteLineTotal + quoteVatAmount) * quoteDepositRate / 100,
    quotePrimaryLineTotal =
      quoteQty * quotePrice * quoteReferralFactor * (1 - quoteDiscountRate / 100),
    materialMarginWarning =
      quoteMaterialEstimate?.minimumMaterialSalePrice != null &&
      quotePrimaryLineTotal < quoteMaterialEstimate.minimumMaterialSalePrice;
  function selectPrimaryQuoteCatalogItem(catalogItemId: string) {
    const catalogItem = (data.serviceCatalogItems || []).find(
      (item) => item.id === catalogItemId,
    );
    setQuoteCatalogItemId(catalogItemId);
    if (!catalogItem) return;
    setQuoteDescription(catalogItem.name);
    setQuotePrice(Number(catalogItem.default_unit_price_ex_vat || 0));
    setQuoteVat(Number(catalogItem.default_vat_rate || 0));
  }
  function selectGuidedCatalogItem(catalogItemId: string) {
    selectPrimaryQuoteCatalogItem(catalogItemId);
    setQuoteGuidedAnswers({});
    setQuoteGuidedNotes("");
    setQuoteGuidedDescriptionOverride("");
    setQuoteGuidedPriceOverride("");
    setQuoteGuidedOverrideReason("");
    setQuoteAdditionalLines([]);
  }
  function setGuidedAnswer(questionKey: string, value: unknown) {
    setQuoteGuidedAnswers((current) => ({ ...current, [questionKey]: value }));
  }
  function quoteCatalogOptions(selectedId: string) {
    const selected = (data.serviceCatalogItems || []).find(
      (item) => item.id === selectedId && item.active,
    );
    return selected && !quoteCatalogItems.some((item) => item.id === selected.id)
      ? [selected, ...quoteCatalogItems]
      : quoteCatalogItems;
  }
  function addQuoteLine() {
    setQuoteAdditionalLines((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        catalogItemId: "",
        description: "",
        quantity: 1,
        unitPriceExVat: 0,
        vatRate: data.organization?.default_vat_rate ?? 20,
      },
    ]);
  }
  function updateQuoteLine(
    key: string,
    patch: Partial<QuickQuoteLineDraft>,
  ) {
    setQuoteAdditionalLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }
  function selectAdditionalQuoteCatalogItem(key: string, catalogItemId: string) {
    const catalogItem = (data.serviceCatalogItems || []).find(
      (item) => item.id === catalogItemId,
    );
    updateQuoteLine(
      key,
      catalogItem
        ? {
            catalogItemId,
            description: catalogItem.name,
            unitPriceExVat: Number(catalogItem.default_unit_price_ex_vat || 0),
            vatRate: Number(catalogItem.default_vat_rate || 0),
          }
        : { catalogItemId },
    );
  }
  async function saveChantierProductEstimates(quoteLineId: string) {
    if (chantier.productUseMode !== "referenced") return null;
    for (const product of chantier.referencedProducts) {
      const estimate = await supabase!.rpc("save_quote_material_estimate", {
        p_quote_line_id: quoteLineId,
        p_inventory_item_id: product.inventoryItemId,
        p_target_quantity: product.targetQuantity,
        p_target_unit: product.targetUnit,
        p_application_passes: product.applicationPasses,
      });
      if (estimate.error) return estimate.error as { message: string };
    }
    return null;
  }
  async function createClient(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase!.from("clients").insert({
      organization_id: profile.organization_id,
      name: clientName,
      category: clientCategory || null,
      address: clientAddress || null,
      contact_first_name: clientContactFirstName.trim() || null,
      contact_last_name: clientContactLastName.trim() || null,
      contact_email: clientContactEmail.trim().toLowerCase() || null,
      contact_phone: clientContactPhone.trim() || null,
      billing_emission:
        data.organization?.default_billing_emission || "month_end",
      billing_grouping:
        data.organization?.default_billing_grouping || "grouped",
      active: true,
    });
    setBusy(false);
    setMessage(error ? error.message : "Client créé.");
    if (!error) {
      reload();
      setTimeout(onClose, 500);
    }
  }
  async function createQuote(e: React.FormEvent) {
    e.preventDefault();
    if (quoteCreationMode === "choice") {
      setMessage("Choisissez un devis libre ou un devis guidé.");
      return;
    }
    if (quoteCreationMode === "chantier") {
      if (chantierSavedQuote) { setMessage("Ce brouillon est déjà créé. Retrouvez-le dans les devis pour poursuivre son contrôle."); return; }
      const errors = chantierSubmissionErrors(chantier);
      if (profile.role !== "admin") errors.push("Création réservée à l’administrateur.");
      if (chantierResult && (chantierResult.margin < 40 || chantierResult.price < chantierResult.floor) && !chantierReason.trim()) errors.push("Justifiez la dérogation au seuil de marque ou au plancher commercial.");
      if (errors.length || !chantierResult) { setMessage(errors.join(" ")); return; }
    }
    if (quoteCreationMode === "guided") {
      if (!guidedQuestionnaire || !guidedCatalogItem || !guidedEvaluation) {
        setMessage("Choisissez un sujet de devis automatisé.");
        return;
      }
      if (guidedEvaluation.missingRequired.length) {
        const labels = guidedQuestions
          .filter((question) =>
            guidedEvaluation.missingRequired.includes(question.question_key),
          )
          .map((question) => question.label);
        setMessage(`Complétez les informations obligatoires : ${labels.join(", ")}.`);
        return;
      }
      if (guidedEvaluation.validationErrors.length) {
        setMessage(guidedEvaluation.validationErrors.join(" "));
        return;
      }
      if (!guidedEvaluation.canPrepareDraft || guidedRetainedPrice == null) {
        setMessage(
          "Le calcul ne dispose pas encore d’un prix explicable. Complétez ou corrigez les règles du sujet avant de préparer le brouillon.",
        );
        return;
      }
      if (!Number.isFinite(guidedRetainedPrice) || guidedRetainedPrice < 0) {
        setMessage("Le prix HT retenu pour le brouillon est invalide.");
        return;
      }
      if (guidedPriceChanged && !quoteGuidedOverrideReason.trim()) {
        setMessage(
          "Expliquez pourquoi le prix retenu diffère de la recommandation calculée.",
        );
        return;
      }
    }
    if (!quoteClient && (!quoteNewClientName.trim() || !quoteNewClientAddress.trim() || !quoteNewClientFirstName.trim() || !quoteNewClientLastName.trim() || !quoteNewClientEmail.trim() || !quoteNewClientPhone.trim())) {
      setMessage("Pour un nouveau client, la raison sociale ou le nom, l’adresse, le prénom, le nom, l’e-mail et le téléphone du contact sont obligatoires.");
      return;
    }
    if (quoteUseSourcing && !quoteSourcingWorker) {
      setMessage("Choisissez le sous-traitant qui doit chiffrer cette demande.");
      return;
    }
    if (
      quoteHasReferral &&
      (!quoteReferralPartnerId && !quoteNewReferralPartnerName.trim())
    ) {
      setMessage("Choisissez un apporteur d’affaires ou renseignez son nom.");
      return;
    }
    if (
      quoteHasReferral &&
      (!Number.isFinite(quoteReferralCommissionRate) ||
        quoteReferralCommissionRate <= 0 ||
        quoteReferralCommissionRate >= 100 ||
        !Number.isFinite(quoteReferralProtectionRate) ||
        quoteReferralProtectionRate <
          Number(quoteReferralSummary.exactNeutralProtectionRate || 0))
    ) {
      setMessage(
        `La protection tarifaire doit couvrir la commission. Minimum neutre : ${quoteReferralSummary.exactNeutralProtectionRate?.toLocaleString("fr-FR")} %.`,
      );
      return;
    }
    if (
      quoteDraftLines.some(
        (line) =>
          !line.description.trim() ||
          !Number.isFinite(line.quantity) ||
          line.quantity <= 0 ||
          !Number.isFinite(line.unitPriceExVat) ||
          line.unitPriceExVat < 0 ||
          !Number.isFinite(line.vatRate) ||
          line.vatRate < 0 ||
          line.vatRate > 100,
      )
    ) {
      setMessage("Chaque ligne doit avoir une description, une quantité positive, un prix HT positif ou nul et une TVA comprise entre 0 et 100 %.");
      return;
    }
    setBusy(true);
    setMessage("");
    const createsNewClient = !quoteClient;
    let resolvedClientId = quoteClient;
    if (!resolvedClientId) {
      const clientCreated = await supabase!
        .from("clients")
        .insert({
          organization_id: profile.organization_id,
          name: quoteNewClientName.trim(),
          address: quoteNewClientAddress.trim(),
          contact_first_name: quoteNewClientFirstName.trim(),
          contact_last_name: quoteNewClientLastName.trim(),
          contact_email: quoteNewClientEmail.trim().toLowerCase(),
          contact_phone: quoteNewClientPhone.trim(),
          billing_emission:
            data.organization?.default_billing_emission || "month_end",
          billing_grouping:
            data.organization?.default_billing_grouping || "grouped",
          active: true,
        })
        .select("id")
        .single();
      if (clientCreated.error) {
        setBusy(false);
        setMessage(clientCreated.error.message);
        return;
      }
      resolvedClientId = clientCreated.data.id;
      setQuoteClient(resolvedClientId);
    }
    let resolvedReferralPartnerId = quoteReferralPartnerId;
    if (quoteHasReferral && !resolvedReferralPartnerId) {
      if (isShowcaseMode) {
        resolvedReferralPartnerId = crypto.randomUUID();
        const partner = await supabase!.from("referral_partners").insert({
          id: resolvedReferralPartnerId,
          organization_id: profile.organization_id,
          display_name: quoteNewReferralPartnerName.trim(),
          default_commission_rate: quoteReferralCommissionRate,
          default_pricing_protection_rate: quoteReferralProtectionRate,
          active: true,
          created_by: profile.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        if (partner.error) {
          setBusy(false);
          setMessage(partner.error.message);
          return;
        }
      } else {
        const partner = await supabase!.rpc("save_referral_partner", {
          p_id: null,
          p_display_name: quoteNewReferralPartnerName.trim(),
          p_company_name: null,
          p_email: null,
          p_phone: null,
          p_default_commission_rate: quoteReferralCommissionRate,
          p_default_pricing_protection_rate: quoteReferralProtectionRate,
          p_notes: "Créé lors de la préparation d’un devis.",
          p_active: true,
        });
        if (partner.error) {
          setBusy(false);
          setMessage(partner.error.message);
          return;
        }
        resolvedReferralPartnerId = String(partner.data || "");
      }
    }
    const number = `DEV-${Date.now().toString().slice(-8)}`,
      issuedOn = parisDay(new Date()),
      validUntil = new Date(`${issuedOn}T12:00:00`),
      ht = quoteLineTotal,
      tva = quoteVatAmount;
    validUntil.setDate(validUntil.getDate() + (quoteCreationMode === "chantier" ? 30 : quoteValidityDays));
    const created = await supabase!
      .from("quotes")
      .insert({
        organization_id: profile.organization_id,
        client_id: resolvedClientId,
        number,
        issued_on: issuedOn,
        valid_until: parisDay(validUntil),
        payment_terms_mode: quoteCreationMode === "chantier" ? "days_after_issue" : quotePaymentTermsMode,
        payment_terms_days: quoteCreationMode === "chantier" ? 15 : quotePaymentTermsDays,
        discount_rate: quoteCreationMode === "chantier" ? 0 : quoteDiscountRate,
        deposit_rate: quoteCreationMode === "chantier" ? (chantier.profile === "standard" || ht >= 2000 ? 50 : 0) : quoteDepositRate,
        status: "draft",
        total_ex_vat: ht,
        total_vat: tva,
        total_inc_vat: ht + tva,
        created_by: profile.id,
      })
      .select("id")
      .single();
    let lineError = null as { message: string } | null,
      estimateError = null as { message: string } | null,
      sourcingError = null as { message: string } | null,
      invitationError = null as { message: string } | null,
      guidedError = null as { message: string } | null,
      referralError = null as { message: string } | null;
    if (!created.error) {
      if (quoteCreationMode === "chantier") setChantierSavedQuote(created.data.id);
      const resolvedServiceIds = new Map<string, string | null>();
      for (const line of quoteDraftLines) {
        let serviceId: string | null = null;
        if (line.reuseScope === "client") {
          const existing = data.services.find(
            (service) =>
              service.client_id === resolvedClientId &&
              service.active !== false &&
              service.name.trim().toLocaleLowerCase("fr") ===
                line.description.trim().toLocaleLowerCase("fr"),
          );
          if (existing) serviceId = existing.id;
          else {
            const service = await supabase!
              .from("client_services")
              .insert({
                organization_id: profile.organization_id,
                client_id: resolvedClientId,
                name: line.description.trim(),
                billing_mode: "fixed",
                unit_price_ex_vat: line.unitPriceExVat,
                vat_rate: line.vatRate,
                default_duration_hours: null,
                active: true,
              })
              .select("id")
              .single();
            if (service.error) {
              lineError = service.error;
              break;
            }
            serviceId = service.data.id;
          }
        } else if (line.catalogItemId) {
          const existing = data.services.find(
            (service) =>
              service.client_id === resolvedClientId &&
              service.active !== false &&
              service.catalog_item_id === line.catalogItemId,
          );
          if (existing) serviceId = existing.id;
          else {
            const catalogItem = (data.serviceCatalogItems || []).find(
              (item) => item.id === line.catalogItemId,
            );
            if (catalogItem) {
              const service = await supabase!.rpc("apply_service_catalog_item", {
                p_catalog_item_id: catalogItem.id,
                p_client_id: resolvedClientId,
                p_property_id: null,
                p_name: catalogItem.name,
                p_billing_mode: catalogItem.default_billing_mode,
                p_unit_price_ex_vat: line.unitPriceExVat,
                p_vat_rate: line.vatRate,
                p_default_duration_hours: catalogItem.default_duration_hours,
                p_notes: "Créée depuis un devis guidé pour conserver l’analyse par prestation.",
              });
              if (service.error) {
                lineError = service.error;
                break;
              }
              serviceId = String(service.data);
            }
          }
        }
        resolvedServiceIds.set(line.key, serviceId);
      }
      const lineRows = quoteDraftLines.map((line, index) => ({
          quote_id: created.data.id,
          service_id: resolvedServiceIds.get(line.key) || null,
          description: line.description.trim(),
          quantity: line.quantity,
          unit_price_ex_vat: line.unitPriceExVat,
          vat_rate: line.vatRate,
          position: index + 1,
        })),
        lines = lineError
          ? { data: null, error: lineError }
          : await supabase!
              .from("quote_lines")
              .insert(lineRows)
              .select("id,position");
      lineError = lines.error;
      if (
        !lines.error &&
        quoteHasReferral &&
        resolvedReferralPartnerId &&
        quoteReferralFeatureAvailable
      ) {
        if (isShowcaseMode) {
          const partner = quoteReferralPartners.find(
            (item) => item.id === resolvedReferralPartnerId,
          );
          const referral = await supabase!
            .from("quote_referral_commissions")
            .insert({
              id: crypto.randomUUID(),
              organization_id: profile.organization_id,
              quote_id: created.data.id,
              client_id: resolvedClientId,
              referral_partner_id: resolvedReferralPartnerId,
              partner_name_snapshot:
                partner?.display_name || quoteNewReferralPartnerName.trim(),
              commission_rate: quoteReferralCommissionRate,
              pricing_protection_rate: quoteReferralProtectionRate,
              base_total_ex_vat: quoteReferralSummary.baseAfterDiscountExVat,
              protected_total_ex_vat: quoteLineTotal,
              estimated_commission_ex_vat:
                quoteReferralSummary.estimatedCommissionExVat,
              created_by: profile.id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          referralError = referral.error;
        } else {
          const referral = await supabase!.rpc(
            "save_quote_referral_commission",
            {
              p_quote_id: created.data.id,
              p_referral_partner_id: resolvedReferralPartnerId,
              p_commission_rate: quoteReferralCommissionRate,
              p_pricing_protection_rate: quoteReferralProtectionRate,
              p_base_total_ex_vat:
                quoteReferralSummary.baseAfterDiscountExVat,
              p_protected_total_ex_vat: quoteLineTotal,
            },
          );
          referralError = referral.error;
        }
      }
      if (!lines.error && quoteUseSourcing) {
        for (const insertedLine of lines.data || []) {
          const draftLine = quoteDraftLines[Number(insertedLine.position || 1) - 1],
            lineTarget = draftLine
              ? draftLine.quantity *
                draftLine.unitPriceExVat *
                (1 - quoteDiscountRate / 100)
              : 0,
            sourcing = await supabase!.rpc("create_quote_sourcing_workflow", {
              p_quote_id: created.data.id,
              p_quote_line_id: insertedLine.id,
              p_worker_id: quoteSourcingWorker,
              p_client_target_price_ex_vat: lineTarget > 0 ? lineTarget : null,
            });
          if (sourcing.error) {
            sourcingError = sourcing.error;
            break;
          }
        }
      }
      const primaryLine = (lines.data || []).find(
        (line) => Number(line.position) === 1,
      );
      if (!lines.error && primaryLine && quoteCreationMode === "chantier" && chantierResult) {
        const referralSnapshot = quoteHasReferral ? {
          commissionRate: quoteReferralCommissionRate,
          pricingProtectionRate: quoteReferralProtectionRate,
          baseTotalExVat: quoteReferralSummary.baseAfterDiscountExVat,
          protectedTotalExVat: quoteReferralSummary.protectedTotalExVat,
          estimatedCommissionExVat: quoteReferralSummary.estimatedCommissionExVat,
        } : null;
        const params = {
          ...chantierCostingParams(chantier, quoteLineTotal),
          p_quote_line_id: primaryLine.id,
          p_notes: JSON.stringify(chantierSnapshot(chantier, chantierReason, profile.id, referralSnapshot)),
        };
        estimateError = await saveChantierProductEstimates(primaryLine.id);
        if (estimateError) setChantierPendingCost(params);
        else {
          const costing = await supabase!.rpc("create_quote_costing_run", params);
          if (costing.error) { guidedError = costing.error; setChantierPendingCost(params); }
        }
      }
      if (
        !lines.error &&
        primaryLine &&
        quoteCreationMode === "guided" &&
        guidedQuestionnaire &&
        guidedEvaluation &&
        guidedCatalogItem
      ) {
        const qualification = await supabase!.rpc(
          "evaluate_quote_line_qualification",
          {
            p_quote_line_id: primaryLine.id,
            p_questionnaire_id: guidedQuestionnaire.id,
            p_answers: quoteGuidedAnswers,
            p_notes:
              [
                quoteGuidedNotes.trim(),
                guidedPriceChanged
                  ? `Prix ajusté manuellement : ${quoteGuidedOverrideReason.trim()}`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ") || null,
          },
        );
        if (qualification.error) guidedError = qualification.error;
        else {
          const costing = await supabase!.rpc("create_quote_costing_run", {
            p_quote_line_id: primaryLine.id,
            p_catalog_item_id: guidedCatalogItem.id,
            p_output_quantity: guidedEvaluation.outputQuantity,
            p_output_unit: guidedEvaluation.outputUnit,
            p_total_labor_hours: guidedEvaluation.totalLaborHours,
            p_labor_cost_per_hour:
              guidedCostingProfile?.default_labor_cost_per_hour ?? null,
            p_team_size: Number(
              guidedEvaluation.derivedInputs.team_size ??
                guidedCostingProfile?.default_team_size ??
                1,
            ),
            p_travel_cost_ex_vat: Number(
              guidedEvaluation.derivedInputs.travel_cost_ex_vat ??
                guidedCostingProfile?.default_travel_cost_ex_vat ??
                0,
            ),
            p_other_cost_ex_vat: Number(
              guidedEvaluation.derivedInputs.other_cost_ex_vat || 0,
            ) + Number(
              guidedEvaluation.derivedInputs.material_cost_ex_vat || 0,
            ),
            p_contingency_rate: Number(
              guidedEvaluation.derivedInputs.contingency_rate ??
                guidedCostingProfile?.contingency_rate ??
                10,
            ),
            p_target_mode:
              guidedCostingProfile?.target_mode ||
              data.organization?.profitability_target_mode ||
              "margin",
            p_target_rate: Number(
              guidedCostingProfile?.target_rate ??
                guidedCatalogItem.target_margin_rate ??
                data.organization?.profitability_target_rate ??
                40,
            ),
            p_proposed_total_ex_vat:
              guidedRetainedPrice == null
                ? null
                : guidedRetainedPrice * quoteReferralFactor,
            p_notes: `Préparé depuis ${guidedQuestionnaire.name} v${guidedQuestionnaire.version}. Validation manuelle obligatoire avant envoi.`,
          });
          if (costing.error) guidedError = costing.error;
          else {
            const linked = await supabase!.rpc(
              "link_quote_qualification_costing",
              {
                p_qualification_run_id: qualification.data,
                p_costing_run_id: costing.data,
              },
            );
            if (linked.error) guidedError = linked.error;
          }
        }
      }
      if (!lines.error && primaryLine && quoteMaterialItemId && quoteMaterialTarget > 0) {
        const estimate = await supabase!.rpc("save_quote_material_estimate", {
          p_quote_line_id: primaryLine.id,
          p_inventory_item_id: quoteMaterialItemId,
          p_target_quantity: quoteMaterialTarget,
          p_target_unit: quoteMaterialUnit,
          p_application_passes: quoteMaterialPasses,
        });
        estimateError = estimate.error;
      }
      if (!lines.error && !estimateError && !sourcingError && createsNewClient && quotePrepareClientInvitation) {
        const invitation = await supabase!.rpc("prepare_client_invitation_from_quote", {
          p_quote_id: created.data.id,
        });
        invitationError = invitation.error;
      }
    }
    setBusy(false);
    let successMessage = quoteMaterialItemId
      ? "Devis brouillon créé avec son estimation matière figée."
      : quoteCreationMode === "chantier"
        ? "Devis Chantier créé en brouillon avec son calcul et ses hypothèses internes. À relire au bureau avant envoi."
      : quoteCreationMode === "guided"
        ? guidedEvaluation?.requiresCostReview
          ? "Devis guidé transformé en brouillon. Le tarif qualitatif est calculé, mais le coût et la marge restent À contrôler avant tout envoi."
          : "Devis guidé transformé en brouillon avec ses réponses, règles et hypothèses de coût figées. Une relecture humaine reste obligatoire avant tout envoi."
      : quoteUseSourcing
        ? `Devis brouillon créé avec ${quoteDraftLines.length} ligne(s) et demande(s) de chiffrage transmise(s) au ST.`
        : `Devis brouillon créé avec ${quoteDraftLines.length} ligne(s).`;
    if (quoteHasReferral && !referralError) {
      successMessage += ` Coût apporteur interne enregistré : ${quoteReferralCommissionRate.toLocaleString("fr-FR")} % du CA HT ; protection tarifaire ${quoteReferralProtectionRate.toLocaleString("fr-FR")} %.`;
    }
    if (createsNewClient && quotePrepareClientInvitation) {
      successMessage += invitationError
        ? ` La fiche client est créée, mais l’invitation n’a pas pu être préparée : ${invitationError.message}`
        : " L’invitation d’ouverture de compte est préparée dans la fiche client ; aucun e-mail n’a encore été envoyé.";
    }
    setMessage(created.error
      ? created.error.message
      : lineError
        ? `Devis créé, mais sa ligne n’a pas pu être ajoutée : ${lineError.message}`
        : sourcingError
          ? `Devis brouillon créé, mais le circuit ST n’a pas pu être activé : ${sourcingError.message}`
          : guidedError
            ? `Devis brouillon créé, mais son dossier de calcul doit être contrôlé : ${guidedError.message}`
          : referralError
            ? `Devis brouillon créé, mais le coût apporteur interne doit être contrôlé : ${referralError.message}`
          : estimateError
            ? `Devis créé, mais l’estimation matière n’a pas été enregistrée : ${estimateError.message}`
            : successMessage);
    if (!created.error && !lineError && !estimateError && !sourcingError && !referralError) {
      reload();
      if (quoteCreationMode !== "chantier" || !guidedError) setTimeout(onClose, invitationError || guidedError ? 1_500 : 800);
    }
  }
  async function createInvoice(e: React.FormEvent) {
    e.preventDefault();
    if (!invoiceClient || !invoiceDescription.trim() || invoiceQty <= 0) {
      setMessage("Le client, la description et une quantité positive sont obligatoires.");
      return;
    }
    setBusy(true);
    setMessage("");
    const client = data.clients.find((item) => item.id === invoiceClient),
      ht = invoiceQty * invoicePrice,
      tva = (ht * invoiceVat) / 100,
      dueOn = paymentDueOn(
        invoiceIssueOn,
        invoicePaymentTermsMode,
        invoicePaymentTermsDays,
      ),
      created = await supabase!
        .from("invoices")
        .insert({
          organization_id: profile.organization_id,
          client_id: invoiceClient,
          number: null,
          billing_period: `${invoiceIssueOn.slice(0, 7)}-01`,
          grouping:
            client?.billing_grouping === "individual" ? "individual" : "grouped",
          document_kind: "standard",
          status: "draft",
          issue_on: invoiceIssueOn,
          due_on: dueOn,
          payment_terms_mode: invoicePaymentTermsMode,
          payment_terms_days: invoicePaymentTermsDays,
          total_ex_vat: ht,
          total_vat: tva,
          total_inc_vat: ht + tva,
        })
        .select("id")
        .single();
    let lineError = null as { message: string } | null;
    if (!created.error) {
      const line = await supabase!.from("invoice_lines").insert({
        invoice_id: created.data.id,
        description: invoiceDescription.trim(),
        quantity: invoiceQty,
        unit_price_ex_vat: invoicePrice,
        vat_rate: invoiceVat,
        position: 1,
      });
      lineError = line.error;
      if (!line.error) {
        await supabase!.from("commercial_document_events").insert({
          organization_id: profile.organization_id,
          invoice_id: created.data.id,
          event_type: "created",
          status_to: "draft",
          note: "Facture brouillon créée manuellement.",
        });
      }
    }
    setBusy(false);
    setMessage(
      created.error
        ? created.error.message
        : lineError
          ? `Le brouillon a été créé, mais sa première ligne doit être ajoutée depuis sa fiche : ${lineError.message}`
          : "Facture brouillon créée. Elle doit être relue avant toute préparation ou émission dans Henrri.",
    );
    if (!created.error && !lineError) {
      reload();
      setTimeout(onClose, 650);
    }
  }
  async function createExpense(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const ht = expenseAmount / (1 + expenseVat / 100);
    let resolvedStockItemId = stockItemId;
    if (addToStock && !resolvedStockItemId) {
      if (!newStockName.trim()) {
        setBusy(false);
        setMessage("Choisissez un article ou donnez un nom au nouvel article.");
        return;
      }
      const item = await supabase!
        .from("inventory_items")
        .insert({
          organization_id: profile.organization_id,
          name: newStockName.trim(),
          unit: stockUnit.trim() || "unité",
          minimum_quantity: 0,
          active: true,
        })
        .select("id")
        .single();
      if (item.error) {
        setBusy(false);
        setMessage(item.error.message);
        return;
      }
      resolvedStockItemId = item.data.id;
    }
    const { error } = await supabase!.from("expense_reports").insert({
      organization_id: profile.organization_id,
      owner_profile_id: profile.id,
      spent_on: spentOn,
      category: expenseCategory,
      description: expenseDescription,
      payment_method: "company_card",
      amount_inc_vat: expenseAmount,
      amount_ex_vat: ht,
      vat_rate: expenseVat,
      vat_amount: expenseAmount - ht,
      status: "approved",
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
      stock_item_id: addToStock ? resolvedStockItemId || null : null,
      stock_quantity: addToStock ? stockQuantity : null,
      stock_unit_cost:
        addToStock && stockQuantity > 0 ? ht / stockQuantity : null,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : addToStock
          ? "Dépense enregistrée et stock mis à jour."
          : "Dépense enregistrée.",
    );
    if (!error) {
      reload();
      setTimeout(onClose, 500);
    }
  }
  return (
    <div className="portal-overlay" role="dialog" aria-modal="true">
      <section className="portal-detail create-modal">
        <div className="portal-detail-head">
          <div>
            <span className="eyebrow">Action rapide</span>
            <h2>
              {kind === "menu"
                ? "Créer"
                : kind === "client"
                  ? "Nouveau client"
                  : kind === "quote"
                    ? quoteCreationMode === "choice"
                      ? "Créer un devis"
                      : quoteCreationMode === "chantier" ? "Devis guidé Chantier" : quoteCreationMode === "guided"
                        ? "Créer un devis guidé"
                        : "Devis libre"
                    : kind === "invoice"
                      ? "Nouvelle facture"
                      : "Nouvelle note de frais"}
            </h2>
          </div>
          <button className="icon-close" onClick={onClose}>
            ×
          </button>
        </div>
        {kind === "menu" && (
          <div className="create-grid">
            <button
              onClick={() => {
                onClose();
                onMission();
              }}
            >
              <b>Mission</b>
              <small>Planifier et affecter</small>
            </button>
            <button onClick={() => setKind("client")}>
              <b>Client</b>
              <small>Créer sa fiche</small>
            </button>
            <button onClick={() => setKind("quote")}>
              <b>Devis</b>
              <small>Créer un brouillon</small>
            </button>
            <button onClick={() => setKind("invoice")}>
              <b>Facture</b>
              <small>Créer un brouillon à contrôler</small>
            </button>
            <button onClick={() => setKind("expense")}>
              <b>Note de frais</b>
              <small>Mettre à jour les dépenses</small>
            </button>
          </div>
        )}
        {kind === "client" && (
          <form className="form-grid" onSubmit={createClient}>
            <label>
              Nom / raison sociale
              <input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
              />
            </label>
            <label>
              Catégorie
              <input
                value={clientCategory}
                onChange={(e) => setClientCategory(e.target.value)}
              />
            </label>
            <label className="wide">
              Adresse
              <input
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
              />
            </label>
            <label>
              Prénom du contact
              <input value={clientContactFirstName} onChange={(e) => setClientContactFirstName(e.target.value)} />
            </label>
            <label>
              Nom du contact
              <input value={clientContactLastName} onChange={(e) => setClientContactLastName(e.target.value)} />
            </label>
            <label>
              E-mail du contact
              <input type="email" value={clientContactEmail} onChange={(e) => setClientContactEmail(e.target.value)} />
            </label>
            <label>
              Téléphone du contact
              <input type="tel" value={clientContactPhone} onChange={(e) => setClientContactPhone(e.target.value)} />
            </label>
            <button disabled={busy}>Créer le client</button>
          </form>
        )}
        {kind === "quote" && quoteCreationMode === "choice" && (
          <div className="quote-mode-choice" data-testid="quote-mode-choice">
            <p className="wide">
              Choisissez le parcours adapté. ARD Manager crée
              uniquement un brouillon interne à relire : aucun devis n’est envoyé.
            </p>
            <button
              type="button"
              onClick={() => setQuoteCreationMode("free")}
            >
              <b>Devis libre</b>
              <small>
                Saisir librement les lignes, remises, acomptes et conditions.
              </small>
            </button>
            <button
              type="button"
              onClick={() => setQuoteCreationMode("guided")}
              disabled={!guidedCatalogItems.length}
            >
              <b>Devis guidé</b>
              <small>
                Utiliser un sujet guidé publié, répondre à ses questions et obtenir
                un prix explicable à contrôler.
              </small>
            </button>
            <button type="button" disabled={!hasDeploymentCapability(data, "guided_quote_costing")} onClick={() => {
              setQuoteCreationMode("chantier"); setQuoteUseSourcing(false);
              setQuoteMaterialItemId(""); setQuoteDiscountRate(0);
            }}><b>Chantier</b><small>Milieu de chantier, OPR ou livraison habitable : surfaces, temps, frais et taux de marque.</small></button>
            {!guidedCatalogItems.length && (
              <Notice>
                Aucun autre sujet guidé publié n’est encore disponible. Créez et
                publiez-en un depuis Processus et automatisations.
              </Notice>
            )}
          </div>
        )}
        {kind === "quote" && quoteCreationMode !== "choice" && (
          <form className="form-grid" onSubmit={createQuote}>
            <div className="wide quote-mode-heading">
              <button
                type="button"
                className="secondary compact"
                onClick={() => {
                  setQuoteCreationMode("choice");
                  setMessage("");
                }}
              >
                ← Changer de parcours
              </button>
              <div>
                <strong>
                  {quoteCreationMode === "chantier" ? "Devis guidé Chantier" : quoteCreationMode === "guided"
                    ? "Devis réel à partir d’un sujet guidé"
                    : "Saisie libre"}
                </strong>
                <small>
                  {quoteCreationMode === "guided"
                    ? "Les règles produisent une recommandation, jamais un envoi automatique."
                    : "Le brouillon sera modifiable avant toute validation ou transmission."}
                </small>
              </div>
            </div>
            <label>
              Client existant ou nouveau
              <select
                value={quoteClient}
                onChange={(e) => {
                  const client = data.clients.find((item) => item.id === e.target.value),
                    terms = resolvePaymentTerms(data.organization, client);
                  setQuoteClient(e.target.value);
                  setQuotePaymentTermsMode(terms.mode);
                  setQuotePaymentTermsDays(terms.days);
                }}
              >
                <option value="">＋ Créer un nouveau client avec ce devis</option>
                {data.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            {!quoteClient && (
              <>
                <div className="wide form-subsection-heading">
                  <strong>Nouveau client</strong>
                  <small>
                    La fiche sera créée avec les coordonnées minimales nécessaires
                    au devis et à la future invitation.
                  </small>
                </div>
                <label>
                  Nom / raison sociale
                  <input value={quoteNewClientName} onChange={(event) => setQuoteNewClientName(event.target.value)} required />
                </label>
                <label>
                  Adresse
                  <input value={quoteNewClientAddress} onChange={(event) => setQuoteNewClientAddress(event.target.value)} required />
                </label>
                <label>
                  Prénom du contact
                  <input value={quoteNewClientFirstName} onChange={(event) => setQuoteNewClientFirstName(event.target.value)} required />
                </label>
                <label>
                  Nom du contact
                  <input value={quoteNewClientLastName} onChange={(event) => setQuoteNewClientLastName(event.target.value)} required />
                </label>
                <label>
                  E-mail du contact
                  <input type="email" value={quoteNewClientEmail} onChange={(event) => setQuoteNewClientEmail(event.target.value)} required />
                </label>
                <label>
                  Téléphone du contact
                  <input type="tel" value={quoteNewClientPhone} onChange={(event) => setQuoteNewClientPhone(event.target.value)} required />
                </label>
                <label className="wide inline-check quote-invitation-toggle">
                  <input type="checkbox" checked={quotePrepareClientInvitation} onChange={(event) => setQuotePrepareClientInvitation(event.target.checked)} />
                  Préparer l’invitation d’ouverture de compte du client
                </label>
                <small className="wide">L’invitation sera préremplie et conservée dans la fiche client. Vous devrez la relire puis cliquer sur « Envoyer l’invitation » : aucun e-mail ne part lors de la création du devis.</small>
              </>
            )}
            {quoteCreationMode === "chantier" ? <>
              <fieldset className="wide chantier-fields" disabled={!!chantierSavedQuote || busy}><ChantierQuoteWizard value={chantier} onChange={setChantier} reason={chantierReason} onReasonChange={setChantierReason} canOverride={profile.role === "admin"} productOptions={quoteYieldItems} /></fieldset>
              <label>TVA (%)<input type="number" min="0" max="100" step="0.1" value={quoteVat} onChange={e => setQuoteVat(Number(e.target.value))} /></label>
              <details className="wide"><summary>Texte du devis client</summary><p style={{whiteSpace:"pre-line"}}>{chantierResult ? chantierDescription(chantier) : "Complétez les valeurs."}</p></details>
            </> : quoteCreationMode === "free" ? (
              <>
                <div className="wide form-subsection-heading">
                  <strong>Lignes du devis</strong>
                  <small>Choisissez un produit ou une prestation existante pour préremplir la ligne, ou saisissez une ligne libre. Vous pouvez en ajouter plusieurs.</small>
                </div>
                <label className="wide">
                  Rechercher dans les produits et prestations
                  <input value={quoteCatalogSearch} onChange={(event) => setQuoteCatalogSearch(event.target.value)} placeholder="Nom, code ou description…" />
                </label>
                <label className="wide">
                  Produit / prestation du catalogue · ligne 1
                  <select value={quoteCatalogItemId} onChange={(event) => selectPrimaryQuoteCatalogItem(event.target.value)}>
                    <option value="">Ligne libre</option>
                    {quoteCatalogOptions(quoteCatalogItemId).map((item) => <option key={item.id} value={item.id}>{item.name} · {money(item.default_unit_price_ex_vat)} / {item.default_unit_label}</option>)}
                  </select>
                </label>
                <label>
                  Description
                  <input value={quoteDescription} onChange={(e) => setQuoteDescription(e.target.value)} required />
                </label>
                <label>
                  Quantité
                  <input type="number" min="0.01" step="0.01" value={quoteQty} onChange={(e) => setQuoteQty(+e.target.value)} />
                </label>
                <label>
                  Prix unitaire HT
                  <input type="number" min="0" step="0.01" value={quotePrice || ""} onChange={(e) => setQuotePrice(+e.target.value)} placeholder={quoteUseSourcing ? "Facultatif tant que le ST n’a pas chiffré" : "0,00"} />
                </label>
                <label>
                  TVA (%)
                  <input type="number" min="0" max="100" step="0.1" value={quoteVat} onChange={(e) => setQuoteVat(+e.target.value)} />
                </label>
                {quoteAdditionalLines.map((line, index) => (
                  <div className="wide form-grid quick-quote-line" key={line.key} data-testid={`quick-quote-line-${index + 2}`}>
                    <div className="wide section-heading-row"><div><strong>Ligne {index + 2}</strong></div><button type="button" className="danger compact" onClick={() => setQuoteAdditionalLines((current) => current.filter((item) => item.key !== line.key))}>Supprimer</button></div>
                    <label className="wide">Produit / prestation ligne {index + 2}<select value={line.catalogItemId} onChange={(event) => selectAdditionalQuoteCatalogItem(line.key, event.target.value)}><option value="">Ligne libre</option>{quoteCatalogOptions(line.catalogItemId).map((item) => <option key={item.id} value={item.id}>{item.name} · {money(item.default_unit_price_ex_vat)} / {item.default_unit_label}</option>)}</select></label>
                    <label>Description ligne {index + 2}<input value={line.description} onChange={(event) => updateQuoteLine(line.key, { description: event.target.value })} required /></label>
                    <label>Quantité ligne {index + 2}<input type="number" min="0.01" step="0.01" value={line.quantity} onChange={(event) => updateQuoteLine(line.key, { quantity: Number(event.target.value) })} required /></label>
                    <label>Prix unitaire HT ligne {index + 2}<input type="number" min="0" step="0.01" value={line.unitPriceExVat || ""} onChange={(event) => updateQuoteLine(line.key, { unitPriceExVat: Number(event.target.value) })} /></label>
                    <label>TVA ligne {index + 2} (%)<input type="number" min="0" max="100" step="0.1" value={line.vatRate} onChange={(event) => updateQuoteLine(line.key, { vatRate: Number(event.target.value) })} required /></label>
                  </div>
                ))}
                <div className="row-actions wide"><button type="button" className="secondary" onClick={addQuoteLine}>＋ Ajouter une ligne</button></div>
              </>
            ) : (
              <>
                <div className="wide form-subsection-heading">
                  <strong>Sujet guidé à utiliser</strong>
                  <small>Le sujet est le modèle réutilisable. Ce parcours crée le devis réel du client à partir de sa version publiée.</small>
                </div>
                <label className="wide">
                  Sujet / modèle métier
                  <select value={quoteCatalogItemId} onChange={(event) => selectGuidedCatalogItem(event.target.value)} required>
                    <option value="">Choisir un sujet guidé publié</option>
                    {guidedCatalogItems.map((item) => {
                      const questionnaire = publishedGuidedQuestionnaires.find((entry) => entry.catalog_item_id === item.id);
                      return <option key={item.id} value={item.id}>{item.name} · questionnaire v{questionnaire?.version}</option>;
                    })}
                  </select>
                </label>
                {guidedQuestionnaire && (
                  <div className="wide guided-quote-questionnaire" data-testid="guided-quote-questionnaire">
                    <div className="guided-quote-version">
                      <strong>{guidedQuestionnaire.name}</strong>
                      <small>Version {guidedQuestionnaire.version} publiée · cette version sera figée avec le brouillon.</small>
                    </div>
                    <div className="form-grid">
                      {guidedQuestions.map((question) => {
                        const answer = quoteGuidedAnswers[question.question_key];
                        if (question.response_type === "boolean")
                          return <label key={question.id}>{question.label}<select value={answer == null ? "" : String(answer)} onChange={(event) => setGuidedAnswer(question.question_key, event.target.value === "" ? undefined : event.target.value === "true")} required={question.required}><option value="">À renseigner</option><option value="true">Oui</option><option value="false">Non</option></select>{question.help_text && <small>{question.help_text}</small>}</label>;
                        if (["number", "integer"].includes(question.response_type))
                          return <label key={question.id}>{question.label}<input type="number" min="0" step={question.response_type === "integer" ? "1" : "0.01"} value={typeof answer === "number" ? answer : ""} onChange={(event) => setGuidedAnswer(question.question_key, event.target.value === "" ? undefined : Number(event.target.value))} required={question.required} />{question.unit_label && <small>Unité : {question.unit_label}</small>}{question.help_text && <small>{question.help_text}</small>}</label>;
                        if (question.response_type === "single_choice")
                          return <label key={question.id}>{question.label}<select value={typeof answer === "string" ? answer : ""} onChange={(event) => setGuidedAnswer(question.question_key, event.target.value || undefined)} required={question.required}><option value="">Choisir</option>{question.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>{question.help_text && <small>{question.help_text}</small>}</label>;
                        if (question.response_type === "multiple_choice") {
                          const selected = Array.isArray(answer) ? answer : [];
                          return <fieldset key={question.id} className="guided-multiple-choice"><legend>{question.label}</legend>{question.options.map((option) => <label className="inline-check" key={option.value}><input type="checkbox" checked={selected.includes(option.value)} onChange={(event) => setGuidedAnswer(question.question_key, event.target.checked ? [...selected, option.value] : selected.filter((value) => value !== option.value))} />{option.label}</label>)}</fieldset>;
                        }
                        return <label className="wide" key={question.id}>{question.label}<textarea value={typeof answer === "string" ? answer : ""} onChange={(event) => setGuidedAnswer(question.question_key, event.target.value)} required={question.required} />{question.help_text && <small>{question.help_text}</small>}</label>;
                      })}
                    </div>
                    <label className="wide">Note interne de qualification<textarea value={quoteGuidedNotes} onChange={(event) => setQuoteGuidedNotes(event.target.value)} placeholder="Précisions utiles à la relecture…" /></label>
                  </div>
                )}
                {guidedEvaluation && (
                  <section className="wide guided-quote-preview" data-testid="guided-quote-preview">
                    <div className="section-heading-row"><div><span className="eyebrow">Calcul provisoire explicable</span><h3>Proposition à relire</h3></div><span className={`status ${guidedEvaluation.canPrepareDraft ? "active" : "warning"}`}>{guidedEvaluation.canPrepareDraft ? "Brouillon possible" : "Informations manquantes"}</span></div>
                    <p className="section-intro">Mécanique du sujet : {guidedEvaluation.derivedInputs.guided_pricing_strategy === "composition" ? "composition de forfaits et options connus" : guidedEvaluation.derivedInputs.guided_pricing_strategy === "parametric" ? "base ajustée selon les réponses" : guidedEvaluation.derivedInputs.guided_pricing_strategy === "hybrid" ? "base ajustée avec forfaits et options complémentaires" : "qualification informative"}.</p>
                    {guidedEvaluation.missingRequired.length > 0 && <Notice tone="error">Complétez les questions obligatoires avant de préparer un brouillon.</Notice>}
                    {guidedEvaluation.validationErrors.length > 0 && <Notice tone="error">{guidedEvaluation.validationErrors.join(" ")}</Notice>}
                    <div className="guided-quote-metrics">
                      <article><span>Quantité retenue</span><strong>{guidedEvaluation.outputQuantity == null ? "À contrôler" : `${guidedEvaluation.outputQuantity} ${guidedEvaluation.outputUnit || "unité"}`}</strong></article>
                      <article><span>Temps humain estimé</span><strong>{guidedEvaluation.totalLaborHours == null ? "À contrôler" : `${guidedEvaluation.totalLaborHours} h`}</strong></article>
                      <article><span>Tarif issu des règles</span><strong>{guidedEvaluation.commercialRulePriceExVat == null ? "À contrôler" : money(guidedEvaluation.commercialRulePriceExVat)}</strong></article>
                      <article><span>Seuil coût + objectif</span><strong>{guidedEvaluation.costBasedMinimumExVat == null ? "À contrôler" : money(guidedEvaluation.costBasedMinimumExVat)}</strong></article>
                      <article className="recommended"><span>Recommandation HT</span><strong>{guidedRecommendedPrice == null ? "À contrôler" : money(guidedRecommendedPrice)}</strong></article>
                    </div>
                    {guidedEvaluation.materialEstimates.length > 0 && (
                      <div className="guided-material-estimates">
                        <strong>Produits calculés depuis le stock</strong>
                        {guidedEvaluation.materialEstimates.map((estimate) => (
                          <span key={estimate.inventoryItemId}>
                            {estimate.inventoryItemName} · {estimate.materialQuantity} {materialUnitLabels[estimate.materialUnit] || estimate.materialUnit} à prévoir · coût estimé {money(estimate.estimatedCostExVat)} · prix conseillé {money(estimate.suggestedSalePriceExVat)} {estimate.billingMode === "separate" ? "ajouté au devis" : "absorbé dans la prestation"}
                          </span>
                        ))}
                      </div>
                    )}
                    {guidedEvaluation.lineItems.length > 0 && (
                      <div className="guided-material-estimates" data-testid="guided-service-components">
                        <strong>Prestations identifiables qui composeront le devis</strong>
                        {guidedEvaluation.lineItems.map((item) => (
                          <span key={`${item.sourceRuleId}-${item.catalogItemId || item.name}`}>
                            {item.name} · {item.quantity} × {money(item.unitPriceExVat)} = {money(item.totalExVat)} · {item.mode === "catalog" ? "commune à tous les clients" : item.mode === "client" ? "réutilisable pour ce client" : "propre à ce devis"}
                          </span>
                        ))}
                        <small>Ces lignes restent séparées pour analyser leurs marges et leurs temps. Le sujet guidé conserve en parallèle la lecture globale du chantier.</small>
                      </div>
                    )}
                    {guidedEvaluation.appliedRules.length > 0 && <div className="guided-applied-rules"><strong>Règles appliquées</strong>{guidedEvaluation.appliedRules.map((rule) => <span key={`${rule.questionKey}-${rule.ruleId}`}>✓ {rule.ruleLabel} · {rule.questionLabel}</span>)}</div>}
                    {guidedEvaluation.requiresCostReview && <Notice>Le tarif qualitatif est disponible, mais le coût complet et la marge restent <strong>À contrôler</strong> car le modèle de coût ne contient pas encore toutes les données. Le futur devis restera bloqué au statut brouillon.</Notice>}
                    <div className="form-grid">
                      <label>Prix HT retenu pour le brouillon<input type="number" min="0" step="0.01" value={quoteGuidedPriceOverride || guidedRecommendedPrice || ""} onChange={(event) => setQuoteGuidedPriceOverride(event.target.value)} disabled={guidedRecommendedPrice == null} /></label>
                      <div className="row-actions align-end"><button type="button" className="secondary compact" onClick={() => { setQuoteGuidedPriceOverride(""); setQuoteGuidedOverrideReason(""); }}>Reprendre la recommandation</button></div>
                      {guidedPriceChanged && <label className="wide">Justification de l’ajustement<textarea required value={quoteGuidedOverrideReason} onChange={(event) => setQuoteGuidedOverrideReason(event.target.value)} placeholder="Pourquoi le prix proposé est-il différent du calcul ?" /></label>}
                      <label className="wide">
                        Texte détaillé du devis
                        <textarea
                          rows={5}
                          value={quoteGuidedDescriptionOverride || guidedSuggestedDescription}
                          onChange={(event) => setQuoteGuidedDescriptionOverride(event.target.value)}
                          placeholder="Les actions prévues apparaîtront ici selon les réponses."
                        />
                        <small>Vous pouvez le compléter avant de créer le brouillon. Les anciens devis conserveront leur texte.</small>
                      </label>
                    </div>
                  </section>
                )}
              </>
            )}
            {quoteCreationMode !== "chantier" && <>
            <label>
              Remise globale (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={quoteDiscountRate}
                onChange={(event) => setQuoteDiscountRate(+event.target.value)}
              />
            </label>
            <label>
              Acompte demandé (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={quoteDepositRate}
                onChange={(event) => setQuoteDepositRate(+event.target.value)}
              />
            </label>
            </>}
            {quoteReferralFeatureAvailable && (
              <section className="wide quote-referral-panel">
                <label className="inline-check">
                  <input
                    type="checkbox"
                    checked={quoteHasReferral}
                    onChange={(event) => setQuoteHasReferral(event.target.checked)}
                  />
                  <span>
                    <strong>Apporteur d’affaires</strong>
                    <small>
                      Coût strictement interne : aucune mention ni ligne ne sera visible sur le devis client.
                    </small>
                  </span>
                </label>
                {quoteHasReferral && (
                  <>
                    <div className="form-grid">
                      <label>
                        Apporteur existant
                        <select
                          value={quoteReferralPartnerId}
                          onChange={(event) => {
                            const partner = quoteReferralPartners.find(
                              (item) => item.id === event.target.value,
                            );
                            setQuoteReferralPartnerId(event.target.value);
                            setQuoteNewReferralPartnerName("");
                            if (partner) {
                              setQuoteReferralCommissionRate(
                                Number(partner.default_commission_rate),
                              );
                              setQuoteReferralProtectionRate(
                                Number(partner.default_pricing_protection_rate),
                              );
                            }
                          }}
                        >
                          <option value="">Créer un nouvel apporteur</option>
                          {quoteReferralPartners.map((partner) => (
                            <option key={partner.id} value={partner.id}>
                              {partner.display_name}
                            </option>
                          ))}
                        </select>
                      </label>
                      {!quoteReferralPartnerId && (
                        <label>
                          Nom du nouvel apporteur
                          <input
                            required
                            value={quoteNewReferralPartnerName}
                            onChange={(event) =>
                              setQuoteNewReferralPartnerName(event.target.value)
                            }
                          />
                        </label>
                      )}
                      <label>
                        Commission reversée (%)
                        <input
                          type="number"
                          min="0.01"
                          max="99.99"
                          step="0.01"
                          value={quoteReferralCommissionRate}
                          onChange={(event) =>
                            setQuoteReferralCommissionRate(Number(event.target.value))
                          }
                        />
                      </label>
                      <label>
                        Protection tarifaire interne (%)
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={quoteReferralProtectionRate}
                          onChange={(event) =>
                            setQuoteReferralProtectionRate(Number(event.target.value))
                          }
                        />
                      </label>
                    </div>
                    <div className="quote-referral-preview">
                      <span>
                        Base après remise
                        <strong>{money(quoteReferralSummary.baseAfterDiscountExVat)}</strong>
                      </span>
                      <span>
                        Prix protégé
                        <strong>{money(quoteReferralSummary.protectedTotalExVat)}</strong>
                      </span>
                      <span>
                        Commission estimée
                        <strong>{money(quoteReferralSummary.estimatedCommissionExVat)}</strong>
                      </span>
                      <span>
                        Net après commission
                        <strong>{money(quoteReferralSummary.netAfterCommissionExVat)}</strong>
                      </span>
                    </div>
                    <small>
                      Pour {quoteReferralCommissionRate.toLocaleString("fr-FR")} % reversés,
                      le minimum neutre est {quoteReferralSummary.exactNeutralProtectionRate?.toLocaleString("fr-FR")} %.
                      ARD retient par défaut 15 % pour préserver la marge.
                    </small>
                  </>
                )}
              </section>
            )}
            {quoteCreationMode !== "chantier" && <>
            <div className="wide quote-material-summary" data-testid="quick-quote-totals">
              <strong>
                Total après remise : {money(quoteLineTotal)} HT · {money(quoteLineTotal + quoteVatAmount)} TTC
              </strong>
              <span>
                Brut {money(quoteGrossTotal)} HT · remise {money(quoteDiscountAmount)}
              </span>
              <span>
                {quoteDepositRate > 0
                  ? `Acompte demandé : ${money(quoteDepositAmount)} TTC`
                  : "Aucun acompte demandé"}
              </span>
            </div>
            <label>
              Validité du devis (jours)
              <input
                type="number"
                min="1"
                max="365"
                value={quoteValidityDays}
                onChange={(event) => setQuoteValidityDays(+event.target.value)}
              />
            </label>
            <label>
              Conditions de paiement proposées
              <select
                value={quotePaymentTermsMode}
                onChange={(event) =>
                  setQuotePaymentTermsMode(event.target.value as PaymentTermsMode)
                }
              >
                <option value="immediate">Paiement comptant</option>
                <option value="days_after_issue">Délai après émission</option>
                <option value="end_of_month">Fin de mois</option>
              </select>
            </label>
            {quotePaymentTermsMode === "days_after_issue" && (
              <label>
                Délai de paiement (jours)
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={quotePaymentTermsDays}
                  onChange={(event) => setQuotePaymentTermsDays(+event.target.value)}
                />
              </label>
            )}
            {hasDeploymentCapability(data, "quote_sourcing") ? (
              <label className="wide inline-check quote-sourcing-toggle">
                <input
                  type="checkbox"
                  checked={quoteUseSourcing}
                  onChange={(event) => setQuoteUseSourcing(event.target.checked)}
                />
                Demander d’abord le chiffrage d’un sous-traitant (ST → ARD → client)
              </label>
            ) : (
              <div className="wide">
                <Notice>
                  Le circuit ST → ARD → client sera proposé après activation contrôlée du module de chiffrage. Le devis brouillon simple reste disponible.
                </Notice>
              </div>
            )}
            {hasDeploymentCapability(data, "quote_sourcing") && quoteUseSourcing && (
              <>
                <label>
                  Sous-traitant sollicité
                  <select
                    value={quoteSourcingWorker}
                    onChange={(event) => setQuoteSourcingWorker(event.target.value)}
                    required
                  >
                    <option value="">Choisir</option>
                    {data.workers
                      .filter((worker) => worker.kind === "contractor" && worker.active)
                      .map((worker) => (
                        <option key={worker.id} value={worker.id}>{workerName(worker)}</option>
                      ))}
                  </select>
                </label>
                <div className="wide">
                  <Notice>
                    {quotePrice > 0
                      ? "Le montant HT saisi sert de prix client cible. ARD Manager calculera le budget ST maximum compatible avec votre objectif de rentabilité."
                      : "Aucun prix client n’est imposé : le ST propose librement son coût, puis ARD Manager calcule le prix client minimal selon votre objectif. Vous validerez toujours le prix final avant Henrri."}
                  </Notice>
                </div>
              </>
            )}
            {quoteCreationMode === "free" && <><div className="wide form-subsection-heading">
              <strong>Besoin matière associé</strong>
              <small>Facultatif. Le calcul est rattaché à la ligne 1 et conservé avec le devis pour expliquer le prix et la marge.</small>
            </div>
            <label>
              Produit technique
              <select value={quoteMaterialItemId} onChange={(event) => { const selected = quoteYieldItems.find((item) => item.id === event.target.value); setQuoteMaterialItemId(event.target.value); if (selected?.yield_output_unit) setQuoteMaterialUnit(selected.yield_output_unit); setQuoteMaterialPasses(Number(selected?.default_application_passes || 1)); }}>
                <option value="">Aucun calcul matière</option>
                {quoteYieldItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            {quoteMaterialItemId && <>
              <label>Surface / quantité traitée<input type="number" min="0.001" step="0.001" value={quoteMaterialTarget || ""} onChange={(event) => setQuoteMaterialTarget(+event.target.value)} required /></label>
              <label>Unité<select value={quoteMaterialUnit} onChange={(event) => setQuoteMaterialUnit(event.target.value)}><option value="m2">m²</option><option value="m3">m³</option><option value="metre_lineaire">m linéaire</option><option value="unite">unité</option></select></label>
              <label>Couches / passages<input type="number" min="1" step="1" value={quoteMaterialPasses} onChange={(event) => setQuoteMaterialPasses(+event.target.value)} required /></label>
              {quoteMaterialEstimate ? <div className="wide quote-material-summary">
                <strong>{quoteMaterialEstimate.theoreticalMaterialQuantity.toFixed(2)} {materialUnitLabels[quoteMaterialItem?.yield_material_unit || ""]} nécessaires · {quoteMaterialEstimate.packagesRequired} conditionnement(s)</strong>
                <span>Coût matière {money(quoteMaterialEstimate.theoreticalMaterialCost)} · achat immédiat {money(quoteMaterialEstimate.purchaseCost)}</span>
                <span>Seuil matière seul : {quoteMaterialEstimate.minimumMaterialSalePrice == null ? "marge à définir" : money(quoteMaterialEstimate.minimumMaterialSalePrice)}. Main-d’œuvre, trajets et charges à ajouter.</span>
                {materialMarginWarning && <Notice tone="error">Le total HT de la ligne 1 ({money(quotePrimaryLineTotal)}) est inférieur au seuil de marge matière ({money(quoteMaterialEstimate.minimumMaterialSalePrice || 0)}).</Notice>}
              </div> : <Notice tone="error">Rendement incomplet ou unité incompatible avec le produit choisi.</Notice>}
            </>}</>}
            </>}
            {chantierPendingCost && quoteCreationMode === "chantier" && <button type="button" disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                const lineId = String(chantierPendingCost.p_quote_line_id || ""), materialError = await saveChantierProductEstimates(lineId);
                if (materialError) setMessage(`Le brouillon est conservé, mais ses produits restent à enregistrer : ${materialError.message}`);
                else {
                  const retry = await supabase!.rpc("create_quote_costing_run", chantierPendingCost);
                  if (retry.error) setMessage(`Le brouillon est conservé, mais son calcul reste à enregistrer : ${retry.error.message}`);
                  else { setChantierPendingCost(null); setMessage("Dossier Chantier enregistré avec ses produits référencés. Brouillon prêt à relire."); reload(); setTimeout(onClose, 800); }
                }
              } catch { setMessage("Connexion interrompue. Le brouillon est conservé ; réessayez l’enregistrement du calcul."); }
              finally { setBusy(false); }
            }}>Réessayer l’enregistrement du calcul sans recréer le devis</button>}
            {quoteCreationMode === "chantier" && <div className="wide quote-material-summary"><strong>{money(quoteLineTotal)} HT · {money(quoteLineTotal + quoteVatAmount)} TTC</strong><span>Acompte : {money((chantier.profile === "standard" || quoteLineTotal >= 2000 ? .5 : 0) * (quoteLineTotal + quoteVatAmount))} TTC · solde sous 15 jours.</span></div>}
            <button disabled={busy || (quoteCreationMode === "chantier" && (!!chantierSavedQuote || chantierSubmissionErrors(chantier).length > 0 || !chantierResult || ((chantierResult.margin < 40 || chantierResult.price < chantierResult.floor) && !chantierReason.trim()))) || (quoteCreationMode === "guided" && !guidedEvaluation?.canPrepareDraft)}>
              {quoteCreationMode === "guided"
                ? "Transformer en devis brouillon"
                : "Créer le devis brouillon"}
            </button>
          </form>
        )}
        {kind === "invoice" && (
          <form className="form-grid" onSubmit={createInvoice}>
            <label>
              Client
              <select
                value={invoiceClient}
                onChange={(event) => {
                  const client = data.clients.find(
                      (item) => item.id === event.target.value,
                    ),
                    terms = resolvePaymentTerms(data.organization, client);
                  setInvoiceClient(event.target.value);
                  setInvoicePaymentTermsMode(terms.mode);
                  setInvoicePaymentTermsDays(terms.days);
                }}
                required
              >
                <option value="">Choisir</option>
                {data.clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date d’émission prévue
              <input
                type="date"
                value={invoiceIssueOn}
                onChange={(event) => setInvoiceIssueOn(event.target.value)}
                required
              />
            </label>
            <label className="wide">
              Description de la première ligne
              <input
                value={invoiceDescription}
                onChange={(event) => setInvoiceDescription(event.target.value)}
                required
              />
            </label>
            <label>
              Quantité
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={invoiceQty}
                onChange={(event) => setInvoiceQty(Number(event.target.value))}
                required
              />
            </label>
            <label>
              Prix unitaire HT
              <input
                type="number"
                min="0"
                step="0.01"
                value={invoicePrice || ""}
                onChange={(event) => setInvoicePrice(Number(event.target.value))}
                required
              />
            </label>
            <label>
              TVA (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={invoiceVat}
                onChange={(event) => setInvoiceVat(Number(event.target.value))}
                required
              />
            </label>
            <label>
              Conditions de paiement
              <select
                value={invoicePaymentTermsMode}
                onChange={(event) =>
                  setInvoicePaymentTermsMode(
                    event.target.value as PaymentTermsMode,
                  )
                }
              >
                <option value="immediate">Paiement comptant</option>
                <option value="days_after_issue">Délai après émission</option>
                <option value="end_of_month">Fin de mois</option>
              </select>
            </label>
            {invoicePaymentTermsMode === "days_after_issue" && (
              <label>
                Délai de paiement (jours)
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={invoicePaymentTermsDays}
                  onChange={(event) =>
                    setInvoicePaymentTermsDays(Number(event.target.value))
                  }
                />
              </label>
            )}
            <div className="wide">
              <Notice>
                Échéance calculée : {frDate(
                  paymentDueOn(
                    invoiceIssueOn,
                    invoicePaymentTermsMode,
                    invoicePaymentTermsDays,
                  ),
                )}. Aucun document n’est envoyé depuis cette action rapide.
              </Notice>
            </div>
            <button disabled={busy}>Créer la facture brouillon</button>
          </form>
        )}
        {kind === "expense" && (
          <form className="form-grid" onSubmit={createExpense}>
            <label>
              Date
              <input
                type="date"
                value={spentOn}
                onChange={(e) => setSpentOn(e.target.value)}
              />
            </label>
            <label>
              Catégorie
              <select
                value={expenseCategory}
                onChange={(e) => setExpenseCategory(e.target.value)}
              >
                <option value="meal">Restaurant</option>
                <option value="purchase">Achat</option>
                <option value="travel">Déplacement</option>
                <option value="other">Autre</option>
              </select>
            </label>
            <label className="wide">
              Description
              <input
                value={expenseDescription}
                onChange={(e) => setExpenseDescription(e.target.value)}
                required
              />
            </label>
            <label>
              Montant TTC
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(+e.target.value)}
              />
            </label>
            <label>
              TVA (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={expenseVat}
                onChange={(e) => setExpenseVat(+e.target.value)}
              />
            </label>
            <label className="check wide">
              <input
                type="checkbox"
                checked={addToStock}
                onChange={(e) => setAddToStock(e.target.checked)}
              />
              Ajouter cet achat au stock
            </label>
            {addToStock && (
              <>
                <label>
                  Article existant
                  <select
                    value={stockItemId}
                    onChange={(e) => setStockItemId(e.target.value)}
                  >
                    <option value="">Créer un nouvel article</option>
                    {(data.inventoryItems || [])
                      .filter((item) => item.active)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} · {item.current_quantity} {item.unit}
                        </option>
                      ))}
                  </select>
                </label>
                {!stockItemId && (
                  <>
                    <label>
                      Nom du nouvel article
                      <input
                        value={newStockName}
                        onChange={(e) => setNewStockName(e.target.value)}
                        placeholder="Ex. Produit vitres 5 L"
                        required
                      />
                    </label>
                    <label>
                      Unité de stock
                      <input
                        value={stockUnit}
                        onChange={(e) => setStockUnit(e.target.value)}
                        placeholder="bidon, litre, rouleau…"
                      />
                    </label>
                  </>
                )}
                <label>
                  Quantité entrée
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={stockQuantity}
                    onChange={(e) => setStockQuantity(+e.target.value)}
                    required
                  />
                </label>
              </>
            )}
            <button disabled={busy}>Enregistrer la dépense</button>
          </form>
        )}
        {message && (
          <Notice
            tone={
              message.includes("créé") || message.includes("enregistrée")
                ? "success"
                : "error"
            }
          >
            {message}
          </Notice>
        )}
      </section>
    </div>
  );
}

function VehicleManager({
  profile,
  data,
  reload,
  selfWorker,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  selfWorker?: Worker | null;
}) {
  const admin = profile.role === "admin",
    ownerOptions = admin
      ? [
          { value: "admin", label: "Dirigeant (moi)" },
          ...data.workers.map((worker) => ({
            value: worker.id,
            label: workerName(worker),
          })),
        ]
      : selfWorker
        ? [{ value: selfWorker.id, label: workerName(selfWorker) }]
        : [],
    [owner, setOwner] = useState(ownerOptions[0]?.value || ""),
    [kind, setKind] = useState<WorkerVehicle["vehicle_kind"]>("car"),
    [energy, setEnergy] =
      useState<WorkerVehicle["energy_type"]>("thermal_hybrid"),
    [brand, setBrand] = useState(""),
    [model, setModel] = useState(""),
    [registration, setRegistration] = useState(""),
    [fiscalPower, setFiscalPower] = useState(4),
    [registrationFile, setRegistrationFile] = useState<File | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const visibleVehicles = (data.vehicles || []).filter((vehicle) =>
    admin
      ? true
      : vehicle.worker_id === selfWorker?.id ||
        vehicle.owner_profile_id === profile.id,
  );
  const ownerName = (vehicle: WorkerVehicle) => {
    if (vehicle.worker_id) {
      const worker = data.workers.find((item) => item.id === vehicle.worker_id);
      return worker ? workerName(worker) : "Personne inconnue";
    }
    return "Dirigeant";
  };
  async function saveVehicle(event: React.FormEvent) {
    event.preventDefault();
    if (!registrationFile || !owner) {
      setMessage(
        "La carte grise est obligatoire pour enregistrer le véhicule.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const cleanName = registrationFile.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      storagePath = `${profile.organization_id}/${profile.id}/vehicles/${Date.now()}-${cleanName}`,
      uploaded = await supabase!.storage
        .from("private-documents")
        .upload(storagePath, registrationFile, { upsert: false });
    if (uploaded.error) {
      setBusy(false);
      setMessage(uploaded.error.message);
      return;
    }
    const isDirector = owner === "admin",
      saved = await supabase!.from("worker_vehicles").insert({
        organization_id: profile.organization_id,
        worker_id: isDirector ? null : owner,
        owner_profile_id: isDirector ? profile.id : null,
        vehicle_kind: kind,
        energy_type: energy,
        brand: brand.trim(),
        model: model.trim() || null,
        registration_number: registration.trim() || null,
        fiscal_power: fiscalPower,
        registration_document_path: storagePath,
        document_status: "pending",
        active: true,
      });
    setBusy(false);
    setMessage(
      saved.error
        ? saved.error.message
        : "Véhicule enregistré. Les IK seront débloquées après validation de la carte grise.",
    );
    if (!saved.error) {
      setBrand("");
      setModel("");
      setRegistration("");
      setRegistrationFile(null);
      reload();
    }
  }
  async function reviewVehicle(
    vehicle: WorkerVehicle,
    status: "approved" | "rejected",
  ) {
    setBusy(true);
    const { error } = await supabase!
      .from("worker_vehicles")
      .update({
        document_status: status,
        reviewed_by: profile.id,
        reviewed_at: new Date().toISOString(),
        document_notes:
          status === "approved"
            ? "Carte grise vérifiée par l’administration."
            : "Carte grise refusée : nouveau document requis.",
      })
      .eq("id", vehicle.id);
    setBusy(false);
    setMessage(
      error
        ? error.message
        : status === "approved"
          ? "Carte grise validée : les IK sont débloquées pour ce véhicule."
          : "Carte grise refusée.",
    );
    if (!error) reload();
  }
  async function openRegistration(vehicle: WorkerVehicle) {
    if (!vehicle.registration_document_path) return;
    const { data: signed, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(vehicle.registration_document_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  }
  return (
    <div className="vehicle-manager">
      <h3>Véhicules et cartes grises</h3>
      <p className="section-intro">
        Une IK ne peut être créée qu’avec un véhicule dont la carte grise a été
        vérifiée. Le calcul utilise le cumul annuel propre à chaque véhicule.
      </p>
      <form className="form-grid" onSubmit={saveVehicle}>
        {admin && (
          <label>
            Propriétaire / utilisateur
            <select
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
            >
              {ownerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Type de véhicule
          <select
            value={kind}
            onChange={(event) =>
              setKind(event.target.value as WorkerVehicle["vehicle_kind"])
            }
          >
            <option value="car">Voiture</option>
            <option value="motorcycle">Moto / scooter &gt; 50 cm³</option>
            <option value="moped">Cyclomoteur ≤ 50 cm³</option>
          </select>
        </label>
        <label>
          Motorisation
          <select
            value={energy}
            onChange={(event) =>
              setEnergy(event.target.value as WorkerVehicle["energy_type"])
            }
          >
            <option value="thermal_hybrid">
              Thermique, hydrogène ou hybride
            </option>
            <option value="electric">100 % électrique</option>
          </select>
        </label>
        <label>
          Marque
          <input
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
            required
          />
        </label>
        <label>
          Modèle
          <input
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </label>
        <label>
          Immatriculation
          <input
            value={registration}
            onChange={(event) => setRegistration(event.target.value)}
          />
        </label>
        <label>
          Puissance fiscale (CV, case P.6)
          <input
            type="number"
            min="1"
            max="99"
            value={fiscalPower}
            onChange={(event) => setFiscalPower(+event.target.value)}
            required
          />
        </label>
        <label className="wide">
          Carte grise (PDF ou image)
          <input
            type="file"
            accept=".pdf,image/jpeg,image/png,image/webp"
            onChange={(event) =>
              setRegistrationFile(event.target.files?.[0] || null)
            }
            required
          />
        </label>
        <button disabled={busy || !registrationFile}>
          {busy ? "Enregistrement…" : "Enregistrer et envoyer pour validation"}
        </button>
      </form>
      {message && (
        <Notice
          tone={
            message.includes("enregistré") ||
            message.includes("validée") ||
            message.includes("débloquées")
              ? "success"
              : "error"
          }
        >
          {message}
        </Notice>
      )}
      <div className="vehicle-list">
        {visibleVehicles.map((vehicle) => (
          <article key={vehicle.id}>
            <div>
              <strong>
                {admin ? ownerName(vehicle) + " · " : ""}
                {vehicle.brand} {vehicle.model || ""} · {vehicle.fiscal_power}{" "}
                CV
              </strong>
              <small>
                {vehicle.vehicle_kind === "car"
                  ? "Voiture"
                  : vehicle.vehicle_kind === "motorcycle"
                    ? "Moto / scooter"
                    : "Cyclomoteur"}{" "}
                ·{" "}
                {vehicle.energy_type === "electric"
                  ? "100 % électrique"
                  : "thermique, hydrogène ou hybride"}
                {vehicle.registration_number
                  ? " · " + vehicle.registration_number
                  : ""}
              </small>
            </div>
            <div className="row-actions">
              <Status value={vehicle.document_status} />
              <button
                className="secondary compact"
                onClick={() => openRegistration(vehicle)}
              >
                Voir la carte grise
              </button>
              {admin && vehicle.document_status === "pending" && (
                <>
                  <button
                    className="success compact"
                    disabled={busy}
                    onClick={() => reviewVehicle(vehicle, "approved")}
                  >
                    Valider
                  </button>
                  <button
                    className="danger compact"
                    disabled={busy}
                    onClick={() => reviewVehicle(vehicle, "rejected")}
                  >
                    Refuser
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
        {!visibleVehicles.length && (
          <p className="empty">Aucun véhicule déclaré.</p>
        )}
      </div>
      <p className="official-scale-note">
        Barème fiscal officiel 2026 (revenus 2025), à mettre à jour à chaque
        nouvelle publication annuelle. {" "}
        <a
          href="https://www.impots.gouv.fr/sites/default/files/media/3_Documentation/depliants/nid_4002_gp_120.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Consulter le barème officiel
        </a>
      </p>
    </div>
  );
}

function RouteMileagePlanner({
  profile,
  data,
  reload,
  workerId,
  ownerProfileId,
  routeTeamId = null,
  title = "Calculer la tournée réelle",
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  workerId: string | null;
  ownerProfileId: string | null;
  routeTeamId?: string | null;
  title?: string;
}) {
  const [date, setDate] = useState(parisDay(new Date())),
    [vehicleId, setVehicleId] = useState(""),
    [selectedMissionIds, setSelectedMissionIds] = useState<string[]>([]),
    [includeReturn, setIncludeReturn] = useState(
      data.organization?.mileage_return_to_headquarters === true,
    ),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const inferredTeam = workerId
      ? (data.dailyRouteTeams || []).find(
          (team) =>
            team.work_date === date &&
            (data.dailyRouteTeamMembers || []).some(
              (member) => member.team_id === team.id && member.worker_id === workerId,
            ),
        )
      : undefined,
    effectiveRouteTeamId = routeTeamId || inferredTeam?.id || null,
    teamWorkerIds = effectiveRouteTeamId
      ? (data.dailyRouteTeamMembers || [])
          .filter((member) => member.team_id === effectiveRouteTeamId)
          .map((member) => member.worker_id)
      : [],
    beneficiaryWorkerIds = effectiveRouteTeamId
      ? teamWorkerIds
      : workerId
        ? [workerId]
        : [],
    missionIds = new Set(
      data.assignments
        .filter(
          (assignment) =>
            beneficiaryWorkerIds.includes(assignment.worker_id) &&
            ["accepted", "assigned"].includes(assignment.status),
        )
        .map((assignment) => assignment.mission_id),
    ),
    missions = data.missions
      .filter(
        (mission) =>
          mission.status !== "cancelled" &&
          parisDay(mission.starts_at) === date &&
          (ownerProfileId ? true : missionIds.has(mission.id)),
      )
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    vehicles = (data.vehicles || []).filter(
      (vehicle) =>
        vehicle.active &&
        vehicle.document_status === "approved" &&
        (workerId
          ? vehicle.worker_id === workerId
          : vehicle.owner_profile_id === ownerProfileId),
    ),
    routes = (data.routeMileageCalculations || [])
      .filter(
        (route) =>
          route.work_date === date &&
          route.beneficiary_worker_id === workerId &&
          route.beneficiary_profile_id === ownerProfileId &&
          route.status !== "cancelled",
      )
      .sort((a, b) => (b.calculated_at || "").localeCompare(a.calculated_at || "")),
    canPrepareTeamRoute =
      profile.role === "admin" ||
      !effectiveRouteTeamId ||
      (data.dailyRouteTeams || []).some(
        (team) => team.id === effectiveRouteTeamId && team.leader_worker_id === workerId,
      );

  useEffect(() => {
    setSelectedMissionIds(missions.map((mission) => mission.id));
  }, [date, workerId, ownerProfileId, effectiveRouteTeamId]);
  useEffect(() => {
    if (!vehicles.some((vehicle) => vehicle.id === vehicleId))
      setVehicleId(vehicles[0]?.id || "");
  }, [workerId, ownerProfileId, data.vehicles]);

  async function prepareRoute() {
    if (!vehicleId || !selectedMissionIds.length) {
      setMessage("Choisissez un véhicule validé et au moins une mission réellement parcourue.");
      return;
    }
    setBusy(true);
    setMessage("");
    const prepared = await supabase!.rpc("prepare_daily_route_mileage", {
      p_work_date: date,
      p_vehicle_id: vehicleId,
      p_worker_id: workerId,
      p_owner_profile_id: ownerProfileId,
      p_route_team_id: effectiveRouteTeamId,
      p_include_return: includeReturn,
      p_mission_ids: selectedMissionIds,
    });
    if (prepared.error) {
      setBusy(false);
      setMessage(prepared.error.message);
      return;
    }
    const calculated = await supabase!.functions.invoke(
      "google-maps-compute-route",
      { body: { route_calculation_id: prepared.data } },
    );
    setBusy(false);
    setMessage(
      calculated.error
        ? calculated.error.message
        : `Tournée calculée : ${Number(calculated.data?.distance_km || 0).toLocaleString("fr-FR")} km, soumise pour validation.`,
    );
    reload();
  }

  return (
    <section className="route-mileage-planner">
      <h3>{title}</h3>
      <p className="section-intro">
        Le trajet suit l’ordre final des missions, au départ du siège. Les
        kilomètres proviennent de Google Routes ; aucune distance n’est
        inventée si le service n’est pas connecté.
      </p>
      <div className="form-grid">
        <label>
          Date de la tournée
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <label>
          Véhicule et carte grise validés
          <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
            <option value="">Choisir</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.brand} {vehicle.model || ""} · {vehicle.fiscal_power} CV
              </option>
            ))}
          </select>
        </label>
        <fieldset className="wide route-mission-selection">
          <legend>Missions réellement parcourues, dans cet ordre</legend>
          {missions.map((mission, index) => (
            <label key={mission.id}>
              <input
                type="checkbox"
                checked={selectedMissionIds.includes(mission.id)}
                onChange={() =>
                  setSelectedMissionIds((current) =>
                    current.includes(mission.id)
                      ? current.filter((id) => id !== mission.id)
                      : [...current, mission.id],
                  )
                }
              />
              <b>{index + 1}</b>
              <span>
                {new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })} · {mission.client?.name || mission.client_display_name} · {mission.location}
              </span>
            </label>
          ))}
          {!missions.length && <p className="empty">Aucune mission éligible ce jour.</p>}
        </fieldset>
        <label className="wide inline-check">
          <input
            type="checkbox"
            checked={includeReturn}
            onChange={(event) => setIncludeReturn(event.target.checked)}
          />
          J’atteste que le retour au siège social a réellement été effectué
        </label>
        <button type="button" disabled={busy || !vehicleId || !selectedMissionIds.length || routes.length > 0 || !canPrepareTeamRoute} onClick={prepareRoute}>
          {busy
            ? "Calcul en cours…"
            : routes.length
              ? "Tournée déjà préparée"
              : "Calculer et soumettre les IK"}
        </button>
      </div>
      {!vehicles.length && (
        <Notice>
          Aucun véhicule utilisable : la carte grise doit d’abord être déposée
          puis validée par l’administration.
        </Notice>
      )}
      {!canPrepareTeamRoute && (
        <Notice>
          Cette journée est organisée en équipe : le responsable prépare la
          feuille de route commune. Aucune IK séparée ne peut être créée.
        </Notice>
      )}
      {message && <Notice tone={message.startsWith("Tournée calculée") ? "success" : "info"}>{message}</Notice>}
      {routes.map((route) => {
        const stops = (data.routeMileageStops || []).filter(
            (stop) => stop.route_calculation_id === route.id,
          ),
          legs = (data.routeMileageLegs || []).filter(
            (leg) => leg.route_calculation_id === route.id,
          );
        return (
          <article className="route-proof" key={route.id}>
            <div>
              <strong>
                {route.total_distance_meters == null
                  ? "Distance à contrôler"
                  : `${(route.total_distance_meters / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} km`}
              </strong>
              <small>
                {stops.length} étapes · {legs.length} tronçons · {route.include_return_to_headquarters ? "retour siège inclus" : "sans retour siège"}
              </small>
              {route.error_message && <small>{route.error_message}</small>}
            </div>
            <Status value={route.status} />
          </article>
        );
      })}
    </section>
  );
}

function AdminWorkerProposalReview({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const proposals = data.workerCommercialProposals || [],
    [reviewing, setReviewing] = useState<WorkerCommercialProposal | null>(null),
    [clientPrice, setClientPrice] = useState(""),
    [notes, setNotes] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  function open(proposal: WorkerCommercialProposal) {
    setReviewing(proposal);
    setClientPrice(
      String(
        proposal.client_price_reference_ex_vat ??
          proposal.minimum_client_price_ex_vat ??
          "",
      ),
    );
    setNotes(proposal.decision_notes || "");
    setMessage("");
  }
  async function decide(decision: "accepted" | "rejected" | "countered" | "under_review") {
    if (!reviewing) return;
    setBusy(true);
    const result = await supabase!.rpc("review_worker_commercial_proposal", {
      p_proposal_id: reviewing.id,
      p_decision: decision,
      p_client_price_reference_ex_vat: clientPrice ? Number(clientPrice) : null,
      p_decision_notes: notes.trim() || null,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Décision enregistrée et historisée.");
    if (!result.error) {
      setReviewing(null);
      reload();
    }
  }
  return (
    <details>
      <summary>
        Propositions commerciales ST
        <span>{proposals.filter((item) => item.status === "submitted").length} à étudier</span>
      </summary>
      <div className="details-content">
        <p className="section-intro">
          Le sous-traitant communique uniquement son coût. Le prix client, la
          marge, la majoration et le seuil minimal restent réservés à
          l’administration.
        </p>
        {proposals.map((proposal) => {
          const worker = data.workers.find((item) => item.id === proposal.worker_id),
            mission = data.missions.find((item) => item.id === proposal.mission_id),
            client = data.clients.find((item) => item.id === proposal.client_id || item.id === mission?.client_id);
          return (
            <article className={`workflow-row proposal-review-row ${proposal.profitability_alert ? "warning-row" : ""}`} key={proposal.id}>
              <div>
                <strong>
                  {worker ? workerName(worker) : "Sous-traitant"} · {proposal.proposal_kind === "mission" ? client?.name || mission?.client_display_name || "Mission" : proposal.proposed_service_name}
                </strong>
                <small>
                  Version {proposal.version} · coût ST {money(proposal.proposed_supplier_total_ex_vat)}
                  {proposal.client_price_reference_ex_vat != null ? ` · prix client ${money(proposal.client_price_reference_ex_vat)}` : " · prix client à définir"}
                  {proposal.minimum_client_price_ex_vat != null ? ` · minimum conseillé ${money(proposal.minimum_client_price_ex_vat)}` : ""}
                </small>
                <small>
                  Objectif figé : {proposal.target_rate_snapshot?.toLocaleString("fr-FR")} % de {proposal.target_mode_snapshot === "markup" ? "majoration" : "marge"}
                  {proposal.projected_margin_rate != null ? ` · marge projetée ${proposal.projected_margin_rate.toLocaleString("fr-FR")} %` : ""}
                  {proposal.projected_markup_rate != null ? ` · majoration projetée ${proposal.projected_markup_rate.toLocaleString("fr-FR")} %` : ""}
                </small>
                {proposal.profitability_alert && <strong className="danger-text">Alerte : proposition sous le seuil de rentabilité défini.</strong>}
              </div>
              <div className="row-actions">
                <Status value={proposal.status} />
                {["submitted", "under_review"].includes(proposal.status) && (
                  <button className="compact" onClick={() => open(proposal)}>Étudier</button>
                )}
              </div>
            </article>
          );
        })}
        {!proposals.length && <p className="empty">Aucune proposition commerciale ST.</p>}
        {message && <Notice tone={message.startsWith("Décision") ? "success" : "error"}>{message}</Notice>}
      </div>
      {reviewing && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Étudier la proposition ST">
          <section className="modal-card portal-detail proposal-review-modal">
            <button className="icon-close" onClick={() => setReviewing(null)} aria-label="Fermer">×</button>
            <span className="eyebrow">Décision ARD obligatoire</span>
            <h2>Étudier la proposition ST</h2>
            <div className="stats compact-stats">
              <article><span>Coût fournisseur</span><strong>{money(reviewing.proposed_supplier_total_ex_vat)}</strong></article>
              <article><span>Minimum conseillé</span><strong>{money(reviewing.minimum_client_price_ex_vat || 0)}</strong></article>
              <article><span>Objectif figé</span><strong>{reviewing.target_rate_snapshot} %</strong><small>{reviewing.target_mode_snapshot === "markup" ? "majoration" : "marge"}</small></article>
            </div>
            <div className="form-grid">
              <label>
                Prix client HT étudié
                <input type="number" min="0" step="0.01" value={clientPrice} onChange={(event) => setClientPrice(event.target.value)} />
              </label>
              <label className="wide">
                Motif de la décision / contre-proposition
                <textarea value={notes} onChange={(event) => setNotes(event.target.value)} />
              </label>
            </div>
            {clientPrice && Number(clientPrice) < Number(reviewing.minimum_client_price_ex_vat || 0) && (
              <Notice tone="error">Le prix étudié reste inférieur au seuil minimal calculé avec l’objectif figé.</Notice>
            )}
            <div className="modal-actions">
              <button className="success" disabled={busy} onClick={() => decide("accepted")}>Accepter</button>
              <button disabled={busy} onClick={() => decide("countered")}>Envoyer une contre-proposition</button>
              <button className="secondary" disabled={busy} onClick={() => decide("under_review")}>Mettre à l’étude</button>
              <button className="danger" disabled={busy} onClick={() => decide("rejected")}>Refuser</button>
            </div>
          </section>
        </div>
      )}
    </details>
  );
}

function WorkerProposalPage({
  profile,
  worker,
  data,
  reload,
}: {
  profile: Profile;
  worker: Worker | null;
  data: DataState;
  reload: () => void;
}) {
  const [kind, setKind] = useState<"mission" | "new_service">("mission"),
    [missionId, setMissionId] = useState(""),
    [serviceName, setServiceName] = useState(""),
    [pricingMode, setPricingMode] = useState<"fixed" | "hourly" | "per_unit" | "monthly">("fixed"),
    [quantity, setQuantity] = useState(1),
    [unitRate, setUnitRate] = useState(""),
    [complementaryCost, setComplementaryCost] = useState("0"),
    [description, setDescription] = useState(""),
    [previousVersionId, setPreviousVersionId] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [sourcingTask, setSourcingTask] = useState<QuoteSourcingTask | null>(null),
    [sourcingPricingMode, setSourcingPricingMode] = useState<"fixed" | "hourly" | "per_unit" | "monthly">("fixed"),
    [sourcingQuantity, setSourcingQuantity] = useState(1),
    [sourcingUnitRate, setSourcingUnitRate] = useState(""),
    [sourcingComplementaryCost, setSourcingComplementaryCost] = useState("0"),
    [sourcingNotes, setSourcingNotes] = useState("");
  const proposals = data.workerCommercialProposals || [],
    sourcingTasks = data.quoteSourcingTasks || [],
    assignedMissionIds = new Set(
      data.assignments
        .filter((item) => item.worker_id === worker?.id && ["proposed", "assigned", "accepted"].includes(item.status))
        .map((item) => item.mission_id),
    ),
    missions = data.missions.filter((mission) => assignedMissionIds.has(mission.id) && mission.status !== "cancelled"),
    supplierTotal = Math.max(0, Number(unitRate || 0) * quantity + Number(complementaryCost || 0));
  const selectedProposalMission = missions.find((mission) => mission.id === missionId),
    selectedProposalProperty = selectedProposalMission
      ? data.properties.find((property) => property.id === selectedProposalMission.property_id)
      : undefined,
    selectedProposalLines = selectedProposalMission
      ? (data.missionServiceLines || [])
          .filter((line) => line.mission_id === selectedProposalMission.id)
          .sort((left, right) => left.position - right.position)
      : [];
  const missionPricingTitle = (mission: Mission) => {
    const property = data.properties.find((item) => item.id === mission.property_id),
      services = (data.missionServiceLines || [])
        .filter((line) => line.mission_id === mission.id)
        .sort((left, right) => left.position - right.position)
        .map((line) => line.label_snapshot),
      work = services.length ? services.join(" + ") : "Prestation à préciser",
      place = property?.name || mission.location || "Lieu à préciser";
    return `Chiffrage ARD — ${work} — ${place}`;
  };
  const qualificationAnswer = (
    answer: unknown,
    options: Array<{ value: string; label: string }> = [],
    unitLabel?: string | null,
  ) => {
    const values = Array.isArray(answer) ? answer : [answer],
      text = values
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) =>
          options.find((option) => option.value === String(value))?.label ||
          (typeof value === "boolean" ? (value ? "Oui" : "Non") : String(value)),
        )
        .join(", ");
    return `${text || "Non renseigné"}${text && unitLabel ? ` ${unitLabel}` : ""}`;
  };
  const sourcingTotal = Math.max(
    0,
    Number(sourcingUnitRate || 0) * sourcingQuantity + Number(sourcingComplementaryCost || 0),
  );
  function openSourcingTask(task: QuoteSourcingTask) {
    setSourcingTask(task);
    setSourcingPricingMode("fixed");
    setSourcingQuantity(1);
    setSourcingUnitRate(
      task.counter_budget_ex_vat == null ? "" : String(task.counter_budget_ex_vat),
    );
    setSourcingComplementaryCost("0");
    setSourcingNotes("");
    setMessage("");
  }
  async function submitSourcingOffer(event: React.FormEvent) {
    event.preventDefault();
    if (!sourcingTask) return;
    setBusy(true);
    const result = await supabase!.rpc("submit_quote_sourcing_offer", {
      p_workflow_id: sourcingTask.id,
      p_pricing_mode: sourcingPricingMode,
      p_quantity: sourcingQuantity,
      p_supplier_unit_rate_ex_vat: Number(sourcingUnitRate),
      p_complementary_cost_ex_vat: Number(sourcingComplementaryCost || 0),
      p_contractor_notes: sourcingNotes.trim() || null,
      p_previous_offer_id:
        sourcingTask.status === "countered" ? sourcingTask.latest_offer_id : null,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Chiffrage transmis à ARD. Il reste invisible au client tant qu’ARD ne l’a pas validé.",
    );
    if (!result.error) {
      setSourcingTask(null);
      reload();
    }
  }
  function restartFrom(proposal: WorkerCommercialProposal) {
    setPreviousVersionId(proposal.id);
    setKind(proposal.proposal_kind);
    setMissionId(proposal.mission_id || "");
    setServiceName(proposal.proposed_service_name || "");
    setPricingMode(proposal.pricing_mode);
    setQuantity(Number(proposal.quantity));
    setUnitRate(String(proposal.proposed_supplier_unit_rate));
    setComplementaryCost(String(proposal.complementary_cost_ex_vat));
    setDescription(proposal.description || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!worker) return;
    setBusy(true);
    const result = await supabase!.rpc("submit_worker_commercial_proposal", {
      p_proposal_kind: kind,
      p_mission_id: kind === "mission" ? missionId || null : null,
      p_proposed_service_name: kind === "new_service" ? serviceName.trim() : null,
      p_pricing_mode: pricingMode,
      p_quantity: quantity,
      p_supplier_unit_rate: Number(unitRate),
      p_complementary_cost: Number(complementaryCost || 0),
      p_description: description.trim() || null,
      p_previous_version_id: previousVersionId,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Proposition transmise à ARD pour étude.");
    if (!result.error) {
      setUnitRate("");
      setComplementaryCost("0");
      setDescription("");
      setPreviousVersionId(null);
      reload();
    }
  }
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">Relation commerciale avec ARD</span><h1>Mes propositions</h1></div></div>
      <section className="panel quote-sourcing-tasks">
        <h2>Demandes de chiffrage reçues d’ARD</h2>
        <p className="section-intro">
          Vous indiquez seulement ce qu’ARD devra vous régler. Le prix facturé au client, la marge et la règle de calcul interne ne sont jamais affichés ici.
        </p>
        {sourcingTasks.map((task) => (
          <article className="workflow-row" key={task.id}>
            <div>
              <strong>{task.request_title || task.description_snapshot}</strong>
              <small>
                {task.latest_offer_version
                  ? `Votre dernière version : ${task.latest_offer_version} · ${money(task.latest_supplier_total_ex_vat || 0)}`
                  : "Aucun chiffrage transmis"}
              </small>
              {task.counter_budget_ex_vat != null && (
                <small className="data-warning">
                  ARD vous propose un budget maximum de {money(task.counter_budget_ex_vat)} HT.
                  {task.decision_notes ? ` ${task.decision_notes}` : " Acceptez-vous ?"}
                </small>
              )}
            </div>
            <div className="row-actions">
              <Status value={task.status} />
              {["awaiting_supplier", "countered"].includes(task.status) && (
                <button className="compact" onClick={() => openSourcingTask(task)}>
                  {task.status === "countered" ? "Répondre au budget" : "Chiffrer"}
                </button>
              )}
            </div>
          </article>
        ))}
        {!sourcingTasks.length && <p className="empty">Aucune demande de chiffrage en attente.</p>}
      </section>
      {sourcingTask && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Chiffrer la demande ARD">
          <section className="modal-card portal-detail">
            <button className="icon-close" onClick={() => setSourcingTask(null)} aria-label="Fermer">×</button>
            <span className="eyebrow">Demande de chiffrage ARD</span>
            <h2>{sourcingTask.request_title || sourcingTask.description_snapshot}</h2>
            <div className="pricing-request-brief">
              <div className="detail-grid compact-grid">
                <div><span>Besoin</span><strong>{sourcingTask.description_snapshot}</strong></div>
                <div><span>Bien / site</span><strong>{sourcingTask.property_name || "À préciser"}</strong></div>
                <div><span>Adresse</span><strong>{sourcingTask.property_address || "À préciser"}</strong></div>
                <div><span>Quantité demandée</span><strong>{sourcingTask.requested_quantity ?? "À préciser"}</strong></div>
              </div>
              {!!sourcingTask.qualification_items?.length && (
                <div className="pricing-qualification-summary">
                  <strong>Informations relevées par ARD</strong>
                  <dl>
                    {sourcingTask.qualification_items.map((item, index) => (
                      <div key={`${item.label}-${index}`}>
                        <dt>{item.label}</dt>
                        <dd>{qualificationAnswer(item.answer, item.options, item.unit_label)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {sourcingTask.qualification_notes && <p><strong>Observations ARD :</strong> {sourcingTask.qualification_notes}</p>}
              <small>Les prix clients, objectifs de marge et calculs internes d’ARD ne sont pas communiqués.</small>
            </div>
            {sourcingTask.counter_budget_ex_vat != null && (
              <Notice>
                Budget maximum proposé par ARD : {money(sourcingTask.counter_budget_ex_vat)} HT. Vous pouvez l’accepter en l’utilisant comme total ou transmettre une autre version qui sera réétudiée.
              </Notice>
            )}
            <form className="form-grid" onSubmit={submitSourcingOffer}>
              <label>
                Mode de rémunération
                <select value={sourcingPricingMode} onChange={(event) => setSourcingPricingMode(event.target.value as typeof sourcingPricingMode)}>
                  <option value="fixed">Forfait</option><option value="hourly">Taux horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Forfait mensuel</option>
                </select>
              </label>
              <label>Quantité<input type="number" min="0.001" step="0.001" value={sourcingQuantity} onChange={(event) => setSourcingQuantity(Number(event.target.value))} required /></label>
              <label>Votre tarif unitaire HT<input type="number" min="0" step="0.01" value={sourcingUnitRate} onChange={(event) => setSourcingUnitRate(event.target.value)} required /></label>
              <label>Coût complémentaire HT<input type="number" min="0" step="0.01" value={sourcingComplementaryCost} onChange={(event) => setSourcingComplementaryCost(event.target.value)} /></label>
              <label className="wide">Précisions<textarea value={sourcingNotes} onChange={(event) => setSourcingNotes(event.target.value)} /></label>
              <div className="wide proposal-total"><span>Total proposé à ARD</span><strong>{money(sourcingTotal)}</strong></div>
              <button disabled={busy}>{busy ? "Transmission…" : "Transmettre cette version à ARD"}</button>
            </form>
          </section>
        </div>
      )}
      <section className="panel">
        <h2>{previousVersionId ? "Nouvelle version de la proposition" : "Proposer un tarif"}</h2>
        <p className="section-intro">
          Indiquez uniquement ce qu’ARD devra vous régler. Les prix clients et
          la marge de l’entreprise ne sont pas affichés dans votre espace.
        </p>
        <form className="form-grid" onSubmit={submit}>
          <label>
            Proposition pour
            <select value={kind} onChange={(event) => setKind(event.target.value as "mission" | "new_service")}>
              <option value="mission">Une mission proposée</option>
              <option value="new_service">Une nouvelle prestation</option>
            </select>
          </label>
          {kind === "mission" ? (
            <label className="wide">
              Mission
              <select value={missionId} onChange={(event) => setMissionId(event.target.value)} required>
                <option value="">Choisir</option>
                {missions.map((mission) => (
                  <option key={mission.id} value={mission.id}>{missionPricingTitle(mission)}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="wide">
              Nom de la nouvelle prestation
              <input value={serviceName} onChange={(event) => setServiceName(event.target.value)} minLength={3} required />
            </label>
          )}
          {kind === "mission" && selectedProposalMission && (
            <section className="wide pricing-request-brief">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Dossier de chiffrage ARD</span>
                  <h3>{missionPricingTitle(selectedProposalMission)}</h3>
                </div>
              </div>
              <div className="detail-grid compact-grid">
                <div><span>Bien concerné</span><strong>{selectedProposalProperty?.name || "À préciser"}</strong></div>
                <div><span>Adresse d’intervention</span><strong>{selectedProposalMission.location || selectedProposalProperty?.address || "À préciser"}</strong></div>
                <div><span>Période indicative</span><strong>{frDate(selectedProposalMission.starts_at, true)}</strong></div>
                <div><span>Échéance indicative</span><strong>{selectedProposalMission.ends_at ? frDate(selectedProposalMission.ends_at, true) : "À préciser"}</strong></div>
              </div>
              <div className="pricing-service-scope">
                <strong>Travaux demandés</strong>
                {selectedProposalLines.length ? (
                  <ul>
                    {selectedProposalLines.map((line) => (
                      <li key={line.id}>
                        <span>{line.label_snapshot}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="notice warning">Le détail des prestations doit être précisé par ARD avant votre chiffrage.</p>
                )}
              </div>
              {selectedProposalMission.notes && <p><strong>Contraintes et observations :</strong> {selectedProposalMission.notes}</p>}
              {!!selectedProposalMission.required_skills?.length && <p><strong>Compétences / moyens attendus :</strong> {selectedProposalMission.required_skills.join(" · ")}</p>}
              <small>Les tarifs facturés au client et les objectifs de marge d’ARD restent confidentiels.</small>
            </section>
          )}
          <label>
            Mode de rémunération
            <select value={pricingMode} onChange={(event) => setPricingMode(event.target.value as typeof pricingMode)}>
              <option value="fixed">Forfait</option><option value="hourly">Taux horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Forfait mensuel</option>
            </select>
          </label>
          <label>Quantité<input type="number" min="0.001" step="0.001" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} required /></label>
          <label>Votre tarif unitaire HT<input type="number" min="0" step="0.01" value={unitRate} onChange={(event) => setUnitRate(event.target.value)} required /></label>
          <label>Coût complémentaire HT<input type="number" min="0" step="0.01" value={complementaryCost} onChange={(event) => setComplementaryCost(event.target.value)} /></label>
          <label className="wide">Précisions<textarea value={description} onChange={(event) => setDescription(event.target.value)} /></label>
          <div className="wide proposal-total"><span>Total proposé à ARD</span><strong>{money(supplierTotal)}</strong></div>
          <button disabled={busy}>{busy ? "Transmission…" : "Transmettre à ARD"}</button>
        </form>
        {message && <Notice tone={message.startsWith("Proposition transmise") ? "success" : "error"}>{message}</Notice>}
      </section>
      <Records
        title="Historique des versions"
        empty="Aucune proposition transmise."
        rows={proposals.map((proposal) => (
          <article key={proposal.id}>
            <div>
              <strong>{proposal.proposal_kind === "mission" ? "Mission" : proposal.proposed_service_name} · version {proposal.version}</strong>
              <small>{money(proposal.proposed_supplier_total_ex_vat)} · {frDate(proposal.submitted_at, true)}{proposal.decision_notes ? ` · ${proposal.decision_notes}` : ""}</small>
            </div>
            <div className="row-actions">
              <Status value={proposal.status} />
              {["countered", "rejected"].includes(proposal.status) && <button className="secondary compact" onClick={() => restartFrom(proposal)}>Créer une nouvelle version</button>}
            </div>
          </article>
        ))}
      />
    </>
  );
}

function AdminPeopleHub({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const employees = data.workers.filter((w) => w.kind === "employee"),
    [leaveWorker, setLeaveWorker] = useState(employees[0]?.id || ""),
    [leaveFrom, setLeaveFrom] = useState(""),
    [leaveTo, setLeaveTo] = useState(""),
    [leaveReason, setLeaveReason] = useState(""),
    [mileageOwner, setMileageOwner] = useState("admin"),
    [mileageVehicle, setMileageVehicle] = useState(""),
    [mileageDate, setMileageDate] = useState(parisDay(new Date())),
    [mileageKm, setMileageKm] = useState(""),
    [mileageReason, setMileageReason] = useState(""),
    [mileageEvidence, setMileageEvidence] = useState<File | null>(null),
    [payrollWorker, setPayrollWorker] = useState(
      employees.find((w) => w.profile_id)?.id || "",
    ),
    [payrollPeriod, setPayrollPeriod] = useState(
      parisDay(new Date()).slice(0, 7),
    ),
    [payrollFile, setPayrollFile] = useState<File | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const approvedMileageVehicles = (data.vehicles || []).filter(
    (vehicle) =>
      vehicle.active &&
      vehicle.document_status === "approved" &&
      (mileageOwner === "admin"
        ? vehicle.owner_profile_id === profile.id
        : vehicle.worker_id === mileageOwner),
  );
  const missionMileageLabel = (mission: Mission) => {
    const client = data.clients.find((item) => item.id === mission.client_id);
    return `${new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })} · ${client?.name || mission.client_display_name || "Client"}`;
  };
  const ownerLabel = (item: Mileage) => {
    if (item.worker_id) {
      const w = data.workers.find((x) => x.id === item.worker_id);
      return w ? workerName(w) : "Personne inconnue";
    }
    return item.owner_profile_id === profile.id
      ? "Dirigeant"
      : "Profil administrateur";
  };
  async function setStatus(
    table: "time_entries" | "leave_requests" | "mileage_entries",
    id: string,
    status: "approved" | "rejected" | "cancelled",
  ) {
    setBusy(true);
    const payload =
      table === "mileage_entries"
        ? { status }
        : {
            status,
            reviewed_by: profile.id,
            reviewed_at: new Date().toISOString(),
          };
    const { error } = await supabase!.from(table).update(payload).eq("id", id);
    setBusy(false);
    setMessage(error ? error.message : "État mis à jour.");
    if (!error) reload();
  }
  async function addLeave(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase!.from("leave_requests").insert({
      organization_id: profile.organization_id,
      worker_id: leaveWorker,
      starts_on: leaveFrom,
      ends_on: leaveTo,
      reason: leaveReason || null,
      status: "approved",
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
    });
    setBusy(false);
    setMessage(error ? error.message : "Congé ajouté au planning.");
    if (!error) {
      setLeaveFrom("");
      setLeaveTo("");
      setLeaveReason("");
      reload();
    }
  }
  async function addMileage(e: React.FormEvent) {
    e.preventDefault();
    if (!mileageVehicle || !mileageEvidence || mileageReason.trim().length < 10) {
      setMessage(
        "Une correction exceptionnelle exige un véhicule validé, un motif détaillé et une preuve.",
      );
      return;
    }
    setBusy(true);
    const admin = mileageOwner === "admin";
    const cleanName = mileageEvidence.name.replace(/[^a-zA-Z0-9._-]/g, "-"),
      evidencePath = `${profile.organization_id}/${profile.id}/mileage-corrections/${Date.now()}-${cleanName}`,
      uploaded = await supabase!.storage
        .from("private-documents")
        .upload(evidencePath, mileageEvidence, { upsert: false });
    if (uploaded.error) {
      setBusy(false);
      setMessage(uploaded.error.message);
      return;
    }
    const { error } = await supabase!.from("mileage_entries").insert({
      organization_id: profile.organization_id,
      worker_id: admin ? null : mileageOwner,
      owner_profile_id: admin ? profile.id : null,
      vehicle_id: mileageVehicle,
      travel_date: mileageDate,
      kilometers: +mileageKm,
      reason: `Correction exceptionnelle : ${mileageReason.trim()}`,
      entry_source: "exceptional_correction",
      correction_reason: mileageReason.trim(),
      correction_evidence_path: evidencePath,
      correction_requested_by: profile.id,
      status: "pending",
    });
    setBusy(false);
    setMessage(error ? error.message : "Correction exceptionnelle soumise à validation.");
    if (!error) {
      setMileageKm("");
      setMileageReason("");
      setMileageEvidence(null);
      reload();
    }
  }
  async function uploadPayslip(e: React.FormEvent) {
    e.preventDefault();
    const employee = employees.find((w) => w.id === payrollWorker);
    if (!employee?.profile_id || !payrollFile)
      return setMessage(
        "Cette personne doit avoir un compte de connexion avant de recevoir une fiche de paie.",
      );
    setBusy(true);
    const clean = payrollFile.name.replace(/[^a-zA-Z0-9._-]/g, "-"),
      path = `${profile.organization_id}/${employee.profile_id}/payslips/${payrollPeriod}-${Date.now()}-${clean}`;
    const uploaded = await supabase!.storage
      .from("private-documents")
      .upload(path, payrollFile, { upsert: false });
    if (uploaded.error) {
      setBusy(false);
      return setMessage(uploaded.error.message);
    }
    const { error } = await supabase!.from("documents").insert({
      organization_id: profile.organization_id,
      owner_profile_id: employee.profile_id,
      kind: "payslip",
      period: payrollPeriod,
      storage_path: path,
      original_name: payrollFile.name,
      created_by: profile.id,
      status: "validated",
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Fiche de paie déposée dans l’espace privé du salarié.",
    );
    if (!error) {
      setPayrollFile(null);
      reload();
    }
  }
  return (
    <section className="hr-hub">
      <div className="page-title">
        <div>
          <span className="eyebrow">Personnel et conformité</span>
          <h2>Suivi RH</h2>
        </div>
      </div>
      {message && (
        <Notice
          tone={
            message.includes("ajout") ||
            message.includes("mise à jour") ||
            message.includes("déposée")
              ? "success"
              : "error"
          }
        >
          {message}
        </Notice>
      )}
      {hasDeploymentCapability(data, "worker_pricing_proposals") ? (
        <AdminWorkerProposalReview profile={profile} data={data} reload={reload} />
      ) : (
        <Notice>
          Les propositions tarifaires ST sont préparées dans le code mais ne sont pas encore activées sur cette base.
        </Notice>
      )}
      <details open>
        <summary>
          Heures et pointages{" "}
          <span>
            {data.times.filter((t) => t.status === "pending").length} à valider
          </span>
        </summary>
        <div className="details-content">
          <p className="section-intro">
            Les pointages servent au calcul mensuel des heures et à la
            préparation de la paie. Ils sont regroupés ici au lieu d’occuper un
            onglet isolé.
          </p>
          {data.times.slice(0, 20).map((t) => {
            const owner = data.workers.find((w) => w.id === t.worker_id);
            return (
              <div className="workflow-row" key={t.id}>
                <div>
                  <strong>
                    {owner ? workerName(owner) : "Personne inconnue"} ·{" "}
                    {frDate(t.started_at_ts || t.work_date, true)}
                  </strong>
                  <small>
                    {hours(t.worked_minutes)} · pause {t.break_minutes} min
                  </small>
                </div>
                <div className="row-actions">
                  <Status value={t.status} />
                  {t.status === "pending" && (
                    <>
                      <button
                        className="success compact"
                        disabled={busy}
                        onClick={() =>
                          setStatus("time_entries", t.id, "approved")
                        }
                      >
                        Valider
                      </button>
                      <button
                        className="danger compact"
                        disabled={busy}
                        onClick={() =>
                          setStatus("time_entries", t.id, "rejected")
                        }
                      >
                        Refuser
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!data.times.length && <p className="empty">Aucun pointage.</p>}
        </div>
      </details>
      <details>
        <summary>
          Congés{" "}
          <span>
            {data.leaves.filter((l) => l.status === "pending").length}{" "}
            demande(s)
          </span>
        </summary>
        <div className="details-content">
          <form className="form-grid" onSubmit={addLeave}>
            <label>
              Salarié
              <select
                value={leaveWorker}
                onChange={(e) => setLeaveWorker(e.target.value)}
                required
              >
                <option value="">Choisir</option>
                {employees.map((w) => (
                  <option key={w.id} value={w.id}>
                    {workerName(w)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Du
              <input
                type="date"
                value={leaveFrom}
                onChange={(e) => setLeaveFrom(e.target.value)}
                required
              />
            </label>
            <label>
              Au
              <input
                type="date"
                min={leaveFrom}
                value={leaveTo}
                onChange={(e) => setLeaveTo(e.target.value)}
                required
              />
            </label>
            <label>
              Motif
              <input
                value={leaveReason}
                onChange={(e) => setLeaveReason(e.target.value)}
              />
            </label>
            <button disabled={busy}>Ajouter le congé</button>
          </form>
          {data.leaves.map((l) => {
            const owner = data.workers.find((w) => w.id === l.worker_id);
            return (
              <div className="workflow-row" key={l.id}>
                <div>
                  <strong>
                    {owner ? workerName(owner) : "Personne inconnue"} ·{" "}
                    {frDate(l.starts_on)} → {frDate(l.ends_on)}
                  </strong>
                  <small>{l.reason || "Sans précision"}</small>
                </div>
                <div className="row-actions">
                  <Status value={l.status} />
                  {l.status === "pending" && (
                    <button
                      className="success compact"
                      onClick={() =>
                        setStatus("leave_requests", l.id, "approved")
                      }
                    >
                      Valider
                    </button>
                  )}
                  {l.status !== "cancelled" && (
                    <button
                      className="danger compact"
                      onClick={() =>
                        setStatus("leave_requests", l.id, "cancelled")
                      }
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </details>
      <details>
        <summary>
          Indemnités kilométriques{" "}
          <span>
            {data.mileages.filter((i) => i.status === "pending").length} à
            valider
          </span>
        </summary>
        <div className="details-content">
          <form className="form-grid" onSubmit={addMileage}>
            <label>
              Pour
              <select
                value={mileageOwner}
                onChange={(e) => {
                  setMileageOwner(e.target.value);
                  setMileageVehicle("");
                }}
              >
                <option value="admin">Dirigeant (moi)</option>
                {data.workers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {workerName(w)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Véhicule validé
              <select
                value={mileageVehicle}
                onChange={(e) => setMileageVehicle(e.target.value)}
                required
              >
                <option value="">Choisir</option>
                {approvedMileageVehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.brand} {vehicle.model || ""} ·{" "}
                    {vehicle.fiscal_power} CV
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date
              <input
                type="date"
                value={mileageDate}
                onChange={(e) => setMileageDate(e.target.value)}
                required
              />
            </label>
            <label>
              Kilomètres corrigés
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={mileageKm}
                onChange={(e) => setMileageKm(e.target.value)}
                required
              />
            </label>
            <label>
              Motif détaillé de l’exception
              <input
                value={mileageReason}
                onChange={(e) => setMileageReason(e.target.value)}
                required
              />
            </label>
            <label className="wide">
              Justificatif obligatoire
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(event) => setMileageEvidence(event.target.files?.[0] || null)}
                required
              />
            </label>
            <button disabled={busy || !mileageVehicle}>Soumettre la correction</button>
          </form>
          {!approvedMileageVehicles.length && (
            <Notice>
              Aucun véhicule validé pour cette personne : la saisie des IK reste
              verrouillée.
            </Notice>
          )}
          <RouteMileagePlanner
            profile={profile}
            data={data}
            reload={reload}
            workerId={mileageOwner === "admin" ? null : mileageOwner}
            ownerProfileId={mileageOwner === "admin" ? profile.id : null}
            title="Calcul normal depuis la tournée du jour"
          />
          {data.mileages.map((i) => (
            <div className="workflow-row" key={i.id}>
              <div>
                <strong>
                  {ownerLabel(i)} · {frDate(i.travel_date)} · {i.kilometers} km
                </strong>
                <small>
                  {i.reason}
                  {i.calculated_amount != null
                    ? " · " +
                      money(i.calculated_amount) +
                      " · cumul annuel " +
                      (i.annual_km_after || 0) +
                      " km"
                    : " · montant en attente d’un véhicule validé"}
                  {i.mission_id
                    ? " · mission " +
                      missionMileageLabel(
                        data.missions.find(
                          (mission) => mission.id === i.mission_id,
                        ) || ({
                          starts_at: i.travel_date,
                          client_id: "",
                        } as Mission),
                      )
                    : " · déplacement général"}
                </small>
              </div>
              <div className="row-actions">
                <Status value={i.status} />
                {i.status === "pending" && (
                  <button
                    className="success compact"
                    onClick={() =>
                      setStatus("mileage_entries", i.id, "approved")
                    }
                  >
                    Valider
                  </button>
                )}
                {i.status !== "cancelled" && (
                  <button
                    className="danger compact"
                    onClick={() =>
                      setStatus("mileage_entries", i.id, "cancelled")
                    }
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
          <VehicleManager profile={profile} data={data} reload={reload} />
        </div>
      </details>
      <details>
        <summary>
          Paie et fiches de paie{" "}
          <span>
            {data.documents.filter((d) => d.kind === "payslip").length}{" "}
            document(s)
          </span>
        </summary>
        <div className="details-content">
          <p className="section-intro">
            Une fiche déposée ici devient immédiatement visible uniquement dans
            l’espace privé du salarié concerné.
          </p>
          <form className="form-grid" onSubmit={uploadPayslip}>
            <label>
              Salarié
              <select
                value={payrollWorker}
                onChange={(e) => setPayrollWorker(e.target.value)}
                required
              >
                <option value="">Choisir</option>
                {employees.map((w) => (
                  <option key={w.id} value={w.id}>
                    {workerName(w)}
                    {!w.profile_id ? " · compte à créer" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Période
              <input
                type="month"
                value={payrollPeriod}
                onChange={(e) => setPayrollPeriod(e.target.value)}
                required
              />
            </label>
            <label className="wide">
              Fiche de paie PDF
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setPayrollFile(e.target.files?.[0] || null)}
                required
              />
            </label>
            <button disabled={busy}>Déposer la fiche</button>
          </form>
        </div>
      </details>
    </section>
  );
}
function OrganizationLeaderManager({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const blank: Partial<OrganizationLeader> = {
      first_name: "",
      last_name: "",
      title: "",
      email: "",
      phone: "",
      starts_on: "",
      ends_on: "",
      notes: "",
      profile_id: null,
      worker_id: null,
      active: true,
    },
    leaders = data.organizationLeaders || [],
    [editing, setEditing] = useState<Partial<OrganizationLeader>>(blank),
    [open, setOpen] = useState(false),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const name = (leader: Partial<OrganizationLeader>) =>
    [leader.first_name, leader.last_name].filter(Boolean).join(" ") ||
    "Dirigeant";
  function select(leader?: OrganizationLeader) {
    setEditing(leader ? { ...leader } : blank);
    setMessage("");
    setOpen(true);
  }
  function selectWorker(workerId: string) {
    const worker = data.workers.find((item) => item.id === workerId),
      linkedProfile = worker?.profile_id || null,
      account = data.profiles?.find((item) => item.id === linkedProfile);
    setEditing((value) => ({
      ...value,
      worker_id: workerId || null,
      profile_id: linkedProfile,
      first_name: worker?.first_name || value.first_name || "",
      last_name: worker?.last_name || value.last_name || "",
      email: worker?.contact_email || account?.email || value.email || "",
      phone: worker?.contact_phone || account?.phone || value.phone || "",
    }));
  }
  function selectProfile(profileId: string) {
    const account = data.profiles?.find((item) => item.id === profileId);
    const words = (account?.display_name || "").trim().split(/\s+/);
    setEditing((value) => ({
      ...value,
      profile_id: profileId || null,
      first_name: value.first_name || words.shift() || "",
      last_name: value.last_name || words.join(" ") || "",
      email: value.email || account?.email || "",
      phone: value.phone || account?.phone || "",
    }));
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = {
        organization_id: profile.organization_id,
        profile_id: editing.profile_id || null,
        worker_id: editing.worker_id || null,
        first_name: editing.first_name?.trim() || null,
        last_name: editing.last_name?.trim(),
        title: editing.title?.trim(),
        email: editing.email?.trim().toLowerCase() || null,
        phone: editing.phone?.trim() || null,
        starts_on: editing.starts_on || null,
        ends_on: editing.ends_on || null,
        notes: editing.notes?.trim() || null,
        active: editing.active !== false,
      },
      result = editing.id
        ? await supabase!
            .from("organization_leaders")
            .update(payload)
            .eq("id", editing.id)
        : await supabase!.from("organization_leaders").insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editing.id
          ? "Fiche dirigeant mise à jour."
          : "Dirigeant ajouté à la gouvernance.",
    );
    if (!result.error) {
      setOpen(false);
      reload();
    }
  }
  async function toggle() {
    if (!editing.id) return;
    setBusy(true);
    const next = editing.active === false;
    const { error } = await supabase!
      .from("organization_leaders")
      .update({ active: next })
      .eq("id", editing.id);
    setBusy(false);
    setMessage(error ? error.message : next ? "Mandat réactivé." : "Mandat archivé.");
    if (!error) {
      setOpen(false);
      reload();
    }
  }
  return (
    <section className="panel leader-roster">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Gouvernance</span>
          <h2>Dirigeants de la société</h2>
        </div>
        <button className="secondary" onClick={() => select()}>
          Ajouter un dirigeant
        </button>
      </div>
      <p className="section-intro">
        Plusieurs mandats peuvent coexister : président, directeur général,
        cogérant… Une fiche peut être reliée à un compte ou à un membre de
        l’équipe, sans transformer automatiquement le dirigeant en salarié.
      </p>
      <div className="leader-cards">
        {leaders.map((leader) => (
          <article key={leader.id} className={!leader.active ? "archived" : ""}>
            <div>
              <strong>{name(leader)}</strong>
              <small>
                {leader.title}
                {leader.email ? ` · ${leader.email}` : ""}
                {!leader.active ? " · Mandat archivé" : ""}
              </small>
            </div>
            <button className="secondary compact" onClick={() => select(leader)}>
              Informations
            </button>
          </article>
        ))}
        {!leaders.length && (
          <p className="empty">Aucun dirigeant défini pour la société.</p>
        )}
      </div>
      {message && <Notice tone={message.includes("mise à jour") || message.includes("ajouté") ? "success" : "info"}>{message}</Notice>}
      {open && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Fiche dirigeant">
          <section className="portal-detail leader-detail-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Équipe &amp; RH</span>
                <h2>{editing.id ? name(editing) : "Nouveau dirigeant"}</h2>
              </div>
              <button className="icon-close" aria-label="Fermer" onClick={() => setOpen(false)}>×</button>
            </div>
            <form className="form-grid" onSubmit={save}>
              <label>
                Fonction dans la société
                <input value={editing.title || ""} onChange={(e) => setEditing((value) => ({ ...value, title: e.target.value }))} placeholder="Président, directeur général…" required />
              </label>
              <label>
                Relier à une fiche Équipe &amp; RH
                <select value={editing.worker_id || ""} onChange={(e) => selectWorker(e.target.value)}>
                  <option value="">Aucune fiche personnel</option>
                  {data.workers.map((worker) => <option key={worker.id} value={worker.id}>{workerName(worker)}</option>)}
                </select>
              </label>
              <label>
                Relier à un compte utilisateur
                <select value={editing.profile_id || ""} onChange={(e) => selectProfile(e.target.value)}>
                  <option value="">Aucun compte</option>
                  {(data.profiles || []).map((account) => <option key={account.id} value={account.id}>{account.display_name}</option>)}
                </select>
              </label>
              <label>Prénom<input value={editing.first_name || ""} onChange={(e) => setEditing((value) => ({ ...value, first_name: e.target.value }))} /></label>
              <label>Nom<input value={editing.last_name || ""} onChange={(e) => setEditing((value) => ({ ...value, last_name: e.target.value }))} required /></label>
              <label>E-mail<input type="email" value={editing.email || ""} onChange={(e) => setEditing((value) => ({ ...value, email: e.target.value }))} /></label>
              <label>Téléphone<input type="tel" value={editing.phone || ""} onChange={(e) => setEditing((value) => ({ ...value, phone: e.target.value }))} /></label>
              <label>Début du mandat<input type="date" value={editing.starts_on || ""} onChange={(e) => setEditing((value) => ({ ...value, starts_on: e.target.value }))} /></label>
              <label>Fin du mandat<input type="date" value={editing.ends_on || ""} onChange={(e) => setEditing((value) => ({ ...value, ends_on: e.target.value }))} /></label>
              <label className="wide">Notes internes<textarea value={editing.notes || ""} onChange={(e) => setEditing((value) => ({ ...value, notes: e.target.value }))} /></label>
              <button disabled={busy}>{editing.id ? "Enregistrer la fiche" : "Créer la fiche dirigeant"}</button>
            </form>
            {editing.id && (
              <div className="detail-actions">
                <button className={editing.active === false ? "secondary" : "danger"} disabled={busy} onClick={toggle}>
                  {editing.active === false ? "Réactiver le mandat" : "Archiver le mandat"}
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
function Team({
  profile,
  data,
  reload,
  focusWorkerId,
  onFocusWorkerConsumed,
  onSelectMission,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  focusWorkerId?: string | null;
  onFocusWorkerConsumed?: () => void;
  onSelectMission?: (mission: Mission) => void;
}) {
  const month = parisDay(new Date()).slice(0, 7),
    year = parisDay(new Date()).slice(0, 4),
    pricing: Record<string, string> = {
      salary: "Salaire mensuel",
      hourly: "Taux horaire",
      fixed: "Forfait",
      mixed: "Mixte",
      monthly: "Mensuel",
    },
    blank = {
      id: "",
      kind: "employee" as const,
      first_name: "",
      last_name: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      business_name: "",
      siret: "",
      vat_number: "",
      profile_notes: "",
      skills: [] as string[],
      monthly_hours: 151.67,
      daily_hours: 7,
      net_salary: 0,
      employer_cost_monthly: 0,
      ticket_count: 0,
      ticket_value: 0,
      mileage_enabled: false,
      pricing_mode: "salary",
      hourly_rate: 0,
      fixed_fee: 0,
      monthly_fee: 0,
    },
    [editing, setEditing] = useState<Partial<Worker>>(blank),
    [workerOpen, setWorkerOpen] = useState(false),
    [financialWorkerOpen, setFinancialWorkerOpen] = useState(false),
    [message, setMessage] = useState(""),
    [historyQuery, setHistoryQuery] = useState(""),
    [historyCategory, setHistoryCategory] = useState("all"),
    [historyFrom, setHistoryFrom] = useState(""),
    [historyTo, setHistoryTo] = useState(""),
    [remoteWorkerHistory, setRemoteWorkerHistory] = useState<WorkerHistoryEntry[]>([]),
    [remoteHistoryLoaded, setRemoteHistoryLoaded] = useState(false),
    [historyLoading, setHistoryLoading] = useState(false),
    [historyError, setHistoryError] = useState("");
  const selectedWorker = data.workers.find((candidate) => candidate.id === editing.id),
    selectedMissionIds = new Set(data.assignments.filter((item) => item.worker_id === editing.id).map((item) => item.mission_id)),
    selectedMissions = data.missions.filter((mission) => selectedMissionIds.has(mission.id)),
    workerMonthMinutes = data.times.filter((item) => item.worker_id === editing.id && item.work_date.startsWith(month) && item.status !== "cancelled").reduce((sum, item) => sum + item.worked_minutes, 0),
    workerYearMinutes = data.times.filter((item) => item.worker_id === editing.id && item.work_date.startsWith(year) && item.status !== "cancelled").reduce((sum, item) => sum + item.worked_minutes, 0),
    workerYearMileage = data.mileages.filter((item) => item.worker_id === editing.id && item.travel_date.startsWith(year) && item.status !== "rejected").reduce((sum, item) => sum + item.kilometers, 0),
    workerLeaveDays = data.leaves.filter((item) => item.worker_id === editing.id && item.starts_on.startsWith(year) && item.status !== "rejected").reduce((sum, item) => sum + Math.max(1, Math.round((new Date(`${item.ends_on}T12:00:00`).getTime() - new Date(`${item.starts_on}T12:00:00`).getTime()) / 86_400_000) + 1), 0),
    workerIncidentMissionIds = new Set([
      ...data.reports
        .filter((item) => {
          const observation = item.observations || "";
          return item.worker_id === editing.id && (
            item.result !== "completed"
            || (!/(?:sans|aucun) incident/i.test(observation)
              && /incident|anomal|probl[eè]me|d[eé]g[aâ]t|cass|manquant/i.test(observation))
          );
        })
        .map((item) => item.mission_id),
      ...(data.missionReportSubmissions || [])
        .filter((item) => item.worker_id === editing.id && item.result !== "completed")
        .map((item) => item.mission_id),
    ]),
    workerIncidentCount = workerIncidentMissionIds.size,
    workerQualityMetric = (data.workerQualityMetrics || []).find(
      (metric) => metric.worker_id === editing.id,
    ),
    workerContracts = data.contracts
      .filter((item) => item.worker_id === editing.id)
      .sort((a, b) => (b.signed_at || b.sent_at || b.generated_at || b.starts_on || "").localeCompare(a.signed_at || a.sent_at || a.generated_at || a.starts_on || "")),
    currentContract = workerContracts.find((item) => !["expired", "terminated"].includes(item.status)) || workerContracts[0],
    pendingRequirements = data.requirements.filter((item) => item.worker_id === editing.id && !["validated", "waived"].includes(item.status)),
    selectedAccess = editing.id
      ? organizationAccessSummary(
          "worker",
          editing.id,
          Boolean(editing.profile_id),
          data.organizationUserInvitations,
          data.profiles?.find((profile) => profile.id === editing.profile_id)?.access_status,
        )
      : null;
  const localWorkerHistory = useMemo(() => {
    if (!editing.id) return [];
    const rows: WorkerHistoryEntry[] = [];
    selectedMissions.forEach((mission) => {
      const client = data.clients.find((item) => item.id === mission.client_id),
        property = data.properties.find((item) => item.id === mission.property_id),
        service = data.services.find((item) => item.id === mission.service_id) || data.propertyServices.find((item) => item.id === mission.property_service_id);
      rows.push({id:`mission-${mission.id}`,date:mission.starts_at,category:"missions",label:"Mission",title:`${client?.name || mission.client_display_name || "Client"}${property ? ` · ${property.name}` : ""}`,detail:`${service?.name || "Prestation"} · ${labels[mission.status] || mission.status} · ${frDate(mission.starts_at, true)}`,missionId:mission.id});
    });
    data.reports.filter((item) => item.worker_id === editing.id).forEach((report) => {
      const mission = data.missions.find((item) => item.id === report.mission_id);
      rows.push({id:`report-${report.id}`,date:report.submitted_at,category:"reports",label:"Compte rendu",title:`Rapport ${labels[report.status] || report.status}`,detail:`${report.actual_hours} h · ${report.observations || report.client_note || "Aucune observation"}`,missionId:mission?.id});
    });
    (data.missionReportSubmissions || []).filter((item) => item.worker_id === editing.id).forEach((submission) => {
      rows.push({
        id:`submission-${submission.id}`,
        date:submission.submitted_at,
        category:"reports",
        label:submission.source === "team_leader" ? "Déclaration du responsable" : "Déclaration individuelle",
        title:submission.result === "completed" ? "Mission terminée" : submission.result === "issue" ? "Incident signalé" : "Suivi demandé",
        detail:`${submission.actual_hours} h · ${submission.observations || submission.client_note || "Aucune observation"}`,
        missionId:submission.mission_id,
      });
      if (submission.result !== "completed") rows.push({
        id:`incident-${submission.id}`,
        date:submission.submitted_at,
        category:"incidents",
        label:"Incident",
        title:submission.result === "issue" ? "Incident signalé" : "Action de suivi demandée",
        detail:submission.observations || submission.client_note || "Aucun détail fourni",
        missionId:submission.mission_id,
      });
    });
    data.times.filter((item) => item.worker_id === editing.id).forEach((item) => rows.push({id:`time-${item.id}`,date:item.work_date,category:"time",label:"Pointage",title:`${hours(item.worked_minutes)} enregistrées`,detail:`Pause ${item.break_minutes} min · ${labels[item.status] || item.status}`}));
    data.leaves.filter((item) => item.worker_id === editing.id).forEach((item) => rows.push({id:`leave-${item.id}`,date:item.starts_on,category:"leave",label:"Congé",title:`Du ${frDate(item.starts_on)} au ${frDate(item.ends_on)}`,detail:`${labels[item.status] || item.status}${item.reason ? ` · ${item.reason}` : ""}`}));
    data.mileages.filter((item) => item.worker_id === editing.id).forEach((item) => rows.push({id:`mileage-${item.id}`,date:item.travel_date,category:"mileage",label:"IK",title:`${item.kilometers.toLocaleString("fr-FR")} km`,detail:`${labels[item.status] || item.status} · ${item.calculated_amount == null ? "Montant à contrôler" : money(item.calculated_amount)}`,missionId:item.mission_id || undefined}));
    data.requirements.filter((item) => item.worker_id === editing.id).forEach((item) => rows.push({id:`requirement-${item.id}`,date:item.due_on || `${year}-12-31`,category:"documents",label:"Document",title:item.label,detail:`${labels[item.status] || item.status}${item.due_on ? ` · échéance ${frDate(item.due_on)}` : ""}`}));
    if (selectedWorker?.profile_id) data.documents.filter((item) => item.owner_profile_id === selectedWorker.profile_id).forEach((item) => rows.push({id:`document-${item.id}`,date:item.created_at || item.expires_on || `${year}-01-01`,category:"documents",label:"Dépôt",title:item.original_name,detail:`${item.kind}${item.status ? ` · ${labels[item.status] || item.status}` : ""}`}));
    data.contracts.filter((item) => item.worker_id === editing.id).forEach((item) => rows.push({id:`contract-${item.id}`,date:item.signed_at || item.sent_at || item.generated_at || item.starts_on || `${year}-01-01`,category:"contracts",label:"Contrat",title:item.title,detail:`${labels[item.status] || item.status}${item.ends_on ? ` · fin ${frDate(item.ends_on)}` : ""}`}));
    (data.profileChangeRequests || []).filter((item) => item.target_type === "worker" && item.target_id === editing.id).forEach((item) => rows.push({id:`change-${item.id}`,date:item.created_at,category:"changes",label:"Modification",title:"Demande de modification de la fiche",detail:`${labels[item.status] || item.status}${item.admin_notes ? ` · ${item.admin_notes}` : ""}`}));
    (data.missionQualityClaims || []).filter((claim)=>selectedMissions.some((mission)=>mission.id===claim.mission_id)).forEach((claim)=>rows.push({id:`quality-claim-${claim.id}`,date:claim.created_at,category:"incidents",label:"Retour client",title:`${labels[claim.status] || claim.status} · ${claim.admin_qualification ? labels[claim.admin_qualification] || claim.admin_qualification : "non qualifié"}`,detail:claim.description,missionId:claim.mission_id}));
    const query = historyQuery.trim().toLocaleLowerCase("fr-FR");
    return rows.filter((item) => {
      const day = item.date.slice(0,10);
      return (historyCategory === "all" || item.category === historyCategory) && (!historyFrom || day >= historyFrom) && (!historyTo || day <= historyTo) && (!query || `${item.label} ${item.title} ${item.detail}`.toLocaleLowerCase("fr-FR").includes(query));
    }).sort((a,b) => b.date.localeCompare(a.date));
  }, [editing.id, selectedWorker?.profile_id, selectedMissions, data.clients, data.properties, data.services, data.propertyServices, data.reports, data.missionReportSubmissions, data.times, data.leaves, data.mileages, data.requirements, data.documents, data.contracts, data.profileChangeRequests, data.missionQualityClaims, historyQuery, historyCategory, historyFrom, historyTo, year]);
  const workerHistory = !isShowcaseMode && remoteHistoryLoaded ? remoteWorkerHistory : localWorkerHistory;
  useEffect(() => {
    let cancelled = false;
    if (!workerOpen || !editing.id || isShowcaseMode) {
      setRemoteHistoryLoaded(false);
      setHistoryLoading(false);
      setHistoryError("");
      return;
    }
    setHistoryLoading(true);
    setHistoryError("");
    const timer = window.setTimeout(async () => {
      const result = await supabase!.rpc("get_worker_activity_history", {
        p_worker_id: editing.id,
        p_from: historyFrom || null,
        p_to: historyTo || null,
        p_category: historyCategory,
        p_search: historyQuery.trim() || null,
        p_limit: 200,
        p_offset: 0,
      });
      if (cancelled) return;
      setHistoryLoading(false);
      if (result.error) {
        setRemoteHistoryLoaded(false);
        if (!/get_worker_activity_history|schema cache|does not exist|could not find the function/i.test(result.error.message))
          setHistoryError(result.error.message);
        return;
      }
      setRemoteWorkerHistory(((result.data || []) as Array<{event_id:string;event_at:string;category:string;label:string;title:string;detail:string;mission_id:string|null}>).map((item) => ({
        id:item.event_id,
        date:item.event_at,
        category:item.category,
        label:item.label,
        title:item.title,
        detail:item.detail,
        missionId:item.mission_id || undefined,
      })));
      setRemoteHistoryLoaded(true);
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [workerOpen, editing.id, historyFrom, historyTo, historyCategory, historyQuery]);
  const select = (w?: Worker) => {
    setEditing(w ? { ...w } : blank);
    setFinancialWorkerOpen(false);
    setMessage("");
    setHistoryQuery("");
    setHistoryCategory("all");
    setHistoryFrom("");
    setHistoryTo("");
    setRemoteWorkerHistory([]);
    setRemoteHistoryLoaded(false);
    setHistoryError("");
    setWorkerOpen(true);
  };
  useEffect(() => {
    if (!focusWorkerId) return;
    const worker = data.workers.find((candidate) => candidate.id === focusWorkerId);
    if (worker) select(worker);
    onFocusWorkerConsumed?.();
  }, [focusWorkerId]);

  async function sendDocumentReminder(requirement: Requirement) {
    if (!editing.id) return;
    const worker = data.workers.find((candidate) => candidate.id === editing.id);
    if (!worker?.profile_id) {
      setMessage("Créez ou invitez d’abord le compte de cette personne pour lui transmettre un rappel dans l’application.");
      return;
    }
    const { error } = await supabase!.from("notifications").insert({
      organization_id: profile.organization_id,
      recipient_profile_id: worker.profile_id,
      kind: "document_reminder",
      title: "Document à renouveler",
      body: `${requirement.label}${requirement.due_on ? ` · échéance ${frDate(requirement.due_on)}` : ""}`,
    });
    setMessage(
      error
        ? error.message
        : "Rappel transmis dans l’application. L’envoi par e-mail sera proposé séparément pour éviter tout envoi involontaire.",
    );
    if (!error) reload();
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      organization_id: profile.organization_id,
      kind: editing.kind,
      first_name: editing.first_name || null,
      last_name: editing.last_name || null,
      contact_email: editing.contact_email?.trim().toLowerCase() || null,
      contact_phone: editing.contact_phone?.trim() || null,
      address: editing.address?.trim() || null,
      business_name:
        editing.kind === "contractor" ? editing.business_name?.trim() || null : null,
      siret: editing.kind === "contractor" ? editing.siret?.trim() || null : null,
      vat_number:
        editing.kind === "contractor" ? editing.vat_number?.trim() || null : null,
      profile_notes: editing.profile_notes?.trim() || null,
      skills: uniqueSkills(editing.skills || []),
      monthly_hours: +(editing.monthly_hours || 0),
      daily_hours: +(editing.daily_hours || 0),
      net_salary:
        editing.kind === "employee" ? +(editing.net_salary || 0) : null,
      employer_cost_monthly:
        editing.kind === "employee"
          ? +(editing.employer_cost_monthly || 0) || null
          : null,
      ticket_count:
        editing.kind === "employee" ? +(editing.ticket_count || 0) : null,
      ticket_value:
        editing.kind === "employee" ? +(editing.ticket_value || 0) : null,
      mileage_enabled: !!editing.mileage_enabled,
      pricing_mode: editing.pricing_mode || null,
      hourly_rate: +(editing.hourly_rate || 0) || null,
      fixed_fee: +(editing.fixed_fee || 0) || null,
      monthly_fee: +(editing.monthly_fee || 0) || null,
      active: editing.id ? editing.active !== false : true,
    };
    const result = editing.id
      ? await supabase!.from("workers").update(payload).eq("id", editing.id)
      : await supabase!.from("workers").insert(payload);
    setMessage(
      result.error
        ? result.error.message
        : editing.id
          ? "Intervenant mis à jour."
          : "Intervenant ajouté.",
    );
    if (!result.error) {
      setWorkerOpen(false);
      reload();
    }
  }
  async function toggleWorkerStatus() {
    if (!editing.id) return;
    const nextActive = editing.active === false;
    const { error } = await supabase!
      .from("workers")
      .update({ active: nextActive })
      .eq("id", editing.id);
    setMessage(
      error
        ? error.message
        : nextActive
          ? "Fiche réactivée."
          : "Fiche archivée. Les historiques sont conservés.",
    );
    if (!error) {
      setWorkerOpen(false);
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <h1>Équipe</h1>
        <button onClick={() => select()}>Ajouter une personne</button>
      </div>
      <OrganizationLeaderManager profile={profile} data={data} reload={reload} />
      <section className="panel table-wrap team-directory-panel">
        <table className="team-directory-table">
          <thead>
            <tr>
              <th>Personne</th>
              <th>Statut</th>
              <th>Rémunération</th>
              <th>Objectif</th>
              <th>Réalisé</th>
              <th>IK</th>
              <th>Accès &amp; conformité</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.workers.map((w) => {
              const access = organizationAccessSummary(
                  "worker",
                  w.id,
                  Boolean(w.profile_id),
                  data.organizationUserInvitations,
                  data.profiles?.find((profile) => profile.id === w.profile_id)?.access_status,
                ),
                currentWorkerContract = data.contracts.find(
                  (contract) => contract.worker_id === w.id && !["expired", "terminated"].includes(contract.status),
                ),
                openRequirements = data.requirements.filter(
                  (requirement) => requirement.worker_id === w.id && !["validated", "waived"].includes(requirement.status),
                ).length;
              return <tr key={w.id} className={!w.active ? "archived" : ""}>
                <td>
                  <strong>{workerName(w)}</strong>
                  <small>{w.contact_email || w.contact_phone || "Coordonnées à compléter"}</small>
                </td>
                <td>
                  {w.kind === "employee" ? "Employé" : "Sous-traitant"}
                  {!w.active && <small> · Archivé</small>}
                </td>
                <td>
                  {w.kind === "contractor" &&
                  (data.workerRateCards || []).some(
                    (rate) => rate.worker_id === w.id && rate.active,
                  )
                    ? `${(data.workerRateCards || []).filter((rate) => rate.worker_id === w.id && rate.active).length} tarif(s)`
                    : pricing[w.pricing_mode || ""] || "Non définie"}
                </td>
                <td>{w.kind === "employee" ? `${w.monthly_hours} h` : "—"}</td>
                <td>
                  {hours(
                    data.times
                      .filter(
                        (t) =>
                          t.worker_id === w.id &&
                          t.work_date.startsWith(month) &&
                          t.status !== "cancelled",
                      )
                      .reduce((s, t) => s + t.worked_minutes, 0),
                  )}
                </td>
                <td>{w.mileage_enabled ? "Oui" : "Non"}</td>
                <td>
                  <div className="directory-readiness">
                    <span className={`status-badge ${accessToneClass(access.tone)}`}>{access.label}</span>
                    <small>{access.detail}</small>
                    <small>{currentWorkerContract ? "Contrat présent" : "Contrat à préparer"} · {openRequirements ? `${openRequirements} document(s) à traiter` : "documents à jour"}</small>
                  </div>
                </td>
                <td>
                  <button
                    className="secondary compact"
                    onClick={() => select(w)}
                  >
                    Informations
                  </button>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
        {!data.workers.length && (
          <p className="empty">Aucun membre accessible.</p>
        )}
      </section>
      {workerOpen && <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Fiche intervenant">
      <section className="portal-detail worker-detail-modal">
        <div className="portal-detail-head">
          <div>
            <span className="eyebrow">Équipe &amp; RH</span>
            <h2>{editing.id ? workerName(editing as Worker) : "Ajouter une personne"}</h2>
          </div>
          <div className="row-actions">
            {editing.id && editing.kind === "contractor" && (
              <button type="button" className="secondary compact" onClick={() => setFinancialWorkerOpen(true)}>
                Synthèse financière
              </button>
            )}
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setWorkerOpen(false)}>×</button>
          </div>
        </div>
        {editing.id && <section className="worker-identity-summary" aria-label="Résumé de la fiche personne">
          <div>
            <span className="eyebrow">Identité et accès</span>
            <strong>{selectedAccess?.label || "Accès à contrôler"} · {editing.kind === "employee" ? "Employé" : "Sous-traitant"}</strong>
            <small>{selectedAccess?.detail || "Fiche sans accès"} · {editing.active === false ? "fiche archivée" : "fiche active"}</small>
            <small>{editing.contact_email || "E-mail à compléter"}{editing.contact_phone ? ` · ${editing.contact_phone}` : " · téléphone à compléter"}</small>
          </div>
          <div>
            <span className="eyebrow">Contrat</span>
            <strong>{currentContract ? currentContract.title : "Aucun contrat préparé"}</strong>
            <small>{currentContract ? `${labels[currentContract.status] || currentContract.status}${currentContract.ends_on ? ` · fin ${frDate(currentContract.ends_on)}` : ""}` : "À préparer depuis le dossier contractuel"}</small>
          </div>
          <div>
            <span className="eyebrow">Alertes</span>
            <strong>{pendingRequirements.length + workerIncidentCount} élément(s) à suivre</strong>
            <small>{pendingRequirements.length} document(s) · {workerIncidentCount} mission(s) avec incident ou suivi</small>
          </div>
          <div className="worker-identity-actions">
            {editing.contact_email && <a className="button secondary compact" href={`mailto:${editing.contact_email}`}>Écrire</a>}
            {editing.contact_phone && <a className="button secondary compact" href={`tel:${editing.contact_phone}`}>Appeler</a>}
          </div>
        </section>}
        {editing.id && <div className="stats worker-summary">
          <article><span>État</span><strong>{editing.active === false ? "Archivé" : "Actif"}</strong></article>
          <article><span>Missions</span><strong>{selectedMissions.length}</strong></article>
          <article><span>Heures du mois</span><strong>{hours(workerMonthMinutes)}</strong><small>{editing.kind === "employee" ? `objectif ${editing.monthly_hours || 0} h` : "pointages validés et en attente"}</small></article>
          <article><span>Heures de l’année</span><strong>{hours(workerYearMinutes)}</strong></article>
          <article><span>Congés de l’année</span><strong>{workerLeaveDays} j</strong></article>
          <article><span>IK de l’année</span><strong>{workerYearMileage.toLocaleString("fr-FR")} km</strong><small>{editing.mileage_enabled ? "autorisées" : "non autorisées"}</small></article>
          <article><span>Incidents signalés</span><strong>{workerIncidentCount}</strong></article>
          {workerQualityMetric && <>
            <article><span>Retours client contrôlés</span><strong>{workerQualityMetric.reviewed_claim_count}</strong><small>{workerQualityMetric.total_claim_count-workerQualityMetric.reviewed_claim_count} encore non qualifié(s)</small></article>
            <article><span>Taux de retour vérifié</span><strong>{Number(workerQualityMetric.verified_return_rate).toLocaleString("fr-FR",{maximumFractionDigits:1})} %</strong><small>fondé + 50 % des partiellement fondés</small></article>
            <article><span>Retouches suivies</span><strong>{workerQualityMetric.touchup_count}</strong><small>sur {workerQualityMetric.completed_mission_count} mission(s) terminée(s)</small></article>
          </>}
          <article><span>Documents à traiter</span><strong>{pendingRequirements.length}</strong></article>
        </div>}
        {editing.id && <details className="billing-kpi-guide worker-kpi-guide" data-testid="worker-kpi-guide">
          <summary>Comprendre la synthèse RH</summary>
          <p className="section-intro">
            Ces indicateurs concernent uniquement la personne affichée. Ils
            rapprochent son activité, ses temps, ses déplacements, ses absences,
            la qualité contrôlée et les documents encore à traiter.
          </p>
          <div className="billing-kpi-guide-grid">
            <article><strong>État</strong><span>État administratif de la fiche : active ou archivée. Archiver conserve tout l’historique.</span></article>
            <article><strong>Missions</strong><span>Nombre de missions auxquelles la personne est affectée dans les données accessibles, quel que soit leur état.</span></article>
            <article><strong>Heures du mois</strong><span>Somme des temps saisis sur le mois courant, hors pointages annulés. Pour un employé, l’objectif contractuel reste affiché séparément.</span></article>
            <article><strong>Heures de l’année</strong><span>Somme des temps saisis depuis le début de l’année, hors pointages annulés. Une saisie manquante ne vaut jamais zéro heure travaillée.</span></article>
            <article><strong>Congés de l’année</strong><span>Jours calendaires des absences commençant dans l’année, hors demandes refusées. Ce total n’est pas un compteur de paie.</span></article>
            <article><strong>IK de l’année</strong><span>Kilomètres déclarés depuis le début de l’année, hors lignes rejetées. Ils ne deviennent pas automatiquement un montant à payer.</span></article>
            <article><strong>Incidents signalés</strong><span>Nombre de missions distinctes comportant un résultat incomplet, un incident ou une observation nécessitant un suivi.</span></article>
            <article><strong>Retours client contrôlés</strong><span>Réclamations déjà qualifiées par l’administration. Les retours encore non qualifiés sont indiqués séparément.</span></article>
            <article><strong>Taux de retour vérifié</strong><span>Retours fondés, augmentés de la moitié des retours partiellement fondés, rapportés aux missions terminées suivies.</span></article>
            <article><strong>Retouches suivies</strong><span>Retouches réellement enregistrées sur les missions terminées de la personne. Aucune pénalité ni prime n’est décidée automatiquement.</span></article>
            <article><strong>Documents à traiter</strong><span>Pièces demandées qui ne sont ni validées ni explicitement dispensées.</span></article>
          </div>
          <p className="billing-kpi-guide-limit">
            <strong>Limite :</strong> cette synthèse aide à préparer une décision
            RH. Elle ne remplace ni le contrat, ni le contrôle des comptes rendus,
            ni la paie, ni une décision humaine sur une prime ou une pénalité.
          </p>
        </details>}
        {editing.id && <details className="panel file-chapter worker-history-panel">
          <summary>
            <span>
              <strong>Dossier permanent et historique</strong>
              <small>Missions, comptes rendus, incidents, temps, congés et modifications</small>
            </span>
            <span className="file-chapter-summary-value">{workerHistory.length} élément(s)</span>
          </summary>
          <div className="file-chapter-body">
          <div className="section-heading">
            <div><span className="eyebrow">Dossier permanent</span><h3>Historique d’activité</h3><p>Les éléments restent consultables même si la fiche est archivée.</p></div>
            <strong>{workerHistory.length}</strong>
          </div>
          <div className="worker-history-filters">
            <label>Rechercher<input type="search" value={historyQuery} onChange={(event)=>setHistoryQuery(event.target.value)} placeholder="Client, mission, observation…" /></label>
            <label>Catégorie<select aria-label="Catégorie de l’historique" value={historyCategory} onChange={(event)=>setHistoryCategory(event.target.value)}><option value="all">Toutes</option><option value="missions">Missions</option><option value="reports">Comptes rendus</option><option value="incidents">Incidents et suivis</option><option value="time">Pointages</option><option value="leave">Congés</option><option value="mileage">IK</option><option value="documents">Documents</option><option value="contracts">Contrats</option><option value="changes">Modifications</option></select></label>
            <label>Du<input type="date" value={historyFrom} onChange={(event)=>setHistoryFrom(event.target.value)} /></label>
            <label>Au<input type="date" value={historyTo} onChange={(event)=>setHistoryTo(event.target.value)} /></label>
          </div>
          {historyLoading&&<p className="section-intro">Mise à jour de l’historique sécurisé…</p>}
          {historyError&&<Notice tone="error">Historique sécurisé indisponible : {historyError}</Notice>}
          <div className="worker-history-list">
            {workerHistory.slice(0,200).map((item)=>{
              const mission=item.missionId ? data.missions.find((candidate)=>candidate.id===item.missionId) : undefined;
              return <article className="worker-history-row" key={item.id}><time dateTime={item.date}>{frDate(item.date,item.date.includes("T"))}</time><span className="status-badge">{item.label}</span><div><strong>{item.title}</strong><small>{item.detail}</small></div>{mission&&onSelectMission?<button type="button" className="secondary compact" onClick={()=>{setWorkerOpen(false);onSelectMission(mission);}}>Ouvrir la mission</button>:<span />}</article>;
            })}
            {!workerHistory.length&&<p className="empty">Aucun élément ne correspond aux filtres.</p>}
            {workerHistory.length>=200&&<p className="section-intro">Les 200 éléments les plus récents sont affichés. Affinez la recherche ou les dates pour cibler une période précise.</p>}
          </div>
          </div>
        </details>}
        {editing.id && <details className="file-chapter worker-file-chapter">
          <summary>
            <span><strong>Habilitation astreinte</strong><small>Niveaux autorisés et disponibilité dans les rotations</small></span>
            <span className="file-chapter-summary-value">Contrôler</span>
          </summary>
          <div className="file-chapter-body"><WorkerOnCallEligibility profileId={editing.profile_id || null} /></div>
        </details>}
        <details className="file-chapter worker-file-chapter" {...(!editing.id ? { open: true } : {})}>
          <summary>
            <span><strong>Identité, coordonnées et rémunération</strong><small>Informations personnelles, contrat de travail et règles de paiement</small></span>
            <span className="file-chapter-summary-value">{editing.kind === "employee" ? "Employé" : "Sous-traitant"}</span>
          </summary>
          <div className="file-chapter-body">
        <form className="form-grid" onSubmit={save}>
          <label>
            Statut
            <select
              value={editing.kind || "employee"}
              onChange={(e) =>
                setEditing((v) => ({
                  ...v,
                  kind: e.target.value as Worker["kind"],
                  pricing_mode:
                    e.target.value === "employee" ? "salary" : "hourly",
                }))
              }
            >
              <option value="employee">Employé</option>
              <option value="contractor">Sous-traitant</option>
            </select>
          </label>
          <label>
            Mode de rémunération
            <select
              value={editing.pricing_mode || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, pricing_mode: e.target.value }))
              }
            >
              {editing.kind === "employee" ? (
                <option value="salary">Salaire mensuel</option>
              ) : (
                <>
                  <option value="hourly">Taux horaire</option>
                  <option value="fixed">Forfait</option>
                  <option value="mixed">Mixte</option>
                  <option value="monthly">Mensuel</option>
                </>
              )}
            </select>
          </label>
          <label>
            Prénom
            <input
              value={editing.first_name || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, first_name: e.target.value }))
              }
            />
          </label>
          <label>
            Nom
            <input
              value={editing.last_name || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, last_name: e.target.value }))
              }
              required
            />
          </label>
          <label>
            E-mail de contact
            <input
              type="email"
              value={editing.contact_email || ""}
              onChange={(e) =>
                setEditing((v) => v ? ({ ...v, contact_email: e.target.value }) : v)
              }
              placeholder="nom@entreprise.fr"
            />
          </label>
          <label>
            Téléphone
            <input
              type="tel"
              value={editing.contact_phone || ""}
              onChange={(e) =>
                setEditing((v) => v ? ({ ...v, contact_phone: e.target.value }) : v)
              }
            />
          </label>
          <label className="wide">
            Adresse
            <input
              value={editing.address || ""}
              onChange={(e) =>
                setEditing((v) => v ? ({ ...v, address: e.target.value }) : v)
              }
            />
          </label>
          {editing.kind === "employee" && (
            <>
              <label>
                Salaire net mensuel (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.net_salary || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, net_salary: +e.target.value }))
                  }
                />
              </label>
              <label>
                Coût employeur mensuel (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.employer_cost_monthly || 0}
                  onChange={(e) =>
                    setEditing((v) => ({
                      ...v,
                      employer_cost_monthly: +e.target.value,
                    }))
                  }
                />
                <small>
                  À renseigner pour calculer un coût réel ; sinon le salaire net
                  sera affiché comme estimation incomplète.
                </small>
              </label>
              <label>
                Objectif mensuel (h)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.monthly_hours || 0}
                  onChange={(e) =>
                    setEditing((v) => ({
                      ...v,
                      monthly_hours: +e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Heures par jour
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={editing.daily_hours || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, daily_hours: +e.target.value }))
                  }
                />
              </label>
              <label>
                Tickets restaurant / mois
                <input
                  type="number"
                  min="0"
                  value={editing.ticket_count || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, ticket_count: +e.target.value }))
                  }
                />
              </label>
              <label>
                Valeur d’un ticket (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.ticket_value || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, ticket_value: +e.target.value }))
                  }
                />
              </label>
            </>
          )}
          {editing.kind === "contractor" && (
            <>
              <label>
                Raison sociale
                <input value={editing.business_name || ""} onChange={(e) => setEditing((v) => v ? ({...v,business_name:e.target.value}) : v)} />
              </label>
              <label>
                SIRET
                <input value={editing.siret || ""} onChange={(e) => setEditing((v) => v ? ({...v,siret:e.target.value}) : v)} />
              </label>
              <label>
                N° TVA
                <input value={editing.vat_number || ""} onChange={(e) => setEditing((v) => v ? ({...v,vat_number:e.target.value}) : v)} />
              </label>
              <label>
                Taux horaire (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.hourly_rate || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, hourly_rate: +e.target.value }))
                  }
                />
              </label>
              <label>
                Forfait (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.fixed_fee || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, fixed_fee: +e.target.value }))
                  }
                />
              </label>
              <label>
                Mensuel (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.monthly_fee || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, monthly_fee: +e.target.value }))
                  }
                />
              </label>
            </>
          )}
          <label className="check">
            <input
              type="checkbox"
              checked={!!editing.mileage_enabled}
              onChange={(e) =>
                setEditing((v) => ({ ...v, mileage_enabled: e.target.checked }))
              }
            />{" "}
            IK autorisées
          </label>
          <label className="wide">
            Compétences validées
            <input
              aria-label="Compétences validées"
              value={(editing.skills || []).join(", ")}
              onChange={(e) =>
                setEditing((value) => ({
                  ...value,
                  skills: e.target.value.split(/[,;\n]/),
                }))
              }
              placeholder="Ex. Traitement de sols, utilisation monobrosse"
            />
            <small>Séparez les compétences par une virgule. Elles servent à sécuriser les affectations.</small>
          </label>
          <label className="wide">
            Notes internes
            <textarea value={editing.profile_notes || ""} onChange={(e) => setEditing((v) => v ? ({...v,profile_notes:e.target.value}) : v)} />
          </label>
          <button>
            {editing.id
              ? "Enregistrer les modifications"
              : "Ajouter à l’équipe"}
          </button>
        </form>
          </div>
        </details>
        {editing.id && (
          <details className="panel file-chapter worker-compliance-summary">
            <summary>
              <span><strong>Conformité et échéances</strong><small>Documents obligatoires et rappels</small></span>
              <span className="file-chapter-summary-value">{pendingRequirements.length} à traiter</span>
            </summary>
            <div className="file-chapter-body">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Conformité documentaire</span>
                <h3>Documents et échéances</h3>
              </div>
              <strong>
                {data.requirements.filter((item) => item.worker_id === editing.id).length}
              </strong>
            </div>
            {data.requirements
              .filter((item) => item.worker_id === editing.id)
              .map((requirement) => (
                <article className="mini-record" key={requirement.id}>
                  <div>
                    <strong>{requirement.label}</strong>
                    <small>
                      {labels[requirement.status] || requirement.status}
                      {requirement.due_on ? ` · échéance ${frDate(requirement.due_on)}` : ""}
                    </small>
                  </div>
                  {!['validated', 'waived'].includes(requirement.status) && (
                    <button
                      type="button"
                      className="secondary compact"
                      onClick={() => sendDocumentReminder(requirement)}
                    >
                      Envoyer un rappel
                    </button>
                  )}
                </article>
              ))}
            {!data.requirements.some((item) => item.worker_id === editing.id) && (
              <p className="empty">Aucune exigence documentaire enregistrée.</p>
            )}
            </div>
          </details>
        )}
        {editing.id&&<details className="file-chapter worker-file-chapter">
          <summary>
            <span><strong>Compte de connexion</strong><small>Invitation, activation, suspension et adresse d’accès</small></span>
            <span className="file-chapter-summary-value">{editing.profile_id ? "Espace relié" : "À inviter"}</span>
          </summary>
          <div className="file-chapter-body"><AccountInvitationPanel
          targetType="worker"
          targetId={editing.id}
          displayName={[editing.first_name,editing.last_name].filter(Boolean).join(" ")||"Intervenant"}
          hasAccount={!!editing.profile_id}
          accountEmail={data.profiles?.find((item)=>item.id===editing.profile_id)?.email || editing.contact_email}
          accountProfile={data.profiles?.find((item)=>item.id===editing.profile_id)}
          onChanged={reload}
        /></div></details>}
        {editing.id && editing.kind === "employee" && <details className="file-chapter worker-file-chapter" open>
          <summary>
            <span><strong>Parcours RH, objectifs et primes</strong><small>Entretiens, évolution, objectifs mesurés et décisions partagées</small></span>
            <span className="file-chapter-summary-value">Piloter</span>
          </summary>
          <div className="file-chapter-body"><AdminWorkerDevelopment workers={data.workers} initialWorkerId={editing.id} lockWorkerSelection /></div>
        </details>}
        {editing.id && <details className="file-chapter worker-file-chapter" {...(editing.kind === "employee" ? { open: true } : {})}>
          <summary>
            <span><strong>Dossier contractuel</strong><small>Contrat, signature et règles d’annulation</small></span>
            <span className="file-chapter-summary-value">{workerContracts.length} document(s)</span>
          </summary>
          <div className="file-chapter-body"><WorkerContractAdmin
          worker={editing as Worker}
          contracts={workerContracts}
          envelopes={data.signatureEnvelopes || []}
          cancellationRules={(data.workerCancellationRules || []).filter((item) => item.worker_id === editing.id)}
          reload={reload}
        /></div></details>}
        {message && (
          <Notice
            tone={
              message.includes("mis à jour") || message.includes("ajouté")
                ? "success"
                : "error"
            }
          >
            {message}
          </Notice>
        )}
        <p className="section-intro">Le compte de connexion est invité depuis cette fiche. L’employé ou le sous-traitant choisit lui-même son mot de passe et ne peut pas modifier son rôle.</p>
        {editing.id && <div className="detail-actions">
          <button type="button" className={editing.active === false ? "secondary" : "danger"} onClick={toggleWorkerStatus}>
            {editing.active === false ? "Réactiver la fiche" : "Archiver la fiche"}
          </button>
        </div>}
      {editing.id && editing.kind === "contractor" && (
        <details className="file-chapter worker-file-chapter">
          <summary>
            <span><strong>Société, conformité et tarifs</strong><small>Dossier entreprise, représentants et conditions commerciales</small></span>
            <span className="file-chapter-summary-value">Sous-traitant</span>
          </summary>
          <div className="file-chapter-body">
          {hasDeploymentCapability(data, "worker_company_dossier") ? (
            <WorkerCompanyDossierManager
              worker={editing as Worker}
              companyProfile={(data.workerCompanyProfiles || []).find(
                (item) => item.worker_id === editing.id,
              )}
              representatives={(data.workerCompanyRepresentatives || []).filter(
                (item) => item.worker_id === editing.id,
              )}
              events={(data.workerCompanyProfileEvents || []).filter(
                (item) => item.worker_id === editing.id,
              )}
              reload={reload}
            />
          ) : (
            <Notice>
              Le dossier entreprise ST avancé est préparé mais non activé sur cette base. La fiche de contact, les tarifs, contrats et documents existants restent disponibles.
            </Notice>
          )}
          <WorkerRateCardManager
            profile={profile}
            worker={editing as Worker}
            rates={(data.workerRateCards || []).filter(
              (rate) => rate.worker_id === editing.id,
            )}
            clients={data.clients}
            properties={data.properties}
            propertyServices={data.propertyServices}
            reload={reload}
          />
          </div>
        </details>
      )}
      </section>
      </div>}
      {selectedWorker && selectedWorker.kind === "contractor" && financialWorkerOpen && (
        <AdminEntityFinancialSummary
          data={data}
          subject={{ type: "worker", worker: selectedWorker }}
          onClose={() => setFinancialWorkerOpen(false)}
        />
      )}
    </>
  );
}

function WorkerCompanyDossierManager({
  worker,
  companyProfile,
  representatives,
  events,
  reload,
}: {
  worker: Worker;
  companyProfile?: WorkerCompanyProfile;
  representatives: WorkerCompanyRepresentative[];
  events: WorkerCompanyProfileEvent[];
  reload: () => void;
}) {
  const [open, setOpen] = useState(false),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [draft, setDraft] = useState({
      legal_name: "",
      legal_form: "",
      siren: "",
      registered_address: "",
      postal_code: "",
      city: "",
      country: "France",
      website: "",
      billing_email: "",
      authorized_services: "",
      service_areas: "",
      compliance_status: "to_review" as WorkerCompanyProfile["compliance_status"],
      restriction_reason: "",
      bank_name: "",
      account_holder: "",
      iban: "",
      bic: "",
      review_note: "",
    }),
    [representativeEditor, setRepresentativeEditor] = useState<{
      id?: string;
      first_name: string;
      last_name: string;
      title: string;
      email: string;
      phone: string;
      is_primary: boolean;
    } | null>(null);
  const activeRepresentatives = representatives.filter((item) => item.active),
    maskedIban = companyProfile?.iban
      ? `•••• ${companyProfile.iban.slice(-4)}`
      : "Non renseigné";
  function start() {
    setDraft({
      legal_name: companyProfile?.legal_name || worker.business_name || "",
      legal_form: companyProfile?.legal_form || "",
      siren: companyProfile?.siren || worker.siret?.replace(/\s/g, "").slice(0, 9) || "",
      registered_address: companyProfile?.registered_address || worker.address || "",
      postal_code: companyProfile?.postal_code || "",
      city: companyProfile?.city || "",
      country: companyProfile?.country || "France",
      website: companyProfile?.website || "",
      billing_email: companyProfile?.billing_email || worker.contact_email || "",
      authorized_services: (companyProfile?.authorized_services || []).join("\n"),
      service_areas: (companyProfile?.service_areas || []).join("\n"),
      compliance_status: companyProfile?.compliance_status || "to_review",
      restriction_reason: companyProfile?.restriction_reason || "",
      bank_name: companyProfile?.bank_name || "",
      account_holder: companyProfile?.account_holder || companyProfile?.legal_name || worker.business_name || "",
      iban: companyProfile?.iban || "",
      bic: companyProfile?.bic || "",
      review_note: "",
    });
    setMessage("");
    setRepresentativeEditor(null);
    setOpen(true);
  }
  const list = (value: string) =>
    value
      .split(/[\n,;]/)
      .map((item) => item.trim())
      .filter(Boolean);
  async function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    const result = await supabase!.rpc("save_worker_company_profile", {
      p_worker_id: worker.id,
      p_legal_name: draft.legal_name,
      p_legal_form: draft.legal_form,
      p_siren: draft.siren,
      p_registered_address: draft.registered_address,
      p_postal_code: draft.postal_code,
      p_city: draft.city,
      p_country: draft.country,
      p_website: draft.website,
      p_billing_email: draft.billing_email,
      p_authorized_services: list(draft.authorized_services),
      p_service_areas: list(draft.service_areas),
      p_compliance_status: draft.compliance_status,
      p_restriction_reason: draft.restriction_reason,
      p_bank_name: draft.bank_name,
      p_account_holder: draft.account_holder,
      p_iban: draft.iban,
      p_bic: draft.bic,
      p_review_note: draft.review_note,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Dossier entreprise enregistré et contrôle historisé.",
    );
    if (!result.error) reload();
  }
  function editRepresentative(item?: WorkerCompanyRepresentative) {
    setRepresentativeEditor(
      item
        ? {
            id: item.id,
            first_name: item.first_name || "",
            last_name: item.last_name,
            title: item.title,
            email: item.email || "",
            phone: item.phone || "",
            is_primary: item.is_primary,
          }
        : {
            first_name: "",
            last_name: "",
            title: "",
            email: "",
            phone: "",
            is_primary: !activeRepresentatives.length,
          },
    );
  }
  async function saveRepresentative(event: React.FormEvent) {
    event.preventDefault();
    if (!representativeEditor) return;
    setBusy(true);
    const result = await supabase!.rpc("save_worker_company_representative", {
      p_worker_id: worker.id,
      p_representative_id: representativeEditor.id || null,
      p_first_name: representativeEditor.first_name,
      p_last_name: representativeEditor.last_name,
      p_title: representativeEditor.title,
      p_email: representativeEditor.email,
      p_phone: representativeEditor.phone,
      p_is_primary: representativeEditor.is_primary,
    });
    setBusy(false);
    setMessage(
      result.error ? result.error.message : "Représentant enregistré dans le dossier.",
    );
    if (!result.error) {
      setRepresentativeEditor(null);
      reload();
    }
  }
  async function archiveRepresentative(item: WorkerCompanyRepresentative) {
    const reason = window.prompt(
      `Motif d’archivage de ${[item.first_name, item.last_name].filter(Boolean).join(" ")} :`,
    );
    if (!reason) return;
    setBusy(true);
    const result = await supabase!.rpc("archive_worker_company_representative", {
      p_representative_id: item.id,
      p_reason: reason,
    });
    setBusy(false);
    setMessage(
      result.error ? result.error.message : "Représentant archivé sans effacer l’historique.",
    );
    if (!result.error) reload();
  }
  async function provisionHenrriWorkspace() {
    if (!worker.profile_id) {
      setMessage(
        "Le sous-traitant doit d’abord activer son compte ARD Manager.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    const result = await supabase!.rpc("provision_supplier_company_workspace", {
      p_worker_id: worker.id,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : "Espace entreprise fournisseur activé. Le sous-traitant peut maintenant connecter son propre compte Henrri.",
    );
    if (!result.error) reload();
  }
  return (
    <section className="panel worker-company-dossier-summary">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Dossier entreprise ST</span>
          <h2>{companyProfile?.legal_name || worker.business_name || workerName(worker)}</h2>
        </div>
        <div className="row-actions">
          <button type="button" className="secondary compact" onClick={start}>
            Ouvrir le dossier
          </button>
          <button
            type="button"
            className="secondary compact"
            disabled={busy || !!worker.company_organization_id}
            onClick={provisionHenrriWorkspace}
          >
            {worker.company_organization_id
              ? "Espace fournisseur activé"
              : "Activer l’espace Henrri fournisseur"}
          </button>
        </div>
      </div>
      <div className="stats compact-stats">
        <article><span>Conformité</span><Status value={companyProfile?.compliance_status || "to_review"} /></article>
        <article><span>Représentants</span><strong>{activeRepresentatives.length}</strong></article>
        <article><span>RIB privé</span><strong>{maskedIban}</strong></article>
        <article><span>Prestations autorisées</span><strong>{companyProfile?.authorized_services.length || 0}</strong></article>
        <article><span>Henrri du fournisseur</span><strong>{worker.company_organization_id ? "Espace isolé prêt" : "À activer"}</strong></article>
      </div>
      {!worker.profile_id && (
        <Notice tone="error">
          L’espace fournisseur ne peut être créé qu’après activation du compte ARD Manager du sous-traitant.
        </Notice>
      )}
      {companyProfile?.compliance_status === "blocked" && (
        <Notice tone="error">
          Affectation de mission verrouillée par la base : {companyProfile.restriction_reason}
        </Notice>
      )}
      {open && (
        <div className="portal-overlay dossier-overlay" role="dialog" aria-modal="true" aria-label="Dossier entreprise sous-traitante">
          <section className="portal-detail worker-company-dossier-modal">
            <div className="portal-detail-head">
              <div><span className="eyebrow">Dossier privé et conformité</span><h2>{companyProfile?.legal_name || worker.business_name || workerName(worker)}</h2></div>
              <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setOpen(false)}>×</button>
            </div>
            <form className="form-grid" onSubmit={saveProfile}>
              <div className="form-section-title wide"><strong>Identité légale</strong><small>Ces données alimenteront les contrats et les contrôles documentaires.</small></div>
              <label>Raison sociale<input value={draft.legal_name} onChange={(e) => setDraft((value) => ({ ...value, legal_name: e.target.value }))} /></label>
              <label>Forme juridique<input value={draft.legal_form} onChange={(e) => setDraft((value) => ({ ...value, legal_form: e.target.value }))} placeholder="EI, SAS, SARL…" /></label>
              <label>SIREN<input value={draft.siren} onChange={(e) => setDraft((value) => ({ ...value, siren: e.target.value }))} /></label>
              <label>E-mail facturation<input type="email" value={draft.billing_email} onChange={(e) => setDraft((value) => ({ ...value, billing_email: e.target.value }))} /></label>
              <label className="wide">Adresse du siège<input value={draft.registered_address} onChange={(e) => setDraft((value) => ({ ...value, registered_address: e.target.value }))} /></label>
              <label>Code postal<input value={draft.postal_code} onChange={(e) => setDraft((value) => ({ ...value, postal_code: e.target.value }))} /></label>
              <label>Ville<input value={draft.city} onChange={(e) => setDraft((value) => ({ ...value, city: e.target.value }))} /></label>
              <label>Pays<input value={draft.country} onChange={(e) => setDraft((value) => ({ ...value, country: e.target.value }))} /></label>
              <label>Site internet<input type="url" value={draft.website} onChange={(e) => setDraft((value) => ({ ...value, website: e.target.value }))} /></label>
              <div className="form-section-title wide"><strong>Périmètre opérationnel</strong><small>Une ligne par prestation ou zone. Aucun tarif n’est modifié ici.</small></div>
              <label>Prestations autorisées<textarea value={draft.authorized_services} onChange={(e) => setDraft((value) => ({ ...value, authorized_services: e.target.value }))} placeholder={'Ménage\nVitres\nTraitement des sols'} /></label>
              <label>Zones d’intervention<textarea value={draft.service_areas} onChange={(e) => setDraft((value) => ({ ...value, service_areas: e.target.value }))} placeholder={'Sainte-Maxime\nGolfe de Saint-Tropez'} /></label>
              <div className="form-section-title wide"><strong>Conformité et droit d’affectation</strong><small>Le statut « Bloqué » empêche techniquement toute nouvelle affectation.</small></div>
              <label>Statut<select value={draft.compliance_status} onChange={(e) => setDraft((value) => ({ ...value, compliance_status: e.target.value as WorkerCompanyProfile["compliance_status"] }))}><option value="to_review">À contrôler</option><option value="compliant">Conforme</option><option value="restricted">Restreint</option><option value="blocked">Bloqué</option></select></label>
              <label>Motif de restriction / blocage<input value={draft.restriction_reason} onChange={(e) => setDraft((value) => ({ ...value, restriction_reason: e.target.value }))} disabled={!['restricted','blocked'].includes(draft.compliance_status)} /></label>
              <label className="wide">Note du contrôle humain<textarea value={draft.review_note} onChange={(e) => setDraft((value) => ({ ...value, review_note: e.target.value }))} placeholder="Obligatoire pour débloquer un dossier précédemment bloqué" /></label>
              <div className="form-section-title wide"><strong>RIB privé</strong><small>Visible uniquement par l’administrateur et le sous-traitant propriétaire du dossier.</small></div>
              <label>Banque<input value={draft.bank_name} onChange={(e) => setDraft((value) => ({ ...value, bank_name: e.target.value }))} /></label>
              <label>Titulaire<input value={draft.account_holder} onChange={(e) => setDraft((value) => ({ ...value, account_holder: e.target.value }))} /></label>
              <label className="wide">IBAN<input autoComplete="off" value={draft.iban} onChange={(e) => setDraft((value) => ({ ...value, iban: e.target.value }))} /></label>
              <label>BIC<input autoComplete="off" value={draft.bic} onChange={(e) => setDraft((value) => ({ ...value, bic: e.target.value }))} /></label>
              <button disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer le dossier"}</button>
            </form>
            <section className="panel">
              <div className="section-heading"><div><span className="eyebrow">Contacts juridiques</span><h3>Représentants</h3></div><button type="button" className="secondary compact" onClick={() => editRepresentative()}>Ajouter</button></div>
              {activeRepresentatives.map((item) => <article className="mini-record" key={item.id}><div><strong>{[item.first_name,item.last_name].filter(Boolean).join(" ")}{item.is_primary ? " · principal" : ""}</strong><small>{item.title}{item.email ? ` · ${item.email}` : ""}{item.phone ? ` · ${item.phone}` : ""}</small></div><div className="row-actions"><button type="button" className="secondary compact" onClick={() => editRepresentative(item)}>Modifier</button><button type="button" className="danger compact" disabled={busy} onClick={() => archiveRepresentative(item)}>Archiver</button></div></article>)}
              {!activeRepresentatives.length && <p className="empty">Aucun représentant enregistré.</p>}
              {representativeEditor && <form className="form-grid nested-editor" onSubmit={saveRepresentative}><label>Prénom<input value={representativeEditor.first_name} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, first_name: e.target.value }) : value)} /></label><label>Nom<input required value={representativeEditor.last_name} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, last_name: e.target.value }) : value)} /></label><label>Fonction<input required value={representativeEditor.title} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, title: e.target.value }) : value)} /></label><label>E-mail<input type="email" value={representativeEditor.email} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, email: e.target.value }) : value)} /></label><label>Téléphone<input value={representativeEditor.phone} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, phone: e.target.value }) : value)} /></label><label className="check"><input type="checkbox" checked={representativeEditor.is_primary} onChange={(e) => setRepresentativeEditor((value) => value ? ({ ...value, is_primary: e.target.checked }) : value)} />Représentant principal</label><div className="row-actions"><button disabled={busy}>{representativeEditor.id ? "Enregistrer" : "Ajouter"}</button><button type="button" className="secondary" onClick={() => setRepresentativeEditor(null)}>Annuler</button></div></form>}
            </section>
            <section className="panel"><div className="section-heading"><div><span className="eyebrow">Traçabilité</span><h3>Derniers contrôles</h3></div></div>{events.slice().sort((a,b) => b.created_at.localeCompare(a.created_at)).slice(0,10).map((item) => <article className="mini-record" key={item.id}><strong>{item.event_type === 'status_changed' ? `${labels[item.previous_status || ''] || item.previous_status || 'Création'} → ${labels[item.new_status || ''] || item.new_status}` : item.event_type === 'representative_saved' ? 'Représentant enregistré' : item.event_type === 'representative_archived' ? 'Représentant archivé' : 'Dossier mis à jour'}</strong><small>{frDate(item.created_at, true)}{item.note ? ` · ${item.note}` : ''}</small></article>)}{!events.length && <p className="empty">Aucun contrôle historisé.</p>}</section>
            {message && <Notice tone={message.includes("enregistré") || message.includes("archivé") ? "success" : "error"}>{message}</Notice>}
          </section>
        </div>
      )}
      {!open && message && (
        <Notice tone={message.startsWith("Espace entreprise") ? "success" : "error"}>
          {message}
        </Notice>
      )}
    </section>
  );
}

function WorkerContractAdmin({
  worker,
  contracts,
  envelopes,
  cancellationRules,
  reload,
}: {
  worker: Worker;
  contracts: WorkerContract[];
  envelopes: TenantSignatureEnvelope[];
  cancellationRules: WorkerCancellationRule[];
  reload: () => void;
}) {
  const [message,setMessage]=useState(""),[busy,setBusy]=useState(false);
  async function prepare(contract:WorkerContract){
    setBusy(true);
    const {error}=await supabase!.rpc("prepare_tenant_contract_signature",{p_source_type:"worker_contract",p_source_id:contract.id});
    setBusy(false);
    setMessage(error?error.message:"Dossier de signature électronique préparé. Aucun envoi externe n’a lieu tant que le prestataire n’est pas connecté.");
    if(!error) reload();
  }
  return <section className="panel">
    <div className="section-heading"><div><span className="eyebrow">{worker.kind === "employee" ? "Contrat salarié" : "Contrat ST"}</span><h2>Dossier contractuel de {workerName(worker)}</h2></div></div>
    <p className="section-intro">{worker.kind === "employee" ? "Le contrat de travail rédigé par le comptable et ses avenants restent consultables dans la fiche. ARD Manager ne réécrit pas leur contenu juridique." : "Le texte prérempli est figé avant envoi. La signature électronique est disponible seulement si elle est incluse dans la formule ARD Manager active."}</p>
    {contracts.map((contract)=>{const envelope=envelopes.find((item)=>item.source_type==="worker_contract"&&item.source_id===contract.id);return <article className="mini-record" key={contract.id}>
      <strong>{contract.title} · version {contract.template_version||"—"}</strong>
      <small>{labels[contract.status]||contract.status}{envelope?` · signature ${labels[envelope.status]||envelope.status}${envelope.provider?` via ${envelope.provider}`:" préparée"}`:""}</small>
      {contract.rendered_content&&<details className="contract-preview"><summary>Voir le contrat prérempli</summary><div>{contract.rendered_content}</div></details>}
      {worker.kind === "contractor" && <WorkerCancellationRuleEditor contract={contract} rule={cancellationRules.find((item)=>item.contract_id===contract.id)} reload={reload} />}
      {worker.kind === "contractor" && ["draft","sent"].includes(contract.status)&&<button className="secondary compact" disabled={busy||!!envelope&&["draft","queued","sent","viewed"].includes(envelope.status)} onClick={()=>prepare(contract)}>Préparer la signature électronique</button>}
    </article>;})}
    {!contracts.length&&<p className="empty">{worker.kind === "employee" ? "Aucun contrat de travail ou avenant déposé pour cet employé." : "Aucun contrat préparé pour ce sous-traitant."}</p>}
    {message&&<Notice tone={message.startsWith("Dossier")?"success":"error"}>{message}</Notice>}
  </section>;
}

function WorkerCancellationRuleEditor({contract,rule,reload}:{contract:WorkerContract;rule?:WorkerCancellationRule;reload:()=>void}){
  const [enabled,setEnabled]=useState(rule?.enabled||false),
    [noticeHours,setNoticeHours]=useState(String(rule?.notice_hours??24)),
    [mode,setMode]=useState<WorkerCancellationRule["penalty_mode"]>(rule?.penalty_mode||"none"),
    [value,setValue]=useState(String(rule?.penalty_value??0)),
    [clause,setClause]=useState(rule?.clause_reference||""),
    [busy,setBusy]=useState(false),[message,setMessage]=useState("");
  async function save(){
    setBusy(true);
    const {error}=await supabase!.rpc("save_worker_cancellation_rule",{
      p_contract_id:contract.id,p_enabled:enabled,p_notice_hours:Number(noticeHours),
      p_penalty_mode:enabled?mode:"none",p_penalty_value:enabled?Number(value):0,
      p_clause_reference:clause.trim()||null,
    });
    setBusy(false);setMessage(error?error.message:"Règle d’annulation enregistrée dans le dossier contractuel.");
    if(!error)reload();
  }
  return <details className="cancellation-rule-editor">
    <summary>Règle d’annulation tardive</summary>
    <p className="section-intro">La règle calcule seulement une proposition. Toute pénalité reste soumise au contrôle de l’administrateur.</p>
    <div className="form-grid">
      <label className="wide check"><input type="checkbox" checked={enabled} onChange={(event)=>{setEnabled(event.target.checked);if(!event.target.checked)setMode("none");}} />Activer la règle contractuelle</label>
      <label>Délai minimum avant mission (h)<input type="number" min="0" max="720" value={noticeHours} onChange={(event)=>setNoticeHours(event.target.value)} /></label>
      <label>Calcul proposé<select value={mode} disabled={!enabled} onChange={(event)=>setMode(event.target.value as WorkerCancellationRule["penalty_mode"])}><option value="none">Aucune pénalité</option><option value="fixed">Montant fixe HT</option><option value="percentage_of_assignment">Pourcentage du montant convenu</option></select></label>
      <label>Valeur {mode==="percentage_of_assignment"?"(%)":"(€ HT)"}<input type="number" min="0" max={mode==="percentage_of_assignment"?100:undefined} step="0.01" disabled={!enabled||mode==="none"} value={value} onChange={(event)=>setValue(event.target.value)} /></label>
      <label className="wide">Référence de la clause<textarea value={clause} onChange={(event)=>setClause(event.target.value)} placeholder="Article ou extrait contractuel à contrôler" /></label>
      <button type="button" disabled={busy} onClick={save}>{busy?"Enregistrement…":"Enregistrer la règle"}</button>
    </div>
    {message&&<Notice tone={message.startsWith("Règle")?"success":"error"}>{message}</Notice>}
  </details>;
}

function WorkerRateCardManager({
  profile,
  worker,
  rates,
  clients,
  properties,
  propertyServices,
  reload,
}: {
  profile: Profile;
  worker: Worker;
  rates: WorkerRateCard[];
  clients: Client[];
  properties: ClientProperty[];
  propertyServices: PropertyService[];
  reload: () => void;
}) {
  const blank = {
      id: "",
      name: "",
      pricing_mode: "fixed" as WorkerRateCard["pricing_mode"],
      rate: 0,
      unit_label: "intervention",
      notes: "",
      client_id: "",
      property_id: "",
      property_service_id: "",
    },
    [editingRate, setEditingRate] = useState(blank),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const selectRate = (rate?: WorkerRateCard) =>
    setEditingRate(
      rate
        ? {
            id: rate.id,
            name: rate.name,
            pricing_mode: rate.pricing_mode,
            rate: rate.rate,
            unit_label: rate.unit_label || "",
            notes: rate.notes || "",
            client_id: rate.client_id || "",
            property_id: rate.property_id || "",
            property_service_id: rate.property_service_id || "",
          }
        : blank,
    );
  async function saveRate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingRate.name.trim()) {
      setMessage("Donnez un nom clair à cette intervention.");
      return;
    }
    setBusy(true);
    const payload = {
        organization_id: profile.organization_id,
        worker_id: worker.id,
        name: editingRate.name.trim(),
        pricing_mode: editingRate.pricing_mode,
        rate: Number(editingRate.rate || 0),
        unit_label: editingRate.unit_label.trim() || null,
        notes: editingRate.notes.trim() || null,
        client_id: editingRate.client_id || null,
        property_id: editingRate.property_id || null,
        property_service_id: editingRate.property_service_id || null,
        active: true,
      },
      result = editingRate.id
        ? await supabase!
            .from("worker_rate_cards")
            .update(payload)
            .eq("id", editingRate.id)
        : await supabase!.from("worker_rate_cards").insert(payload);
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editingRate.id
          ? "Tarif mis à jour. Les missions déjà affectées gardent leur montant convenu."
          : "Nouveau tarif ajouté au sous-traitant.",
    );
    if (!result.error) {
      selectRate();
      reload();
    }
  }
  async function toggleRate(rate: WorkerRateCard) {
    setBusy(true);
    const { error } = await supabase!
      .from("worker_rate_cards")
      .update({ active: !rate.active })
      .eq("id", rate.id);
    setBusy(false);
    setMessage(
      error
        ? error.message
        : rate.active
          ? "Tarif archivé. Il reste conservé sur les anciennes missions."
          : "Tarif réactivé.",
    );
    if (!error) reload();
  }
  const modeLabel: Record<string, string> = {
    hourly: "Horaire",
    fixed: "Forfait par intervention",
    per_unit: "À l’unité",
  }, scopedProperties=properties.filter((item)=>!editingRate.client_id||item.client_id===editingRate.client_id),
    scopedServices=propertyServices.filter((item)=>!editingRate.property_id||item.property_id===editingRate.property_id),
    scopeLabel=(rate:WorkerRateCard)=>{
      const client=clients.find((item)=>item.id===rate.client_id),
        property=properties.find((item)=>item.id===rate.property_id),
        service=propertyServices.find((item)=>item.id===rate.property_service_id);
      return [client?.name,property?.name,service?.name].filter(Boolean).join(" · ")||"Tous les clients et prestations";
    };
  return (
    <section className="panel rate-card-manager">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Rémunération ST</span>
          <h2>Tarifs de {workerName(worker)}</h2>
        </div>
        <button className="secondary compact" onClick={() => selectRate()}>
          Nouveau tarif
        </button>
      </div>
      <p className="section-intro">
        Créez un tarif par type d’intervention. Le tarif choisi lors de
        l’affectation est copié sur la mission : une modification ultérieure ne
        change donc jamais l’accord déjà proposé au ST.
      </p>
      <div className="rate-card-list">
        {rates.map((rate) => (
          <article key={rate.id} className={!rate.active ? "archived" : ""}>
            <div>
              <strong>{rate.name}</strong>
              <small>
                {modeLabel[rate.pricing_mode]} · {money(rate.rate)}
                {rate.unit_label ? ` / ${rate.unit_label}` : ""}
              </small>
              <small>{scopeLabel(rate)}</small>
            </div>
            <Status value={rate.active ? "active" : "expired"} />
            <div className="row-actions">
              <button
                className="secondary compact"
                onClick={() => selectRate(rate)}
              >
                Modifier
              </button>
              <button
                className="secondary compact"
                disabled={busy}
                onClick={() => toggleRate(rate)}
              >
                {rate.active ? "Archiver" : "Réactiver"}
              </button>
            </div>
          </article>
        ))}
        {!rates.length && (
          <p className="empty">
            Aucun tarif détaillé. Ajoutez le premier forfait ou taux horaire.
          </p>
        )}
      </div>
      <form className="form-grid" onSubmit={saveRate}>
        <label>
          Intervention / nom du forfait
          <input
            value={editingRate.name}
            onChange={(e) =>
              setEditingRate((value) => ({ ...value, name: e.target.value }))
            }
            placeholder="Ex. Ménage complet T3"
            required
          />
        </label>
        <label>
          Calcul du tarif
          <select
            value={editingRate.pricing_mode}
            onChange={(e) =>
              setEditingRate((value) => ({
                ...value,
                pricing_mode: e.target.value as WorkerRateCard["pricing_mode"],
              }))
            }
          >
            <option value="fixed">Forfait par intervention</option>
            <option value="hourly">Taux horaire</option>
            <option value="per_unit">Prix à l’unité</option>
          </select>
        </label>
        <label>
          Montant convenu (€)
          <input
            type="number"
            min="0"
            step="0.01"
            value={editingRate.rate}
            onChange={(e) =>
              setEditingRate((value) => ({
                ...value,
                rate: +e.target.value,
              }))
            }
            required
          />
        </label>
        <label>
          Unité affichée
          <input
            value={editingRate.unit_label}
            onChange={(e) =>
              setEditingRate((value) => ({
                ...value,
                unit_label: e.target.value,
              }))
            }
            placeholder="heure, logement, passage…"
          />
        </label>
        <label>
          Client concerné
          <select value={editingRate.client_id} onChange={(e)=>setEditingRate((value)=>({...value,client_id:e.target.value,property_id:"",property_service_id:""}))}>
            <option value="">Tous les clients</option>
            {clients.map((item)=><option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          Bien concerné
          <select value={editingRate.property_id} disabled={!editingRate.client_id} onChange={(e)=>setEditingRate((value)=>({...value,property_id:e.target.value,property_service_id:""}))}>
            <option value="">Tous les biens du client</option>
            {scopedProperties.filter((item)=>item.active).map((item)=><option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          Prestation concernée
          <select value={editingRate.property_service_id} disabled={!editingRate.property_id} onChange={(e)=>setEditingRate((value)=>({...value,property_service_id:e.target.value}))}>
            <option value="">Toutes les prestations du bien</option>
            {scopedServices.filter((item)=>item.active).map((item)=><option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label className="wide">
          Notes internes
          <textarea
            value={editingRate.notes}
            onChange={(e) =>
              setEditingRate((value) => ({ ...value, notes: e.target.value }))
            }
          />
        </label>
        <button disabled={busy}>
          {busy
            ? "Enregistrement…"
            : editingRate.id
              ? "Enregistrer le tarif"
              : "Ajouter le tarif"}
        </button>
      </form>
      {message && (
        <Notice tone={message.includes("Tarif") ? "success" : "error"}>
          {message}
        </Notice>
      )}
    </section>
  );
}

function WorkforceCostComparison({ data }: { data: DataState }) {
  const employees = data.workers.filter(
      (worker) => worker.kind === "employee" && worker.active,
    ),
    contractors = data.workers.filter(
      (worker) => worker.kind === "contractor" && worker.active,
    ),
    [employeeId, setEmployeeId] = useState(employees[0]?.id || ""),
    [employeeVehicleId, setEmployeeVehicleId] = useState(
      (data.vehicles || []).find(
        (vehicle) =>
          vehicle.worker_id === employees[0]?.id &&
          vehicle.document_status === "approved" &&
          vehicle.active,
      )?.id || "",
    ),
    [contractorId, setContractorId] = useState(contractors[0]?.id || ""),
    [rateCardId, setRateCardId] = useState(
      (data.workerRateCards || []).find(
        (rate) => rate.worker_id === contractors[0]?.id && rate.active,
      )?.id || "",
    ),
    [missionsPerMonth, setMissionsPerMonth] = useState(30),
    [activeMonths, setActiveMonths] = useState(8),
    [serviceHours, setServiceHours] = useState(2),
    [roundTripMinutes, setRoundTripMinutes] = useState(30),
    [roundTripKm, setRoundTripKm] = useState(20);
  const employee = employees.find((item) => item.id === employeeId),
    contractor = contractors.find((item) => item.id === contractorId),
    contractorRates = (data.workerRateCards || []).filter(
      (rate) => rate.worker_id === contractorId && rate.active,
    ),
    selectedRate = contractorRates.find((rate) => rate.id === rateCardId),
    employeeVehicles = (data.vehicles || []).filter(
      (vehicle) =>
        vehicle.worker_id === employeeId &&
        vehicle.document_status === "approved" &&
        vehicle.active,
    ),
    employeeVehicle = employeeVehicles.find(
      (vehicle) => vehicle.id === employeeVehicleId,
    ),
    employeeMonthlyCost = employee
      ? Number(employee.employer_cost_monthly ?? employee.net_salary ?? 0) +
        Number(employee.ticket_count || 0) * Number(employee.ticket_value || 0)
      : 0,
    employeeCostComplete = employee?.employer_cost_monthly != null,
    employeeHourlyCost = employee?.monthly_hours
      ? employeeMonthlyCost / employee.monthly_hours
      : 0,
    routeHours = roundTripMinutes / 60,
    yearlyMissions = missionsPerMonth * activeMonths,
    yearlyKm = yearlyMissions * roundTripKm,
    yearlyMileage = employeeVehicle
      ? mileageEntitlement2026(employeeVehicle, yearlyKm)
      : 0,
    employeeYearlyCost = employeeMonthlyCost * 12 + yearlyMileage,
    contractorRate = Number(
      selectedRate?.rate ??
        (contractor?.pricing_mode === "fixed"
          ? contractor.fixed_fee
          : contractor?.hourly_rate) ??
        0,
    ),
    contractorMissionCost =
      (selectedRate?.pricing_mode || contractor?.pricing_mode) === "hourly"
        ? contractorRate * serviceHours
        : contractorRate,
    contractorYearlyCost = contractorMissionCost * yearlyMissions,
    loadHoursPerMonth = missionsPerMonth * (serviceHours + routeHours),
    capacityRate = employee?.monthly_hours
      ? (loadHoursPerMonth / employee.monthly_hours) * 100
      : null,
    difference = employeeYearlyCost - contractorYearlyCost;
  return (
    <details className="panel workforce-comparison decision-support-details">
      <summary>
        <div>
          <span className="eyebrow">Aide à la décision</span>
          <h2>Salarié ou sous-traitant ?</h2>
          <small>Déplier la simulation de coût et de capacité</small>
        </div>
      </summary>
      <div className="decision-support-body">
      <Notice>
        Cette simulation ne décide pas à votre place. Elle compare un coût de
        poste salarié sur 12 mois avec le coût ST sur les mois réellement
        actifs, en affichant les hypothèses modifiables.
      </Notice>
      <div className="form-grid comparison-inputs">
        <label>
          Salarié comparé
          <select
            value={employeeId}
            onChange={(e) => {
              const id = e.target.value,
                vehicle = (data.vehicles || []).find(
                  (item) =>
                    item.worker_id === id &&
                    item.document_status === "approved" &&
                    item.active,
                );
              setEmployeeId(id);
              setEmployeeVehicleId(vehicle?.id || "");
            }}
          >
            {employees.map((item) => (
              <option key={item.id} value={item.id}>
                {workerName(item)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Véhicule IK salarié
          <select
            value={employeeVehicleId}
            onChange={(e) => setEmployeeVehicleId(e.target.value)}
          >
            <option value="">Aucun véhicule validé</option>
            {employeeVehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.brand} {vehicle.model || ""} · {vehicle.fiscal_power}{" "}
                CV
              </option>
            ))}
          </select>
        </label>
        <label>
          Sous-traitant comparé
          <select
            value={contractorId}
            onChange={(e) => {
              const id = e.target.value,
                rates = (data.workerRateCards || []).filter(
                  (rate) => rate.worker_id === id && rate.active,
                );
              setContractorId(id);
              setRateCardId(rates[0]?.id || "");
            }}
          >
            {contractors.map((item) => (
              <option key={item.id} value={item.id}>
                {workerName(item)}
              </option>
            ))}
          </select>
        </label>
        {contractorRates.length > 0 && (
          <label>
            Prestation / tarif ST
            <select
              value={rateCardId}
              onChange={(e) => setRateCardId(e.target.value)}
            >
              {contractorRates.map((rate) => (
                <option key={rate.id} value={rate.id}>
                  {rate.name} · {money(rate.rate)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Missions par mois actif
          <input
            type="number"
            min="0"
            value={missionsPerMonth}
            onChange={(e) => setMissionsPerMonth(+e.target.value)}
          />
        </label>
        <label>
          Mois actifs dans l’année
          <input
            type="number"
            min="1"
            max="12"
            value={activeMonths}
            onChange={(e) => setActiveMonths(+e.target.value)}
          />
        </label>
        <label>
          Travail par mission (h)
          <input
            type="number"
            min="0"
            step="0.25"
            value={serviceHours}
            onChange={(e) => setServiceHours(+e.target.value)}
          />
        </label>
        <label>
          Trajet A/R par mission (min)
          <input
            type="number"
            min="0"
            value={roundTripMinutes}
            onChange={(e) => setRoundTripMinutes(+e.target.value)}
          />
        </label>
        <label>
          Trajet A/R par mission (km)
          <input
            type="number"
            min="0"
            step="0.1"
            value={roundTripKm}
            onChange={(e) => setRoundTripKm(+e.target.value)}
          />
        </label>
      </div>
      {!employeeCostComplete && employee && (
        <Notice tone="error">
          Le coût employeur de {workerName(employee)} manque : le calcul utilise
          seulement son salaire net et reste donc incomplet.
        </Notice>
      )}
      {!employeeVehicle && yearlyKm > 0 && (
        <Notice tone="error">
          Aucun véhicule et aucune carte grise validée pour ce salarié : les
          kilomètres restent visibles mais ne sont pas valorisés.
        </Notice>
      )}
      <div className="comparison-results">
        <article>
          <span>Coût salarié annuel connu</span>
          <strong>{money(employeeYearlyCost)}</strong>
          <small>
            {money(employeeMonthlyCost)} × 12
            {employeeVehicle ? ` + ${money(yearlyMileage)} d’IK` : ""}
          </small>
        </article>
        <article>
          <span>Coût ST annuel simulé</span>
          <strong>{money(contractorYearlyCost)}</strong>
          <small>
            {yearlyMissions} missions × {money(contractorMissionCost)}
          </small>
        </article>
        <article>
          <span>Écart salarié − ST</span>
          <strong>{money(difference)}</strong>
          <small>
            {difference > 0
              ? "Le ST est moins coûteux avec ces hypothèses"
              : difference < 0
                ? "Le poste salarié est moins coûteux avec ces hypothèses"
                : "Coûts identiques avec ces hypothèses"}
          </small>
        </article>
        <article>
          <span>Charge mensuelle du salarié</span>
          <strong>
            {capacityRate == null
              ? "À définir"
              : `${capacityRate.toFixed(0)} %`}
          </strong>
          <small>
            {loadHoursPerMonth.toFixed(1)} h mobilisées, trajets compris
          </small>
        </article>
      </div>
      <p className="section-intro">
        Coût salarié horaire connu : {money(employeeHourlyCost)}. Sont encore à
        apprécier séparément : souplesse, qualité, fidélisation, absences,
        matériel, formation et risque de sous-activité.
      </p>
      </div>
    </details>
  );
}
function PreferredWorkersAdmin({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const [clientId, setClientId] = useState(""),
    [propertyId, setPropertyId] = useState(""),
    [workerId, setWorkerId] = useState(""),
    [priority, setPriority] = useState(1),
    [message, setMessage] = useState(""),
    client = data.clients.find((c) => c.id === clientId),
    properties = data.properties.filter(
      (p) => p.client_id === clientId && p.active,
    ),
    contractors = data.workers.filter((w) => w.kind === "contractor");
  async function setType(type: "standard" | "concierge") {
    if (!client) return;
    const { error } = await supabase!
      .from("clients")
      .update({ client_type: type })
      .eq("id", client.id);
    setMessage(
      error
        ? error.message
        : `${client.name} est maintenant un profil ${type === "concierge" ? "Conciergerie" : "standard"}.`,
    );
    if (!error) reload();
  }
  async function add(e: React.FormEvent) {
    e.preventDefault();
    const { error } = await supabase!
      .from("preferred_worker_assignments")
      .insert({
        organization_id: profile.organization_id,
        client_id: clientId,
        property_id: propertyId || null,
        worker_id: workerId,
        priority,
        active: true,
      });
    setMessage(error ? error.message : "Sous-traitant référent enregistré.");
    if (!error) {
      setWorkerId("");
      reload();
    }
  }
  async function remove(id: string) {
    const { error } = await supabase!
      .from("preferred_worker_assignments")
      .delete()
      .eq("id", id);
    setMessage(error ? error.message : "Référence supprimée.");
    if (!error) reload();
  }
  return (
    <section className="panel">
      <h2>Conciergeries et ST référents</h2>
      <p className="section-intro">
        Le ST référent d’un client ou d’un bien sera proposé avant les autres
        sous-traitants disponibles.
      </p>
      <div className="form-grid">
        <label>
          Client
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setPropertyId("");
            }}
          >
            <option value="">Choisir</option>
            {data.clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ·{" "}
                {c.client_type === "concierge" ? "Conciergerie" : "Standard"}
              </option>
            ))}
          </select>
        </label>
        {client && (
          <div className="inline-actions">
            <button
              className="secondary compact"
              onClick={() =>
                setType(
                  client.client_type === "concierge" ? "standard" : "concierge",
                )
              }
            >
              {client.client_type === "concierge"
                ? "Repasser en client standard"
                : "Définir comme conciergerie"}
            </button>
          </div>
        )}
      </div>
      {clientId && (
        <form className="assignment-controls" onSubmit={add}>
          <label>
            Niveau
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
            >
              <option value="">Tout le client</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sous-traitant
            <select
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
              required
            >
              <option value="">Choisir</option>
              {contractors.map((w) => (
                <option key={w.id} value={w.id}>
                  {workerName(w)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Priorité
            <input
              type="number"
              min="1"
              max="99"
              value={priority}
              onChange={(e) => setPriority(+e.target.value)}
            />
          </label>
          <button>Ajouter le référent</button>
        </form>
      )}
      <div className="assignment-box">
        {data.preferredWorkers
          .filter((p) => !clientId || p.client_id === clientId)
          .map((p) => {
            const owner = data.workers.find((w) => w.id === p.worker_id);
            return (
              <div className="assignment-row" key={p.id}>
                <span>
                  <strong>{owner ? workerName(owner) : "ST inconnu"}</strong> ·{" "}
                  {data.properties.find((x) => x.id === p.property_id)?.name ||
                    data.clients.find((c) => c.id === p.client_id)?.name ||
                    "Client"}{" "}
                  · priorité {p.priority}
                </span>
                <button className="danger compact" onClick={() => remove(p.id)}>
                  Supprimer
                </button>
              </div>
            );
          })}
      </div>
      {message && (
        <Notice
          tone={
            message.includes("enregistré") ||
            message.includes("maintenant") ||
            message.includes("supprimée")
              ? "success"
              : "error"
          }
        >
          {message}
        </Notice>
      )}
    </section>
  );
}
function MissionAdmin({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const blank = {
      id: "",
      client_id: data.clients[0]?.id || "",
      starts_at: "",
      ends_at: "",
      status: "pending",
      location: "",
      planned_hours: 0,
      amount_ex_vat: 0,
      billing_mode: "hourly",
      billing_rate: 0,
      billing_quantity: 1,
      notes: "",
    },
    [editing, setEditing] = useState<Partial<Mission>>(blank),
    [selectedWorker, setSelectedWorker] = useState(""),
    [assignedHours, setAssignedHours] = useState(0),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const select = (m?: Mission) => {
    setEditing(
      m
        ? {
            ...m,
            starts_at: localInput(m.starts_at),
            ends_at: localInput(m.ends_at),
          }
        : { ...blank, client_id: data.clients[0]?.id || "" },
    );
    setSelectedWorker("");
    setMessage("");
  };
  const assignments = editing.id
    ? data.assignments.filter(
        (a) => a.mission_id === editing.id && a.status !== "cancelled",
      )
    : [];
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const start = new Date(editing.starts_at || ""),
      end = new Date(editing.ends_at || "");
    if (!(end > start))
      return setMessage(
        "La fin doit être postérieure au début. Pour une mission de nuit, choisissez le jour suivant pour l’heure de fin.",
      );
    setBusy(true);
    const duration = (end.getTime() - start.getTime()) / 3600000,
      payload = {
        organization_id: profile.organization_id,
        client_id: editing.client_id,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        status: editing.status || "pending",
        location: editing.location || "",
        planned_hours: +(editing.planned_hours || duration),
        amount_ex_vat: +(editing.amount_ex_vat || 0),
        billing_mode: editing.billing_mode || "hourly",
        billing_rate: +(editing.billing_rate || 0),
        billing_quantity: +(editing.billing_quantity || 1),
        notes: editing.notes || null,
        created_by: profile.id,
      };
    const result = editing.id
      ? await supabase!.from("missions").update(payload).eq("id", editing.id)
      : await supabase!.from("missions").insert(payload).select("id").single();
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editing.id
          ? "Mission enregistrée."
          : "Mission créée.",
    );
    if (!result.error) {
      if (!editing.id && result.data)
        setEditing((v) => ({ ...v, id: result.data.id }));
      reload();
    }
  }
  async function assign() {
    if (!editing.id || !selectedWorker)
      return setMessage("Choisissez une mission et une personne.");
    setBusy(true);
    const { error } = await supabase!.from("mission_assignments").upsert(
      {
        mission_id: editing.id,
        worker_id: selectedWorker,
        assigned_hours: +assignedHours || +(editing.planned_hours || 0),
        source: "manual",
        status: "assigned",
      },
      { onConflict: "mission_id,worker_id" },
    );
    setBusy(false);
    setMessage(error ? error.message : "Personne affectée à la mission.");
    if (!error) reload();
  }
  async function suggest() {
    if (!editing.id)
      return setMessage(
        "Enregistrez la mission avant de demander une proposition.",
      );
    setBusy(true);
    const { data: candidates, error } = await supabase!.rpc(
      "mission_candidates",
      { p_mission_id: editing.id },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    if (!candidates?.length)
      return setMessage(
        "Aucune personne disponible sans conflit ni congé validé.",
      );
    setSelectedWorker(candidates[0].worker_id);
    setAssignedHours(+(editing.planned_hours || 0));
    setMessage(
      `${candidates[0].display_name} proposé en priorité : ${candidates[0].reason}.`,
    );
  }
  async function cancelAssignment(id: string) {
    const { error } = await supabase!
      .from("mission_assignments")
      .update({ status: "cancelled" })
      .eq("id", id);
    setMessage(error ? error.message : "Affectation annulée.");
    if (!error) reload();
  }
  return (
    <>
      <div className="page-title">
        <h1>Missions</h1>
        <button onClick={() => select()}>Nouvelle mission</button>
      </div>
      <section className="panel">
        <p className="section-intro">
          Deux personnes différentes peuvent travailler au même créneau. Le
          chevauchement reste interdit pour une même personne.
        </p>
        <MissionList
          items={data.missions}
          actions={(m) => (
            <button className="secondary compact" onClick={() => select(m)}>
              Modifier
            </button>
          )}
        />
      </section>
      <section className="panel">
        <h2>{editing.id ? "Modifier la mission" : "Nouvelle mission"}</h2>
        <form className="form-grid" onSubmit={save}>
          <label>
            Client
            <select
              value={editing.client_id || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, client_id: e.target.value }))
              }
              required
            >
              <option value="">Choisir</option>
              {data.clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Statut
            <select
              value={editing.status || "pending"}
              onChange={(e) =>
                setEditing((v) => ({ ...v, status: e.target.value }))
              }
            >
              <option value="pending">En attente</option>
              <option value="assigned">Affectée</option>
              <option value="completed">Terminée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </label>
          <label>
            Début
            <input
              type="datetime-local"
              value={editing.starts_at || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, starts_at: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Fin
            <input
              type="datetime-local"
              value={editing.ends_at || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, ends_at: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Lieu
            <input
              value={editing.location || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, location: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Durée prévue (h)
            <input
              type="number"
              min="0"
              step="0.25"
              value={editing.planned_hours || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, planned_hours: +e.target.value }))
              }
            />
          </label>
          <label>
            Facturation
            <select
              value={editing.billing_mode || "hourly"}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_mode: e.target.value }))
              }
            >
              <option value="hourly">Horaire</option>
              <option value="fixed">Forfait</option>
              <option value="per_unit">À l’unité</option>
              <option value="monthly">Mensuel</option>
            </select>
          </label>
          <label>
            Taux / prix unitaire (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.billing_rate || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_rate: +e.target.value }))
              }
            />
          </label>
          <label>
            Quantité
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.billing_quantity || 1}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_quantity: +e.target.value }))
              }
            />
          </label>
          <label>
            Montant HT (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.amount_ex_vat || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, amount_ex_vat: +e.target.value }))
              }
            />
          </label>
          <label className="wide">
            Notes
            <textarea
              value={editing.notes || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, notes: e.target.value }))
              }
            />
          </label>
          <button disabled={busy}>
            {busy
              ? "Enregistrement…"
              : editing.id
                ? "Enregistrer la mission"
                : "Créer la mission"}
          </button>
        </form>
        {editing.id && (
          <div className="assignment-box">
            <h3>Affectations</h3>
            {assignments.map((a) => {
              const w = data.workers.find((x) => x.id === a.worker_id);
              return (
                <div className="assignment-row" key={a.id}>
                  <span>
                    <strong>{w ? workerName(w) : "Personne inconnue"}</strong> ·{" "}
                    {a.assigned_hours} h
                  </span>
                  <button
                    className="danger compact"
                    onClick={() => cancelAssignment(a.id)}
                  >
                    Annuler
                  </button>
                </div>
              );
            })}
            {!assignments.length && (
              <p className="empty">Aucune personne affectée.</p>
            )}
            <div className="assignment-controls">
              <label>
                Personne
                <select
                  value={selectedWorker}
                  onChange={(e) => setSelectedWorker(e.target.value)}
                >
                  <option value="">Choisir</option>
                  {data.workers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {workerName(w)} ·{" "}
                      {w.kind === "employee" ? "Employé" : "Sous-traitant"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Heures affectées
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={assignedHours || editing.planned_hours || 0}
                  onChange={(e) => setAssignedHours(+e.target.value)}
                />
              </label>
              <button
                className="secondary"
                type="button"
                onClick={suggest}
                disabled={busy}
              >
                Proposer en priorité
              </button>
              <button
                type="button"
                onClick={assign}
                disabled={busy || !selectedWorker}
              >
                Affecter
              </button>
            </div>
          </div>
        )}
        {message && (
          <Notice
            tone={
              message.includes("créée") ||
              message.includes("enregistrée") ||
              message.includes("affectée") ||
              message.includes("annulée") ||
              message.includes("proposé")
                ? "success"
                : "error"
            }
          >
            {message}
          </Notice>
        )}
      </section>
    </>
  );
}

function ClientMissions({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const client = data.clients[0],
    services = data.services.filter((s) => s.client_id === client?.id);
  const [start, setStart] = useState(""),
    [end, setEnd] = useState(""),
    [service, setService] = useState(""),
    [location, setLocation] = useState(""),
    [notes, setNotes] = useState(""),
    [message, setMessage] = useState("");
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!client) return setMessage("Aucune fiche client reliée à ce compte.");
    const s = new Date(start),
      f = new Date(end);
    if (!(f > s))
      return setMessage("La fin souhaitée doit être postérieure au début.");
    const { error } = await supabase!.from("mission_requests").insert({
      organization_id: profile.organization_id,
      client_id: client.id,
      service_id: service || null,
      requested_by: profile.id,
      desired_starts_at: s.toISOString(),
      desired_ends_at: f.toISOString(),
      location,
      client_notes: notes || null,
      status: "pending",
    });
    setMessage(error ? error.message : "Demande de mission transmise à ARD.");
    if (!error) {
      setStart("");
      setEnd("");
      setService("");
      setLocation("");
      setNotes("");
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <h1>Missions</h1>
      </div>
      <section className="panel">
        <h2>Demander une mission</h2>
        <p className="section-intro">
          ARD vérifiera la disponibilité, confirmera le créneau et vous
          communiquera le tarif applicable.
        </p>
        <form className="form-grid" onSubmit={submit}>
          <label>
            Prestation
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
            >
              <option value="">À préciser avec ARD</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.unit_price_ex_vat} € HT
                </option>
              ))}
            </select>
          </label>
          <label>
            Lieu
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </label>
          <label>
            Début souhaité
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
            />
          </label>
          <label>
            Fin souhaitée
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Précisions
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <button>Envoyer la demande</button>
        </form>
        {message && (
          <Notice tone={message.startsWith("Demande") ? "success" : "error"}>
            {message}
          </Notice>
        )}
      </section>
      <Records
        title="Mes demandes"
        empty="Aucune demande en attente."
        rows={data.requests.map((r) => (
          <article key={r.id}>
            <div>
              <strong>
                {frDate(r.desired_starts_at, true)} →{" "}
                {frDate(r.desired_ends_at, true)}
              </strong>
              <small>
                {r.location}
                {r.client_notes ? ` · ${r.client_notes}` : ""}
              </small>
            </div>
            <Status value={r.status} />
          </article>
        ))}
      />
      <section className="panel">
        <h2>Missions confirmées et passées</h2>
        <MissionList items={data.missions} />
      </section>
    </>
  );
}

function WorkerMissions({
  profile,
  worker,
  data,
  reload,
}: {
  profile: Profile;
  worker: Worker | null;
  data: DataState;
  reload: () => void;
}) {
  const [missionId, setMissionId] = useState(""),
    [actual, setActual] = useState(""),
    [result, setResult] = useState("completed"),
    [observations, setObservations] = useState(""),
    [clientNote, setClientNote] = useState(""),
    [message, setMessage] = useState("");
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!worker || !missionId) return setMessage("Choisissez une mission.");
    const { error } = await supabase!.from("mission_reports").upsert(
      {
        organization_id: profile.organization_id,
        mission_id: missionId,
        worker_id: worker.id,
        result,
        actual_hours: +actual,
        observations: observations || null,
        client_note: clientNote || null,
        status: "pending",
      },
      { onConflict: "mission_id,worker_id" },
    );
    setMessage(
      error ? error.message : "Compte rendu transmis à l’administration.",
    );
    if (!error) {
      setMissionId("");
      setActual("");
      setObservations("");
      setClientNote("");
      reload();
    }
  }
  return (
    <>
      <h1>Missions</h1>
      <section className="panel">
        <MissionList items={data.missions} showBilling={false} />
      </section>
      <section className="panel">
        <h2>Signaler une fin de mission</h2>
        <form className="form-grid" onSubmit={submit}>
          <label>
            Mission
            <select
              value={missionId}
              onChange={(e) => setMissionId(e.target.value)}
              required
            >
              <option value="">Choisir</option>
              {data.missions
                .filter((m) => m.status !== "cancelled")
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {frDate(m.starts_at, true)} · {m.client?.name || "Mission"}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Résultat
            <select value={result} onChange={(e) => setResult(e.target.value)}>
              <option value="completed">Terminée</option>
              <option value="issue">Incident rencontré</option>
              <option value="followup">Action complémentaire nécessaire</option>
            </select>
          </label>
          <label>
            Heures réellement effectuées
            <input
              type="number"
              min="0.25"
              step="0.25"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Observations internes ARD
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
            />
          </label>
          <label className="wide">
            Note pouvant être communiquée au client
            <textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
            />
          </label>
          <button>Transmettre le compte rendu</button>
        </form>
        {message && (
          <Notice tone={message.startsWith("Compte") ? "success" : "error"}>
            {message}
          </Notice>
        )}
      </section>
      <Records
        title="Mes comptes rendus"
        empty="Aucun compte rendu transmis."
        rows={data.reports.map((r) => (
          <article key={r.id}>
            <div>
              <strong>
                {frDate(r.submitted_at, true)} · {r.actual_hours} h
              </strong>
              <small>{r.observations || "Sans observation"}</small>
            </div>
            <Status value={r.status} />
          </article>
        ))}
      />
    </>
  );
}

function MissionAdminRich({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const blank: Partial<Mission> = {
    client_id: data.clients[0]?.id || "",
    property_id: null,
    service_id: null,
    property_service_id: null,
    starts_at: "",
    ends_at: "",
    status: "pending",
    location: "",
    planned_hours: 0,
    amount_ex_vat: 0,
    billing_mode: "hourly",
    billing_rate: 0,
    billing_quantity: 1,
    required_skills: [],
    notes: "",
  };
  const [editing, setEditing] = useState<Partial<Mission>>(blank),
    [workerId, setWorkerId] = useState(""),
    [assignedHours, setAssignedHours] = useState(0),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const properties = data.properties.filter(
      (p) => p.client_id === editing.client_id && p.active,
    ),
    clientServices = data.services.filter(
      (service) => service.client_id === editing.client_id,
    ),
    propertyServices = (data.propertyServices || []).filter(
      (service) => service.property_id === editing.property_id && service.active,
    ),
    selectedServiceKey = editing.property_service_id
      ? `property:${editing.property_service_id}`
      : editing.service_id
        ? `client:${editing.service_id}`
        : "",
    assignments = editing.id
      ? data.assignments.filter(
          (a) => a.mission_id === editing.id && a.status !== "cancelled",
        )
      : [];
  function select(m?: Mission) {
    setEditing(
      m
        ? {
            ...m,
            starts_at: localInput(m.starts_at),
            ends_at: localInput(m.ends_at),
          }
        : { ...blank, client_id: data.clients[0]?.id || "" },
    );
    setWorkerId("");
    setMessage("");
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const start = new Date(editing.starts_at || ""),
      end = new Date(editing.ends_at || "");
    if (!(end > start))
      return setMessage(
        "La fin doit être postérieure au début. Pour une mission de nuit, choisissez le jour suivant.",
      );
    setBusy(true);
    const duration = (end.getTime() - start.getTime()) / 3600000,
      payload = {
        organization_id: profile.organization_id,
        client_id: editing.client_id,
        property_id: editing.property_id || null,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        status: editing.status || "pending",
        location: editing.location || "",
        planned_hours: +(editing.planned_hours || duration),
        amount_ex_vat: +(editing.amount_ex_vat || 0),
        billing_mode: editing.billing_mode || "hourly",
        billing_rate: +(editing.billing_rate || 0),
        billing_quantity: +(editing.billing_quantity || 1),
        notes: editing.notes || null,
        created_by: profile.id,
      };
    const result = editing.id
      ? await supabase!
          .from("missions")
          .update(payload)
          .eq("id", editing.id)
          .select("id")
          .single()
      : await supabase!.from("missions").insert(payload).select("id").single();
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editing.id
          ? "Mission enregistrée."
          : "Mission créée.",
    );
    if (!result.error) {
      setEditing((v) => ({ ...v, id: result.data.id }));
      reload();
    }
  }
  async function assign() {
    if (!editing.id || !workerId) return;
    const worker = data.workers.find((w) => w.id === workerId),
      status = worker?.kind === "contractor" ? "proposed" : "assigned";
    setBusy(true);
    const { error } = await supabase!.from("mission_assignments").upsert(
      {
        mission_id: editing.id,
        worker_id: workerId,
        assigned_hours: +assignedHours || +(editing.planned_hours || 0),
        source: "manual",
        status,
      },
      { onConflict: "mission_id,worker_id" },
    );
    setBusy(false);
    setMessage(
      error
        ? error.message
        : status === "proposed"
          ? "Mission proposée au sous-traitant."
          : "Employé affecté à la mission.",
    );
    if (!error) reload();
  }
  async function suggest() {
    if (!editing.id) return setMessage("Enregistrez d’abord la mission.");
    setBusy(true);
    const { data: candidates, error } = await supabase!.rpc(
      "mission_candidates",
      { p_mission_id: editing.id },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    if (!candidates?.length)
      return setMessage(
        "Aucune personne disponible sans conflit ni indisponibilité.",
      );
    setWorkerId(candidates[0].worker_id);
    setAssignedHours(+(editing.planned_hours || 0));
    setMessage(
      `${candidates[0].display_name} est prioritaire : ${candidates[0].reason}.`,
    );
  }
  async function proposePreferred() {
    if (!editing.id) return;
    setBusy(true);
    const { data: proposed, error } = await supabase!.rpc(
      "propose_mission_to_preferred",
      { p_mission_id: editing.id },
    );
    setBusy(false);
    setMessage(
      error
        ? error.message
        : proposed?.length
          ? `Mission proposée à ${proposed.length} ST référent(s).`
          : "Aucun ST référent disponible pour ce client ou ce bien.",
    );
    if (!error) reload();
  }
  async function cancelAssignment(id: string) {
    const { error } = await supabase!
      .from("mission_assignments")
      .update({ status: "cancelled" })
      .eq("id", id);
    setMessage(error ? error.message : "Affectation annulée.");
    if (!error) reload();
  }
  return (
    <>
      <div className="page-title">
        <h1>Missions</h1>
        <button onClick={() => select()}>Nouvelle mission</button>
      </div>
      <section className="panel">
        <p className="section-intro">
          Les employés disponibles sont prioritaires selon leur quota. Pour une
          conciergerie, les ST référents du client ou du bien arrivent ensuite,
          avant les autres ST.
        </p>
        <MissionList
          items={data.missions}
          actions={(m) => (
            <button className="secondary compact" onClick={() => select(m)}>
              Modifier
            </button>
          )}
        />
      </section>
      <section className="panel">
        <h2>{editing.id ? "Modifier la mission" : "Nouvelle mission"}</h2>
        <form className="form-grid" onSubmit={save}>
          <label>
            Client
            <select
              value={editing.client_id || ""}
              onChange={(e) =>
                setEditing((v) => ({
                  ...v,
                  client_id: e.target.value,
                  property_id: null,
                }))
              }
              required
            >
              <option value="">Choisir</option>
              {data.clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          {properties.length > 0 && (
            <label>
              Bien
              <select
                value={editing.property_id || ""}
                onChange={(e) => {
                  const property = properties.find(
                    (p) => p.id === e.target.value,
                  );
                  setEditing((v) => ({
                    ...v,
                    property_id: e.target.value || null,
                    location: property?.address || v.location,
                  }));
                }}
              >
                <option value="">Aucun / client entier</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Statut
            <select
              value={editing.status || "pending"}
              onChange={(e) =>
                setEditing((v) => ({ ...v, status: e.target.value }))
              }
            >
              <option value="pending">En attente</option>
              <option value="assigned">Affectée</option>
              <option value="completed">Terminée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </label>
          <label>
            Début
            <input
              type="datetime-local"
              value={editing.starts_at || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, starts_at: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Fin
            <input
              type="datetime-local"
              value={editing.ends_at || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, ends_at: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Lieu
            <input
              value={editing.location || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, location: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Durée prévue (h)
            <input
              type="number"
              min="0"
              step="0.25"
              value={editing.planned_hours || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, planned_hours: +e.target.value }))
              }
            />
          </label>
          <label>
            Facturation client
            <select
              value={editing.billing_mode || "hourly"}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_mode: e.target.value }))
              }
            >
              <option value="hourly">Horaire</option>
              <option value="fixed">Forfait</option>
              <option value="per_unit">À l’unité</option>
              <option value="monthly">Mensuel</option>
            </select>
          </label>
          <label>
            Taux / prix unitaire (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.billing_rate || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_rate: +e.target.value }))
              }
            />
          </label>
          <label>
            Quantité
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.billing_quantity || 1}
              onChange={(e) =>
                setEditing((v) => ({ ...v, billing_quantity: +e.target.value }))
              }
            />
          </label>
          <label>
            Montant HT (€)
            <input
              type="number"
              min="0"
              step="0.01"
              value={editing.amount_ex_vat || 0}
              onChange={(e) =>
                setEditing((v) => ({ ...v, amount_ex_vat: +e.target.value }))
              }
            />
          </label>
          <label className="wide">
            Notes et consignes
            <textarea
              value={editing.notes || ""}
              onChange={(e) =>
                setEditing((v) => ({ ...v, notes: e.target.value }))
              }
            />
          </label>
          <button disabled={busy}>
            {busy
              ? "Enregistrement…"
              : editing.id
                ? "Enregistrer la mission"
                : "Créer la mission"}
          </button>
        </form>
        {editing.id && (
          <div className="assignment-box">
            <div className="section-heading">
              <h3>Affectations</h3>
              <button className="secondary compact" onClick={proposePreferred}>
                Proposer aux ST référents
              </button>
            </div>
            {assignments.map((a) => {
              const w = data.workers.find((x) => x.id === a.worker_id);
              return (
                <div className="assignment-row" key={a.id}>
                  <span>
                    <strong>{w ? workerName(w) : "Personne inconnue"}</strong> ·{" "}
                    {a.assigned_hours} h · <Status value={a.status} />
                  </span>
                  <button
                    className="danger compact"
                    onClick={() => cancelAssignment(a.id)}
                  >
                    Annuler
                  </button>
                </div>
              );
            })}
            {!assignments.length && (
              <p className="empty">Aucune personne affectée.</p>
            )}
            <div className="assignment-controls">
              <label>
                Personne
                <select
                  value={workerId}
                  onChange={(e) => setWorkerId(e.target.value)}
                >
                  <option value="">Choisir</option>
                  {data.workers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {workerName(w)} ·{" "}
                      {w.kind === "employee" ? "Employé" : "Sous-traitant"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Heures
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={assignedHours || editing.planned_hours || 0}
                  onChange={(e) => setAssignedHours(+e.target.value)}
                />
              </label>
              <button
                className="secondary"
                type="button"
                onClick={suggest}
                disabled={busy}
              >
                Choisir le prioritaire
              </button>
              <button
                type="button"
                onClick={assign}
                disabled={busy || !workerId}
              >
                Affecter / proposer
              </button>
            </div>
          </div>
        )}
        {message && (
          <Notice
            tone={
              message.includes("créée") ||
              message.includes("enregistrée") ||
              message.includes("proposée") ||
              message.includes("prioritaire") ||
              message.includes("affecté") ||
              message.includes("annulée")
                ? "success"
                : "error"
            }
          >
            {message}
          </Notice>
        )}
      </section>
    </>
  );
}

function MissionAdminModal({
  profile,
  data,
  reload,
  createRequested,
  focusMission,
  onConsumed,
  onCreateConsumed,
  onReviewReport,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  createRequested: boolean;
  focusMission: Mission | null;
  onConsumed: () => void;
  onCreateConsumed: () => void;
  onReviewReport: (missionId: string) => void;
}) {
  const blank = (): Partial<Mission> => ({
    client_id: data.clients[0]?.id || "",
    property_id: null,
    service_id: null,
    property_service_id: null,
    starts_at: "",
    ends_at: "",
    status: "pending",
    location: "",
    planned_hours: 0,
    amount_ex_vat: 0,
    billing_mode: "hourly",
    billing_rate: 0,
    billing_quantity: 1,
    required_skills: [],
    notes: "",
  });
  const [editing, setEditing] = useState<Partial<Mission>>(blank),
    [selectedServiceKeys, setSelectedServiceKeys] = useState<string[]>([]),
    [open, setOpen] = useState(false),
    [editMode, setEditMode] = useState(false),
    [finalizationOpen, setFinalizationOpen] = useState(false),
    [finalizationReportMode, setFinalizationReportMode] = useState<
      "admin_report" | "without_report"
    >("admin_report"),
    [finalizationActualHours, setFinalizationActualHours] = useState(""),
    [finalizationClientReport, setFinalizationClientReport] = useState(""),
    [finalizationNextActions, setFinalizationNextActions] = useState(""),
    [finalizationAdminNotes, setFinalizationAdminNotes] = useState(""),
    [finalizationOverrideReason, setFinalizationOverrideReason] = useState(""),
    [finalizationInvoiceStrategy, setFinalizationInvoiceStrategy] = useState<
      "grouped" | "individual"
    >("grouped"),
    [missionChecklist, setMissionChecklist] = useState<MissionChecklistOverview | null>(null),
    [missionChecklistModels, setMissionChecklistModels] = useState<MissionChecklistModelOption[]>([]),
    [missionChecklistModelId, setMissionChecklistModelId] = useState(""),
    [missionChecklistReason, setMissionChecklistReason] = useState(""),
    [checklistExceptionReviewId, setChecklistExceptionReviewId] = useState(""),
    [checklistExceptionReviewNote, setChecklistExceptionReviewNote] = useState(""),
    [missionChecklistLoading, setMissionChecklistLoading] = useState(false),
    [workerId, setWorkerId] = useState(""),
    [rateCardId, setRateCardId] = useState(""),
    [assignedHours, setAssignedHours] = useState(0),
    [allowSkillOverride, setAllowSkillOverride] = useState(false),
    [skillOverrideReason, setSkillOverrideReason] = useState(""),
    [changeOpen, setChangeOpen] = useState(false),
    [changeType, setChangeType] = useState<MissionChangeEvent["event_type"]>("modification"),
    [changeReason, setChangeReason] = useState(""),
    [changeNotes, setChangeNotes] = useState(""),
    [changeStart, setChangeStart] = useState(""),
    [changeEnd, setChangeEnd] = useState(""),
    [changeLocation, setChangeLocation] = useState(""),
    [changeHours, setChangeHours] = useState(""),
    [changeNeedsClientApproval, setChangeNeedsClientApproval] = useState(false),
    [changeAssignmentId, setChangeAssignmentId] = useState(""),
    [changeEvidenceId, setChangeEvidenceId] = useState(""),
    [changeAdjustmentKind, setChangeAdjustmentKind] = useState<"" | MissionFinancialAdjustment["adjustment_kind"]>(""),
    [changeAdjustmentAmount, setChangeAdjustmentAmount] = useState(""),
    [changeVat, setChangeVat] = useState(String(data.organization?.default_vat_rate ?? 20)),
    [qualityClaimReview, setQualityClaimReview] = useState<MissionQualityClaim | null>(null),
    [qualityClaimDecision, setQualityClaimDecision] = useState("start_review"),
    [qualityClaimQualification, setQualityClaimQualification] = useState(""),
    [qualityClaimAdminNotes, setQualityClaimAdminNotes] = useState(""),
    [qualityClaimClientResponse, setQualityClaimClientResponse] = useState(""),
    [qualityClaimRetouchMissionId, setQualityClaimRetouchMissionId] = useState(""),
    [costingOpen, setCostingOpen] = useState(false),
    [costingRunId, setCostingRunId] = useState(""),
    [costingOutputQuantity, setCostingOutputQuantity] = useState(""),
    [costingOutputUnit, setCostingOutputUnit] = useState("m2"),
    [costingEquipmentCost, setCostingEquipmentCost] = useState(""),
    [costingOtherCost, setCostingOtherCost] = useState(""),
    [costingNotes, setCostingNotes] = useState(""),
    [costingDecisionNotes, setCostingDecisionNotes] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const select = useCallback(
    (m?: Mission) => {
      const existingLines = m
        ? (data.missionServiceLines || [])
            .filter((line) => line.mission_id === m.id)
            .sort((left, right) => left.position - right.position)
        : [];
      setEditing(
        m
          ? {
              ...m,
              starts_at: localInput(m.starts_at),
              ends_at: localInput(m.ends_at),
            }
          : blank(),
      );
      setSelectedServiceKeys(
        existingLines.length
          ? existingLines.map((line) =>
              line.property_service_id
                ? `property:${line.property_service_id}`
                : `client:${line.client_service_id}`,
            )
          : m?.property_service_id
            ? [`property:${m.property_service_id}`]
            : m?.service_id
              ? [`client:${m.service_id}`]
              : [],
      );
      setWorkerId("");
      setRateCardId("");
      setAssignedHours(m?.planned_hours || 0);
      setAllowSkillOverride(false);
      setSkillOverrideReason("");
      setChangeOpen(false);
      setCostingOpen(false);
      setFinalizationOpen(false);
      setMissionChecklist(null);
      setMissionChecklistModelId(m?.checklist_model_id || "");
      setMissionChecklistReason("");
      setChecklistExceptionReviewId("");
      setChecklistExceptionReviewNote("");
      setCostingDecisionNotes("");
      setMessage("");
      setEditMode(!m);
      setOpen(true);
    },
    [data.clients, data.missionServiceLines],
  );
  useEffect(() => {
    if (focusMission) {
      select(focusMission);
      onConsumed();
    }
  }, [focusMission, onConsumed, select]);
  useEffect(() => {
    if (createRequested) {
      select();
      onCreateConsumed();
    }
  }, [createRequested, onCreateConsumed, select]);
  const loadMissionChecklist = useCallback(async (missionId: string) => {
    setMissionChecklistLoading(true);
    const [overviewResult, modelsResult, versionsResult] = await Promise.all([
      supabase!.rpc("get_mission_checklist", { p_mission_id: missionId }),
      supabase!
        .from("mission_checklist_models")
        .select("id,name,description")
        .eq("active", true)
        .order("name"),
      supabase!
        .from("service_mission_checklists")
        .select("model_id")
        .eq("status", "published")
        .eq("is_current_version", true),
    ]);
    setMissionChecklistLoading(false);
    if (overviewResult.error) {
      setMissionChecklist(null);
      if (!missingDeploymentObject(overviewResult.error.message))
        setMessage(overviewResult.error.message);
      return;
    }
    const publishedModelIds = new Set(
      (versionsResult.data || [])
        .map((version) => version.model_id)
        .filter((id): id is string => Boolean(id)),
    );
    setMissionChecklist(overviewResult.data as MissionChecklistOverview);
    setMissionChecklistModels(
      ((modelsResult.data || []) as MissionChecklistModelOption[]).filter(
        (model) => publishedModelIds.has(model.id),
      ),
    );
  }, []);
  useEffect(() => {
    if (open && editing.id) void loadMissionChecklist(editing.id);
  }, [open, editing.id, loadMissionChecklist]);
  const properties = data.properties.filter(
      (p) => p.client_id === editing.client_id && p.active,
    ),
    clientServices = data.services.filter(
      (service) => service.client_id === editing.client_id,
    ),
    propertyServices = (data.propertyServices || []).filter(
      (service) => service.property_id === editing.property_id && service.active,
    ),
    selectedContractualServices = selectedServiceKeys
      .map((key) => {
        const [scope, id] = key.split(":");
        return scope === "property"
          ? propertyServices.find((service) => service.id === id)
          : clientServices.find((service) => service.id === id);
      })
      .filter(
        (service): service is ClientService | PropertyService => Boolean(service),
      ),
    assignments = editing.id
      ? data.assignments.filter(
          (a) => a.mission_id === editing.id && a.status !== "cancelled",
        )
      : [],
    selectedWorker = data.workers.find((worker) => worker.id === workerId),
    selectedCostingProfiles = selectedContractualServices
      .map((service) =>
        (data.serviceCostingProfiles || [])
          .filter(
            (costingProfile) =>
              costingProfile.catalog_item_id === service.catalog_item_id &&
              costingProfile.active,
          )
          .sort((left, right) => right.version - left.version)[0],
      )
      .filter((profile): profile is ServiceCostingProfile => Boolean(profile)),
    missionRequiredSkills = uniqueSkills(
      (editing.required_skills || []).length
        ? editing.required_skills || []
        : selectedCostingProfiles.flatMap((profile) => profile.required_skills || []),
    ),
    selectedMissingSkills = missingWorkerSkills(selectedWorker, missionRequiredSkills),
    selectedRates = (data.workerRateCards || []).filter(
      (rate) =>
        rate.worker_id === workerId &&
        rate.active &&
        workerRateAppliesToMission(rate, editing),
    ),
    missionReports = editing.id
      ? data.reports.filter((report) => report.mission_id === editing.id)
      : [],
    pendingMissionReport = missionReports.find(
      (report) => report.status === "pending",
    ),
    missionReportIds = new Set(missionReports.map((report) => report.id)),
    missionAttachments = (data.reportAttachments || []).filter((attachment) =>
      missionReportIds.has(attachment.report_id),
    ),
    missionActivityEntries = (data.missionReportActivityEntries || []).filter(
      (entry) => missionReportIds.has(entry.report_id),
    ),
    missionStockUsages = editing.id
      ? (data.missionStockUsages || []).filter(
          (usage) => usage.mission_id === editing.id,
        )
      : [],
    missionInvoiceLines = editing.id
      ? (data.invoiceLines || []).filter(
          (line) => line.mission_id === editing.id,
        )
      : [],
    missionInvoices = data.invoices.filter((invoice) =>
      missionInvoiceLines.some((line) => line.invoice_id === invoice.id),
    ),
    missionSubmissions = editing.id
      ? (data.missionReportSubmissions || []).filter(
          (submission) => submission.mission_id === editing.id,
        )
      : [],
    missionBillingSnapshot = editing.id
      ? (data.missionBillingSnapshots || []).find(
          (snapshot) => snapshot.mission_id === editing.id,
        )
      : null;
  const missionChanges = editing.id
      ? (data.missionChangeEvents || []).filter((event) => event.mission_id === editing.id)
      : [],
    missionAdjustments = editing.id
      ? (data.missionFinancialAdjustments || []).filter((adjustment) => adjustment.mission_id === editing.id)
      : [],
    missionCancellationNotices = editing.id
      ? (data.workerCancellationNotices || []).filter((notice) => notice.mission_id === editing.id)
      : [],
    missionQualityClaims = editing.id
      ? (data.missionQualityClaims || []).filter((claim) => claim.mission_id === editing.id)
      : [],
    availableRetouchMissions = editing.id
      ? data.missions.filter(
          (mission) =>
            mission.id !== editing.id &&
            mission.client_id === editing.client_id &&
            mission.status !== "cancelled",
        )
      : [],
    missionCostingReviews = editing.id
      ? (data.missionCostingReviews || [])
          .filter((review) => review.mission_id === editing.id)
          .sort((a, b) => b.review_number - a.review_number)
      : [],
    latestMissionCostingReview = missionCostingReviews[0] || null,
    latestMissionCostingLines = latestMissionCostingReview
      ? (data.missionCostingReviewLines || [])
          .filter((line) => line.review_id === latestMissionCostingReview.id)
          .sort((a, b) => a.position - b.position)
      : [],
    availableCostingRuns = (data.quoteCostingRuns || []).filter((run) => {
      const quote = data.quotes.find((item) => item.id === run.quote_id);
      return run.status === "applied" && quote?.client_id === editing.client_id;
    }),
    selectedMissionCostingRun = availableCostingRuns.find((run) => run.id === costingRunId);

  function startMissionChange() {
    setChangeType("modification");
    setChangeReason("");
    setChangeNotes("");
    setChangeStart(String(editing.starts_at || ""));
    setChangeEnd(String(editing.ends_at || ""));
    setChangeLocation(String(editing.location || ""));
    setChangeHours(String(editing.planned_hours ?? ""));
    setChangeNeedsClientApproval(false);
    setChangeAssignmentId("");
    setChangeEvidenceId("");
    setChangeAdjustmentKind("");
    setChangeAdjustmentAmount("");
    setChangeOpen(true);
  }

  function startMissionCostingReview() {
    const suggestedRun = availableCostingRuns[0] || null;
    setCostingRunId(suggestedRun?.id || "");
    setCostingOutputQuantity(
      suggestedRun?.output_quantity == null ? "" : String(suggestedRun.output_quantity),
    );
    setCostingOutputUnit(suggestedRun?.output_unit || "m2");
    setCostingEquipmentCost("");
    setCostingOtherCost("");
    setCostingNotes("");
    setCostingOpen(true);
  }

  async function createMissionCostingReview(event: React.FormEvent) {
    event.preventDefault();
    if (!editing.id) return;
    setBusy(true);
    const result = await supabase!.rpc("create_mission_costing_review", {
      p_mission_id: editing.id,
      p_quote_costing_run_id: costingRunId || null,
      p_actual_output_quantity:
        costingOutputQuantity === "" ? null : Number(costingOutputQuantity),
      p_output_unit: costingOutputQuantity === "" ? null : costingOutputUnit,
      p_actual_equipment_cost_ex_vat:
        costingEquipmentCost === "" ? null : Number(costingEquipmentCost),
      p_actual_other_cost_ex_vat:
        costingOtherCost === "" ? null : Number(costingOtherCost),
      p_notes: costingNotes.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage("Comparaison prévu / réel créée à partir des données approuvées.");
    setCostingOpen(false);
    reload();
  }

  async function decideMissionCostingReview(
    review: MissionCostingReview,
    decision: "approved" | "rejected",
    publishObservedYield = false,
  ) {
    if (
      decision === "approved" &&
      review.status === "needs_review" &&
      !costingDecisionNotes.trim()
    ) {
      setMessage("Expliquez la validation d’une comparaison incomplète.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("review_mission_costing_review", {
      p_review_id: review.id,
      p_decision: decision,
      p_publish_observed_yield: publishObservedYield,
      p_decision_notes: costingDecisionNotes.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage(
      decision === "rejected"
        ? "Comparaison refusée et conservée dans l’historique."
        : publishObservedYield
          ? "Comparaison validée et nouveau rendement publié comme version contrôlée."
          : "Comparaison validée sans modifier le modèle de chiffrage.",
    );
    setCostingDecisionNotes("");
    reload();
  }

  async function proposeMissionChange(event: React.FormEvent) {
    event.preventDefault();
    if (!editing.id || changeReason.trim().length < 3) {
      setMessage("Décrivez précisément le motif du changement.");
      return;
    }
    const needsSupplierTarget = changeAdjustmentKind && changeAdjustmentKind !== "client_supplement";
    if (needsSupplierTarget && !changeAssignmentId) {
      setMessage("Choisissez l’affectation ST concernée par l’ajustement.");
      return;
    }
    if (changeAdjustmentKind && Number(changeAdjustmentAmount) <= 0) {
      setMessage("Renseignez le montant positif de l’ajustement.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("propose_mission_change", {
      p_mission_id: editing.id,
      p_event_type: changeType,
      p_reason: changeReason.trim(),
      p_operational_notes: changeNotes.trim() || null,
      p_new_starts_at: changeType === "reschedule" || changeType === "modification"
        ? new Date(changeStart).toISOString()
        : null,
      p_new_ends_at: changeType === "reschedule" || changeType === "modification"
        ? new Date(changeEnd).toISOString()
        : null,
      p_new_location: changeLocation.trim() || null,
      p_new_planned_hours: changeHours === "" ? null : Number(changeHours),
      p_new_notes: null,
      p_client_amount_delta_ex_vat:
        changeAdjustmentKind === "client_supplement"
          ? Number(changeAdjustmentAmount || 0)
          : 0,
      p_supplier_amount_delta_ex_vat:
        changeAdjustmentKind === "supplier_surcharge"
          ? Number(changeAdjustmentAmount || 0)
          : changeAdjustmentKind === "supplier_penalty" ||
              changeAdjustmentKind === "supplier_deduction"
            ? -Number(changeAdjustmentAmount || 0)
            : 0,
      p_requires_client_approval: changeNeedsClientApproval,
      p_evidence_document_id: changeEvidenceId || null,
      p_assignment_id: changeAssignmentId || null,
      p_adjustment_kind: changeAdjustmentKind || null,
      p_adjustment_amount_ex_vat: changeAdjustmentKind ? Number(changeAdjustmentAmount) : null,
      p_adjustment_vat_rate: Number(changeVat || 0),
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage("Changement enregistré pour contrôle. Aucun montant n’a été appliqué automatiquement.");
    setChangeOpen(false);
    reload();
  }

  async function reviewMissionChange(event: MissionChangeEvent, approve: boolean) {
    setBusy(true);
    const result = await supabase!.rpc("review_mission_change", {
      p_event_id: event.id,
      p_approve: approve,
      p_review_notes: approve ? "Contrôle administratif validé" : "Refus administratif",
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : !approve
          ? "Changement refusé et conservé dans l’historique."
          : event.requires_client_approval
            ? "Changement contrôlé : validation du client demandée avant application."
            : "Changement validé et répercuté sur la mission.",
    );
    if (!result.error) reload();
  }
  async function reviewCancellationNotice(notice: WorkerCancellationNotice, approve: boolean, withPenalty: boolean) {
    setBusy(true);
    const result = await supabase!.rpc("review_worker_cancellation_notice", {
      p_notice_id: notice.id,
      p_approve_cancellation: approve,
      p_approve_penalty: approve && withPenalty,
      p_penalty_amount_ex_vat: approve && withPenalty ? notice.proposed_penalty_ex_vat : 0,
      p_review_notes: approve
        ? withPenalty
          ? "Annulation acceptée et proposition contractuelle de pénalité validée."
          : "Annulation acceptée sans pénalité."
        : "Annulation refusée après contrôle administratif.",
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : approve
          ? withPenalty
            ? "Annulation acceptée et pénalité validée pour le décompte fournisseur."
            : "Annulation acceptée sans pénalité ; la mission reste à réattribuer."
          : "Demande d’annulation refusée et conservée dans l’historique.",
    );
    if (!result.error) reload();
  }
  function startQualityClaimReview(claim: MissionQualityClaim) {
    setQualityClaimReview(claim);
    setQualityClaimDecision(
      claim.status === "submitted"
        ? "start_review"
        : claim.retouch_mission_id
          ? "resolve"
          : "request_retouch",
    );
    setQualityClaimQualification(claim.admin_qualification || "");
    setQualityClaimAdminNotes(claim.admin_notes || "");
    setQualityClaimClientResponse(claim.client_response || "");
    setQualityClaimRetouchMissionId(claim.retouch_mission_id || "");
  }
  async function reviewQualityClaim(event: React.FormEvent) {
    event.preventDefault();
    if (!qualityClaimReview) return;
    if (
      ["request_retouch", "link_retouch", "resolve"].includes(qualityClaimDecision) &&
      !qualityClaimQualification
    ) {
      setMessage("Choisissez la qualification administrative avant cette décision.");
      return;
    }
    if (qualityClaimDecision === "link_retouch" && !qualityClaimRetouchMissionId) {
      setMessage("Choisissez la mission qui réalisera la retouche.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("review_mission_quality_claim", {
      p_claim_id: qualityClaimReview.id,
      p_decision: qualityClaimDecision,
      p_qualification: qualityClaimQualification || null,
      p_admin_notes: qualityClaimAdminNotes.trim() || null,
      p_client_response: qualityClaimClientResponse.trim() || null,
      p_retouch_mission_id: qualityClaimRetouchMissionId || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage(
      "Décision qualité enregistrée et historisée. Les indicateurs ne retiennent que la qualification validée.",
    );
    setQualityClaimReview(null);
    reload();
  }
  async function openQualityClaimAttachment(attachment: MissionQualityClaimAttachment) {
    const result = await supabase!.storage
      .from("quality-claims")
      .createSignedUrl(attachment.storage_path, 120);
    if (result.error) setMessage(result.error.message);
    else window.open(result.data.signedUrl, "_blank", "noopener,noreferrer");
  }
  async function openMissionAttachment(attachment: ReportAttachment) {
    const { data: signed, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(attachment.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  }
  function toggleMissionService(serviceKey: string) {
    const nextKeys = selectedServiceKeys.includes(serviceKey)
        ? selectedServiceKeys.filter((key) => key !== serviceKey)
        : [...selectedServiceKeys, serviceKey],
      nextServices = nextKeys
        .map((key) => {
          const [scope, id] = key.split(":");
          return scope === "property"
            ? propertyServices.find((service) => service.id === id)
            : clientServices.find((service) => service.id === id);
        })
        .filter((service): service is ClientService | PropertyService => Boolean(service)),
      firstKey = nextKeys[0] || "",
      [firstScope, firstId] = firstKey.split(":"),
      totalAmount = nextServices.reduce(
        (sum, service) => sum + Number(service.unit_price_ex_vat || 0),
        0,
      ),
      totalHours = nextServices.reduce(
        (sum, service) => sum + Number(service.default_duration_hours || 0),
        0,
      ),
      requiredSkills = uniqueSkills(
        nextServices.flatMap((service) =>
          (data.serviceCostingProfiles || [])
            .filter(
              (profile) =>
                profile.active && profile.catalog_item_id === service.catalog_item_id,
            )
            .sort((left, right) => right.version - left.version)[0]
            ?.required_skills || [],
        ),
      );
    setSelectedServiceKeys(nextKeys);
    setEditing((value) => ({
      ...value,
      service_id: firstScope === "client" ? firstId : null,
      property_service_id: firstScope === "property" ? firstId : null,
      billing_mode:
        nextServices.length > 1
          ? "fixed"
          : nextServices[0]?.billing_mode || value.billing_mode,
      billing_rate: totalAmount,
      billing_quantity: 1,
      amount_ex_vat: totalAmount,
      planned_hours: totalHours || value.planned_hours,
      required_skills: requiredSkills,
    }));
    setWorkerId("");
    setRateCardId("");
    setAllowSkillOverride(false);
    setSkillOverrideReason("");
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedServiceKeys.length) {
      setMessage(
        "Sélectionnez au moins une prestation ou un supplément pour que chacun sache précisément ce qui doit être réalisé.",
      );
      return;
    }
    const start = new Date(editing.starts_at || ""),
      end = new Date(editing.ends_at || "");
    if (!(end > start))
      return setMessage(
        "La fin doit être postérieure au début. Pour une mission de nuit, choisissez le jour suivant.",
      );
    setBusy(true);
    const duration = (end.getTime() - start.getTime()) / 3600000,
      payload = {
        organization_id: profile.organization_id,
        client_id: editing.client_id,
        property_id: editing.property_id || null,
        service_id: editing.service_id || null,
        property_service_id: editing.property_service_id || null,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        status: editing.status || "pending",
        location: editing.location || "",
        planned_hours: +(editing.planned_hours || duration),
        amount_ex_vat: +(editing.amount_ex_vat || 0),
        billing_mode: editing.billing_mode || "hourly",
        billing_rate: +(editing.billing_rate || 0),
        billing_quantity: +(editing.billing_quantity || 1),
        billing_status: editing.billing_status || "planned",
        required_skills: missionRequiredSkills,
        notes: editing.notes || null,
        created_by: profile.id,
      };
    const result = editing.id
      ? await supabase!
          .from("missions")
          .update(payload)
          .eq("id", editing.id)
          .select("id")
          .single()
      : await supabase!.from("missions").insert(payload).select("id").single();
    if (!result.error) {
      const composition = await supabase!.rpc("set_mission_service_lines", {
        p_mission_id: result.data.id,
        p_service_keys: selectedServiceKeys,
        p_change_reason: editing.id
          ? "Composition mise à jour depuis la fiche mission"
          : "Composition définie à la création de la mission",
      });
      if (composition.error) {
        setBusy(false);
        setMessage(
          `La mission est enregistrée, mais sa composition ne l’est pas : ${composition.error.message}`,
        );
        return;
      }
      setEditing((v) => ({ ...v, id: result.data.id }));
      setEditMode(false);
      reload();
    }
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : editing.id
          ? "Mission et composition enregistrées."
          : "Mission et composition créées.",
    );
  }
  async function assign() {
    if (!editing.id || !workerId) return;
    const worker = data.workers.find((w) => w.id === workerId),
      status = worker?.kind === "contractor" ? "proposed" : "assigned";
    if (worker?.kind === "contractor" && selectedRates.length && !rateCardId) {
      setMessage("Choisissez le tarif convenu pour cette intervention.");
      return;
    }
    if (selectedMissingSkills.length && !allowSkillOverride) {
      setMessage(
        `Affectation bloquée : compétence${selectedMissingSkills.length > 1 ? "s" : ""} manquante${selectedMissingSkills.length > 1 ? "s" : ""} (${selectedMissingSkills.join(", ")}).`,
      );
      return;
    }
    if (selectedMissingSkills.length && skillOverrideReason.trim().length < 10) {
      setMessage("Expliquez la dérogation en au moins 10 caractères pour conserver une trace exploitable.");
      return;
    }
    setBusy(true);
    const assignmentHours = +assignedHours || +(editing.planned_hours || 0);
    let { error } = await supabase!.rpc("save_mission_assignment", {
      p_mission_id: editing.id,
      p_worker_id: workerId,
      p_assigned_hours: assignmentHours,
      p_worker_rate_card_id: rateCardId || null,
      p_allow_skill_override: selectedMissingSkills.length > 0 && allowSkillOverride,
      p_skill_override_reason:
        selectedMissingSkills.length > 0 ? skillOverrideReason.trim() : null,
    });
    if (
      error &&
      /save_mission_assignment|schema cache|could not find the function|function .* does not exist/i.test(
        error.message,
      )
    ) {
      if (selectedMissingSkills.length) {
        setBusy(false);
        setMessage(
          "La dérogation sécurisée n’est pas encore activée sur cette base. L’affectation n’a pas été enregistrée.",
        );
        return;
      }
      const fallback = await supabase!.from("mission_assignments").upsert(
        {
          mission_id: editing.id,
          worker_id: workerId,
          assigned_hours: assignmentHours,
          source: "manual",
          status,
          worker_rate_card_id: rateCardId || null,
          pricing_mode: null,
          agreed_rate: null,
          agreed_amount: null,
        },
        { onConflict: "mission_id,worker_id" },
      );
      error = fallback.error;
    }
    setBusy(false);
    setMessage(
      error
        ? error.message
        : status === "proposed"
          ? "Mission proposée au sous-traitant."
          : "Employé affecté à la mission.",
    );
    if (!error) {
      setAllowSkillOverride(false);
      setSkillOverrideReason("");
      reload();
    }
  }
  async function suggest() {
    if (!editing.id) return setMessage("Enregistrez d’abord la mission.");
    setBusy(true);
    const { data: candidates, error } = await supabase!.rpc(
      "mission_candidates",
      { p_mission_id: editing.id },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    if (!candidates?.length)
      return setMessage(
        "Aucune personne disponible sans conflit ni indisponibilité.",
      );
    const suggestedWorkerId = candidates[0].worker_id,
      suggestedRates = (data.workerRateCards || []).filter(
        (rate) =>
          rate.worker_id === suggestedWorkerId &&
          rate.active &&
          workerRateAppliesToMission(rate, editing),
      );
    setWorkerId(suggestedWorkerId);
    setRateCardId(suggestedRates.length === 1 ? suggestedRates[0].id : "");
    setAssignedHours(+(editing.planned_hours || 0));
    setMessage(
      `${candidates[0].display_name} est prioritaire : ${candidates[0].reason}.`,
    );
  }
  async function cancelAssignment(id: string) {
    const { error } = await supabase!
      .from("mission_assignments")
      .update({ status: "cancelled" })
      .eq("id", id);
    setMessage(error ? error.message : "Affectation annulée.");
    if (!error) reload();
  }
  async function reopenForFieldReport() {
    if (!editing.id) return;
    setBusy(true);
    const activeAssignment = assignments.some((assignment) =>
        ["assigned", "accepted"].includes(assignment.status),
      ),
      updated = await supabase!
        .from("missions")
        .update({
          status: activeAssignment ? "assigned" : "pending",
          workflow_status: activeAssignment ? "confirmed" : "planned",
          completed_at: null,
        })
        .eq("id", editing.id)
        .is("admin_validated_at", null);
    if (updated.error) {
      setBusy(false);
      setMessage(updated.error.message);
      return;
    }
    const recipientIds = Array.from(
      new Set(
        assignments
          .map(
            (assignment) =>
              data.workers.find((worker) => worker.id === assignment.worker_id)
                ?.profile_id,
          )
          .filter((id): id is string => Boolean(id)),
      ),
    );
    if (recipientIds.length) {
      await supabase!.from("notifications").insert(
        recipientIds.map((recipientProfileId) => ({
          organization_id: profile.organization_id,
          recipient_profile_id: recipientProfileId,
          mission_id: editing.id,
          kind: "mission_report_required",
          title: "Compte rendu de mission attendu",
          body:
            "Terminez les obligations, ajoutez les preuves demandées puis transmettez le compte rendu pour contrôle administratif.",
        })),
      );
    }
    setBusy(false);
    setMessage(
      recipientIds.length
        ? "Mission réouverte et intervenant informé : le compte rendu est attendu."
        : "Mission réouverte. Affectez un intervenant avant de demander le compte rendu.",
    );
    setEditing((current) => ({
      ...current,
      status: activeAssignment ? "assigned" : "pending",
      workflow_status: activeAssignment ? "confirmed" : "planned",
    }));
    reload();
  }
  function openAdminFinalization() {
    const client = data.clients.find((item) => item.id === editing.client_id);
    setFinalizationReportMode("admin_report");
    setFinalizationActualHours("");
    setFinalizationClientReport(editing.client_report || "");
    setFinalizationNextActions(editing.next_actions || "");
    setFinalizationAdminNotes(editing.admin_notes || "");
    setFinalizationOverrideReason("");
    setFinalizationInvoiceStrategy(
      client?.billing_grouping === "individual" ? "individual" : "grouped",
    );
    setFinalizationOpen(true);
    setMessage("");
  }
  async function finalizeAdminMission(event: React.FormEvent) {
    event.preventDefault();
    if (!editing.id) return;
    if (
      finalizationReportMode === "admin_report" &&
      finalizationActualHours === "" &&
      !finalizationClientReport.trim() &&
      !finalizationNextActions.trim() &&
      !finalizationAdminNotes.trim()
    ) {
      setMessage(
        "Renseignez au moins une information du compte rendu administratif ou choisissez « Sans compte rendu ».",
      );
      return;
    }
    if (
      finalizationReportMode === "without_report" &&
      finalizationOverrideReason.trim().length < 5
    ) {
      setMessage(
        "Indiquez brièvement pourquoi vous clôturez cette mission sans compte rendu.",
      );
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("finalize_mission_admin", {
      p_mission_id: editing.id,
      p_report_mode: finalizationReportMode,
      p_source_report_id: null,
      p_actual_hours:
        finalizationActualHours === "" ? null : Number(finalizationActualHours),
      p_client_report: finalizationClientReport.trim() || null,
      p_next_actions: finalizationNextActions.trim() || null,
      p_admin_notes: finalizationAdminNotes.trim() || null,
      p_override_reason: finalizationOverrideReason.trim() || null,
      p_invoice_strategy: finalizationInvoiceStrategy,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    const validatedAt = new Date().toISOString();
    setEditing((current) => ({
      ...current,
      status: "completed",
      workflow_status: "approved",
      billing_status: "to_invoice",
      admin_validated_at: validatedAt,
      invoice_strategy: finalizationInvoiceStrategy,
      client_report:
        finalizationClientReport.trim() ||
        "La mission a été terminée et validée par ARD.",
      next_actions: finalizationNextActions.trim() || null,
      admin_notes: finalizationAdminNotes.trim() || null,
    }));
    setFinalizationOpen(false);
    setEditMode(false);
    setMessage(
      finalizationInvoiceStrategy === "grouped"
        ? "Mission clôturée et placée à facturer sur la facture globale du client."
        : "Mission clôturée et placée à facturer sur une facture indépendante.",
    );
    reload();
  }
  async function saveMissionChecklistModel() {
    if (!editing.id) return;
    if (missionChecklistReason.trim().length < 3) {
      setMessage("Indiquez brièvement pourquoi cette checklist est choisie.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("set_mission_checklist_model", {
      p_mission_id: editing.id,
      p_model_id: missionChecklistModelId || null,
      p_change_reason: missionChecklistReason.trim(),
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMissionChecklist(result.data as MissionChecklistOverview);
    setEditing((current) => ({
      ...current,
      checklist_model_id: missionChecklistModelId || null,
    }));
    setMissionChecklistReason("");
    setMessage(
      missionChecklistModelId
        ? "Checklist affectée à cette mission."
        : "Règle automatique de checklist réappliquée à cette mission.",
    );
    reload();
  }
  async function reviewChecklistProofException(exceptionId: string, accept: boolean) {
    if (!editing.id) return;
    if (!accept && checklistExceptionReviewNote.trim().length < 5) {
      setMessage("Expliquez pourquoi la justification est refusée.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("review_mission_checklist_proof_exception", {
      p_exception_id: exceptionId,
      p_accept: accept,
      p_review_note: checklistExceptionReviewNote.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMissionChecklist(result.data as MissionChecklistOverview);
    setChecklistExceptionReviewId("");
    setChecklistExceptionReviewNote("");
    setMessage(
      accept
        ? "Justification de preuve acceptée et auditée."
        : "Justification refusée : l’intervenant doit compléter la preuve avant validation.",
    );
    reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Activité opérationnelle</span>
          <h1>Missions</h1>
        </div>
        <button onClick={() => select()}>Nouvelle mission</button>
      </div>
      <section className="panel">
        <p className="section-intro">
          Cliquez sur une mission pour ouvrir sa fiche sans quitter la liste.
        </p>
        <MissionList
          items={data.missions}
          actions={(m) => (
            <button className="secondary compact" onClick={() => select(m)}>
              Ouvrir
            </button>
          )}
        />
      </section>
      {open && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail mission-editor-modal">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">
                  {editing.id ? "Dossier permanent" : "Création"}
                </span>
                <h2>
                  {editing.id ? "Fiche mission" : "Nouvelle mission"}
                </h2>
              </div>
              <div className="row-actions">
                {editing.id && !editing.admin_validated_at && editing.status !== "completed" && (
                  <button
                    type="button"
                    className="secondary compact"
                    onClick={() => setEditMode((value) => !value)}
                  >
                    {editMode ? "Fermer les réglages" : "Modifier la planification"}
                  </button>
                )}
                <button className="icon-close" onClick={() => setOpen(false)}>
                  ×
                </button>
              </div>
            </div>
            {editMode && (
            <form className="form-grid mission-planning-form" onSubmit={save}>
              <label>
                Client
                <select
                  value={editing.client_id || ""}
                  onChange={(e) =>
                    {
                      setSelectedServiceKeys([]);
                      setEditing((v) => ({
                        ...v,
                        client_id: e.target.value,
                        property_id: null,
                        service_id: null,
                        property_service_id: null,
                        amount_ex_vat: 0,
                        billing_rate: 0,
                        planned_hours: 0,
                        required_skills: [],
                      }));
                    }
                  }
                  required
                >
                  <option value="">Choisir</option>
                  {data.clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              {properties.length > 0 && (
                <label>
                  Bien
                  <select
                    value={editing.property_id || ""}
                    onChange={(e) => {
                      const p = properties.find((x) => x.id === e.target.value);
                      setSelectedServiceKeys([]);
                      setEditing((v) => ({
                        ...v,
                        property_id: e.target.value || null,
                        service_id: null,
                        property_service_id: null,
                        location: p?.address || v.location,
                        amount_ex_vat: 0,
                        billing_rate: 0,
                        planned_hours: 0,
                        required_skills: [],
                      }));
                    }}
                  >
                    <option value="">Client entier</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <fieldset className="wide mission-service-picker">
                <legend>Ce que comporte la mission</legend>
                <p>
                  Cochez toutes les prestations et tous les suppléments réalisés pendant la même intervention.
                </p>
                {!clientServices.length && !propertyServices.length ? (
                  <Notice tone="warning">
                    Aucune prestation contractuelle active n’est disponible pour ce client ou ce bien.
                  </Notice>
                ) : (
                  <div className="mission-service-options">
                    {clientServices.map((service) => {
                      const key = `client:${service.id}`;
                      return (
                        <label key={key} className="mission-service-option">
                          <input
                            type="checkbox"
                            checked={selectedServiceKeys.includes(key)}
                            onChange={() => toggleMissionService(key)}
                          />
                          <span>
                            <strong>{service.name}</strong>
                            <small>
                              Client entier · {money(service.unit_price_ex_vat)} HT
                              {service.default_duration_hours != null
                                ? ` · ${service.default_duration_hours} h prévues`
                                : ""}
                            </small>
                          </span>
                        </label>
                      );
                    })}
                    {propertyServices.map((service) => {
                      const key = `property:${service.id}`;
                      return (
                        <label key={key} className="mission-service-option">
                          <input
                            type="checkbox"
                            checked={selectedServiceKeys.includes(key)}
                            onChange={() => toggleMissionService(key)}
                          />
                          <span>
                            <strong>{service.name}</strong>
                            <small>
                              Bien sélectionné · {money(service.unit_price_ex_vat)} HT
                              {service.default_duration_hours != null
                                ? ` · ${service.default_duration_hours} h prévues`
                                : ""}
                            </small>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="mission-service-total">
                  <span>{selectedContractualServices.length} élément(s) sélectionné(s)</span>
                  <strong>
                    {money(Number(editing.amount_ex_vat || 0))} HT · {Number(editing.planned_hours || 0)} h prévues
                  </strong>
                </div>
                <small>
                  Ces libellés, prix et temps sont figés sur la mission lors de l’enregistrement.
                </small>
              </fieldset>
              <div className="wide">
                <small>Compétences requises</small>
                <strong>
                  {missionRequiredSkills.length
                    ? missionRequiredSkills.join(" · ")
                    : "Aucune compétence particulière définie"}
                </strong>
              </div>
              <label>
                Statut
                <select
                  value={editing.status || "pending"}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, status: e.target.value }))
                  }
                >
                  <option value="pending">En attente</option>
                  <option value="assigned">Affectée</option>
                  {editing.status === "completed" && (
                    <option value="completed">Terminée · compte rendu à contrôler</option>
                  )}
                  <option value="cancelled">Annulée</option>
                </select>
                <small>
                  « Terminée » ne valide ni les coûts ni la facturation. Ces deux
                  éléments sont acquis seulement après contrôle administratif du
                  compte rendu.
                </small>
              </label>
              <label>
                Début
                <input
                  type="datetime-local"
                  value={editing.starts_at || ""}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, starts_at: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Fin
                <input
                  type="datetime-local"
                  value={editing.ends_at || ""}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, ends_at: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Lieu
                <input
                  value={editing.location || ""}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, location: e.target.value }))
                  }
                  required
                />
              </label>
              <label>
                Durée prévue (h)
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={editing.planned_hours || 0}
                  onChange={(e) =>
                    setEditing((v) => ({
                      ...v,
                      planned_hours: +e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Facturation
                <select
                  value={editing.billing_mode || "hourly"}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, billing_mode: e.target.value }))
                  }
                >
                  <option value="hourly">Horaire</option>
                  <option value="fixed">Forfait</option>
                  <option value="per_unit">À l’unité</option>
                  <option value="monthly">Mensuel</option>
                </select>
              </label>
              <label>
                Taux / prix (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.billing_rate || 0}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, billing_rate: +e.target.value }))
                  }
                />
              </label>
              <label>
                Quantité
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.billing_quantity || 1}
                  onChange={(e) =>
                    setEditing((v) => ({
                      ...v,
                      billing_quantity: +e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Montant HT
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.amount_ex_vat || 0}
                  onChange={(e) =>
                    setEditing((v) => ({
                      ...v,
                      amount_ex_vat: +e.target.value,
                    }))
                  }
                />
              </label>
              <label className="wide">
                Notes et consignes
                <textarea
                  value={editing.notes || ""}
                  onChange={(e) =>
                    setEditing((v) => ({ ...v, notes: e.target.value }))
                  }
                />
              </label>
              <button disabled={busy}>
                {busy
                  ? "Enregistrement…"
                  : editing.id
                    ? "Enregistrer les modifications"
                    : "Créer la mission"}
              </button>
            </form>
            )}
            {editing.id && editMode && (
              <div className="assignment-box">
                <h3>Affectations</h3>
                {assignments.map((a) => {
                  const w = data.workers.find((x) => x.id === a.worker_id),
                    rate = (data.workerRateCards || []).find(
                      (item) => item.id === a.worker_rate_card_id,
                    );
                  return (
                    <div className="assignment-row" key={a.id}>
                      <span>
                        <strong>
                          {w ? workerName(w) : "Personne inconnue"}
                        </strong>{" "}
                        · {a.assigned_hours} h{rate ? ` · ${rate.name}` : ""}
                        {a.agreed_amount != null
                          ? ` · ${money(a.agreed_amount)} convenus`
                          : ""}{" "}
                        · <Status value={a.status} />
                        {a.skill_override && (
                          <small>
                            Dérogation compétences : {a.skill_override_reason || "motif conservé dans l’audit"}
                          </small>
                        )}
                      </span>
                      <button
                        className="danger compact"
                        onClick={() => cancelAssignment(a.id)}
                      >
                        Retirer
                      </button>
                    </div>
                  );
                })}
                {!assignments.length && (
                  <p className="empty">Aucune personne affectée.</p>
                )}
                <div className="assignment-controls">
                  <label>
                    Personne
                    <select
                      value={workerId}
                      onChange={(e) => {
                        const nextWorkerId = e.target.value,
                          rates = (data.workerRateCards || []).filter(
                            (rate) =>
                              rate.worker_id === nextWorkerId &&
                              rate.active &&
                              workerRateAppliesToMission(rate, editing),
                          );
                        setWorkerId(nextWorkerId);
                        setRateCardId(rates.length === 1 ? rates[0].id : "");
                      }}
                    >
                      <option value="">Choisir</option>
                      {data.workers.filter((w) => w.active).map((w) => (
                        <option key={w.id} value={w.id}>
                          {workerName(w)} ·{" "}
                          {w.kind === "employee" ? "Employé" : "ST"} ·{" "}
                          {missingWorkerSkills(w, missionRequiredSkills).length
                            ? `manque ${missingWorkerSkills(w, missionRequiredSkills).join(", ")}`
                            : "qualifié"}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selectedWorker?.kind === "contractor" &&
                    selectedRates.length > 0 && (
                      <label>
                        Tarif ST pour cette intervention
                        <select
                          value={rateCardId}
                          onChange={(e) => setRateCardId(e.target.value)}
                          required
                        >
                          <option value="">Choisir le forfait / tarif</option>
                          {selectedRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.name} · {money(rate.rate)}
                              {rate.pricing_mode === "hourly"
                                ? " / heure"
                                : rate.unit_label
                                  ? ` / ${rate.unit_label}`
                                  : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  <label>
                    Heures
                    <input
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={assignedHours || editing.planned_hours || 0}
                      onChange={(e) => setAssignedHours(+e.target.value)}
                    />
                  </label>
                  {selectedWorker && missionRequiredSkills.length > 0 && (
                    <div className="wide">
                      {selectedMissingSkills.length ? (
                        <>
                          <Notice tone="warning">
                            Affectation normalement bloquée : {selectedMissingSkills.join(", ")}.
                          </Notice>
                          <label className="check">
                            <input
                              type="checkbox"
                              checked={allowSkillOverride}
                              onChange={(event) =>
                                setAllowSkillOverride(event.target.checked)
                              }
                            />{" "}
                            Déroger exceptionnellement après contrôle administratif
                          </label>
                          {allowSkillOverride && (
                            <label>
                              Motif obligatoire de la dérogation
                              <textarea
                                aria-label="Motif obligatoire de la dérogation"
                                value={skillOverrideReason}
                                onChange={(event) => setSkillOverrideReason(event.target.value)}
                                placeholder="Ex. compétence équivalente vérifiée et mission supervisée"
                              />
                            </label>
                          )}
                        </>
                      ) : (
                        <Notice tone="success">
                          Toutes les compétences requises sont présentes sur la fiche de cette personne.
                        </Notice>
                      )}
                    </div>
                  )}
                  <button className="secondary" type="button" onClick={suggest}>
                    Choisir le prioritaire
                  </button>
                  <button
                    type="button"
                    onClick={assign}
                    disabled={
                      !workerId ||
                      busy ||
                      (selectedMissingSkills.length > 0 &&
                        (!allowSkillOverride || skillOverrideReason.trim().length < 10))
                    }
                  >
                    Affecter / proposer
                  </button>
                </div>
              </div>
            )}
            {editing.id && (
              <section className="mission-dossier" aria-label="Fiche mission complète">
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Historique opérationnel</span>
                    <h3>Fiche mission complète</h3>
                  </div>
                  <Status value={editing.workflow_status || editing.status || "pending"} />
                </div>
                {editing.admin_validated_at ? (
                  <Notice tone="success">
                    Mission validée par l’administration : les coûts sont acquis et
                    la facturation peut être préparée en {editing.invoice_strategy === "individual"
                      ? "facture indépendante"
                      : "facture globale client"}.
                  </Notice>
                ) : pendingMissionReport ? (
                  <Notice tone="warning">
                    Le compte rendu est transmis et attend votre contrôle. Sa
                    validation fera passer automatiquement la mission dans « À
                    facturer » et rendra ses coûts acquis.
                    <span className="row-actions">
                      <button
                        type="button"
                        className="success compact"
                        onClick={() => {
                          setOpen(false);
                          onReviewReport(pendingMissionReport.mission_id);
                        }}
                      >
                        Lire et valider le compte rendu
                      </button>
                    </span>
                  </Notice>
                ) : editing.status === "completed" ? (
                  <Notice tone="warning">
                    Le statut a été changé en « Terminée », mais la clôture
                    administrative n’a pas été faite. Vous pouvez rédiger le
                    compte rendu vous-même ou assumer une clôture sans rapport ;
                    la décision restera tracée avant la mise en facturation.
                  </Notice>
                ) : new Date(String(editing.starts_at || "")) > new Date() ? (
                  <Notice tone="info">
                    Mission future : son chiffre d’affaires et ses coûts estimés
                    alimentent le prévisionnel sans être comptés dans le réalisé.
                  </Notice>
                ) : null}
                {!editing.admin_validated_at && editing.status !== "cancelled" && (
                  <div className="mission-finalization-actions detail-actions">
                    <button
                      type="button"
                      className="success"
                      disabled={busy}
                      onClick={openAdminFinalization}
                    >
                      Clôturer par décision administrateur
                    </button>
                    {!pendingMissionReport && (
                      <button
                        type="button"
                        className="secondary"
                        disabled={busy}
                        onClick={reopenForFieldReport}
                      >
                        Demander un compte rendu terrain
                      </button>
                    )}
                  </div>
                )}
                <div className="detail-grid">
                  <div>
                    <span>Client</span>
                    <strong>
                      {data.clients.find((client) => client.id === editing.client_id)?.name ||
                        editing.client_display_name ||
                        "Client"}
                    </strong>
                  </div>
                  <div>
                    <span>Bien</span>
                    <strong>
                      {data.properties.find((property) => property.id === editing.property_id)?.name ||
                        "Client entier"}
                    </strong>
                  </div>
                  <div>
                    <span>Contenu de la mission</span>
                    <strong>
                      {selectedContractualServices.length
                        ? selectedContractualServices.map((service) => service.name).join(" · ")
                        : "À préciser"}
                    </strong>
                  </div>
                  <div>
                    <span>Intervenant</span>
                    <strong>
                      {assignments.length
                        ? assignments
                            .map((assignment) => {
                              const worker = data.workers.find(
                                (candidate) => candidate.id === assignment.worker_id,
                              );
                              return worker ? workerName(worker) : "Intervenant inconnu";
                            })
                            .join(" · ")
                        : "Non affectée"}
                    </strong>
                  </div>
                  <div>
                    <span>Compétences requises</span>
                    <strong>
                      {missionRequiredSkills.length
                        ? missionRequiredSkills.join(" · ")
                        : "Aucune compétence particulière"}
                    </strong>
                  </div>
                  <div>
                    <span>Qualification des affectations</span>
                    <strong>
                      {assignments.some((assignment) => assignment.skill_override)
                        ? "Dérogation administrateur tracée"
                        : assignments.length
                          ? "Compétences conformes"
                          : "À vérifier lors de l’affectation"}
                    </strong>
                    {assignments
                      .filter((assignment) => assignment.skill_override)
                      .map((assignment) => (
                        <small key={`skill-override-${assignment.id}`}>
                          Dérogation compétences : {assignment.skill_override_reason || "motif conservé dans l’audit"}
                        </small>
                      ))}
                  </div>
                  <div>
                    <span>Horaire</span>
                    <strong>
                      {editing.starts_at ? frDate(new Date(editing.starts_at).toISOString(), true) : "À définir"}
                    </strong>
                  </div>
                  <div>
                    <span>Temps réel déclaré</span>
                    <strong>
                      {missionReports.length
                        ? `${missionReports.reduce((sum, report) => sum + Number(report.actual_hours || 0), 0)} h cumulées`
                        : "Pas encore déclaré"}
                    </strong>
                  </div>
                  <div>
                    <span>Facturation</span>
                    <strong>{labels[editing.billing_status || ""] || editing.billing_status || "À préparer"}</strong>
                  </div>
                  <div>
                    <span>Montant prévu</span>
                    <strong>{money(Number(editing.amount_ex_vat || 0))} HT</strong>
                  </div>
                  <div>
                    <span>Traitement commercial</span>
                    <strong>{missionBillingSnapshot ? labels[missionBillingSnapshot.billing_treatment] : "Prestation standard"}</strong>
                    {missionBillingSnapshot?.supplier_total_cost_ex_vat != null && <small>Coût fournisseur figé : {money(missionBillingSnapshot.supplier_total_cost_ex_vat)} HT</small>}
                  </div>
                </div>
                <div className="mission-dossier-chapters" data-testid="mission-dossier-chapters">
                  <details
                    className="mission-dossier-chapter"
                    data-testid="mission-checklist-assignment"
                    open={
                      !missionChecklist?.available ||
                      (missionChecklist?.missing || []).length > 0 ||
                      (missionChecklist?.items || []).some(
                        (item) => item.proof_exception?.status === "pending_review",
                      )
                    }
                  >
                    <summary>
                      <span>
                        <strong>Checklist et obligations terrain</strong>
                        <small>
                          {missionChecklist?.available
                            ? `${(missionChecklist.missing || []).length} obligation(s) à terminer`
                            : "Checklist à attribuer"}
                        </small>
                      </span>
                      <span className="mission-dossier-chapter-state">
                        {missionChecklist?.available && !(missionChecklist.missing || []).length
                          ? "Complet"
                          : "À traiter"}
                      </span>
                    </summary>
                    <div className="mission-dossier-block mission-checklist-assignment-card">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Contrôle terrain</span>
                      <h4>Checklist de cette mission</h4>
                    </div>
                    {missionChecklist?.available && (
                      <Status value={missionChecklist.status || "active"} />
                    )}
                  </div>
                  {missionChecklistLoading ? (
                    <p>Chargement de la checklist…</p>
                  ) : missionChecklist?.available ? (
                    <>
                      <div className="detail-grid compact-grid">
                        <div>
                          <span>Modèle appliqué</span>
                          <strong>{missionChecklist.name || "Checklist mission"}</strong>
                          <small>Version {missionChecklist.version || 1}</small>
                        </div>
                        <div>
                          <span>Obligations</span>
                          <strong>
                            {(missionChecklist.items || []).filter((item) => item.is_required).length} obligatoire(s)
                          </strong>
                          <small>
                            {(missionChecklist.items || []).reduce(
                              (total, item) => total + Number(item.minimum_photo_count || 0),
                              0,
                            )} photo(s) minimale(s)
                          </small>
                        </div>
                        <div>
                          <span>À terminer</span>
                          <strong>{(missionChecklist.missing || []).length}</strong>
                          <small>La mission ne pourra pas être clôturée tant que les obligations manquent.</small>
                        </div>
                      </div>
                      {missionChecklist.instructions && (
                        <p className="section-intro">{missionChecklist.instructions}</p>
                      )}
                      {(missionChecklist.items || []).some((item) => item.proof_exception?.status === "pending_review") && (
                        <div className="mission-checklist-exception-review-list">
                          <h5>Justifications de preuve à contrôler</h5>
                          {(missionChecklist.items || [])
                            .filter((item) => item.proof_exception?.status === "pending_review")
                            .map((item) => (
                              <article key={item.proof_exception!.id}>
                                <div>
                                  <strong>{item.zone_display_label || item.zone_label || "Général"} · {item.label}</strong>
                                  <p>{item.proof_exception!.reason}</p>
                                </div>
                                {checklistExceptionReviewId === item.proof_exception!.id ? (
                                  <div className="checklist-exception-review-form">
                                    <label>Note de décision<textarea value={checklistExceptionReviewNote} onChange={(event) => setChecklistExceptionReviewNote(event.target.value)} placeholder="Obligatoire en cas de refus" /></label>
                                    <div className="row-actions">
                                      <button type="button" className="secondary compact" onClick={() => { setChecklistExceptionReviewId(""); setChecklistExceptionReviewNote(""); }}>Annuler</button>
                                      <button type="button" className="danger compact" disabled={busy || checklistExceptionReviewNote.trim().length < 5} onClick={() => void reviewChecklistProofException(item.proof_exception!.id, false)}>Refuser</button>
                                      <button type="button" className="success compact" disabled={busy} onClick={() => void reviewChecklistProofException(item.proof_exception!.id, true)}>Accepter</button>
                                    </div>
                                  </div>
                                ) : (
                                  <button type="button" className="secondary compact" onClick={() => { setChecklistExceptionReviewId(item.proof_exception!.id); setChecklistExceptionReviewNote(""); }}>Contrôler</button>
                                )}
                              </article>
                            ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Notice tone="warning">
                      Aucune checklist ne correspond encore à cette mission. Choisissez-en une ci-dessous avant l’intervention.
                    </Notice>
                  )}
                  {!editing.admin_validated_at &&
                    editing.status !== "cancelled" &&
                    editing.status !== "completed" && (
                      <div className="mission-checklist-selector">
                        <label>
                          Checklist à appliquer
                          <select
                            value={missionChecklistModelId}
                            onChange={(event) => setMissionChecklistModelId(event.target.value)}
                          >
                            <option value="">Automatique selon le client, le bien et la prestation</option>
                            {missionChecklistModels.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Pourquoi ce choix ?
                          <input
                            value={missionChecklistReason}
                            onChange={(event) => setMissionChecklistReason(event.target.value)}
                            placeholder="Ex. checklist fin de location prévue au contrat"
                          />
                        </label>
                        <button
                          type="button"
                          className="secondary"
                          disabled={busy || missionChecklistReason.trim().length < 3}
                          onClick={saveMissionChecklistModel}
                        >
                          Appliquer à cette mission
                        </button>
                      </div>
                    )}
                  <small>
                    Les modèles réutilisables et leurs règles automatiques se gèrent dans Réglages → Processus et automatisations → Checklists.
                  </small>
                    </div>
                  </details>
                {missionBillingSnapshot?.profitability_alert && <Notice tone="warning">Le prix prévu est inférieur au seuil de rentabilité figé de {money(missionBillingSnapshot.minimum_client_price_ex_vat || 0)} HT.</Notice>}
                {missionBillingSnapshot?.billing_treatment === "disbursement" && <Notice tone="warning">Débours : {missionBillingSnapshot.disbursement_principal_name} · mandat {missionBillingSnapshot.disbursement_mandate_reference}. Vérification comptable requise avant émission.</Notice>}
                <div className="mission-dossier-actions">
                  <MapsLink location={editing.location || ""} />
                  {hasDeploymentCapability(data, "mission_changes") && (
                    <button type="button" onClick={startMissionChange}>
                      Déclarer un changement / supplément
                    </button>
                  )}
                </div>

                {hasDeploymentCapability(data, "mission_costing_feedback") && (
                  <details
                    className="mission-dossier-chapter"
                    open={latestMissionCostingReview?.status === "needs_review"}
                  >
                    <summary>
                      <span>
                        <strong>Coûts, rentabilité et écarts</strong>
                        <small>Comparer le prévu au réel et décider des ajustements.</small>
                      </span>
                      <span className="mission-dossier-chapter-state">
                        {latestMissionCostingReview
                          ? labels[latestMissionCostingReview.status] || latestMissionCostingReview.status
                          : "À venir"}
                      </span>
                    </summary>
                    <div className="mission-dossier-block" data-testid="mission-costing-feedback">
                    <div className="section-heading">
                      <div>
                        <h4>Coûts prévus / coûts réels</h4>
                        <small>
                          Les données réelles proviennent uniquement des éléments approuvés. Aucun rendement n’est modifié automatiquement.
                        </small>
                      </div>
                      {editing.status === "completed" && editing.workflow_status === "approved" && (
                        <button type="button" className="secondary compact" onClick={startMissionCostingReview}>
                          Nouvelle comparaison
                        </button>
                      )}
                    </div>
                    {latestMissionCostingReview ? (
                      <>
                        <div className="detail-grid compact-grid">
                          <div>
                            <span>Coût prévu</span>
                            <strong>{money(latestMissionCostingReview.planned_total_cost_ex_vat)} HT</strong>
                          </div>
                          <div>
                            <span>Coût réel validé</span>
                            <strong>{money(latestMissionCostingReview.actual_total_cost_ex_vat)} HT</strong>
                          </div>
                          <div>
                            <span>Écart</span>
                            <strong className={latestMissionCostingReview.cost_variance_ex_vat > 0 ? "data-warning" : "data-ok"}>
                              {latestMissionCostingReview.cost_variance_ex_vat > 0 ? "+" : ""}
                              {money(latestMissionCostingReview.cost_variance_ex_vat)}
                              {latestMissionCostingReview.cost_variance_rate == null
                                ? ""
                                : ` · ${latestMissionCostingReview.cost_variance_rate > 0 ? "+" : ""}${Number(latestMissionCostingReview.cost_variance_rate).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`}
                            </strong>
                          </div>
                          <div>
                            <span>Heures prévues / réelles</span>
                            <strong>
                              {latestMissionCostingReview.planned_labor_hours == null ? "À contrôler" : `${latestMissionCostingReview.planned_labor_hours} h`}
                              {` / ${latestMissionCostingReview.actual_labor_hours} h`}
                            </strong>
                          </div>
                          <div>
                            <span>Rendement prévu</span>
                            <strong>
                              {latestMissionCostingReview.planned_yield_per_labor_hour == null
                                ? "Non défini"
                                : `${latestMissionCostingReview.planned_yield_per_labor_hour} ${latestMissionCostingReview.output_unit || "unité"}/h`}
                            </strong>
                          </div>
                          <div>
                            <span>Rendement observé</span>
                            <strong>
                              {latestMissionCostingReview.observed_yield_per_labor_hour == null
                                ? "Non calculable"
                                : `${latestMissionCostingReview.observed_yield_per_labor_hour} ${latestMissionCostingReview.output_unit || "unité"}/h`}
                            </strong>
                          </div>
                          <div>
                            <span>Décision</span>
                            <Status value={latestMissionCostingReview.status} />
                          </div>
                        </div>
                        <div className="record-list">
                          {latestMissionCostingLines.map((line) => (
                            <article className="mini-record" key={line.id}>
                              <strong>{line.label}</strong>
                              <small>
                                Prévu {money(line.planned_amount_ex_vat)} · Réel {money(line.actual_amount_ex_vat)} · Écart {line.variance_ex_vat > 0 ? "+" : ""}{money(line.variance_ex_vat)}
                              </small>
                              <small>
                                {line.completeness === "missing"
                                  ? "Source incomplète — contrôle requis"
                                  : line.completeness === "manual"
                                    ? "Montant confirmé manuellement"
                                    : "Source approuvée et figée"}
                              </small>
                            </article>
                          ))}
                        </div>
                        {!!latestMissionCostingReview.missing_inputs.length && (
                          <Notice tone="warning">
                            À contrôler : {latestMissionCostingReview.missing_inputs.map((item) => item.replaceAll("_", " ").replace(":", " · ")).join(" ; ")}.
                          </Notice>
                        )}
                        {(latestMissionCostingReview.status === "ready" || latestMissionCostingReview.status === "needs_review") && (
                          <div className="validated-report">
                            <label>
                              Note de décision {latestMissionCostingReview.status === "needs_review" ? "obligatoire" : "facultative"}
                              <textarea
                                value={costingDecisionNotes}
                                onChange={(event) => setCostingDecisionNotes(event.target.value)}
                                placeholder="Contrôles réalisés, écarts expliqués…"
                              />
                            </label>
                            <div className="detail-actions">
                              <button type="button" className="danger compact" disabled={busy} onClick={() => decideMissionCostingReview(latestMissionCostingReview, "rejected")}>
                                Refuser l’analyse
                              </button>
                              <button type="button" className="secondary compact" disabled={busy} onClick={() => decideMissionCostingReview(latestMissionCostingReview, "approved")}>
                                Valider sans modifier le modèle
                              </button>
                              {latestMissionCostingReview.status === "ready" && latestMissionCostingReview.observed_yield_per_labor_hour != null && (
                                <button type="button" className="success compact" disabled={busy} onClick={() => decideMissionCostingReview(latestMissionCostingReview, "approved", true)}>
                                  Valider et publier ce rendement
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {latestMissionCostingReview.decision_notes && (
                          <small>Décision : {latestMissionCostingReview.decision_notes}</small>
                        )}
                      </>
                    ) : (
                      <p className="empty">
                        {editing.status === "completed" && editing.workflow_status === "approved"
                          ? "La mission est validée : créez la première comparaison pour mesurer les écarts."
                          : "La comparaison sera disponible après validation administrative de la mission."}
                      </p>
                    )}
                    </div>
                  </details>
                )}

                {hasDeploymentCapability(data, "mission_quality_claims") && (
                  <details
                    className="mission-dossier-chapter"
                    open={missionQualityClaims.some(
                      (claim) => !["resolved", "rejected", "cancelled"].includes(claim.status),
                    )}
                  >
                    <summary>
                      <span>
                        <strong>Qualité, réclamations et retouches</strong>
                        <small>{missionQualityClaims.length} demande(s) reliée(s) à la mission.</small>
                      </span>
                      <span className="mission-dossier-chapter-state">
                        {missionQualityClaims.some(
                          (claim) => !["resolved", "rejected", "cancelled"].includes(claim.status),
                        )
                          ? "À contrôler"
                          : "Aucune action"}
                      </span>
                    </summary>
                    <div className="mission-dossier-block" data-testid="mission-quality-claims-admin">
                    <div className="section-heading">
                      <div>
                        <h4>Réclamations et retouches ({missionQualityClaims.length})</h4>
                        <small>Une déclaration client n’a aucun impact automatique avant votre qualification.</small>
                      </div>
                    </div>
                    {missionQualityClaims.map((claim) => {
                      const attachments = (data.missionQualityClaimAttachments || []).filter(
                          (attachment) => attachment.claim_id === claim.id,
                        ),
                        events = (data.missionQualityClaimEvents || [])
                          .filter((item) => item.claim_id === claim.id)
                          .sort((left, right) => left.created_at.localeCompare(right.created_at));
                      return (
                        <article className="mission-report-card quality-claim-card" key={claim.id}>
                          <div className="mission-report-card-head">
                            <div>
                              <strong>{
                                claim.request_kind === "touchup"
                                  ? "Demande de retouche"
                                  : claim.request_kind === "complaint_and_touchup"
                                    ? "Problème et retouche demandée"
                                    : "Réclamation client"
                              }</strong>
                              <small>{frDate(claim.created_at, true)} · {labels[claim.category] || claim.category}</small>
                            </div>
                            <Status value={claim.status} />
                          </div>
                          <p>{claim.description}</p>
                          <div className="detail-grid compact-grid">
                            <div><span>Qualification</span><strong>{claim.admin_qualification ? labels[claim.admin_qualification] || claim.admin_qualification : "Non contrôlée"}</strong></div>
                            <div><span>Photos</span><strong>{attachments.length}</strong></div>
                            <div><span>Retouche liée</span><strong>{claim.retouch_mission_id ? "Mission planifiée" : "Aucune"}</strong></div>
                            <div><span>Délai</span><strong>{claim.resolved_at ? `${Math.max(0,Math.round((new Date(claim.resolved_at).getTime()-new Date(claim.created_at).getTime())/3_600_000))} h` : "En cours"}</strong></div>
                          </div>
                          {!!attachments.length && <div className="attachment-list">{attachments.map((attachment)=><button type="button" className="secondary compact" key={attachment.id} onClick={()=>openQualityClaimAttachment(attachment)}>📷 {attachment.original_name}</button>)}</div>}
                          {!!events.length && <details><summary>Historique de traitement ({events.length})</summary><div className="record-list">{events.map((item)=><article className="mini-record" key={item.id}><strong>{labels[item.new_status] || item.new_status}</strong><small>{frDate(item.created_at,true)}{item.note?` · ${item.note}`:""}</small></article>)}</div></details>}
                          {claim.client_response && <div className="detail-note"><strong>Réponse transmise au client</strong><p>{claim.client_response}</p></div>}
                          {!['resolved','rejected','cancelled'].includes(claim.status) && <div className="detail-actions"><button type="button" className="success compact" onClick={()=>startQualityClaimReview(claim)}>Contrôler et décider</button></div>}
                        </article>
                      );
                    })}
                    {!missionQualityClaims.length && <p className="empty">Aucune réclamation client pour cette mission.</p>}
                    </div>
                  </details>
                )}

                <details
                  className="mission-dossier-chapter"
                  open={missionCancellationNotices.some((notice) => notice.status === "submitted")}
                >
                  <summary>
                    <span>
                      <strong>Affectations et annulations ST</strong>
                      <small>{missionCancellationNotices.length} signalement(s) conservé(s).</small>
                    </span>
                    <span className="mission-dossier-chapter-state">
                      {missionCancellationNotices.some((notice) => notice.status === "submitted")
                        ? "À contrôler"
                        : "Aucune action"}
                    </span>
                  </summary>
                  <div className="mission-dossier-block">
                    <h4>Annulations d’affectation ST à contrôler ({missionCancellationNotices.length})</h4>
                  {missionCancellationNotices.map((notice) => {
                    const worker = data.workers.find((item) => item.id === notice.worker_id);
                    return <article className="mission-report-card" key={notice.id}>
                      <div className="mission-report-card-head"><div><strong>{worker ? workerName(worker) : "Sous-traitant"}</strong><small>{frDate(notice.submitted_at,true)} · {notice.notice_hours.toLocaleString("fr-FR",{maximumFractionDigits:1})} h avant la mission</small></div><Status value={notice.status} /></div>
                      <p>{notice.reason}</p>
                      {notice.evidence_document_id&&<small>Justificatif : {data.documents.find((document)=>document.id===notice.evidence_document_id)?.original_name||"Pièce jointe enregistrée"}</small>}
                      <div className="detail-grid compact-grid"><div><span>Proposition contractuelle</span><strong>{money(notice.proposed_penalty_ex_vat)} HT</strong></div><div><span>Pénalité retenue</span><strong>{notice.approved_penalty_ex_vat==null?"En attente":`${money(notice.approved_penalty_ex_vat)} HT`}</strong></div></div>
                      {notice.status === "submitted" && <div className="detail-actions">
                        <button type="button" className="danger compact" disabled={busy} onClick={()=>reviewCancellationNotice(notice,false,false)}>Refuser l’annulation</button>
                        <button type="button" className="secondary compact" disabled={busy} onClick={()=>reviewCancellationNotice(notice,true,false)}>Accepter sans pénalité</button>
                        {notice.proposed_penalty_ex_vat>0&&<button type="button" className="success compact" disabled={busy} onClick={()=>reviewCancellationNotice(notice,true,true)}>Accepter avec pénalité</button>}
                      </div>}
                    </article>;
                  })}
                  {!missionCancellationNotices.length&&<p className="empty">Aucune annulation d’affectation signalée.</p>}
                  </div>
                </details>

                <details
                  className="mission-dossier-chapter"
                  open={missionChanges.some((change) => change.status === "proposed")}
                >
                  <summary>
                    <span>
                      <strong>Changements, suppléments et impacts</strong>
                      <small>{missionChanges.length} changement(s) versionné(s).</small>
                    </span>
                    <span className="mission-dossier-chapter-state">
                      {missionChanges.some((change) => change.status === "proposed")
                        ? "À décider"
                        : "Historique"}
                    </span>
                  </summary>
                  <div className="mission-dossier-block">
                    <h4>Changements et impacts ({missionChanges.length})</h4>
                  {missionChanges.map((change) => {
                    const adjustment = missionAdjustments.find(
                      (item) => item.change_event_id === change.id,
                    );
                    return (
                      <article className="mission-report-card" key={change.id}>
                        <div className="mission-report-card-head">
                          <div>
                            <strong>
                              {{
                                modification: "Modification",
                                supplement: "Supplément",
                                urgent: "Urgence",
                                reschedule: "Report / nouvel horaire",
                                cancellation: "Annulation",
                                access_issue: "Impossibilité d’accès",
                              }[change.event_type]}
                            </strong>
                            <small>{frDate(change.created_at, true)}</small>
                          </div>
                          <Status value={change.status} />
                        </div>
                        <p>{change.reason}</p>
                        {change.operational_notes && <small>{change.operational_notes}</small>}
                        <div className="detail-grid compact-grid">
                          <div>
                            <span>Impact client</span>
                            <strong>{money(change.client_amount_delta_ex_vat)} HT</strong>
                          </div>
                          <div>
                            <span>Impact fournisseur</span>
                            <strong>{money(change.supplier_amount_delta_ex_vat)} HT</strong>
                          </div>
                          <div>
                            <span>Accord client</span>
                            <strong>{labels[change.client_approval_status] || change.client_approval_status}</strong>
                          </div>
                          {adjustment && (
                            <div>
                              <span>Ajustement contrôlé</span>
                              <strong>{money(adjustment.amount_ex_vat)} HT · {labels[adjustment.adjustment_kind] || adjustment.adjustment_kind}</strong>
                            </div>
                          )}
                        </div>
                        {change.status === "proposed" && String(change.proposed_changes?.scope || "") !== "assignment" && (
                          <div className="detail-actions">
                            <button type="button" className="danger compact" disabled={busy} onClick={() => reviewMissionChange(change, false)}>
                              Refuser
                            </button>
                            <button type="button" className="success compact" disabled={busy} onClick={() => reviewMissionChange(change, true)}>
                              Valider après contrôle
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                  {!missionChanges.length && (
                    <p className="empty">Aucun changement versionné pour cette mission.</p>
                  )}
                  </div>
                </details>

                <details
                  className="mission-dossier-chapter"
                  open={
                    missionReports.some((report) => report.status === "pending") ||
                    (!missionReports.length &&
                      (editing.status === "completed" || editing.workflow_status === "completed"))
                  }
                >
                  <summary>
                    <span>
                      <strong>Comptes rendus et activité terrain</strong>
                      <small>{missionReports.length} compte(s) rendu(s) transmis.</small>
                    </span>
                    <span className="mission-dossier-chapter-state">
                      {missionReports.some((report) => report.status === "pending")
                        ? "À valider"
                        : missionReports.length
                          ? "Historique"
                          : "En attente"}
                    </span>
                  </summary>
                  <div className="mission-dossier-block">
                    <h4>Comptes rendus terrain ({missionReports.length})</h4>
                  {missionReports.map((report) => {
                    const worker = data.workers.find((item) => item.id === report.worker_id),
                      attachments = missionAttachments.filter(
                        (attachment) => attachment.report_id === report.id,
                      ),
                      activityEntries = missionActivityEntries.filter(
                        (entry) => entry.report_id === report.id,
                      ),
                      submissions = missionSubmissions.filter(
                        (submission) => submission.worker_id === report.worker_id,
                      );
                    return (
                      <article className="mission-report-card" key={report.id}>
                        <div className="mission-report-card-head">
                          <div>
                            <strong>{worker ? workerName(worker) : "Intervenant"}</strong>
                            <small>
                              {report.actual_hours} h · envoyé le {frDate(report.submitted_at, true)}
                            </small>
                          </div>
                          <Status value={report.status} />
                        </div>
                        <dl>
                          <div><dt>Résultat</dt><dd>{labels[report.result] || report.result}</dd></div>
                          <div><dt>Observations terrain</dt><dd>{report.observations || "Aucune observation."}</dd></div>
                          <div><dt>Message proposé au client</dt><dd>{report.client_note || "Aucun message."}</dd></div>
                        </dl>
                        {!!activityEntries.length && (
                          <div
                            className="activity-review-list"
                            data-testid="mission-file-activity-breakdown"
                          >
                            <strong>Temps réel par étape</strong>
                            {activityEntries.map((entry) => (
                              <div className="activity-review-row" key={entry.id}>
                                <span>{entry.activity_label}</span>
                                <strong>
                                  {(Number(entry.minutes) / 60).toLocaleString(
                                    "fr-FR",
                                    { maximumFractionDigits: 2 },
                                  )} h
                                </strong>
                                {entry.notes && <small>{entry.notes}</small>}
                              </div>
                            ))}
                            <small>
                              Données terrain historisées après validation du compte rendu.
                            </small>
                          </div>
                        )}
                        {submissions.length > 1 && (
                          <small className="data-warning">
                            {submissions.length} déclarations conservées dans l’historique pour contrôle des écarts.
                          </small>
                        )}
                        {!!attachments.length && (
                          <div className="attachment-list">
                            {attachments.map((attachment) => (
                              <button
                                type="button"
                                className="secondary compact"
                                key={attachment.id}
                                onClick={() => openMissionAttachment(attachment)}
                              >
                                {attachment.mime_type.startsWith("image/") ? "📷" : "📄"} {attachment.original_name}
                              </button>
                            ))}
                          </div>
                        )}
                        {report.status === "pending" && (
                          <div className="detail-actions mission-report-review-action">
                            <button
                              type="button"
                              className="success compact"
                              onClick={() => {
                                setOpen(false);
                                onReviewReport(report.mission_id);
                              }}
                            >
                              Lire et valider ce compte rendu
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                  {!missionReports.length && (
                    <p className="empty">
                      {editing.status === "completed" || editing.workflow_status === "completed"
                        ? "Mission terminée sans compte rendu enregistré : contrôle administratif nécessaire."
                        : "Le compte rendu apparaîtra ici dès sa transmission par l’intervenant."}
                    </p>
                  )}
                  </div>
                </details>

                {(editing.client_report || editing.next_actions || editing.admin_notes) && (
                  <details className="mission-dossier-chapter">
                    <summary>
                      <span>
                        <strong>Décision administrative</strong>
                        <small>Résumé client, suites et notes internes.</small>
                      </span>
                      <span className="mission-dossier-chapter-state">Validée</span>
                    </summary>
                    <div className="mission-dossier-block validated-report">
                      <h4>Validation administrative</h4>
                      {editing.client_report && <p><strong>Résumé client :</strong> {editing.client_report}</p>}
                      {editing.next_actions && <p><strong>Actions menées ou à prévoir :</strong> {editing.next_actions}</p>}
                      {editing.admin_notes && <p><strong>Notes internes :</strong> {editing.admin_notes}</p>}
                    </div>
                  </details>
                )}

                <details className="mission-dossier-chapter">
                  <summary>
                    <span>
                      <strong>Stock, kits et facturation liée</strong>
                      <small>
                        {missionStockUsages.length} consommation(s) · {missionInvoices.length} facture(s)
                      </small>
                    </span>
                    <span className="mission-dossier-chapter-state">Consulter</span>
                  </summary>
                  <div className="mission-dossier-columns">
                    <div className="mission-dossier-block">
                      <h4>Stock et kits ({missionStockUsages.length})</h4>
                    {missionStockUsages.map((usage) => {
                      const item = (data.inventoryItems || []).find(
                        (candidate) => candidate.id === usage.item_id,
                      );
                      return (
                        <article className="mini-record" key={usage.id}>
                          <strong>{item?.name || "Article de stock"} · {usage.quantity} {item?.unit || "unité"}</strong>
                          <small>
                            {usage.billable ? "Facturable" : "Non facturé"} · {labels[usage.billing_status] || usage.billing_status}
                          </small>
                          {usage.notes && <small>{usage.notes}</small>}
                        </article>
                      );
                    })}
                    {!missionStockUsages.length && <p className="empty">Aucune consommation déclarée.</p>}
                    </div>
                    <div className="mission-dossier-block">
                      <h4>Facturation liée</h4>
                    {missionInvoices.map((invoice) => (
                      <article className="mini-record" key={invoice.id}>
                        <strong>{invoice.number || "Brouillon"} · {money(invoice.total_inc_vat)} TTC</strong>
                        <small>{labels[invoice.status] || invoice.status}{invoice.issue_on ? ` · émise le ${frDate(invoice.issue_on)}` : " · non émise"}</small>
                      </article>
                    ))}
                    {!missionInvoices.length && (
                      <p className="empty">
                        {editing.billing_status === "to_invoice"
                          ? "Mission comptabilisée dans les éléments à facturer, sans facture encore émise."
                          : "Aucune facture reliée à cette mission."}
                      </p>
                    )}
                    </div>
                  </div>
                </details>
                </div>
              </section>
            )}
            {editing.id && changeOpen && (
              <div className="nested-dialog" role="dialog" aria-modal="true" aria-label="Déclarer un changement de mission">
                <section className="portal-detail mission-change-dialog">
                  <div className="portal-detail-head">
                    <div>
                      <span className="eyebrow">Action versionnée</span>
                      <h2>Changement, supplément ou incident</h2>
                    </div>
                    <button type="button" className="icon-close" onClick={() => setChangeOpen(false)}>×</button>
                  </div>
                  <Notice>
                    Cette saisie conserve l’état précédent. Les montants restent proposés jusqu’au contrôle administratif et, si demandé, à l’accord du client.
                  </Notice>
                  <form className="form-grid" onSubmit={proposeMissionChange}>
                    <label>
                      Nature
                      <select value={changeType} onChange={(event) => setChangeType(event.target.value as MissionChangeEvent["event_type"])}>
                        <option value="modification">Modification opérationnelle</option>
                        <option value="supplement">Prestation / supplément</option>
                        <option value="urgent">Urgence</option>
                        <option value="reschedule">Report / nouvel horaire</option>
                        <option value="cancellation">Annulation</option>
                        <option value="access_issue">Impossibilité d’accès</option>
                      </select>
                    </label>
                    <label className="wide">
                      Motif obligatoire
                      <textarea value={changeReason} onChange={(event) => setChangeReason(event.target.value)} required minLength={3} />
                    </label>
                    <label className="wide">
                      Consignes et contexte opérationnel
                      <textarea value={changeNotes} onChange={(event) => setChangeNotes(event.target.value)} />
                    </label>
                    {(changeType === "modification" || changeType === "reschedule") && (
                      <>
                        <label>Nouveau début<input type="datetime-local" value={changeStart} onChange={(event) => setChangeStart(event.target.value)} required /></label>
                        <label>Nouvelle fin<input type="datetime-local" value={changeEnd} onChange={(event) => setChangeEnd(event.target.value)} required /></label>
                      </>
                    )}
                    <label>Nouveau lieu, si concerné<input value={changeLocation} onChange={(event) => setChangeLocation(event.target.value)} /></label>
                    <label>Nouvelle durée totale (h)<input type="number" min="0" step="0.25" value={changeHours} onChange={(event) => setChangeHours(event.target.value)} /></label>
                    <label className="wide check">
                      <input type="checkbox" checked={changeNeedsClientApproval} onChange={(event) => setChangeNeedsClientApproval(event.target.checked)} />
                      Demander l’accord du client avant toute application
                    </label>
                    <label>
                      Ajustement financier contrôlé
                      <select value={changeAdjustmentKind} onChange={(event) => setChangeAdjustmentKind(event.target.value as typeof changeAdjustmentKind)}>
                        <option value="">Aucun</option>
                        <option value="client_supplement">Supplément client</option>
                        <option value="supplier_surcharge">Surcoût ST</option>
                        <option value="supplier_penalty">Pénalité ST</option>
                        <option value="supplier_deduction">Déduction ST</option>
                      </select>
                    </label>
                    {changeAdjustmentKind && changeAdjustmentKind !== "client_supplement" && (
                      <label>
                        Affectation ST concernée
                        <select value={changeAssignmentId} onChange={(event) => setChangeAssignmentId(event.target.value)} required>
                          <option value="">Choisir</option>
                          {assignments.filter((assignment) => data.workers.find((worker) => worker.id === assignment.worker_id)?.kind === "contractor").map((assignment) => (
                            <option key={assignment.id} value={assignment.id}>
                              {workerName(data.workers.find((worker) => worker.id === assignment.worker_id)!)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {changeAdjustmentKind && (
                      <>
                        <label>Montant HT de l’ajustement<input type="number" min="0.01" step="0.01" value={changeAdjustmentAmount} onChange={(event) => setChangeAdjustmentAmount(event.target.value)} required /></label>
                        <label>TVA (%)<input type="number" min="0" max="100" step="0.1" value={changeVat} onChange={(event) => setChangeVat(event.target.value)} /></label>
                      </>
                    )}
                    <label className="wide">
                      Justificatif déjà déposé (facultatif)
                      <select value={changeEvidenceId} onChange={(event)=>setChangeEvidenceId(event.target.value)}>
                        <option value="">Aucun justificatif</option>
                        {data.documents.map((document)=><option key={document.id} value={document.id}>{document.original_name}</option>)}
                      </select>
                    </label>
                    <div className="detail-actions wide">
                      <button type="button" className="secondary" onClick={() => setChangeOpen(false)}>Fermer sans enregistrer</button>
                      <button disabled={busy}>Enregistrer pour contrôle</button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {editing.id && qualityClaimReview && (
              <div className="nested-dialog" role="dialog" aria-modal="true" aria-label="Contrôler la réclamation client">
                <section className="portal-detail mission-change-dialog quality-claim-review-dialog">
                  <div className="portal-detail-head">
                    <div><span className="eyebrow">Décision humaine obligatoire</span><h2>Contrôler la demande client</h2></div>
                    <button type="button" className="icon-close" onClick={()=>setQualityClaimReview(null)}>×</button>
                  </div>
                  <Notice>Le signalement brut ne modifie aucun score. Seule la qualification enregistrée ci-dessous alimentera les indicateurs qualité.</Notice>
                  <div className="detail-note"><strong>Déclaration du client</strong><p>{qualityClaimReview.description}</p></div>
                  <form className="form-grid" onSubmit={reviewQualityClaim}>
                    <label>Décision<select value={qualityClaimDecision} onChange={(event)=>setQualityClaimDecision(event.target.value)}><option value="start_review">Passer en cours de contrôle</option><option value="request_retouch">Valider le besoin de retouche</option><option value="link_retouch">Relier une mission de retouche</option><option value="resolve">Clôturer comme traitée</option><option value="reject">Classer non fondée</option></select></label>
                    <label>Qualification administrative<select value={qualityClaimDecision==="reject"?"unfounded":qualityClaimQualification} disabled={qualityClaimDecision==="reject"} onChange={(event)=>setQualityClaimQualification(event.target.value)}><option value="">À déterminer</option><option value="founded">Fondée</option><option value="partly_founded">Partiellement fondée</option><option value="unfounded">Non fondée</option><option value="commercial_gesture">Geste commercial sans défaut confirmé</option></select></label>
                    {qualityClaimDecision==="link_retouch"&&<label className="wide">Mission de retouche<select value={qualityClaimRetouchMissionId} onChange={(event)=>setQualityClaimRetouchMissionId(event.target.value)} required><option value="">Choisir une mission du même client</option>{availableRetouchMissions.map((mission)=><option key={mission.id} value={mission.id}>{frDate(mission.starts_at,true)} · {mission.location||"Lieu à préciser"}</option>)}</select></label>}
                    <label className="wide">Notes internes de contrôle<textarea value={qualityClaimAdminNotes} onChange={(event)=>setQualityClaimAdminNotes(event.target.value)} placeholder="Constats, personnes contactées, éléments vérifiés…" /></label>
                    <label className="wide">Réponse visible par le client<textarea value={qualityClaimClientResponse} onChange={(event)=>setQualityClaimClientResponse(event.target.value)} placeholder="Expliquez simplement la décision et la prochaine étape." /></label>
                    <div className="detail-actions wide"><button type="button" className="secondary" onClick={()=>setQualityClaimReview(null)}>Annuler</button><button disabled={busy}>{busy?"Enregistrement…":"Enregistrer la décision"}</button></div>
                  </form>
                </section>
              </div>
            )}
            {editing.id && costingOpen && (
              <div className="nested-dialog" role="dialog" aria-modal="true" aria-label="Comparer les coûts prévus et réels">
                <section className="portal-detail mission-change-dialog">
                  <div className="portal-detail-head">
                    <div>
                      <span className="eyebrow">Mission validée</span>
                      <h2>Comparer le prévu au réel</h2>
                    </div>
                    <button type="button" className="icon-close" onClick={() => setCostingOpen(false)}>×</button>
                  </div>
                  <Notice>
                    Les heures, coûts de personnel, consommations de stock, IK et frais sont relus depuis leurs sources approuvées. Les montants ci-dessous servent uniquement aux éléments qui ne sont pas encore structurés.
                  </Notice>
                  <form className="form-grid" onSubmit={createMissionCostingReview}>
                    <label className="wide">
                      Chiffrage appliqué au devis de référence
                      <select
                        value={costingRunId}
                        onChange={(event) => {
                          const run = availableCostingRuns.find((item) => item.id === event.target.value);
                          setCostingRunId(event.target.value);
                          if (run) {
                            setCostingOutputQuantity(run.output_quantity == null ? "" : String(run.output_quantity));
                            setCostingOutputUnit(run.output_unit || "m2");
                          }
                        }}
                      >
                        <option value="">Aucun — calculer le réel et signaler le prévu manquant</option>
                        {availableCostingRuns.map((run) => {
                          const quote = data.quotes.find((item) => item.id === run.quote_id),
                            line = (data.quoteLines || []).find((item) => item.id === run.quote_line_id);
                          return (
                            <option key={run.id} value={run.id}>
                              {quote?.number || "Devis"} · {line?.description || "Ligne chiffrée"} · {money(run.total_cost_ex_vat)} prévus
                            </option>
                          );
                        })}
                      </select>
                    </label>
                    {!availableCostingRuns.length && (
                      <Notice tone="warning">
                        Aucun chiffrage appliqué n’est disponible pour ce client. La comparaison restera « à contrôler » jusqu’à ce lien.
                      </Notice>
                    )}
                    <label>
                      Quantité réellement traitée
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={costingOutputQuantity}
                        onChange={(event) => setCostingOutputQuantity(event.target.value)}
                        placeholder={selectedMissionCostingRun?.output_quantity == null ? "Facultatif" : String(selectedMissionCostingRun.output_quantity)}
                      />
                    </label>
                    <label>
                      Unité du rendement
                      <select value={costingOutputUnit} onChange={(event) => setCostingOutputUnit(event.target.value)} disabled={costingOutputQuantity === ""}>
                        <option value="m2">m²</option>
                        <option value="m3">m³</option>
                        <option value="metre_lineaire">mètre linéaire</option>
                        <option value="unite">unité</option>
                        <option value="forfait">forfait</option>
                      </select>
                    </label>
                    <label>
                      Matériel / location réellement consommé (€ HT)
                      <input type="number" min="0" step="0.01" value={costingEquipmentCost} onChange={(event) => setCostingEquipmentCost(event.target.value)} placeholder="Vide = à contrôler si prévu" />
                    </label>
                    <label>
                      Autres coûts non déjà saisis (€ HT)
                      <input type="number" min="0" step="0.01" value={costingOtherCost} onChange={(event) => setCostingOtherCost(event.target.value)} placeholder="0 si aucun" />
                    </label>
                    <label className="wide">
                      Note de contrôle
                      <textarea value={costingNotes} onChange={(event) => setCostingNotes(event.target.value)} placeholder="Contexte, location, aléa ou justification utile…" />
                    </label>
                    <div className="detail-actions wide">
                      <button type="button" className="secondary" onClick={() => setCostingOpen(false)}>Fermer sans calculer</button>
                      <button disabled={busy}>{busy ? "Calcul en cours…" : "Créer la comparaison versionnée"}</button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {editing.id && finalizationOpen && (
              <div className="portal-overlay nested-overlay" role="dialog" aria-modal="true">
                <form className="portal-detail create-modal" onSubmit={finalizeAdminMission}>
                  <div className="portal-detail-head">
                    <div>
                      <span className="eyebrow">Décision souveraine et tracée</span>
                      <h2>Clôturer la mission</h2>
                      <p>
                        Cette étape rend les coûts acquis et place la mission dans
                        « À facturer ». Elle ne transmet encore aucune facture à Henrri.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="icon-close"
                      onClick={() => setFinalizationOpen(false)}
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                  <div className="choice-grid two-columns" role="radiogroup" aria-label="Traitement du compte rendu">
                    <label className={`choice-card ${finalizationReportMode === "admin_report" ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="admin-report-mode"
                        checked={finalizationReportMode === "admin_report"}
                        onChange={() => setFinalizationReportMode("admin_report")}
                      />
                      <strong>Je rédige le compte rendu</strong>
                      <small>L’administrateur renseigne tout ou partie des informations utiles à la place de l’intervenant.</small>
                    </label>
                    <label className={`choice-card ${finalizationReportMode === "without_report" ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="admin-report-mode"
                        checked={finalizationReportMode === "without_report"}
                        onChange={() => setFinalizationReportMode("without_report")}
                      />
                      <strong>Je clôture sans compte rendu</strong>
                      <small>La dérogation est assumée par l’administrateur et conservée dans l’audit.</small>
                    </label>
                  </div>
                  {finalizationReportMode === "admin_report" ? (
                    <div className="form-grid">
                      <label>
                        Temps réel connu (h)
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={finalizationActualHours}
                          onChange={(event) => setFinalizationActualHours(event.target.value)}
                          placeholder="Facultatif"
                        />
                      </label>
                      <label className="wide">
                        Résumé visible par le client
                        <textarea
                          value={finalizationClientReport}
                          onChange={(event) => setFinalizationClientReport(event.target.value)}
                          placeholder="Travaux réalisés, résultat et informations utiles…"
                        />
                      </label>
                      <label className="wide">
                        Actions restantes
                        <textarea
                          value={finalizationNextActions}
                          onChange={(event) => setFinalizationNextActions(event.target.value)}
                          placeholder="Vide si aucune suite n’est nécessaire"
                        />
                      </label>
                      <label className="wide">
                        Note administrative interne
                        <textarea
                          value={finalizationAdminNotes}
                          onChange={(event) => setFinalizationAdminNotes(event.target.value)}
                          placeholder="Contrôle effectué, contexte ou justification interne…"
                        />
                      </label>
                      <label className="wide">
                        Motif de dérogation si une obligation terrain manque
                        <textarea
                          value={finalizationOverrideReason}
                          onChange={(event) => setFinalizationOverrideReason(event.target.value)}
                          placeholder="À renseigner seulement si une checklist ou une preuve obligatoire est incomplète"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="modal-field">
                      Motif de clôture sans compte rendu
                      <textarea
                        required
                        minLength={5}
                        value={finalizationOverrideReason}
                        onChange={(event) => setFinalizationOverrideReason(event.target.value)}
                        placeholder="Ex. mission vérifiée directement par l’administrateur"
                      />
                    </label>
                  )}
                  <section className="mission-billing-choice">
                    <h3>Comment cette mission sera-t-elle facturée ?</h3>
                    <div className="choice-grid two-columns" role="radiogroup" aria-label="Mode de regroupement de la facture">
                      <label className={`choice-card ${finalizationInvoiceStrategy === "grouped" ? "selected" : ""}`}>
                        <input
                          type="radio"
                          name="invoice-strategy"
                          checked={finalizationInvoiceStrategy === "grouped"}
                          onChange={() => setFinalizationInvoiceStrategy("grouped")}
                        />
                        <strong>Facture globale du client</strong>
                        <small>La mission rejoindra le brouillon regroupé de la période, par exemple pour Maeva.</small>
                      </label>
                      <label className={`choice-card ${finalizationInvoiceStrategy === "individual" ? "selected" : ""}`}>
                        <input
                          type="radio"
                          name="invoice-strategy"
                          checked={finalizationInvoiceStrategy === "individual"}
                          onChange={() => setFinalizationInvoiceStrategy("individual")}
                        />
                        <strong>Facture indépendante</strong>
                        <small>Cette mission créera son propre brouillon lors de la préparation de la facture.</small>
                      </label>
                    </div>
                  </section>
                  <Notice tone="warning">
                    Si des étapes ou photos obligatoires manquent, la clôture ne sera
                    acceptée qu’avec un motif de dérogation administrative suffisamment précis.
                  </Notice>
                  {message && <Notice tone="error">{message}</Notice>}
                  <div className="detail-actions">
                    <button type="button" className="secondary" onClick={() => setFinalizationOpen(false)}>
                      Annuler
                    </button>
                    <button type="submit" className="success" disabled={busy}>
                      {busy ? "Clôture en cours…" : "Clôturer et préparer la facturation"}
                    </button>
                  </div>
                </form>
              </div>
            )}
            {message && (
              <Notice
                tone={
                  message.includes("créée") ||
                  message.includes("enregistrée") ||
                  message.includes("proposée") ||
                  message.includes("prioritaire") ||
                  message.includes("affecté") ||
                  message.includes("annulée") ||
                  message.includes("clôturée") ||
                  message.includes("Checklist affectée") ||
                  message.includes("Règle automatique") ||
                  message.includes("Décision qualité")
                    ? "success"
                    : "error"
                }
              >
                {message}
              </Notice>
            )}
          </section>
        </div>
      )}
    </>
  );
}

function AdminConciergeRequests({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const [propertyRequest, setPropertyRequest] =
      useState<ClientPropertyRequest | null>(null),
    [serviceRequest, setServiceRequest] =
      useState<PropertyServiceQuoteRequest | null>(null),
    [adminNote, setAdminNote] = useState(""),
    [mode, setMode] = useState("fixed"),
    [price, setPrice] = useState(""),
    [vat, setVat] = useState(String(data.organization?.default_vat_rate ?? 20)),
    [duration, setDuration] = useState(""),
    [validUntil, setValidUntil] = useState(() => {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      return parisDay(date);
    }),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const propertyRequests = (data.propertyRequests || []).filter(
      (request) => request.status === "pending",
    ),
    serviceRequests = (data.propertyServiceQuoteRequests || []).filter(
      (request) => ["pending", "changes_requested"].includes(request.status),
    );

  function openServiceRequest(request: PropertyServiceQuoteRequest) {
    const terms = (data.propertyServiceQuoteTerms || []).find(
        (item) => item.request_id === request.id,
      ),
      quote = data.quotes.find((item) => item.id === request.quote_id);
    setServiceRequest(request);
    setPropertyRequest(null);
    setAdminNote(terms?.admin_notes || request.admin_response || "");
    setMode(terms?.billing_mode || "fixed");
    setPrice(terms ? String(terms.unit_price_ex_vat) : "");
    setVat(String(terms?.vat_rate ?? data.organization?.default_vat_rate ?? 20));
    setDuration(
      terms?.default_duration_hours == null
        ? ""
        : String(terms.default_duration_hours),
    );
    setValidUntil(
      quote?.valid_until || (() => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return parisDay(date);
      })(),
    );
  }

  async function reviewProperty(approve: boolean) {
    if (!propertyRequest) return;
    setBusy(true);
    const { error } = await supabase!.rpc("review_client_property_request", {
      p_request_id: propertyRequest.id,
      p_approve: approve,
      p_admin_notes: adminNote.trim() || null,
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : approve
          ? propertyRequest.request_kind === "archive"
            ? "Retrait validé : le bien et ses prestations sont archivés, sans effacer l’historique."
            : propertyRequest.request_kind === "update"
              ? "Modification validée et fiche du bien mise à jour."
              : "Bien validé et ajouté au portefeuille du client."
          : "Demande refusée et client informé.",
    );
    if (!error) {
      setPropertyRequest(null);
      setAdminNote("");
      reload();
    }
  }

  async function reviewService(approve: boolean) {
    if (!serviceRequest) return;
    if (approve && price === "") {
      setMessage("Renseignez le prix HT du devis.");
      return;
    }
    setBusy(true);
    const { error } = await supabase!.rpc(
      "review_property_service_quote_request",
      {
        p_request_id: serviceRequest.id,
        p_approve: approve,
        p_billing_mode: approve ? mode : null,
        p_unit_price_ex_vat: approve ? +price : null,
        p_vat_rate: approve ? +vat : null,
        p_default_duration_hours:
          approve && duration !== "" ? +duration : null,
        p_admin_response: adminNote.trim() || null,
        p_valid_until: approve ? validUntil : null,
      },
    );
    setBusy(false);
    setMessage(
      error
        ? error.message
        : approve
          ? serviceRequest.status === "changes_requested"
            ? "Devis révisé et renvoyé au client. La durée reste strictement interne."
            : "Devis créé et envoyé au client. La durée reste strictement interne."
          : "Demande refusée et client informé.",
    );
    if (!error) {
      setServiceRequest(null);
      setAdminNote("");
      setPrice("");
      setDuration("");
      reload();
    }
  }

  if (!propertyRequests.length && !serviceRequests.length && !message)
    return null;
  return (
    <section className="panel">
      <h2>Biens et prestations à valider</h2>
      <p className="section-intro">
        Les demandes client ne modifient jamais directement le portefeuille ni
        les tarifs. Vous contrôlez chaque création avant sa prise d’effet.
      </p>
      {message && <Notice tone="info">{message}</Notice>}
      <h3>Demandes concernant les biens ({propertyRequests.length})</h3>
      {propertyRequests.map((request) => (
        <div className="workflow-row" key={request.id}>
          <div>
            <strong>
              {data.clients.find((client) => client.id === request.client_id)
              ?.name || "Client"} · {request.name}
            </strong>
            <small>
              {request.request_kind === "archive"
                ? "Retrait demandé"
                : request.request_kind === "update"
                  ? "Modification proposée"
                  : "Nouveau bien proposé"}
              {" · "}{request.address}
            </small>
          </div>
          <button
            className="secondary compact"
            onClick={() => {
              setPropertyRequest(request);
              setServiceRequest(null);
              setAdminNote("");
            }}
          >
            Examiner la demande
          </button>
        </div>
      ))}
      {!propertyRequests.length && (
        <p className="empty">Aucune demande concernant un bien.</p>
      )}
      <h3>Devis prestation à chiffrer ou réviser ({serviceRequests.length})</h3>
      {serviceRequests.map((request) => (
        <div className="workflow-row" key={request.id}>
          <div>
            <strong>
              {data.clients.find((client) => client.id === request.client_id)
                ?.name || "Client"} · {request.service_name}
            </strong>
            <small>
              {data.properties.find(
                (property) => property.id === request.property_id,
              )?.name || "Bien"} · {request.requested_description}
            </small>
            {request.status === "changes_requested" && (
              <small>
                <strong>Retour client :</strong> {request.client_response_reason || "Motif non renseigné"}
              </small>
            )}
          </div>
          <button
            className="secondary compact"
            onClick={() => openServiceRequest(request)}
          >
            {request.status === "changes_requested" ? "Réviser le devis" : "Chiffrer le devis"}
          </button>
        </div>
      ))}
      {!serviceRequests.length && (
        <p className="empty">Aucune demande de devis en attente.</p>
      )}

      {propertyRequest && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">
                  {propertyRequest.request_kind === "archive"
                    ? "Demande de retrait"
                    : propertyRequest.request_kind === "update"
                      ? "Proposition de modification"
                      : "Proposition de nouveau bien"}
                </span>
                <h2>{propertyRequest.name}</h2>
              </div>
              <button className="icon-close" onClick={() => setPropertyRequest(null)}>×</button>
            </div>
            <div className="detail-grid">
              <div><span>Client</span><strong>{data.clients.find((c) => c.id === propertyRequest.client_id)?.name}</strong></div>
              <div><span>Contact</span><strong>{propertyRequest.contact_name || "Non renseigné"}</strong></div>
              <div className="wide"><span>Adresse</span><strong>{propertyRequest.address}</strong></div>
            </div>
            <div className="detail-note"><strong>Consignes proposées</strong><p>{propertyRequest.access_notes || "Aucune."}</p></div>
            {propertyRequest.requested_reason && (
              <div className="detail-note"><strong>Motif ou précision du client</strong><p>{propertyRequest.requested_reason}</p></div>
            )}
            {propertyRequest.request_kind === "archive" && (
              <Notice>
                Le retrait archive le bien et ses prestations sans supprimer les missions, devis ou factures passés. Les missions futures et récurrences actives doivent être traitées avant validation.
              </Notice>
            )}
            <label className="modal-field">Réponse / note administrative<textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} /></label>
            <div className="detail-actions">
              <button className="danger" disabled={busy} onClick={() => reviewProperty(false)}>Refuser</button>
              <button className="success" disabled={busy} onClick={() => reviewProperty(true)}>
                {propertyRequest.request_kind === "archive"
                  ? "Valider le retrait"
                  : propertyRequest.request_kind === "update"
                    ? "Valider la modification"
                    : "Valider et créer le bien"}
              </button>
            </div>
          </section>
        </div>
      )}

      {serviceRequest && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Demande de devis</span>
                <h2>{serviceRequest.service_name}</h2>
              </div>
              <button className="icon-close" onClick={() => setServiceRequest(null)}>×</button>
            </div>
            <div className="detail-grid">
              <div><span>Client</span><strong>{data.clients.find((c) => c.id === serviceRequest.client_id)?.name}</strong></div>
              <div><span>Bien</span><strong>{data.properties.find((p) => p.id === serviceRequest.property_id)?.name}</strong></div>
            </div>
            <div className="detail-note"><strong>Besoin exprimé</strong><p>{serviceRequest.requested_description}</p></div>
            {serviceRequest.client_notes && <div className="detail-note"><strong>Précisions client</strong><p>{serviceRequest.client_notes}</p></div>}
            {serviceRequest.status === "changes_requested" && (
              <div className="detail-note quote-revision-note">
                <strong>Pourquoi le client demande une nouvelle proposition</strong>
                <p>{serviceRequest.client_response_reason || "Motif non renseigné"}</p>
              </div>
            )}
            <div className="form-grid">
              <label>Mode de facturation<select value={mode} onChange={(event) => setMode(event.target.value)}><option value="fixed">Forfait</option><option value="hourly">Horaire</option><option value="per_unit">À l’unité</option><option value="monthly">Mensuel</option></select></label>
              <label>Prix HT (€)<input type="number" min="0" step="0.01" value={price} onChange={(event) => setPrice(event.target.value)} required /></label>
              <label>TVA (%)<input type="number" min="0" max="100" step="0.1" value={vat} onChange={(event) => setVat(event.target.value)} /></label>
              <label>Validité du devis<input type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} /></label>
              <label>Durée interne prévue (h)<input type="number" min="0.25" step="0.25" value={duration} onChange={(event) => setDuration(event.target.value)} /><small>Jamais transmise au client.</small></label>
              <label className="wide">Réponse visible par le client<textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} /></label>
            </div>
            <div className="detail-actions">
              <button className="danger" disabled={busy} onClick={() => reviewService(false)}>Refuser</button>
              <button className="success" disabled={busy} onClick={() => reviewService(true)}>
                {serviceRequest.status === "changes_requested" ? "Enregistrer et renvoyer le devis" : "Créer et envoyer le devis"}
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function AdminMissionChecklistSettings({
  data,
  reload,
}: {
  data: DataState;
  reload: () => void;
}) {
  type ChecklistModel = {
    id: string;
    name: string;
    description: string | null;
    active: boolean;
    updated_at: string;
  };
  type ChecklistAssignment = {
    id: string;
    model_id: string;
    catalog_item_id: string | null;
    client_id: string | null;
    property_id: string | null;
    priority: number;
    active: boolean;
    change_reason: string;
    updated_at: string;
  };
  type ChecklistVersion = {
    id: string;
    model_id: string;
    version: number;
    name: string;
    instructions: string | null;
    status: "draft" | "published" | "archived";
    is_current_version: boolean;
  };
  type ChecklistStep = {
    item_key: string;
    label: string;
    instructions: string;
    is_required: boolean;
    minimum_photo_count: number;
    zone_key: string;
    zone_label: string;
    zone_repeatable: boolean;
  };
  type ChecklistPilotage = {
    missions_with_checklist: number;
    required_items: number;
    completed_required_items: number;
    completion_rate: number;
    photo_evidence_count: number;
    repeatable_room_instances: number;
    proof_exceptions: {
      total: number;
      pending_review: number;
      accepted: number;
      rejected: number;
      resolved_by_evidence: number;
    };
    zones: Array<{
      zone_key: string;
      zone_label: string;
      required_items: number;
      completed_items: number;
      completion_rate: number;
      proof_exceptions: number;
    }>;
  };
  const catalogItems = (data.serviceCatalogItems || [])
      .filter((item) => item.active)
      .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    activeClients = data.clients.filter((client) => client.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    activeProperties = data.properties.filter((property) => property.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    [models, setModels] = useState<ChecklistModel[]>([]),
    [versions, setVersions] = useState<ChecklistVersion[]>([]),
    [assignments, setAssignments] = useState<ChecklistAssignment[]>([]),
    [pilotage, setPilotage] = useState<ChecklistPilotage | null>(null),
    [loading, setLoading] = useState(true),
    [moduleAvailable, setModuleAvailable] = useState(true),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [modelEditor, setModelEditor] = useState<{
      model_id: string | null;
      name: string;
      description: string;
      instructions: string;
      change_reason: string;
      latest_version: number | null;
      latest_status: string | null;
      items: ChecklistStep[];
    } | null>(null),
    [assignmentEditor, setAssignmentEditor] = useState<{
      id: string | null;
      model_id: string;
      catalog_item_id: string;
      client_id: string;
      property_id: string;
      active: boolean;
      change_reason: string;
    } | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const [modelsResult, versionsResult, assignmentsResult, pilotageResult] = await Promise.all([
      supabase!.from("mission_checklist_models").select("id,name,description,active,updated_at").order("name"),
      supabase!.from("service_mission_checklists").select("id,model_id,version,name,instructions,status,is_current_version").order("version", { ascending: false }),
      supabase!.from("mission_checklist_assignments").select("id,model_id,catalog_item_id,client_id,property_id,priority,active,change_reason,updated_at").order("updated_at", { ascending: false }),
      supabase!.rpc("get_mission_checklist_pilotage", { p_from: `${new Date().getFullYear()}-01-01`, p_to: parisDay(new Date()) }),
    ]);
    setLoading(false);
    const firstError = modelsResult.error || versionsResult.error || assignmentsResult.error;
    if (firstError) {
      setModuleAvailable(false);
      setModels([]);
      setVersions([]);
      setAssignments([]);
      setMessage(
        /mission_checklist_models|mission_checklist_assignments|schema cache|does not exist|could not find/i.test(firstError.message)
          ? "La bibliothèque de modèles est prête dans le code, mais sa migration contrôlée n’est pas encore activée sur cette base."
          : firstError.message,
      );
      return;
    }
    setModuleAvailable(true);
    setModels((modelsResult.data || []) as ChecklistModel[]);
    setVersions(((versionsResult.data || []) as ChecklistVersion[]).filter((version) => version.model_id));
    setAssignments((assignmentsResult.data || []) as ChecklistAssignment[]);
    setPilotage(pilotageResult.error ? null : pilotageResult.data as ChecklistPilotage);
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const latestVersion = (modelId: string) =>
    versions.filter((version) => version.model_id === modelId)
      .sort((a, b) => b.version - a.version)[0] || null;
  const openModel = async (model?: ChecklistModel) => {
    setMessage("");
    if (!model) {
      setModelEditor({
        model_id: null,
        name: "",
        description: "",
        instructions: "Validez chaque étape obligatoire et ajoutez les photos demandées avant de terminer la mission.",
        change_reason: "Création du modèle",
        latest_version: null,
        latest_status: null,
        items: [{ item_key: "step-1", label: "", instructions: "", is_required: true, minimum_photo_count: 0, zone_key: "general", zone_label: "Général", zone_repeatable: false }],
      });
      return;
    }
    setBusy(true);
    const current = latestVersion(model.id),
      itemsResult = current
        ? await supabase!.from("service_mission_checklist_items").select("item_key,label,instructions,is_required,minimum_photo_count,position,zone_key,zone_label,zone_repeatable").eq("checklist_id", current.id).order("position")
        : { data: [], error: null };
    setBusy(false);
    if (itemsResult.error) {
      setMessage(itemsResult.error.message);
      return;
    }
    setModelEditor({
      model_id: model.id,
      name: model.name,
      description: model.description || "",
      instructions: current?.instructions || "Validez chaque étape obligatoire et ajoutez les photos demandées avant de terminer la mission.",
      change_reason: "",
      latest_version: current?.version || null,
      latest_status: current?.status || null,
      items: (itemsResult.data || []).length
        ? (itemsResult.data || []).map((item) => ({
            item_key: item.item_key,
            label: item.label,
            instructions: item.instructions || "",
            is_required: item.is_required,
            minimum_photo_count: Number(item.minimum_photo_count || 0),
            zone_key: item.zone_key || "general",
            zone_label: item.zone_label || "Général",
            zone_repeatable: Boolean(item.zone_repeatable),
          }))
        : [{ item_key: "step-1", label: "", instructions: "", is_required: true, minimum_photo_count: 0, zone_key: "general", zone_label: "Général", zone_repeatable: false }],
    });
  };
  const saveModel = async (publish: boolean) => {
    if (!modelEditor) return;
    if (
      modelEditor.name.trim().length < 3 ||
      modelEditor.change_reason.trim().length < 3 ||
      !modelEditor.items.length ||
      modelEditor.items.some((item) => item.label.trim().length < 2 || item.minimum_photo_count < 0 || item.minimum_photo_count > 20)
    ) {
      setMessage("Renseignez le nom, le motif de version et chaque étape (0 à 20 photos obligatoires).");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("save_mission_checklist_model", {
      p_model_id: modelEditor.model_id,
      p_name: modelEditor.name.trim(),
      p_description: modelEditor.description.trim() || null,
      p_items: modelEditor.items.map((item, index) => ({
        item_key: item.item_key || `step-${index + 1}`,
        label: item.label.trim(),
        instructions: item.instructions.trim() || null,
        is_required: item.is_required || item.minimum_photo_count > 0,
        minimum_photo_count: item.minimum_photo_count,
        zone_key: item.zone_key || null,
        zone_label: item.zone_label.trim() || "Général",
        zone_repeatable: item.zone_repeatable,
      })),
      p_instructions: modelEditor.instructions.trim() || null,
      p_change_reason: modelEditor.change_reason.trim(),
      p_publish: publish,
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : publish
          ? "Modèle publié. Les missions futures utiliseront automatiquement la règle la plus précise."
          : "Brouillon versionné enregistré. La version publiée reste active.",
    );
    if (!result.error) {
      setModelEditor(null);
      await loadSettings();
      reload();
    }
  };
  const openAssignment = (assignment?: ChecklistAssignment) => {
    setMessage("");
    setAssignmentEditor(
      assignment
        ? {
            id: assignment.id,
            model_id: assignment.model_id,
            catalog_item_id: assignment.catalog_item_id || "",
            client_id: assignment.client_id || "",
            property_id: assignment.property_id || "",
            active: assignment.active,
            change_reason: "",
          }
        : {
            id: null,
            model_id: models[0]?.id || "",
            catalog_item_id: "",
            client_id: "",
            property_id: "",
            active: true,
            change_reason: "Création de la règle",
          },
    );
  };
  const saveAssignment = async () => {
    if (!assignmentEditor) return;
    if (
      !assignmentEditor.model_id ||
      !assignmentEditor.catalog_item_id && !assignmentEditor.client_id && !assignmentEditor.property_id ||
      assignmentEditor.change_reason.trim().length < 3
    ) {
      setMessage("Choisissez un modèle, au moins un critère d’utilisation et indiquez le motif de la règle.");
      return;
    }
    const selectedProperty = activeProperties.find((property) => property.id === assignmentEditor.property_id),
      clientId = selectedProperty?.client_id || assignmentEditor.client_id || null;
    setBusy(true);
    const result = await supabase!.rpc("save_mission_checklist_assignment", {
      p_assignment_id: assignmentEditor.id,
      p_model_id: assignmentEditor.model_id,
      p_catalog_item_id: assignmentEditor.catalog_item_id || null,
      p_client_id: clientId,
      p_property_id: assignmentEditor.property_id || null,
      p_priority: 0,
      p_active: assignmentEditor.active,
      p_effective_from: null,
      p_effective_to: null,
      p_change_reason: assignmentEditor.change_reason.trim(),
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Règle enregistrée. Elle s’appliquera automatiquement aux missions futures correspondantes.");
    if (!result.error) {
      setAssignmentEditor(null);
      await loadSettings();
    }
  };
  const labelFor = (assignment: ChecklistAssignment) => ({
    model: models.find((model) => model.id === assignment.model_id)?.name || "Modèle archivé",
    catalog: catalogItems.find((item) => item.id === assignment.catalog_item_id)?.name || null,
    client: activeClients.find((client) => client.id === assignment.client_id)?.name || null,
    property: activeProperties.find((property) => property.id === assignment.property_id)?.name || null,
  });

  return (
    <section className="panel mission-checklist-settings-panel" data-testid="mission-checklist-settings">
      <div className="section-heading-row">
        <div>
          <span className="eyebrow">Règles de clôture terrain</span>
          <h2>Checklists de mission et obligations</h2>
          <p className="section-intro">
            Créez plusieurs modèles réutilisables, puis indiquez simplement pour quel type de mission, quel client ou quel bien chacun doit être utilisé.
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="secondary" disabled={loading || busy || !moduleAvailable} onClick={() => void openModel()}>＋ Nouveau modèle</button>
          <button type="button" disabled={loading || busy || !moduleAvailable || !models.length} onClick={() => openAssignment()}>＋ Nouvelle règle d’utilisation</button>
        </div>
      </div>
      {!hasDeploymentCapability(data, "mission_checklists") ? (
        <Notice tone="warning">
          Le module de checklists terrain n’est pas encore activé sur cette base. Aucun réglage ne peut être publié tant que la migration contrôlée n’est pas appliquée.
        </Notice>
      ) : loading ? <p className="empty">Chargement des modèles…</p> : null}
      <div className="mission-checklist-settings-guide">
        <div><strong>1</strong><span>Créez le modèle et ses obligations</span></div>
        <div><strong>2</strong><span>Choisissez mission, client et/ou bien</span></div>
        <div><strong>3</strong><span>ARD applique la règle la plus précise</span></div>
      </div>
      {pilotage && (
        <section className="mission-checklist-pilotage" data-testid="mission-checklist-pilotage">
          <div className="section-heading-row"><div><h3>Pilotage automatique · année en cours</h3><p className="section-intro">Calculé depuis les missions, validations, photos et justifications réelles.</p></div><Status value={pilotage.proof_exceptions.pending_review ? "warning" : "verified"} /></div>
          <div className="detail-grid compact-grid">
            <div><span>Missions suivies</span><strong>{pilotage.missions_with_checklist}</strong><small>avec checklist appliquée</small></div>
            <div><span>Points obligatoires validés</span><strong>{Number(pilotage.completion_rate || 0).toFixed(1)} %</strong><small>{pilotage.completed_required_items}/{pilotage.required_items}</small></div>
            <div><span>Preuves photo</span><strong>{pilotage.photo_evidence_count}</strong><small>photos reliées aux contrôles</small></div>
            <div><span>Pièces ajoutées sur place</span><strong>{pilotage.repeatable_room_instances}</strong><small>blocs répétés par l’équipe</small></div>
            <div><span>Justifications à contrôler</span><strong>{pilotage.proof_exceptions.pending_review}</strong><small>{pilotage.proof_exceptions.accepted} acceptée(s) · {pilotage.proof_exceptions.rejected} refusée(s)</small></div>
          </div>
          {!!pilotage.zones?.length && <details><summary>Voir le détail par pièce ou zone</summary><div className="table-wrap"><table><thead><tr><th>Pièce / zone</th><th>Points validés</th><th>Taux</th><th>Preuves impossibles</th></tr></thead><tbody>{pilotage.zones.map((zone) => <tr key={zone.zone_key}><td>{zone.zone_label}</td><td>{zone.completed_items}/{zone.required_items}</td><td>{Number(zone.completion_rate || 0).toFixed(1)} %</td><td>{zone.proof_exceptions}</td></tr>)}</tbody></table></div></details>}
        </section>
      )}
      <div className="mission-checklist-library-grid">
        <article>
          <div className="section-heading-row"><div><h3>Modèles disponibles</h3><p className="section-intro">Une modification crée une nouvelle version sans toucher aux missions commencées.</p></div><span className="status-badge">{models.length}</span></div>
          <div className="mission-checklist-model-list">
            {models.map((model) => {
              const current = latestVersion(model.id);
              return <button type="button" className="mission-checklist-model-row" key={model.id} onClick={() => void openModel(model)}>
                <span><strong>{model.name}</strong><small>{model.description || "Aucune description"}</small></span>
                <span><Status value={current?.status || "draft"} /><small>{current ? `Version ${current.version}` : "À publier"}</small></span>
              </button>;
            })}
            {!models.length && !message && <p className="empty">Aucun modèle. Créez le premier sans réglage technique.</p>}
          </div>
        </article>
        <article>
          <div className="section-heading-row"><div><h3>Utilisation automatique</h3><p className="section-intro">Le bien est prioritaire, puis le client, puis le type de mission.</p></div><span className="status-badge">{assignments.filter((item) => item.active).length}</span></div>
          <div className="mission-checklist-assignment-list">
            {assignments.map((assignment) => {
              const labels = labelFor(assignment);
              return <button type="button" className="mission-checklist-assignment-row" key={assignment.id} onClick={() => openAssignment(assignment)}>
                <span><strong>{labels.model}</strong><small>{[labels.catalog, labels.client, labels.property].filter(Boolean).join(" · ")}</small></span>
                <Status value={assignment.active ? "active" : "archived"} />
              </button>;
            })}
            {!assignments.length && !message && <p className="empty">Aucune règle : les modèles ne sont pas encore attribués.</p>}
          </div>
        </article>
      </div>
      {message && <Notice tone={/erreur|inaccessible|pas encore activée/i.test(message) ? "warning" : "info"}>{message}</Notice>}
      <Notice>
        Une mission commencée conserve toujours sa checklist d’origine. Tant que toutes ses obligations ne sont pas remplies, elle reste non terminée et non facturable par le sous-traitant.
      </Notice>
      {modelEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Configurer un modèle de checklist">
          <section className="portal-detail create-modal mission-checklist-editor">
            <div className="portal-detail-head">
              <div><span className="eyebrow">Bibliothèque réutilisable</span><h2>{modelEditor.model_id ? "Modifier le modèle" : "Nouveau modèle de checklist"}</h2><p>{modelEditor.latest_version ? `La version ${modelEditor.latest_version} (${modelEditor.latest_status}) sert de base.` : "Aucun code ni identifiant technique à renseigner."}</p></div>
              <button type="button" className="icon-close" onClick={() => setModelEditor(null)} aria-label="Fermer">×</button>
            </div>
            <div className="form-grid">
              <label>Nom du modèle<input value={modelEditor.name} onChange={(event) => setModelEditor((current) => current && ({ ...current, name: event.target.value }))} placeholder="Ex. Rotation premium" /></label>
              <label>Motif de cette version<input value={modelEditor.change_reason} onChange={(event) => setModelEditor((current) => current && ({ ...current, change_reason: event.target.value }))} placeholder="Ex. ajout du contrôle terrasse" /></label>
              <label className="wide">À quoi sert ce modèle ?<textarea value={modelEditor.description} onChange={(event) => setModelEditor((current) => current && ({ ...current, description: event.target.value }))} /></label>
              <label className="wide">Consigne générale visible par l’intervenant<textarea value={modelEditor.instructions} onChange={(event) => setModelEditor((current) => current && ({ ...current, instructions: event.target.value }))} /></label>
            </div>
            <div className="mission-checklist-step-list">
              {modelEditor.items.map((item, index) => <article className="mission-checklist-step-editor" key={`${item.item_key}-${index}`}>
                <div className="section-heading-row"><strong>Étape {index + 1}</strong>{modelEditor.items.length > 1 && <button type="button" className="danger compact" onClick={() => setModelEditor((current) => current && ({ ...current, items: current.items.filter((_entry, itemIndex) => itemIndex !== index) }))}>Supprimer</button>}</div>
                <div className="form-grid">
                  <label>Action à réaliser<input value={item.label} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, label: event.target.value } : entry) }))} placeholder="Ex. contrôler toutes les vitres" /></label>
                  <label>Photos obligatoires<input type="number" min="0" max="20" value={item.minimum_photo_count} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, minimum_photo_count: Number(event.target.value), is_required: Number(event.target.value) > 0 || entry.is_required } : entry) }))} /></label>
                  <label>Pièce ou zone<input value={item.zone_label} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, zone_label: event.target.value, zone_key: "" } : entry) }))} placeholder="Ex. Salle de bain" /></label>
                  <label className="wide">Précision utile<textarea value={item.instructions} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, instructions: event.target.value } : entry) }))} /></label>
                  <label className="checkline wide"><input type="checkbox" checked={item.is_required} disabled={item.minimum_photo_count > 0} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, is_required: event.target.checked } : entry) }))} /> Obligatoire pour pouvoir terminer la mission</label>
                  <label className="checkline wide"><input type="checkbox" checked={item.zone_repeatable} onChange={(event) => setModelEditor((current) => current && ({ ...current, items: current.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, zone_repeatable: event.target.checked } : entry) }))} /> Cette pièce peut être ajoutée plusieurs fois sur place</label>
                </div>
              </article>)}
            </div>
            <button type="button" className="secondary" onClick={() => setModelEditor((current) => current && ({ ...current, items: [...current.items, { item_key: `step-${current.items.length + 1}-${crypto.randomUUID().slice(0, 8)}`, label: "", instructions: "", is_required: true, minimum_photo_count: 0, zone_key: "general", zone_label: "Général", zone_repeatable: false }] }))}>＋ Ajouter une étape</button>
            <Notice>
              <strong>Validation métier.</strong> Le brouillon reste modifiable et sans effet sur les missions. « Valider et publier » enregistre automatiquement l’administrateur, la date et la nouvelle version ; aucune identité de validateur n’est à recopier depuis le document Word.
            </Notice>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="row-actions"><button type="button" className="secondary" onClick={() => setModelEditor(null)}>Annuler</button><button type="button" className="secondary" disabled={busy} onClick={() => void saveModel(false)}>Enregistrer en brouillon</button><button type="button" disabled={busy} onClick={() => void saveModel(true)}>Valider et publier</button></div>
          </section>
        </div>
      )}
      {assignmentEditor && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Règle d’utilisation d’un modèle">
          <section className="portal-detail create-modal">
            <div className="portal-detail-head"><div><span className="eyebrow">Automatisation des missions futures</span><h2>{assignmentEditor.id ? "Modifier la règle" : "Nouvelle règle d’utilisation"}</h2><p>Plus vous précisez le bien ou le client, plus cette règle sera prioritaire.</p></div><button type="button" className="icon-close" onClick={() => setAssignmentEditor(null)} aria-label="Fermer">×</button></div>
            <div className="form-grid">
              <label>Modèle à utiliser<select value={assignmentEditor.model_id} onChange={(event) => setAssignmentEditor((current) => current && ({ ...current, model_id: event.target.value }))}><option value="">Choisir</option>{models.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}</select></label>
              <label>Type de mission (facultatif)<select value={assignmentEditor.catalog_item_id} onChange={(event) => setAssignmentEditor((current) => current && ({ ...current, catalog_item_id: event.target.value }))}><option value="">Tous les types concernés par les autres choix</option>{catalogItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>Client (facultatif)<select value={assignmentEditor.client_id} onChange={(event) => setAssignmentEditor((current) => current && ({ ...current, client_id: event.target.value, property_id: current.property_id && activeProperties.find((property) => property.id === current.property_id)?.client_id !== event.target.value ? "" : current.property_id }))}><option value="">Tous les clients concernés</option>{activeClients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
              <label>Bien (facultatif)<select value={assignmentEditor.property_id} onChange={(event) => { const property = activeProperties.find((item) => item.id === event.target.value); setAssignmentEditor((current) => current && ({ ...current, property_id: event.target.value, client_id: property?.client_id || current.client_id })); }}><option value="">Tous les biens concernés</option>{activeProperties.filter((property) => !assignmentEditor.client_id || property.client_id === assignmentEditor.client_id).map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}</select></label>
              <label className="wide">Pourquoi cette règle ?<input value={assignmentEditor.change_reason} onChange={(event) => setAssignmentEditor((current) => current && ({ ...current, change_reason: event.target.value }))} placeholder="Ex. contrôle renforcé demandé pour ce bien" /></label>
              <label className="checkline wide"><input type="checkbox" checked={assignmentEditor.active} onChange={(event) => setAssignmentEditor((current) => current && ({ ...current, active: event.target.checked }))} /> Règle active pour les missions futures</label>
            </div>
            <Notice>Ordre automatique : bien précis, puis client, puis type de mission. À précision égale, la règle la plus récente est utilisée.</Notice>
            {message && <Notice tone="info">{message}</Notice>}
            <div className="row-actions"><button type="button" className="secondary" onClick={() => setAssignmentEditor(null)}>Annuler</button><button type="button" disabled={busy} onClick={() => void saveAssignment()}>Enregistrer la règle</button></div>
          </section>
        </div>
      )}
    </section>
  );
}

function AdminQualityAnalytics() {
  const today = parisDay(new Date()),
    initialFrom = `${today.slice(0, 4)}-01-01`,
    [fromDraft, setFromDraft] = useState(initialFrom),
    [toDraft, setToDraft] = useState(today),
    [period, setPeriod] = useState({ from: initialFrom, to: today }),
    [dimension, setDimension] = useState<MissionQualityAnalytic["dimension"]>("worker"),
    [rows, setRows] = useState<MissionQualityAnalytic[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [dossier, setDossier] = useState<WorkerQualityControlDossier | null>(null),
    [dossierBusy, setDossierBusy] = useState(false),
    [dossierMessage, setDossierMessage] = useState(""),
    [controlDraft, setControlDraft] = useState<null | {
      missionId: string; claimId: string; sourceKind: WorkerQualityControlReview["source_kind"];
      title: string; observation: string; purpose: string; penaltyEligible: boolean; amount: string;
    }>(null),
    [decisionDraft, setDecisionDraft] = useState<null | {
      review: WorkerQualityControlReview; decision: "applied" | "waived"; action: string; note: string;
    }>(null);
  const previousDate = (value: string) => {
      const date = new Date(`${value}T12:00:00`);
      date.setFullYear(date.getFullYear() - 1);
      return parisDay(date);
    },
    previousFrom = previousDate(period.from),
    previousTo = previousDate(period.to);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    supabase!
      .rpc("get_mission_quality_analytics", {
        p_from: period.from,
        p_to: period.to,
        p_previous_from: previousFrom,
        p_previous_to: previousTo,
      })
      .then((result) => {
        if (!active) return;
        setLoading(false);
        if (result.error) {
          setRows([]);
          setError(
            /get_mission_quality_analytics|schema cache|does not exist|could not find/i.test(result.error.message)
              ? "Les analyses croisées sont prêtes dans le code mais ne sont pas encore activées sur cette base."
              : result.error.message,
          );
          return;
        }
        setRows((result.data || []) as MissionQualityAnalytic[]);
      });
    return () => {
      active = false;
    };
  }, [period.from, period.to, previousFrom, previousTo]);

  const clientRows = rows.filter((row) => row.dimension === "client"),
    overview = clientRows.reduce(
      (total, row) => ({
        missions: total.missions + Number(row.completed_mission_count || 0),
        raw: total.raw + Number(row.raw_claim_count || 0),
        reviewed: total.reviewed + Number(row.reviewed_claim_count || 0),
        verifiedWeight:
          total.verifiedWeight +
          (Number(row.verified_return_rate || 0) * Number(row.completed_mission_count || 0)) / 100,
        touchups: total.touchups + Number(row.touchup_count || 0),
      }),
      { missions: 0, raw: 0, reviewed: 0, verifiedWeight: 0, touchups: 0 },
    ),
    overviewRate = overview.missions
      ? (100 * overview.verifiedWeight) / overview.missions
      : 0,
    visibleRows = rows
      .filter((row) => row.dimension === dimension)
      .sort(
        (left, right) =>
          Number(right.verified_return_rate) - Number(left.verified_return_rate) ||
          Number(right.reviewed_claim_count) - Number(left.reviewed_claim_count) ||
          left.label.localeCompare(right.label, "fr"),
      ),
    dimensionLabels: Record<MissionQualityAnalytic["dimension"], string> = {
      worker: "Intervenants",
      team: "Équipes",
      service: "Prestations",
      client: "Clients",
    };

  function applyPeriod(event: React.FormEvent) {
    event.preventDefault();
    if (!fromDraft || !toDraft || toDraft < fromDraft) {
      setError("La date de fin doit être postérieure à la date de début.");
      return;
    }
    setPeriod({ from: fromDraft, to: toDraft });
  }

  async function openWorkerDossier(workerId: string) {
    setDossierBusy(true);
    setDossierMessage("");
    const result = await supabase!.rpc("get_worker_quality_control_dossier", {
      p_worker_id: workerId,
      p_from: period.from,
      p_to: period.to,
    });
    setDossierBusy(false);
    if (result.error) {
      setDossierMessage(result.error.message);
      return;
    }
    setDossier(result.data as WorkerQualityControlDossier);
  }

  async function refreshWorkerDossier() {
    if (dossier?.worker.id) await openWorkerDossier(dossier.worker.id);
  }

  function startControl(mission: WorkerQualityControlDossier["missions"][number]) {
    const missingLabels = mission.missing_non_blocking_items.map((item) => item.label || "Information demandée");
    setControlDraft({
      missionId: mission.id,
      claimId: mission.claims[0]?.id || "",
      sourceKind: missingLabels.length ? "non_blocking_missing_information" : mission.claims.length ? "client_return" : "manual_control",
      title: missingLabels.length ? "Informations demandées non renseignées" : mission.claims.length ? "Retour client à contrôler" : "Contrôle qualité de la mission",
      observation: "",
      purpose: missingLabels.length ? "Ces informations protègent l’intervenant et la société et servent aux statistiques internes." : "",
      penaltyEligible: false,
      amount: "",
    });
  }

  async function saveControl(event: React.FormEvent) {
    event.preventDefault();
    if (!dossier || !controlDraft) return;
    const mission = dossier.missions.find((item) => item.id === controlDraft.missionId);
    setDossierBusy(true);
    const result = await supabase!.rpc("record_worker_quality_control_review", {
      p_worker_id: dossier.worker.id,
      p_mission_id: controlDraft.missionId,
      p_quality_claim_id: controlDraft.claimId || null,
      p_source_kind: controlDraft.sourceKind,
      p_title: controlDraft.title,
      p_observation: controlDraft.observation,
      p_missing_information: controlDraft.sourceKind === "non_blocking_missing_information" ? mission?.missing_non_blocking_items || [] : [],
      p_information_purpose: controlDraft.purpose || null,
      p_penalty_eligible: controlDraft.penaltyEligible,
      p_proposed_penalty_amount_ex_vat: controlDraft.penaltyEligible && controlDraft.amount !== "" ? Number(controlDraft.amount) : null,
    });
    setDossierBusy(false);
    setDossierMessage(result.error ? result.error.message : "Contrôle ajouté à l’historique. Aucune pénalité n’a été appliquée automatiquement.");
    if (!result.error) {
      setControlDraft(null);
      await refreshWorkerDossier();
    }
  }

  async function decidePenalty(event: React.FormEvent) {
    event.preventDefault();
    if (!decisionDraft) return;
    setDossierBusy(true);
    const result = await supabase!.rpc("decide_worker_quality_penalty", {
      p_review_id: decisionDraft.review.id,
      p_decision: decisionDraft.decision,
      p_decision_action: decisionDraft.action || null,
      p_decision_note: decisionDraft.note,
    });
    setDossierBusy(false);
    setDossierMessage(result.error ? result.error.message : decisionDraft.decision === "applied" ? "Décision appliquée et historisée." : "Pénalité non appliquée ; la décision et son motif restent comptabilisés.");
    if (!result.error) {
      setDecisionDraft(null);
      await refreshWorkerDossier();
    }
  }

  return (
    <section className="panel quality-analytics-panel" data-testid="admin-quality-analytics">
      <div className="section-heading quality-analytics-heading">
        <div>
          <span className="eyebrow">Décisions humaines uniquement</span>
          <h2>Pilotage qualité contrôlé</h2>
          <p className="section-intro">
            Comparez les missions de la période avec les mêmes dates de l’année précédente. Les demandes client non qualifiées restent visibles en volume brut, mais n’entrent jamais dans le taux vérifié.
          </p>
        </div>
        <form className="quality-period-form" onSubmit={applyPeriod}>
          <label>Du<input type="date" value={fromDraft} onChange={(event) => setFromDraft(event.target.value)} /></label>
          <label>Au<input type="date" value={toDraft} onChange={(event) => setToDraft(event.target.value)} /></label>
          <button className="secondary compact">Actualiser</button>
        </form>
      </div>
      <p className="quality-comparison-caption">
        Période comparée : {frDate(previousFrom)} au {frDate(previousTo)}.
      </p>
      {error && <Notice tone={error.includes("prêtes dans le code") ? "warning" : "error"}>{error}</Notice>}
      {!error && (
        <>
          <div className="quality-overview-grid" aria-label="Synthèse qualité de la période">
            <article><span>Missions terminées</span><strong>{overview.missions}</strong><small>base de calcul sans double comptage client</small></article>
            <article><span>Retours client bruts</span><strong>{overview.raw}</strong><small>{Math.max(0, overview.raw - overview.reviewed)} encore non qualifié(s)</small></article>
            <article><span>Retours contrôlés</span><strong>{overview.reviewed}</strong><small>décision administrateur enregistrée</small></article>
            <article><span>Taux de retour vérifié</span><strong>{overviewRate.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %</strong><small>fondé + 50 % des partiellement fondés</small></article>
            <article><span>Retouches nécessaires</span><strong>{overview.touchups}</strong><small>uniquement après qualification fondée</small></article>
          </div>
          <div className="quality-dimension-tabs" role="tablist" aria-label="Niveau d’analyse qualité">
            {(Object.keys(dimensionLabels) as MissionQualityAnalytic["dimension"][]).map((key) => (
              <button
                type="button"
                role="tab"
                aria-selected={dimension === key}
                className={dimension === key ? "active" : "secondary"}
                key={key}
                onClick={() => setDimension(key)}
              >
                {dimensionLabels[key]}
              </button>
            ))}
          </div>
          {loading ? (
            <p className="empty">Calcul des indicateurs contrôlés…</p>
          ) : visibleRows.length ? (
            <div className="quality-analytics-table-wrap">
              <table className="quality-analytics-table">
                <thead><tr><th>{dimensionLabels[dimension].slice(0, -1)}</th><th>Missions</th><th>Bruts / contrôlés</th><th>Taux vérifié</th><th>Comparaison</th><th>Retouches</th><th>Récidives</th><th>Résolution</th></tr></thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const delta = Number(row.verified_return_rate_delta || 0);
                    return (
                      <tr key={`${row.dimension}:${row.entity_id}`}>
                        <td data-label={dimensionLabels[dimension].slice(0, -1)}>{dimension === "worker" ? <button type="button" className="quality-person-link" disabled={dossierBusy} onClick={() => void openWorkerDossier(row.entity_id)}><strong>{row.label}</strong><small>{row.verified_issue_mission_count} mission(s) avec retour vérifié · ouvrir l’historique</small></button> : <><strong>{row.label}</strong><small>{row.verified_issue_mission_count} mission(s) avec retour vérifié</small></>}</td>
                        <td data-label="Missions">{row.completed_mission_count}</td>
                        <td data-label="Bruts / contrôlés">{row.raw_claim_count} / {row.reviewed_claim_count}</td>
                        <td data-label="Taux vérifié"><strong>{Number(row.verified_return_rate).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %</strong></td>
                        <td data-label="Comparaison"><span className={`quality-delta ${delta > 0 ? "up" : delta < 0 ? "down" : "stable"}`}>{delta > 0 ? "+" : ""}{delta.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} pt</span><small>{Number(row.previous_verified_return_rate).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} % auparavant</small></td>
                        <td data-label="Retouches">{row.touchup_count}</td>
                        <td data-label="Récidives">{row.repeated_verified_return_count}</td>
                        <td data-label="Résolution">{row.average_resolution_hours == null ? "—" : `${Number(row.average_resolution_hours).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty">Aucune mission terminée pour ce niveau d’analyse sur les périodes comparées.</p>
          )}
          <Notice>
            Ces indicateurs servent au pilotage et à l’analyse. Ils ne déclenchent automatiquement ni pénalité, ni retenue, ni baisse de score, ni prime, ni modification de facture.
          </Notice>
        </>
      )}
      {dossier && !controlDraft && !decisionDraft && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label={`Historique qualité de ${dossier.worker.display_name}`}>
          <section className="portal-detail quality-dossier">
            <div className="portal-detail-head"><div><span className="eyebrow">Historique explicable</span><h2>{dossier.worker.display_name}</h2><p>{dossier.worker.kind === "contractor" ? "Sous-traitant" : "Employé"} · {frDate(dossier.period.from)} au {frDate(dossier.period.to)}</p></div><button type="button" className="icon-close" onClick={() => { setDossier(null); setDossierMessage(""); }} aria-label="Fermer">×</button></div>
            <div className="quality-dossier-summary">
              <article><span>Missions</span><strong>{dossier.summary.mission_count}</strong></article>
              <article><span>Retours client</span><strong>{dossier.summary.client_return_count}</strong></article>
              <article><span>Missions incomplètes</span><strong>{dossier.summary.mission_with_missing_non_blocking_count}</strong><small>{dossier.summary.missing_non_blocking_item_count} information(s) non bloquante(s)</small></article>
              <article><span>À décider</span><strong>{dossier.summary.penalty_pending_count}</strong></article>
              <article><span>Appliquées</span><strong>{dossier.summary.penalty_applied_count}</strong></article>
              <article><span>Non appliquées</span><strong>{dossier.summary.penalty_waived_count}</strong></article>
            </div>
            <Notice>Une information non bloquante n’est pas facultative : si elle est demandée, elle sert à protéger l’intervenant ou la société, à documenter la prestation, ou à fiabiliser les statistiques internes.</Notice>
            {dossierMessage && <Notice tone={/inaccessible|erreur|obligatoire/i.test(dossierMessage) ? "error" : "info"}>{dossierMessage}</Notice>}
            <div className="quality-dossier-missions">
              {dossier.missions.map((mission) => <article className="quality-dossier-mission" key={mission.id}>
                <header><div><strong>{mission.client_name || "Client"} · {frDate(mission.starts_at)}</strong><small>{mission.location || "Adresse non renseignée"}</small></div><Status value={mission.workflow_status || mission.status} /></header>
                <div className="quality-dossier-facts">
                  <span>{mission.reports.length} compte(s) rendu(s)</span><span>{mission.claims.length} retour(s) client</span><span>{mission.missing_non_blocking_count} information(s) demandée(s) manquante(s)</span>
                </div>
                {mission.reports.map((report) => <div className="quality-history-entry" key={report.id}><strong>Compte rendu · {frDate(report.submitted_at)}</strong><p>{report.observations || "Aucune observation terrain renseignée."}</p>{report.client_note && <small>Message client : {report.client_note}</small>}</div>)}
                {mission.claims.map((claim) => <div className="quality-history-entry client-return" key={claim.id}><strong>Retour client · {claim.category}</strong><p>{claim.description}</p><small>{claim.qualification ? `Qualification : ${claim.qualification}` : "À qualifier par l’administration"}{claim.admin_notes ? ` · ${claim.admin_notes}` : ""}</small></div>)}
                {mission.missing_non_blocking_items.length > 0 && <div className="quality-missing-list"><strong>Informations demandées mais absentes</strong>{mission.missing_non_blocking_items.map((item, index) => <span key={`${mission.id}-missing-${index}`}>{item.label || "Information non renseignée"}{item.instructions ? <small>{item.instructions}</small> : null}</span>)}</div>}
                {mission.controls.map((control) => <div className="quality-control-entry" key={control.id}><div><strong>{control.title}</strong><p>{control.observation}</p>{control.information_purpose && <small>Utilité : {control.information_purpose}</small>}</div><div className="quality-control-decision"><Status value={control.penalty_decision} />{control.proposed_penalty_amount_ex_vat != null && <small>Proposition : {money(control.proposed_penalty_amount_ex_vat)} HT</small>}{control.decision_note && <small>{control.decision_note}</small>}{control.penalty_decision === "pending" && <span className="row-actions"><button type="button" onClick={() => setDecisionDraft({ review: control, decision: "applied", action: "", note: "" })}>Appliquer</button><button type="button" className="secondary" onClick={() => setDecisionDraft({ review: control, decision: "waived", action: "", note: "" })}>Ne pas appliquer</button></span>}</div></div>)}
                <button type="button" className="secondary compact" onClick={() => startControl(mission)}>＋ Ajouter un contrôle documenté</button>
              </article>)}
              {!dossier.missions.length && <p className="empty">Aucune mission reliée à cet intervenant sur la période.</p>}
            </div>
          </section>
        </div>
      )}
      {dossier && controlDraft && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Ajouter un contrôle qualité">
          <form className="portal-detail create-modal" onSubmit={saveControl}>
            <div className="portal-detail-head"><div><span className="eyebrow">Décision humaine</span><h2>Nouveau contrôle qualité</h2></div><button type="button" className="icon-close" onClick={() => setControlDraft(null)} aria-label="Fermer">×</button></div>
            <div className="form-grid">
              <label>Origine<select value={controlDraft.sourceKind} onChange={(event) => setControlDraft({ ...controlDraft, sourceKind: event.target.value as WorkerQualityControlReview["source_kind"] })}><option value="client_return">Retour client</option><option value="non_blocking_missing_information">Information demandée manquante</option><option value="mission_observation">Observation de mission</option><option value="manual_control">Contrôle manuel</option></select></label>
              <label>Retour client lié<select value={controlDraft.claimId} onChange={(event) => setControlDraft({ ...controlDraft, claimId: event.target.value })}><option value="">Aucun</option>{dossier.missions.find((mission) => mission.id === controlDraft.missionId)?.claims.map((claim) => <option key={claim.id} value={claim.id}>{claim.category} · {claim.description.slice(0, 70)}</option>)}</select></label>
              <label className="wide">Titre<input required minLength={3} value={controlDraft.title} onChange={(event) => setControlDraft({ ...controlDraft, title: event.target.value })} /></label>
              <label className="wide">Observation vérifiée<textarea required minLength={5} rows={4} value={controlDraft.observation} onChange={(event) => setControlDraft({ ...controlDraft, observation: event.target.value })} /></label>
              {controlDraft.sourceKind === "non_blocking_missing_information" && <label className="wide">Pourquoi ces informations servent-elles ?<textarea required minLength={5} rows={3} value={controlDraft.purpose} onChange={(event) => setControlDraft({ ...controlDraft, purpose: event.target.value })} /></label>}
              <label className="checkline wide"><input type="checkbox" checked={controlDraft.penaltyEligible} onChange={(event) => setControlDraft({ ...controlDraft, penaltyEligible: event.target.checked, amount: event.target.checked ? controlDraft.amount : "" })} /> Une pénalité prévue doit faire l’objet d’une décision</label>
              {controlDraft.penaltyEligible && <label>Montant envisagé HT (ST uniquement)<input type="number" min="0" step="0.01" value={controlDraft.amount} onChange={(event) => setControlDraft({ ...controlDraft, amount: event.target.value })} /></label>}
            </div>
            <Notice>Aucun retour client, oubli ou indicateur n’applique seul une pénalité. La décision séparée restera comptée, qu’elle soit appliquée ou non.</Notice>
            {dossierMessage && <Notice tone="error">{dossierMessage}</Notice>}
            <div className="detail-actions"><button type="button" className="secondary" onClick={() => setControlDraft(null)}>Annuler</button><button disabled={dossierBusy}>{dossierBusy ? "Enregistrement…" : "Ajouter à l’historique"}</button></div>
          </form>
        </div>
      )}
      {dossier && decisionDraft && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Décider d’une pénalité qualité">
          <form className="portal-detail create-modal" onSubmit={decidePenalty}>
            <div className="portal-detail-head"><div><span className="eyebrow">Contrôle obligatoire</span><h2>{decisionDraft.decision === "applied" ? "Appliquer la décision" : "Ne pas appliquer la pénalité"}</h2><p>{decisionDraft.review.title}</p></div><button type="button" className="icon-close" onClick={() => setDecisionDraft(null)} aria-label="Fermer">×</button></div>
            <div className="form-grid">
              <label className="wide">Action réellement menée{decisionDraft.decision === "applied" ? " (obligatoire)" : ""}<input required={decisionDraft.decision === "applied"} minLength={decisionDraft.decision === "applied" ? 3 : undefined} value={decisionDraft.action} onChange={(event) => setDecisionDraft({ ...decisionDraft, action: event.target.value })} placeholder={decisionDraft.decision === "applied" ? "Ex. retenue ST, rappel formel, accompagnement" : "Ex. rappel et contrôle renforcé"} /></label>
              <label className="wide">Motif de la décision<textarea required minLength={5} rows={4} value={decisionDraft.note} onChange={(event) => setDecisionDraft({ ...decisionDraft, note: event.target.value })} /></label>
            </div>
            {dossier.worker.kind === "employee" && decisionDraft.review.proposed_penalty_amount_ex_vat ? <Notice tone="warning">Aucune retenue financière automatique ne sera appliquée à un salarié. Utilisez une action RH non financière et documentée.</Notice> : null}
            {dossierMessage && <Notice tone="error">{dossierMessage}</Notice>}
            <div className="detail-actions"><button type="button" className="secondary" onClick={() => setDecisionDraft(null)}>Annuler</button><button disabled={dossierBusy}>{dossierBusy ? "Validation…" : "Confirmer la décision"}</button></div>
          </form>
        </div>
      )}
    </section>
  );
}

function AdminWorkflow({
  profile,
  data,
  reload,
  focusReportMissionId,
  onFocusReportConsumed,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
  focusReportMissionId?: string | null;
  onFocusReportConsumed?: () => void;
}) {
  const [busy, setBusy] = useState(""),
    [message, setMessage] = useState(""),
    [selectedRequest, setSelectedRequest] = useState<MissionRequest | null>(
      null,
    ),
    [selectedReport, setSelectedReport] = useState<MissionReport | null>(null),
    [serviceChoices, setServiceChoices] = useState<string[]>([]),
    [reportClientMessage, setReportClientMessage] = useState(""),
    [reportNextActions, setReportNextActions] = useState(""),
    [reportAdminNotes, setReportAdminNotes] = useState(""),
    [reportInvoiceStrategy, setReportInvoiceStrategy] = useState<
      "grouped" | "individual"
    >("grouped"),
    [reportStockDecisions, setReportStockDecisions] = useState<
      Record<
        string,
        {
          treatment: "" | "billable" | "complimentary";
          price: string;
          vat: string;
          notes: string;
        }
      >
    >({}),
    [showNewService, setShowNewService] = useState(false),
    [newServiceName, setNewServiceName] = useState(""),
    [newServiceMode, setNewServiceMode] = useState("fixed"),
    [newServicePrice, setNewServicePrice] = useState(""),
    [newServiceVat, setNewServiceVat] = useState(
      String(data.organization?.default_vat_rate ?? 20),
    ),
    [newServiceDuration, setNewServiceDuration] = useState(""),
    [newServiceNotes, setNewServiceNotes] = useState("");

  function openReport(report: MissionReport) {
    const selectedMission = data.missions.find(
        (mission) => mission.id === report.mission_id,
      ),
      decisions: typeof reportStockDecisions = {};
    (data.missionStockUsages || [])
      .filter((usage) => usage.report_id === report.id)
      .forEach((usage) => {
        const item = (data.inventoryItems || []).find(
          (candidate) => candidate.id === usage.item_id,
        );
        decisions[usage.id] = {
          treatment:
            usage.billing_status === "ready"
              ? "billable"
              : usage.billing_status === "not_billable"
                ? "complimentary"
                : "",
          price: String(
            usage.unit_price_ex_vat ?? item?.sale_price_ex_vat ?? "",
          ),
          vat: String(
            usage.vat_rate ||
              item?.sale_vat_rate ||
              data.organization?.default_vat_rate ||
              20,
          ),
          notes: usage.billing_notes || "",
        };
      });
    setSelectedReport(report);
    setReportStockDecisions(decisions);
    setReportClientMessage(
      selectedMission?.client_report || report.client_note || "",
    );
    setReportNextActions(selectedMission?.next_actions || "");
    setReportAdminNotes(selectedMission?.admin_notes || "");
    setReportInvoiceStrategy(
      data.clients.find((client) => client.id === selectedMission?.client_id)
        ?.billing_grouping === "individual"
        ? "individual"
        : "grouped",
    );
  }

  useEffect(() => {
    if (!focusReportMissionId) return;
    const report = data.reports.find(
      (candidate) => candidate.mission_id === focusReportMissionId,
    );
    if (report) openReport(report);
    onFocusReportConsumed?.();
  }, [focusReportMissionId]);

  async function proposeService(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRequest) return;
    if (!newServiceName.trim() || newServicePrice === "") {
      setMessage("Renseignez le nom et le prix de la nouvelle prestation.");
      return;
    }
    setBusy(selectedRequest.id);
    const { error } = await supabase!.from("service_proposals").insert({
      organization_id: profile.organization_id,
      client_id: selectedRequest.client_id,
      property_id: selectedRequest.property_id || null,
      mission_request_id: selectedRequest.id,
      proposed_by: profile.id,
      name: newServiceName.trim(),
      billing_mode: newServiceMode,
      unit_price_ex_vat: +newServicePrice,
      vat_rate: +newServiceVat,
      default_duration_hours: newServiceDuration ? +newServiceDuration : null,
      notes: newServiceNotes.trim() || null,
      status: "pending",
    });
    const client = data.clients.find((c) => c.id === selectedRequest.client_id);
    if (!error && client?.profile_id) {
      await supabase!.from("notifications").insert({
        organization_id: profile.organization_id,
        recipient_profile_id: client.profile_id,
        kind: "service_proposal",
        title: "Nouvelle prestation à valider",
        body: `${newServiceName.trim()} · ${money(+newServicePrice)} HT`,
      });
    }
    setBusy("");
    setMessage(
      error
        ? error.message
        : "Proposition envoyée au client. La prestation ne sera créée qu’après son accord.",
    );
    if (!error) {
      setShowNewService(false);
      setNewServiceName("");
      setNewServicePrice("");
      setNewServiceDuration("");
      setNewServiceNotes("");
      reload();
    }
  }

  async function decideRequest(r: MissionRequest, accept: boolean) {
    setBusy(r.id);
    const acceptedProposal = (data.serviceProposals || []).find(
        (p) => p.mission_request_id === r.id && p.status === "accepted",
      ),
      acceptedProposalKey = acceptedProposal?.created_property_service_id
        ? `property:${acceptedProposal.created_property_service_id}`
        : acceptedProposal?.created_service_id
          ? `client:${acceptedProposal.created_service_id}`
          : null,
      selectedKeys = serviceChoices.length
        ? serviceChoices
        : acceptedProposalKey
          ? [acceptedProposalKey]
          : r.requested_service_keys?.length
            ? r.requested_service_keys
            : r.property_service_id
              ? [`property:${r.property_service_id}`]
              : r.service_id
                ? [`client:${r.service_id}`]
                : [],
      selectedServices = selectedKeys
        .map((key) => {
          const [scope, id] = key.split(":");
          return scope === "property"
            ? data.propertyServices.find((service) => service.id === id)
            : data.services.find((service) => service.id === id);
        })
        .filter(Boolean);
    if (
      accept &&
      (!selectedKeys.length || selectedServices.length !== selectedKeys.length)
    ) {
      setBusy("");
      setMessage(
        "Choisissez au moins une prestation existante et accessible, ou attendez la validation de la nouvelle prestation par le client.",
      );
      return;
    }
    let reviewed = await supabase!.rpc("review_mission_request", {
      p_request_id: r.id,
      p_approve: accept,
      p_selected_service_keys: accept ? selectedKeys : [],
    });
    if (
      reviewed.error &&
      /p_selected_service_keys|review_mission_request|schema cache|function/i.test(
        reviewed.error.message,
      ) &&
      (!accept || selectedKeys.length <= 1)
    ) {
      reviewed = await supabase!.rpc("review_mission_request", {
        p_request_id: r.id,
        p_approve: accept,
        p_selected_service_id: accept
          ? selectedKeys[0]?.split(":")[1] || null
          : null,
      });
    }
    setBusy("");
    setMessage(
      reviewed.error
        ? reviewed.error.message
        : !accept
          ? "Demande refusée et client informé."
          : reviewed.data?.[0]?.assignment_status === "assigned"
            ? "Demande acceptée, mission créée et employé prioritaire affecté."
            : reviewed.data?.[0]?.assignment_status === "proposed"
              ? "Demande acceptée, mission créée et proposée au ST prioritaire."
              : "Demande acceptée et mission créée. Aucun intervenant disponible pour le moment.",
    );
    if (!reviewed.error) {
      setSelectedRequest(null);
      setServiceChoices([]);
    }
    reload();
  }
  async function decideReport(r: MissionReport, accept: boolean) {
    if (accept) {
      const pendingStock = (data.missionStockUsages || []).filter(
        (usage) =>
          usage.report_id === r.id && usage.billing_status === "pending_review",
      );
      const incomplete = pendingStock.find(
        (usage) => !reportStockDecisions[usage.id]?.treatment,
      );
      if (incomplete) {
        setMessage(
          "Choisissez le traitement de chaque produit utilisé avant de valider le compte rendu.",
        );
        return;
      }
      setBusy(r.id);
      for (const usage of pendingStock) {
        const decision = reportStockDecisions[usage.id],
          billable = decision.treatment === "billable";
        if (billable && decision.price === "") {
          setBusy("");
          setMessage("Renseignez le prix HT de chaque produit à facturer.");
          return;
        }
        const reviewedStock = await supabase!.rpc(
          "review_mission_stock_usage",
          {
            p_usage_id: usage.id,
            p_billable: billable,
            p_unit_price_ex_vat: billable ? +decision.price : null,
            p_vat_rate: billable ? +decision.vat : null,
            p_admin_notes: decision.notes.trim() || null,
          },
        );
        if (reviewedStock.error) {
          setBusy("");
          setMessage(reviewedStock.error.message);
          return;
        }
      }
    }
    setBusy(r.id);
    let reviewed;
    if (accept) {
      reviewed = await supabase!.rpc("finalize_mission_admin", {
        p_mission_id: r.mission_id,
        p_report_mode: "terrain_report",
        p_source_report_id: r.id,
        p_actual_hours: selectedReportDeclaredHours || r.actual_hours,
        p_client_report: reportClientMessage.trim() || null,
        p_next_actions: reportNextActions.trim() || null,
        p_admin_notes: reportAdminNotes.trim() || null,
        p_override_reason: null,
        p_invoice_strategy: reportInvoiceStrategy,
      });
    } else {
      reviewed = await supabase!.rpc(
        r.route_team_id
          ? "review_route_team_mission_reports"
          : "review_mission_report",
        {
          p_report_id: r.id,
          p_approve: false,
          p_client_report: reportClientMessage.trim() || null,
          p_next_actions: reportNextActions.trim() || null,
          p_admin_notes: reportAdminNotes.trim() || null,
        },
      );
      if (
        r.route_team_id &&
        reviewed.error &&
        /review_route_team_mission_reports|schema cache|function/i.test(
          reviewed.error.message,
        )
      ) {
        reviewed = await supabase!.rpc("review_mission_report", {
          p_report_id: r.id,
          p_approve: false,
          p_client_report: reportClientMessage.trim() || null,
          p_next_actions: reportNextActions.trim() || null,
          p_admin_notes: reportAdminNotes.trim() || null,
        });
      }
    }
    const actionResolutionErrors: string[] = [];
    let resolvedActionCount = 0;
    if (!reviewed.error) {
      const relatedEventIds = new Set(
        (data.processBusinessEvents || [])
          .filter((event) => {
            if (
              event.event_type !== "mission.report_submitted" ||
              event.subject_type !== "mission" ||
              event.subject_id !== r.mission_id
            )
              return false;
            const eventWorkerId = String(event.context.worker_id || ""),
              eventSubmitterId = String(event.context.submitted_by_worker_id || "");
            return r.route_team_id
              ? eventSubmitterId === String(r.submitted_by_worker_id || r.worker_id)
              : eventWorkerId === r.worker_id;
          })
          .map((event) => event.id),
      );
      const relatedActions = (data.processRequiredActions || []).filter(
        (action) =>
          relatedEventIds.has(action.event_id) &&
          ["open", "in_progress"].includes(action.status),
      );
      for (const action of relatedActions) {
        const resolution = await supabase!.rpc("resolve_required_action", {
          p_action_id: action.id,
          p_decision: accept ? "approve" : "request_changes",
          p_resolution_note:
            reportAdminNotes.trim() ||
            (accept
              ? "Compte rendu contrôlé et validé depuis la fiche mission."
              : "Corrections demandées depuis la fiche mission."),
        });
        if (resolution.error) actionResolutionErrors.push(resolution.error.message);
        else resolvedActionCount += 1;
      }
    }
    setBusy("");
    setMessage(
      reviewed.error
        ? reviewed.error.message
        : accept
          ? `Compte rendu validé et mission placée à facturer en ${reportInvoiceStrategy === "grouped" ? "facture globale" : "facture indépendante"}.${actionResolutionErrors.length ? " Le centre d’actions reste à contrôler : " + actionResolutionErrors.join(" ") : resolvedActionCount ? " L’action requise associée est clôturée." : ""}`
          : `Compte rendu refusé.${actionResolutionErrors.length ? " Le centre d’actions reste à contrôler : " + actionResolutionErrors.join(" ") : resolvedActionCount ? " La demande de correction est historisée dans les actions requises." : ""}`,
    );
    if (!reviewed.error) setSelectedReport(null);
    reload();
  }
  async function openReportAttachment(item: ReportAttachment) {
    const { data: signed, error } = await supabase!.storage
      .from("mission-evidence")
      .createSignedUrl(item.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  }
  const requests = data.requests.filter((r) => r.status === "pending"),
    pendingReports = data.reports.filter((r) => r.status === "pending"),
    reports = pendingReports.filter(
      (report, index, all) =>
        !report.route_team_id ||
        all.findIndex(
          (candidate) =>
            candidate.mission_id === report.mission_id &&
            candidate.route_team_id === report.route_team_id,
        ) === index,
    ),
    reportHistory = data.reports
      .filter((r) => r.status !== "pending")
      .sort(
        (a, b) =>
          new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
      );
  const requestServices = selectedRequest
      ? [
          ...data.services.filter(
            (s) => s.client_id === selectedRequest.client_id,
          ).map((service) => ({
            ...service,
            serviceKey: `client:${service.id}`,
            scopeLabel: "Prestation ou supplément client",
          })),
          ...data.propertyServices.filter(
            (s) => s.property_id === selectedRequest.property_id,
          ).map((service) => ({
            ...service,
            serviceKey: `property:${service.id}`,
            scopeLabel: "Tarif du bien",
          })),
        ]
      : [],
    requestProposal = selectedRequest
      ? (data.serviceProposals || []).find(
          (p) => p.mission_request_id === selectedRequest.id,
        )
      : null,
    reportMission = selectedReport
      ? data.missions.find((m) => m.id === selectedReport.mission_id)
      : null,
    reportWorker = selectedReport
      ? data.workers.find((w) => w.id === selectedReport.worker_id)
      : null,
    reportAttachments = selectedReport
      ? (data.reportAttachments || []).filter(
          (item) => item.report_id === selectedReport.id,
        )
      : [],
    reportStockUsages = selectedReport
      ? (data.missionStockUsages || []).filter(
          (item) => item.report_id === selectedReport.id,
        )
      : [],
    reportActivityEntries = selectedReport
      ? (data.missionReportActivityEntries || []).filter(
          (item) => item.report_id === selectedReport.id,
        )
      : [],
    selectedTeamReports = selectedReport?.route_team_id
      ? data.reports.filter(
          (item) =>
            item.mission_id === selectedReport.mission_id &&
            item.route_team_id === selectedReport.route_team_id,
        )
      : [],
    selectedReportDeclaredHours = selectedReport
      ? selectedReport.route_team_id
        ? data.reports
            .filter(
              (item) =>
                item.mission_id === selectedReport.mission_id &&
                item.route_team_id === selectedReport.route_team_id,
            )
            .reduce((sum, item) => sum + Number(item.actual_hours || 0), 0)
        : Number(selectedReport.actual_hours || 0)
      : 0,
    selectedReportSubmissions = selectedReport
      ? (data.missionReportSubmissions || []).filter(
          (item) => item.mission_id === selectedReport.mission_id,
        )
      : [],
    hourDiscrepancies = selectedReportSubmissions
      .map((submission) => submission.worker_id)
      .filter((workerId, index, all) => all.indexOf(workerId) === index)
      .map((workerId) => {
        const entries = selectedReportSubmissions.filter(
            (item) => item.worker_id === workerId,
          ),
          values = [...new Set(entries.map((item) => Number(item.actual_hours)))];
        return values.length > 1 ? { workerId, entries } : null;
      })
      .filter(Boolean) as Array<{
        workerId: string;
        entries: MissionReportSubmission[];
      }>;
  return (
    <>
    <AdminConciergeRequests profile={profile} data={data} reload={reload} />
    <section className="panel">
      <h2>Validation sécurisée</h2>
      <p className="section-intro">
        Chaque validation s’ouvre avec le contexte complet avant de modifier une
        mission ou la facturation.
      </p>
      {message && (
        <Notice
          tone={
            message.includes("acceptée") || message.includes("validé")
              ? "success"
              : "info"
          }
        >
          {message}
        </Notice>
      )}
      <h3>Demandes client ({requests.length})</h3>
      {requests.map((r) => (
        <div className="workflow-row" key={r.id}>
          <div>
            <strong>
              {data.clients.find((c) => c.id === r.client_id)?.name || "Client"}{" "}
              · {frDate(r.desired_starts_at, true)}
            </strong>
            <small>
              {r.location} · {r.client_notes || "Sans observation"}
            </small>
          </div>
          <button
            className="secondary compact"
            onClick={() => {
              setSelectedRequest(r);
              setServiceChoices(
                r.requested_service_keys?.length
                  ? r.requested_service_keys
                  : r.property_service_id
                    ? [`property:${r.property_service_id}`]
                    : r.service_id
                      ? [`client:${r.service_id}`]
                      : [],
              );
              setShowNewService(false);
              setNewServiceName("");
              setNewServicePrice("");
            }}
          >
            Examiner la demande
          </button>
        </div>
      ))}
      {!requests.length && (
        <p className="empty">Aucune demande client en attente.</p>
      )}
      <h3>Comptes rendus terrain ({reports.length})</h3>
      {reports.map((r) => {
        const mission = data.missions.find((m) => m.id === r.mission_id),
          client = data.clients.find((c) => c.id === mission?.client_id),
          owner = data.workers.find((w) => w.id === r.worker_id);
        return (
          <div className="workflow-row" key={r.id}>
            <div>
              <strong>
                {owner ? workerName(owner) : "Intervenant"} ·{" "}
                {client?.name ||
                  mission?.client_display_name ||
                  "Client non retrouvé"}
              </strong>
              <small>
                {frDate(mission?.starts_at || r.submitted_at, true)} ·{" "}
                {r.route_team_id
                  ? `${pendingReports.filter((item) => item.mission_id === r.mission_id && item.route_team_id === r.route_team_id).length} membre(s) · compte rendu d’équipe`
                  : `${r.actual_hours} h réalisées`}
              </small>
            </div>
            <button
              className="secondary compact"
              onClick={() => openReport(r)}
            >
              Lire et valider
            </button>
          </div>
        );
      })}
      {!reports.length && (
        <p className="empty">Aucun compte rendu en attente.</p>
      )}
      <details className="report-history">
        <summary>
          Historique des comptes rendus <span>{reportHistory.length}</span>
        </summary>
        <div className="details-content">
          {reportHistory.map((report) => {
            const mission = data.missions.find(
                (candidate) => candidate.id === report.mission_id,
              ),
              client = data.clients.find(
                (candidate) => candidate.id === mission?.client_id,
              ),
              owner = data.workers.find(
                (candidate) => candidate.id === report.worker_id,
              );
            return (
              <div className="workflow-row" key={report.id}>
                <div>
                  <strong>
                    {client?.name || mission?.client_display_name || "Client"} · {owner ? workerName(owner) : "Intervenant"}
                  </strong>
                  <small>
                    {frDate(mission?.starts_at || report.submitted_at, true)} · {report.actual_hours} h · {labels[report.status] || report.status}
                  </small>
                </div>
                <button
                  type="button"
                  className="secondary compact"
                  onClick={() => openReport(report)}
                >
                  Consulter
                </button>
              </div>
            );
          })}
          {!reportHistory.length && (
            <p className="empty">Aucun compte rendu archivé.</p>
          )}
        </div>
      </details>
      {selectedRequest && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Demande client</span>
                <h2>
                  {
                    data.clients.find((c) => c.id === selectedRequest.client_id)
                      ?.name
                  }
                </h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedRequest(null)}
              >
                ×
              </button>
            </div>
            <div className="detail-grid">
              <div>
                <span>Début demandé</span>
                <strong>
                  {frDate(selectedRequest.desired_starts_at, true)}
                </strong>
              </div>
              <div>
                <span>Fin demandée</span>
                <strong>{frDate(selectedRequest.desired_ends_at, true)}</strong>
              </div>
              <div>
                <span>Adresse</span>
                <strong>{selectedRequest.location}</strong>
              </div>
              <div>
                <span>Bien</span>
                <strong>
                  {data.properties.find(
                    (p) => p.id === selectedRequest.property_id,
                  )?.name || "Client entier"}
                </strong>
              </div>
            </div>
            {selectedTeamReports.length > 1 && (
              <div className="detail-note team-report-summary">
                <strong>Temps déclaré pour chaque membre du binôme</strong>
                <div className="team-report-hours">
                  {selectedTeamReports.map((report) => {
                    const member = data.workers.find(
                      (candidate) => candidate.id === report.worker_id,
                    );
                    return (
                      <div key={report.id}>
                        <span>{member ? workerName(member) : "Intervenant"}</span>
                        <strong>{report.actual_hours} h</strong>
                      </div>
                    );
                  })}
                </div>
                <small>
                  Le temps client de la mission n’est pas multiplié : ces lignes
                  servent au suivi individuel et aux coûts réels.
                </small>
              </div>
            )}
            {hourDiscrepancies.length > 0 && (
              <Notice tone="error">
                <strong>Écart de déclaration à contrôler.</strong>{" "}
                {hourDiscrepancies
                  .map(({ workerId, entries }) => {
                    const member = data.workers.find(
                      (candidate) => candidate.id === workerId,
                    );
                    return `${member ? workerName(member) : "Intervenant"} : ${entries
                      .map((entry) => {
                        const author = data.workers.find(
                          (candidate) =>
                            candidate.id === entry.submitted_by_worker_id,
                        );
                        return `${entry.actual_hours} h par ${author ? workerName(author) : "un membre"}`;
                      })
                      .join(" / ")}`;
                  })
                  .join(" · ")}
              </Notice>
            )}
            <div className="detail-note">
              <strong>Observations du client</strong>
              <p>{selectedRequest.client_notes || "Aucune observation."}</p>
            </div>
            <fieldset className="mission-service-picker request-review-service-picker">
              <legend>Composition à appliquer à la mission</legend>
              <p>
                Contrôlez le tarif du bien et chaque supplément demandé avant
                de créer la mission.
              </p>
              <div className="mission-service-options">
                {requestServices.map((service) => (
                  <label
                    key={service.serviceKey}
                    className="mission-service-option"
                  >
                    <input
                      type="checkbox"
                      checked={serviceChoices.includes(service.serviceKey)}
                      onChange={(event) =>
                        setServiceChoices((current) =>
                          event.target.checked
                            ? [...current, service.serviceKey]
                            : current.filter(
                                (key) => key !== service.serviceKey,
                              ),
                        )
                      }
                    />
                    <span>
                      <strong>{service.name}</strong>
                      <small>
                        {service.scopeLabel} · {money(service.unit_price_ex_vat)} HT
                      </small>
                    </span>
                  </label>
                ))}
              </div>
              <small data-testid="admin-request-service-summary">
                {serviceChoices.length
                  ? `${serviceChoices.length} prestation(s) · ${money(
                      requestServices
                        .filter((service) =>
                          serviceChoices.includes(service.serviceKey),
                        )
                        .reduce(
                          (sum, service) =>
                            sum + Number(service.unit_price_ex_vat || 0),
                          0,
                        ),
                    )} HT`
                  : "Aucune prestation sélectionnée"}
              </small>
            </fieldset>
            {requestProposal && (
              <Notice
                tone={
                  requestProposal.status === "accepted" ? "success" : "info"
                }
              >
                Proposition « {requestProposal.name} » :{" "}
                {labels[requestProposal.status] || requestProposal.status}.
                {requestProposal.status === "pending" &&
                  " La mission restera en attente jusqu’à la réponse du client."}
                {requestProposal.status === "accepted" &&
                  " La prestation est maintenant disponible pour cette mission."}
                {requestProposal.status === "changes_requested" &&
                  " Le client souhaite recevoir une nouvelle proposition : la discussion reste ouverte."}
                {requestProposal.status === "rejected" &&
                  " Le client ne souhaite pas poursuivre cette proposition."}
                {requestProposal.client_response_reason && (
                  <small>
                    Motif transmis par le client : {requestProposal.client_response_reason}
                  </small>
                )}
              </Notice>
            )}
            {(!requestProposal || requestProposal.status === "changes_requested") && !showNewService && (
              <button
                type="button"
                className="secondary"
                onClick={() => setShowNewService(true)}
              >
                {requestProposal?.status === "changes_requested"
                  ? "Préparer une nouvelle proposition"
                  : "Proposer une nouvelle prestation au client"}
              </button>
            )}
            {showNewService && (
              <form
                className="form-grid service-proposal-form"
                onSubmit={proposeService}
              >
                <h3 className="wide">Nouvelle prestation à faire valider</h3>
                <label>
                  Nom de la prestation
                  <input
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Facturation
                  <select
                    value={newServiceMode}
                    onChange={(e) => setNewServiceMode(e.target.value)}
                  >
                    <option value="fixed">Forfait</option>
                    <option value="hourly">Horaire</option>
                    <option value="per_unit">À l’unité</option>
                    <option value="monthly">Mensuel</option>
                  </select>
                </label>
                <label>
                  Prix HT
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newServicePrice}
                    onChange={(e) => setNewServicePrice(e.target.value)}
                    required
                  />
                </label>
                <label>
                  TVA (%)
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={newServiceVat}
                    onChange={(e) => setNewServiceVat(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Durée indicative (h)
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={newServiceDuration}
                    onChange={(e) => setNewServiceDuration(e.target.value)}
                  />
                </label>
                <label className="wide">
                  Précisions visibles par le client
                  <textarea
                    value={newServiceNotes}
                    onChange={(e) => setNewServiceNotes(e.target.value)}
                  />
                </label>
                <div className="detail-actions wide">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setShowNewService(false)}
                  >
                    Annuler
                  </button>
                  <button disabled={!!busy}>Envoyer pour validation</button>
                </div>
              </form>
            )}
            <div className="detail-actions">
              <button
                className="danger"
                disabled={!!busy}
                onClick={() => decideRequest(selectedRequest, false)}
              >
                Refuser
              </button>
              <button
                className="success"
                disabled={!!busy}
                onClick={() => decideRequest(selectedRequest, true)}
              >
                Valider et créer la mission
              </button>
            </div>
          </section>
        </div>
      )}
      {selectedReport && (
        <div className="portal-overlay" role="dialog" aria-modal="true">
          <section className="portal-detail">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Compte rendu terrain</span>
                <h2>
                  {reportMission?.client?.name ||
                    reportMission?.client_display_name ||
                    "Mission"}
                </h2>
              </div>
              <button
                className="icon-close"
                onClick={() => setSelectedReport(null)}
              >
                ×
              </button>
            </div>
            <div className="detail-grid">
              <div>
                <span>Sous-traitant</span>
                <strong>
                  {reportWorker ? workerName(reportWorker) : "Intervenant"}
                </strong>
              </div>
              <div>
                <span>Mission</span>
                <strong>
                  {reportMission
                    ? frDate(reportMission.starts_at, true)
                    : "Non retrouvée"}
                </strong>
              </div>
              <div>
                <span>Heures réalisées</span>
                <strong>{selectedReport.actual_hours} h</strong>
              </div>
              <div>
                <span>Résultat</span>
                <strong>
                  {labels[selectedReport.result] || selectedReport.result}
                </strong>
              </div>
              <div>
                <span>État du compte rendu</span>
                <strong>{labels[selectedReport.status] || selectedReport.status}</strong>
              </div>
            </div>
            <div className="detail-note">
              <strong>Observations internes</strong>
              <p>{selectedReport.observations || "Aucune observation."}</p>
            </div>
            <div className="detail-note">
              <strong>Message prévu pour le client</strong>
              <textarea
                aria-label="Message final destiné au client"
                value={reportClientMessage}
                onChange={(e) => setReportClientMessage(e.target.value)}
                placeholder="Compte rendu synthétique communiqué au client"
                readOnly={selectedReport.status !== "pending"}
              />
            </div>
            <div className="detail-note">
              <strong>Actions réalisées ou à prévoir</strong>
              <textarea
                aria-label="Actions réalisées ou à prévoir"
                value={reportNextActions}
                onChange={(e) => setReportNextActions(e.target.value)}
                placeholder="Action menée, anomalie à traiter, prochaine étape…"
                readOnly={selectedReport.status !== "pending"}
              />
            </div>
            <div className="detail-note">
              <strong>Notes internes administrateur</strong>
              <textarea
                aria-label="Notes internes administrateur"
                value={reportAdminNotes}
                onChange={(e) => setReportAdminNotes(e.target.value)}
                placeholder="Ces notes ne sont pas visibles par le client."
                readOnly={selectedReport.status !== "pending"}
              />
            </div>
            <div className="detail-note">
              <strong>Photos et pièces jointes</strong>
              {reportAttachments.length ? (
                <div className="attachment-list">
                  {reportAttachments.map((item) => (
                    <button
                      className="secondary"
                      key={item.id}
                      onClick={() => openReportAttachment(item)}
                    >
                      {item.mime_type.startsWith("image/") ? "📷" : "📄"}{" "}
                      {item.original_name}
                    </button>
                  ))}
                </div>
              ) : (
                <p>Aucune pièce jointe enregistrée sur ce compte rendu.</p>
              )}
            </div>
            <div className="detail-note" data-testid="admin-report-activity-breakdown">
              <strong>Répartition du temps par étape</strong>
              {reportActivityEntries.length ? (
                <div className="activity-review-list">
                  {reportActivityEntries.map((entry) => (
                    <div className="activity-review-row" key={entry.id}>
                      <span>{entry.activity_label}</span>
                      <strong>{(Number(entry.minutes) / 60).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong>
                      {entry.notes && <small>{entry.notes}</small>}
                    </div>
                  ))}
                  <small>
                    Total détaillé : {(reportActivityEntries.reduce((sum, entry) => sum + Number(entry.minutes), 0) / 60).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h sur {selectedReportDeclaredHours.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h humaines déclarées. Ces données ne modifieront aucun tarif sans validation humaine.
                  </small>
                </div>
              ) : (
                <p>Aucune répartition détaillée : seules les heures globales sont utilisables.</p>
              )}
            </div>
            <div className="detail-note">
              <strong>Produits et kits déclarés par le terrain</strong>
              {reportStockUsages.length ? (
                <div className="stock-review-list">
                  {reportStockUsages.map((usage) => {
                    const item = (data.inventoryItems || []).find(
                        (candidate) => candidate.id === usage.item_id,
                      ),
                      decision = reportStockDecisions[usage.id];
                    return (
                      <div className="stock-review-row" key={usage.id}>
                        <div>
                          <strong>{item?.name || "Article de stock"}</strong>
                          <small>
                            Quantité déclarée : {usage.quantity} {item?.unit || "unité"}
                          </small>
                          {usage.notes && <p>{usage.notes}</p>}
                        </div>
                        {usage.billing_status === "pending_review" && selectedReport.status === "pending" ? (
                          <div className="form-grid compact-grid">
                            <label>
                              Décision administrateur
                              <select
                                value={decision?.treatment || ""}
                                onChange={(event) =>
                                  setReportStockDecisions((current) => ({
                                    ...current,
                                    [usage.id]: {
                                      ...(current[usage.id] || {
                                        price: "",
                                        vat: String(
                                          data.organization?.default_vat_rate || 20,
                                        ),
                                        notes: "",
                                      }),
                                      treatment: event.target.value as
                                        | ""
                                        | "billable"
                                        | "complimentary",
                                    },
                                  }))
                                }
                              >
                                <option value="">À décider</option>
                                <option value="billable">À facturer</option>
                                <option value="complimentary">Non facturé / geste commercial</option>
                              </select>
                            </label>
                            {decision?.treatment === "billable" && (
                              <>
                                <label>
                                  Prix HT
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={decision.price}
                                    onChange={(event) =>
                                      setReportStockDecisions((current) => ({
                                        ...current,
                                        [usage.id]: {
                                          ...current[usage.id],
                                          price: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </label>
                                <label>
                                  TVA (%)
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={decision.vat}
                                    onChange={(event) =>
                                      setReportStockDecisions((current) => ({
                                        ...current,
                                        [usage.id]: {
                                          ...current[usage.id],
                                          vat: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </label>
                              </>
                            )}
                            <label className="wide">
                              Note interne
                              <input
                                value={decision?.notes || ""}
                                onChange={(event) =>
                                  setReportStockDecisions((current) => ({
                                    ...current,
                                    [usage.id]: {
                                      ...(current[usage.id] || {
                                        treatment: "",
                                        price: "",
                                        vat: String(
                                          data.organization?.default_vat_rate || 20,
                                        ),
                                      }),
                                      notes: event.target.value,
                                    },
                                  }))
                                }
                              />
                            </label>
                          </div>
                        ) : (
                          <Notice tone="info">
                            {usage.billing_status === "ready"
                              ? `À facturer · ${money(usage.unit_price_ex_vat || 0)} HT`
                              : "Non facturé / geste commercial"}
                          </Notice>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>Aucun produit déclaré sur cette mission.</p>
              )}
            </div>
            {selectedReport.status === "pending" && (
              <section className="mission-billing-choice">
                <h3>Destination de la facturation</h3>
                <p className="section-intro">
                  Ce choix est propre à cette mission et remplace, si nécessaire,
                  le réglage habituel du client.
                </p>
                <div className="choice-grid two-columns" role="radiogroup" aria-label="Destination de la facturation">
                  <label className={`choice-card ${reportInvoiceStrategy === "grouped" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="report-invoice-strategy"
                      checked={reportInvoiceStrategy === "grouped"}
                      onChange={() => setReportInvoiceStrategy("grouped")}
                    />
                    <strong>Facture globale du client</strong>
                    <small>La mission rejoindra le brouillon regroupé de la période.</small>
                  </label>
                  <label className={`choice-card ${reportInvoiceStrategy === "individual" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="report-invoice-strategy"
                      checked={reportInvoiceStrategy === "individual"}
                      onChange={() => setReportInvoiceStrategy("individual")}
                    />
                    <strong>Facture indépendante</strong>
                    <small>La mission sera préparée dans un brouillon distinct.</small>
                  </label>
                </div>
              </section>
            )}
            {selectedReport.status === "pending" ? (
              <div className="detail-actions">
                <button
                  className="danger"
                  disabled={!!busy}
                  onClick={() => decideReport(selectedReport, false)}
                >
                  Refuser / demander correction
                </button>
                <button
                  className="success"
                  disabled={!!busy}
                  onClick={() => decideReport(selectedReport, true)}
                >
                  Valider la fin et facturer
                </button>
              </div>
            ) : (
              <div className="detail-actions">
                <button className="secondary" onClick={() => setSelectedReport(null)}>
                  Fermer l’historique
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
    </>
  );
}
function DecisionButtons({
  table,
  id,
  profile,
  reload,
}: {
  table: "time_entries" | "leave_requests" | "mileage_entries";
  id: string;
  profile: Profile;
  reload: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function decide(status: "approved" | "rejected") {
    setBusy(true);
    const payload: Record<string, string> = { status };
    if (table !== "mileage_entries") {
      payload.reviewed_by = profile.id;
      payload.reviewed_at = new Date().toISOString();
    }
    const { error } = await supabase!.from(table).update(payload).eq("id", id);
    setBusy(false);
    if (error) alert(error.message);
    else reload();
  }
  return (
    <div className="row-actions">
      <button
        className="success compact"
        disabled={busy}
        onClick={() => decide("approved")}
      >
        Valider
      </button>
      <button
        className="danger compact"
        disabled={busy}
        onClick={() => decide("rejected")}
      >
        Refuser
      </button>
    </div>
  );
}
function TimeTracking({
  profile,
  worker,
  workers,
  items,
  reload,
}: {
  profile: Profile;
  worker: Worker | null;
  workers: Worker[];
  items: TimeEntry[];
  reload: () => void;
}) {
  const [start, setStart] = useState(""),
    [end, setEnd] = useState(""),
    [pause, setPause] = useState(0),
    [message, setMessage] = useState("");
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!worker) return;
    const s = new Date(start),
      f = new Date(end),
      mins = Math.round((f.getTime() - s.getTime()) / 60000) - pause;
    if (mins <= 0)
      return setMessage(
        "La fin doit être postérieure au début, pause comprise.",
      );
    const { error } = await supabase!.from("time_entries").insert({
      organization_id: profile.organization_id,
      worker_id: worker.id,
      work_date: start.slice(0, 10),
      started_at: start.slice(11) + ":00",
      ended_at: end.slice(11) + ":00",
      started_at_ts: s.toISOString(),
      ended_at_ts: f.toISOString(),
      break_minutes: pause,
      worked_minutes: mins,
      status: "pending",
    });
    setMessage(error ? error.message : "Pointage envoyé pour validation.");
    if (!error) {
      setStart("");
      setEnd("");
      reload();
    }
  }
  return (
    <>
      <h1>Pointage</h1>
      {worker?.kind === "employee" && (
        <section className="panel">
          <h2>Ajouter une plage</h2>
          <p className="section-intro">
            Les services de nuit et plusieurs plages dans une journée sont
            acceptés.
          </p>
          <form className="form-grid" onSubmit={save}>
            <label>
              Début
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </label>
            <label>
              Fin
              <input
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                required
              />
            </label>
            <label>
              Pause (minutes)
              <input
                type="number"
                min="0"
                value={pause}
                onChange={(e) => setPause(+e.target.value)}
              />
            </label>
            <button>Envoyer le pointage</button>
          </form>
          {message && (
            <Notice tone={message.startsWith("Pointage") ? "success" : "error"}>
              {message}
            </Notice>
          )}
        </section>
      )}
      <Records
        title="Historique"
        empty="Aucun pointage."
        rows={items.map((t) => {
          const owner = workers.find((w) => w.id === t.worker_id);
          return (
            <article key={t.id}>
              <div>
                <strong>
                  {profile.role === "admin" && owner
                    ? `${workerName(owner)} · `
                    : ""}
                  {frDate(t.started_at_ts || t.work_date, true)}
                </strong>
                <small>
                  {hours(t.worked_minutes)} · pause {t.break_minutes} min
                </small>
              </div>
              <div className="row-actions">
                <Status value={t.status} />
                {profile.role === "admin" && t.status === "pending" && (
                  <DecisionButtons
                    table="time_entries"
                    id={t.id}
                    profile={profile}
                    reload={reload}
                  />
                )}
              </div>
            </article>
          );
        })}
      />
    </>
  );
}
function LeavePage({
  profile,
  worker,
  workers,
  items,
  reload,
}: {
  profile: Profile;
  worker: Worker | null;
  workers: Worker[];
  items: Leave[];
  reload: () => void;
}) {
  const [from, setFrom] = useState(""),
    [to, setTo] = useState(""),
    [reason, setReason] = useState(""),
    [message, setMessage] = useState("");
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!worker) return;
    const { error } = await supabase!.from("leave_requests").insert({
      organization_id: profile.organization_id,
      worker_id: worker.id,
      starts_on: from,
      ends_on: to,
      reason: reason || null,
      status: "pending",
    });
    setMessage(error ? error.message : "Demande de congé envoyée.");
    if (!error) {
      setFrom("");
      setTo("");
      setReason("");
      reload();
    }
  }
  return (
    <>
      <h1>Congés</h1>
      {worker?.kind === "employee" && (
        <section className="panel">
          <h2>Nouvelle demande</h2>
          <form className="form-grid" onSubmit={save}>
            <label>
              Premier jour
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
              />
            </label>
            <label>
              Dernier jour
              <input
                type="date"
                min={from}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
              />
            </label>
            <label className="wide">
              Précision
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <button>Envoyer la demande</button>
          </form>
          {message && (
            <Notice tone={message.startsWith("Demande") ? "success" : "error"}>
              {message}
            </Notice>
          )}
        </section>
      )}
      <Records
        title="Demandes"
        empty="Aucune demande."
        rows={items.map((l) => {
          const owner = workers.find((w) => w.id === l.worker_id);
          return (
            <article key={l.id}>
              <div>
                <strong>
                  {profile.role === "admin" && owner
                    ? `${workerName(owner)} · `
                    : ""}
                  {frDate(l.starts_on)} → {frDate(l.ends_on)}
                </strong>
                <small>{l.reason || "Sans précision"}</small>
              </div>
              <div className="row-actions">
                <Status value={l.status} />
                {profile.role === "admin" && l.status === "pending" && (
                  <DecisionButtons
                    table="leave_requests"
                    id={l.id}
                    profile={profile}
                    reload={reload}
                  />
                )}
              </div>
            </article>
          );
        })}
      />
    </>
  );
}
function MileagePage({
  profile,
  worker,
  workers,
  items,
  data,
  reload,
}: {
  profile: Profile;
  worker: Worker | null;
  workers: Worker[];
  items: Mileage[];
  data: DataState;
  reload: () => void;
}) {
  const [date, setDate] = useState(parisDay(new Date())),
    [km, setKm] = useState(""),
    [vehicleId, setVehicleId] = useState(""),
    [reason, setReason] = useState(""),
    [evidence, setEvidence] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const approvedVehicles = (data.vehicles || []).filter(
    (vehicle) =>
      vehicle.worker_id === worker?.id &&
      vehicle.active &&
      vehicle.document_status === "approved",
  );
  const mileageMissionLabel = (mission: Mission) => {
      const client = data.clients.find((item) => item.id === mission.client_id);
      return `${new Date(mission.starts_at).toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      })} · ${client?.name || mission.client_display_name || "Client"}`;
    };
  async function saveCorrection(e: React.FormEvent) {
    e.preventDefault();
    if (!worker) return;
    if (!vehicleId || !evidence || reason.trim().length < 10)
      return setMessage(
        "Une correction exceptionnelle exige un véhicule validé, un motif détaillé et une preuve.",
      );
    setBusy(true);
    const cleanName = evidence.name.replace(/[^a-zA-Z0-9._-]/g, "-"),
      evidencePath = `${profile.organization_id}/${profile.id}/mileage-corrections/${Date.now()}-${cleanName}`,
      uploaded = await supabase!.storage
        .from("private-documents")
        .upload(evidencePath, evidence, { upsert: false });
    if (uploaded.error) {
      setBusy(false);
      setMessage(uploaded.error.message);
      return;
    }
    const { error } = await supabase!.from("mileage_entries").insert({
      organization_id: profile.organization_id,
      worker_id: worker.id,
      vehicle_id: vehicleId,
      travel_date: date,
      kilometers: +km,
      reason: `Correction exceptionnelle : ${reason.trim()}`,
      entry_source: "exceptional_correction",
      correction_reason: reason.trim(),
      correction_evidence_path: evidencePath,
      correction_requested_by: profile.id,
      status: "pending",
    });
    setBusy(false);
    setMessage(error ? error.message : "Correction exceptionnelle envoyée pour validation.");
    if (!error) {
      setKm("");
      setReason("");
      setEvidence(null);
      reload();
    }
  }
  return (
    <>
      <h1>Indemnités kilométriques</h1>
      {worker &&
        (worker.mileage_enabled ? (
          <section className="panel">
            <VehicleManager
              profile={profile}
              data={data}
              reload={reload}
              selfWorker={worker}
            />
            <RouteMileagePlanner
              profile={profile}
              data={data}
              reload={reload}
              workerId={worker.id}
              ownerProfileId={null}
            />
            <details>
              <summary>Signaler exceptionnellement une distance différente</summary>
              <div className="details-content">
                <p className="section-intro">
                  À utiliser uniquement si le trajet calculé ne correspond pas au
                  déplacement réellement effectué. Un justificatif est obligatoire.
                </p>
                <form className="form-grid" onSubmit={saveCorrection}>
                  <label>
                    Véhicule validé
                    <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required>
                      <option value="">Choisir</option>
                      {approvedVehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.brand} {vehicle.model || ""} · {vehicle.fiscal_power} CV
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Date
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                  </label>
                  <label>
                    Kilomètres réellement parcourus
                    <input type="number" min="0.1" step="0.1" value={km} onChange={(e) => setKm(e.target.value)} required />
                  </label>
                  <label className="wide">
                    Motif détaillé
                    <input value={reason} onChange={(e) => setReason(e.target.value)} minLength={10} required />
                  </label>
                  <label className="wide">
                    Preuve (photo ou PDF)
                    <input type="file" accept="image/*,application/pdf" onChange={(e) => setEvidence(e.target.files?.[0] || null)} required />
                  </label>
                  <button disabled={busy || !vehicleId}>{busy ? "Envoi…" : "Soumettre la correction"}</button>
                </form>
              </div>
            </details>
            {!approvedVehicles.length && (
              <Notice>
                La saisie reste verrouillée jusqu’à la validation de votre carte
                grise par l’administration.
              </Notice>
            )}
            {message && (
              <Notice
                tone={message.startsWith("Correction exceptionnelle envoyée") ? "success" : "error"}
              >
                {message}
              </Notice>
            )}
          </section>
        ) : (
          <Notice>Les IK ne sont pas activées pour votre contrat.</Notice>
        ))}
      <Records
        title="Historique des IK"
        empty="Aucune IK."
        rows={items.map((i) => {
          const owner = workers.find((w) => w.id === i.worker_id);
          return (
            <article key={i.id}>
              <div>
                <strong>
                  {profile.role === "admin" && owner
                    ? `${workerName(owner)} · `
                    : ""}
                  {frDate(i.travel_date)} · {i.kilometers} km
                </strong>
                <small>
                  {i.reason}
                  {i.entry_source === "route_calculation"
                    ? " · distance issue de la tournée"
                    : i.entry_source === "exceptional_correction"
                      ? " · correction exceptionnelle justifiée"
                      : ""}
                  {i.calculated_amount != null
                    ? " · " +
                      money(i.calculated_amount) +
                      " · cumul annuel " +
                      (i.annual_km_after || 0) +
                      " km"
                    : " · montant non calculé"}
                  {i.mission_id
                    ? " · " +
                      mileageMissionLabel(
                        data.missions.find(
                          (mission) => mission.id === i.mission_id,
                        ) || ({
                          starts_at: i.travel_date,
                          client_id: "",
                        } as Mission),
                      )
                    : i.route_calculation_id
                      ? " · tournée journalière"
                      : " · déplacement exceptionnel"}
                </small>
              </div>
              <div className="row-actions">
                <Status value={i.status} />
                {profile.role === "admin" && i.status === "pending" && (
                  <DecisionButtons
                    table="mileage_entries"
                    id={i.id}
                    profile={profile}
                    reload={reload}
                  />
                )}
              </div>
            </article>
          );
        })}
      />
    </>
  );
}
function Payroll({
  worker,
  docs,
}: {
  worker: Worker | null;
  docs: DocumentRow[];
}) {
  return (
    <>
      <h1>Paie</h1>
      {worker?.kind === "employee" && (
        <div className="stats">
          <article>
            <span>Objectif contractuel</span>
            <strong>{worker.monthly_hours} h</strong>
          </article>
          <article>
            <span>Tickets restaurant</span>
            <strong>{worker.ticket_count || 0}</strong>
            <small>
              {worker.ticket_value
                ? `${worker.ticket_value} € par ticket`
                : "Valeur non renseignée"}
            </small>
          </article>
          <article>
            <span>IK</span>
            <strong>{worker.mileage_enabled ? "Activées" : "Non"}</strong>
          </article>
        </div>
      )}
      <Records
        title="Fiches de paie"
        empty="Aucune fiche de paie disponible."
        rows={docs
          .filter((d) => d.kind === "payslip")
          .map((d) => (
            <article key={d.id}>
              <div>
                <strong>{d.original_name}</strong>
                <small>{d.period || "Période non renseignée"}</small>
              </div>
              <span className="status">Document privé</span>
            </article>
          ))}
      />
    </>
  );
}
function Workspace({ session }: { session: Session }) {
  const [profile, setProfile] = useState<Profile | null>(null),
    [data, setData] = useState(emptyData),
    [tab, setTab] = useState<Tab>("dashboard"),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const worker = useMemo(
    () => data.workers.find((w) => w.profile_id === session.user.id) || null,
    [data.workers, session.user.id],
  );
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const p = await supabase!
      .from("profiles")
      .select(
        "id,role,display_name,organization_id,email,phone,permission_set_id,access_status,dashboard_preferences",
      )
      .eq("id", session.user.id)
      .single();
    if (p.error) {
      setError(`Profil inaccessible : ${p.error.message}`);
      return setLoading(false);
    }
    setProfile(p.data as Profile);
    const q = await Promise.all([
      supabase!
        .from("missions")
        .select(
          "id,client_id,starts_at,ends_at,status,location,planned_hours,amount_ex_vat,billing_mode,billing_rate,billing_quantity,notes,is_test,client:clients(name)",
        )
        .order("starts_at"),
      supabase!
        .from("clients")
        .select("id,name,profile_id")
        .eq("active", true)
        .order("name"),
      supabase!
        .from("mission_assignments")
        .select("id,mission_id,worker_id,assigned_hours,source,status"),
      supabase!
        .from("workers")
        .select(
          "id,profile_id,kind,first_name,last_name,monthly_hours,daily_hours,net_salary,ticket_count,ticket_value,mileage_enabled,active,pricing_mode,hourly_rate,fixed_fee,monthly_fee",
        )
        .eq("active", true),
      supabase!
        .from("time_entries")
        .select("*")
        .order("work_date", { ascending: false })
        .limit(100),
      supabase!
        .from("leave_requests")
        .select("*")
        .order("starts_on", { ascending: false })
        .limit(100),
      supabase!
        .from("mileage_entries")
        .select("*")
        .order("travel_date", { ascending: false })
        .limit(100),
      supabase!
        .from("documents")
        .select("id,kind,period,original_name")
        .order("created_at", { ascending: false }),
      supabase!
        .from("client_services")
        .select(
          "id,client_id,name,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours",
        )
        .eq("active", true)
        .order("name"),
      supabase!
        .from("mission_requests")
        .select(
          "id,client_id,service_id,desired_starts_at,desired_ends_at,location,client_notes,status,created_at",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("mission_reports")
        .select(
          "id,mission_id,worker_id,result,actual_hours,observations,client_note,status,submitted_at",
        )
        .order("submitted_at", { ascending: false }),
    ]);
    const err = q.find((x) => x.error)?.error;
    if (err)
      setError(`Certaines données ne sont pas accessibles : ${err.message}`);
    setData({
      missions: (q[0].data || []) as unknown as Mission[],
      clients: (q[1].data || []) as Client[],
      assignments: (q[2].data || []) as Assignment[],
      workers: (q[3].data || []) as Worker[],
      times: (q[4].data || []) as TimeEntry[],
      leaves: (q[5].data || []) as Leave[],
      mileages: (q[6].data || []) as Mileage[],
      documents: (q[7].data || []) as DocumentRow[],
      services: (q[8].data || []) as ClientService[],
      requests: (q[9].data || []) as MissionRequest[],
      reports: (q[10].data || []) as MissionReport[],
      invoices: [],
      quotes: [],
      expenses: [],
      organization: null,
      cashBalances: [],
      availability: [],
      requirements: [],
      contracts: [],
      properties: [],
      propertyServices: [],
      preferredWorkers: [],
    });
    setLoading(false);
  }, [session.user.id]);
  useEffect(() => {
    load();
  }, [load]);
  if (loading) return <main className="center">Chargement sécurisé…</main>;
  if (!profile)
    return (
      <main className="center">
        <Notice tone="error">{error || "Profil introuvable."}</Notice>
      </main>
    );
  const nav: { id: Tab; label: string; roles?: Role[] }[] = [
    { id: "dashboard", label: "Tableau de bord" },
    { id: "missions", label: "Missions" },
    { id: "team", label: "Équipe", roles: ["admin"] },
    {
      id: "time",
      label: profile.role === "employee" ? "Mon pointage" : "Pointages",
      roles: ["admin", "employee"],
    },
    {
      id: "leave",
      label: profile.role === "employee" ? "Mes congés" : "Congés",
      roles: ["admin", "employee"],
    },
    {
      id: "mileage",
      label: profile.role === "employee" ? "Mes IK" : "IK",
      roles: ["admin", "employee"],
    },
    ...(hasDeploymentCapability(data, "worker_pricing_proposals") ||
    hasDeploymentCapability(data, "quote_sourcing")
      ? [
          {
            id: "proposals" as Tab,
            label: "Mes propositions",
            roles: ["contractor"] as Role[],
          },
        ]
      : []),
    { id: "payroll", label: "Paie", roles: ["admin", "employee"] },
    { id: "profile", label: "Mon profil" },
  ];
  return (
    <div className="shell">
      <header>
        <div>
          <strong>ARD Manager</strong>
          <small>
            {profile.display_name} · {profile.role}
          </small>
        </div>
        <button className="ghost" onClick={() => void deactivateMobilePush().then(() => supabase!.auth.signOut())}>
          Déconnexion
        </button>
      </header>
      <nav>
        {nav
          .filter((n) => !n.roles || n.roles.includes(profile.role))
          .map((n) => (
            <button
              key={n.id}
              className={tab === n.id ? "active" : ""}
              onClick={() => setTab(n.id)}
            >
              {n.label}
            </button>
          ))}
      </nav>
      <main className="content">
        {error && <Notice tone="error">{error}</Notice>}
        {profile.role === "admin" &&
          (data.unavailableCapabilities || []).length > 0 && (
            <Notice>
              <strong>Fonctions préparées mais non activées sur cette base :</strong>{" "}
              {(data.unavailableCapabilities || [])
                .map((capability) => deploymentCapabilityLabels[capability])
                .join(" ; ")}.
              Les actions correspondantes restent désactivées jusqu’à leur migration contrôlée.
            </Notice>
          )}
        {tab === "dashboard" && <Dashboard data={data} worker={worker} />}{" "}
        {tab === "missions" &&
          (profile.role === "admin" ? (
            <>
              <AdminWorkflow profile={profile} data={data} reload={load} />
              <MissionAdmin profile={profile} data={data} reload={load} />
            </>
          ) : profile.role === "client" ? (
            <ClientMissions profile={profile} data={data} reload={load} />
          ) : (
            <WorkerMissions
              profile={profile}
              worker={worker}
              data={data}
              reload={load}
            />
          ))}{" "}
        {tab === "team" && <Team profile={profile} data={data} reload={load} />}{" "}
        {tab === "time" && (
          <TimeTracking
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.times}
            reload={load}
          />
        )}{" "}
        {tab === "leave" && (
          <LeavePage
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.leaves}
            reload={load}
          />
        )}{" "}
        {tab === "mileage" && (
          <MileagePage
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.mileages}
            data={data}
            reload={load}
          />
        )}{" "}
        {tab === "proposals" &&
          profile.role === "contractor" &&
          (hasDeploymentCapability(data, "worker_pricing_proposals") ||
            hasDeploymentCapability(data, "quote_sourcing")) && (
          <WorkerProposalPage profile={profile} worker={worker} data={data} reload={load} />
        )}{" "}
        {tab === "payroll" && <Payroll worker={worker} docs={data.documents} />}{" "}
        {tab === "profile" && (
          <>
            <h1>Mon profil</h1>
            <section className="panel definition">
              <div>
                <span>Nom</span>
                <strong>{profile.display_name}</strong>
              </div>
              <div>
                <span>Rôle</span>
                <strong>{profile.role}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{profile.email || "—"}</strong>
              </div>
              {worker && (
                <div>
                  <span>Journée habituelle</span>
                  <strong>{worker.daily_hours} h</strong>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function NotificationCenter({
  profile,
  items,
  reload,
  onOpen,
  mobilePush,
  onEnableMobilePush,
  onDisableMobilePush,
}: {
  profile: Profile;
  items: AppNotification[];
  reload: () => void;
  onOpen: (item: AppNotification) => void;
  mobilePush: MobilePushState;
  onEnableMobilePush: () => void;
  onDisableMobilePush: () => void;
}) {
  const [busy, setBusy] = useState(false),
    mine = items.filter((item) => item.recipient_profile_id === profile.id),
    unread = mine.filter((item) => !item.read_at);
  async function markRead(item: AppNotification) {
    if (!item.read_at) {
      setBusy(true);
      await supabase!
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", item.id);
      setBusy(false);
      reload();
    }
    onOpen(item);
  }
  async function markAllRead() {
    if (!unread.length) return;
    setBusy(true);
    await supabase!
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("recipient_profile_id", profile.id)
      .is("read_at", null);
    setBusy(false);
    reload();
  }
  return (
    <details className="notification-center">
      <summary aria-label={`${unread.length} notification(s) non lue(s)`}>
        <span aria-hidden="true">🔔</span>
        <span className="notification-label">Alertes</span>
        {unread.length > 0 && <b>{unread.length}</b>}
      </summary>
      <div className="notification-popover">
        <div className="notification-heading">
          <strong>Notifications</strong>
          <button
            type="button"
            className="ghost compact"
            disabled={busy || !unread.length}
            onClick={markAllRead}
          >
            Tout marquer comme lu
          </button>
        </div>
        {mobilePush.status !== "web" && <section className={`mobile-push-status status-${mobilePush.status}`}>
          <div>
            <strong>Notifications sur ce téléphone</strong>
            <small>{mobilePush.message}</small>
          </div>
          {mobilePush.status === "registered" ? (
            <button type="button" className="secondary compact" onClick={onDisableMobilePush}>Désactiver</button>
          ) : (
            <button
              type="button"
              className="create-button compact"
              disabled={mobilePush.status === "registering" || mobilePush.status === "unavailable"}
              onClick={onEnableMobilePush}
            >
              {mobilePush.status === "registering" ? "Activation…" : "Activer"}
            </button>
          )}
        </section>}
        <div className="notification-list">
          {mine.slice(0, 20).map((item) => (
            <button
              type="button"
              key={item.id}
              className={item.read_at ? "read" : "unread"}
              disabled={busy}
              onClick={() => markRead(item)}
            >
              <span>
                <strong>{item.title}</strong>
                {item.body && <small>{item.body}</small>}
              </span>
              <time>{frDate(item.created_at, true)}</time>
            </button>
          ))}
          {!mine.length && <p className="empty">Aucune notification.</p>}
        </div>
      </div>
    </details>
  );
}

function ShowcaseToolbar() {
  const [blockedAction, setBlockedAction] = useState("");
  useEffect(() => {
    const stopExternalNavigation = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      try {
        const target = new URL(anchor.href, location.href);
        if (target.protocol === "http:" || target.protocol === "https:") {
          if (target.origin !== location.origin) {
            event.preventDefault();
            event.stopPropagation();
            setBlockedAction("Action externe simulée : aucune donnée n’a quitté ARD Manager Showcase.");
            window.setTimeout(() => setBlockedAction(""), 3500);
          }
        }
      } catch {
        // Invalid links remain inert in the demonstration.
        event.preventDefault();
      }
    };
    document.addEventListener("click", stopExternalNavigation, true);
    return () => document.removeEventListener("click", stopExternalNavigation, true);
  }, []);
  const names: Record<ShowcaseRole, string> = {
    admin: "Administrateur — Romain Dreville",
    coach: "Coach — lecture seule",
    client: "Client conciergerie — Let Me Do",
    contractor: "Sous-traitante — Sophie Martin",
    employee: "Employé — Lucas Bernard",
  };
  const switchRole = (role: ShowcaseRole) => {
    setShowcaseRole(role);
    location.reload();
  };
  const reset = () => {
    resetShowcaseData();
    location.reload();
  };
  return (
    <section className="showcase-toolbar" aria-label="Contrôles de la démonstration">
      <div>
        <strong>ARD Manager Showcase</strong>
        <span>Données fictives temporaires · aucun envoi ni enregistrement Supabase</span>
      </div>
      <label>
        Voir l’application comme
        <select value={getShowcaseRole()} onChange={(event) => switchRole(event.target.value as ShowcaseRole)}>
          {Object.entries(names).map(([role, name]) => <option key={role} value={role}>{name}</option>)}
        </select>
      </label>
      <button type="button" className="ghost" onClick={reset}>Réinitialiser</button>
      <button type="button" className="ghost" onClick={() => (location.href = "/?showcase=1&screen=login")}>Connexion</button>
      <button type="button" className="ghost" onClick={() => {setShowcaseRole("admin");location.href="/?showcase=1&platform=1";}}>Superadmin</button>
      <button type="button" className="ghost" onClick={() => (location.href = "/")}>Quitter</button>
      {blockedAction && <div className="showcase-blocked-action" role="status">{blockedAction}</div>}
    </section>
  );
}

const defaultProcessNotificationPreferences: ProcessNotificationPreferenceSettings = {
  information: { internal: true, push: false, email: false, sms: false },
  alert: { internal: true, push: true, email: false, sms: false },
  quiet_hours: { enabled: false, start: "22:00", end: "07:00" },
};

function ProcessNotificationPreferencesPanel({
  profile,
  data,
  reload,
}: {
  profile: Profile;
  data: DataState;
  reload: () => void;
}) {
  const stored = (data.profiles || []).find((item) => item.id === profile.id)
      ?.process_notification_preferences,
    [form, setForm] = useState<ProcessNotificationPreferenceSettings>(
      stored || defaultProcessNotificationPreferences,
    ),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  useEffect(() => {
    setForm(stored || defaultProcessNotificationPreferences);
  }, [stored]);
  if (!hasDeploymentCapability(data, "process_notification_preferences"))
    return (
      <Notice tone="info">
        Les préférences de notification sont préparées dans la recette isolée,
        mais ne sont pas encore installées sur cet environnement.
      </Notice>
    );
  const toggle = (
    level: "information" | "alert",
    channel: keyof ProcessNotificationChannelSettings,
  ) =>
    setForm((current) => ({
      ...current,
      [level]: {
        ...current[level],
        [channel]: !current[level][channel],
      },
    }));
  async function save() {
    setBusy(true);
    setMessage("");
    const { error } = await supabase!.rpc(
      "update_my_process_notification_preferences",
      { p_preferences: form },
    );
    setBusy(false);
    if (error) return setMessage(error.message);
    setMessage(
      isShowcaseMode
        ? "Préférences enregistrées dans le Showcase, sans envoi externe."
        : "Préférences enregistrées pour ce compte.",
    );
    reload();
  }
  const channels: Array<{
    key: keyof ProcessNotificationChannelSettings;
    label: string;
    help: string;
  }> = [
    { key: "internal", label: "Dans ARD Manager", help: "Visible dans l’application." },
    { key: "push", label: "Notification push", help: "Préférence prête ; fournisseur non activé." },
    { key: "email", label: "E-mail", help: "Préférence prête ; fournisseur non activé." },
    { key: "sms", label: "SMS", help: "Préférence prête ; fournisseur non activé." },
  ];
  return (
    <section className="panel process-preferences-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Processus et communication</span>
          <h2>Mes préférences de notification</h2>
          <p>
            Ces choix préparent les canaux autorisés pour ce compte. Ils
            n’activent aucun fournisseur et ne prouvent aucun envoi.
          </p>
        </div>
      </div>
      <div className="process-preference-levels">
        {(["information", "alert"] as const).map((level) => (
          <fieldset key={level}>
            <legend>{level === "information" ? "Informations" : "Alertes"}</legend>
            {channels.map((channel) => (
              <label key={channel.key}>
                <input
                  type="checkbox"
                  checked={form[level][channel.key]}
                  onChange={() => toggle(level, channel.key)}
                />
                <span>
                  <strong>{channel.label}</strong>
                  <small>{channel.help}</small>
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <fieldset className="quiet-hours-settings">
        <legend>Plage silencieuse</legend>
        <label className="quiet-hours-toggle">
          <input
            type="checkbox"
            checked={form.quiet_hours.enabled}
            onChange={() =>
              setForm((current) => ({
                ...current,
                quiet_hours: {
                  ...current.quiet_hours,
                  enabled: !current.quiet_hours.enabled,
                },
              }))
            }
          />
          <span>
            <strong>Suspendre les canaux externes sur cette plage</strong>
            <small>Les actions requises restent visibles dans ARD Manager.</small>
          </span>
        </label>
        <div className="quiet-hours-times">
          <label>
            Début
            <input
              type="time"
              value={form.quiet_hours.start}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  quiet_hours: {
                    ...current.quiet_hours,
                    start: event.target.value,
                  },
                }))
              }
            />
          </label>
          <label>
            Fin
            <input
              type="time"
              value={form.quiet_hours.end}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  quiet_hours: {
                    ...current.quiet_hours,
                    end: event.target.value,
                  },
                }))
              }
            />
          </label>
        </div>
      </fieldset>
      {message && <Notice tone={/erreur|refus|invalide/i.test(message) ? "error" : "success"}>{message}</Notice>}
      <div className="detail-actions">
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? "Enregistrement…" : "Enregistrer mes préférences"}
        </button>
      </div>
    </section>
  );
}

function AppearanceSettings({
  value,
  onChange,
}: {
  value: AppearanceMode;
  onChange: (value: AppearanceMode) => void;
}) {
  return (
    <section className="panel appearance-settings">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Affichage</span>
          <h2>Thème de l’application</h2>
          <p>
            Choisissez la présentation la plus confortable. Le réglage est
            mémorisé sur cet appareil et ne change ni vos droits ni le contenu.
          </p>
        </div>
      </div>
      <div className="appearance-options" role="group" aria-label="Thème de l’application">
        <button
          type="button"
          className={value === "light" ? "active" : ""}
          aria-pressed={value === "light"}
          onClick={() => onChange("light")}
        >
          <span aria-hidden="true">☀</span>
          <strong>Mode clair</strong>
          <small>Fond lumineux et cartes blanches</small>
        </button>
        <button
          type="button"
          className={value === "dark" ? "active" : ""}
          aria-pressed={value === "dark"}
          onClick={() => onChange("dark")}
        >
          <span aria-hidden="true">◐</span>
          <strong>Mode sombre</strong>
          <small>Fond anthracite adapté au terrain</small>
        </button>
      </div>
    </section>
  );
}

const presentationProfiles: Array<{
  role: Exclude<ShowcaseRole, "admin" | "coach">;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    role: "client",
    label: "Espace client",
    description: "Demandes, biens, missions, documents et facturation côté client.",
    icon: "C",
  },
  {
    role: "contractor",
    label: "Espace sous-traitant",
    description: "Missions terrain, obligations, preuves, planning et facturation.",
    icon: "ST",
  },
  {
    role: "employee",
    label: "Espace employé",
    description: "Journée, missions, check-lists, photos et pointage.",
    icon: "E",
  },
];

function DemoPresentationLauncher({ compact = false }: { compact?: boolean }) {
  const openPresentation = (role: Exclude<ShowcaseRole, "admin" | "coach">) => {
    setShowcaseRole(role);
    location.assign("/?showcase=1");
  };

  return (
    <section className={`panel demo-presentation-settings${compact ? " compact" : ""}`} data-testid={compact ? "crm-demo-presentation" : "demo-presentation-settings"}>
      <div className="section-heading">
        <div>
          <span className="eyebrow">Démonstration commerciale</span>
          <h2>{compact ? "Préparer un rendez-vous avec la bonne interface" : "Présenter ARD Manager"}</h2>
          <p>
            Ouvrez l’interface adaptée à votre interlocuteur sur ce téléphone,
            cette tablette ou cet ordinateur. La démonstration utilise uniquement
            des données fictives et ne modifie jamais les dossiers réels.
          </p>
        </div>
        <span className="demo-presentation-badge">Données isolées</span>
      </div>
      <div className="demo-presentation-options" role="group" aria-label="Choisir le profil à présenter">
        {presentationProfiles.map((item) => (
          <button
            type="button"
            key={item.role}
            data-demo-role={item.role}
            onClick={() => openPresentation(item.role)}
          >
            <span className="demo-presentation-icon" aria-hidden="true">{item.icon}</span>
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
            <span className="demo-presentation-action" aria-hidden="true">Présenter →</span>
          </button>
        ))}
      </div>
      <p className="demo-presentation-note">
        Une barre « ARD Manager Showcase » signale toujours le mode fictif et permet
        de changer de profil, réinitialiser les données ou revenir à l’espace réel.
      </p>
    </section>
  );
}

function WorkspaceRich({ session }: { session: Session }) {
  const [profile, setProfile] = useState<Profile | null>(null),
    [organizationContexts, setOrganizationContexts] = useState<OrganizationContext[]>([]),
    [switchingOrganization, setSwitchingOrganization] = useState(false),
    [data, setData] = useState(emptyData),
    [tab, setTab] = useState<Tab>("dashboard"),
    [appearance, setAppearance] = useState<AppearanceMode>("light"),
    [mobileNavOpen, setMobileNavOpen] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [coachGuardMessage, setCoachGuardMessage] = useState(""),
    [createOpen, setCreateOpen] = useState(false),
    [feedbackOpen, setFeedbackOpen] = useState(false),
    [createKind, setCreateKind] = useState<CreateKind>("menu"),
    [missionCreateRequested, setMissionCreateRequested] = useState(false),
    [focusedMission, setFocusedMission] = useState<Mission | null>(null),
    [focusedReportMissionId, setFocusedReportMissionId] = useState<string | null>(null),
    [focusedWorkerId, setFocusedWorkerId] = useState<string | null>(null),
    [guidedQuestionnaireRequest, setGuidedQuestionnaireRequest] =
      useState<string | null>(null),
    [servicePricingRequest, setServicePricingRequest] =
      useState<BillingCatalogFocus | null>(null),
    [billingOrigin, setBillingOrigin] = useState<Tab | null>(null),
    [focusedInventoryItemId, setFocusedInventoryItemId] =
      useState<string | null>(null),
    [stockOpenedFromCatalog, setStockOpenedFromCatalog] = useState(false),
    [billingCatalogOpenRequest, setBillingCatalogOpenRequest] =
      useState<number | null>(null),
    [focusedBillingInvoiceId, setFocusedBillingInvoiceId] =
      useState<string | null>(null),
    [focusedSupplierInvoiceId, setFocusedSupplierInvoiceId] =
      useState<string | null>(null),
    [focusedExpenseId, setFocusedExpenseId] = useState<string | null>(null),
    [mobilePush, setMobilePush] = useState<MobilePushState>({
      status: "web",
      message: "Notifications internes actives dans le navigateur.",
    }),
    [pendingPushTarget, setPendingPushTarget] = useState<MobilePushTarget | null>(null);
  useEffect(() => {
    if (!profile) return;
    const stored = localStorage.getItem(`ard-manager-appearance:${profile.id}`);
    setAppearance(
      stored === "light" || stored === "dark"
        ? stored
        : profile.role === "contractor" || profile.role === "employee"
          ? "dark"
          : "light",
    );
  }, [profile?.id, profile?.role]);
  const changeAppearance = (value: AppearanceMode) => {
    setAppearance(value);
    if (profile)
      localStorage.setItem(`ard-manager-appearance:${profile.id}`, value);
  };
  const prepareTerrainContextExit = async (activeProfile: Profile) => {
    if (
      isShowcaseMode ||
      (activeProfile.role !== "employee" && activeProfile.role !== "contractor")
    )
      return true;
    try {
      const { createOfflineMissionRuntime, listBlockingOfflineMutations } = await import(
        "./offline-missions-runtime.ts"
      );
      const runtime = await createOfflineMissionRuntime(supabase!);
      const mutations = await runtime.store.listMutations();
      const unresolved = listBlockingOfflineMutations(mutations, activeProfile.id);
      if (unresolved.length) {
        setError(
          `Déconnexion suspendue : ${unresolved.length} saisie(s) terrain restent à synchroniser ou à contrôler. Reconnectez le réseau et synchronisez-les avant de quitter ce compte.`,
        );
        return false;
      }
      await runtime.purgeProfile(activeProfile.id);
      return true;
    } catch (purgeError) {
      setError(
        purgeError instanceof Error
          ? `Déconnexion suspendue : le stockage terrain local n’a pas pu être contrôlé (${purgeError.message}).`
          : "Déconnexion suspendue : le stockage terrain local n’a pas pu être contrôlé.",
      );
      return false;
    }
  };
  const signOutSecurely = async () => {
    if (profile && !(await prepareTerrainContextExit(profile))) return;
    await deactivateMobilePush();
    await supabase!.auth.signOut();
  };
  const worker = useMemo(
    () => data.workers.find((w) => w.profile_id === session.user.id) || null,
    [data.workers, session.user.id],
  );
  const load = useCallback(async () => {
    setError("");
    const context = await loadApplicationProfile(session),
      activeProfile = context.profile;
    if (!activeProfile) {
      setError(`Profil inaccessible : ${context.error || "contexte actif introuvable"}`);
      setLoading(false);
      return;
    }
    setProfile(activeProfile);
    setOrganizationContexts(await loadOrganizationContexts(activeProfile));
    if (activeProfile.role === "admin") {
      const refreshedAlerts = await supabase!.rpc("refresh_operational_alerts");
      if (refreshedAlerts.error)
        console.warn("Actualisation des alertes indisponible", refreshedAlerts.error);
    }
    const unavailableCapabilities = new Set<DeploymentCapability>();
    const missingDeploymentObject = (message: string) =>
      /does not exist|schema cache|could not find the table|could not find the function|relation|function/i.test(
        message,
      );
    const inventoryCatalogQuery = (async () => {
      if (activeProfile.role === "admin") {
        const extended = await supabase!
          .from("inventory_items")
          .select(
            "id,name,category,sku,unit,current_quantity,minimum_quantity,average_unit_cost,preferred_supplier,item_kind,supplier_id,supplier_product_url,sale_enabled,sale_price_ex_vat,sale_vat_rate,sale_label,purchase_package_quantity,purchase_package_unit,purchase_package_price_ex_vat,yield_material_quantity,yield_material_unit,yield_output_quantity,yield_output_unit,default_application_passes,loss_rate_percent,target_margin_rate,active",
          )
          .order("name");
        if (!extended.error) return extended;
        if (!/purchase_package_|yield_|default_application_passes|loss_rate_percent|target_margin_rate|column|schema cache/i.test(extended.error.message))
          return extended;
        return supabase!
          .from("inventory_items")
          .select(
            "id,name,category,sku,unit,current_quantity,minimum_quantity,average_unit_cost,preferred_supplier,item_kind,supplier_id,supplier_product_url,sale_enabled,sale_price_ex_vat,sale_vat_rate,sale_label,active",
          )
          .order("name");
      }
      if (activeProfile.role === "employee" || activeProfile.role === "contractor")
        return supabase!.rpc("get_worker_inventory_catalog");
      return { data: [], error: null };
    })();
    const dailyRouteTeamsQuery = (async () => {
      const current = await supabase!
        .from("daily_route_teams")
        .select("id,work_date,name,notes,leader_worker_id")
        .order("work_date", { ascending: false });
      if (!current.error) return current;
      if (!/leader_worker_id|column/i.test(current.error.message)) return current;
      return supabase!
        .from("daily_route_teams")
        .select("id,work_date,name,notes")
        .order("work_date", { ascending: false });
    })();
    const missionReportsQuery = (async () => {
      const current = await supabase!
        .from("mission_reports")
        .select(
          "id,mission_id,worker_id,result,actual_hours,observations,client_note,status,submitted_at,route_team_id,submitted_by_worker_id,team_report_batch_id",
        )
        .order("submitted_at", { ascending: false });
      if (!current.error) return current;
      if (
        !/route_team_id|submitted_by_worker_id|team_report_batch_id|column/i.test(
          current.error.message,
        )
      )
        return current;
      return supabase!
        .from("mission_reports")
        .select(
          "id,mission_id,worker_id,result,actual_hours,observations,client_note,status,submitted_at",
        )
        .order("submitted_at", { ascending: false });
    })();
    const missionReportActivityEntriesQuery = (async () => {
      if (activeProfile.role === "client") return { data: [], error: null };
      const result = await supabase!
        .from("mission_report_activity_entries")
        .select(
          "id,report_id,mission_id,worker_id,activity_key,activity_label,minutes,notes,created_at",
        )
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_activity_learning");
        return { data: [], error: null };
      }
      return result;
    })();
    const missionChecklistCapabilityQuery = (async () => {
      if (!['admin', 'employee', 'contractor'].includes(activeProfile.role))
        return { data: [], error: null };
      const result = await supabase!
        .from("mission_checklist_runs")
        .select("id")
        .limit(1);
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_checklists");
        return { data: [], error: null };
      }
      return result;
    })();
    const missionQualityClaimsQuery = (async () => {
      if (!['admin', 'client'].includes(activeProfile.role)) return { data: [], error: null };
      const result = await supabase!
        .from("mission_quality_claims")
        .select("id,mission_id,client_id,requested_by,request_kind,category,description,status,admin_qualification,admin_notes,client_response,retouch_mission_id,reviewed_at,resolved_at,created_at")
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_quality_claims");
        return { data: [], error: null };
      }
      return result;
    })();
    const missionQualityClaimEventsQuery = (async () => {
      if (!['admin', 'client'].includes(activeProfile.role)) return { data: [], error: null };
      const result = await supabase!
        .from("mission_quality_claim_events")
        .select("id,claim_id,event_type,previous_status,new_status,note,client_visible,created_at")
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_quality_claims");
        return { data: [], error: null };
      }
      return result;
    })();
    const missionQualityClaimAttachmentsQuery = (async () => {
      if (!['admin', 'client'].includes(activeProfile.role)) return { data: [], error: null };
      const result = await supabase!
        .from("mission_quality_claim_attachments")
        .select("id,claim_id,storage_path,original_name,mime_type,size_bytes,created_at")
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_quality_claims");
        return { data: [], error: null };
      }
      return result;
    })();
    const workerQualityMetricsQuery = (async () => {
      if (activeProfile.role === 'client') return { data: [], error: null };
      const result = await supabase!.rpc("get_worker_quality_metrics", {
        p_worker_id: null,
        p_from: `${parisDay(new Date()).slice(0, 4)}-01-01`,
        p_to: parisDay(new Date()),
      });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_quality_claims");
        return { data: [], error: null };
      }
      return result;
    })();
    const propertyRequestsQuery = (async () => {
      const current = await supabase!
        .from("client_property_requests")
        .select(
          "id,client_id,requested_by,request_kind,target_property_id,name,address,contact_name,access_notes,requested_reason,previous_snapshot,status,reviewed_by,reviewed_at,admin_notes,created_property_id,created_at",
        )
        .order("created_at", { ascending: false });
      if (!current.error) return current;
      if (
        !/request_kind|target_property_id|requested_reason|previous_snapshot|column/i.test(
          current.error.message,
        )
      )
        return current;
      return supabase!
        .from("client_property_requests")
        .select(
          "id,client_id,requested_by,name,address,contact_name,access_notes,status,reviewed_by,reviewed_at,admin_notes,created_property_id,created_at",
        )
        .order("created_at", { ascending: false });
    })();
    const propertyServiceQuoteRequestsQuery = (async () => {
      const base =
          "id,client_id,property_id,requested_by,service_name,requested_description,client_notes,status,quote_id,created_property_service_id,reviewed_by,reviewed_at,admin_response,created_at",
        current = await supabase!
          .from("property_service_quote_requests")
          .select(
            `${base},client_response_kind,client_response_reason,responded_by,responded_at`,
          )
          .order("created_at", { ascending: false });
      if (!current.error) return current;
      if (
        !/client_response_kind|client_response_reason|responded_by|responded_at|schema cache|column/i.test(
          current.error.message,
        )
      )
        return current;
      return supabase!
        .from("property_service_quote_requests")
        .select(base)
        .order("created_at", { ascending: false });
    })();
    const optionalRouteTeamQuery = async (
      table:
        | "route_team_templates"
        | "route_team_template_members"
        | "route_mileage_calculations"
        | "route_mileage_stops"
        | "route_mileage_legs",
      select: string,
    ) => {
      const result = await supabase!.from(table).select(select);
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("advanced_routes");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalAdminBillingQuery = async (
      table: string,
      select: string,
      capability: "billing_treatments" | "quote_sourcing",
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table).select(select);
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add(capability);
        return { data: [], error: null };
      }
      return result;
    };
    const optionalSupplierInvoiceQuery = async (
      table: "supplier_invoices" | "supplier_invoice_missions",
      select: string,
    ) => {
      if (!['admin', 'contractor'].includes(activeProfile.role))
        return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("supplier_invoicing");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalMissionChangeQuery = async (table: string, select: string) => {
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_changes");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalWorkerCompanyQuery = async (table: string, select: string) => {
      const result = await (supabase!.from as any)(table).select(select);
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("worker_company_dossier");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalHenrriDocumentsQuery = async () => {
      if (!['admin', 'client'].includes(activeProfile.role))
        return { data: [], error: null };
      const result = await supabase!
        .from("henrri_document_files")
        .select("id")
        .limit(1);
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("henrri_documents");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalPaymentReconciliationQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await supabase!
        .from("billing_payment_reconciliation_candidates")
        .select(
          "id,provider,environment,external_payment_id,amount,received_at,match_status,suggested_allocations,review_status,confirmed_allocations,resolution_note,reviewed_at,last_error,created_at",
        )
        .order("received_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("billing_payment_reconciliation");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalQuoteConversionQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await supabase!
        .from("billing_provider_document_transformations")
        .select(
          "id,provider,environment,source_quote_id,target_invoice_id,source_external_id,target_external_id,status,last_error,requested_at,completed_at",
        )
        .order("requested_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("henrri_quote_conversion");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalBillingCaseQuery = async (
      table:
        | "billing_case_summaries"
        | "billing_case_links"
        | "billing_case_progress_versions"
        | "billing_case_ledger_entries",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("billing_cases");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalBillingCaseGovernanceQuery = async (
      table: "billing_case_amendments" | "billing_case_consistency_reviews",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("billing_case_governance");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalBillingCaseOperationsQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(
        "billing_case_operational_syncs",
      )
        .select(
          "id,billing_case_id,mission_id,mission_costing_review_id,sync_number,total_actual_cost_ex_vat,component_count,supersedes_sync_id,notes,created_at",
        )
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("billing_case_operations");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalBillingCaseInvoicingQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(
        "billing_case_invoice_preparation_summaries",
      )
        .select(
          "id,billing_case_id,invoice_id,preparation_number,document_kind,source_progress_version,source_invoice_id,source_invoice_number,target_cumulative_ex_vat,prepared_amount_ex_vat,vat_rate,notes,created_at,invoice_number,invoice_status,issue_on,due_on,total_inc_vat",
        )
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("billing_case_invoicing");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalServiceCatalogQuery = async (
      table: "service_catalog_items" | "service_catalog_item_versions",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("service_catalog");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalCostingQuery = async (
      table:
        | "service_costing_profiles"
        | "service_costing_profile_components"
        | "quote_costing_runs"
        | "quote_costing_run_lines"
        | "procurement_requirements",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("guided_quote_costing");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalQualificationQuery = async (
      table:
        | "service_qualification_questionnaires"
        | "service_qualification_questions"
        | "quote_qualification_runs"
        | "service_qualification_rule_evidence",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      let result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (
        result.error &&
        table === "service_qualification_rule_evidence" &&
        select.includes("median_actual_labor_hours_per_output_unit") &&
        /column|colonne|schema cache/i.test(result.error.message)
      ) {
        result = await (supabase!.from as any)(table)
          .select(
            "catalog_item_id,questionnaire_id,question_key,rule_id,rule_label,evidence_count,average_actual_labor_hours_per_output_unit,minimum_actual_labor_hours_per_output_unit,maximum_actual_labor_hours_per_output_unit,average_actual_cost_ex_vat_per_output_unit,last_observed_at",
          )
          .order(orderColumn, { ascending: false });
      }
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("service_qualification");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalMissionCostingQuery = async (
      table:
        | "mission_costing_reviews"
        | "mission_costing_review_lines"
        | "service_costing_yield_recommendations",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("mission_costing_feedback");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalProcessQuery = async (
      table:
        | "business_events"
        | "process_runs"
        | "required_actions"
        | "process_notification_intents"
        | "process_journal_entries",
      select: string,
      orderColumn: string,
    ) => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(table)
        .select(select)
        .order(orderColumn, { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("process_actions");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalProcessDeliveryQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(
        "process_notification_deliveries",
      )
        .select(
          "id,notification_intent_id,process_run_id,event_id,recipient_profile_id,channel,status,suppression_reason,scheduled_at,next_attempt_at,lease_owner,lease_until,attempt_count,max_attempts,provider,external_message_id,destination_fingerprint,last_error,delivered_at,created_at",
        )
        .order("created_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("process_notification_executor");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalProcessDefinitionQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)("process_definitions")
        .select(
          "id,organization_id,process_key,name,version,status,trigger_event_types,trigger_conditions,steps,published_at,effective_from,effective_until,source_definition_id,draft_notes,created_at,updated_at",
        )
        .order("process_key")
        .order("version", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        unavailableCapabilities.add("process_definition_editor");
        return { data: [], error: null };
      }
      return result;
    };
    const optionalProcessPublicationReviewQuery = async () => {
      if (activeProfile.role !== "admin") return { data: [], error: null };
      const result = await (supabase!.from as any)(
        "process_definition_publication_reviews",
      )
        .select(
          "id,organization_id,definition_id,process_key_snapshot,version_snapshot,definition_fingerprint,definition_snapshot,simulation_evidence,request_note,status,requested_by,requested_at,reviewed_by,reviewed_at,review_note,activated_at",
        )
        .order("requested_at", { ascending: false });
      if (result.error && missingDeploymentObject(result.error.message)) {
        return { data: [], error: null };
      }
      return result;
    };
    const workersQuery = async () => {
      const secured = await supabase!.rpc("get_visible_workers");
      if (!secured.error) return secured;
      if (!missingDeploymentObject(secured.error.message)) return secured;

      // Compatibility while the matching migration is still pending on an
      // environment. Once deployed, the checked RPC above is the only path
      // that exposes payroll, contractor pricing and internal notes.
      const base =
          "id,profile_id,kind,first_name,last_name,monthly_hours,daily_hours,net_salary,employer_cost_monthly,ticket_count,ticket_value,mileage_enabled,active,pricing_mode,hourly_rate,fixed_fee,monthly_fee,business_name,siret,vat_number,address,contact_email,contact_phone,profile_notes,skills",
        result = await supabase!
          .from("workers")
          .select(`${base},company_organization_id,business_relationship_id`)
          .order("last_name");
      if (
        result.error &&
        /company_organization_id|business_relationship_id|schema cache|column/i.test(
          result.error.message,
        )
      )
        return supabase!.from("workers").select(base).order("last_name");
      return result;
    };
    const assignmentsQuery = async () => {
      const base =
          "id,mission_id,worker_id,assigned_hours,source,status,pricing_mode,agreed_rate,agreed_amount,worker_rate_card_id,proposed_at,responded_at,response_notes",
        result = await supabase!
          .from("mission_assignments")
          .select(`${base},skill_override,skill_override_reason,missing_skills_snapshot`);
      if (
        result.error &&
        /skill_override|missing_skills_snapshot|schema cache|column/i.test(
          result.error.message,
        )
      )
        return supabase!.from("mission_assignments").select(base);
      return result;
    };
    const q = await Promise.all([
      supabase!
        .from("missions")
        .select(
          "id,client_id,property_id,service_id,property_service_id,client_display_name,starts_at,ends_at,status,workflow_status,billing_status,invoice_strategy,checklist_model_id,location,planned_hours,amount_ex_vat,billing_mode,billing_rate,billing_quantity,notes,client_report,next_actions,admin_notes,admin_validated_at,required_skills,is_test,client:clients(name)",
        )
        .order("starts_at"),
      (async () => {
        const base =
            "id,name,profile_id,category,client_type,address,siret,vat_number,contact_first_name,contact_last_name,contact_title,contact_email,contact_phone,notes,calendar_enabled,billing_emission,billing_grouping,target_margin_rate,average_round_trip_minutes,monthly_admin_minutes,incident_count,strategic_priority,scoring_notes,commission_enabled,commission_rate,commission_basis,commission_vat_rate,commission_label",
          result = await supabase!
            .from("clients")
            .select(`${base},payment_terms_mode,payment_terms_days`)
            .eq("active", true)
            .order("name");
        if (result.error && /payment_terms_|schema cache|column/i.test(result.error.message))
          return supabase!.from("clients").select(base).eq("active", true).order("name");
        return result;
      })(),
      assignmentsQuery(),
      workersQuery(),
      supabase!
        .from("time_entries")
        .select("*")
        .order("work_date", { ascending: false })
        .limit(200),
      supabase!
        .from("leave_requests")
        .select("*")
        .order("starts_on", { ascending: false })
        .limit(200),
      supabase!
        .from("mileage_entries")
        .select("*")
        .order("travel_date", { ascending: false })
        .limit(200),
      supabase!
        .from("documents")
        .select(
          "id,owner_profile_id,kind,period,original_name,storage_path,expires_on,status,requirement_id,client_requirement_id,created_at",
        )
        .order("created_at", { ascending: false }),
      (async () => {
        const base = "id,client_id,name,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours",
          result = await supabase!
            .from("client_services")
            .select(`${base},catalog_item_id`)
            .eq("active", true)
            .order("name");
        if (result.error && /catalog_item_id|schema cache|column/i.test(result.error.message)) {
          unavailableCapabilities.add("service_catalog");
          unavailableCapabilities.add("guided_quote_costing");
          unavailableCapabilities.add("service_qualification");
          return supabase!.from("client_services").select(base).eq("active", true).order("name");
        }
        return result;
      })(),
      (async () => {
        const extended = await supabase!
          .from("mission_requests")
          .select(
            "id,client_id,property_id,service_id,property_service_id,requested_service_keys,desired_starts_at,desired_ends_at,location,client_notes,status,created_at",
          )
          .order("created_at", { ascending: false });
        if (
          extended.error &&
          /requested_service_keys|schema cache|column/i.test(
            extended.error.message,
          )
        ) {
          unavailableCapabilities.add("mission_request_multi_services");
          return supabase!
            .from("mission_requests")
            .select(
              "id,client_id,property_id,service_id,property_service_id,desired_starts_at,desired_ends_at,location,client_notes,status,created_at",
            )
            .order("created_at", { ascending: false });
        }
        return extended;
      })(),
      missionReportsQuery,
      (async () => {
        const base =
            "id,client_id,quote_id,number,billing_period,grouping,document_kind,parent_invoice_id,progress_percent,project_total_ex_vat,credit_reason,status,issue_on,due_on,total_ex_vat,total_vat,total_inc_vat,amount_paid,last_payment_on,next_reminder_on,reminder_count,henrri_id,henrri_pdf_url",
          result = await supabase!
            .from("invoices")
            .select(
              `${base},payment_terms_mode,payment_terms_days,collection_track,collection_notes,collection_next_review_on,collection_updated_at,collection_updated_by`,
            )
            .order("created_at", { ascending: false });
        if (
          result.error &&
          /collection_track|collection_notes|collection_next_review_on|collection_updated_|schema cache|column/i.test(
            result.error.message,
          )
        ) {
          unavailableCapabilities.add("receivables_collection_portfolio");
          const compatible = await supabase!
            .from("invoices")
            .select(`${base},payment_terms_mode,payment_terms_days`)
            .order("created_at", { ascending: false });
          if (!compatible.error) return compatible;
        }
        if (result.error && /payment_terms_|schema cache|column/i.test(result.error.message))
          return supabase!.from("invoices").select(base).order("created_at", { ascending: false });
        return result;
      })(),
      supabase!
        .from("worker_availability")
        .select("id,worker_id,starts_at,ends_at,availability,notes")
        .order("starts_at", { ascending: false }),
      supabase!
        .from("worker_document_requirements")
        .select("id,worker_id,kind,label,due_on,status,notes")
        .order("due_on"),
      supabase!
        .from("worker_contracts")
        .select(
          "id,worker_id,title,status,starts_on,ends_on,document_id,template_id,template_version,sent_at,signed_at,next_reminder_at,rendered_content,generated_at,notes",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("client_properties")
        .select(
          "id,client_id,property_type_id,name,address,latitude,longitude,contact_name,access_notes,active",
        )
        .eq("active", true)
        .order("name"),
      activeProfile.role === "admin"
        ? supabase!
            .from("property_services")
            .select(
              "id,property_id,name,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours,notes,active",
            )
            .eq("active", true)
            .order("name")
        : activeProfile.role === "client"
          ? supabase!.rpc("get_client_property_services")
          : Promise.resolve({ data: [], error: null }),
      supabase!
        .from("preferred_worker_assignments")
        .select("id,client_id,property_id,worker_id,priority,active")
        .eq("active", true)
        .order("priority"),
      (async () => {
        const base =
            "id,client_id,mission_id,number,issued_on,valid_until,status,discount_rate,deposit_rate,total_ex_vat,total_vat,total_inc_vat,notes",
          legacyBase =
            "id,client_id,number,issued_on,valid_until,status,discount_rate,deposit_rate,total_ex_vat,total_vat,total_inc_vat,notes",
          result = await supabase!
            .from("quotes")
            .select(`${base},payment_terms_mode,payment_terms_days`)
            .order("created_at", { ascending: false });
        if (result.error && /payment_terms_|mission_id|schema cache|column/i.test(result.error.message)) {
          const compatible = await supabase!
            .from("quotes")
            .select(base)
            .order("created_at", { ascending: false });
          if (!compatible.error) return compatible;
          if (/mission_id|schema cache|column/i.test(compatible.error.message))
            return supabase!
              .from("quotes")
              .select(legacyBase)
              .order("created_at", { ascending: false });
          return compatible;
        }
        return result;
      })(),
      (async () => {
        const base =
            "id,owner_profile_id,mission_id,client_id,spent_on,category,description,merchant,payment_method,amount_inc_vat,amount_ex_vat,vat_rate,vat_amount,receipt_path,status,reviewed_by,reviewed_at,stock_item_id,stock_quantity,stock_unit_cost",
          legacyBase =
            "id,owner_profile_id,mission_id,client_id,spent_on,category,description,merchant,payment_method,amount_inc_vat,amount_ex_vat,vat_rate,vat_amount,receipt_path,status,reviewed_by,reviewed_at",
          result = await supabase!
            .from("expense_reports")
            .select(`${base},reimbursement_status,reimbursed_on,reimbursement_reference`)
            .order("spent_on", { ascending: false });
        if (result.error && /reimbursement_|schema cache|column/i.test(result.error.message)) {
          const compatible = await supabase!
            .from("expense_reports")
            .select(base)
            .order("spent_on", { ascending: false });
          if (!compatible.error) return compatible;
          if (/stock_|schema cache|column/i.test(compatible.error.message))
            return supabase!
              .from("expense_reports")
              .select(legacyBase)
              .order("spent_on", { ascending: false });
          return compatible;
        }
        return result;
      })(),
      (async () => {
        const baseColumns =
            "id,name,legal_name,siret,vat_number,address,postal_code,city,country,iban,bic,bank_name,representative_first_name,representative_last_name,representative_title,email,phone,website,current_account_balance,current_account_updated_at,dashboard_preferences,default_vat_rate,target_margin_rate,fiscal_year_start_month,route_buffer_minutes,mileage_rate_per_km,mileage_return_to_headquarters,scoring_preferences,default_invoice_due_days,invoice_reminder_days_after_due,document_alert_days,contract_alert_days,low_stock_alerts_enabled,default_billing_emission,default_billing_grouping,use_preferred_workers,prioritize_available_employees",
          result = await supabase!
            .from("organizations")
            .select(`${baseColumns},default_payment_terms_mode,profitability_target_mode,profitability_target_rate,cash_plan_settings`)
            .eq("id", activeProfile.organization_id)
            .maybeSingle();
        if (
          result.error &&
          /default_payment_terms_mode|profitability_target_(mode|rate)|cash_plan_settings|schema cache|column/i.test(
            result.error.message,
          )
        ) {
          const withoutPaymentTerms = await supabase!
            .from("organizations")
            .select(`${baseColumns},profitability_target_mode,profitability_target_rate`)
            .eq("id", activeProfile.organization_id)
            .maybeSingle();
          if (!withoutPaymentTerms.error) return withoutPaymentTerms;
          return supabase!
            .from("organizations")
            .select(baseColumns)
            .eq("id", activeProfile.organization_id)
            .maybeSingle();
        }
        return result;
      })(),
      supabase!
        .from("cash_balance_snapshots")
        .select(
          "id,recorded_at,balance,previous_balance,difference,known_receipts,known_outflows,expected_net_change,unexplained_difference,reconciliation_from,notes",
        )
        .order("recorded_at", { ascending: false })
        .limit(24),
      supabase!
        .from("service_proposals")
        .select(
          "id,client_id,property_id,mission_request_id,name,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours,notes,status,client_response_reason,revision_requested,responded_at,created_service_id,created_property_service_id,created_at",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("mission_report_attachments")
        .select(
          "id,report_id,worker_id,storage_path,original_name,mime_type,size_bytes,client_visible,created_at",
        )
        .order("created_at"),
      dailyRouteTeamsQuery,
      supabase!
        .from("daily_route_team_members")
        .select("team_id,work_date,worker_id")
        .order("work_date", { ascending: false }),
      supabase!
        .from("mission_recurrences")
        .select(
          "id,client_id,property_id,service_id,property_service_id,preferred_worker_id,name,frequency,starts_on,ends_on,start_time,duration_minutes,location,billing_mode,billing_rate,billing_quantity,amount_ex_vat,vat_rate,notes,active,next_occurrence_on,generated_until",
        )
        .order("created_at", { ascending: false }),
      (async () => {
        const base =
            "id,role,display_name,email,phone,permission_set_id,access_status,dashboard_preferences",
          result = await supabase!
            .from("profiles")
            .select(`${base},process_notification_preferences`)
            .order("display_name");
        if (
          result.error &&
          /process_notification_preferences|schema cache|column/i.test(
            result.error.message,
          )
        ) {
          unavailableCapabilities.add("process_notification_preferences");
          return supabase!.from("profiles").select(base).order("display_name");
        }
        return result;
      })(),
      supabase!
        .from("contract_templates")
        .select(
          "id,audience,name,version,content,required_document_kinds,renewal_notice_days,active",
        )
        .order("audience")
        .order("version", { ascending: false }),
      (async () => {
        const base =
            "id,client_id,template_id,title,template_version,status,starts_on,ends_on,document_id,sent_at,signed_at,next_reminder_at,rendered_content,generated_at,notes",
          result = await supabase!
            .from("client_contracts")
            .select(
              `${base},contract_kind,offer_name,service_scope,billing_frequency,billing_amount_ex_vat,vat_rate,payment_terms_days,quote_id,payment_provider,payment_instruction,prepared_at`,
            )
            .order("created_at", { ascending: false });
        if (
          result.error &&
          /contract_kind|billing_frequency|payment_provider|schema cache|column/i.test(
            result.error.message,
          )
        ) {
          unavailableCapabilities.add("recurring_client_contracts");
          return supabase!
            .from("client_contracts")
            .select(base)
            .order("created_at", { ascending: false });
        }
        return result;
      })(),
      supabase!
        .from("client_document_requirements")
        .select("id,client_id,contract_id,kind,label,due_on,status,notes")
        .order("due_on"),
      supabase!
        .from("quote_lines")
        .select(
          "id,quote_id,service_id,description,quantity,unit_price_ex_vat,vat_rate,position",
        )
        .order("position"),
      supabase!
        .from("invoice_lines")
        .select(
          "id,invoice_id,mission_id,service_id,description,quantity,unit_price_ex_vat,vat_rate,position,turnover_declaration_id,mission_stock_usage_id",
        )
        .order("position"),
      supabase!
        .from("commercial_document_attachments")
        .select("id,quote_id,invoice_id,document_id,label,created_at")
        .order("created_at", { ascending: false }),
      supabase!
        .from("commercial_document_events")
        .select(
          "id,quote_id,invoice_id,event_type,status_from,status_to,note,created_at",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("invoice_payments")
        .select(
          "id,invoice_id,received_on,amount,method,reference,status,henrri_payment_id,notes,created_at",
        )
        .order("received_on", { ascending: false }),
      (async () => {
        const base =
            "id,invoice_id,channel,status,subject,body,scheduled_on,sent_at,created_at",
          result = await supabase!
            .from("invoice_reminders")
            .select(`${base},milestone_days_after_due`)
            .order("created_at", { ascending: false });
        if (
          result.error &&
          /milestone_days_after_due|schema cache|column/i.test(result.error.message)
        ) {
          unavailableCapabilities.add("receivables_collection_portfolio");
          return supabase!
            .from("invoice_reminders")
            .select(base)
            .order("created_at", { ascending: false });
        }
        return result;
      })(),
      supabase!
        .from("recurring_fixed_charges")
        .select(
          "id,name,vendor,category,amount_ex_vat,vat_rate,frequency,starts_on,ends_on,next_due_on,payment_method,notes,active",
        )
        .order("name"),
      supabase!
        .from("worker_rate_cards")
        .select("id,worker_id,name,pricing_mode,rate,unit_label,notes,active,client_id,property_id,property_service_id")
        .order("name"),
      inventoryCatalogQuery,
      supabase!
        .from("inventory_movements")
        .select(
          "id,item_id,movement_type,quantity,unit_cost,occurred_on,expense_report_id,mission_id,mission_stock_usage_id,notes",
        )
        .order("occurred_on", { ascending: false })
        .limit(500),
      supabase!
        .from("director_compensations")
        .select(
          "id,profile_id,leader_id,period,net_salary,social_contributions,other_costs,dividends,status,paid_on,notes",
        )
        .order("period", { ascending: false }),
      supabase!
        .from("profile_change_requests")
        .select(
          "id,requester_profile_id,target_type,target_id,profile_changes,target_changes,status,reviewed_by,reviewed_at,admin_notes,created_at",
        )
        .order("created_at", { ascending: false }),
      (async () => {
        const result = await supabase!
          .from("permission_sets")
          .select("id,name,description,permissions,active,access_mode")
          .order("name");
        if (result.error && /access_mode|schema cache|column/i.test(result.error.message))
          return supabase!.from("permission_sets").select("id,name,description,permissions,active").order("name");
        return result;
      })(),
      supabase!
        .from("worker_vehicles")
        .select(
          "id,worker_id,owner_profile_id,vehicle_kind,energy_type,brand,model,registration_number,fiscal_power,registration_document_path,document_status,document_notes,active",
        )
        .order("brand"),
      supabase!
        .from("organization_integrations")
        .select(
          "id,organization_id,provider,enabled,environment,status,account_identifier,provider_name,settings,last_sync_at,last_error",
        )
        .order("provider"),
      supabase!
        .from("integration_sync_cursors")
        .select(
          "id,provider,resource,min_external_id,last_started_at,last_success_at,status,last_error",
        )
        .order("resource"),
      supabase!
        .from("notifications")
        .select(
          "id,recipient_profile_id,mission_id,kind,title,body,read_at,created_at",
        )
        .order("created_at", { ascending: false })
        .limit(100),
      supabase!
        .from("audit_logs")
        .select(
          "id,actor_id,action,entity_type,entity_id,old_value,new_value,created_at",
        )
        .in("action", [
          "profile_access_updated",
          "permission_set_created",
          "permission_set_updated",
          "permission_set_deleted",
        ])
        .order("created_at", { ascending: false })
        .limit(50),
      supabase!
        .from("support_access_grants")
        .select(
          "id,platform_admin_user_id,status,reason,access_scope,expires_at,approved_at,revoked_at,created_at",
        )
        .order("created_at", { ascending: false })
        .limit(20),
      propertyRequestsQuery,
      propertyServiceQuoteRequestsQuery,
      supabase!
        .from("property_service_quote_terms")
        .select(
          "request_id,billing_mode,unit_price_ex_vat,vat_rate,default_duration_hours,admin_notes",
        ),
      supabase!
        .from("subscription_plans")
        .select(
          "id,name,monthly_price_ex_vat,annual_price_ex_vat,currency,limits",
        )
        .eq("active", true)
        .order("sort_order"),
      supabase!
        .from("subscription_upgrade_requests")
        .select(
          "id,organization_id,current_plan_id,recommended_plan_id,trigger_metric,current_value,current_limit,status,client_response_notes,created_at",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("subscription_amendments")
        .select(
          "id,upgrade_request_id,new_plan_id,billing_cycle,status,signed_at",
        )
        .order("created_at", { ascending: false }),
      supabase!
        .from("calendar_connections")
        .select(
          "id,profile_id,status,calendar_id,calendar_name,google_account_email,sync_enabled,last_sync_at,last_error,connected_at",
        )
        .order("updated_at", { ascending: false }),
      supabase!
        .from("signature_envelopes")
        .select(
          "id,source_type,source_id,signer_profile_id,signer_name,signer_email,provider,status,requested_at,viewed_at,signed_at,last_error",
        )
        .eq("scope", "tenant")
        .order("created_at", { ascending: false }),
      supabase!
        .from("client_turnover_declarations")
        .select(
          "id,client_id,period,turnover_amount,turnover_basis,commission_rate,commission_amount_ex_vat,vat_rate,label,source_reference,notes,status,invoice_id,created_at",
        )
        .order("period", { ascending: false }),
      supabase!
        .from("organization_leaders")
        .select(
          "id,profile_id,worker_id,first_name,last_name,title,email,phone,starts_on,ends_on,notes,active",
        )
        .order("last_name"),
      supabase!
        .from("inventory_suppliers")
        .select("id,name,contact_name,email,phone,website,address,notes,active")
        .order("name"),
      supabase!
        .from("inventory_kit_components")
        .select("id,kit_item_id,component_item_id,quantity"),
      supabase!
        .from("mission_stock_usages")
        .select("id,mission_id,report_id,worker_id,item_id,quantity,billable,unit_price_ex_vat,vat_rate,billing_status,invoice_line_id,notes,billing_notes,reviewed_by,reviewed_at,created_at")
        .order("created_at", { ascending: false }),
      optionalRouteTeamQuery(
        "route_team_templates",
        "id,name,notes,leader_worker_id,active",
      ),
      optionalRouteTeamQuery(
        "route_team_template_members",
        "template_id,worker_id",
      ),
      (async () => {
        const result = await supabase!
          .from("mission_report_submissions")
          .select(
            "id,mission_id,worker_id,submitted_by_worker_id,route_team_id,team_report_batch_id,source,result,actual_hours,observations,client_note,submitted_at",
          )
          .order("submitted_at", { ascending: false });
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("advanced_routes");
          return { data: [], error: null };
        }
        return result;
      })(),
      (async () => {
        if (!['employee', 'contractor'].includes(activeProfile.role))
          return { data: [], error: null };
        const result = await supabase!.rpc("get_my_route_team_context");
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("advanced_routes");
          return { data: [], error: null };
        }
        return result;
      })(),
      optionalRouteTeamQuery(
        "route_mileage_calculations",
        "id,work_date,route_team_id,beneficiary_worker_id,beneficiary_profile_id,vehicle_id,include_return_to_headquarters,status,provider,provider_route_version,origin_address,total_distance_meters,total_duration_seconds,calculated_at,error_message",
      ),
      optionalRouteTeamQuery(
        "route_mileage_stops",
        "id,route_calculation_id,mission_id,stop_kind,position,address_snapshot,planned_starts_at",
      ),
      optionalRouteTeamQuery(
        "route_mileage_legs",
        "id,route_calculation_id,position,origin_address_snapshot,destination_address_snapshot,distance_meters,duration_seconds,provider_route_version,calculated_at",
      ),
      (async () => {
        const result = activeProfile.role === "admin"
          ? await supabase!
              .from("worker_commercial_proposals")
              .select("id,worker_id,proposal_kind,mission_id,client_id,property_id,proposed_service_name,pricing_mode,quantity,proposed_supplier_unit_rate,complementary_cost_ex_vat,proposed_supplier_total_ex_vat,client_price_reference_ex_vat,target_mode_snapshot,target_rate_snapshot,minimum_client_price_ex_vat,projected_margin_rate,projected_markup_rate,profitability_alert,description,status,version,previous_version_id,submitted_at,reviewed_at,decision_notes")
              .order("submitted_at", { ascending: false })
          : activeProfile.role === "contractor"
            ? await supabase!.rpc("get_my_worker_commercial_proposals")
            : { data: [], error: null };
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("worker_pricing_proposals");
          return { data: [], error: null };
        }
        return result;
      })(),
      optionalAdminBillingQuery(
        "service_billing_rules",
        "id,client_service_id,property_service_id,billing_treatment,supplier_unit_cost_ex_vat,target_mode,target_rate,disbursement_principal_name,disbursement_mandate_reference,fiscal_warning_acknowledged_at",
        "billing_treatments",
      ),
      optionalAdminBillingQuery(
        "mission_billing_snapshots",
        "id,mission_id,billing_treatment,supplier_unit_cost_ex_vat,supplier_total_cost_ex_vat,client_price_ex_vat,target_mode_snapshot,target_rate_snapshot,minimum_client_price_ex_vat,projected_margin_rate,projected_markup_rate,profitability_alert,disbursement_principal_name,disbursement_mandate_reference,frozen_at",
        "billing_treatments",
      ),
      optionalAdminBillingQuery(
        "commercial_line_billing_snapshots",
        "id,quote_line_id,invoice_line_id,billing_treatment,supplier_unit_cost_ex_vat,supplier_total_cost_ex_vat,client_price_ex_vat,target_mode_snapshot,target_rate_snapshot,minimum_client_price_ex_vat,projected_margin_rate,projected_markup_rate,profitability_alert,disbursement_principal_name,disbursement_mandate_reference,frozen_at",
        "billing_treatments",
      ),
      optionalSupplierInvoiceQuery(
        "supplier_invoices",
        "id,worker_id,source,invoice_number,issue_on,service_period_start,service_period_end,due_on,currency,total_ex_vat,total_vat,total_inc_vat,document_id,external_provider,external_id,external_pdf_url,status,contractor_notes,review_notes,submitted_at,reviewed_at,paid_on,payment_reference,created_at",
      ),
      optionalSupplierInvoiceQuery(
        "supplier_invoice_missions",
        "invoice_id,mission_id,created_at",
      ),
      optionalMissionChangeQuery(
        "mission_change_events",
        "id,mission_id,event_type,status,reason,operational_notes,previous_snapshot,proposed_changes,applied_snapshot,client_amount_delta_ex_vat,supplier_amount_delta_ex_vat,requires_client_approval,client_approval_status,evidence_document_id,requested_by,reviewed_by,review_notes,reviewed_at,applied_at,created_at",
      ),
      optionalMissionChangeQuery(
        "mission_financial_adjustments",
        "id,mission_id,change_event_id,assignment_id,worker_id,adjustment_kind,amount_ex_vat,vat_rate,reason,evidence_document_id,status,supplier_invoice_id,created_at",
      ),
      optionalMissionChangeQuery(
        "worker_cancellation_rules",
        "id,worker_id,contract_id,enabled,notice_hours,penalty_mode,penalty_value,clause_reference,created_at",
      ),
      optionalMissionChangeQuery(
        "worker_cancellation_notices",
        "id,mission_id,assignment_id,worker_id,contract_id,change_event_id,reason,evidence_document_id,submitted_at,notice_hours,rule_snapshot,proposed_penalty_ex_vat,approved_penalty_ex_vat,financial_adjustment_id,status,review_notes,reviewed_at,created_at",
      ),
      optionalAdminBillingQuery(
        "quote_sourcing_workflows",
        "id,quote_id,quote_line_id,client_id,worker_id,description_snapshot,client_target_price_ex_vat,target_mode_snapshot,target_rate_snapshot,max_supplier_budget_ex_vat,accepted_offer_id,accepted_supplier_cost_ex_vat,suggested_client_price_ex_vat,final_client_price_ex_vat,status,approved_at,created_at",
        "quote_sourcing",
      ),
      optionalAdminBillingQuery(
        "quote_sourcing_offers",
        "id,workflow_id,worker_id,pricing_mode,quantity,supplier_unit_rate_ex_vat,complementary_cost_ex_vat,supplier_total_ex_vat,minimum_client_price_ex_vat,status,version,previous_offer_id,contractor_notes,counter_budget_ex_vat,decision_notes,submitted_at",
        "quote_sourcing",
      ),
      (async () => {
        if (activeProfile.role !== "contractor") return { data: [], error: null };
        const result = await supabase!.rpc("get_my_quote_sourcing_tasks");
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("quote_sourcing");
          return { data: [], error: null };
        }
        return result;
      })(),
      optionalWorkerCompanyQuery(
        "worker_company_profiles",
        "id,worker_id,legal_name,legal_form,siren,registered_address,postal_code,city,country,website,billing_email,authorized_services,service_areas,compliance_status,restriction_reason,bank_name,account_holder,iban,bic,last_reviewed_at",
      ),
      optionalWorkerCompanyQuery(
        "worker_company_representatives",
        "id,worker_id,first_name,last_name,title,email,phone,is_primary,active",
      ),
      optionalWorkerCompanyQuery(
        "worker_company_profile_events",
        "id,worker_id,event_type,previous_status,new_status,note,snapshot,created_at",
      ),
      optionalHenrriDocumentsQuery(),
      optionalPaymentReconciliationQuery(),
      supabase!
        .from("integration_external_links")
        .select(
          "id,provider,entity_type,local_id,external_id,external_updated_at,last_synced_at,metadata",
        )
        .in("entity_type", ["quote", "invoice"])
        .order("last_synced_at", { ascending: false }),
      optionalBillingCaseQuery(
        "billing_case_summaries",
        "id,organization_id,client_id,property_id,case_code,title,case_kind,status,billing_strategy,currency,billing_provider,contract_value_ex_vat,target_margin_rate_snapshot,latest_progress_version,latest_progress_method,latest_progress_percent,latest_approved_amount_ex_vat,expected_revenue_ex_vat,actual_revenue_ex_vat,expected_cost_ex_vat,actual_cost_ex_vat,expected_margin_ex_vat,actual_margin_ex_vat,invoiced_ex_vat,invoiced_inc_vat,paid_inc_vat,remaining_to_collect_inc_vat,remaining_to_invoice_ex_vat,updated_at",
        "updated_at",
      ),
      optionalBillingCaseQuery(
        "billing_case_links",
        "id,billing_case_id,entity_type,entity_id,link_role,label_snapshot,linked_at",
        "linked_at",
      ),
      optionalBillingCaseQuery(
        "billing_case_progress_versions",
        "id,billing_case_id,version,progress_method,progress_percent,approved_amount_ex_vat,breakdown,status,notes,approved_at,created_at",
        "created_at",
      ),
      optionalBillingCaseQuery(
        "billing_case_ledger_entries",
        "id,billing_case_id,direction,stage,category,description,quantity,unit,unit_amount_ex_vat,amount_ex_vat,vat_rate,expected_on,occurred_on,source_type,source_id,confidence,supersedes_entry_id,created_at",
        "created_at",
      ),
      optionalServiceCatalogQuery(
        "service_catalog_items",
        "id,item_code,name,description,item_kind,default_billing_mode,default_unit_label,default_unit_price_ex_vat,default_vat_rate,default_duration_hours,target_margin_rate,active,updated_at",
        "name",
      ),
      optionalServiceCatalogQuery(
        "service_catalog_item_versions",
        "id,catalog_item_id,version,snapshot,change_reason,created_at",
        "created_at",
      ),
      optionalCostingQuery(
        "service_costing_profiles",
        "id,catalog_item_id,version,active,output_unit,expected_output_per_labor_hour,default_team_size,default_labor_cost_per_hour,default_travel_cost_ex_vat,contingency_rate,target_mode,target_rate,source_kind,evidence_count,confidence,required_skills,assumptions_notes,created_at",
        "created_at",
      ),
      optionalCostingQuery(
        "service_costing_profile_components",
        "id,profile_id,component_type,business_kind,label,cost_basis,quantity,unit_label,unit_cost_ex_vat,supplier_id,purchase_reference,purchase_url,source_note,position",
        "position",
      ),
      optionalCostingQuery(
        "quote_costing_runs",
        "id,quote_id,quote_line_id,catalog_item_id,costing_profile_id,run_number,status,output_quantity,output_unit,team_size,total_labor_hours,elapsed_hours,labor_cost_per_hour,labor_cost_ex_vat,material_cost_ex_vat,equipment_cost_ex_vat,travel_cost_ex_vat,other_cost_ex_vat,contingency_rate,contingency_cost_ex_vat,total_cost_ex_vat,target_mode,target_rate,suggested_sale_price_ex_vat,proposed_sale_price_ex_vat,projected_margin_rate,projected_markup_rate,profitability_alert,missing_inputs,notes,applied_at,created_at",
        "created_at",
      ),
      optionalCostingQuery(
        "quote_costing_run_lines",
        "id,costing_run_id,component_type,label,quantity,unit_label,unit_cost_ex_vat,amount_ex_vat,source_kind,confidence,position",
        "position",
      ),
      optionalMissionCostingQuery(
        "mission_costing_reviews",
        "id,mission_id,quote_costing_run_id,catalog_item_id,costing_profile_id,review_number,status,planned_output_quantity,actual_output_quantity,output_unit,planned_labor_hours,actual_labor_hours,planned_labor_cost_ex_vat,actual_labor_cost_ex_vat,planned_material_cost_ex_vat,actual_material_cost_ex_vat,planned_equipment_cost_ex_vat,actual_equipment_cost_ex_vat,planned_travel_cost_ex_vat,actual_travel_cost_ex_vat,planned_other_cost_ex_vat,actual_other_cost_ex_vat,planned_contingency_cost_ex_vat,planned_total_cost_ex_vat,actual_total_cost_ex_vat,cost_variance_ex_vat,cost_variance_rate,planned_yield_per_labor_hour,observed_yield_per_labor_hour,missing_inputs,notes,created_at,decided_at,decision_notes,published_profile_id",
        "created_at",
      ),
      optionalMissionCostingQuery(
        "mission_costing_review_lines",
        "id,review_id,component_type,label,planned_amount_ex_vat,actual_amount_ex_vat,variance_ex_vat,source_kind,source_ids,completeness,note,position",
        "position",
      ),
      optionalMissionCostingQuery(
        "service_costing_yield_recommendations",
        "id,catalog_item_id,base_profile_id,recommendation_number,status,output_unit,minimum_sample_size,outlier_tolerance_percent,candidate_review_count,included_review_count,excluded_review_count,current_yield_per_labor_hour,median_yield_per_labor_hour,recommended_yield_per_labor_hour,total_output_quantity,total_labor_hours,included_review_ids,excluded_review_ids,explanation,created_at,decided_at,decision_notes,published_profile_id",
        "created_at",
      ),
      optionalBillingCaseGovernanceQuery(
        "billing_case_amendments",
        "id,billing_case_id,amendment_number,title,reason,status,previous_contract_value_ex_vat,change_amount_ex_vat,revised_contract_value_ex_vat,source_quote_id,created_at,decided_at,decision_note",
        "created_at",
      ),
      optionalBillingCaseGovernanceQuery(
        "billing_case_consistency_reviews",
        "id,billing_case_id,review_number,severity,warning_count,blocking_count,findings,contract_value_ex_vat,accepted_quote_total_ex_vat,invoiced_ex_vat,paid_inc_vat,latest_approved_amount_ex_vat,created_at",
        "created_at",
      ),
      optionalBillingCaseOperationsQuery(),
      optionalBillingCaseInvoicingQuery(),
      optionalQualificationQuery(
        "service_qualification_questionnaires",
        "id,catalog_item_id,version,name,description,status,is_current_version,change_reason,created_at,published_at",
        "created_at",
      ),
      optionalQualificationQuery(
        "service_qualification_questions",
        "id,questionnaire_id,question_key,label,help_text,response_type,required,unit_label,options,costing_target,pricing_rules,position",
        "position",
      ),
      optionalQualificationQuery(
        "quote_qualification_runs",
        "id,quote_id,quote_line_id,questionnaire_id,catalog_item_id,run_number,status,answers,derived_inputs,missing_required,costing_run_id,notes,created_at",
        "created_at",
      ),
      optionalQualificationQuery(
        "service_qualification_rule_evidence",
        "catalog_item_id,questionnaire_id,question_key,rule_id,rule_label,evidence_count,average_actual_labor_hours_per_output_unit,minimum_actual_labor_hours_per_output_unit,maximum_actual_labor_hours_per_output_unit,average_actual_cost_ex_vat_per_output_unit,last_observed_at,median_actual_labor_hours_per_output_unit,prudent_actual_labor_hours_per_output_unit",
        "last_observed_at",
      ),
      optionalProcessQuery(
        "business_events",
        "id,event_type,occurred_at,actor_role,subject_type,subject_id,context,correlation_id",
        "occurred_at",
      ),
      optionalProcessQuery(
        "process_runs",
        "id,event_id,process_key_snapshot,process_version_snapshot,status,explanation,planned_at,completed_at",
        "planned_at",
      ),
      optionalProcessQuery(
        "required_actions",
        "id,process_run_id,event_id,step_key,title,description,assignee_role,assignee_profile_id,status,due_at,reminder_schedule,escalation_at,requires_human_decision,allowed_decisions,direct_link,decision,resolution_note,resolved_by,resolved_at,created_at",
        "created_at",
      ),
      optionalProcessQuery(
        "process_notification_intents",
        "id,process_run_id,event_id,step_key,level,title,body,recipient_role,recipient_profile_id,channel_intents,status,dispatched_at,last_error,created_at",
        "created_at",
      ),
      optionalProcessQuery(
        "process_journal_entries",
        "id,process_run_id,event_id,entry_type,message,payload,actor_profile_id,created_at",
        "created_at",
      ),
      optionalProcessDeliveryQuery(),
      optionalProcessDefinitionQuery(),
      missionReportActivityEntriesQuery,
      optionalCostingQuery(
        "procurement_requirements",
        "id,costing_run_id,costing_run_line_id,material_estimate_id,quote_id,quote_line_id,mission_id,supplier_id,inventory_item_id,source_kind,label,quantity,unit_label,estimated_unit_cost_ex_vat,estimated_total_cost_ex_vat,purchase_reference,purchase_url,needed_on,status,source_snapshot,review_note,reviewed_at,created_at",
        "created_at",
      ),
      optionalQuoteConversionQuery(),
      optionalProcessPublicationReviewQuery(),
      missionChecklistCapabilityQuery,
      missionQualityClaimsQuery,
      missionQualityClaimEventsQuery,
      missionQualityClaimAttachmentsQuery,
      workerQualityMetricsQuery,
      (async () => {
        if (!['admin','client'].includes(activeProfile.role))
          return { data: [], error: null };
        const result = await supabase!
          .from("google_review_requests")
          .select(
            "id,organization_id,mission_id,client_id,recipient_profile_id,source_report_id,request_url_snapshot,status,available_at,opened_at,dismissed_at,created_at",
          )
          .order("created_at", { ascending: false });
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("google_review_requests");
          return { data: [], error: null };
        }
        return result;
      })(),
      (async () => {
        if (activeProfile.role !== "admin") return { data: [], error: null };
        const result = await supabase!
          .from("client_portfolio_reviews")
          .select(
            "id,organization_id,client_id,period_start,period_end,suggested_class,decided_class,decision,causes,metrics_snapshot,minimum_revenue_ex_vat,rationale,next_action,next_review_on,status,reviewed_at",
          )
          .order("reviewed_at", { ascending: false });
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("client_portfolio_reviews");
          return { data: [], error: null };
        }
        return result;
      })(),
      (async () => {
        if (activeProfile.role !== "admin") return { data: [], error: null };
        const result = await supabase!
          .from("director_time_entries")
          .select("id,leader_id,work_date,category,minutes,note,created_by,created_at")
          .order("work_date", { ascending: false })
          .limit(1000);
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("director_time_allocation");
          return { data: [], error: null };
        }
        return result;
      })(),
      (async () => {
        if (activeProfile.role !== "admin") return { data: [], error: null };
        const result = await supabase!
          .from("organization_user_invitations")
          .select("id,target_type,target_id,role,email,display_name,status,sent_at,accepted_at,expires_at,last_error,source_quote_id,created_at")
          .order("created_at", { ascending: false });
        if (
          result.error &&
          /source_quote_id.*does not exist/i.test(result.error.message)
        ) {
          const fallback = await supabase!
            .from("organization_user_invitations")
            .select("id,target_type,target_id,role,email,display_name,status,sent_at,accepted_at,expires_at,last_error,created_at")
            .order("created_at", { ascending: false });
          if (fallback.error && missingDeploymentObject(fallback.error.message))
            return { data: [], error: null };
          return {
            data: (fallback.data || []).map((item) => ({
              ...item,
              source_quote_id: null,
            })),
            error: fallback.error,
          };
        }
        if (result.error && missingDeploymentObject(result.error.message))
          return { data: [], error: null };
        return result;
      })(),
      (async () => {
        const result = await supabase!.rpc("get_visible_mission_service_lines");
        if (result.error && missingDeploymentObject(result.error.message))
          return { data: [], error: null };
        return result;
      })(),
      (async () => {
        if (!['admin', 'coach'].includes(activeProfile.role))
          return { data: [], error: null };
        const result = await supabase!
          .from("referral_partners")
          .select("id,display_name,company_name,default_commission_rate,default_pricing_protection_rate,active")
          .order("display_name");
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("referral_commissions");
          return { data: [], error: null };
        }
        return result;
      })(),
      (async () => {
        if (!['admin', 'coach'].includes(activeProfile.role))
          return { data: [], error: null };
        const result = await supabase!
          .from("invoice_referral_commissions")
          .select("id,invoice_id,client_id,referral_partner_id,partner_name_snapshot,commission_rate,commission_base_ex_vat,commission_amount_ex_vat,status,approved_by,approved_at,paid_by,paid_at,payment_reference,review_note,created_at,updated_at")
          .order("created_at", { ascending: false });
        if (result.error && missingDeploymentObject(result.error.message)) {
          unavailableCapabilities.add("referral_commissions");
          return { data: [], error: null };
        }
        return result;
      })(),
    ]);
    const err = q.find((x) => x.error)?.error;
    if (err)
      setError(`Certaines données ne sont pas accessibles : ${err.message}`);
    setData({
      unavailableCapabilities: [...unavailableCapabilities],
      missions: (q[0].data || []) as unknown as Mission[],
      clients: (q[1].data || []) as Client[],
      assignments: (q[2].data || []) as Assignment[],
      workers: (q[3].data || []) as Worker[],
      times: (q[4].data || []) as TimeEntry[],
      leaves: (q[5].data || []) as Leave[],
      mileages: (q[6].data || []) as Mileage[],
      documents: (q[7].data || []) as DocumentRow[],
      services: (q[8].data || []) as ClientService[],
      requests: (q[9].data || []) as MissionRequest[],
      reports: (q[10].data || []) as MissionReport[],
      invoices: (q[11].data || []) as Invoice[],
      availability: (q[12].data || []) as Availability[],
      requirements: (q[13].data || []) as Requirement[],
      contracts: (q[14].data || []) as WorkerContract[],
      properties: (q[15].data || []) as ClientProperty[],
      propertyServices: (q[16].data || []) as PropertyService[],
      preferredWorkers: (q[17].data || []) as PreferredWorker[],
      quotes: (q[18].data || []) as Quote[],
      expenses: (q[19].data || []) as Expense[],
      organization: (q[20].data || null) as Organization | null,
      cashBalances: (q[21].data || []) as CashBalance[],
      serviceProposals: (q[22].data || []) as ServiceProposal[],
      reportAttachments: (q[23].data || []) as ReportAttachment[],
      dailyRouteTeams: (q[24].data || []) as DailyRouteTeam[],
      dailyRouteTeamMembers: (q[25].data || []) as DailyRouteTeamMember[],
      recurrences: (q[26].data || []) as MissionRecurrence[],
      profiles: (q[27].data || []) as OrgProfile[],
      contractTemplates: (q[28].data || []) as ContractTemplate[],
      clientContracts: (q[29].data || []) as ClientContract[],
      clientRequirements: (q[30].data || []) as ClientRequirement[],
      quoteLines: (q[31].data || []) as QuoteLine[],
      invoiceLines: (q[32].data || []) as InvoiceLine[],
      commercialAttachments: (q[33].data || []) as CommercialAttachment[],
      commercialEvents: (q[34].data || []) as CommercialEvent[],
      invoicePayments: (q[35].data || []) as InvoicePayment[],
      invoiceReminders: (q[36].data || []) as InvoiceReminder[],
      fixedCharges: (q[37].data || []) as FixedCharge[],
      workerRateCards: (q[38].data || []) as WorkerRateCard[],
      inventoryItems: (q[39].data || []) as InventoryItem[],
      inventoryMovements: (q[40].data || []) as InventoryMovement[],
      directorCompensations: (q[41].data || []) as DirectorCompensation[],
      profileChangeRequests: (q[42].data || []) as ProfileChangeRequest[],
      permissionSets: (q[43].data || []) as PermissionSet[],
      vehicles: (q[44].data || []) as WorkerVehicle[],
      integrations: (q[45].data || []) as OrganizationIntegration[],
      integrationCursors: (q[46].data || []) as IntegrationSyncCursor[],
      notifications: (q[47].data || []) as AppNotification[],
      accessAuditLogs: (q[48].data || []) as AccessAuditLog[],
      supportAccessGrants: (q[49].data || []) as SupportAccessGrant[],
      propertyRequests: (q[50].data || []) as ClientPropertyRequest[],
      propertyServiceQuoteRequests: (q[51].data || []) as PropertyServiceQuoteRequest[],
      propertyServiceQuoteTerms: (q[52].data || []) as PropertyServiceQuoteTerms[],
      subscriptionPlans: (q[53].data || []) as SubscriptionPlanSummary[],
      subscriptionUpgradeRequests: (q[54].data || []) as SubscriptionUpgradeRequest[],
      subscriptionAmendments: (q[55].data || []) as SubscriptionAmendmentSummary[],
      calendarConnections: (q[56].data || []) as CalendarConnection[],
      signatureEnvelopes: (q[57].data || []) as TenantSignatureEnvelope[],
      turnoverDeclarations: (q[58].data || []) as ClientTurnoverDeclaration[],
      organizationLeaders: (q[59].data || []) as OrganizationLeader[],
      inventorySuppliers: (q[60].data || []) as InventorySupplier[],
      inventoryKitComponents: (q[61].data || []) as InventoryKitComponent[],
      missionStockUsages: (q[62].data || []) as MissionStockUsage[],
      routeTeamTemplates: (q[63].data || []) as unknown as RouteTeamTemplate[],
      routeTeamTemplateMembers: (q[64].data || []) as unknown as RouteTeamTemplateMember[],
      missionReportSubmissions: (q[65].data || []) as MissionReportSubmission[],
      routeTeamContext: (q[66].data || []) as RouteTeamContext[],
      routeMileageCalculations: (q[67].data || []) as unknown as RouteMileageCalculation[],
      routeMileageStops: (q[68].data || []) as unknown as RouteMileageStop[],
      routeMileageLegs: (q[69].data || []) as unknown as RouteMileageLeg[],
      workerCommercialProposals: (q[70].data || []) as unknown as WorkerCommercialProposal[],
      serviceBillingRules: (q[71].data || []) as unknown as ServiceBillingRule[],
      missionBillingSnapshots: (q[72].data || []) as unknown as MissionBillingSnapshot[],
      commercialLineBillingSnapshots: (q[73].data || []) as unknown as CommercialLineBillingSnapshot[],
      supplierInvoices: (q[74].data || []) as unknown as PortalSupplierInvoice[],
      supplierInvoiceMissions: (q[75].data || []) as unknown as PortalSupplierInvoiceMission[],
      missionChangeEvents: (q[76].data || []) as unknown as MissionChangeEvent[],
      missionFinancialAdjustments: (q[77].data || []) as unknown as MissionFinancialAdjustment[],
      workerCancellationRules: (q[78].data || []) as unknown as WorkerCancellationRule[],
      workerCancellationNotices: (q[79].data || []) as unknown as WorkerCancellationNotice[],
      quoteSourcingWorkflows: (q[80].data || []) as unknown as QuoteSourcingWorkflow[],
      quoteSourcingOffers: (q[81].data || []) as unknown as QuoteSourcingOffer[],
      quoteSourcingTasks: (q[82].data || []) as unknown as QuoteSourcingTask[],
      workerCompanyProfiles: (q[83].data || []) as unknown as WorkerCompanyProfile[],
      workerCompanyRepresentatives: (q[84].data || []) as unknown as WorkerCompanyRepresentative[],
      workerCompanyProfileEvents: (q[85].data || []) as unknown as WorkerCompanyProfileEvent[],
      paymentReconciliationCandidates: (q[87].data || []) as BillingPaymentCandidate[],
      integrationExternalLinks: (q[88].data || []) as unknown as IntegrationExternalLink[],
      billingCases: (q[89].data || []) as unknown as BillingCaseSummary[],
      billingCaseLinks: (q[90].data || []) as unknown as BillingCaseLink[],
      billingCaseProgress: (q[91].data || []) as unknown as BillingCaseProgress[],
      billingCaseLedger: (q[92].data || []) as unknown as BillingCaseLedgerEntry[],
      serviceCatalogItems: (q[93].data || []) as unknown as ServiceCatalogItem[],
      serviceCatalogVersions: (q[94].data || []) as unknown as ServiceCatalogVersion[],
      serviceCostingProfiles: (q[95].data || []) as unknown as ServiceCostingProfile[],
      serviceCostingComponents: (q[96].data || []) as unknown as ServiceCostingComponent[],
      quoteCostingRuns: (q[97].data || []) as unknown as QuoteCostingRun[],
      quoteCostingRunLines: (q[98].data || []) as unknown as QuoteCostingRunLine[],
      missionCostingReviews: (q[99].data || []) as unknown as MissionCostingReview[],
      missionCostingReviewLines: (q[100].data || []) as unknown as MissionCostingReviewLine[],
      costingYieldRecommendations: (q[101].data || []) as unknown as CostingYieldRecommendation[],
      billingCaseAmendments: (q[102].data || []) as unknown as BillingCaseAmendment[],
      billingCaseConsistencyReviews: (q[103].data || []) as unknown as BillingCaseConsistencyReview[],
      billingCaseOperationalSyncs: (q[104].data || []) as unknown as BillingCaseOperationalSync[],
      billingCaseInvoicePreparations: (q[105].data || []) as unknown as BillingCaseInvoicePreparation[],
      serviceQualificationQuestionnaires: (q[106].data || []) as unknown as ServiceQualificationQuestionnaire[],
      serviceQualificationQuestions: (q[107].data || []) as unknown as ServiceQualificationQuestion[],
      quoteQualificationRuns: (q[108].data || []) as unknown as QuoteQualificationRun[],
      qualificationRuleEvidence: (q[109].data || []) as unknown as QualificationRuleEvidence[],
      processBusinessEvents: (q[110].data || []) as unknown as ProcessBusinessEvent[],
      processRuns: (q[111].data || []) as unknown as ProcessRun[],
      processRequiredActions: (q[112].data || []) as unknown as ProcessRequiredAction[],
      processNotificationIntents: (q[113].data || []) as unknown as ProcessNotificationIntent[],
      processJournalEntries: (q[114].data || []) as unknown as ProcessJournalEntry[],
      processNotificationDeliveries: (q[115].data || []) as unknown as ProcessNotificationDelivery[],
      processDefinitions: (q[116].data || []) as unknown as ProcessDefinitionRow[],
      missionReportActivityEntries: (q[117].data || []) as MissionReportActivityEntry[],
      procurementRequirements: (q[118].data || []) as unknown as ProcurementRequirement[],
      billingProviderDocumentTransformations: (q[119].data || []) as unknown as BillingProviderDocumentTransformation[],
      processDefinitionPublicationReviews: (q[120].data || []) as unknown as ProcessDefinitionPublicationReview[],
      missionQualityClaims: (q[122].data || []) as unknown as MissionQualityClaim[],
      missionQualityClaimEvents: (q[123].data || []) as unknown as MissionQualityClaimEvent[],
      missionQualityClaimAttachments: (q[124].data || []) as unknown as MissionQualityClaimAttachment[],
      workerQualityMetrics: (q[125].data || []) as unknown as WorkerQualityMetric[],
      googleReviewRequests: (q[126].data || []) as unknown as GoogleReviewRequest[],
      clientPortfolioReviews: (q[127].data || []) as unknown as ClientPortfolioReview[],
      directorTimeEntries: (q[128].data || []) as unknown as DirectorTimeEntry[],
      organizationUserInvitations: (q[129].data || []) as OrganizationUserInvitation[],
      missionServiceLines: (q[130].data || []) as MissionServiceLine[],
      referralPartners: (q[131].data || []) as ReferralPartner[],
      invoiceReferralCommissions: (q[132].data || []) as InvoiceReferralCommission[],
    });
    setLoading(false);
  }, [session.user.id]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!profile || isShowcaseMode) return;
    let dispose: (() => void | Promise<void>) | undefined;
    let cancelled = false;
    void initializeMobilePush({
      onState: setMobilePush,
      onReceived: () => { void load(); },
      onOpened: setPendingPushTarget,
    }).then((cleanup) => {
      if (cancelled) void cleanup();
      else dispose = cleanup;
    });
    return () => {
      cancelled = true;
      if (dispose) void dispose();
    };
  }, [profile?.id, load]);
  useEffect(() => {
    if (!pendingPushTarget) return;
    const allowedTabs: Tab[] = [
      "dashboard", "agenda", "routes", "missions", "clients", "crm", "action_plan",
      "billing", "accounting", "profitability", "stocks", "director", "properties",
      "compensation", "availability", "documents", "team", "time", "leave", "mileage",
      "proposals", "payroll", "company", "profile", "settings",
    ];
    if (pendingPushTarget.missionId) {
      const mission = data.missions.find((item) => item.id === pendingPushTarget.missionId);
      if (mission) setFocusedMission(mission);
      setTab("missions");
    } else if (pendingPushTarget.tab && allowedTabs.includes(pendingPushTarget.tab as Tab)) {
      setTab(pendingPushTarget.tab as Tab);
    }
    setPendingPushTarget(null);
  }, [pendingPushTarget, data.missions]);
  if (loading) return <main className="center">Chargement sécurisé…</main>;
  if (!profile)
    return (
      <main className="center">
        <Notice tone="error">{error || "Profil introuvable."}</Notice>
      </main>
    );
  const portalData = data as unknown as PortalData,
    client = data.clients[0];
  const openMission = (mission: Mission) => {
      setMissionCreateRequested(false);
      setFocusedMission(mission);
      setTab("missions");
    },
    newMission = () => {
      setFocusedMission(null);
      setMissionCreateRequested(true);
      setTab("missions");
    },
    openNotification = (item: AppNotification) => {
      if (profile?.role === "admin" && item.kind === "report_submitted" && item.mission_id) {
        const mission = data.missions.find(
          (candidate) => candidate.id === item.mission_id,
        );
        if (mission) openMission(mission);
        return;
      }
      if (profile?.role === "admin" && item.kind === "document_due") {
        const body = (item.body || "").toLocaleLowerCase("fr-FR"),
          relatedWorker = data.workers.find((candidate) => {
            const names = [
              workerName(candidate),
              candidate.first_name || "",
              candidate.last_name || "",
              candidate.business_name || "",
            ].filter(Boolean);
            return names.some((name) => body.includes(name.toLocaleLowerCase("fr-FR")));
          });
        setFocusedWorkerId(relatedWorker?.id || null);
        setTab("team");
        return;
      }
      const mission = data.missions.find((value) => value.id === item.mission_id);
      if (mission) openMission(mission);
    };
  type NavigationItem = { id: Tab; label: string; roles?: Role[]; group?: string };
  const adminNavigationGroups = [
    "Accueil",
    "Opérations",
    "Commerce",
    "Gestion",
    "Ressources",
    "Configuration",
  ];
  const adminNav: NavigationItem[] = [
    { id: "dashboard", label: "Centre de contrôle", group: "Accueil" },
    { id: "action_plan", label: "Plan d’action 4 mois", group: "Accueil" },
    { id: "agenda", label: "Agenda", group: "Opérations" },
    { id: "routes", label: "Tournées", group: "Opérations" },
    { id: "missions", label: "Missions", group: "Opérations" },
    { id: "clients", label: "Clients", group: "Commerce" },
    { id: "crm", label: "CRM & acquisition", group: "Commerce" },
    { id: "billing", label: "Devis & factures", group: "Commerce" },
    { id: "accounting", label: "Comptabilité", group: "Gestion" },
    { id: "profitability", label: "Rentabilité", group: "Gestion" },
    { id: "stocks", label: "Stocks", group: "Gestion" },
    { id: "director", label: "Dirigeant", group: "Ressources" },
    { id: "team", label: "Équipe & RH", group: "Ressources" },
    { id: "company", label: "Société", group: "Configuration" },
    { id: "profile", label: "Mon profil & agenda", group: "Configuration" },
    { id: "settings", label: "Réglages", group: "Configuration" },
  ];
  const portalNav: NavigationItem[] = profile.role === "employee"
    ? [
        { id: "dashboard", label: "Accueil" },
        { id: "agenda", label: "Mon agenda" },
        { id: "missions", label: "Mes missions" },
        { id: "journey", label: "Mon parcours" },
        { id: "leave", label: "Mes congés" },
        { id: "documents", label: "Mes documents" },
        { id: "profile", label: "Mon profil" },
      ]
    : [
        { id: "dashboard", label: "Tableau de bord" },
        { id: "agenda", label: "Ma journée", roles: ["client"] },
        { id: "missions", label: "Missions" },
        { id: "billing", label: "Facturation", roles: ["client"] },
        { id: "documents", label: "Documents & contrat", roles: ["client"] },
        ...(profile.role === "client" && client?.client_type === "concierge"
          ? [{ id: "properties" as Tab, label: "Biens & prestations", roles: ["client"] as Role[] }]
          : []),
        { id: "compensation", label: "Facturation", roles: ["contractor"] },
        { id: "availability", label: "Disponibilités", roles: ["contractor"] },
        { id: "mileage", label: "Mes IK", roles: ["contractor"] },
        { id: "proposals", label: "Mes propositions", roles: ["contractor"] },
        { id: "documents", label: "Documents & contrat", roles: ["contractor"] },
        { id: "profile", label: "Mon profil" },
      ];
  const currentPermissionSet = (data.permissionSets || []).find(
      (set) => set.id === profile.permission_set_id && set.active,
    ),
    isReadOnlyCoach = currentPermissionSet?.access_mode === "coach_read_only" || (isShowcaseMode && getShowcaseRole() === "coach"),
    activeOrganization = organizationContexts.find((item) => item.is_active) ||
      organizationContexts.find((item) => item.organization_id === profile.organization_id),
    nav =
      profile.role === "admin"
        ? currentPermissionSet
          ? adminNav.filter((item) =>
              currentPermissionSet.permissions.includes(item.id),
            )
          : adminNav
        : portalNav.filter((n) => !n.roles || n.roles.includes(profile.role));
  const mobilePrimaryTabs: Tab[] =
    profile.role === "admin"
      ? ["dashboard", "agenda", "missions", "clients"]
      : profile.role === "client"
        ? ["dashboard", "agenda", "missions", "billing"]
        : profile.role === "contractor"
          ? ["dashboard", "missions", "compensation", "profile"]
          : ["dashboard", "agenda", "missions", "journey"];
  const blockCoachMutation = (event: React.SyntheticEvent<HTMLElement>) => {
    if (!isReadOnlyCoach) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(".coach-notes-panel")) return;
    const button = target.closest("button");
    const label = (button?.textContent || "").replace(/\s+/g, " ").trim();
    const mutation = event.type === "submit" || !!button && /(?:^|\s)(?:créer|ajouter|modifier|enregistrer|valider|supprimer|révoquer|envoyer|publier|archiver|affecter|accepter|refuser|terminer|clôturer|préparer|transmettre|activer|désactiver|connecter|synchroniser|importer|calculer|lancer|gérer)(?:\s|$)/i.test(label);
    if (!mutation) return;
    event.preventDefault();
    event.stopPropagation();
    setCoachGuardMessage("Action bloquée : l’accès Coach est en lecture seule. Seul le journal Coaching accepte des notes.");
  };
  const switchOrganization = async (organizationId: string) => {
    if (
      isShowcaseMode ||
      switchingOrganization ||
      organizationId === profile.organization_id
    )
      return;
    if (!(await prepareTerrainContextExit(profile))) return;
    setSwitchingOrganization(true);
    setError("");
    const result = await supabase!.rpc("set_active_organization", {
      p_organization_id: organizationId,
    });
    if (result.error) {
      setError(`Changement de société refusé : ${result.error.message}`);
      setSwitchingOrganization(false);
      return;
    }
    location.reload();
  };
  return (
    <div
      className={`shell role-${profile.role} theme-${appearance}${profile.role === "contractor" || profile.role === "employee" ? " terrain-layout" : ""}${isShowcaseMode ? " showcase-product" : ""}${isReadOnlyCoach ? " coach-read-only" : ""}${mobileNavOpen ? " mobile-nav-open" : ""}`}
      data-user-role={profile.role}
      data-access-mode={isReadOnlyCoach ? "coach_read_only" : "standard"}
    >
      {isShowcaseMode && <ShowcaseToolbar />}
      {isReadOnlyCoach && <aside className="coach-read-only-banner" role="status"><strong>Accès Coach · lecture seule</strong><span>Vous pouvez consulter l’interface administrateur et rédiger uniquement des notes dans l’espace Coaching. Toute autre modification est bloquée par la base.</span></aside>}
      <header>
        <div>
          <strong className="workspace-title">ARD Manager</strong>
          <small>
            {profile.display_name} ·{" "}
            {profile.role === "admin"
              ? isReadOnlyCoach ? "Coach · lecture seule" : "Administrateur"
              : profile.role === "client"
                ? "Client"
                : profile.role === "employee"
                ? "Employé"
                  : "Sous-traitant"}
            {activeOrganization ? ` · ${activeOrganization.organization_name}` : ""}
          </small>
        </div>
        <div className="header-actions">
          <button
            className="mobile-nav-toggle"
            type="button"
            aria-label="Ouvrir la navigation"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((current) => !current)}
          >
            <span aria-hidden="true">☰</span>
            <span>Menu</span>
          </button>
          {!isShowcaseMode && organizationContexts.length > 1 && (
            <label className="workspace-switcher">
              <span>Société active</span>
              <select
                aria-label="Changer de société active"
                value={profile.organization_id}
                disabled={switchingOrganization}
                onChange={(event) => void switchOrganization(event.target.value)}
              >
                {organizationContexts.map((item) => (
                  <option key={item.organization_id} value={item.organization_id}>
                    {item.organization_name} · {item.role === "admin" ? "Administrateur" : item.role === "client" ? "Client" : item.role === "employee" ? "Employé" : "Sous-traitant"}
                  </option>
                ))}
              </select>
            </label>
          )}
          <NotificationCenter
            profile={profile}
            items={data.notifications || []}
            reload={load}
            onOpen={openNotification}
            mobilePush={mobilePush}
            onEnableMobilePush={() => { void requestMobilePushPermission(); }}
            onDisableMobilePush={() => { void deactivateMobilePush(); }}
          />
          <button
            className="ghost application-feedback-trigger"
            type="button"
            title="Proposer une amélioration ou signaler un défaut"
            onClick={() => setFeedbackOpen(true)}
          >
            <span aria-hidden="true">💬</span> Améliorer l’app
          </button>
          <button
            className="ghost theme-quick-toggle"
            type="button"
            title="Changer le thème d’affichage"
            onClick={() => changeAppearance(appearance === "dark" ? "light" : "dark")}
          >
            <span aria-hidden="true">{appearance === "dark" ? "☀" : "◐"}</span>{" "}
            {appearance === "dark" ? "Clair" : "Sombre"}
          </button>
          {profile.role === "admin" && !isReadOnlyCoach && (
            <button
              className="create-button"
              onClick={() => {
                setCreateKind("menu");
                setCreateOpen(true);
              }}
            >
              ＋ Créer
            </button>
          )}
          <button className="ghost" onClick={() => isShowcaseMode ? (location.href = "/") : void signOutSecurely()}>
            {isShowcaseMode ? "Retour au réel" : "Déconnexion"}
          </button>
        </div>
      </header>
      <nav className="primary-navigation" aria-label="Navigation principale">
        <div className="nav-brand">
          <BrandLockup />
          <button type="button" className="nav-close" aria-label="Fermer la navigation" onClick={() => setMobileNavOpen(false)}>×</button>
        </div>
        {profile.role === "admin"
          ? adminNavigationGroups.map((group) => {
              const items = nav.filter((item) => item.group === group);
              if (!items.length) return null;
              return (
                <div className="nav-group" role="group" aria-label={group} key={group}>
                  <span className="nav-group-label">{group}</span>
                  <div className="nav-group-items">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        className={`${tab === item.id ? "active" : ""}${mobilePrimaryTabs.includes(item.id) ? " mobile-primary" : ""}`}
                        aria-current={tab === item.id ? "page" : undefined}
                        title={item.label}
                        onClick={() => {
                          setTab(item.id);
                          setMobileNavOpen(false);
                        }}
                      >
                        <NavigationIcon tab={item.id} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          : nav.map((item) => (
              <button
                key={item.id}
                className={`${tab === item.id ? "active" : ""}${mobilePrimaryTabs.includes(item.id) ? " mobile-primary" : ""}`}
                aria-current={tab === item.id ? "page" : undefined}
                title={item.label}
                onClick={() => {
                  setTab(item.id);
                  setMobileNavOpen(false);
                }}
              >
                <NavigationIcon tab={item.id} />
                <span>{item.label}</span>
              </button>
            ))}
        <div className="nav-account-actions">
          <span>
            <strong>{profile.display_name}</strong>
            <small>{profile.email || (profile.role === "admin" ? "Administrateur" : labels[profile.role] || profile.role)}</small>
          </span>
          <button type="button" onClick={() => isShowcaseMode ? (location.href = "/") : void signOutSecurely()}>
            {isShowcaseMode ? "Quitter le Showcase" : "Se déconnecter"}
          </button>
        </div>
      </nav>
      <button className="navigation-scrim" type="button" aria-label="Fermer la navigation" onClick={() => setMobileNavOpen(false)} />
      <main className="content" onClickCapture={blockCoachMutation} onSubmitCapture={blockCoachMutation}>
        {error && <Notice tone="error">{error}</Notice>}
        {coachGuardMessage && <Notice tone="info">{coachGuardMessage}</Notice>}
        {tab === "dashboard" &&
          (profile.role === "client" ? (
            <ClientDashboardRich profile={profile} data={portalData} />
          ) : profile.role === "admin" ? (
            <>
              {/* La commercialisation d’ARD Manager est différée. Les données
                  d’abonnement restent conservées pour une éventuelle reprise,
                  mais aucune sollicitation commerciale ne s’affiche dans
                  l’application opérationnelle. */}
              <AdminDashboard
                profile={profile}
                data={data}
                reload={load}
                onEditMission={openMission}
                onOpenReport={(missionId) => {
                  setFocusedMission(null);
                  setFocusedReportMissionId(missionId);
                  setTab("missions");
                }}
                onOpenInvoice={(invoiceId) => {
                  setFocusedBillingInvoiceId(invoiceId);
                  setTab("billing");
                }}
                onOpenSupplierInvoice={(invoiceId) => {
                  setFocusedSupplierInvoiceId(invoiceId);
                  setTab("billing");
                }}
                onOpenExpense={(expenseId) => {
                  setFocusedExpenseId(expenseId);
                  setTab("accounting");
                }}
                onNavigate={setTab}
              />
            </>
          ) : (
            <WorkerDashboardRich
              profile={profile}
              worker={worker}
              data={portalData}
              reload={load}
              onNavigate={(target) => setTab(target)}
            />
          ))}
        {tab === "agenda" && profile.role === "admin" && (
          <AdminAgenda
            data={data}
            reload={load}
            onEditMission={openMission}
          />
        )}
        {tab === "agenda" && profile.role === "employee" && (
          <WorkerAgendaRich
            worker={worker}
            data={portalData}
            onSelectMission={openMission}
            onRequestLeave={() => setTab("leave")}
          />
        )}
        {tab === "agenda" && profile.role === "client" && (
          <ClientDayAgenda
            data={data}
            onOpenMissions={() => setTab("missions")}
          />
        )}
        {tab === "journey" && profile.role === "employee" && (
          <WorkerJourneyRich worker={worker} data={portalData} />
        )}
        {tab === "routes" && profile.role === "admin" && (
          <AdminRoutes
            profile={profile}
            data={data}
            reload={load}
            onSelect={openMission}
          />
        )}
        {tab === "missions" &&
          (profile.role === "admin" ? (
            <>
              <EmergencyOperationsCenter profile={profile} data={data} />
              <AdminWorkflow
                profile={profile}
                data={data}
                reload={load}
                focusReportMissionId={focusedReportMissionId}
                onFocusReportConsumed={() => setFocusedReportMissionId(null)}
              />
              <AdminMissionChecklistSettings data={data} reload={load} />
              <AdminQualityAnalytics />
              <MissionAdminModal
                profile={profile}
                data={data}
                reload={load}
                createRequested={missionCreateRequested}
                focusMission={focusedMission}
                onConsumed={() => setFocusedMission(null)}
                onCreateConsumed={() => setMissionCreateRequested(false)}
                onReviewReport={(missionId) => {
                  setFocusedReportMissionId(missionId);
                  setTab("missions");
                }}
              />
              <AdminRecurrences profile={profile} data={data} reload={load} />
            </>
          ) : profile.role === "client" ? (
            <ClientMissionsRich
              profile={profile}
              data={portalData}
              reload={load}
            />
          ) : (
            <WorkerMissionsRich
              profile={profile}
              worker={worker}
              data={portalData}
              reload={load}
            />
          ))}
        {tab === "clients" && profile.role === "admin" && (
          <AdminClients data={data} onSelect={openMission} reload={load} />
        )}{" "}
        {tab === "crm" && profile.role === "admin" && (
          <AdminCrm
            organizationId={profile.organization_id}
            profileId={profile.id}
            profiles={(data.profiles || []).map((item) => ({
              id: item.id,
              display_name: item.display_name,
            }))}
            clients={data.clients.map((item) => ({
              id: item.id,
              name: item.name,
            }))}
            presentationLauncher={<DemoPresentationLauncher compact />}
          />
        )}{" "}
        {tab === "action_plan" && profile.role === "admin" && (
          <>
            <AdminActionPlan
              organizationId={profile.organization_id}
              profileId={profile.id}
              profiles={(data.profiles || []).map((item) => ({
                id: item.id,
                display_name: item.display_name,
              }))}
              data={data}
              onNavigate={(target) => setTab(target)}
            />
            <CoachNotesPanel profileId={profile.id} readOnlyCoach={isReadOnlyCoach} />
          </>
        )}{" "}
        {tab === "billing" &&
          (profile.role === "admin" ? (
            <>
              {hasDeploymentCapability(data, "supplier_invoicing") && (
                <AdminSupplierInvoicesRich
                  data={portalData}
                  reload={load}
                  focusInvoiceId={focusedSupplierInvoiceId}
                  onFocusConsumed={() => setFocusedSupplierInvoiceId(null)}
                />
              )}
              <AdminBilling
                data={data}
                reload={load}
                onSelectMission={openMission}
                onNewQuote={() => {
                  setCreateKind("quote");
                  setCreateOpen(true);
                }}
                onNewInvoice={() => {
                  setCreateKind("invoice");
                  setCreateOpen(true);
                }}
                guidedQuestionnaireRequest={guidedQuestionnaireRequest}
                onGuidedQuestionnaireRequestConsumed={() =>
                  setGuidedQuestionnaireRequest(null)
                }
                servicePricingRequest={servicePricingRequest}
                onServicePricingRequestConsumed={() =>
                  setServicePricingRequest(null)
                }
                onReturnToProfitability={
                  billingOrigin === "profitability"
                    ? () => {
                        setBillingOrigin(null);
                        setTab("profitability");
                      }
                    : undefined
                }
                onOpenStockItem={(itemId) => {
                  setFocusedInventoryItemId(itemId);
                  setStockOpenedFromCatalog(true);
                  setTab("stocks");
                }}
                catalogOpenRequest={billingCatalogOpenRequest}
                onCatalogOpenRequestConsumed={() =>
                  setBillingCatalogOpenRequest(null)
                }
                invoiceRequest={focusedBillingInvoiceId}
                onInvoiceRequestConsumed={() =>
                  setFocusedBillingInvoiceId(null)
                }
              />
            </>
          ) : (
            <ClientBillingRich data={portalData} />
          ))}{" "}
        {tab === "accounting" && profile.role === "admin" && (
          <AdminAccounting
            profile={profile}
            data={data}
            reload={load}
            focusExpenseId={focusedExpenseId}
            onFocusConsumed={() => setFocusedExpenseId(null)}
          />
        )}{" "}
        {tab === "profitability" && profile.role === "admin" && (
          <AdminProfitability
            data={data}
            onOpenServicePricing={(focus) => {
              setBillingOrigin("profitability");
              setServicePricingRequest(focus);
              setTab("billing");
            }}
          />
        )}{" "}
        {tab === "stocks" && profile.role === "admin" && (
          <AdminInventory
            profile={profile}
            data={data}
            reload={load}
            focusItemId={focusedInventoryItemId}
            onFocusConsumed={() => setFocusedInventoryItemId(null)}
            onReturnToCatalog={
              stockOpenedFromCatalog
                ? () => {
                    setStockOpenedFromCatalog(false);
                    setBillingCatalogOpenRequest(Date.now());
                    setTab("billing");
                  }
                : undefined
            }
          />
        )}{" "}
        {tab === "director" && profile.role === "admin" && (
          <AdminDirector profile={profile} data={data} reload={load} />
        )}{" "}
        {tab === "properties" && (
          <ClientPropertiesRich
            profile={profile}
            data={portalData}
            reload={load}
          />
        )}{" "}
        {tab === "compensation" && (
          <ContractorBillingRich
            profile={profile}
            worker={worker}
            data={portalData}
            reload={load}
          />
        )}{" "}
        {tab === "availability" && (
          <ContractorAvailabilityRich
            profile={profile}
            worker={worker}
            data={portalData}
            reload={load}
          />
        )}{" "}
        {tab === "documents" &&
          (profile.role === "client" ? (
            <ClientDocumentsRich
              profile={profile}
              data={portalData}
              reload={load}
            />
          ) : (
            <ContractorDocumentsRich
              profile={profile}
              worker={worker}
              data={portalData}
              reload={load}
            />
          ))}{" "}
        {tab === "team" && profile.role === "admin" && (
          <section className="admin-hr-workspace" data-testid="admin-hr-workspace">
            <header className="panel admin-hr-workspace-head">
              <div>
                <span className="eyebrow">Équipe &amp; RH</span>
                <h1>Piloter l’équipe sans parcourir toute la page</h1>
                <p>Ouvrez uniquement le sujet utile : fiches individuelles, planning, parcours, administration ou coûts.</p>
              </div>
              <span className="status-badge">{data.workers.filter((item) => item.active).length} personne(s) active(s)</span>
            </header>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-directory-chapter" open>
              <summary>
                <span><strong>Annuaire et fiches individuelles</strong><small>Coordonnées, accès, contrat, documents, tarifs et historique</small></span>
                <span className="file-chapter-summary-value">{data.workers.length} fiche(s)</span>
              </summary>
              <div className="file-chapter-body">
                <Team
                  profile={profile}
                  data={data}
                  reload={load}
                  focusWorkerId={focusedWorkerId}
                  onFocusWorkerConsumed={() => setFocusedWorkerId(null)}
                  onSelectMission={openMission}
                />
              </div>
            </details>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-planning-chapter">
              <summary>
                <span><strong>Planning, horaires et astreintes</strong><small>Semaine de l’équipe, rythmes de travail, repos et chaîne N1/N2</small></span>
                <span className="file-chapter-summary-value">Organiser</span>
              </summary>
              <div className="file-chapter-body">
                <AdminHrPlanning
                  organizationId={profile.organization_id}
                  workers={data.workers}
                  profiles={data.profiles || []}
                  missions={data.missions}
                  assignments={data.assignments}
                />
              </div>
            </details>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-development-chapter">
              <summary>
                <span><strong>Parcours, objectifs et primes</strong><small>Entretiens, progression mesurée, décisions et règles de prime</small></span>
                <span className="file-chapter-summary-value">Faire évoluer</span>
              </summary>
              <div className="file-chapter-body"><AdminWorkerDevelopment workers={data.workers} /></div>
            </details>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-preferences-chapter">
              <summary>
                <span><strong>Référents par client et par bien</strong><small>Conciergeries et sous-traitants à proposer en priorité</small></span>
                <span className="file-chapter-summary-value">Affecter</span>
              </summary>
              <div className="file-chapter-body">
                <PreferredWorkersAdmin profile={profile} data={data} reload={load} />
              </div>
            </details>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-administration-chapter">
              <summary>
                <span><strong>Administration du personnel</strong><small>Congés, temps, kilomètres, paie et propositions commerciales ST</small></span>
                <span className="file-chapter-summary-value">Traiter</span>
              </summary>
              <div className="file-chapter-body"><AdminPeopleHub profile={profile} data={data} reload={load} /></div>
            </details>
            <details className="file-chapter admin-hr-chapter" data-testid="hr-costs-chapter">
              <summary>
                <span><strong>Coûts et aide à la décision</strong><small>Comparer les hypothèses salarié / sous-traitant avec leurs limites</small></span>
                <span className="file-chapter-summary-value">Comparer</span>
              </summary>
              <div className="file-chapter-body"><WorkforceCostComparison data={data} /></div>
            </details>
          </section>
        )}{" "}
        {tab === "time" && (
          <TimeTracking
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.times}
            reload={load}
          />
        )}{" "}
        {tab === "leave" && (
          <LeavePage
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.leaves}
            reload={load}
          />
        )}{" "}
        {tab === "mileage" && (
          <MileagePage
            profile={profile}
            worker={worker}
            workers={data.workers}
            items={data.mileages}
            data={data}
            reload={load}
          />
        )}{" "}
        {tab === "proposals" && profile.role === "contractor" && (
          <WorkerProposalPage profile={profile} worker={worker} data={data} reload={load} />
        )}{" "}
        {tab === "payroll" && <Payroll worker={worker} docs={data.documents} />}{" "}
        {tab === "company" && profile.role === "admin" && (
          <AdminCompanyProfile profile={profile} data={data} reload={load} />
        )}{" "}
        {tab === "settings" && profile.role === "admin" && (
          <AdminSettings
            profile={profile}
            data={data}
            reload={load}
            appearance={appearance}
            onAppearanceChange={changeAppearance}
            onManageGuidedQuote={(catalogItemId) => {
              setGuidedQuestionnaireRequest(catalogItemId);
              setTab("billing");
            }}
          />
        )}{" "}
        {tab === "profile" && (
          <>
            <AppearanceSettings value={appearance} onChange={changeAppearance} />
            <ProfileEditorRich
              profile={profile}
              client={profile.role === "client" ? client : undefined}
              worker={worker}
              calendarConnection={(data.calendarConnections || []).find(
                (connection) => connection.profile_id === profile.id,
              )}
              reload={load}
            />
            <PrivacyRightsPanel profile={profile} />
            <ProcessNotificationPreferencesPanel
              profile={profile}
              data={data}
              reload={load}
            />
          </>
        )}
      </main>
      {createOpen && profile.role === "admin" && !isReadOnlyCoach && (
        <AdminCreateModal
          profile={profile}
          data={data}
          initialKind={createKind}
          reload={load}
          onClose={() => setCreateOpen(false)}
          onMission={newMission}
        />
      )}
      <ApplicationFeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        role={profile.role}
        screenKey={tab}
        screenLabel={nav.find((item) => item.id === tab)?.label || "ARD Manager"}
      />
    </div>
  );
}
type LegacyFrameWindow = Window & { ardExportDB?: () => unknown };
function LegacyAdmin({ profile }: { profile: Profile }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const remote = useRef<unknown | undefined>(undefined);
  const local = useRef<unknown | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState("Ouverture de la référence V26…");
  useEffect(() => {
    let active = true,
      bootstrapped = false;
    const send = (state: unknown) =>
      frame.current?.contentWindow?.postMessage(
        { type: "ARD_INIT", state },
        location.origin,
      );
    const save = async (state: unknown) => {
      setStatus("Sauvegarde de la référence…");
      const { error } = await supabase!.from("admin_app_snapshots").upsert(
        {
          organization_id: profile.organization_id,
          state,
          schema_version: 26,
          updated_by: profile.id,
        },
        { onConflict: "organization_id" },
      );
      if (!active) return;
      setStatus(
        error
          ? "Référence non sauvegardée : " + error.message
          : "Référence V26 sauvegardée séparément · aucune donnée importée",
      );
    };
    const schedule = (state: unknown) => {
      local.current = state;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => save(state), 900);
    };
    const bootstrap = () => {
      if (bootstrapped || remote.current === undefined) return;
      const child = frame.current?.contentWindow as
        | LegacyFrameWindow
        | null
        | undefined;
      const state = child?.ardExportDB?.();
      if (!state) return;
      bootstrapped = true;
      local.current = state;
      send(remote.current);
      if (remote.current === null) schedule(state);
    };
    const onMessage = (
      event: MessageEvent<{ type?: string; state?: unknown }>,
    ) => {
      if (event.origin !== location.origin) return;
      const { type, state } = event.data || {};
      if (
        (type === "ARD_BRIDGE_LOADED" || type === "ARD_BRIDGE_READY") &&
        state
      )
        local.current = state;
      if (type === "ARD_DB_CHANGED" && state) schedule(state);
      bootstrap();
    };
    window.addEventListener("message", onMessage);
    const poll = setInterval(bootstrap, 300);
    supabase!
      .from("admin_app_snapshots")
      .select("state,updated_at")
      .eq("organization_id", profile.organization_id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          remote.current = null;
          setStatus(
            error.code === "42P01"
              ? "Application complète active — migration Supabase 005 à appliquer"
              : "Mode local : " + error.message,
          );
        } else {
          remote.current = data?.state ?? null;
          setStatus(
            data
              ? "Référence V26 chargée · données isolées du produit"
              : "Référence V26 locale · données isolées du produit",
          );
        }
        bootstrap();
      });
    return () => {
      active = false;
      clearInterval(poll);
      window.removeEventListener("message", onMessage);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [profile.id, profile.organization_id]);
  return (
    <div className="legacy-admin">
      <header>
        <div>
          <strong>ARD Clean &amp; Manage</strong>
          <small>{profile.display_name} · référence fonctionnelle V26</small>
        </div>
        <span className="sync-state">{status}</span>
        <button className="ghost" onClick={() => (location.href = "/")}>
          Retour à l’application
        </button>
        <button className="ghost" onClick={() => void deactivateMobilePush().then(() => supabase!.auth.signOut())}>
          Déconnexion
        </button>
      </header>
      <iframe
        ref={frame}
        title="Référence fonctionnelle ARD V26"
        src="/ard-manager-v26.html?embedded=1"
      />
    </div>
  );
}
function AuthenticatedApp({ session }: { session: Session }) {
  const [profile, setProfile] = useState<Profile | null>(null),
    [platformAdmin, setPlatformAdmin] = useState<{ user_id: string; display_name: string; active: boolean } | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  useEffect(() => {
    if (isShowcaseMode) configureShowcaseUser(session.user.id);
    let active=true;
    void (async()=>{
      const platformClaim=await supabase!.rpc("claim_organization_admin_invitation");
      const userClaim=platformClaim.data
        ? {data:null,error:null}
        : await supabase!.rpc("claim_organization_user_invitation");
      const [profileResult,platformResult]=await Promise.all([
        loadApplicationProfile(session),
        supabase!.from("platform_admins").select("user_id,display_name,active").eq("user_id",session.user.id).eq("active",true).maybeSingle(),
      ]);
      if(!active) return;
      setProfile(profileResult.profile);
      setPlatformAdmin(platformResult.data as typeof platformAdmin);
      setError(platformClaim.error?.message||userClaim.error?.message||profileResult.error||platformResult.error?.message||"");
      setLoading(false);
    })();
    return ()=>{active=false;};
  }, [session.user.id]);
  if (loading)
    return <main className="center">Ouverture de votre espace…</main>;
  const platformRequested=new URLSearchParams(location.search).has("platform");
  if(platformRequested&&platformAdmin) return <PlatformAdminApp admin={platformAdmin}/>;
  if(platformRequested&&!platformAdmin) return <main className="center"><Notice tone="error">Ce compte n’est pas autorisé à administrer la plateforme ARD Manager.</Notice></main>;
  if (!profile&&platformAdmin) return <PlatformAdminApp admin={platformAdmin}/>;
  if (!profile)
    return (
      <main className="center">
        <Notice tone="error">{error || "Profil introuvable."}</Notice>
      </main>
    );
  if (profile.access_status === "suspended")
    return (
      <main className="center">
        <Notice tone="error">
          Ce compte est suspendu. Contactez l’administrateur de votre société.
        </Notice>
      </main>
    );
  const referenceV26 = new URLSearchParams(location.search).has(
    "reference-v26",
  );
  return profile.role === "admin" && referenceV26 && !isShowcaseMode ? (
    <LegacyAdmin profile={profile} />
  ) : (
    <>
      <WorkspaceRich session={session} />
      {profile.role === "admin" && !isShowcaseMode && import.meta.env.DEV && !Capacitor.isNativePlatform() && (
        <div className="admin-shortcuts">
          {platformAdmin&&<button onClick={() => (location.href = "/?platform=1")}>Superadministration</button>}
        </div>
      )}
    </>
  );
}
const showcaseSession = {
  access_token: "showcase-local-only",
  refresh_token: "showcase-local-only",
  expires_in: 86400,
  token_type: "bearer",
  user: {
    id: "showcase-local-user",
    aud: "authenticated",
    role: "authenticated",
    email: "showcase@local.invalid",
    app_metadata: {},
    user_metadata: {},
    created_at: "2026-01-01T00:00:00.000Z",
  },
} as unknown as Session;
function App() {
  const [session, setSession] = useState<Session | null>(null),
    [loading, setLoading] = useState(true),
    [recovering, setRecovering] = useState(
      location.pathname === "/update-password",
    );
  useEffect(() => {
    if (isShowcaseMode) {
      setLoading(false);
      return;
    }
    if (!supabase) return setLoading(false);
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const sub = supabase.auth.onAuthStateChange((e: AuthChangeEvent, s) => {
      setSession(s);
      if (e === "PASSWORD_RECOVERY") setRecovering(true);
    }).data.subscription;
    return () => sub.unsubscribe();
  }, []);
  if (isShowcaseMode)
    return new URLSearchParams(location.search).get("screen") === "login"
      ? <Login />
      : <AuthenticatedApp session={showcaseSession} />;
  if (!configured) return <Setup />;
  if (recovering)
    return (
      <PasswordUpdate
        done={() => setRecovering(false)}
        session={session}
      />
    );
  if (loading) return <main className="center">Chargement…</main>;
  return session ? <AuthenticatedApp session={session} /> : <Login />;
}
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    if (!registrations.length) return;
    await Promise.all(registrations.map((registration) => registration.unregister()));
    if (
      navigator.serviceWorker.controller &&
      sessionStorage.getItem("ard-dev-sw-reset") !== "done"
    ) {
      sessionStorage.setItem("ard-dev-sw-reset", "done");
      location.reload();
    }
  });
}
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
